import React, { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, useNavigate, Link, useParams, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { api, fmtINR, fmtDate, fmtINRPdf, fmtGeneratedAt, downloadCSV } from './lib/api';
import { supabase } from './lib/supabaseClient';
import { toast } from 'sonner';
import {
  Sun, Moon, LogOut, Plus, Trash2, Edit3, X, ChevronRight, ChevronDown, ChevronUp,
  FolderKanban, Users, Calculator, FileText, Database, Home, Shield, Upload, Download, CheckCircle,
  PenLine, Scale, ArrowRight, ArrowLeft, ListChecks, Building2, Lock, Unlock, Truck, Menu,
  TrendingUp, TrendingDown, Clock3, Award, Landmark, BookOpen, ExternalLink, Eye, EyeOff,
  Package, Layers, GitMerge, Undo2, Receipt, Printer, Calendar, Flag, ArrowRightLeft, RefreshCw,
  Phone, Mail, LayoutGrid, List as ListIcon, Search as SearchIcon
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from 'recharts';
// jspdf/jspdf-autotable/xlsx are loaded on demand (see loadPdf/loadXlsx below) —
// they're only needed for PDF export and Excel import, not on every page load.

// ============= LAZY-LOADED HEAVY LIBS =============
// jspdf/autotable and xlsx are sizeable (the PDF stack alone is ~29MB unpacked)
// and only needed at the moment someone exports a PDF or imports an Excel file —
// loading them upfront made every page load pay for both, even the login screen.
let _pdfLibs = null;
const loadPdf = async () => {
  if (!_pdfLibs) {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'), import('jspdf-autotable'),
    ]);
    _pdfLibs = { jsPDF, autoTable };
  }
  return _pdfLibs;
};
let _xlsxLib = null;
const loadXlsx = async () => {
  if (!_xlsxLib) _xlsxLib = await import('xlsx');
  return _xlsxLib;
};

// ============= AUTH CONTEXT =============
const AuthContext = React.createContext(null);
const useAuth = () => React.useContext(AuthContext);

// Remembers a page's filter/tab choice for this browser tab, so opening a
// record and pressing Back returns to the list exactly as it was left rather
// than the default view. sessionStorage: clears when the tab closes, and on
// logout (see logout below) so one login never inherits another's filters.
const PERSIST_PREFIX = 'anvid:ui:';
function usePersistedState(key, initial) {
  const storageKey = PERSIST_PREFIX + key;
  const [value, setValue] = useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw !== null) return JSON.parse(raw);
    } catch (e) { /* storage blocked or corrupt: fall back to the default */ }
    return typeof initial === 'function' ? initial() : initial;
  });
  useEffect(() => {
    try { sessionStorage.setItem(storageKey, JSON.stringify(value)); } catch (e) { /* ignore */ }
  }, [storageKey, value]);
  return [value, setValue];
}
const clearPersistedUi = () => {
  try {
    Object.keys(sessionStorage).filter(k => k.startsWith(PERSIST_PREFIX)).forEach(k => sessionStorage.removeItem(k));
  } catch (e) { /* ignore */ }
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async () => {
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
    } catch { setUser(null); }
  };

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (data?.session) await loadProfile();
      if (mounted) setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) await loadProfile();
      else setUser(null);
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  const logout = async () => {
    clearPersistedUi();
    await supabase.auth.signOut();
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refresh: loadProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

// ============= THEME =============
function useTheme() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.classList.toggle('light', next === 'light');
    window.dispatchEvent(new Event('themechange'));
  };
  return { theme, toggle };
}

// Chart components live outside the component that owns useTheme()'s state,
// so they can't just call useTheme() and get the same instance — they'd read
// localStorage once on mount and never see the other instance's toggle().
// This subscribes to the 'themechange' event useTheme().toggle() now fires,
// so charts re-render with correct axis/tooltip colors when the theme flips.
function useIsLightTheme() {
  const [isLight, setIsLight] = useState(() => document.documentElement.classList.contains('light'));
  useEffect(() => {
    const onChange = () => setIsLight(document.documentElement.classList.contains('light'));
    window.addEventListener('themechange', onChange);
    return () => window.removeEventListener('themechange', onChange);
  }, []);
  return isLight;
}

// ============= NAV =============
// Auto-fit desktop nav: how many links fit changes with both window width
// and role (master_admin gets 2 extra links) — a fixed CSS breakpoint can't
// track that, so widths are measured for real via a hidden clone and
// whatever doesn't fit collapses into a "More" dropdown, recomputed on
// resize via ResizeObserver.
function OverflowNav({ navLinks }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);

  // onMouseLeave doesn't fire on touch, so tapping "More" would open a
  // dropdown with no way to close it again on mobile/tablet — tap-outside
  // (or Escape) is the pattern that works on both touch and mouse.
  useEffect(() => {
    if (!moreOpen) return;
    const onOutside = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const onEscape = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('pointerdown', onOutside);
    document.addEventListener('keydown', onEscape);
    return () => { document.removeEventListener('pointerdown', onOutside); document.removeEventListener('keydown', onEscape); };
  }, [moreOpen]);

  useLayoutEffect(() => {
    const recompute = () => {
      const container = containerRef.current;
      const measurer = measureRef.current;
      if (!container || !measurer) return;
      const available = container.offsetWidth;
      const itemEls = Array.from(measurer.querySelectorAll('[data-measure-item]'));
      const moreEl = measurer.querySelector('[data-measure-more]');
      const moreWidth = moreEl ? moreEl.offsetWidth : 0;
      const totalWidth = itemEls.reduce((sum, el) => sum + el.offsetWidth, 0);
      if (totalWidth <= available) { setVisibleCount(itemEls.length); return; }
      let used = 0, count = 0;
      for (let i = 0; i < itemEls.length; i++) {
        used += itemEls[i].offsetWidth;
        if (used + moreWidth > available) break;
        count = i + 1;
      }
      setVisibleCount(count);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', recompute);
    return () => { ro.disconnect(); window.removeEventListener('resize', recompute); };
  }, [navLinks]);

  const visible = navLinks.slice(0, visibleCount);
  const overflow = navLinks.slice(visibleCount);
  const navLinkClass = "text-sm px-3 py-2 rounded-full hover:text-[color:var(--gold)] whitespace-nowrap";

  return (
    // Deliberately NOT overflow-hidden on this outer element — it's what's
    // measured for available width, but if the dropdown panel below is a
    // descendant of an overflow-hidden ancestor, it gets clipped away the
    // instant it renders below that box's edge (absolute positioning does
    // NOT exempt it — this bit the Modal component too, see its portal).
    // Only the links row itself, which never needs to show anything past
    // its own edge, gets the defensive overflow-hidden.
    <div ref={containerRef} className="flex-1 min-w-0 flex items-center gap-1">
      {/* Off-screen clone, same markup/classes as the real links, used only
          to measure natural widths — never visible, never interactive. */}
      <div ref={measureRef} className="flex items-center gap-1" style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', top: -9999, left: -9999 }} aria-hidden="true">
        {navLinks.map(([to, label, Icon]) => (
          <span key={to} data-measure-item className={navLinkClass}><span className="inline-flex items-center gap-1.5"><Icon size={14}/>{label}</span></span>
        ))}
        <span data-measure-more className={navLinkClass}><span className="inline-flex items-center gap-1">More<ChevronDown size={14}/></span></span>
      </div>
      <div className="flex items-center gap-1 min-w-0 overflow-hidden">
        {visible.map(([to, label, Icon]) => (
          <Link key={to} to={to} className={navLinkClass} data-testid={`nav-${label.toLowerCase()}`}>
            <span className="inline-flex items-center gap-1.5"><Icon size={14}/>{label}</span>
          </Link>
        ))}
      </div>
      {overflow.length > 0 && (
        <div ref={moreRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button type="button" onClick={() => setMoreOpen(v => !v)} className={navLinkClass} data-testid="nav-more-toggle">
            <span className="inline-flex items-center gap-1">More<ChevronDown size={14}/></span>
          </button>
          {moreOpen && (
            <div className="card card-dark" style={{ position: 'absolute', zIndex: 50, top: '100%', right: 0, marginTop: 4, minWidth: 200, padding: 8 }}>
              {overflow.map(([to, label, Icon]) => (
                <Link key={to} to={to} onClick={() => setMoreOpen(false)} className="text-sm px-3 py-2.5 rounded-lg hover:text-[color:var(--gold)] flex items-center gap-2" data-testid={`nav-more-${label.toLowerCase()}`}>
                  <Icon size={14}/>{label}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TopNav() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // While the phone menu is open it must be the thing that scrolls: the page behind is frozen and the menu is capped to the
  // screen height (it used to grow past the screen, so the page scrolled first and the menu only after the page hit its bottom).
  const menuRef = useRef(null);
  const [menuMaxH, setMenuMaxH] = useState(null);
  useEffect(() => {
    if (!mobileOpen) { setMenuMaxH(null); return undefined; }
    const fit = () => {
      if (window.innerWidth >= 768) { setMobileOpen(false); return; }
      const el = menuRef.current; if (!el) return;
      const safeBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom'), 10) || 0;
      setMenuMaxH(Math.max(160, window.innerHeight - Math.max(el.getBoundingClientRect().top, 0) - safeBottom - 8));
    };
    fit();
    window.addEventListener('resize', fit);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('resize', fit); document.body.style.overflow = prev; };
  }, [mobileOpen]);

  // Ordered by realistic daily-use frequency, not alphabetically or by
  // when each screen was built — this list also feeds OverflowNav, so
  // order has a real functional effect: whatever sorts last is the first
  // to get tucked behind "More" on a narrower screen. Dashboard/Projects
  // are constant daily traffic; Income/Company Expenses/Extra Works/
  // Partner Settlement are routine ongoing entry; Vendors/Security are
  // periodic; Fixed Assets is largely set-and-forget; Forecast/Reports/
  // Audit Queue are occasional, look-things-up screens.
  // Real-usage-gap finding: the old standalone "Partners" tab (plain
  // name/phone/email master-data CRUD, no financial data) moved under
  // Settings (it's master data, alongside Companies/Masters/Profit
  // Sharing) — replaced here by "Partner Settlement," the new
  // consolidated per-partner dashboard (position + ledger + record a
  // transaction), which is what this slot's daily use actually is.
  const navLinks = [
    ['/app/dashboard','Dashboard',Home],
    ['/app/projects','Projects',FolderKanban],
    ['/app/income','Income',TrendingUp],
    ['/app/company-expenses','Company Expenses',Building2],
    ['/app/extra-works','Extra Works',Layers],
    ['/app/bills','Bills',Receipt],
    ['/app/gst-input','GST Input Credit',Calculator],
    ['/app/partner-settlement','Partner Settlement',Users],
    ['/app/vendors','Staff & Vendor',Truck],
    ['/app/security','Security & Refundables',Lock],
    ['/app/assets','Fixed Assets',Package],
    ['/app/reports','Reports',FileText],
    ['/app/audit-queue','Audit Queue',ListChecks],
  ];
  // Round 3 audit P1-2: Data (bulk import/export/restore) is
  // administrative tooling with no legitimate partner/viewer use — was
  // shown to every role. Gated to the same role set DataPanel's own
  // canEdit already uses internally, matching how Settings/Admin below
  // are gated. UX-only: the backend endpoints this page calls remain the
  // authoritative check regardless of frontend nav visibility.
  if (['admin','accountant','master_admin'].includes(user?.role)) {
    navLinks.push(['/app/data','Data',Database]);
  }
  if (user?.is_platform_admin) navLinks.push(['/app/platform','Platform',Shield]);
  // Plan & billing: the account owner / company admin on the website only (the iPhone and Android apps never show purchases).
  if (['master_admin', 'admin'].includes(user?.role) && !isNativeApp() && user?.organization && user.organization.plan !== 'internal') navLinks.push(['/app/billing','Plan & Billing',Receipt]);
  if (isAdmin(user)) {
    navLinks.push(['/app/settings','Settings',Database], ['/app/admin','User Management',Shield]);
    // User-requested: the printable Payment Voucher, plus a live-generated
    // reference sheet of real names to write on it (Projects not yet
    // finally billed, EW Buckets, Overhead/Project Categories, Security
    // Instrument types) -- restricted to master_admin/company_admin,
    // matching this exact isAdmin gate, not shown to every role like the
    // Dashboard's Handbook banner is.
    navLinks.push(['/app/voucher-tools','Voucher Tools',Receipt]);
  }
  // A Partner login only gets the screens that show its own stake.
  if (user?.role === 'partner') {
    const keep = navLinks.filter(l => PARTNER_NAV_PATHS.includes(l[0]));
    navLinks.splice(0, navLinks.length, ...keep);
  }
  // A Data Entry login only ever gets the four screens it can use.
  if (isDataEntry(user)) {
    const keep = navLinks.filter(l => DATA_ENTRY_PATHS.includes(l[0]));
    navLinks.splice(0, navLinks.length, ...keep);
  }

  const [appUpdate, recheckUpdate] = useNativeUpdate();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-subtle" style={{ background: theme==='light'?'rgba(255,255,255,0.72)':'rgba(23,20,15,0.72)' }}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to={user ? '/app/dashboard' : '/'} className="flex items-center gap-3" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#c9a84c', color: '#1a1610' }}>
            <span className="serif text-xl font-semibold">A</span>
          </div>
          <div>
            <div className="serif text-lg leading-none">Anvid Buildcon</div>
            <div className="text-[10px] tracking-widest uppercase text-muted">Financials</div>
          </div>
        </Link>
        {user && (
          <nav className="hidden md:flex flex-1 min-w-0">
            <OverflowNav navLinks={navLinks}/>
          </nav>
        )}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Mobile audit finding: this header (present on every single
              page) genuinely overflowed a narrow phone -- logo + theme
              toggle text + a permanently-shown Logout button + the menu
              toggle no longer fit side by side (measured ~58px overflow at
              320px width). The toggle now shows its label only from `sm`
              up, and Logout moves into the mobile menu below instead of
              staying in the header bar on small screens. */}
          <button onClick={toggle} className="tab" data-testid="theme-toggle">
            {theme === 'dark' ? <Sun size={14}/> : <Moon size={14}/>} <span className="hidden sm:inline">{theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
          {user ? (
            <>
              <span className="hidden sm:inline text-sm text-muted">{user.full_name?.split(' ')[0]} · <span className="chip">{user.role}</span></span>
              <button onClick={logout} className="btn-outline btn-small hidden md:inline-flex items-center gap-1" data-testid="logout-btn"><LogOut size={14}/>Logout</button>
            </>
          ) : (
            <button onClick={() => nav('/login')} className="btn-gold btn-small" data-testid="login-nav-btn">Sign in</button>
          )}
          {user && (
            <button onClick={()=>setMobileOpen(v=>!v)} className="tab md:hidden" data-testid="nav-mobile-toggle" aria-label="Menu">
              {mobileOpen ? <X size={16}/> : <Menu size={16}/>}
            </button>
          )}
        </div>
      </div>
      {user && mobileOpen && (
        <nav ref={menuRef} className="md:hidden border-t border-subtle px-4 py-3 flex flex-col gap-1" style={{ maxHeight: menuMaxH || undefined, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }} data-testid="nav-mobile-menu">
          <div className="text-sm text-muted px-3 pb-2 sm:hidden">{user.full_name?.split(' ')[0]} · <span className="chip">{user.role}</span></div>
          {navLinks.map(([to,label,Icon]) => (
            <Link key={to} to={to} onClick={()=>setMobileOpen(false)} className="text-sm px-3 py-2.5 rounded-lg hover:text-[color:var(--gold)]" data-testid={`nav-mobile-${label.toLowerCase()}`}>
              <span className="inline-flex items-center gap-2"><Icon size={15}/>{label}</span>
            </Link>
          ))}
          {isNativeApp() && window.Capacitor.getPlatform() === 'android' && (
            <button onClick={async()=>{ const u = await recheckUpdate(); setMobileOpen(false); if (!u) toast.error('Could not check for updates — check your connection'); else if (u.available) startNativeUpdate(u); else toast.success(`You have the latest version (${u.installedName})`); }} className="text-sm px-3 py-2.5 rounded-lg text-left hover:text-[color:var(--gold)]" data-testid="nav-mobile-update">
              <span className="inline-flex items-center gap-2"><Download size={15}/>Check for app update</span>
            </button>
          )}
          <button onClick={()=>{setMobileOpen(false); logout();}} className="text-sm px-3 py-2.5 rounded-lg text-left hover:text-[color:var(--gold)] md:hidden" data-testid="nav-mobile-logout">
            <span className="inline-flex items-center gap-2"><LogOut size={15}/>Logout</span>
          </button>
        </nav>
      )}
      {user && <NativeUpdateBanner upd={appUpdate}/>}
    </header>
  );
}

// ============= LANDING =============
function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen">
      <TopNav/>
      <section className="hero-grid py-24 md:py-36 px-6 fade-up">
        <div className="max-w-5xl mx-auto text-center">
          <div className="section-eyebrow mb-6">Tender Financial Management System</div>
          <h1 className="serif" style={{ fontSize: 'clamp(48px, 8vw, 88px)', lineHeight: 1.05 }}>
            Calculate Project Profit, <em>Partner Share</em> & Financial Position
          </h1>
          <p className="text-muted mt-6 text-lg max-w-2xl mx-auto">
            Multi-project, multi-partner financial control with true separation of profit, cash, capital and settlement —
            role-based access and a full audit trail on every entry.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <button onClick={() => nav('/login')} className="btn-gold" data-testid="hero-signin">Sign in</button>
            <a href="#capabilities" className="btn-outline">Explore Capabilities</a>
          </div>
        </div>
      </section>
      <section id="capabilities" className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-5">
        {[
          ['Multi-Project Ledger','Track investment, expenses, receipts and deductions across unlimited tenders.'],
          ['Flexible Profit Sharing','Equal, Variable %, Money+Work, or an Investment Bonus on top — every model transparent and reproducible.'],
          ['Cash vs Profit','Never confuse the two. Cash position and project profit are computed independently.'],
          ['Partner Current Account','Capital, loans, reimbursements, profit payments—each classified correctly.'],
          ['Audit Trail','Who changed what, when, and to what. Every write is captured.'],
          ['Financial Year Reports','Indian FY (Apr–Mar). Project P&L, Settlement, Cash Position, Receivables, and more.'],
        ].map(([t,d]) => (
          <div key={t} className="card card-dark">
            <div className="section-eyebrow mb-3">Capability</div>
            <div className="serif text-2xl mb-2">{t}</div>
            <div className="text-muted text-sm">{d}</div>
          </div>
        ))}
      </section>
      <footer className="border-t border-subtle py-10 text-center text-muted text-sm">
        © {new Date().getFullYear()} Anvid Buildcon Pvt. Ltd. · Financials Module
      </footer>
    </div>
  );
}

// A plain type="password" input hides typos as readily as the real
// password, and copy-relaying a generated one (invite/reset temporary
// passwords, both shown once) off an obscured field is exactly how a
// mistyped or misread character turns into a failed sign-in. One toggle,
// reused everywhere a password gets typed in these auth flows.
function PasswordField({ value, onChange, placeholder, testId, autoComplete, name = 'password' }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'} required minLength={6} placeholder={placeholder}
        value={value} onChange={onChange} className="input w-full" style={{ paddingRight: 40 }}
        autoComplete={autoComplete} name={name} id={testId} data-testid={testId} aria-label={placeholder}
      />
      <button
        type="button" onClick={() => setShow(v => !v)} tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
        className="text-muted" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
      >
        {show ? <EyeOff size={16}/> : <Eye size={16}/>}
      </button>
    </div>
  );
}

// ============= LOGIN =============
// Open "Create your workspace" sign-up is OFF until the product's Terms / Privacy Policy are published and approved: set
// REACT_APP_SIGNUP_ENABLED=1 at build time to turn it on. Until then accounts are created by an administrator.
const SIGNUP_ENABLED = process.env.REACT_APP_SIGNUP_ENABLED === '1';
// Saved sign-in for the phone apps. The phone's own autofill / "save password" prompt does not reliably appear inside an app's web view,
// so the app keeps the sign-in itself: encrypted in the Android Keystore / iOS Keychain and released only after the phone's
// fingerprint / face / screen-lock check. Browsers keep using their own password manager.
const SAVED_LOGIN_SERVER = 'finance.anvidbuildcon.com';
const bioPlugin = () => { try { return isNativeApp() ? (window.Capacitor.Plugins || {}).NativeBiometric || null : null; } catch { return null; } };
async function bioStatus() {   // { ok, pin } -- ok: the phone has a fingerprint / face / screen lock to guard it
  const b = bioPlugin(); if (!b) return { ok: false };
  try { const r = await b.isAvailable({ useFallback: true }); return { ok: !!r.isAvailable, pin: !r.biometryType && !!r.deviceIsSecure }; } catch { return { ok: false }; }
}
async function hasSavedLogin() { const b = bioPlugin(); if (!b) return false; try { return !!(await b.isCredentialsSaved({ server: SAVED_LOGIN_SERVER })).isSaved; } catch { return false; } }
async function saveLoginOnPhone(username, password) { const b = bioPlugin(); await b.setCredentials({ server: SAVED_LOGIN_SERVER, username, password }); }
async function forgetLoginOnPhone() { const b = bioPlugin(); if (b) { try { await b.deleteCredentials({ server: SAVED_LOGIN_SERVER }); } catch {} } }
async function readSavedLogin() {   // throws if the person cancels or fails the phone's check
  const b = bioPlugin(); const st = await bioStatus();
  await b.verifyIdentity({ reason: 'Sign in to Anvid Financials', title: 'Sign in', subtitle: 'Confirm it is you', ...(st.pin ? { allowedBiometryTypes: [7] } : {}) });
  return b.getCredentials({ server: SAVED_LOGIN_SERVER });
}
function Login() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState(() => { try { return isNativeApp() ? (localStorage.getItem('anvid_last_email') || '') : ''; } catch { return ''; } });
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedLogin, setSavedLogin] = useState(false);
  useEffect(() => { if (isNativeApp()) hasSavedLogin().then(setSavedLogin); }, []);
  const quickSignIn = async () => {
    setBusy(true);
    try {
      let creds;
      try { creds = await readSavedLogin(); } catch { return; }   // cancelled or not recognised: stay on the form
      const { error } = await supabase.auth.signInWithPassword({ email: creds.username, password: creds.password });
      if (error) {
        if (/invalid/i.test(error.message || '')) { await forgetLoginOnPhone(); setSavedLogin(false); toast.error('Your saved password is out of date (it was changed). Sign in with your new password.'); }
        else toast.error(error.message || 'Could not sign in');
        return;
      }
      toast.success('Signed in');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    // Round 20 finding: this always sent a freshly-authenticated user to
    // the generic dashboard, even if Protected redirected them here from
    // a specific deep link (a bookmarked/shared project page, etc.) --
    // silently discarding where they were actually trying to go. Send
    // them back there when we have it, matching what location.state.from
    // is only ever set to by that redirect (never user-suppliable some
    // other way, since it's plain React Router in-memory state, not a
    // URL param).
    if (user) nav(location.state?.from?.pathname ? `${location.state.from.pathname}${location.state.from.search || ''}` : '/app/dashboard', { replace: true });
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        try { if (isNativeApp()) localStorage.setItem('anvid_last_email', email); } catch {}
        if (isNativeApp() && !savedLogin) {
          let declined = false; try { declined = localStorage.getItem('anvid_no_save_login') === '1'; } catch {}
          if (!declined && (await bioStatus()).ok) {
            if (window.confirm('Save this sign-in on this phone?\n\nNext time you can sign in with your fingerprint / face / screen lock instead of typing your password. It is stored encrypted on this phone only.')) {
              try { await saveLoginOnPhone(email, password); } catch { toast.error('Could not save the sign-in on this phone'); }
            } else { try { localStorage.setItem('anvid_no_save_login', '1'); } catch {} }
          }
        }
        toast.success('Signed in');
      } else if (mode === 'signup') {
        if (!SIGNUP_ENABLED) throw new Error('Sign-up is not open yet — ask your administrator for an account.');
        if (!fullName.trim() || !orgName.trim()) throw new Error('Enter your name and your organisation name.');
        if (!agree) throw new Error('Please accept the Terms of Service and Privacy Policy.');
        if (password.length < 8) throw new Error('Choose a password of at least 8 characters.');
        const { error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: fullName.trim(), signup_org_name: orgName.trim() }, emailRedirectTo: `${window.location.origin}/login` } });
        if (error) throw error;
        toast.success('Almost there — we sent a confirmation link to your email. Click it, then sign in to open your workspace.');
        setMode('signin');
      } else {
        // Free — Supabase's own built-in auth email, not a paid SMS/OTP
        // service — but routed through our own backend rather than
        // calling supabase.auth.resetPasswordForEmail directly, so a
        // link only actually goes out for a profile an admin has
        // approved (status=active). Signing up alone creates a real
        // Supabase auth user immediately, before any approval — calling
        // Supabase's reset endpoint straight from here would happily
        // mail a link to that address regardless. Always the same
        // response either way, so this can't be used to probe which
        // emails are registered.
        await api.post('/auth/request-password-reset', { email });
        toast.success('If that email has an approved account, a reset link is on its way.');
        setMode('signin');
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <TopNav/>
      <div className="max-w-md mx-auto mt-24 px-6 fade-up">
        <div className="card card-dark">
          <div className="serif text-3xl mb-2 text-center">
            {mode === 'signin' ? 'Welcome back' : mode === 'signup' ? 'Create your workspace' : 'Reset your password'}
          </div>
          <p className="text-muted text-sm mb-8 text-center">
            {mode === 'signin'
              ? 'Sign in to access the financials workspace.'
              : mode === 'signup'
              ? 'Free for 14 days. You become the administrator of your own private workspace.'
              : "Enter your email and we'll send you a link to set a new password."}
          </p>
          <form onSubmit={submit} className="space-y-4">
            {mode === 'signup' && <>
              <input required placeholder="Your name" aria-label="Your name" value={fullName} onChange={e=>setFullName(e.target.value)} className="input w-full" data-testid="signup-name"/>
              <input required placeholder="Organisation / business name" aria-label="Organisation name" value={orgName} onChange={e=>setOrgName(e.target.value)} className="input w-full" data-testid="signup-org"/>
            </>}
            <input type="email" name="username" id="login-email" autoComplete={mode === 'forgot' ? 'email' : 'username'} autoCapitalize="none" autoCorrect="off" spellCheck={false} required placeholder="Email" aria-label="Email" value={email} onChange={e=>setEmail(e.target.value)} className="input w-full" data-testid="login-email"/>
            {mode !== 'forgot' && (
              <PasswordField placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} testId="login-password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}/>
            )}
            {mode === 'signup' && <label className="flex items-start gap-2 text-xs text-muted cursor-pointer"><input type="checkbox" className="mt-0.5" checked={agree} onChange={e=>setAgree(e.target.checked)} data-testid="signup-agree"/><span>I agree to the <a href="/terms.html" target="_blank" rel="noreferrer" className="underline">Terms of Service</a> and <a href="/privacy.html" target="_blank" rel="noreferrer" className="underline">Privacy Policy</a>.</span></label>}
            <button disabled={busy || loading} type="submit" className="btn-gold w-full" data-testid="login-submit-btn">
              {busy ? 'Please wait…' : (mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create my workspace' : 'Send reset link')}
            </button>
          </form>
          {mode === 'signin' && savedLogin && (
            <div className="mt-3 text-center">
              <button type="button" disabled={busy || loading} onClick={quickSignIn} className="btn-outline w-full" data-testid="login-saved-btn">Sign in with fingerprint / screen lock</button>
              <button type="button" onClick={async () => { await forgetLoginOnPhone(); setSavedLogin(false); try { localStorage.removeItem('anvid_no_save_login'); } catch {} toast.success('Saved sign-in removed from this phone'); }} className="mt-2 text-xs text-muted underline" data-testid="login-forget-saved">Forget the saved sign-in on this phone</button>
            </div>
          )}
          {mode === 'signin' && (
            <button onClick={() => setMode('forgot')} className="mt-4 text-xs text-muted underline w-full text-center" data-testid="login-forgot-link">
              Forgot your password?
            </button>
          )}
          {((SIGNUP_ENABLED && !isNativeApp()) || mode !== 'signin') && <button onClick={() => setMode(mode === 'signup' ? 'signin' : mode === 'forgot' ? 'signin' : 'signup')} className="mt-2 text-xs text-muted underline w-full text-center" data-testid="login-toggle-mode">
            {mode === 'signup' ? 'Already have an account? Sign in' : mode === 'forgot' ? 'Back to sign in' : "New here? Create your workspace"}
          </button>}
          {mode === 'signin' && !isNativeApp() && <a href="/get-app" className="mt-2 block text-xs text-muted underline w-full text-center" data-testid="login-get-app-link">Get the phone / tablet app</a>}
        </div>
      </div>
    </div>
  );
}

// ============= RESET PASSWORD =============
// Landing point for the link Login's "forgot password" flow emails out.
// Supabase's client parses the recovery token from the URL on load and
// establishes a short-lived recovery session on its own; this page just
// waits for that (via PASSWORD_RECOVERY) and then lets the user set a
// new password with it.
function ResetPassword() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { if (data?.session) setReady(true); });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // Also clears must_change_password, in case this login had a
      // temporary/admin-reset password and used this flow instead of the
      // forced change-password screen to set their own — either path
      // should count. Best-effort: the password itself is already
      // changed regardless of whether this call succeeds.
      try { await api.post('/profiles/me/mark-password-changed'); } catch {}
      await refresh();
      toast.success('Password updated');
      nav('/app/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Could not update password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <TopNav/>
      <div className="max-w-md mx-auto mt-24 px-6 fade-up">
        <div className="card card-dark">
          <div className="serif text-3xl mb-2 text-center">Set a new password</div>
          <p className="text-muted text-sm mb-8 text-center">
            {ready ? 'Choose a new password for your account.' : 'Verifying your reset link…'}
          </p>
          {ready ? (
            <form onSubmit={submit} className="space-y-4">
              <PasswordField placeholder="New password" value={password} onChange={e=>setPassword(e.target.value)} testId="reset-password" autoComplete="new-password"/>
              <PasswordField placeholder="Confirm new password" value={confirm} onChange={e=>setConfirm(e.target.value)} testId="reset-password-confirm" autoComplete="new-password"/>
              <button disabled={busy} type="submit" className="btn-gold w-full" data-testid="reset-password-submit">
                {busy ? 'Please wait…' : 'Update password'}
              </button>
            </form>
          ) : (
            <p className="text-xs text-muted text-center">If this doesn't move on in a moment, the link may have expired — request a new one from the sign-in page.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============= FORCE CHANGE PASSWORD =============
// Where Protected sends a login whose profiles.must_change_password is
// still true — a brand-new invite's temporary password, or one an admin
// just reset, neither ever chosen by the user themselves. Deliberately
// does NOT use TopNav (which shows the full app nav once a user is
// present) — just a bare logo and Logout, so there's no way to wander
// into the app past this screen other than actually setting a password.
function ForceChangePassword() {
  const { user, loading, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/login', { replace: true }); return; }
    if (!user.must_change_password) nav('/app/dashboard', { replace: true });
  }, [user, loading]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      await api.post('/profiles/me/mark-password-changed');
      await refresh();
      toast.success('Password set');
      nav('/app/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || 'Could not set password');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) return <Loading full/>;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-subtle">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#c9a84c', color: '#1a1610' }}>
              <span className="serif text-xl font-semibold">A</span>
            </div>
            <div>
              <div className="serif text-lg leading-none">Anvid Buildcon</div>
              <div className="text-[10px] tracking-widest uppercase text-muted">Financials</div>
            </div>
          </div>
          <button onClick={logout} className="btn-outline btn-small inline-flex items-center gap-1"><LogOut size={14}/>Logout</button>
        </div>
      </header>
      <div className="max-w-md mx-auto mt-24 px-6 fade-up">
        <div className="card card-dark">
          <div className="serif text-3xl mb-2 text-center">Set your password</div>
          <p className="text-muted text-sm mb-8 text-center">You're signing in with a temporary password — choose your own before continuing.</p>
          <form onSubmit={submit} className="space-y-4">
            <PasswordField placeholder="New password" value={password} onChange={e=>setPassword(e.target.value)} testId="force-change-password" autoComplete="new-password"/>
            <PasswordField placeholder="Confirm new password" value={confirm} onChange={e=>setConfirm(e.target.value)} testId="force-change-password-confirm" autoComplete="new-password"/>
            <button disabled={busy} type="submit" className="btn-gold w-full" data-testid="force-change-password-submit">
              {busy ? 'Please wait…' : 'Set password & continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============= LOADING =============
// Animation only, no words: five gold bars rising and falling like a chart.
// The text label is screen-reader-only so the loader is still announced.
function LoadingMark({ size = 28 }) {
  return (
    <span className="app-loader" role="status" aria-live="polite" style={{ '--loader-size': `${size}px` }} data-testid="app-loader">
      <i/><i/><i/><i/><i/>
      <span className="sr-only">Loading</span>
    </span>
  );
}
function Loading({ full }) {
  return (
    <div className={"flex items-center justify-center text-muted " + (full ? "min-h-screen" : "p-12")}>
      <LoadingMark/>
    </div>
  );
}
function LoadingRow({ colSpan }) {
  return <tr><td colSpan={colSpan} className="text-center text-muted py-8"><LoadingMark size={22}/></td></tr>;
}

// ============= PROTECTED =============
function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading full/>;
  // Round 20 finding: this used to redirect with no state at all, so a
  // deep link (e.g. a bookmarked project page) visited while logged out
  // was silently discarded -- Login always sent a freshly-authenticated
  // user to the generic dashboard instead of back to what they wanted.
  if (!user) return <Navigate to="/login" replace state={{ from: location }}/>;
  // A brand-new invite or an admin-triggered reset leaves this true until
  // the login sets their own password — no part of the real app is
  // reachable until then, however they got a valid session (normal
  // sign-in, or the recovery-link flow landing them here already signed in).
  if (user.must_change_password) return <Navigate to="/change-password" replace/>;
  return children;
}

// ============= UI PRIMITIVES =============
// Round 21 finding: this had zero focus management -- opening never
// moved focus in (a keyboard user tabbing had to walk through the rest
// of the page first, since the portal appends at the end of
// document.body), Tab wasn't trapped (background content stayed
// reachable while "modal"), Escape didn't close it, and closing never
// restored focus to whatever triggered it. Systemic since this backs
// essentially every Add/Edit form in the app. The tap-outside pattern
// already existed elsewhere in the file (the nav "More" dropdown, the
// company-access picker) -- this brings Modal up to the same bar plus
// the keyboard-specific pieces neither of those needed.
function Modal({ open, onClose, title, children, maxW=720 }) {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const closeBtnRef = useRef(null);
  // Every call site passes a fresh inline arrow for onClose (onClose={()=>
  // setOpen(false)}), so it gets a new identity on every parent re-render
  // -- e.g. every keystroke in a field. Reading it via a ref (kept current
  // every render, but NOT a dependency below) means typing in the modal
  // can't retrigger the effect and yank focus back to the first field.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const FOCUSABLE = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

  useLayoutEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement;
    // Round 22 finding: the header (with its always-present Close button)
    // renders before {children} in the DOM, so a plain querySelector(FOCUSABLE)
    // always matched Close first -- every modal's first Tab press was consumed
    // skipping past it, and a reflexive Enter/Space right after opening a
    // modal dismissed it instead of reaching the form. Skip the close button
    // specifically so autofocus lands on the first real field, falling back
    // to the container (as before) only when children has nothing focusable.
    const first = Array.from(modalRef.current?.querySelectorAll(FOCUSABLE) || []).find(el => el !== closeBtnRef.current);
    (first || modalRef.current)?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusables = Array.from(modalRef.current.querySelectorAll(FOCUSABLE)).filter(el => el.offsetParent !== null);
      if (!focusables.length) return;
      const firstEl = focusables[0], lastEl = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div ref={modalRef} className="modal" style={{maxWidth: maxW}} onClick={e => e.stopPropagation()} data-testid="modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <div className="modal-header flex items-center justify-between mb-6">
          <h3 className="modal-title serif text-2xl">{title}</h3>
          <button ref={closeBtnRef} onClick={onClose} className="tab btn-small" aria-label="Close" data-testid="modal-close"><X size={14}/></button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  // A trailing " *" on any label (however it was built — static string,
  // template literal, or the import-mapper's dynamic required flag) marks
  // a mandatory field — rendered as a red asterisk instead of plain text,
  // consistently, from this one place.
  const isRequired = typeof label === 'string' && label.endsWith(' *');
  const base = isRequired ? label.slice(0, -2) : label;
  return (
    <label className="block">
      <div className="text-xs text-muted mb-1.5 uppercase tracking-wider">{base}{isRequired && <span style={{color:'#e66767'}}> *</span>}</div>
      {children}
    </label>
  );
}

// The companies this login can see — GET /companies is scoped server-side
// to effective_company_ids (or unfiltered for all_companies_access), so
// this is exactly the picker list for "which company does this record
// belong to." Fetched once and shared by every screen that needs it.
let _companiesCache = null;
function useMyCompanies() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState(_companiesCache || []);
  useEffect(() => {
    let mounted = true;
    api.get('/companies').then(r => { _companiesCache = r.data; if (mounted) setCompanies(r.data); });
    return () => { mounted = false; };
  }, []);
  return { companies, needsPicker: companies.length > 1 || !!user?.all_companies_access };
}

// Shown in an Add/Edit form only when the login's scope includes more than
// one company — with exactly one, the backend defaults it automatically
// and this renders nothing, so single-company logins see zero change.
// clear: field keys to null out when the company changes — anything
// downstream (a project, master, or partner picker scoped to this
// company) would otherwise keep a stale selection from the old company.
function CompanyField({ form, setForm, companies, needsPicker, clear = [] }) {
  if (!needsPicker) return null;
  return (
    <Field label="Company">
      <select className="input" value={form.company_id || ''} onChange={e => setForm({ ...form, company_id: e.target.value, ...Object.fromEntries(clear.map(k => [k, null])) })} data-testid="field-company">
        <option value="">— select —</option>
        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </Field>
  );
}

const AUDIT_STATUS_STYLE = {
  pending_review: { color: '#7dd3fc', borderColor: 'rgba(125,211,252,0.35)' },
  matched: { color: '#86efac', borderColor: 'rgba(134,239,172,0.35)' },
  discrepancy: { color: '#fb923c', borderColor: 'rgba(251,146,60,0.4)' },
  final: { color: '#c9a84c', borderColor: 'rgba(201,168,76,0.5)' },
};
function AuditStatusChip({ status }) {
  const s = AUDIT_STATUS_STYLE[status] || AUDIT_STATUS_STYLE.pending_review;
  return <span className="badge" style={s}>{(status || 'pending_review').replace('_',' ')}</span>;
}
// Mirrors server.py's CALC_ELIGIBLE_AUDIT_STATUSES exactly — the set the
// profit engine treats as "confirmed" (excluded: pending_review,
// discrepancy). Any frontend total meant to agree with the backend's own
// total_receipts/total_expenses must filter through this, not sum
// everything regardless of status.
const CALC_ELIGIBLE_AUDIT_STATUSES = new Set(['matched', 'final']);

// Original 7 statuses have hand-picked CSS colours (see index.css); a
// custom status added via Settings > Masters (e.g. "Tender Applied")
// won't match any of those classes, so it falls back to a neutral chip
// instead of an unstyled badge.
const KNOWN_PROJECT_STATUSES = new Set(['planning','running','completed','financial_review','profit_approved','settled','closed']);
// A project's profit distribution being approved (project.status ===
// 'profit_approved') is the one state that should stop casual editing —
// see isProjectLocked() below, used everywhere an Edit action is offered.
function ProjectStatusBadge({ status }) {
  if (KNOWN_PROJECT_STATUSES.has(status)) {
    return <span className={"badge status-"+status}>{status==='profit_approved' && <Lock size={11} className="inline mr-1 -mt-0.5"/>}{status.replace('_',' ')}</span>;
  }
  return <span className="chip">{status}</span>;
}
// Displayed beside a project's status wherever profit is shown — a project
// running at a loss should never look identical to one running at a
// healthy margin just because both say "running".
// Shared by every plain (non-badge) Project Profit figure -- same
// green/gold/red rule as ProfitLossBadge, as a Tailwind class instead of
// a badge, for the plain mono table cells that show this figure.
const profitColorClass = (profit) => {
  const p = Number(profit || 0);
  if (Math.abs(p) < 0.5) return 'text-[color:var(--gold)]';
  return p > 0 ? 'text-emerald-400' : 'text-red-400';
};
function ProfitLossBadge({ profit }) {
  if (profit == null) return null;
  const p = Number(profit);
  // User-requested: PROFIT should read as an actual positive signal
  // (green) and LOSS as a warning (red) -- this always showed gold
  // regardless of the real number before, which read as neutral even
  // for a genuinely large profit. Gold is now reserved specifically for
  // exact break-even (no profit, no loss) -- a real, different state,
  // not just "profit that happens to round to zero."
  if (Math.abs(p) < 0.5) {
    return <span className="badge" style={{color:'var(--gold)', borderColor:'rgba(201,168,76,0.4)'}}>NO PROFIT / NO LOSS</span>;
  }
  const loss = p < 0;
  return <span className={"badge " + (loss ? "status-financial_review" : "status-running")} style={loss ? {color:'var(--accent-red)', borderColor:'rgba(230,103,103,0.4)'} : {color:'var(--accent-green)', borderColor:'rgba(95,181,131,0.4)'}}>{loss ? 'LOSS' : 'PROFIT'}</span>;
}
// True once a project's profit has been approved & signed off — from that
// point, its financial rows (expenses, receipts, investments, deductions,
// partner transactions, the project record itself) feed a number partners
// have already been told is final, so editing needs an explicit "yes,
// really" instead of the normal single-click pencil icon.
// Matches the backend's _LOCKED_PROJECT_STATUSES exactly (server.py) —
// once profit_approved/settled/closed, expenses/receipts/investments/
// deductions/roles/partnership-model edits are actually rejected there
// (423), not just risky. This dialog now mostly exists so the user gets
// a plain-language reason before hitting that error, rather than the
// raw backend message with no context.
const isProjectLocked = (project) => ['profit_approved','settled','closed'].includes(project?.status);
const LOCK_EDIT_CONFIRM = 'This project\'s profit has been approved and signed off. Financial edits are blocked while it\'s locked — an admin can reopen it (with a reason) from the Settlement tab first if a correction is genuinely needed. Try anyway?';
const confirmUnlockedEdit = (project) => !isProjectLocked(project) || window.confirm(LOCK_EDIT_CONFIRM);
// Matches the backend's _LOCK_PROGRESSION exactly (server.py) — the only
// moves POST /projects/{id}/advance-status allows once a project is
// locked. Anything else (including moving backward) needs Reopen instead.
const LOCK_PROGRESSION = ['profit_approved','settled','closed'];
const lockAdvanceOptions = (project) => LOCK_PROGRESSION.slice(LOCK_PROGRESSION.indexOf(project?.status) + 1);

// tone identifies what KIND of figure this is (money in, money out, headline
// profit, ...) so it reads at a glance instead of every KPI tile looking the
// same shade of gold. Colors are the --accent-* set validated colorblind-safe
// in index.css; icon is redundant secondary encoding for the same reason.
const KPI_TONES = {
  gold:   { color: 'var(--gold)',         soft: 'rgba(201,168,76,0.12)',  Icon: Award },
  blue:   { color: 'var(--accent-blue)',  soft: 'rgba(57,135,229,0.14)',  Icon: Landmark },
  red:    { color: 'var(--accent-red)',   soft: 'rgba(230,103,103,0.14)', Icon: TrendingDown },
  green:  { color: 'var(--accent-green)', soft: 'rgba(25,158,112,0.14)',  Icon: TrendingUp },
  amber:  { color: 'var(--accent-amber)', soft: 'rgba(201,133,0,0.14)',   Icon: Clock3 },
};
function KpiCard({ label, value, hint, gold, tone, icon: IconOverride }) {
  const t = KPI_TONES[tone || (gold ? 'gold' : '')];
  const Icon = IconOverride || t?.Icon;
  return (
    <div className="card card-dark kpi-card" style={t ? { '--kpi-accent': t.color, '--kpi-accent-soft': t.soft } : undefined}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-muted">{label}</div>
        {Icon && <div className="kpi-icon"><Icon size={16}/></div>}
      </div>
      <div className="serif text-3xl mono kpi-value" style={t ? { color: t.color } : undefined}>{value}</div>
      {hint && <div className="text-xs text-muted mt-1">{hint}</div>}
    </div>
  );
}

// Real-usage-requested: descriptive metadata fields (tender no., client,
// department, work order, financial year, and the project's own status-axis
// values) rendered as plain grey text throughout the app -- turned into
// small colorful pill chips instead, reusing the same --accent-* palette
// KPI_TONES already validated colorblind-safe, so the app doesn't grow a
// second competing color language.
const META_CHIP_TONES = {
  gold:  { color: 'var(--gold)',         soft: 'rgba(201,168,76,0.12)',  border: 'rgba(201,168,76,0.4)',  hover: 'rgba(201,168,76,0.22)' },
  blue:  { color: 'var(--accent-blue)',  soft: 'rgba(57,135,229,0.14)',  border: 'rgba(57,135,229,0.4)',  hover: 'rgba(57,135,229,0.24)' },
  green: { color: 'var(--accent-green)', soft: 'rgba(25,158,112,0.14)',  border: 'rgba(25,158,112,0.4)',  hover: 'rgba(25,158,112,0.24)' },
  amber: { color: 'var(--accent-amber)', soft: 'rgba(201,133,0,0.14)',   border: 'rgba(201,133,0,0.4)',   hover: 'rgba(201,133,0,0.24)' },
  violet: { color: 'var(--accent-violet)', soft: 'rgba(139,127,209,0.14)', border: 'rgba(139,127,209,0.4)', hover: 'rgba(139,127,209,0.24)' },
};
const META_CHIP_TONE_CYCLE = ['gold', 'blue', 'green', 'amber'];
function MetaChip({ icon: Icon, label, value, tone = 'gold' }) {
  if (!value) return null;
  const t = META_CHIP_TONES[tone] || META_CHIP_TONES.gold;
  return (
    <span className="meta-chip" style={{ '--chip-color': t.color, '--chip-soft': t.soft, '--chip-border': t.border, '--chip-hover': t.hover }} title={label || undefined}>
      {Icon && <Icon size={12}/>}
      <span>{value}</span>
    </span>
  );
}

// Real-usage-requested: sortable column headers "wherever any list of
// data appears" -- one small, reusable pair (a hook + a header cell)
// instead of hand-rolling sort logic per table, so any table in the app
// can adopt it with a few lines. Handles strings (locale/numeric-aware,
// so "2" sorts before "10") and numbers/dates alike; nulls always sort
// last regardless of direction, so an empty field never jumps to the top
// on a descending sort.
// Round 33 finding: this helper's own default "today" used
// new Date().toISOString(), which is the server/browser's UTC calendar
// date -- exactly the bug class _today_ist() exists to prevent on the
// backend (IST runs a full day ahead of UTC for the first 5.5 hours of
// each IST day). Since this helper exists specifically to mirror the
// backend's _resolve_active_profit_rule eligibility check, disagreeing
// with it during that same 5.5-hour window every day defeats the whole
// point of the fix. A fixed +5:30 shift is exact for IST (no DST).
function todayIST() {
  return new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// Round 32 finding: the Reserve %/Interest Rate "quick editor" panels
// (loadReserve/loadRateRule below) picked the company-wide profit_sharing_
// rules row via a plain .find(status==='active'), with NO date-eligibility
// check at all -- unlike the backend's real resolver (_resolve_active_
// profit_rule), which only considers a rule whose effective_from/
// effective_to window actually covers today. Since the rules list is
// ordered by effective_from desc, a future-dated rule (fully supported via
// the free-form Profit Sharing Rules settings screen) sorted first and
// got silently shown/edited here instead of the one that's actually live
// right now. Mirrors the backend's exact eligibility check.
function isProfitRuleCurrentlyActive(r, today = todayIST()) {
  if (r.status !== 'active') return false;
  if (r.effective_from && r.effective_from > today) return false;
  if (r.effective_to && r.effective_to < today) return false;
  return true;
}

function useSortableRows(rows, initialKey = null, initialDir = 'asc') {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if ((av === null || av === undefined || av === '') && (bv === null || bv === undefined || bv === '')) return 0;
      if (av === null || av === undefined || av === '') return 1;
      if (bv === null || bv === undefined || bv === '') return -1;
      let cmp;
      if (typeof av === 'string' || typeof bv === 'string') cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      else cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);
  return { sorted, sortKey, sortDir, toggleSort };
}

function SortTh({ label, sortKeyName, sortKey, sortDir, onSort, right, className }) {
  const active = sortKey === sortKeyName;
  return (
    <th className={(className||'') + ' cursor-pointer select-none'} style={right?{textAlign:'right'}:undefined} onClick={()=>onSort(sortKeyName)} title={`Sort by ${label}`}>
      <span className="inline-flex items-center gap-1" style={right?{justifyContent:'flex-end', width:'100%'}:undefined}>
        {label}
        {active ? (sortDir==='asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:0.25}}/>}
      </span>
    </th>
  );
}

// Real-usage finding: flags could be configured in Settings but the ONLY
// place to actually attach one was the Project Overview tab (this
// component, formerly ProjectFlagsBar) -- Bills, Expenses, and Receipts
// (the other places the user wants "Mark attention" on) had no attachment
// path at all, and toggling worked via clicking a colored pill with no
// explicit "ticked" affordance. Generalized to any entity type (backed by
// the equally generalized /entity-flags/* endpoints) and switched to real
// checkboxes -- each independently tickable, so multiple flags apply
// naturally -- with the flag's own description shown as a tooltip and,
// for whichever flags are actually active, spelled out inline below so
// "what does this mean" never requires hovering to find out.
// List-view companion to EntityFlagsBar: one bulk fetch for every row's
// flags at once (never N+1), so a "Flagged (N)" filter + per-row badges
// can sit above/inside any list the same way "Needs Follow-up" already
// does for Projects.
function useEntityFlagsMap(entityType, ids) {
  const [map, setMap] = useState({});
  const idsKey = ids.join(',');
  const reload = async () => {
    if (!ids.length) { setMap({}); return; }
    try { setMap((await api.post(`/entity-flags/${entityType}/bulk`, { entity_ids: ids })).data || {}); }
    catch { setMap({}); }
  };
  useEffect(() => { reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, idsKey]);
  return [map, reload];
}
// Flags used to be bare 7px colour dots (name only in a hover tooltip). They now spell the name out:
// a coloured dot plus the flag's name. followUp adds the Project "Needs follow-up" marker as a chip too.
const FOLLOW_UP_COLOR = '#e66767';
function FlagDots({ flags, followUp, followUpTestId }) {
  const list = flags || [];
  if (!list.length && !followUp) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {followUp && <span className="flag-chip" style={{ '--flag-color': FOLLOW_UP_COLOR }} title="Needs follow-up" data-testid={followUpTestId}><i/>Needs follow-up</span>}
      {list.map(f => <span key={f.id} className="flag-chip" style={{ '--flag-color': f.color || 'var(--gold)' }} title={`${f.name}${f.description ? ': '+f.description : ''}`}><i/>{f.name}</span>)}
    </div>
  );
}
function entityFlagList(entityType, entityId) {
  return api.get(`/entity-flags/${entityType}/${entityId}`);
}
function entityFlagAdd(entityType, entityId, flagId) {
  return api.post(`/entity-flags/${entityType}/${entityId}/${flagId}`);
}
function entityFlagRemove(entityType, entityId, flagId) {
  return api.delete(`/entity-flags/${entityType}/${entityId}/${flagId}`);
}
function EntityFlagsBar({ entityType, entityId, canEdit }) {
  const [all, setAll] = useState([]);
  const [active, setActive] = useState([]);
  const load = async () => {
    const [a, p] = await Promise.all([api.get('/flags'), entityFlagList(entityType, entityId)]);
    setAll(a.data.filter(f=>f.status==='active')); setActive(p.data);
  };
  useEffect(() => { load(); }, [entityType, entityId]);
  const toggle = async (flagId, on) => {
    try {
      if (on) await entityFlagRemove(entityType, entityId, flagId);
      else await entityFlagAdd(entityType, entityId, flagId);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  if (!all.length) return null;
  const activeIds = active.map(f=>f.id);
  return (
    <div className="mb-1">
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {all.map(f => {
          const on = activeIds.includes(f.id);
          return (
            <label key={f.id} title={f.description || undefined}
              className="inline-flex items-center gap-1.5 text-xs" style={{opacity: canEdit ? 1 : (on?1:0.45), cursor: canEdit?'pointer':'default'}}>
              <input type="checkbox" checked={on} disabled={!canEdit} onChange={()=>toggle(f.id, on)} style={{accentColor: f.color}}/>
              <span style={{width:7,height:7,borderRadius:'50%',background:f.color,display:'inline-block'}}/>
              <span style={{color: on ? f.color : undefined}}>{f.name}</span>
            </label>
          );
        })}
      </div>
      {active.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-1.5">
          {active.map(f => (
            <span key={f.id} className="chip" style={{background: f.color+'20', color: f.color}}>
              <span style={{width:6,height:6,borderRadius:'50%',background:f.color,display:'inline-block'}}/>
              {f.name}{f.description ? `: ${f.description}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ============= DASHBOARD =============
// Same static page for every role (public asset, bundled with the
// frontend build) — a role-by-role guide to using the app. Surfaced
// only here on the Dashboard, deliberately not as its own nav item.
// User-requested: the Payment Voucher used to live here too (as a second
// row on this same banner), but reads better as an admin-only tool than
// a Dashboard-for-everyone banner -- moved to Voucher Tools (master_admin/
// company_admin only, see isAdmin gate in TopNav) instead.
function HandbookBanner() {
  return (
    <div className="card card-dark mb-6 flex items-center justify-between gap-4 flex-wrap" style={{padding:'14px 20px'}}>
      <div className="flex items-center gap-3 text-sm">
        <BookOpen size={18} className="text-[color:var(--gold)] flex-shrink-0"/>
        <span>New here, or just need a refresher? The <b>User Handbook</b> covers what your role can do and how the numbers work.</span>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <a href="/handbook.html" target="_blank" rel="noopener noreferrer" className="tab btn-small inline-flex items-center gap-1"><ExternalLink size={12}/>View</a>
        <a href="/handbook.html" download="Anvid-Buildcon-Financials-Handbook.html" className="tab btn-small inline-flex items-center gap-1"><Download size={12}/>Download</a>
      </div>
    </div>
  );
}

// Step 15: role-based dashboard. Master Admin gets a group overview
// layered on top of the company view; Company Admin/Accountant keep
// the existing company-scoped view (already correct for "Company
// overview" / "Operational financial view"); Partner gets a narrowed
// view of only their own stake, not full project financials.
function Dashboard() {
  const { user } = useAuth();
  return isDataEntry(user) ? <DataEntryHome/> : <FullDashboard/>;
}
function FullDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [fy, setFy] = useState('');
  const statusF = useMultiFilter('Dashboard.status');
  const [allProjects, setAllProjects] = useState([]);
  const masters = useMasters();
  const statusOptions = (masters.project_status?.map(m=>m.name)) || DEFAULT_PROJECT_STATUSES;
  const isPartner = user.role === 'partner';
  const { companies, needsPicker } = useMyCompanies();
  // User-requested: Projects Snapshot can run long once a company has many
  // projects -- same collapse affordance as Import History/Company Fund
  // Ledger elsewhere, defaulting to EXPANDED (unlike those two) since this
  // is the dashboard's own main content, not a secondary panel above
  // something else.
  const [snapshotCollapsed, setSnapshotCollapsed] = useState(false);
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  const [showForecast, setShowForecast] = useState(false);
  // Independent of the fy/status filters below — an unfiltered list, just
  // to populate the FY dropdown with years that actually exist, rather
  // than only whichever year happens to already be selected.
  useEffect(() => { api.get('/projects').then(r => setAllProjects(r.data)).catch(() => {}); }, []);

  const load = async () => {
    const params = {};
    if (fy) params.financial_year = fy;
    // Status is multi-select: several are sent comma-separated; "none ticked" asks for a status nothing has (so the totals show zero, like the other pages).
    if (!statusF.all) params.status = statusF.ids.length ? statusF.ids.join(',') : '__none__';
    try {
      const r = await api.get('/dashboard', { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load dashboard'); }
  };
  useEffect(() => { load(); }, [fy, statusF.all, statusF.ids.join(',')]);
  useLiveRefresh(() => load());

  if (!data) return <Loading/>;
  const t = data.totals;

  if (isPartner) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
        <div className="mb-8">
          <div className="section-eyebrow mb-2">Your Stake</div>
          <h1 className="serif text-4xl">My Dashboard</h1>
        </div>
        <HandbookBanner/>
        {/* User-flagged: the table below already has a "Paid to Me" column,
            and the backend already computes its matching total
            (totals.my_paid, server.py) -- it just never got its own KPI
            tile here. grid-cols-5 (was 4) to fit it in cleanly. */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <KpiCard label="My Projects" value={t.total_projects} icon={FolderKanban}/>
          <KpiCard label="My Investment" value={fmtINR(t.my_investment)} tone="blue"/>
          <KpiCard label="My Profit" value={fmtINR(t.my_profit)} tone="gold" hint="Live estimate across all projects, running ones included. Partner Settlement counts approved projects only."/>
          <KpiCard label="Paid to Me" value={fmtINR(t.my_paid)} tone="green"/>
          <KpiCard label="Balance Due to Me" value={fmtINR(t.my_balance)} tone="amber" hint="Profit share only, not money you spent. See Partner Settlement for the full position."/>
        </div>
        <div className="card card-dark">
          <div className="serif text-2xl mb-4">My Projects</div>
          <div className="overflow-x-auto">
          <table className="tbl">
            <thead><tr><th>Project</th><th>My Investment</th><th>My Profit</th><th>Paid to Me</th><th>Balance</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {data.projects.length === 0 && <tr><td colSpan="7" className="text-muted text-center py-8">No projects yet.</td></tr>}
              {data.projects.map(({project, my}) => (
                <tr key={project.id} className="hover:bg-white/5">
                  <td><div className="serif text-base">{project.project_name}</div><div className="text-xs text-muted">{project.financial_year}</div></td>
                  <td className="mono">{fmtINR(my?.investment||0)}</td>
                  <td className="mono text-[color:var(--gold)]">{fmtINR(my?.total_profit||0)}</td>
                  <td className="mono">{fmtINR(my?.profit_paid||0)}</td>
                  <td className={"mono " + ((my?.balance||0) >= 0 ? "text-[color:var(--gold)]" : "text-red-400")}>{fmtINR(my?.balance||0)}</td>
                  <td><span className={"badge status-"+project.status}>{project.status.replace('_',' ')}</span></td>
                  <td className="text-right"><Link to={`/app/projects/${project.id}`} className="tab btn-small inline-flex items-center gap-1 whitespace-nowrap">Open <ChevronRight size={12}/></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        <PartnerGrowthChart projects={data.projects}/>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="section-eyebrow mb-2">{user.role === 'master_admin' ? 'Company Overview' : (user.role === 'accountant' ? 'Operational View' : 'Overview')}</div>
          <h1 className="serif text-4xl">Financial Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <MultiFilterPicker filter={statusF} items={statusOptions.map(st => ({ id: st, name: st.replace(/_/g,' ') }))} allLabel="All Statuses" width={160} testId="filter-status"/>
          <FySelect value={fy} onChange={setFy} years={distinctFYs(allProjects.map(p=>p.financial_year))} style={{width:160}} testId="filter-fy"/>
        </div>
      </div>
      <HandbookBanner/>
      <GettingStartedCard/>
      {['admin','accountant','master_admin'].includes(user.role) && <OpenPartnerReportsCard/>}
      {user.role === 'master_admin' && <GroupOverviewPanel fy={fy} statusFilterActive={!statusF.all}/>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KpiCard label="Total Projects" value={t.total_projects} icon={FolderKanban}/>
        <KpiCard label="Total Investment" value={fmtINR(t.investment)} tone="blue"/>
        <KpiCard label="Total Billed" value={fmtINR(t.billed)} tone="gold"/>
        <KpiCard label="Total Expenses" value={fmtINR(t.expenses)} tone="red" hint={t.expenses_pending_review>0 ? `+ ${fmtINR(t.expenses_pending_review)} pending review` : null}/>
        <KpiCard label="Total Receipts" value={fmtINR(t.receipts)} tone="green" hint={t.receipts_pending_review>0 ? `+ ${fmtINR(t.receipts_pending_review)} pending review` : null}/>
        <KpiCard label="Receivables" value={fmtINR(t.receivables)} tone="amber"/>
        <KpiCard label="Total Profit" value={fmtINR(t.profit)} tone="gold"/>
        <KpiCard label="Partner Payable" value={fmtINR(t.partner_payable)} tone="amber" hint="Billed basis — includes profit not yet collected"/>
        <KpiCard label="Cash-backed Profit" value={fmtINR(t.cash_backed_profit)} tone="green" hint="Partners' share backed by money actually received (costs recovered first)"/>
        <KpiCard label="Awaiting Collection" value={fmtINR(t.uncollected_profit)} tone="amber" hint="Profit billed but not yet paid by clients"/>
        <KpiCard label="Payable Now" value={fmtINR(t.payable_now)} tone="gold" hint="Cash-backed profit not yet paid out (loss-making projects excluded)"/>
        <KpiCard label="Paid Ahead of Cash" value={fmtINR(t.paid_ahead)} tone={t.paid_ahead > 0.5 ? 'red' : undefined} hint="Paid to partners beyond what clients have paid"/>
      </div>
      <div className="card card-dark">
        <button onClick={()=>setSnapshotCollapsed(v=>!v)} className="flex items-center justify-between w-full text-left" data-testid="dashboard-snapshot-toggle">
          <div className="flex items-center gap-2">
            <div className="serif text-2xl">Projects Snapshot</div>
            <span className="text-xs text-muted">({data.projects.length} project{data.projects.length===1?'':'s'})</span>
          </div>
          {snapshotCollapsed ? <ChevronRight size={18}/> : <ChevronDown size={18}/>}
        </button>
        {!snapshotCollapsed && (
        <div className="overflow-x-auto mt-4">
        <table className="tbl">
          <thead><tr>
            <th>Project</th>{needsPicker && <th>Company</th>}<th>Contract</th><th>Expenses</th><th>Receipts</th>
            <th>Receivable</th><th>Profit</th><th title="Receipts − costs: profit actually in cash">Cash profit</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {data.projects.length === 0 && <tr><td colSpan="9" className="text-muted text-center py-8">No projects yet.</td></tr>}
            {data.projects.map(({project, totals, cash_backing}) => (
              <tr key={project.id} className="hover:bg-white/5" data-testid={`row-project-${project.id}`}>
                <td>
                  <div className="serif text-base">{project.project_name}</div>
                  <div className="text-xs text-muted">{project.tender_number || '—'} · {project.financial_year}</div>
                </td>
                {needsPicker && <td className="text-xs">{companyName(project.company_id)}</td>}
                <td className="mono">{fmtINR(totals.net_contract_value)}</td>
                <td className="mono">{fmtINR(totals.total_expenses)}</td>
                <td className="mono">{fmtINR(totals.total_receipts)}</td>
                <td className="mono">{fmtINR(totals.outstanding_receivable)}</td>
                <td className={"mono " + profitColorClass(totals.project_profit)}>{fmtINR(totals.project_profit)}</td>
                <td className={"mono " + (cash_backing ? profitColorClass(cash_backing.cash_realised_profit) : '')}>{cash_backing ? fmtINR(cash_backing.cash_realised_profit) : '—'}</td>
                <td className="whitespace-nowrap"><ProjectStatusBadge status={project.status}/> <ProfitLossBadge profit={totals.project_profit}/></td>
                <td className="text-right"><Link to={`/app/projects/${project.id}`} className="tab btn-small inline-flex items-center gap-1 whitespace-nowrap">Open <ChevronRight size={12}/></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        )}
      </div>
      <DashboardCharts projects={data.projects}/>
      <div className="card card-dark mt-8">
        <button onClick={()=>setShowForecast(v=>!v)} className="flex items-center justify-between w-full text-left" data-testid="dashboard-forecast-toggle">
          <div className="flex items-center gap-2">
            <Calculator size={18} className="text-[color:var(--gold)]"/>
            <div className="serif text-2xl">Forecast</div>
            <span className="text-xs text-muted">— simulate a hypothetical project's profit split, not actual data</span>
          </div>
          {showForecast ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
        </button>
        {showForecast && <div className="mt-4"><Forecast embedded/></div>}
      </div>
    </div>
  );
}

// User-requested: nothing in this app pushed an admin/accountant/
// master_admin toward a partner's flagged report -- they'd only ever see
// one by opening that specific partner's own Profit Distribution tab.
// This is the one place that gap closes: every OPEN partner_reports row
// the caller can see (GET /partner-reports/open, same company-scoping as
// list_partners), in one card, each linking straight to that partner's
// page with the Profit Distribution tab already selected.
//
// User-reported real gap: an earlier version of this returned null
// entirely whenever there were zero open reports -- with no partner
// having filed one yet (true for a brand-new feature), that made the
// card genuinely invisible, indistinguishable from "this isn't built" or
// "this is broken." Always renders now, with an explicit empty state,
// same "show the empty state, don't hide the section" convention every
// other list/table in this app already uses.
function OpenPartnerReportsCard() {
  const [reports, setReports] = useState(null);
  useEffect(() => { api.get('/partner-reports/open').then(r=>setReports(r.data)).catch(()=>setReports([])); }, []);
  if (reports === null) return null;
  return (
    <div className="card card-dark mb-8" data-testid="open-partner-reports" style={{borderColor:'rgba(217,164,65,0.35)'}}>
      <div className="flex items-center gap-2 mb-1">
        <Flag size={16} style={{color:'var(--accent-amber)'}}/>
        <div className="serif text-xl text-[color:var(--gold)]">Open Partner Reports</div>
        {reports.length > 0 && <span className="chip" style={{background:'rgba(217,164,65,0.14)', color:'var(--accent-amber)'}}>{reports.length}</span>}
      </div>
      <div className="text-xs text-muted mb-4">A reopen request, a suspected miscalculation, or another comment a partner has flagged — not yet resolved.</div>
      {reports.length === 0 ? (
        <div className="text-sm text-muted text-center py-4">No open reports right now.</div>
      ) : (
      <div className="overflow-x-auto">
        <table className="tbl">
          <thead><tr><th>Date</th><th>Partner</th><th>Category</th><th>Project</th><th>Message</th><th></th></tr></thead>
          <tbody>
            {reports.map(r => (
              <tr key={r.id}>
                <td className="text-xs">{fmtDate(r.created_at)}</td>
                <td className="text-xs">{r.partner_name || '—'}</td>
                <td className="text-xs">{PARTNER_REPORT_CATEGORY_LABELS[r.category] || r.category}</td>
                <td className="text-xs">{r.project_name || '—'}</td>
                <td className="text-xs" style={{maxWidth:320, whiteSpace:'normal'}}>{r.message}</td>
                <td className="text-right whitespace-nowrap"><Link to={`/app/partner-settlement?partner=${r.partner_id}&tab=distributions`} className="tab btn-small">Review</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function GroupOverviewPanel({ fy, statusFilterActive }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(null);
  useEffect(() => {
    const params = { dimension: 'company' };
    if (fy) params.financial_year = fy;
    api.get('/reports/breakdown', { params }).then(r => { setRows(r.data.rows); setTotal(r.data.total); }).catch(()=>{});
  }, [fy]);
  if (!rows || rows.length <= 1) return null;  // singleton group — the company view below already is the group
  return (
    <div className="card card-dark mb-8">
      <div className="serif text-xl mb-4 text-[color:var(--gold)] flex items-center gap-2">Group Overview — All Companies{fy && <span className="badge status-running text-xs font-normal">{fy}</span>}</div>
      {statusFilterActive && <div className="text-xs text-muted -mt-3 mb-4">Reflects the Financial Year filter above, but not the Status filter — this breakdown always includes every status.</div>}
      <div className="overflow-x-auto">
        <table className="tbl">
          <thead><tr><th>Company</th><th>Projects</th><th>Contract Value</th><th>Expenses</th><th>Profit</th><th>Cash-backed</th><th>Awaiting collection</th></tr></thead>
          <tbody>
            {rows.map((r,i) => <tr key={i}><td>{r.company_name}</td><td className="mono">{r.project_count}</td><td className="mono">{fmtINR(r.contract_value)}</td><td className="mono">{fmtINR(r.expenses)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(r.profit)}</td><td className="mono">{fmtINR(r.cash_backed_profit)}</td><td className="mono text-amber-400">{fmtINR(r.uncollected_profit)}</td></tr>)}
          </tbody>
          {total && <tfoot><tr className="font-semibold"><td>{total.company_name}</td><td className="mono">{total.project_count}</td><td className="mono">{fmtINR(total.contract_value)}</td><td className="mono">{fmtINR(total.expenses)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(total.profit)}</td><td className="mono">{fmtINR(total.cash_backed_profit)}</td><td className="mono text-amber-400">{fmtINR(total.uncollected_profit)}</td></tr></tfoot>}
        </table>
      </div>
    </div>
  );
}

function PartnerGrowthChart({ projects }) {
  const isLight = useIsLightTheme();
  const axisColor = isLight ? '#333' : '#c9a84c';
  const gridColor = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(236,230,216,0.08)';
  const data = projects.filter(({my}) => my).map(({project, my}) => ({
    name: project.project_name.length > 18 ? project.project_name.slice(0,16)+'…' : project.project_name,
    'My Profit': Number((my.total_profit/100000).toFixed(2)),
  }));
  if (data.length === 0) return null;
  return (
    <div className="card card-dark mt-6">
      <div className="serif text-xl mb-4 text-[color:var(--gold)]">Growth — My Profit by Project (₹ Lakhs)</div>
      <div style={{width:'100%', height:280}}>
        <ResponsiveContainer>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
            <XAxis dataKey="name" stroke={axisColor} fontSize={11}/>
            <YAxis stroke={axisColor} fontSize={11}/>
            <Tooltip contentStyle={{background: isLight?'#fff':'#1e1a13', border:'1px solid rgba(201,168,76,0.3)', borderRadius:6}}/>
            <Bar dataKey="My Profit" fill="#c9a84c"/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DashboardCharts({ projects }) {
  const isLight = useIsLightTheme();
  const axisColor = isLight ? '#333' : '#c9a84c';
  const gridColor = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(236,230,216,0.08)';
  const revExpData = projects.map(({project, totals}) => ({
    name: project.project_name.length > 18 ? project.project_name.slice(0,16)+'…' : project.project_name,
    Revenue: Number((totals.net_contract_value/100000).toFixed(2)),
    Expenses: Number((totals.total_expenses/100000).toFixed(2)),
    // distributable_profit, not project_profit -- matches the Dashboard's
    // own "Total Profit" KPI card on this same page (fixed the same
    // audit round for the identical reason: it must agree with what
    // settlements actually distribute, or this chart silently disagrees
    // with the KPI sitting right above it for any project carrying a
    // profit_adjustment).
    Profit: Number((totals.distributable_profit/100000).toFixed(2)),
  }));
  const invAgg = {};
  projects.forEach(({project, totals}) => {
    invAgg[project.project_name] = (invAgg[project.project_name] || 0) + totals.total_investments;
  });
  const pieData = Object.entries(invAgg).filter(([_,v])=>v>0).map(([name, value]) => ({ name, value: Number((value/100000).toFixed(2)) }));
  // Validated colorblind-safe categorical order (see index.css --accent-*
  // and dataviz skill's validate_palette.js) — CSS custom properties work as
  // SVG fill values, so this auto-follows the light/dark theme toggle.
  const COLORS = ['var(--accent-blue)','var(--accent-red)','var(--accent-green)','var(--accent-amber)','var(--gold)','#9085e9'];
  if (projects.length === 0) return null;
  return (
    <div className="grid md:grid-cols-2 gap-4 mt-6">
      <div className="card card-dark">
        <div className="serif text-xl mb-4 text-[color:var(--gold)]">Revenue vs Expenses vs Profit (₹ Lakhs)</div>
        <div style={{width:'100%', height:280}}>
          <ResponsiveContainer>
            <BarChart data={revExpData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
              <XAxis dataKey="name" stroke={axisColor} fontSize={11}/>
              <YAxis stroke={axisColor} fontSize={11}/>
              <Tooltip contentStyle={{background: isLight?'#fff':'#1e1a13', border:'1px solid rgba(201,168,76,0.3)', borderRadius:6}}/>
              <Legend/>
              <Bar dataKey="Revenue" fill="var(--accent-blue)"/>
              <Bar dataKey="Expenses" fill="var(--accent-red)"/>
              <Bar dataKey="Profit" fill="var(--gold)"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card card-dark">
        <div className="serif text-xl mb-4 text-[color:var(--gold)]">Investment by Project (₹ Lakhs)</div>
        <div style={{width:'100%', height:280}}>
          {pieData.length === 0 ? <div className="text-muted text-sm">No investments recorded yet.</div> :
          <ResponsiveContainer>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
              </Pie>
              <Tooltip contentStyle={{background: isLight?'#fff':'#1e1a13', border:'1px solid rgba(201,168,76,0.3)', borderRadius:6}}/>
              <Legend/>
            </PieChart>
          </ResponsiveContainer>}
        </div>
      </div>
    </div>
  );
}

// ============= PARTNERS =============
// User-requested: an Investor is purely a classification on top of the
// regular partner record -- never a separate entity/table, so "Add
// Investor" and "Add Partner" open the exact same form (see Partners()
// below), just as two differently-labeled entry points. Kept as a shared
// constant so the list-table badge and the form's own select stay in sync.
const INVESTOR_SCOPE_LABELS = { company: 'Investor — Company-wide', project: 'Investor — Project-based' };

function Partners() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const companyF = useMultiFilter('Partners.company');
  const statusF = useMultiFilter('Partners.status');
  const [roleFilter, setRoleFilter] = usePersistedState('Partners.role', '');
  const [search, setSearch] = usePersistedState('Partners.search', '');
  const [view, setView] = usePersistedState('Partners.view', 'cards');
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  // User-requested: "map projects according to company" for a
  // project-based investor -- needs the full project list to filter by
  // whichever company(ies) are ticked above in the same form.
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get('/projects').then(r=>setProjects(r.data)).catch(()=>{}); }, []);
  const investorProjectOptions = projects.filter(p => (form.company_ids||[]).includes(p.company_id));
  const projectName = (pid) => projects.find(p=>p.id===pid)?.project_name || '—';

  const load = async () => setItems((await api.get('/partners')).data);
  useEffect(() => { load(); }, []);
  const q = search.trim().toLowerCase();
  const visibleItems = items
    .filter(p => companyF.all || (p.company_ids||[]).some(cid => companyF.ids.includes(cid)))
    .filter(p => statusF.matches(p.status))
    .filter(p => !roleFilter || (roleFilter === 'investor' ? !!p.investor_scope : !p.investor_scope))
    .filter(p => !q || [p.name, p.phone, p.email, ...(p.company_ids||[]).map(companyName)].some(x => String(x || '').toLowerCase().includes(q)));
  const activeCount = visibleItems.filter(p => p.status === 'active').length;
  const companyWideCount = visibleItems.filter(p => p.investor_scope === 'company').length;
  const projectInvCount = visibleItems.filter(p => p.investor_scope === 'project').length;
  const coveredCompanies = new Set(visibleItems.flatMap(p => p.company_ids || [])).size;
  const hasFilter = !!(companyF.active || statusF.active || roleFilter || q);

  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    if (!form.name?.trim()) { toast.error('Name is required.'); return; }
    if (needsPicker && !(form.company_ids||[]).length) { toast.error('At least one company is required.'); return; }
    if (form.investor_scope === 'project' && !(form.investor_project_ids||[]).length) { toast.error('A project-based investor needs at least one project.'); return; }
    setSaving(true);
    try {
      if (form.id) {
        // Whole-app final-check finding: /api/partners now correctly
        // narrows company_ids to what THIS caller can see (the earlier
        // cross-company grant leak fix) -- but the backend treats an
        // included array as the complete, authoritative new grant list
        // (omitted = untouched). The edit form loads this narrowed array
        // straight into state; sending it back on every save, even for an
        // unrelated field like a phone-number fix, silently stripped a
        // shared partner's access to any company the editing admin
        // couldn't see themselves. Only send it when actually changed
        // from what was loaded.
        const original = items.find(i => i.id === form.id);
        const origIds = original?.company_ids || [];
        const formIds = form.company_ids || [];
        const sameIds = origIds.length === formIds.length && origIds.every(x => formIds.includes(x));
        const payload = sameIds ? { ...form, company_ids: undefined } : form;
        await api.patch(`/partners/${form.id}`, payload);
      }
      else await api.post('/partners', form);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete partner?')) return;
    try { await api.delete(`/partners/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="section-eyebrow mb-2">Team</div><h1 className="serif text-4xl">Partners</h1>
          {needsPicker && <div className="text-xs text-muted mt-1">A partner who's active in more than one company only needs to be entered once — tick every company they're a partner in.</div>}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* User-requested: a separate "Add Investor" button used to sit
              here, opening this exact same form with Investor Type
              pre-set to Company-wide -- removed (confirmed with the user
              first) since it was a pure one-click convenience, never a
              distinct capability: this form's own Investor Type field
              already covers both a regular partner and either kind of
              investor. */}
          {canEdit && <button onClick={()=>{setForm({status:'active', company_ids: companies.length===1?[companies[0].id]:[]});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-partner-btn"><Plus size={14}/>Add Partner</button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="partner-kpis">
        <KpiCard label="Partners" tone="gold" icon={Users} value={visibleItems.length} hint={`${activeCount} active${visibleItems.length - activeCount ? ` · ${visibleItems.length - activeCount} inactive` : ''}`}/>
        <KpiCard label="Investors" tone="blue" icon={Landmark} value={companyWideCount + projectInvCount} hint={`${companyWideCount} company-wide · ${projectInvCount} project-based`}/>
        <KpiCard label="Partners only" tone="green" icon={Award} value={visibleItems.length - companyWideCount - projectInvCount} hint="Not marked as investors"/>
        <KpiCard label="Companies covered" tone="amber" icon={Building2} value={coveredCompanies} hint="Where these partners are active"/>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="vendor-search">
          <SearchIcon size={14}/>
          <input className="input" placeholder="Search name, phone, email, company…" value={search} onChange={e=>setSearch(e.target.value)} data-testid="partner-search"/>
        </div>
        {needsPicker && <MultiFilterPicker filter={companyF} items={companies.map(c=>({id:c.id, name:c.name}))} allLabel="All Companies" width={170} testId="partner-filter-company"/>}
        <MultiFilterPicker filter={statusF} items={[{id:'active', name:'active'},{id:'inactive', name:'inactive'}]} allLabel="All Statuses" width={140} testId="partner-filter-status"/>
        <select className="input" style={{width:190}} value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} data-testid="partner-filter-role">
          <option value="">Partners & Investors</option>
          <option value="partner">Partners only</option>
          <option value="investor">Investors only</option>
        </select>
        {hasFilter && <button onClick={()=>{setSearch('');setRoleFilter('');statusF.reset();companyF.reset();}} className="tab btn-small" data-testid="partner-clear">Clear</button>}
        <div className="ml-auto inline-flex gap-1" role="group" aria-label="View">
          <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='cards'?'active':'')} onClick={()=>setView('cards')} data-testid="partner-view-cards"><LayoutGrid size={13}/>Cards</button>
          <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='table'?'active':'')} onClick={()=>setView('table')} data-testid="partner-view-table"><ListIcon size={13}/>Table</button>
        </div>
      </div>
      <div className="text-xs text-muted mb-3">Showing <b>{visibleItems.length}</b> of <b>{items.length}</b> partners{visibleItems.length !== items.length ? ' (filtered)' : ''}</div>

      {view === 'cards' && (
        <div data-testid="partner-cards">
          {visibleItems.length === 0 && <div className="card card-dark text-center text-muted py-10">{items.length === 0 ? 'No partners yet.' : 'No partners match these filters.'}</div>}
          <div className="vendor-grid">
            {visibleItems.map(p => {
              const tone = META_CHIP_TONES[p.investor_scope === 'company' ? 'blue' : p.investor_scope === 'project' ? 'violet' : 'gold'];
              const cos = (p.company_ids||[]).map(companyName);
              const chip = (extra) => ({ '--chip-color': tone.color, '--chip-soft': tone.soft, '--chip-border': tone.border, '--chip-hover': tone.hover, ...extra });
              return (
                <div key={p.id} className={"card card-dark vendor-card " + (p.status === 'active' ? '' : 'vendor-card-inactive')} style={{ '--vendor-accent': tone.color }} data-testid={`partner-card-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="vendor-avatar" style={{ width: 44, height: 44, fontSize: 16, color: tone.color, background: tone.soft, borderColor: tone.border }} aria-hidden="true">{vendorInitials(p.name)}</div>
                    <div className="min-w-0 flex-1">
                      <Link to={`/app/partners/${p.id}`} className="serif text-lg leading-tight hover:text-[color:var(--gold)] vendor-name" title={p.name}>{p.name}</Link>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="meta-chip" style={chip()}>{p.investor_scope === 'company' ? 'Investor · Company-wide' : p.investor_scope === 'project' ? 'Investor · Project-based' : 'Partner'}</span>
                        <span className={"badge "+(p.status==='active'?'status-running':'status-closed')}>{p.status}</span>
                      </div>
                    </div>
                  </div>
                  {p.investor_scope === 'project' && <div className="text-xs text-muted mt-3">Projects: {(p.investor_project_ids||[]).map(projectName).join(', ') || '—'}</div>}
                  {needsPicker && <div className="flex flex-wrap gap-1 mt-3" data-testid={`partner-card-companies-${p.id}`}>
                    {cos.length === 0 && <span className="text-xs text-muted">No company</span>}
                    {cos.slice(0, 3).map((n, i) => <span key={i} className="text-xs inline-flex items-center gap-1" style={{ padding: '2px 8px', borderRadius: 20, border: '1px solid var(--border-subtle)' }}><Building2 size={11} style={{ opacity: 0.6 }}/>{n}</span>)}
                    {cos.length > 3 && <span className="text-xs text-muted" title={cos.slice(3).join(', ')}>+{cos.length - 3} more</span>}
                  </div>}
                  <div style={{ flex: 1, minHeight: 14 }} aria-hidden="true"/>
                  <div className="vendor-card-foot">
                    <div className="inline-flex items-center gap-2 min-w-0">
                      {p.phone ? <a href={`tel:${String(p.phone).replace(/\s+/g,'')}`} className="vendor-contact" title={p.phone}><Phone size={13}/><span className="vendor-contact-text">{p.phone}</span></a> : null}
                      {p.email ? <a href={`mailto:${p.email}`} className="vendor-contact" title={p.email} aria-label={`Email ${p.name}`}><Mail size={13}/>{!p.phone && <span className="vendor-contact-text">{p.email}</span>}</a> : null}
                      {!p.phone && !p.email && <span className="text-xs text-muted">No contact info</span>}
                    </div>
                    <div className="inline-flex gap-1 flex-shrink-0">
                      <Link to={`/app/partners/${p.id}`} className="tab btn-small">View</Link>
                      {canEdit && <button aria-label="Edit" onClick={()=>{setForm(p);setOpen(true);}} className="tab btn-small"><Edit3 size={12}/></button>}
                      {canEdit && isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(p.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === 'table' && (
      <div className="card card-dark overflow-x-auto" data-testid="partner-table">
        <table className="tbl">
          <thead><tr><th>Name</th><th>Phone</th><th>Email</th>{needsPicker && <th>Companies</th>}<th>Status</th><th>Investor</th><th></th></tr></thead>
          <tbody>
            {visibleItems.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-8">{items.length === 0 ? 'No partners yet.' : 'No partners match these filters.'}</td></tr>}
            {visibleItems.map(p => (
              <tr key={p.id} data-testid={`partner-${p.id}`}>
                <td className="serif text-base"><Link to={`/app/partners/${p.id}`} className="hover:text-[color:var(--gold)]">{p.name}</Link></td>
                <td>{p.phone || '—'}</td>
                <td>{p.email || '—'}</td>
                {needsPicker && <td className="text-xs">{(p.company_ids||[]).map(companyName).join(', ')||'—'}</td>}
                <td><span className="badge status-running">{p.status}</span></td>
                <td className="text-xs">{p.investor_scope === 'company' ? 'Company-wide' : p.investor_scope === 'project' ? `Project: ${(p.investor_project_ids||[]).map(projectName).join(', ')||'—'}` : '—'}</td>
                {/* User-reported: View/Edit were overlapping -- couldn't
                    reproduce the exact mechanism (a same-origin test at
                    desktop/tablet/mobile widths all rendered cleanly, no
                    overlap, just graceful wrap/scroll), so this switches
                    from margin-based spacing (mr-2 only ever pads one
                    side, and can end up doing nothing in some edge case)
                    to a flex row with an explicit gap -- guarantees real
                    separation between every button, in both directions,
                    regardless of how tight the column gets. */}
                <td className="text-right">
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    <Link to={`/app/partners/${p.id}`} className="tab btn-small">View</Link>
                    {canEdit && <>
                      <button aria-label="Edit" onClick={()=>{setForm(p);setOpen(true);}} className="tab btn-small"><Edit3 size={12}/></button>
                      {isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(p.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id ? 'Edit Partner' : 'Add Partner'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {needsPicker ? (
            <div className="col-span-2"><Field label="Companies *"><div data-testid="partner-companies">
              <GrantPicker
                items={companies}
                selectedIds={form.company_ids || []}
                allAccess={false}
                onToggle={cid => setForm(f => { const cur = f.company_ids||[]; return {...f, company_ids: cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid]}; })}
                onSetAllAccess={()=>{}}
                canGrantAll={false}
                allLabel="All Companies"
              />
            </div></Field></div>
          ) : (
            <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker}/>
          )}
          <Field label="Name *"><input className="input" data-testid="p-name" value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
          <Field label="Phone"><input className="input" value={form.phone||''} onChange={e=>setForm({...form, phone:e.target.value})}/></Field>
          <Field label="Email"><input className="input" value={form.email||''} onChange={e=>setForm({...form, email:e.target.value})}/></Field>
          <Field label="Status">
            <select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}>
              <option value="active">active</option><option value="inactive">inactive</option>
            </select>
          </Field>
          {/* User-requested: purely a classification, never touches any
              profit/interest calculation -- Roles/Profit Sharing Rules/
              Investments already handle that. Company-wide = paid via
              profit share or Current Account interest only, hidden by
              default from every Paid By/Borne By picker app-wide.
              Project-based = shown, labeled "(Investor)", only on the
              project(s) picked below. */}
          <Field label="Investor Type">
            <select className="input" data-testid="p-investor-scope" value={form.investor_scope||''} onChange={e=>setForm({...form, investor_scope:e.target.value||null, investor_project_ids: e.target.value==='project'?form.investor_project_ids:[]})}>
              <option value="">— not an investor —</option>
              {Object.entries(INVESTOR_SCOPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          {form.investor_scope === 'project' && (
            <div className="col-span-2"><Field label="Investor Projects *"><div data-testid="partner-investor-projects">
              {!(form.company_ids||[]).length ? (
                <div className="text-xs text-muted">Pick a company above first — projects are filtered to it.</div>
              ) : investorProjectOptions.length === 0 ? (
                <div className="text-xs text-muted">No projects found for the selected company(ies).</div>
              ) : (
                <GrantPicker
                  items={investorProjectOptions.map(p=>({id:p.id, name:p.project_name}))}
                  selectedIds={form.investor_project_ids || []}
                  allAccess={false}
                  onToggle={pid => setForm(f => { const cur = f.investor_project_ids||[]; return {...f, investor_project_ids: cur.includes(pid) ? cur.filter(x=>x!==pid) : [...cur, pid]}; })}
                  onSetAllAccess={()=>{}}
                  canGrantAll={false}
                  allLabel="All Projects"
                />
              )}
            </div></Field></div>
          )}
          <div className="col-span-2"><Field label="Notes"><textarea className="input" rows={2} value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="p-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

// ============= PROJECTS LIST =============
const DEFAULT_PROJECT_STATUSES = ['planning','running','completed','financial_review','profit_approved','settled','closed'];
// Fallback shown before a company's own work_category masters have loaded
// (or for the rare company predating this feature) — same "hardcoded list
// until Settings has real rows" pattern DED_TYPES already uses for
// deduction_type.
const DEFAULT_WORK_CATEGORIES = ['Tender Work', 'Voucher Payment', 'Billed via Vendor'];

// Every partnership model is really the same two ideas combined
// differently — a pool of profit, and how it's split (equal, a custom %
// per partner, or proportional to capital invested). Plain-language
// labels/help here, not "Model A/B/C" — the jargon added nothing.
const PARTNERSHIP_MODEL_LABELS = {
  equal_capital_bonus: 'Equal / Variable + Investment Bonus (default)',
  money_work: 'Money & Work Partnership',
  equal_variable: 'Equal / Variable Partnership',
};
const PARTNERSHIP_MODEL_HELP = {
  equal_variable: "Profit is split equally, or by a custom % you set per partner — no capital weighting. Use when the company fund covered costs, or partners invested the same.",
  equal_capital_bonus: "A base share (equal or custom %) plus an extra bonus shared in proportion to actual capital invested. Use when partners invested different amounts and that should be rewarded.",
  money_work: "Two pools: a Money pool always split by investment ratio, and a Work pool split equally or by custom %. Defaults 50/50 between the pools — adjust under Settings › Profit Sharing.",
};
const BASE_SPLIT_OPTIONS = [
  ['equal', 'Equal — split evenly among partners'],
  ['variable', 'Variable — custom % per partner'],
];
// One-click presets for the 3 models — sets every field a model needs
// in a single click instead of two dropdown steps. Keeps the dropdowns
// fully available below for anyone who wants to configure by hand.
const PARTNERSHIP_MODEL_PRESETS = [
  { key: 'equal_variable', label: PARTNERSHIP_MODEL_LABELS.equal_variable, fields: { partnership_model: 'equal_variable', model_b_type: 'equal' } },
  { key: 'equal_capital_bonus', label: PARTNERSHIP_MODEL_LABELS.equal_capital_bonus, fields: { partnership_model: 'equal_capital_bonus', capital_bonus_base: 'equal' } },
  { key: 'money_work', label: PARTNERSHIP_MODEL_LABELS.money_work, fields: { partnership_model: 'money_work', work_allocation: 'equal' } },
];
// Phase 4 advanced module: tracks the tender lifecycle on axes the
// single `status` field can't represent on its own (billing can be
// "under_process" while execution is still "ongoing"). Additive,
// optional — every value defaults to unset and `status` keeps working
// exactly as it always has.
const PROJECT_STATUS_AXES = {
  tender_status: { label: 'Tender Status', options: ['estimation_sent','tender_submitted','sanctioned','awarded','not_awarded'] },
  documentation_status: { label: 'Documentation Status', options: ['verbal','work_order_pending','work_order_received'] },
  execution_status: { label: 'Execution Status', options: ['not_started','ongoing','completed','discontinued'] },
  billing_status: { label: 'Billing Status', options: ['not_billed','internal_bill_generated','bill_submitted','under_process'] },
  payment_status: { label: 'Payment Status', options: ['pending','partially_received','received'] },
  financial_status: { label: 'Financial Status', options: ['open','post_payment_pending','deductions_reconciled','partner_settlement_pending','closed'] },
};
const axisLabel = (v) => v ? v.replace(/_/g,' ') : '—';
const BILLING_FREQUENCY_LABELS = { monthly: 'Monthly', after_completion: 'After Completion', against_applied: 'Against Applied Bill', milestone: 'Milestone-based', other: 'Other' };

function useMasters() {
  const { user } = useAuth();
  const [byType, setByType] = useState({});
  useEffect(() => {
    if (isDataEntry(user)) return; // a Data Entry login has no access to masters
    api.get('/masters').then(r => {
      const grouped = {};
      for (const m of r.data) { if (m.status === 'active') (grouped[m.type] = grouped[m.type] || []).push(m); }
      setByType(grouped);
    });
  }, []);
  return byType;
}
// useMasters() returns every master the caller can see across every
// company they have access to (each row carries company_ids from the
// API). A form scoped to one specific company (a project, an expense,
// a deduction) must narrow that down further or a multi-company user
// picking e.g. Department/Client sees — and can save — a row that
// belongs to an entirely different company. companyId falsy (no picker
// shown, single-company caller) skips the filter since the API already
// scoped the list to their one company in that case.
// The client a project belongs to: its linked client record, or -- for older / imported rows that only carry the client's
// NAME and no link -- the client record with that exact name (the project's own company first, else a name that is unique).
// Without this such rows showed under "All Clients" but under no individual client.
const clientIdOfProject = (p, clientMasters) => {
  if (!p) return undefined;
  if (p.department_client_id) return p.department_client_id;
  const nm = (p.client_name || '').trim().toLowerCase();
  if (!nm) return undefined;
  const hits = (clientMasters || []).filter(m => (m.name || '').trim().toLowerCase() === nm);
  const own = hits.filter(m => p.company_id && (m.company_ids || []).includes(p.company_id));
  if (own.length === 1) return own[0].id;
  return hits.length === 1 ? hits[0].id : undefined;
};
const mastersForCompany = (list, companyId) => !companyId ? (list || []) : (list || []).filter(m => (m.company_ids || []).includes(companyId));
// Same idea for a plain single-company table like partners — filter the
// caller's full cross-company list down to the one a form is scoped to.
const forCompany = (list, companyId) => !companyId ? (list || []) : (list || []).filter(x => x.company_id === companyId);

// Vendors for an expense form: the company's own ACTIVE vendors (from Staff &
// Vendor), nothing from another company. A value already saved on the expense
// that is not in that list (older free-text entries) stays selectable and is
// never blanked on edit.
function useVendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    if (user?.role === 'partner') return; // a partner has no access to vendor records
    api.get('/vendors').then(r => setVendors(r.data)).catch(() => {});
  }, []);
  return vendors;
}
const vendorNamesFor = (vendors, companyId) =>
  forCompany(vendors, companyId).filter(v => (v.status || 'active') === 'active').map(v => v.name).sort((a, b) => a.localeCompare(b));
// "Not a vendor payment" is an explicit choice on a NEW expense (a blank
// vendor is not accepted), stored as no vendor at all. An expense whose
// category is a Vendor/Sub-contractor one can never be marked "not a vendor
// payment" -- a vendor must be chosen. Same idea for salary/payroll
// categories and the Staff Member.
const NO_VENDOR = '__none__';
const isVendorCategory = (name) => /vendor|sub[\s-]?contract/i.test(name || '');
const isStaffCategory = (name) => /salary|payroll/i.test(name || '');
// Office & Staff style categories: spending here is often, but not always,
// a staff payment -- so the user must answer explicitly: a staff payment
// type + Staff Member, or "Not a staff payment". (Salary/payroll categories
// above are stricter: the answer must be a staff member.)
const isStaffDecisionCategory = (name) => /\bstaff\b/i.test(name || '');
const STAFF_DECISION_MESSAGE = 'This category covers office & staff spending — choose the Payment Type and Staff Member, or select "Not a staff payment".';
const cleanVendor = (v) => (!v || v === NO_VENDOR ? null : v);
// Display only (the red * on the Vendor label): mirrors vendorStaffProblem below -- a vendor answer is
// mandatory on every NEW expense (a vendor, or "Not a vendor payment"), and always a real vendor for a
// Vendor/Sub-contractor category.
const vendorIsRequired = (form, categoryName) => isVendorCategory(categoryName) || !form.id;
// Same idea for the Staff Member: required once a Payment Type is set, and always for a Salary/payroll category.
const staffIsRequired = (form, categoryName) => !!form.staff_payment_type || isStaffCategory(categoryName);
function vendorStaffProblem(form, categoryName) {
  const chosen = !!form.vendor_name && form.vendor_name !== NO_VENDOR;
  if (isVendorCategory(categoryName) && !chosen) return 'Vendor is required for a Vendor/Sub-contractor expense — choose the vendor (it cannot be marked "Not a vendor payment").';
  if (!form.id && !form.vendor_name) return 'Choose the Vendor, or select "Not a vendor payment".';
  if (isStaffCategory(categoryName) && !form.staff_id) return 'Staff Member is required for a Salary/payroll expense — it cannot be marked "not a staff payment".';
  return null;
}
function VendorSelect({ vendors, companyId, value, onChange, categoryName, isNew = true }) {
  const names = companyId ? vendorNamesFor(vendors, companyId) : [];
  const required = isVendorCategory(categoryName);
  const legacy = value && value !== NO_VENDOR && !names.includes(value);
  const shown = value || (!isNew && !required ? NO_VENDOR : '');
  return (
    <select className="input" value={shown} disabled={!companyId && !value} onChange={e => onChange(e.target.value || null)} data-testid="vendor-select">
      <option value="">{!companyId ? '— select a company first —' : names.length || !required ? '— choose —' : '— no vendors added yet (add in Staff & Vendor) —'}</option>
      {!required && <option value={NO_VENDOR}>Not a vendor payment</option>}
      {legacy && <option value={value}>{value} (not in vendor list)</option>}
      {names.map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

// User-requested: an Investor (see INVESTOR_SCOPE_LABELS / Partners()) is
// purely a classification, not a company/project grant change -- this
// layers ON TOP of the existing mastersForCompany scoping to decide which
// partners actually show in a Paid By/Borne By picker by default.
// Company-wide investors are hidden everywhere by default (they don't
// personally pay expenses -- paid via profit share or Current Account
// interest only); project-based investors are shown, but only on the
// specific project(s) they're mapped to (never at all when this form has
// no project context, e.g. a Company Expense). Either kind still shows if
// `showAll` is set (a per-form "Show all partners" toggle) or if they're
// the value already selected in this exact field (never silently hide an
// already-saved choice out from under an edit).
const eligiblePaidByPartners = (list, companyId, projectId, showAll, keepSelectedId) => {
  const scoped = mastersForCompany(list, companyId);
  if (showAll) return scoped;
  return scoped.filter(p => {
    if (keepSelectedId && p.id === keepSelectedId) return true;
    if (p.investor_scope === 'company') return false;
    if (p.investor_scope === 'project') return !!projectId && (p.investor_project_ids||[]).includes(projectId);
    return true;
  });
};
const partnerOptionLabel = (p) => p.investor_scope ? `${p.name} (Investor)` : p.name;

// What a Data Entry login has typed in during THIS browser session, built only
// from what it just submitted (nothing is read back from the server), so it can
// confirm each entry went through. The permanent record is the Audit Queue.
function DataEntryRecent({ items }) {
  if (!items.length) return null;
  return (
    <div className="card card-dark mt-6" data-testid="de-recent">
      <div className="serif text-lg mb-2">Added in this session</div>
      <ul className="text-sm grid gap-1">
        {items.map((r, i) => <li key={i} className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-400 flex-shrink-0"/><span>{r}</span></li>)}
      </ul>
      <div className="text-xs text-muted mt-2">Open the Audit Queue to see where each entry is in review.</div>
    </div>
  );
}

// ---- Projects page: card view helpers ----
// Only roles that already see per-project money on the Dashboard see it on the cards.
const PROJECT_MONEY_ROLES = ['master_admin', 'admin', 'accountant', 'viewer'];
// Optional money columns for the Projects printout: [option key, column label, field in the Dashboard per-project totals].
const PRINT_MONEY_COLS = [['spent', 'Spent', 'total_expenses'], ['received', 'Received', 'total_receipts'], ['receivable', 'Receivable', 'outstanding_receivable'], ['cash', 'Profit in cash', 'cash_realised_profit']];
const PROJECT_STATUS_TONES = { planning: 'blue', running: 'green', completed: 'gold', financial_review: 'amber', profit_approved: 'violet', settled: 'violet' };
const PROJECT_LIVE_STATUSES = ['planning', 'running'];
const projectToneOf = (status) => META_CHIP_TONES[PROJECT_STATUS_TONES[status]] || null;
// Schedule line for a card: dates, share of the planned period already gone, and whether a still-open project is past its end date.
function projectTimeline(p, todayIso) {
  if (!p || (!p.start_date && !p.end_date)) return null;
  const live = PROJECT_LIVE_STATUSES.includes(p.status);
  let pct = null, overdue = false, daysLeft = null;
  if (p.end_date) {
    const end = new Date(`${p.end_date}T00:00:00`), today = new Date(`${todayIso}T00:00:00`);
    daysLeft = Math.round((end - today) / 86400000);
    overdue = live && daysLeft < 0;
    if (p.start_date) {
      const start = new Date(`${p.start_date}T00:00:00`);
      const total = end - start;
      if (total > 0) pct = Math.max(0, Math.min(100, Math.round(((today - start) / total) * 100)));
    }
  }
  return { start: p.start_date || null, end: p.end_date || null, pct, overdue, daysLeft: live ? daysLeft : null };
}
const sortProjectCards = (list, key, statsById) => {
  const c = [...list];
  const money = (p, f) => Number(((statsById[p.id] || {})[f]) ?? -Infinity);
  const byName = (a, b) => (a.project_name || '').localeCompare(b.project_name || '', undefined, { numeric: true, sensitivity: 'base' });
  if (key === 'contract') c.sort((a, b) => Number(b.contract_value || 0) - Number(a.contract_value || 0) || byName(a, b));
  else if (key === 'profit') c.sort((a, b) => money(b, 'project_profit') - money(a, 'project_profit') || byName(a, b));
  else if (key === 'ending') c.sort((a, b) => (a.end_date ? 0 : 1) - (b.end_date ? 0 : 1) || String(a.end_date || '').localeCompare(String(b.end_date || '')) || byName(a, b));
  else c.sort(byName);
  return c;
};
function ProjectKpis({ visible, statsById, showMoney }) {
  const running = visible.filter(p => p.status === 'running').length;
  const contract = visible.reduce((t, p) => t + Number(p.contract_value || 0), 0);
  const withStats = visible.filter(p => statsById[p.id]);
  const sum = (f) => withStats.reduce((t, p) => t + Number(statsById[p.id][f] || 0), 0);
  const followUp = visible.filter(p => p.needs_follow_up).length;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 no-print" data-testid="project-kpis">
      <KpiCard label="Projects" tone="blue" icon={FolderKanban} value={visible.length} hint={`${running} running`}/>
      <KpiCard label="Contract value" tone="gold" value={fmtINR(contract)} hint="Of the projects shown"/>
      {showMoney ? <>
        <KpiCard label="Received" tone="green" icon={CheckCircle} value={withStats.length ? fmtINR(sum('total_receipts')) : '—'} hint={withStats.length ? `${fmtINR(sum('outstanding_receivable'))} receivable` : undefined}/>
        {/* Same definition as the Dashboard's "Total Profit": each project's distributable profit minus its Company Fund reserve. */}
        <KpiCard label="Total profit" tone="amber" value={withStats.length ? fmtINR(withStats.reduce((t, p) => t + Number(statsById[p.id].distributable_profit || 0) - Number(statsById[p.id].company_fund_pool || 0), 0)) : '—'} hint={withStats.length ? `${fmtINR(sum('total_expenses'))} spent · after Company Fund reserve` : undefined}/>
      </> : <>
        <KpiCard label="Needs follow-up" tone="amber" icon={Clock3} value={followUp} hint="Flagged for follow-up"/>
        <KpiCard label="Completed or later" tone="green" icon={CheckCircle} value={visible.filter(p => !PROJECT_LIVE_STATUSES.includes(p.status)).length} hint="Past planning and running"/>
      </>}
    </div>
  );
}
function ProjectCards({ items, statsById, flagsMap, showMoney, canEdit, isMaster, needsPicker, companyName, onEdit, onConvert, onDelete, todayIso }) {
  const maxContract = Math.max(0, ...items.map(p => Number(p.contract_value || 0)));
  return (
    <div className="vendor-grid project-grid">
      {items.map(p => {
        const tone = projectToneOf(p.status);
        const st = statsById[p.id];
        const tl = projectTimeline(p, todayIso);
        const contract = Number(p.contract_value || 0);
        const pctOf = (v) => contract > 0 ? Math.max(0, Math.min(100, Math.round((Number(v || 0) / contract) * 100))) : 0;
        return (
          <div key={p.id} className="card card-dark vendor-card" style={{ '--vendor-accent': tone ? tone.color : 'var(--border-strong)' }} data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-start gap-1">
                  <Link to={`/app/projects/${p.id}`} className="serif text-lg leading-tight hover:text-[color:var(--gold)] vendor-name" title={p.project_name}>{p.project_name}</Link>
                </div>
                <div className="text-xs text-muted mt-0.5">{p.tender_number || '—'}{p.client_name ? ` · ${p.client_name}` : ''}</div>
                <FlagDots flags={flagsMap[p.id]} followUp={p.needs_follow_up} followUpTestId={`project-card-followup-${p.id}`}/>
              </div>
              <div className="flex-shrink-0"><ProjectStatusBadge status={p.status}/></div>
            </div>
            <div className="flex flex-wrap gap-1 mt-3">
              {p.work_category && <span className="text-xs" style={{ padding: '1px 8px', borderRadius: 20, border: '1px solid var(--border-subtle)' }}>{p.work_category}</span>}
              {p.financial_year && <span className="text-xs" style={{ padding: '1px 8px', borderRadius: 20, border: '1px solid var(--border-subtle)' }}>FY {p.financial_year}</span>}
              {needsPicker && <span className="text-xs text-muted" style={{ padding: '1px 4px' }}>{companyName(p.company_id)}</span>}
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-muted">Contract value</div>
              <div className="serif mono text-2xl" style={{ color: 'var(--gold)' }} data-testid={`project-card-contract-${p.id}`}>{fmtINR(contract)}</div>
              <div className="vendor-bar" aria-hidden="true"><div style={{ width: `${maxContract > 0 ? Math.max(2, Math.round((contract / maxContract) * 100)) : 0}%` }}/></div>
            </div>
            {showMoney && st && (
              <div className="mt-3" data-testid={`project-card-money-${p.id}`}>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><div className="text-muted">Spent</div><div className="mono">{fmtINR(st.total_expenses)}</div></div>
                  <div><div className="text-muted">Received</div><div className="mono">{fmtINR(st.total_receipts)}</div></div>
                  <div><div className="text-muted">Receivable</div><div className="mono">{fmtINR(st.outstanding_receivable)}</div></div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted">Profit</span>
                  <span className={"mono " + profitColorClass(st.project_profit)} data-testid={`project-card-profit-${p.id}`}>{fmtINR(st.project_profit)}</span>
                </div>
                {contract > 0 && <div className="mt-2" title={`Received ${pctOf(st.total_receipts)}% · Spent ${pctOf(st.total_expenses)}% of contract value`}>
                  <div className="vendor-bar" style={{ marginTop: 0 }}><div className="bar-green" style={{ width: `${pctOf(st.total_receipts)}%` }}/></div>
                  <div className="vendor-bar" style={{ marginTop: 3 }}><div className="bar-red" style={{ width: `${pctOf(st.total_expenses)}%` }}/></div>
                </div>}
                {(st.expenses_pending_review > 0 || st.receipts_pending_review > 0) && <div className="vendor-pending mt-2" data-testid={`project-card-pending-${p.id}`}><Clock3 size={12}/>{fmtINR((st.expenses_pending_review || 0) + (st.receipts_pending_review || 0))} awaiting review</div>}
              </div>
            )}
            {tl && (
              <div className="mt-3 text-xs" data-testid={`project-card-timeline-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted">{tl.start ? fmtDate(tl.start) : '…'} → {tl.end ? fmtDate(tl.end) : '…'}</span>
                  {tl.overdue ? <span className="text-red-400" data-testid={`project-card-overdue-${p.id}`}>{Math.abs(tl.daysLeft)} day{Math.abs(tl.daysLeft) === 1 ? '' : 's'} past end date</span>
                    : tl.daysLeft !== null && tl.daysLeft >= 0 ? <span className="text-muted">{tl.daysLeft} day{tl.daysLeft === 1 ? '' : 's'} left</span> : null}
                </div>
                {tl.pct !== null && PROJECT_LIVE_STATUSES.includes(p.status) && <div className="vendor-bar" aria-hidden="true"><div className={tl.overdue ? 'bar-red' : ''} style={{ width: `${tl.pct}%` }}/></div>}
              </div>
            )}
            {(p.execution_status || p.billing_status || p.payment_status) && (
              <div className="flex flex-wrap gap-1 mt-3" data-testid={`project-card-lifecycle-${p.id}`}>
                {p.execution_status && <span className="text-xs text-muted" title="Execution">⚙ {axisLabel(p.execution_status)}</span>}
                {p.billing_status && <span className="text-xs text-muted" title="Billing">🧾 {axisLabel(p.billing_status)}</span>}
                {p.payment_status && <span className="text-xs text-muted" title="Payment">₹ {axisLabel(p.payment_status)}</span>}
              </div>
            )}
            <div style={{ flex: 1, minHeight: 14 }} aria-hidden="true"/>
            <div className="vendor-card-foot">
              <div className="text-xs text-muted min-w-0" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(PARTNERSHIP_MODEL_LABELS[p.partnership_model] || p.partnership_model || '').replace(/\s*\(default\)/, '')}</div>
              <div className="inline-flex gap-1 flex-shrink-0">
                <Link to={`/app/projects/${p.id}`} className="tab btn-small" data-testid={`project-card-open-${p.id}`}>Open</Link>
                {canEdit && <button aria-label="Edit" onClick={() => onEdit(p)} className="tab btn-small" data-testid={`edit-project-card-${p.id}`}><Edit3 size={12}/></button>}
                {isMaster && !p.is_extra_work && <button aria-label="Convert to Extra Work" title="Convert to Extra Work bucket" onClick={() => onConvert(p)} className="tab btn-small"><ArrowRightLeft size={12}/></button>}
                {isMaster && <button aria-label="Delete" onClick={() => onDelete(p)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Projects() {
  const { user } = useAuth();
  const entryOnly = isDataEntry(user); // Data Entry: just the Add Expense form, no project list or figures
  const [recentEntries, setRecentEntries] = useState([]);
  const vendors = useVendors();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [followUpOnly, setFollowUpOnly] = usePersistedState('Projects.followUpOnly', false);
  const [flaggedOnly, setFlaggedOnly] = usePersistedState('Projects.flaggedOnly', false);
  const companyF = useMultiFilter('Projects.company');
  // User-requested: multi-select instead of a single-status dropdown, same
  // GrantPicker convention every other multi-select filter in this app
  // already uses (see ExtraWorks' own allCompaniesSelected for the proven
  // pattern this now matches). allAccess must be its OWN independent
  // state, not derived from statusFilterIds.length===0 -- GrantPicker
  // disables every individual checkbox while allAccess is true, so a
  // derived allAccess re-locks them the instant the user unticks the
  // last one, with no way back in (onSetAllAccess(false) had nothing to
  // do, since there was no separate flag to turn off). Live-reported bug
  // from exactly this mistake -- fixed here and in PartnerPayoutsSummary.
  const [allStatusesSelected, setAllStatusesSelected] = usePersistedState('Projects.allStatusesSelected', true);
  const [statusFilterIds, setStatusFilterIds] = usePersistedState('Projects.statusFilterIds', []);
  const categoryF = useMultiFilter('Projects.category');
  const yearF = useMultiFilter('Projects.year');
  // User-requested: a dedicated client-wise filter, same dropdown
  // convention as Company/Status/Category/FY right beside it -- the
  // free-text search box above already matched client_name too, but
  // picking one client from a list (rather than typing their exact name)
  // is what was actually being asked for.
  // Company, Category, Year and Client are multi-select too (same convention as Status above); see useMultiFilter.
  // (The old single-value Projects.*Filter keys are simply no longer read.)
  const clientF = useMultiFilter('Projects.client');
  // User-requested: search and jump to any project directly, same plain-
  // text-input convention already used for Masters/Partner search
  // elsewhere in the app -- matches on name, tender number, or client.
  const [searchQuery, setSearchQuery] = usePersistedState('Projects.searchQuery', '');
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const masters = useMasters();
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  // User-requested: a quick "Add Expense" from the Projects list itself,
  // for entering expenses across several different projects in one
  // sitting without drilling into each project's own Expenses tab first.
  // Posts straight to the same POST /expenses every other Add Expense
  // form already uses -- same required fields, same validation, no new
  // backend surface. Category/Paid-By/Staff options are project-company-
  // scoped, so they only populate once a project is picked, and Company
  // Fund is deliberately NOT offered here (matches CrudPanel's own
  // Project-Expenses config -- that checkbox only ever applies to a
  // Company/overhead expense).
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({});
  const [expenseSaving, setExpenseSaving] = useState(false);
  // User-requested: an Investor is hidden from Paid By by default -- this
  // reveals them for the rare genuine one-off exception.
  const [expenseShowAllPartners, setExpenseShowAllPartners] = useState(false);
  // User-requested: same quick-add shortcut, but for Extra Work bucket
  // expenses specifically -- shares AddExtraWorkExpenseModal with the
  // Extra Works page's own "Add Expenses" button, so both places use the
  // exact same form.
  const [ewExpenseOpen, setEwExpenseOpen] = useState(false);
  const [expensePartners, setExpensePartners] = useState([]);
  const [expenseBankAccounts, setExpenseBankAccounts] = useState([]);
  const [expenseStaff, setExpenseStaff] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  useEffect(() => {
    if (!expenseOpen) return;
    Promise.all([
      api.get('/partners'),
      api.get('/bank-accounts'),
      api.get('/staff'),
      api.get('/expense-categories', { params: { scope: 'project' } }),
    ]).then(([p, b, s, c]) => {
      setExpensePartners(p.data); setExpenseBankAccounts(b.data); setExpenseStaff(s.data); setExpenseCategories(c.data);
    });
  }, [expenseOpen]);
  // User-requested: Company is now picked first and drives Project (same
  // "pick the anchor first" cascade this session already applied to
  // Security & Refundables/Partner Settlement), rather than deriving the
  // company from whichever project happened to be picked.
  const expenseCompanyId = expenseForm.company_id;
  // User-reported real gap: an already Approved & Locked project (backend
  // already rejects a new expense against it, _assert_project_unlocked
  // inside _create) still showed up here, letting the field be picked
  // before hitting that late 400 -- excluded here so the dropdown never
  // offers a target the Save button can't actually accept.
  const expenseProjectOptions = items.filter(p => p.company_id === expenseCompanyId && !isProjectLocked(p));
  const expensePartnerOptions = eligiblePaidByPartners(expensePartners, expenseCompanyId, expenseForm.project_id, expenseShowAllPartners, expenseForm.paid_by_partner_id);
  const expenseBankOptions = expenseBankAccounts.filter(b => b.company_id === expenseCompanyId);
  const expenseStaffOptions = forCompany(expenseStaff, expenseCompanyId);
  const expenseCategoryOptions = mastersForCompany(expenseCategories, expenseCompanyId);
  const saveExpense = async () => {
    if (expenseSaving) return;
    // User-requested: clicking Save with a mandatory field still blank
    // used to just do nothing (the button was disabled with no
    // explanation) -- now it's always clickable and names exactly what's
    // missing.
    if (!expenseForm.company_id) { toast.error('Company is required.'); return; }
    if (!expenseForm.project_id) { toast.error('Project is required.'); return; }
    if (!expenseForm.expense_date) { toast.error('Date is required.'); return; }
    if (!expenseForm.category) { toast.error('Category is required.'); return; }
    if (!expenseForm.amount) { toast.error('Amount is required.'); return; }
    if (!/^\d+$/.test(expenseForm.voucher_number||'')) { toast.error('Voucher number is required and must contain digits only.'); return; }
    if (!expenseForm.paid_by_partner_id && !expenseForm.bank_account_id) { toast.error('Paid By is required — choose the partner who personally paid this, or the bank account it was paid from.'); return; }
    if (expenseForm.staff_payment_type && !expenseForm.staff_id) { toast.error('Staff Member is required when a Payment Type is set.'); return; }
    { const vp = vendorStaffProblem(expenseForm, expenseForm.category); if (vp) { toast.error(vp); return; } }
    setExpenseSaving(true);
    try {
      const payload = { ...expenseForm, vendor_name: cleanVendor(expenseForm.vendor_name), amount: Number(expenseForm.amount || 0) };
      await api.post('/expenses', payload);
      if (entryOnly) setRecentEntries(r => [`Voucher ${expenseForm.voucher_number} · ${(items.find(x => x.id === expenseForm.project_id) || {}).project_name || 'Project'} · ${expenseForm.category} · ${fmtINR(payload.amount)}`, ...r].slice(0, 15));
      // User-requested: stay open and reset to the same opening defaults
      // (today's date + single-company default) instead of closing, so
      // several expenses can be entered back-to-back without re-clicking
      // "Add Expense" each time.
      setExpenseForm({ expense_date: new Date().toISOString().slice(0,10), company_id: companies.length===1?companies[0].id:'' });
      toast.success('Expense added — ready for the next one');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setExpenseSaving(false); }
  };
  // The Data Entry dashboard tiles link here as /app/projects?add=expense|ew and
  // land straight in the matching form (once, then the parameter is cleared).
  const [searchParams, setSearchParams] = useSearchParams();
  const autoAddDone = useRef(false);
  useEffect(() => {
    const what = searchParams.get('add');
    if (!what || autoAddDone.current || !companies.length || !(canEdit || isDataEntry(user))) return;
    autoAddDone.current = true;
    if (what === 'expense') { setExpenseForm({ expense_date: new Date().toISOString().slice(0,10), company_id: companies.length===1?companies[0].id:'' }); setExpenseOpen(true); }
    else if (what === 'ew') setEwExpenseOpen(true);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, companies.length]);
  const statusOptions = (masters.project_status?.map(m=>m.name)) || DEFAULT_PROJECT_STATUSES;
  const categoryOptions = (masters.work_category?.map(m=>m.name)) || DEFAULT_WORK_CATEGORIES;
  // Narrowed to the selected company (same convention as every other
  // company-scoped master picker in the app) once one's picked; shows
  // every client across every company otherwise.
  const clientOptions = (companyF.all ? mastersForCompany(masters.department_client, '') : companyF.ids.flatMap(id => mastersForCompany(masters.department_client, id)).filter((m, i, a) => a.findIndex(x => x.id === m.id) === i)).slice().sort((a,b)=>a.name.localeCompare(b.name));
  const showMoney = PROJECT_MONEY_ROLES.includes(user.role);
  const [statsById, setStatsById] = useState({});
  // Print dialog: which optional money columns go on the printout (kept for the session; all on by default).
  const [printOpen, setPrintOpen] = useState(false);
  const [printOpts, setPrintOpts] = usePersistedState('Projects.printOpts', { spent: true, received: true, receivable: true, cash: true });
  const [view, setView] = usePersistedState('Projects.view', 'cards');
  const [cardSort, setCardSort] = usePersistedState('Projects.cardSort', 'name');
  const load = async () => {
    setItems((await api.get('/projects')).data);
    // Per-project money comes from the same Dashboard figures (same calc engine);
    // a nice-to-have, so a failure just leaves the money lines out of the cards.
    if (showMoney) {
      try {
        const d = (await api.get('/dashboard')).data;
        const m = {};
        (d.projects || []).forEach(r => { m[r.project.id] = { ...r.totals, cash_realised_profit: r.cash_backing ? r.cash_backing.cash_realised_profit : null }; });
        setStatsById(m);
      } catch (e) { /* cards simply show no money lines */ }
    }
  };
  useEffect(() => { load(); }, []);
  useLiveRefresh(() => load());
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    if (!form.project_name?.trim()) { toast.error('Project Name is required.'); return; }
    setSaving(true);
    const isNew = !form.id;
    try {
      const payload = { ...form, contract_value: Number(form.contract_value||0), gst_amount: Number(form.gst_amount||0), billed_amount: Number(form.billed_amount||0), profit_adjustment: Number(form.profit_adjustment||0) };
      if (form.id) await api.patch(`/projects/${form.id}`, payload);
      else await api.post('/projects', payload);
      load();
      // User-requested: after adding a NEW project (not editing an
      // existing one), stay open and reset to the same seed the "Add
      // Project" button itself uses, so several can be entered in a row.
      if (isNew) { setForm({partnership_model:'equal_capital_bonus',status:'planning',work_category:'Tender Work'}); toast.success('Saved — ready for the next one'); }
      else { setOpen(false); setForm({}); toast.success('Saved'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  // User-requested: a project that turned out to never need full tender
  // treatment (no receipts, no bills ever raised) demoted to an Extra
  // Work bucket, dragging its already-recorded Expenses along for free.
  // master_admin-only, matching the backend's own require_master_admin()
  // gate -- every other condition (no receipts/bills/roles/deductions/
  // partner-txns/profit-distributions, not locked, at least one expense)
  // is enforced server-side; this modal only asks for Client Name when
  // the project doesn't already have one (mandatory for any Extra Work
  // bucket, same rule Add Extra Work already enforces).
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertClientName, setConvertClientName] = useState('');
  const [converting, setConverting] = useState(false);
  const openConvert = (p) => { setConvertTarget(p); setConvertClientName(p.client_name || ''); };
  const doConvert = async () => {
    if (converting) return;
    setConverting(true);
    try {
      const r = await api.post(`/projects/${convertTarget.id}/convert-to-extra-work`, { client_name: convertClientName || null });
      toast.success(`Converted to an Extra Work bucket — ${r.data.expenses_moved} expense(s) carried over unchanged.`);
      setConvertTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setConverting(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.project_name}"? This permanently removes the project and cannot be undone.`)) return;
    try { await api.delete(`/projects/${p.id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const [flagsMap] = useEntityFlagsMap('project', entryOnly ? [] : items.map(p=>p.id));
  const availableFYs = distinctFYs(items.map(p => p.financial_year));
  const visibleItems = items
    .filter(p => !followUpOnly || p.needs_follow_up)
    .filter(p => !flaggedOnly || (flagsMap[p.id]||[]).length > 0)
    .filter(p => companyF.matches(p.company_id))
    .filter(p => allStatusesSelected || statusFilterIds.includes(p.status))
    .filter(p => categoryF.matches(p.work_category))
    .filter(p => yearF.matches(p.financial_year))
    .filter(p => clientF.matches(clientIdOfProject(p, masters.department_client)))
    .filter(p => !searchQuery.trim() || [p.project_name, p.tender_number, p.client_name].some(v => (v||'').toLowerCase().includes(searchQuery.trim().toLowerCase())));
  const followUpCount = items.filter(p=>p.needs_follow_up).length;
  const flaggedCount = items.filter(p=>(flagsMap[p.id]||[]).length > 0).length;
  const { sorted: sortedItems, sortKey, sortDir, toggleSort } = useSortableRows(visibleItems, 'project_name', 'asc');
  // Money columns that will appear on the PRINTOUT only (the on-screen table is unchanged). Same per-project figures as the
  // cards / Dashboard; a total shows only when every listed project has its figure loaded.
  const printCols = showMoney ? PRINT_MONEY_COLS.filter(([k]) => printOpts[k] ?? true) : [];
  const moneyOf = (p, field) => statsById[p.id] ? Number(statsById[p.id][field] || 0) : null;
  const moneyTotal = (field) => sortedItems.some(p => moneyOf(p, field) === null) ? null : sortedItems.reduce((sum, p) => sum + moneyOf(p, field), 0);
  // Which filters narrow the list right now (shown on the PDF and the paper printout header).
  const activeFilterSummary = [
    needsPicker && !companyF.all ? `Company: ${companyF.ids.map(companyName).join(', ')}` : null,
    !allStatusesSelected ? `Status: ${statusFilterIds.length ? statusFilterIds.map(st => st.replace(/_/g, ' ')).join(', ') : 'none'}` : null,
    !categoryF.all ? `Category: ${categoryF.ids.join(', ') || 'none'}` : null,
    !clientF.all ? `Client: ${clientF.ids.map(id => clientOptions.find(c => c.id === id)?.name || '—').join(', ') || 'none'}` : null,
    !yearF.all ? `Year: ${yearF.ids.join(', ') || 'none'}` : null,
    searchQuery.trim() ? `Search "${searchQuery.trim()}"` : null,
    followUpOnly ? 'Needs follow-up only' : null,
    flaggedOnly ? 'Flagged only' : null,
  ].filter(Boolean).join(' · ');
  const downloadPdf = async () => {
    setPrintOpen(false);
    try {
      await exportProjectsPDF({
        rows: sortedItems.map(p => ({ p, money: Object.fromEntries(printCols.map(([k, , field]) => [k, moneyOf(p, field)])) })),
        totals: Object.fromEntries(printCols.map(([k, , field]) => [k, moneyTotal(field)])),
        printCols, needsPicker, companyName,
        contractTotal: sortedItems.reduce((sum, p) => sum + Number(p.contract_value || 0), 0),
        summary: activeFilterSummary, shown: visibleItems.length, all: items.length,
      });
    } catch (e) { toast.error('Could not build the PDF'); }
  };
  const doPrint = () => { setPrintOpen(false); setTimeout(() => { if (printCols.length) printLandscape(); else window.print(); }, 150); };
  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
 <div><div className="section-eyebrow mb-2">{entryOnly ? 'Data Entry' : 'Tenders'}</div><h1 className="serif text-4xl">{entryOnly ? 'Project Expenses' : 'Projects'}</h1></div>
        <div className="flex flex-wrap items-center gap-2">
          {!entryOnly && <>
          <input className="input" style={{width:220}} placeholder="Search projects…" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} data-testid="project-search"/>
          {needsPicker && <MultiFilterPicker filter={companyF} items={companies.map(c=>({id:c.id, name:c.name}))} allLabel="All Companies" width={170} testId="project-filter-company" onChange={clientF.reset}/>}
          <div data-testid="project-filter-status">
            <GrantPicker
              items={statusOptions.map(s=>({id:s, name:s.replace(/_/g,' ')}))}
              selectedIds={statusFilterIds}
              allAccess={allStatusesSelected}
              onToggle={s => setStatusFilterIds(cur => cur.includes(s) ? cur.filter(x=>x!==s) : [...cur, s])}
              onSetAllAccess={v => { setAllStatusesSelected(v); if (v) setStatusFilterIds([]); }}
              allLabel="All Statuses"
              noneWidth={160}
            />
          </div>
          <MultiFilterPicker filter={categoryF} items={categoryOptions.map(c=>({id:c, name:c}))} allLabel="All Categories" width={170} testId="project-filter-category"/>
          {/* User-requested: client-wise search -- a dedicated dropdown
              alongside the other filters, narrowed to the selected
              company's own clients once one's picked. */}
          <MultiFilterPicker filter={clientF} items={clientOptions.map(c=>({id:c.id, name:c.name}))} allLabel="All Clients" width={180} testId="project-filter-client"/>
          <MultiFilterPicker filter={yearF} items={availableFYs.map(y=>({id:y, name:y}))} allLabel="All Years" width={130} testId="project-filter-fy"/>
          <ClearFiltersButton filters={[companyF, categoryF, clientF, yearF]} extraActive={!allStatusesSelected} onClear={() => { setAllStatusesSelected(true); setStatusFilterIds([]); }}/>
          {followUpCount > 0 && <button onClick={()=>setFollowUpOnly(v=>!v)} className={"tab btn-small "+(followUpOnly?'active-amber':'')}>Needs Follow-up ({followUpCount})</button>}
          {flaggedCount > 0 && <button onClick={()=>setFlaggedOnly(v=>!v)} className={"tab btn-small "+(flaggedOnly?'active-red':'')}>Flagged ({flaggedCount})</button>}
          <span style={{width:1,alignSelf:'stretch',minHeight:24,background:'var(--border-subtle)',margin:'0 2px'}}/>
          {view === 'cards' && <select className="input" style={{width:190}} value={cardSort} onChange={e=>setCardSort(e.target.value)} data-testid="project-card-sort" title="Order of the cards">
            <option value="name">Sort: Name A–Z</option>
            <option value="contract">Sort: Largest contract</option>
            {showMoney && <option value="profit">Sort: Highest profit</option>}
            <option value="ending">Sort: Ending soonest</option>
          </select>}
          <div className="inline-flex gap-1" role="group" aria-label="View">
            <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='cards'?'active':'')} onClick={()=>setView('cards')} data-testid="project-view-cards"><LayoutGrid size={13}/>Cards</button>
            <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='table'?'active':'')} onClick={()=>setView('table')} data-testid="project-view-table"><ListIcon size={13}/>Table</button>
          </div>
          <button onClick={()=>showMoney ? setPrintOpen(true) : window.print()} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="project-print"><Printer size={14}/>Print</button>
          </>}
          {/* User-requested: a Data Entry login can Add here too (backend
              already allows it, see _crud_endpoints' extra_create_roles),
              even though it fails the regular canEdit check that also
              gates Edit/Delete elsewhere on this page. */}
          {(canEdit || isDataEntry(user)) && <button onClick={()=>{setExpenseForm({expense_date:new Date().toISOString().slice(0,10), company_id: companies.length===1?companies[0].id:''});setExpenseOpen(true);}} className="btn-red btn-small inline-flex items-center gap-1" data-testid="add-expense-btn"><Plus size={14}/>Add Expense</button>}
          {(canEdit || isDataEntry(user)) && <button onClick={()=>setEwExpenseOpen(true)} className="btn-amber btn-small inline-flex items-center gap-1" data-testid="add-ew-expense-btn"><Plus size={14}/>Add EW Expenses</button>}
          {canEdit && <button onClick={()=>{setForm({partnership_model:'equal_capital_bonus',status:'planning',work_category:'Tender Work'});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-project-btn"><Plus size={14}/>Add Project</button>}
        </div>
      </div>
      {entryOnly && (
        <div className="card card-dark" data-testid="de-projects-note">
          <div className="text-sm">Use <b>Add Expense</b> to enter an expense for a project, or <b>Add EW Expenses</b> for an Extra Work bucket. Pick the company, then the project, and fill in the voucher details exactly as written on the voucher. The form stays open after each save so you can enter the next one.</div>
          <DataEntryRecent items={recentEntries}/>
        </div>
      )}
      {!entryOnly && <ProjectKpis visible={visibleItems} statsById={statsById} showMoney={showMoney}/>}
      {!entryOnly && view === 'cards' && (
        <div className="no-print" data-testid="project-cards">
          <div className="text-xs text-muted mb-3">Showing <b>{visibleItems.length}</b> of <b>{items.length}</b> projects{visibleItems.length !== items.length ? ' (filtered)' : ''}</div>
          {visibleItems.length === 0
            ? <div className="card card-dark text-center text-muted py-10">{items.length===0 ? 'No projects yet.' : searchQuery.trim() ? `No projects match "${searchQuery}".` : 'No projects match these filters.'}</div>
            : <ProjectCards items={sortProjectCards(visibleItems, cardSort, statsById)} statsById={statsById} flagsMap={flagsMap} showMoney={showMoney} canEdit={canEdit} isMaster={isMasterAdmin(user)} needsPicker={needsPicker} companyName={companyName} todayIso={new Date().toLocaleDateString('en-CA')}
                onEdit={p=>{if(!confirmUnlockedEdit(p))return;setForm(p);setOpen(true);}} onConvert={openConvert} onDelete={del}/>}
        </div>
      )}
      {!entryOnly && (
      <div className={"card card-dark overflow-x-auto print-area " + (view === 'cards' ? 'vendor-table-print-only' : '')} data-testid="project-table">
        <div className="print-header">
          <div className="print-company">Projects</div>
          <div className="print-report-title">{visibleItems.length} of {items.length} projects</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}{activeFilterSummary ? ` · Filters: ${activeFilterSummary}` : ''}</div>
        </div>
        <div className="text-xs text-muted mb-2 no-print" data-testid="project-count">Showing <b>{visibleItems.length}</b> of <b>{items.length}</b> projects{visibleItems.length !== items.length ? ' (filtered)' : ''}</div>
        <table className="tbl">
          <thead><tr><th></th><SortTh label="Project" sortKeyName="project_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/><SortTh label="Client" sortKeyName="client_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>{needsPicker && <th>Company</th>}<SortTh label="Contract" sortKeyName="contract_value" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>{printCols.map(([k, label]) => <th key={k} className="print-only text-right" data-testid={`print-col-${k}`}>{label}</th>)}<SortTh label="Category" sortKeyName="work_category" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/><SortTh label="Model" sortKeyName="partnership_model" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/><SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/><SortTh label="FY" sortKeyName="financial_year" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/><th className="no-print"></th></tr></thead>
          <tbody>
            {visibleItems.length===0 && <tr><td colSpan="9" className="text-center text-muted py-8">{items.length===0 ? 'No projects yet.' : searchQuery.trim() ? `No projects match "${searchQuery}".` : 'No projects match these filters.'}</td></tr>}
            {sortedItems.map(p => (
              <tr key={p.id} data-testid={`project-row-${p.id}`}>
                <td>{p.needs_follow_up && <span title="Needs follow-up" className="text-red-400">●</span>}</td>
                <td><Link to={`/app/projects/${p.id}`} className="serif text-base hover:text-[color:var(--gold)]">{p.project_name}</Link><div className="text-xs text-muted">{p.tender_number||'—'}</div><FlagDots flags={flagsMap[p.id]} followUp={p.needs_follow_up}/></td>
                <td>{p.client_name||'—'}</td>
                {needsPicker && <td className="text-xs">{companyName(p.company_id)}</td>}
                <td className="mono">{fmtINR(p.contract_value)}</td>
                {printCols.map(([k, , field]) => <td key={k} className="print-only mono text-right">{moneyOf(p, field) === null ? '—' : fmtINR(moneyOf(p, field))}</td>)}
                <td className="text-xs">{p.work_category||'—'}</td>
                <td className="text-xs">{p.partnership_model} {p.model_b_type ? `(${p.model_b_type})` : ''}</td>
                <td><ProjectStatusBadge status={p.status}/></td>
                <td className="text-xs">{p.financial_year}</td>
                <td className="whitespace-nowrap no-print">
                  {canEdit && <button aria-label="Edit" onClick={()=>{if(!confirmUnlockedEdit(p))return;setForm(p);setOpen(true);}} className="tab btn-small mr-1" data-testid={`edit-project-${p.id}`}><Edit3 size={12}/></button>}
                  {isMasterAdmin(user) && !p.is_extra_work && <button aria-label="Convert to Extra Work" title="Convert to Extra Work bucket" onClick={()=>openConvert(p)} className="tab btn-small mr-1" data-testid={`convert-ew-${p.id}`}><ArrowRightLeft size={12}/></button>}
                  {isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(p)} className="tab btn-small text-red-300 mr-1" data-testid={`delete-project-${p.id}`}><Trash2 size={12}/></button>}
                  <Link to={`/app/projects/${p.id}`} className="tab btn-small">Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
          {/* User-requested: a total for Contract Value, matching the
              tfoot convention Reports' own Company/Department/Partner-
              Wise breakdowns already use -- summed over whatever's
              currently visible (filtered + searched), not the full
              unfiltered list, same as every other total on this page
              (the "Showing X of Y" line just above already follows the
              same filtered-not-total convention). */}
          {sortedItems.length > 0 && (
            <tfoot>
              <tr style={{fontWeight:600, borderTop:'2px solid var(--border-subtle)'}}>
                <td colSpan={needsPicker ? 4 : 3} className="text-right text-xs text-muted">{printCols.length ? <><span className="no-print">Total Contract Value</span><span className="print-only-inline">Totals</span></> : 'Total Contract Value'}</td>
                <td className="mono">{fmtINR(sortedItems.reduce((sum,p)=>sum+Number(p.contract_value||0),0))}</td>
                {printCols.map(([k, , field]) => <td key={k} className="print-only mono text-right">{moneyTotal(field) === null ? '—' : fmtINR(moneyTotal(field))}</td>)}
                <td colSpan={5} className="no-print"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}
      <Modal open={printOpen} onClose={()=>setPrintOpen(false)} title="Print projects" maxW={520}>
        <div data-testid="project-print-dialog">
          <p className="text-sm text-muted mb-4">Prints the <b>{visibleItems.length}</b> project{visibleItems.length === 1 ? '' : 's'} shown now (your filters and search apply), with each project's contract value. Tick the figures you also want as columns:</p>
          {[['spent', 'Spent', 'what has been paid out on the project so far'], ['received', 'Received', 'money received so far'], ['receivable', 'Receivable', 'still to be received, as on the Dashboard'], ['cash', 'Profit in cash', 'profit actually collected so far (receipts − costs) — the part partners can be paid from']].map(([k, label, hint]) => (
            <label key={k} className="flex items-start gap-3 mb-3 cursor-pointer">
              <input type="checkbox" className="mt-1" checked={printOpts[k] ?? true} onChange={e => setPrintOpts({ ...printOpts, [k]: e.target.checked })} data-testid={`print-opt-${k}`}/>
              <span><span className="text-sm">{label}</span><span className="block text-xs text-muted">{hint}</span></span>
            </label>
          ))}
          {Object.keys(statsById).length === 0 && <div className="text-xs mb-3" style={{ color: 'var(--accent-amber)' }}>The money figures are still loading — they'll show as "—" until they arrive.</div>}
          <div className="mt-5 flex flex-wrap justify-end gap-2"><button onClick={()=>setPrintOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={downloadPdf} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="project-print-pdf"><Download size={14}/>Download PDF</button><button onClick={doPrint} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="project-print-go"><Printer size={14}/>Print</button></div>
        </div>
      </Modal>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id ? 'Edit Project' : 'Add Project'} maxW={860}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Round 31 audit finding: client_name/department are now purely
              derived from the Department/Client master (see the onChange
              below) -- but switching Company here only ever cleared
              department_client_id, not the two derived fields, so the
              disabled Client/Department boxes kept showing the OLD
              company's master's text with no selection backing it. Saving
              without re-picking a new master under the new company then
              silently wrote that stale client_name/department, now
              belonging to the wrong company, alongside a null
              department_client_id -- reintroducing, via a company switch,
              the exact client/master drift this whole change was meant to
              eliminate. */}
          <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker} clear={['department_client_id', 'client_name', 'department']}/>
          <Field label="Project Name *"><input className="input" data-testid="proj-name" value={form.project_name||''} onChange={e=>setForm({...form, project_name:e.target.value})}/></Field>
          <Field label="Work Category">
            {/* User-reported real gap: mastersForCompany(list, companyId)
                deliberately returns the WHOLE unfiltered list when
                companyId is falsy (the right behavior for a genuine
                "no filter" picker elsewhere) -- but needsPicker starts
                this form with company_id unset, so before a company was
                actually chosen, this dropdown was showing every OTHER
                company's custom Work Category masters blended together.
                Picking one before choosing Company saved a value that
                belonged to a different company's master data entirely.
                Gated behind an explicit company pick first, same
                disabled-placeholder convention every other "pick X
                first" dependent field in this app already uses. */}
            {!form.company_id ? (
              <select className="input" disabled><option>— select a company first —</option></select>
            ) : (
              <select className="input" value={form.work_category||'Tender Work'} onChange={e=>setForm({...form, work_category:e.target.value})} data-testid="proj-work-category">
                {mastersForCompany(masters.work_category, form.company_id).length
                  ? mastersForCompany(masters.work_category, form.company_id).map(m=><option key={m.id} value={m.name}>{m.name}</option>)
                  : DEFAULT_WORK_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <div className="text-xs text-muted mt-1">How this project came to exist — customize the list in Settings &gt; Masters &gt; Work Category.</div>
          </Field>
          <Field label="Tender Number"><input className="input" value={form.tender_number||''} onChange={e=>setForm({...form, tender_number:e.target.value})}/></Field>
          <Field label="Department / Client">{!form.company_id ? (
            <select className="input" disabled><option>— select a company first —</option></select>
          ) : (
          <select className="input" value={form.department_client_id||''} onChange={e=>{
            const m = mastersForCompany(masters.department_client, form.company_id).find(x=>x.id===e.target.value);
            setForm({...form, department_client_id:e.target.value, client_name:m?.name||'', department:m?.client_organisation||m?.name||''});
          }}>
            <option value="">— select —</option>
            {mastersForCompany(masters.department_client, form.company_id).map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          )}</Field>
          {/* Client and Department are no longer freely typed -- both are
              fetched from the selected Department / Client master above
              (Settings > Masters), so a project's client name can never
              drift from what that master actually says. Add one under
              Settings > Masters > Department / Client first if it isn't
              in the list yet. */}
          <Field label="Client"><input className="input" disabled value={form.client_name||''} placeholder="— select Department / Client above —"/></Field>
          <Field label="Department"><input className="input" disabled value={form.department||''} placeholder="— select Department / Client above —"/></Field>
          <Field label="Work Order"><input className="input" value={form.work_order_number||''} onChange={e=>setForm({...form, work_order_number:e.target.value})}/></Field>
          {/* Full-app audit finding: this backend field had no input
              anywhere in the app -- permanently null for every project
              created or edited through the normal UI, only ever settable
              via backup-restore import. save() already spreads the full
              form object, so this is the only change needed. */}
          <Field label="Project Head"><input className="input" value={form.project_head||''} onChange={e=>setForm({...form, project_head:e.target.value})}/></Field>
          <Field label="Contract Value (₹)"><input className="input mono" type="number" min="0" value={form.contract_value||''} onChange={e=>setForm({...form, contract_value:e.target.value})}/></Field>
          <Field label="GST Amount (₹)"><input className="input mono" type="number" min="0" value={form.gst_amount||''} onChange={e=>setForm({...form, gst_amount:e.target.value})}/></Field>
          <Field label="Billed Amount (₹)"><input className="input mono" type="number" min="0" value={form.billed_amount||''} onChange={e=>setForm({...form, billed_amount:e.target.value})}/></Field>
          <Field label="Billing Cycle">
            <select className="input" value={form.billing_frequency||'monthly'} onChange={e=>setForm({...form, billing_frequency:e.target.value})}>
              {Object.entries(BILLING_FREQUENCY_LABELS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
            <div className="text-xs text-muted mt-1">{(form.billing_frequency||'monthly') === 'monthly'
              ? <><b>Monthly:</b> the Contract Value is filled in automatically as the total of the bills you raise, and follows every bill added, changed or deleted — a value typed above is replaced once a bill exists. Choose another cycle for a contract with a fixed value.</>
              : 'Does not affect calculations or due dates.'}</div>
          </Field>
          <Field label="Profit Adjustment (₹)">
            <input className="input mono" type="number" value={form.profit_adjustment||''} onChange={e=>setForm({...form, profit_adjustment:e.target.value})}/>
            <div className="text-xs text-muted mt-1">Adds to Distributable Profit alongside — not instead of — anything logged in the project's Profit Adjustments tab. Most projects should use the tab (it keeps a reason and audit trail) and leave this at 0.</div>
          </Field>
          <Field label="Start Date"><input className="input" type="date" value={form.start_date||''} onChange={e=>setForm({...form, start_date:e.target.value})}/></Field>
          <Field label="End Date"><input className="input" type="date" value={form.end_date||''} onChange={e=>setForm({...form, end_date:e.target.value})}/></Field>
          <div className="col-span-2">
            <div className="text-xs text-muted mb-1.5 uppercase tracking-wider">Quick Setup</div>
            <div className="flex gap-2 flex-wrap">
              {PARTNERSHIP_MODEL_PRESETS.map(t => <button key={t.key} type="button" onClick={()=>setForm({...form, ...t.fields})} className={"tab btn-small "+((form.partnership_model||'equal_capital_bonus')===t.key?'active':'')}>{t.label}</button>)}
            </div>
          </div>
          <Field label="Partnership Model">
            <select className="input" value={form.partnership_model||'equal_capital_bonus'} onChange={e=>setForm({...form, partnership_model:e.target.value})}>
              <option value="equal_capital_bonus">{PARTNERSHIP_MODEL_LABELS.equal_capital_bonus}</option>
              <option value="money_work">{PARTNERSHIP_MODEL_LABELS.money_work}</option>
              <option value="equal_variable">{PARTNERSHIP_MODEL_LABELS.equal_variable}</option>
            </select>
            <div className="text-xs text-muted mt-1">{PARTNERSHIP_MODEL_HELP[form.partnership_model||'equal_capital_bonus']}</div>
          </Field>
          {form.partnership_model === 'equal_variable' && (
            <Field label="Split">
              <select className="input" value={form.model_b_type||'equal'} onChange={e=>setForm({...form, model_b_type:e.target.value})}>
                {BASE_SPLIT_OPTIONS.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          )}
          {form.partnership_model === 'equal_capital_bonus' && (
            <Field label="Base Split (before the investment bonus)">
              <select className="input" value={form.capital_bonus_base||'equal'} onChange={e=>setForm({...form, capital_bonus_base:e.target.value})}>
                {BASE_SPLIT_OPTIONS.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          )}
          {form.partnership_model === 'money_work' && (
            <Field label="Work Pool Split">
              <select className="input" value={form.work_allocation||'equal'} onChange={e=>setForm({...form, work_allocation:e.target.value})}>
                {BASE_SPLIT_OPTIONS.map(([v,l])=><option key={v} value={v}>{l.replace('partners','work partners')}</option>)}
              </select>
            </Field>
          )}
          <Field label="Status">
            {/* Locked statuses (profit_approved/settled/closed) excluded as
                selectable targets — the backend always rejects setting them
                through this form, on both create and edit; reaching them
                requires Approve, and moving further requires Advance/Reopen
                on the project's own page. The project's own current status
                stays in the list even if locked, so editing an already-
                locked project's other fields (name, notes, ...) doesn't show
                a dropdown that silently misrepresents its real status. */}
            <select className="input" value={form.status||'planning'} onChange={e=>setForm({...form, status:e.target.value})}>
              {statusOptions.filter(s=>s===form.status||!isProjectLocked({status:s})).map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </select>
          </Field>
          <div className="col-span-2 border-t border-white/10 pt-4 mt-1">
            <div className="text-xs text-muted mb-1">Tender Lifecycle Tracking (optional — independent of the Status above)</div>
          </div>
          {Object.entries(PROJECT_STATUS_AXES).map(([key, {label, options}]) => (
            <Field key={key} label={label}>
              <select className="input" value={form[key]||''} onChange={e=>setForm({...form, [key]:e.target.value||null})}>
                <option value="">— unset —</option>
                {options.map(o=><option key={o} value={o}>{axisLabel(o)}</option>)}
              </select>
            </Field>
          ))}
          <Field label="Needs Follow-up">
            <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
              <input type="checkbox" checked={!!form.needs_follow_up} onChange={e=>setForm({...form, needs_follow_up:e.target.checked})}/>
              Flag this project for follow-up
            </label>
          </Field>
          <div className="col-span-2"><Field label="Notes"><textarea className="input" rows={2} value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="proj-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      <Modal open={expenseOpen} onClose={()=>setExpenseOpen(false)} title="Add Project Expense" maxW={860}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* User-requested: Company first, drives Project -- clearing
              everything downstream on change since Category/Paid-By/Staff
              are all scoped to whichever company ends up selected. */}
          <Field label="Company *"><select className="input" value={expenseForm.company_id||''} onChange={e=>setExpenseForm({...expenseForm, company_id:e.target.value||null, project_id:null, category:'', paid_by_partner_id:null, bank_account_id:null, staff_id:null, staff_payment_type:null})} data-testid="pe-company">
            <option value="">— select —</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select></Field>
          <Field label="Project *"><select className="input" disabled={!expenseForm.company_id} value={expenseForm.project_id||''} onChange={e=>setExpenseForm({...expenseForm, project_id:e.target.value||null, category:'', paid_by_partner_id:null, bank_account_id:null, staff_id:null, staff_payment_type:null})} data-testid="pe-project">
            <option value="">{expenseForm.company_id ? '— select —' : '— select a company first —'}</option>
            {[...expenseProjectOptions].sort((a,b)=>a.project_name.localeCompare(b.project_name)).map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select></Field>
          {/* User-requested: Voucher No. moved up, right after Company and
              Project. */}
          <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={expenseForm.voucher_number||''} onChange={e=>setExpenseForm({...expenseForm, voucher_number:e.target.value})}/></Field>
          <Field label="Date *"><input type="date" className="input" value={expenseForm.expense_date||''} onChange={e=>setExpenseForm({...expenseForm, expense_date:e.target.value})}/></Field>
          <Field label="Category *"><select className="input" disabled={!expenseForm.project_id} value={expenseForm.category||''} onChange={e=>setExpenseForm({...expenseForm, category:e.target.value})}>
            <option value="">{expenseForm.project_id ? '— select —' : '— select a project first —'}</option>
            {expenseCategoryOptions.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
          </select></Field>
          <Field label="Amount *"><input className="input mono" type="number" min="0" value={expenseForm.amount||''} onChange={e=>setExpenseForm({...expenseForm, amount:e.target.value})}/></Field>
          <Field label="Paid By Partner *"><select className="input" disabled={!expenseForm.company_id} value={expenseForm.paid_by_partner_id||''} onChange={e=>setExpenseForm({...expenseForm, paid_by_partner_id:e.target.value||null, bank_account_id: e.target.value ? null : expenseForm.bank_account_id})}>
            <option value="">— none, paid from bank instead —</option>
            {expensePartnerOptions.map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={expenseShowAllPartners} onChange={e=>setExpenseShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
          </Field>
          <Field label="Or Bank Account *"><select className="input" disabled={!expenseForm.company_id} value={expenseForm.bank_account_id||''} onChange={e=>setExpenseForm({...expenseForm, bank_account_id:e.target.value||null, paid_by_partner_id: e.target.value ? null : expenseForm.paid_by_partner_id})}>
            <option value="">— none, paid by partner instead —</option>
            {expenseBankOptions.map(b=><option key={b.id} value={b.id}>{b.bank_name}{needsPicker ? ` — ${companies.find(c=>c.id===b.company_id)?.name||''}` : ''}</option>)}
          </select></Field>
          {expenseForm.company_id && !expenseForm.paid_by_partner_id && !expenseForm.bank_account_id && <div className="col-span-2 text-xs -mt-2" style={{color:'var(--accent-amber)'}}>Choose a partner or a bank account above — one is required.</div>}
          <Field label={vendorIsRequired(expenseForm, expenseForm.category) ? 'Vendor *' : 'Vendor'}><VendorSelect vendors={vendors} companyId={expenseForm.company_id} value={expenseForm.vendor_name} categoryName={expenseForm.category} onChange={v=>setExpenseForm({...expenseForm, vendor_name:v})}/></Field>
          {/* Payment Type first, drives Staff Member's requirement -- same
              reordered convention Company Expenses/Project Expenses/the
              Audit Queue edit modal all already use. */}
          <Field label="Payment Type"><select className="input" disabled={!expenseForm.company_id} value={expenseForm.staff_payment_type||''} onChange={e=>{ const v = e.target.value||null; setExpenseForm({...expenseForm, staff_payment_type:v, staff_id: staffAfterTypeChange(expenseStaffOptions, v, expenseForm.staff_id)}); }}>
            <option value="">— not a staff payment —</option>
            {Object.entries(STAFF_PAYMENT_TYPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
          </select></Field>
          <Field label={staffIsRequired(expenseForm, expenseForm.category) ? "Staff Member *" : "Staff Member"}><select className="input" disabled={!expenseForm.company_id} value={expenseForm.staff_id||''} onChange={e=>setExpenseForm({...expenseForm, staff_id:e.target.value||null})}>
            <option value="">{staffNoneLabel(expenseForm.staff_payment_type)}</option>
            {staffPickList(expenseStaffOptions, expenseForm.staff_payment_type, expenseForm.staff_id).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select></Field>
          <div className="col-span-2"><Field label="Description"><input className="input" value={expenseForm.description||''} onChange={e=>setExpenseForm({...expenseForm, description:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setExpenseOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={saveExpense} disabled={expenseSaving} className="btn-gold btn-small" data-testid="pe-save">{expenseSaving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      <AddExtraWorkExpenseModal open={ewExpenseOpen} onClose={()=>setEwExpenseOpen(false)} onSaved={load}/>
      <Modal open={!!convertTarget} onClose={()=>setConvertTarget(null)} title="Convert to Extra Work Bucket">
        {convertTarget && <>
          <p className="text-sm text-muted mb-4">
            This converts "<span className="text-[color:var(--gold)]">{convertTarget.project_name}</span>" into an Extra Work bucket — its already-recorded expenses carry over unchanged, and it moves from Projects to the Extra Works list. This only works when the project has no receipts, no bills, no partner roles/deductions/partner transactions/profit distribution history, isn't locked, and has at least one expense — checked server-side, so this may still be refused.
          </p>
          <Field label="Client Name *"><input className="input" value={convertClientName} onChange={e=>setConvertClientName(e.target.value)} placeholder="Required for an Extra Work bucket"/></Field>
        </>}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={()=>setConvertTarget(null)} className="btn-outline btn-small">Cancel</button>
          <button onClick={doConvert} disabled={converting || !convertClientName.trim()} className="btn-amber btn-small">{converting ? 'Converting…' : 'Convert'}</button>
        </div>
      </Modal>
    </div>
  );
}

// ============= EXTRA WORKS =============
// Real spend/billing not yet tied to a formally named project — tracked
// under a generic place/work-type label for memory until it's clear
// which project (if any, and of whatever work_category) it belongs to.
// Under the hood this is just a project with is_extra_work=true, so every
// mechanism below (expenses, receipts, investments, settlement, ...) is
// the exact same machinery ProjectDetail already uses for a real project
// — "Open" on any row goes straight to the normal project page. "Move
// into Project" is the one new operation: it folds this bucket's
// financial history into a real project's project_id (existing, or a
// freshly created one of whatever work_category actually fits — user
// feedback: most Extra Work buckets turn out to be Voucher Payment, not
// a formal tender) so its profit/loss becomes part of that project's
// numbers automatically (nothing is recalculated separately — everything
// here is computed live off project_id already).
// Real-usage-gap finding: "Move into Project" (the action that folds an
// Extra Work bucket's financial history into a real Tender/Voucher
// Payment project) only ever existed as a row action on the ExtraWorks
// LIST page. Opening a bucket ("Open" / clicking its name) lands on the
// ordinary ProjectDetail page, which gave zero indication the project
// was even an Extra Work bucket at all and offered no way to move it
// from there -- a user reviewing a just-imported bucket by opening it
// had no path back to this action short of remembering to navigate back
// to the list. Extracted out of ExtraWorks into its own component (was
// entirely local state/handlers there) so ProjectDetail can render the
// exact same modal too, not a second copy.
function MoveExtraWorkModal({ project, onClose, onMoved }) {
  const masters = useMasters();
  const [mergeTarget, setMergeTarget] = useState('');
  const [targetProjects, setTargetProjects] = useState([]);
  const [mergeMode, setMergeMode] = useState('existing');
  const [newProjectName, setNewProjectName] = useState('');
  // User-usage finding: "most cases extra works goes to voucher payment" --
  // pre-selected to that rather than "Tender Work", since a bucket already
  // NOT going through a real tender (that's the whole reason it's an Extra
  // Work bucket) graduating into a fresh "Tender Work"-categorized project
  // would usually be the wrong default, not the common case.
  const [newProjectCategory, setNewProjectCategory] = useState('Voucher Payment');
  const [moveRows, setMoveRows] = useState({ expenses: [], investments: [], bills: [] });
  const [selectedIds, setSelectedIds] = useState({ expense_ids: new Set(), investment_ids: new Set(), bill_ids: new Set() });
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    (async () => {
      const [projRes, expRes, invRes, billRes] = await Promise.all([
        api.get('/projects'),
        api.get('/expenses', { params: { project_id: project.id } }),
        api.get('/investments', { params: { project_id: project.id } }),
        api.get('/bills', { params: { project_id: project.id } }),
      ]);
      // User-requested: keep this dry/clean, no dead ends -- merging
      // requires the TARGET to be unlocked (_assert_project_unlocked on
      // merge_project/partial_merge_project), so a closed/settled/profit-
      // approved project can never actually be picked here successfully.
      setTargetProjects(projRes.data.filter(p => p.company_id === project.company_id && p.id !== project.id && !isProjectLocked(p)));
      const rows = { expenses: expRes.data, investments: invRes.data, bills: billRes.data };
      setMoveRows(rows);
      // Everything pre-selected by default -- matches "move it totally" as
      // the natural outcome when nothing is unchecked.
      setSelectedIds({
        expense_ids: new Set(rows.expenses.map(r => r.id)),
        investment_ids: new Set(rows.investments.map(r => r.id)),
        bill_ids: new Set(rows.bills.map(r => r.id)),
      });
    })();
  }, [project.id, project.company_id]);

  const toggleMoveRow = (kind, id) => setSelectedIds(s => {
    const next = new Set(s[kind]);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { ...s, [kind]: next };
  });
  const totalMoveRows = moveRows.expenses.length + moveRows.investments.length + moveRows.bills.length;
  const allMoveRowsSelected = totalMoveRows > 0
    && selectedIds.expense_ids.size === moveRows.expenses.length
    && selectedIds.investment_ids.size === moveRows.investments.length
    && selectedIds.bill_ids.size === moveRows.bills.length;
  const selectAllMoveRows = (all) => setSelectedIds(all ? {
    expense_ids: new Set(moveRows.expenses.map(r => r.id)),
    investment_ids: new Set(moveRows.investments.map(r => r.id)),
    bill_ids: new Set(moveRows.bills.map(r => r.id)),
  } : { expense_ids: new Set(), investment_ids: new Set(), bill_ids: new Set() });

  const doMerge = async () => {
    if (merging) return;
    if (mergeMode === 'new' && !newProjectName.trim()) { toast.error('Enter a name for the new project'); return; }
    if (mergeMode === 'existing' && !mergeTarget) { toast.error('Choose a project to move into'); return; }
    setMerging(true);
    try {
      let targetId = mergeTarget;
      let targetIsNew = false;
      if (mergeMode === 'new') {
        const created = await api.post('/projects', { project_name: newProjectName.trim(), company_id: project.company_id, work_category: newProjectCategory });
        targetId = created.data.id;
        targetIsNew = true;
      }
      // Only a genuine subset (something left unchecked) uses the partial
      // endpoint, which leaves the bucket open; an empty bucket or every
      // row still checked goes through the existing full merge unchanged,
      // which closes the bucket -- see partial-merge-into's own comment
      // in server.py for why this split is the right one.
      if (totalMoveRows > 0 && !allMoveRowsSelected) {
        const r = await api.post(`/projects/${project.id}/partial-merge-into/${targetId}`, {
          expense_ids: [...selectedIds.expense_ids],
          investment_ids: [...selectedIds.investment_ids],
          bill_ids: [...selectedIds.bill_ids],
        });
        const moved = Object.entries(r.data.moved).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`).join(', ') || 'nothing';
        toast.success(`Moved into ${targetIsNew ? 'new project ' : ''}${r.data.target_project_name}: ${moved}. The rest stays in this bucket.`);
      } else {
        const r = await api.post(`/projects/${project.id}/merge-into/${targetId}`);
        const moved = Object.entries(r.data.moved).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`).join(', ') || 'nothing — this bucket had no financial entries yet';
        toast.success(`Moved into ${targetIsNew ? 'new project ' : ''}${r.data.target_project_name}: ${moved}`);
        if (r.data.source_had_own_profit_rule) toast.warning('This bucket had its own profit-sharing rule, which was NOT carried over — check the target project\'s rule if it needs adjusting.');
        if (r.data.roles_copied > 0) toast.warning(`${r.data.roles_copied} partner role(s) were added to the target project (carried over from this bucket) — review the Partners tab to confirm each one's split is correct.`);
      }
      onMoved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setMerging(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title={`Move "${project.project_name}" into a Project`} maxW={640}>
      <div className="flex gap-2 mb-4">
        <button className={"tab btn-small " + (mergeMode === 'existing' ? 'active' : '')} onClick={() => setMergeMode('existing')} data-testid="merge-mode-existing">Existing Project</button>
        <button className={"tab btn-small " + (mergeMode === 'new' ? 'active' : '')} onClick={() => setMergeMode('new')} data-testid="merge-mode-new">New Project</button>
      </div>
      {mergeMode === 'existing' ? (
        <>
          <Field label="Move into *"><select className="input" value={mergeTarget} onChange={e => setMergeTarget(e.target.value)} data-testid="merge-target">
            <option value="">— select project —</option>
            {targetProjects.map(p => <option key={p.id} value={p.id}>{p.project_name}{p.tender_number ? ` (${p.tender_number})` : ''}</option>)}
          </select></Field>
          {targetProjects.length === 0 && <div className="text-xs text-red-400 mt-2">No other project found in this company to move into yet.</div>}
        </>
      ) : (
        <>
          <Field label="New Project Name *"><input className="input" placeholder="e.g. Site XYZ — repairs" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} data-testid="merge-new-project-name" /></Field>
          <Field label="Work Category">
            <select className="input" value={newProjectCategory} onChange={e => setNewProjectCategory(e.target.value)} data-testid="merge-new-project-category">
              {(mastersForCompany(masters.work_category, project.company_id).length
                ? mastersForCompany(masters.work_category, project.company_id).map(m=>m.name)
                : DEFAULT_WORK_CATEGORIES
              ).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </>
      )}

      {totalMoveRows > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-muted">Choose what moves now — leave the rest checked out to keep it in this bucket for later.</div>
            <div className="flex gap-2">
              <button className="tab btn-small" onClick={() => selectAllMoveRows(true)}>Select all</button>
              <button className="tab btn-small" onClick={() => selectAllMoveRows(false)}>Select none</button>
            </div>
          </div>
          <div className="border border-white/10 rounded-lg max-h-64 overflow-y-auto divide-y divide-white/5">
            {moveRows.expenses.map(r => (
              <label key={`e-${r.id}`} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer">
                <input type="checkbox" checked={selectedIds.expense_ids.has(r.id)} onChange={() => toggleMoveRow('expense_ids', r.id)} />
                <span className="badge" style={{fontSize:10}}>Expense</span>
                <span className="flex-1">{r.category?.replace(/_/g,' ') || 'expense'} — {fmtDate(r.expense_date)}</span>
                <span className="mono text-red-400">{fmtINR(r.amount)}</span>
              </label>
            ))}
            {moveRows.investments.map(r => (
              <label key={`i-${r.id}`} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer">
                <input type="checkbox" checked={selectedIds.investment_ids.has(r.id)} onChange={() => toggleMoveRow('investment_ids', r.id)} />
                <span className="badge" style={{fontSize:10}}>Investment</span>
                <span className="flex-1">{r.investment_type?.replace(/_/g,' ') || 'investment'} — {fmtDate(r.transaction_date)}</span>
                <span className="mono text-green-400">{fmtINR(r.amount)}</span>
              </label>
            ))}
            {moveRows.bills.map(r => (
              <label key={`b-${r.id}`} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer">
                <input type="checkbox" checked={selectedIds.bill_ids.has(r.id)} onChange={() => toggleMoveRow('bill_ids', r.id)} />
                <span className="badge" style={{fontSize:10}}>Bill</span>
                <span className="flex-1">{r.bill_number || 'bill'} — {fmtDate(r.bill_date)}</span>
                <span className="mono">{fmtINR(r.gross_amount)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-muted mt-4">
        {totalMoveRows === 0
          ? 'This bucket has no expenses, investments, or bills yet — the whole bucket (and any receipts, deductions, or other activity on it) moves in and is closed.'
          : allMoveRowsSelected
          ? 'Everything above is selected, so the whole bucket (plus any receipts, deductions, or other activity already tied to it) moves in and is closed — same as moving it in full.'
          : 'Only what\'s checked above moves now. This bucket stays open with everything left unchecked, ready for more entries or another move later.'}
        {' '}This can't be undone from here.
      </p>
      <div className="mt-6 flex justify-end gap-2"><button onClick={onClose} className="btn-outline btn-small">Cancel</button>
        <button onClick={doMerge} disabled={(mergeMode === 'existing' ? !mergeTarget : !newProjectName.trim()) || merging} className="btn-gold btn-small" data-testid="merge-confirm">{merging ? 'Moving…' : 'Move'}</button></div>
    </Modal>
  );
}

// User-requested: a quick "Add Expenses" for Extra Work buckets specifically
// -- shared between the Extra Works page's own toolbar and a same-shaped
// button on the Projects list page, so a user can log an Extra Work
// expense from either place without hunting for the bucket's own tab
// first. Same "pick the anchor first" cascade (Company > Extra Work
// Project > Voucher No. first) as the regular Projects-list Add Expense
// modal -- just scoped to open, unmoved Extra Work buckets (GET
// /extra-works already filters to is_extra_work=true AND
// merged_into_project_id IS NULL) instead of every project. Posts to the
// same POST /expenses every other Add Expense form uses -- an Extra Work
// bucket is just a project with is_extra_work=true, and the backend
// already allows Expenses against an unmoved bucket
// (_assert_not_unmoved_extra_work explicitly exempts Expenses/
// Investments/Bills) -- no backend change needed.
function AddExtraWorkExpenseModal({ open, onClose, onSaved }) {
  const { user } = useAuth();
  const vendors = useVendors();
  const { companies, needsPicker } = useMyCompanies();
  const [buckets, setBuckets] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [partners, setPartners] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showAllPartners, setShowAllPartners] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ expense_date: new Date().toISOString().slice(0,10), company_id: (!needsPicker && companies.length===1) ? companies[0].id : '' });
    Promise.all([
      // /extra-works carries profit figures, so a Data Entry login lists the
      // buckets through the stripped-down projects list instead.
      isDataEntry(user)
        ? api.get('/projects', { params: { include_extra_work: true } }).then(r => ({ data: { rows: (r.data || []).filter(x => x.is_extra_work).map(x => ({ project: x })) } }))
        : api.get('/extra-works'),
      api.get('/partners'),
      api.get('/bank-accounts'),
      api.get('/staff'),
      api.get('/expense-categories', { params: { scope: 'project' } }),
    ]).then(([ew, p, b, s, c]) => {
      setBuckets((ew.data?.rows || []).map(r => r.project));
      setPartners(p.data); setBankAccounts(b.data); setStaff(s.data); setCategories(c.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const companyId = form.company_id;
  // Same gap as ExpensePanel's expenseProjectOptions -- a locked EW bucket
  // shouldn't be offered here either.
  const bucketOptions = buckets.filter(p => p.company_id === companyId && !isProjectLocked(p));
  const partnerOptions = eligiblePaidByPartners(partners, companyId, form.project_id, showAllPartners, form.paid_by_partner_id);
  const bankOptions = bankAccounts.filter(b => b.company_id === companyId);
  const staffOptions = forCompany(staff, companyId);
  const categoryOptions = mastersForCompany(categories, companyId);

  const save = async () => {
    if (saving) return;
    // User-requested: clicking Save with a mandatory field still blank
    // used to just do nothing (the button was disabled with no
    // explanation) -- now it's always clickable and names exactly what's
    // missing.
    if (!form.company_id) { toast.error('Company is required.'); return; }
    if (!form.project_id) { toast.error('Extra Work Project is required.'); return; }
    if (!form.expense_date) { toast.error('Date is required.'); return; }
    if (!form.category) { toast.error('Category is required.'); return; }
    if (!form.amount) { toast.error('Amount is required.'); return; }
    if (!/^\d+$/.test(form.voucher_number||'')) { toast.error('Voucher number is required and must contain digits only.'); return; }
    if (!form.paid_by_partner_id && !form.bank_account_id) { toast.error('Paid By is required — choose the partner who personally paid this, or the bank account it was paid from.'); return; }
    if (form.staff_payment_type && !form.staff_id) { toast.error('Staff Member is required when a Payment Type is set.'); return; }
    { const vp = vendorStaffProblem(form, form.category); if (vp) { toast.error(vp); return; } }
    setSaving(true);
    try {
      const payload = { ...form, vendor_name: cleanVendor(form.vendor_name), amount: Number(form.amount || 0) };
      await api.post('/expenses', payload);
      // User-requested: stay open and reset to the same opening defaults
      // instead of closing, so several Extra Work expenses can be entered
      // back-to-back without re-clicking the Add button each time.
      setForm({ expense_date: new Date().toISOString().slice(0,10), company_id: (!needsPicker && companies.length===1) ? companies[0].id : '' });
      toast.success('Expense added — ready for the next one');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Extra Work Expense" maxW={860}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Company *"><select className="input" value={form.company_id||''} onChange={e=>setForm({...form, company_id:e.target.value||null, project_id:null, category:'', paid_by_partner_id:null, bank_account_id:null, staff_id:null, staff_payment_type:null})} data-testid="ewe-company">
          <option value="">— select —</option>
          {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select></Field>
        <Field label="Extra Work Project *"><select className="input" disabled={!form.company_id} value={form.project_id||''} onChange={e=>setForm({...form, project_id:e.target.value||null, category:'', paid_by_partner_id:null, bank_account_id:null, staff_id:null, staff_payment_type:null})} data-testid="ewe-project">
          <option value="">{!form.company_id ? '— select a company first —' : bucketOptions.length ? '— select —' : '— no open Extra Work buckets —'}</option>
          {[...bucketOptions].sort((a,b)=>a.project_name.localeCompare(b.project_name)).map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
        </select></Field>
        {/* Voucher No. right after Company/Project, matching the regular
            Add Expense form's field order. */}
        <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={form.voucher_number||''} onChange={e=>setForm({...form, voucher_number:e.target.value})}/></Field>
        <Field label="Date *"><input type="date" className="input" value={form.expense_date||''} onChange={e=>setForm({...form, expense_date:e.target.value})}/></Field>
        <Field label="Category *"><select className="input" disabled={!form.project_id} value={form.category||''} onChange={e=>setForm({...form, category:e.target.value})}>
          <option value="">{form.project_id ? '— select —' : '— select a project first —'}</option>
          {categoryOptions.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
        </select></Field>
        <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
        <Field label="Paid By Partner *"><select className="input" disabled={!form.company_id} value={form.paid_by_partner_id||''} onChange={e=>setForm({...form, paid_by_partner_id:e.target.value||null, bank_account_id: e.target.value ? null : form.bank_account_id})}>
          <option value="">— none, paid from bank instead —</option>
          {partnerOptions.map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={showAllPartners} onChange={e=>setShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
        </Field>
        <Field label="Or Bank Account *"><select className="input" disabled={!form.company_id} value={form.bank_account_id||''} onChange={e=>setForm({...form, bank_account_id:e.target.value||null, paid_by_partner_id: e.target.value ? null : form.paid_by_partner_id})}>
          <option value="">— none, paid by partner instead —</option>
          {bankOptions.map(b=><option key={b.id} value={b.id}>{b.bank_name}{needsPicker ? ` — ${companies.find(c=>c.id===b.company_id)?.name||''}` : ''}</option>)}
        </select></Field>
        {form.company_id && !form.paid_by_partner_id && !form.bank_account_id && <div className="col-span-2 text-xs -mt-2" style={{color:'var(--accent-amber)'}}>Choose a partner or a bank account above — one is required.</div>}
        <Field label={vendorIsRequired(form, form.category) ? 'Vendor *' : 'Vendor'}><VendorSelect vendors={vendors} companyId={form.company_id} value={form.vendor_name} categoryName={form.category} onChange={v=>setForm({...form, vendor_name:v})}/></Field>
        {/* Payment Type first, drives Staff Member's requirement -- same
            reordered convention every other Add Expense form uses. */}
        <Field label="Payment Type"><select className="input" disabled={!form.company_id} value={form.staff_payment_type||''} onChange={e=>{ const v = e.target.value||null; setForm({...form, staff_payment_type:v, staff_id: staffAfterTypeChange(staffOptions, v, form.staff_id)}); }}>
          <option value="">— not a staff payment —</option>
          {Object.entries(STAFF_PAYMENT_TYPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
        </select></Field>
        <Field label={staffIsRequired(form, form.category) ? "Staff Member *" : "Staff Member"}><select className="input" disabled={!form.company_id} value={form.staff_id||''} onChange={e=>setForm({...form, staff_id:e.target.value||null})}>
          <option value="">{staffNoneLabel(form.staff_payment_type)}</option>
          {staffPickList(staffOptions, form.staff_payment_type, form.staff_id).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select></Field>
        <div className="col-span-2"><Field label="Description"><input className="input" value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field></div>
      </div>
      <div className="mt-6 flex justify-end gap-2"><button onClick={onClose} className="btn-outline btn-small">Cancel</button>
        <button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="ewe-save">{saving ? 'Saving…' : 'Save'}</button></div>
    </Modal>
  );
}

// ---- Extra Works page: card view helpers ----
// The only fields an Extra Work bucket's Edit form changes. Sent on their own (never the whole row), so editing a bucket
// can never overwrite anything else on it.
const extraWorkEditPayload = (form) => ({
  project_name: (form.project_name || '').trim(),
  client_name: form.client_name,
  department_client_id: form.department_client_id || null,
  department: form.department || form.client_name || null,
  start_date: form.start_date || null,
  notes: form.notes || null,
  status: form.status,
});
// Buckets grouped by client for the printout / PDF: one section per client (A-Z, "No client" last), buckets A-Z inside it.
function groupExtraWorksByClient(rows) {
  const byClient = new Map();
  rows.forEach(r => {
    const name = (r.project.client_name || '').trim() || 'No client';
    if (!byClient.has(name)) byClient.set(name, []);
    byClient.get(name).push(r);
  });
  const nm = (r) => r.project.project_name || '';
  return [...byClient.entries()]
    .sort((a, b) => (a[0] === 'No client') - (b[0] === 'No client') || a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }))
    .map(([client, list]) => ({ client, rows: list.slice().sort((a, b) => nm(a).localeCompare(nm(b), undefined, { numeric: true, sensitivity: 'base' })) }));
}
const EW_STALE_DAYS = 90; // an Extra Work bucket left open this long is nudged: identify it and move it into a real project
// Whole days a bucket has been open: from its start date (or, failing that, when it was created).
function ewAgeDays(p, todayIso) {
  const from = p?.start_date || (p?.created_at ? String(p.created_at).slice(0, 10) : null);
  if (!from) return null;
  const d = Math.round((new Date(`${todayIso}T00:00:00`) - new Date(`${from}T00:00:00`)) / 86400000);
  return d < 0 ? 0 : d;
}
const sortExtraWorkCards = (rows, key) => {
  const c = [...rows];
  const name = (r) => r.project.project_name || '';
  const byName = (a, b) => name(a).localeCompare(name(b), undefined, { numeric: true, sensitivity: 'base' });
  if (key === 'spent') c.sort((a, b) => Number(b.totals.total_expenses || 0) - Number(a.totals.total_expenses || 0) || byName(a, b));
  else if (key === 'profit') c.sort((a, b) => Number(b.totals.project_profit || 0) - Number(a.totals.project_profit || 0) || byName(a, b));
  else if (key === 'oldest') c.sort((a, b) => String(a.project.start_date || a.project.created_at || '9999').localeCompare(String(b.project.start_date || b.project.created_at || '9999')) || byName(a, b));
  else c.sort(byName);
  return c;
};
function ExtraWorkCards({ rows, canMerge, canEdit, isMaster, needsPicker, companyName, onMerge, onDelete, onEdit, todayIso }) {
  const maxSpent = Math.max(0, ...rows.map(r => Number(r.totals.total_expenses || 0)));
  const maxReceived = Math.max(0, ...rows.map(r => Number(r.totals.total_receipts || 0)));
  const scale = Math.max(maxSpent, maxReceived);
  const pct = (v) => scale > 0 ? Math.max(Number(v || 0) > 0 ? 2 : 0, Math.round((Number(v || 0) / scale) * 100)) : 0;
  return (
    <div className="vendor-grid project-grid">
      {rows.map(({ project: p, totals: t }) => {
        const tone = projectToneOf(p.status);
        const age = ewAgeDays(p, todayIso);
        const stale = age !== null && age > EW_STALE_DAYS && PROJECT_LIVE_STATUSES.includes(p.status);
        const pending = Number(t.expenses_pending_review || 0) + Number(t.receipts_pending_review || 0);
        return (
          <div key={p.id} className="card card-dark vendor-card" style={{ '--vendor-accent': tone ? tone.color : 'var(--border-strong)' }} data-testid={`ew-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/app/projects/${p.id}`} className="serif text-lg leading-tight hover:text-[color:var(--gold)] vendor-name" title={p.project_name}>{p.project_name}</Link>
                <div className="text-xs text-muted mt-0.5">{p.client_name || 'No client'}{needsPicker ? ` · ${companyName(p.company_id)}` : ''}</div>
              </div>
              <div className="flex-shrink-0"><ProjectStatusBadge status={p.status}/></div>
            </div>
            {p.notes && <div className="text-xs text-muted mt-2" style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} data-testid={`ew-card-notes-${p.id}`}>{p.notes}</div>}
            <div className="grid grid-cols-3 gap-2 text-xs mt-4">
              <div><div className="text-muted">Spent</div><div className="mono text-red-400" data-testid={`ew-card-spent-${p.id}`}>{fmtINR(t.total_expenses)}</div></div>
              <div><div className="text-muted">Received</div><div className="mono text-green-400" data-testid={`ew-card-received-${p.id}`}>{fmtINR(t.total_receipts)}</div></div>
              <div><div className="text-muted">Profit</div><div className={"mono " + profitColorClass(t.project_profit)} data-testid={`ew-card-profit-${p.id}`}>{fmtINR(t.project_profit)}</div></div>
            </div>
            <div className="mt-2" title={`Received ${fmtINR(t.total_receipts)} · Spent ${fmtINR(t.total_expenses)} (bars scaled to the largest bucket shown)`}>
              <div className="vendor-bar" style={{ marginTop: 0 }}><div className="bar-green" style={{ width: `${pct(t.total_receipts)}%` }}/></div>
              <div className="vendor-bar" style={{ marginTop: 3 }}><div className="bar-red" style={{ width: `${pct(t.total_expenses)}%` }}/></div>
            </div>
            {pending > 0 && <div className="vendor-pending mt-2" data-testid={`ew-card-pending-${p.id}`}><Clock3 size={12}/>{fmtINR(pending)} awaiting review</div>}
            <div className="mt-3 text-xs flex items-center justify-between gap-2" data-testid={`ew-card-age-${p.id}`}>
              <span className="text-muted">Started {p.start_date ? fmtDate(p.start_date) : '—'}</span>
              {age !== null && <span className={stale ? 'ew-stale' : 'text-muted'} title={stale ? 'Open for a long time — identify this work and move it into a real project' : undefined}>{age} day{age === 1 ? '' : 's'} open{stale ? ' · identify it' : ''}</span>}
            </div>
            <div style={{ flex: 1, minHeight: 14 }} aria-hidden="true"/>
            <div className="vendor-card-foot">
              <div className="min-w-0">
                {canMerge
                  ? <button onClick={() => onMerge(p)} className="tab btn-small inline-flex items-center gap-1" data-testid={`ew-card-merge-${p.id}`}><GitMerge size={12}/>Move into Project</button>
                  : <span className="text-xs text-muted" title="Moving into a project absorbs this bucket with no undo — restricted to admin roles.">Move: admin only</span>}
              </div>
              <div className="inline-flex gap-1 flex-shrink-0">
                <Link to={`/app/projects/${p.id}`} className="tab btn-small" data-testid={`ew-card-open-${p.id}`}>Open</Link>
                {canEdit && <button aria-label="Edit" onClick={() => onEdit(p)} className="tab btn-small" data-testid={`ew-card-edit-${p.id}`}><Edit3 size={12}/></button>}
                {isMaster && <button aria-label="Delete" onClick={() => onDelete(p)} className="tab btn-small text-red-300" data-testid={`ew-card-delete-${p.id}`}><Trash2 size={12}/></button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExtraWorks() {
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  // Originally master_admin-only (merge closes/absorbs the source project
  // with no undo endpoint, similar risk to delete) -- user-requested
  // follow-up opened it to company_admin too, since every real safety
  // check (company ownership, lock state, same-company requirement) is
  // enforced server-side regardless of role.
  const canMerge = isAdmin(user);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  const masters = useMasters();

  const [data, setData] = useState(null);
  // Multi-company selection (same GrantPicker/company_filter pattern as
  // Reports/BillsHub) — replaces the old single-company dropdown.
  const [companyFilterIds, setCompanyFilterIds] = usePersistedState('ExtraWorks.companyFilterIds', []);
  const [allCompaniesSelected, setAllCompaniesSelected] = usePersistedState('ExtraWorks.allCompaniesSelected', true);
  const statusF = useMultiFilter('ExtraWorks.status');
  // User-requested: client-wise filter, same dropdown convention as the
  // Projects list / Bills Hub / Income / Security & Refundables Client
  // filter. Extra Work buckets ARE projects rows (is_extra_work=true), so
  // each row already carries its own department_client_id directly --
  // no separate /projects fetch or project_id -> client_id map needed.
  const clientF = useMultiFilter('ExtraWorks.client');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  // MoveExtraWorkModal (below) owns all the merge-specific state now --
  // this just tracks WHICH row (if any) is being moved.
  const [mergeRow, setMergeRow] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState('new');
  const [bulkBucketId, setBulkBucketId] = useState('');
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [search, setSearch] = usePersistedState('ExtraWorks.search', '');
  const [view, setView] = usePersistedState('ExtraWorks.view', 'cards');
  const [cardSort, setCardSort] = usePersistedState('ExtraWorks.cardSort', 'name');

  const companyFilterParam = (allCompaniesSelected || !companyFilterIds.length) ? undefined : companyFilterIds.join(',');
  const companyFilterSubtitle = !allCompaniesSelected && companyFilterIds.length
    ? (companyFilterIds.length === 1 ? companies.find(c=>c.id===companyFilterIds[0])?.name : `${companyFilterIds.length} companies`)
    : 'All Companies';
  const load = async (silent) => {
    const params = {};
    if (companyFilterParam) params.company_filter = companyFilterParam;
    // Status is filtered in the browser now (it can hold several values); the server returns every status when none is asked for.
    try {
      const r = await api.get('/extra-works', { params });
      setData(r.data);
    } catch (e) {
      // Round 20 finding: this had no error handling at all, and the
      // Round 19 loading-state fix made `data` staying null drive an
      // infinite "still loading" render -- a failed fetch (network blip,
      // expired session, transient 5xx) used to at least settle into a
      // stable (if misleadingly-zeroed) page; now it would spin forever
      // with no feedback. Surface the error and settle into a safe empty
      // state instead of leaving `data` null.
      if (silent === true) return;      // automatic refresh: keep what is on screen, say nothing
      toast.error(e.response?.data?.detail || 'Could not load Extra Works');
      setData({ rows: [], totals: { count: 0, investment: 0, expenses: 0, receipts: 0, profit: 0 } });
    }
  };
  useEffect(() => { load(); }, [companyFilterParam]);
  useLiveRefresh(() => load(true));

  const clientOptions = useMemo(() =>
    (masters.department_client || [])
      .filter(m => allCompaniesSelected || (m.company_ids||[]).some(cid => companyFilterIds.includes(cid)))
      .slice().sort((a,b)=>a.name.localeCompare(b.name)),
    [masters.department_client, allCompaniesSelected, companyFilterIds]);
  // A company selection change can leave a stale Client selection that no
  // longer belongs to it -- clear rather than silently filtering to zero.
  useEffect(() => {
    clientF.prune(clientOptions.map(c => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyFilterIds, allCompaniesSelected]);

  const sq = search.trim().toLowerCase();
  const items = (data?.rows || [])
    .filter(({ project: p }) => statusF.matches(p.status))
    .filter(({ project: p }) => clientF.matches(clientIdOfProject(p, masters.department_client)))
    .filter(({ project: p }) => !sq || [p.project_name, p.notes, p.client_name].some(v => String(v || '').toLowerCase().includes(sq)));
  const cardRows = useMemo(() => sortExtraWorkCards(items, cardSort), [items, cardSort]);
  // Recomputed from the (possibly client-filtered) visible rows rather
  // than taken from data.totals directly, so the KPI cards always match
  // what the table below is actually showing -- data.totals only ever
  // reflects the company/status filters already applied server-side.
  const totals = items.reduce((acc, { totals: t }) => {
    acc.count += 1;
    acc.investment += t.total_investments;
    acc.expenses += t.total_expenses;
    acc.receipts += t.total_receipts;
    acc.profit += t.distributable_profit - t.company_fund_pool;
    return acc;
  }, { count: 0, investment: 0, expenses: 0, receipts: 0, profit: 0 });

  // Print / PDF: grouped by client (one section per client), with optional Spent and Days-open columns.
  const [printOpen, setPrintOpen] = useState(false);
  const [printOpts, setPrintOpts] = usePersistedState('ExtraWorks.printOpts', { spent: true, days: true });
  const todayIsoNow = new Date().toLocaleDateString('en-CA');
  const printGroups = groupExtraWorksByClient(items);
  const activeFilterSummary = [
    needsPicker && !allCompaniesSelected ? `Company: ${companyFilterIds.map(companyName).join(', ')}` : null,
    !statusF.all ? `Status: ${statusF.ids.map(st => st.replace(/_/g, ' ')).join(', ') || 'none'}` : null,
    !clientF.all ? `Client: ${clientF.ids.map(id => clientOptions.find(c => c.id === id)?.name || '—').join(', ') || 'none'}` : null,
    search.trim() ? `Search "${search.trim()}"` : null,
  ].filter(Boolean).join(' · ');
  const downloadPdf = async () => {
    setPrintOpen(false);
    try {
      await exportExtraWorksPDF({ groups: printGroups, opts: printOpts, needsPicker, companyName, days: (p) => ewAgeDays(p, todayIsoNow), summary: activeFilterSummary, todayLabel: todayIsoNow });
    } catch (e) { toast.error('Could not build the PDF'); }
  };
  const doPrint = () => { setPrintOpen(false); setTimeout(() => window.print(), 150); };
  // Edit an existing bucket (admin roles): same form as Add, company fixed, saved with a PATCH of just the editable fields.
  const openEdit = (p) => { setForm({ id: p.id, company_id: p.company_id, project_name: p.project_name, client_name: p.client_name, department_client_id: clientIdOfProject(p, masters.department_client) || '', department: p.department, start_date: p.start_date || '', notes: p.notes || '', status: p.status, contract_value: p.contract_value }); setOpen(true); };
  const clearContract = async () => {
    if (!window.confirm('Clear the Contract value on this bucket?\n\nAn Extra Work bucket has no contract; the old figure is what makes it show a profit. The previous value stays in the audit history.')) return;
    try { await api.patch(`/projects/${form.id}`, { project_name: (form.project_name || '').trim(), contract_value: 0, gst_amount: 0 }); setForm({ ...form, contract_value: 0 }); load(); toast.success('Contract value cleared'); }  // the server requires the name on every update; everything else not sent keeps its stored value
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openAdd = () => setForm({ status: 'running', ...(!allCompaniesSelected && companyFilterIds.length === 1 ? { company_id: companyFilterIds[0] } : {}) });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    if (!form.project_name?.trim()) { toast.error('Work Name / Place is required.'); return; }
    if (!form.client_name?.trim()) { toast.error('Client Name is required.'); return; }
    setSaving(true);
    try {
      if (form.id) {
        await api.patch(`/projects/${form.id}`, extraWorkEditPayload(form));
        load(); setOpen(false); toast.success('Saved');
        return;
      }
      await api.post('/projects', { ...form, is_extra_work: true, partnership_model: form.partnership_model || 'equal_capital_bonus' });
      load();
      // User-requested: stay open and reset to the same opening seed
      // instead of closing, so several buckets can be added in a row.
      openAdd();
      toast.success('Saved — ready for the next one');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.project_name}"? This permanently removes this Extra Work bucket and cannot be undone.`)) return;
    try { await api.delete(`/projects/${p.id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="section-eyebrow mb-2">Unidentified / Not Yet Tendered</div>
          <h1 className="serif text-4xl">Extra Works</h1>
          <div className="text-sm text-muted mt-1 max-w-2xl">Work and spend not yet tied to a named project — tracked under a place or work-type label for memory. Move a bucket into a real project (Tender Work, Voucher Payment, or any other category) once it's identified; every expense, receipt, and rupee of profit moves with it.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {needsPicker && <GrantPicker
            items={companies}
            selectedIds={companyFilterIds}
            allAccess={allCompaniesSelected}
            onToggle={cid => setCompanyFilterIds(cur => cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid])}
            onSetAllAccess={v => { setAllCompaniesSelected(v); if (v) setCompanyFilterIds([]); }}
            allLabel="All Companies"
            noneWidth={200}
            data-testid="ew-filter-company"
          />}
          <div className="vendor-search">
            <SearchIcon size={14}/>
            <input className="input" placeholder="Search work, client, notes…" value={search} onChange={e => setSearch(e.target.value)} data-testid="ew-search"/>
          </div>
          <MultiFilterPicker filter={statusF} items={DEFAULT_PROJECT_STATUSES.map(st => ({ id: st, name: st.replace(/_/g, ' ') }))} allLabel="All Statuses" width={160} testId="ew-filter-status"/>
          {clientOptions.length > 0 && <MultiFilterPicker filter={clientF} items={clientOptions.map(c => ({ id: c.id, name: c.name }))} allLabel="All Clients" width={170} testId="ew-filter-client"/>}
          <ClearFiltersButton filters={[statusF, clientF]} extraActive={needsPicker && !allCompaniesSelected} onClear={() => { setAllCompaniesSelected(true); setCompanyFilterIds([]); }}/>
          {view === 'cards' && <select className="input" style={{ width: 180 }} value={cardSort} onChange={e => setCardSort(e.target.value)} data-testid="ew-card-sort" title="Order of the cards">
            <option value="name">Sort: Name A–Z</option>
            <option value="spent">Sort: Most spent</option>
            <option value="profit">Sort: Highest profit</option>
            <option value="oldest">Sort: Oldest first</option>
          </select>}
          <div className="inline-flex gap-1" role="group" aria-label="View">
            <button className={"tab btn-small inline-flex items-center gap-1 " + (view === 'cards' ? 'active' : '')} onClick={() => setView('cards')} data-testid="ew-view-cards"><LayoutGrid size={13}/>Cards</button>
            <button className={"tab btn-small inline-flex items-center gap-1 " + (view === 'table' ? 'active' : '')} onClick={() => setView('table')} data-testid="ew-view-table"><ListIcon size={13}/>Table</button>
          </div>
          {canEdit && <button onClick={() => setBulkOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="ew-bulk-import"><Upload size={14} />Bulk Import</button>}
          {items.length > 0 && <button onClick={()=>setPrintOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="ew-print"><Printer size={14}/>Print</button>}
          {canEdit && <button onClick={() => { openAdd(); setOpen(true); }} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-extra-work"><Plus size={14} />Add Extra Work</button>}
          {canEdit && <button onClick={() => setExpenseOpen(true)} className="btn-red btn-small inline-flex items-center gap-1" data-testid="add-extra-work-expense"><Plus size={14} />Add Expenses</button>}
        </div>
      </div>

      {/* Round 19 finding: totals defaulted to all-zero via `data?.totals
          || {...}` before the first /extra-works response landed, so a
          slow load looked identical to "confirmed zero buckets" instead
          of still loading -- gate on `data` (null until the first
          response) the same way the rest of the app distinguishes the
          two. */}
      {data && <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 no-print">
        <KpiCard label="Open Buckets" value={totals.count} icon={Layers} />
        <KpiCard label="Total Expenses" value={fmtINR(totals.expenses)} tone="red" />
        <KpiCard label="Total Receipts" value={fmtINR(totals.receipts)} tone="green" />
        <KpiCard label="Net Profit / Loss" value={fmtINR(totals.profit)} tone={totals.profit < 0 ? 'red' : 'gold'} />
      </div>}

      {view === 'cards' && (
        <div className="no-print" data-testid="ew-cards">
          <div className="text-xs text-muted mb-3">Showing <b>{items.length}</b> of <b>{(data?.rows || []).length}</b> Extra Work buckets{items.length !== (data?.rows || []).length ? ' (filtered)' : ''}</div>
          {!data && <div className="card card-dark text-center text-muted py-10">Loading…</div>}
          {data && items.length === 0 && <div className="card card-dark text-center text-muted py-10">{(data.rows || []).length === 0 ? 'No Extra Work buckets open right now.' : 'No Extra Work buckets match these filters.'}</div>}
          {data && items.length > 0 && <ExtraWorkCards rows={cardRows} canMerge={canMerge} canEdit={canEdit} onEdit={openEdit} isMaster={isMasterAdmin(user)} needsPicker={needsPicker} companyName={companyName} todayIso={new Date().toLocaleDateString('en-CA')} onMerge={setMergeRow} onDelete={del}/>}
        </div>
      )}
      <div className="print-area ew-print-only" data-testid="ew-print-groups">
        {printGroups.map((g, gi) => (
          <div key={g.client} className="ew-client-section">
            <div className="print-header">
              <div className="print-company">{companyFilterSubtitle}</div>
              <div className="print-report-title">Extra Works — {g.client}</div>
              <div className="print-meta">{g.rows.length} bucket{g.rows.length === 1 ? '' : 's'} · Client {gi + 1} of {printGroups.length} · Generated: {fmtGeneratedAt()}{activeFilterSummary ? ` · Filters: ${activeFilterSummary}` : ''}</div>
            </div>
            <table className="tbl">
              <thead><tr><th>Work / Place</th>{needsPicker && <th>Company</th>}<th>Started</th>{printOpts.days && <th className="text-right" data-testid="ewp-col-days">Days open</th>}{printOpts.spent && <th className="text-right" data-testid="ewp-col-spent">Spent</th>}<th>Status</th></tr></thead>
              <tbody>
                {g.rows.map(({ project: p, totals: t }) => (
                  <tr key={p.id}>
                    <td>{p.project_name}{p.notes && <div className="text-xs">{p.notes}</div>}</td>
                    {needsPicker && <td className="text-xs">{companyName(p.company_id)}</td>}
                    <td className="text-xs">{fmtDate(p.start_date)}</td>
                    {printOpts.days && <td className="mono text-right">{ewAgeDays(p, todayIsoNow) ?? '—'}</td>}
                    {printOpts.spent && <td className="mono text-right">{fmtINR(t.total_expenses)}</td>}
                    <td className="text-xs">{(p.status || '').replace(/_/g, ' ')}</td>
                  </tr>
                ))}
              </tbody>
              {printOpts.spent && <tfoot><tr style={{ fontWeight: 600 }}>
                <td colSpan={1 + (needsPicker ? 1 : 0) + 1 + (printOpts.days ? 1 : 0)} className="text-right text-xs">Spent — this client</td>
                <td className="mono text-right">{fmtINR(g.rows.reduce((sum, r) => sum + Number(r.totals.total_expenses || 0), 0))}</td>
                <td></td>
              </tr></tfoot>}
            </table>
          </div>
        ))}
      </div>
      <div className="card card-dark overflow-x-auto no-print" data-testid="ew-table" style={view === 'cards' ? { display: 'none' } : undefined}>
        <div className="print-header">
          <div className="print-company">{companyFilterSubtitle}</div>
          <div className="print-report-title">Extra Works</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        <table className="tbl">
          <thead><tr>
            <th>Work / Place</th>{needsPicker && <th>Company</th>}<th>Started</th><th className="text-right">Expenses</th>
            <th className="text-right">Receipts</th><th className="text-right">Profit</th><th>Status</th><th className="no-print"></th>
          </tr></thead>
          <tbody>
            {!data && <LoadingRow colSpan={8}/>}
            {data && items.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-8">No Extra Work buckets open right now.</td></tr>}
            {data && items.map(({ project: p, totals: t }) => (
              <tr key={p.id} data-testid={`row-extra-work-${p.id}`}>
                <td><Link to={`/app/projects/${p.id}`} className="serif text-base hover:text-[color:var(--gold)]">{p.project_name}</Link>{p.notes && <div className="text-xs text-muted">{p.notes}</div>}</td>
                {needsPicker && <td className="text-xs">{companyName(p.company_id)}</td>}
                <td className="text-xs">{fmtDate(p.start_date)}</td>
                <td className="mono text-right text-red-400">{fmtINR(t.total_expenses)}</td>
                <td className="mono text-right text-green-400">{fmtINR(t.total_receipts)}</td>
                <td className={"mono text-right " + profitColorClass(t.project_profit)}>{fmtINR(t.project_profit)}</td>
                <td><ProjectStatusBadge status={p.status} /></td>
                <td className="text-right whitespace-nowrap no-print">
                  {canMerge
                    ? <button onClick={() => setMergeRow(p)} className="tab btn-small mr-1 inline-flex items-center gap-1" data-testid={`merge-${p.id}`}><GitMerge size={12} />Move into Project</button>
                    : <span className="text-xs text-muted mr-1" title="Moving into a project absorbs this bucket with no undo — restricted to admin roles.">Move: admin only</span>}
                  {isMasterAdmin(user) && <button aria-label="Delete" onClick={() => del(p)} className="tab btn-small text-red-300 mr-1" data-testid={`delete-extra-work-${p.id}`}><Trash2 size={12}/></button>}
                  <Link to={`/app/projects/${p.id}`} className="tab btn-small">Open</Link>
                  {canEdit && <button aria-label="Edit" onClick={() => openEdit(p)} className="tab btn-small ml-1" data-testid={`edit-extra-work-${p.id}`}><Edit3 size={12}/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={printOpen} onClose={() => setPrintOpen(false)} title="Print Extra Works" maxW={520}>
        <div data-testid="ew-print-dialog">
          <p className="text-sm text-muted mb-4">Prints the <b>{items.length}</b> bucket{items.length === 1 ? '' : 's'} shown now (filters and search apply), <b>one page per client</b> — {printGroups.length} client{printGroups.length === 1 ? '' : 's'}. Tick what you also want as columns:</p>
          {[['days', 'Days open', 'whole days since the bucket started'], ['spent', 'Spent', 'expenses booked on the bucket, with a subtotal per client']].map(([k, label, hint]) => (
            <label key={k} className="flex items-start gap-3 mb-3 cursor-pointer">
              <input type="checkbox" className="mt-1" checked={!!printOpts[k]} onChange={e => setPrintOpts({ ...printOpts, [k]: e.target.checked })} data-testid={`ew-print-opt-${k}`}/>
              <span><span className="text-sm">{label}</span><span className="block text-xs text-muted">{hint}</span></span>
            </label>
          ))}
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button onClick={() => setPrintOpen(false)} className="btn-outline btn-small">Cancel</button>
            <button onClick={downloadPdf} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="ew-print-pdf"><Download size={14}/>Download PDF</button>
            <button onClick={doPrint} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="ew-print-go"><Printer size={14}/>Print</button>
          </div>
        </div>
      </Modal>
      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? 'Edit Extra Work' : 'Add Extra Work'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {form.id ? <div className="col-span-2 text-xs text-muted">Company: <b>{companyName(form.company_id)}</b> (can't be changed)</div> : <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker} clear={['department_client_id', 'client_name', 'department']} />}
          <div className="col-span-2"><Field label="Work Name / Place *"><input className="input" placeholder="e.g. Site XYZ — misc repairs" value={form.project_name || ''} onChange={e => setForm({ ...form, project_name: e.target.value })} data-testid="ew-name" /></Field></div>
          {/* Same master-backed pattern as the real "Add Project" form's
              Department / Client field -- user-requested, so a client name
              here can never drift from (or be a typo of) an entry that
              already exists in Settings > Masters > Department / Client,
              and matches exactly what the bulk-import path now also
              requires an exact match against. */}
          {/* Same real gap as the main Add Project form's own Work
              Category/Department-Client fields: mastersForCompany
              deliberately returns every company's masters unfiltered
              when company_id is blank (correct for a report filter,
              wrong here) -- openAdd only pre-fills company_id when
              exactly one company is already selected on this page, so
              picking a Client before Company here could otherwise save
              a client belonging to a different company entirely. */}
          {!form.company_id ? (
            <Field label="Client *"><select className="input" disabled><option>— select a company first —</option></select></Field>
          ) : (
          <Field label="Client *"><select className="input" value={form.department_client_id || ''} onChange={e => {
            const m = mastersForCompany(masters.department_client, form.company_id).find(x => x.id === e.target.value);
            setForm({ ...form, department_client_id: e.target.value, client_name: m?.name || '', department: m?.client_organisation || m?.name || '' });
          }} data-testid="ew-client-name">
            <option value="">— select —</option>
            {mastersForCompany(masters.department_client, form.company_id).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          {!mastersForCompany(masters.department_client, form.company_id).length && <div className="text-xs text-muted mt-1">No clients yet — add one under Settings &gt; Masters &gt; Department / Client first.</div>}
          </Field>
          )}
          <Field label="Started"><input type="date" className="input" value={form.start_date || ''} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
          <Field label="Status"><select className="input" value={form.status || 'running'} onChange={e => setForm({ ...form, status: e.target.value })}>
            {DEFAULT_PROJECT_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select></Field>
          <div className="col-span-2"><Field label="Notes"><textarea className="input" rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field></div>
          {form.id && Number(form.contract_value) > 0 && (
            <div className="col-span-2 text-xs" style={{ color: 'var(--accent-amber)' }} data-testid="ew-edit-contract-note">
              This bucket still carries a <b>Contract of {fmtINR(form.contract_value)}</b> from when it was a project — an Extra Work bucket has no contract, so its profit shows as positive. <button type="button" onClick={clearContract} className="tab btn-small ml-1" data-testid="ew-clear-contract">Clear contract value</button>
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={() => setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="ew-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>

      {mergeRow && <MoveExtraWorkModal project={mergeRow} onClose={() => setMergeRow(null)} onMoved={load}/>}

      <AddExtraWorkExpenseModal open={expenseOpen} onClose={() => setExpenseOpen(false)} onSaved={load}/>

      {bulkOpen && (
        <Modal open={true} onClose={() => { setBulkOpen(false); setBulkBucketId(''); load(); }} title="Bulk Import — Extra Works" maxW={860}>
          {/* User-requested: same Import History visibility Data & Backup
              already has, scoped to Extra Works -- covers BOTH new-bucket
              imports and "Expenses into a Bucket" imports (previously only
              the former showed here, and only while that tab was active;
              this now covers both entities and stays visible regardless of
              which tab below is selected, since it's relevant to either). */}
          <ImportHistoryPanel
            entityFilter={(d) => d.entity === 'extra_works' || (d.entity === 'expenses' && d.locked_project_is_extra_work)}
            title="Extra Works Import History"
          />
          <div className="flex gap-2 mb-4">
            <button className={"tab btn-small " + (bulkMode === 'new' ? 'active' : '')} onClick={() => setBulkMode('new')}>New Buckets</button>
            <button className={"tab btn-small " + (bulkMode === 'expenses' ? 'active' : '')} onClick={() => setBulkMode('expenses')}>Expenses into a Bucket</button>
          </div>
          {bulkMode === 'new' ? (
            <ExcelImportWizard lockedEntity="extra_works" />
          ) : items.length === 0 ? (
            <div className="text-sm text-muted">No open Extra Work buckets yet — add one first.</div>
          ) : (
            <>
              <Field label="Which bucket? *"><select className="input" value={bulkBucketId} onChange={e => setBulkBucketId(e.target.value)}>
                <option value="">— select —</option>
                {items.map(({ project: p }) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
              </select></Field>
              {/* Round 19 finding: switching the bucket dropdown only
                  changed the lockedProject prop -- with no `key`, React
                  reused the same wizard instance instead of remounting it,
                  so a preview already generated for the OLD bucket (its
                  duplicate/error/warning counts) stayed on screen and could
                  be committed against the NEWLY selected bucket via Confirm
                  & Import, even though it was never actually computed for
                  it. Keying on bulkBucketId forces a fresh wizard (and a
                  fresh preview) every time the target bucket changes. */}
              {bulkBucketId && <div className="mt-4"><ExcelImportWizard key={bulkBucketId} lockedProject={items.find(i => i.project.id === bulkBucketId)?.project} lockedEntity="expenses" /></div>}
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

// ============= INCOME (cross-project quick entry) =============
// Every income entry made here is exactly the same receipt row a
// project's own Receipts tab creates (same endpoint, same fields) —
// this just puts a project picker up front so recording a bank credit
// doesn't require navigating into that project first. Nothing new to
// trust on the money side: GET/POST /receipts already existed and are
// already company-scoped correctly.
// ---- Income page: income-by-project card helpers ----
const INCOME_NO_PROJECT = '__none__'; // company-level receipts (no project)
// One summary per project from whatever receipts are currently shown. Only matched/final
// receipts count as received (same rule as the Confirmed Amount tile and every P&L);
// the rest is shown apart as pending review.
function groupIncomeByProject(rows, projectById) {
  const m = new Map();
  rows.forEach(r => {
    const key = r.project_id || INCOME_NO_PROJECT;
    let g = m.get(key);
    if (!g) { g = { key, project: r.project_id ? (projectById(r.project_id) || null) : null, confirmed: 0, count: 0, pendingAmount: 0, pendingCount: 0, lastDate: null, types: {} }; m.set(key, g); }
    const amt = Number(r.amount || 0);
    g.count += 1;
    if (CALC_ELIGIBLE_AUDIT_STATUSES.has(r.audit_status)) { g.confirmed += amt; const t = r.receipt_type || 'other'; g.types[t] = (g.types[t] || 0) + amt; }
    else { g.pendingAmount += amt; g.pendingCount += 1; }
    if (r.receipt_date && (!g.lastDate || r.receipt_date > g.lastDate)) g.lastDate = r.receipt_date;
  });
  return [...m.values()];
}
const incomeGroupName = (g) => g.project ? g.project.project_name : (g.key === INCOME_NO_PROJECT ? '(Company-level income)' : '(Unknown project)');
const sortIncomeGroups = (groups, key) => {
  const c = [...groups];
  const byName = (a, b) => incomeGroupName(a).localeCompare(incomeGroupName(b), undefined, { numeric: true, sensitivity: 'base' });
  if (key === 'latest') c.sort((a, b) => String(b.lastDate || '').localeCompare(String(a.lastDate || '')) || byName(a, b));
  else if (key === 'pending') c.sort((a, b) => b.pendingAmount - a.pendingAmount || byName(a, b));
  else if (key === 'name') c.sort(byName);
  else c.sort((a, b) => b.confirmed - a.confirmed || byName(a, b));
  return c;
};
function IncomeCards({ groups, needsPicker, companyName, onViewEntries }) {
  const maxConfirmed = Math.max(0, ...groups.map(g => g.confirmed));
  return (
    <div className="vendor-grid project-grid">
      {groups.map(g => {
        const tone = META_CHIP_TONES.green;
        const topTypes = Object.entries(g.types).sort((a, b) => b[1] - a[1]).slice(0, 2);
        return (
          <div key={g.key} className="card card-dark vendor-card" style={{ '--vendor-accent': tone.color }} data-testid={`income-card-${g.key}`}>
            <div className="min-w-0">
              {g.project
                ? <Link to={`/app/projects/${g.project.id}`} className="serif text-lg leading-tight hover:text-[color:var(--gold)] vendor-name" title={incomeGroupName(g)}>{incomeGroupName(g)}</Link>
                : <span className="serif text-lg leading-tight vendor-name">{incomeGroupName(g)}</span>}
              <div className="text-xs text-muted mt-0.5">{g.project ? `${g.project.client_name || 'No client'}${needsPicker ? ` · ${companyName(g.project.company_id)}` : ''}` : 'Not tied to a project'}</div>
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-muted">Received (confirmed)</div>
              <div className="serif mono text-2xl text-green-400" data-testid={`income-card-confirmed-${g.key}`}>{fmtINR(g.confirmed)}</div>
              <div className="vendor-bar" aria-hidden="true"><div className="bar-green" style={{ width: `${maxConfirmed > 0 ? Math.max(g.confirmed > 0 ? 2 : 0, Math.round((g.confirmed / maxConfirmed) * 100)) : 0}%` }}/></div>
              <div className="text-xs text-muted mt-2" data-testid={`income-card-meta-${g.key}`}>{g.count} receipt{g.count === 1 ? '' : 's'}{g.lastDate ? ` · last ${fmtDate(g.lastDate)}` : ''}</div>
            </div>
            {topTypes.length > 0 && <div className="flex flex-wrap gap-1 mt-3" data-testid={`income-card-types-${g.key}`}>
              {topTypes.map(([t, amt]) => <span key={t} className="text-xs" style={{ padding: '1px 8px', borderRadius: 20, border: '1px solid var(--border-subtle)' }}>{t.replace(/_/g, ' ')} · {fmtINR(amt)}</span>)}
            </div>}
            {g.pendingCount > 0 && <div className="vendor-pending mt-3" data-testid={`income-card-pending-${g.key}`}><Clock3 size={12}/>{fmtINR(g.pendingAmount)} awaiting review ({g.pendingCount})</div>}
            <div style={{ flex: 1, minHeight: 14 }} aria-hidden="true"/>
            <div className="vendor-card-foot">
              <div className="inline-flex gap-1 flex-shrink-0 ml-auto">
                {g.project && <button onClick={() => onViewEntries(g.key)} className="tab btn-small" data-testid={`income-card-entries-${g.key}`}>View entries</button>}
                {g.project && <Link to={`/app/projects/${g.project.id}`} className="tab btn-small" data-testid={`income-card-open-${g.key}`}>Open</Link>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Income() {
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';

  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bills, setBills] = useState([]);
  // User-requested: hover a receipt in the LIST below to see its linked
  // bill's description -- separate from `bills` above (which is only
  // ever the Add/Edit form's own per-project fetch, for the bill
  // picker). Fetched once, unscoped (every bill/allocation this caller
  // can see across every project), since this list itself spans every
  // project at once.
  const [allBills, setAllBills] = useState([]);
  const [allocMap, setAllocMap] = useState({});
  // Multi-company selection (same GrantPicker pattern as Reports/BillsHub)
  // — GET /receipts already returns everything the caller can see with no
  // server-side company filter, so this narrows purely client-side.
  const [companyFilterIds, setCompanyFilterIds] = usePersistedState('Income.companyFilterIds', []);
  const [allCompaniesSelected, setAllCompaniesSelected] = usePersistedState('Income.allCompaniesSelected', true);
  const projectF = useMultiFilter('Income.project');
  // User-requested: client-wise filter, same dropdown convention as
  // Projects/Audit Queue/BillsHub's own Client filter -- resolved via a
  // project_id -> client_id map built off this page's own /projects
  // fetch, no extra request needed.
  const clientF = useMultiFilter('Income.client');
  // User-requested: filter this list to a specific date range (on
  // receipt_date), same plain From/To convention already used by the
  // Company Fund Ledger and Bank Account Ledger filters elsewhere in
  // this app.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = usePersistedState('Income.search', '');
  const [view, setView] = usePersistedState('Income.view', 'cards');
  const [cardSort, setCardSort] = usePersistedState('Income.cardSort', 'received');
  const masters = useMasters();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const projectById = (id) => projects.find(p => p.id === id);
  const load = async () => setItems((await api.get('/receipts')).data);
  useEffect(() => { load(); }, []);
  useLiveRefresh(() => load().catch(() => {}));
  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/bank-accounts').then(r => setBankAccounts(r.data)).catch(() => {});
    api.get('/bills').then(r => setAllBills(r.data)).catch(() => {});
    api.get('/receipts/bill-allocations').then(r => setAllocMap(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (form.project_id) {
      api.get('/bills', { params: { project_id: form.project_id, include_allocation: true } }).then(r => setBills(r.data)).catch(() => setBills([]));
    } else {
      setBills([]);
    }
  }, [form.project_id]);

  const bankName = (id) => bankAccounts.find(b => b.id === id)?.bank_name;
  // User-reported (full-app audit): a bare bank name is ambiguous across
  // companies -- more than one real company here names its account
  // "Hdfc Bank", so a receipts list/export with no adjacent Company
  // column context (the PDF export, unlike the on-screen table) had no
  // way to tell which one a row's Bank Account actually was. Display-only
  // wrapper -- bankName itself stays unqualified since it also feeds the
  // legacy free-text `bank_account` column on save (2830 below), which
  // must never be polluted with a company suffix.
  const bankNameDisplay = (id) => { const b = bankAccounts.find(x => x.id === id); return b ? `${b.bank_name}${needsPicker ? ` — ${companyName(b.company_id)}` : ''}` : null; };
  // Bug fix: `||` treated a genuine 0 outstanding (fully settled) as
  // falsy and fell through to showing the full Net Credit instead --
  // same class of bug fixed in BillsPanel's detail modal.
  const billLabel = (b) => `${b.bill_number || 'Bill'} · ${fmtDate(b.bill_date)} · Outstanding ${fmtINR(b.outstanding_amount ?? b.net_bank_credit)}`;
  const selectedBillIds = new Set((form.billAllocations||[]).map(a=>a.bill_id));
  const openBills = bills.filter(b => (b.outstanding_amount ?? b.net_bank_credit) > 0.5 || selectedBillIds.has(b.id));
  // User-requested re-review of bill-selection auto-fill on Add Receipt:
  // the allocation Amount already auto-fills correctly (BillAllocationPicker's
  // own sync, fixed earlier this session) -- the one gap left is Description,
  // which stayed blank even once a specific bill was picked, losing an easy
  // trace back to what this receipt is actually for. Fills it from the
  // ticked bill's own description, only while exactly one bill is ticked --
  // same "inert once ambiguous" rule the allocation sync already follows
  // for a multi-bill split.
  // Review-pass finding: an earlier version of this only fired once and
  // then stayed inert forever (guarded on "Description is still empty"),
  // so switching from one ticked bill to another left the FIRST bill's
  // description stuck even though it no longer matched what's selected.
  // autoFilledDescRef remembers the exact text this effect itself last
  // set -- safe to overwrite; anything else means the user typed their own
  // description, which must never be clobbered.
  const autoFilledDescRef = useRef(null);
  useEffect(() => {
    const allocs = form.billAllocations || [];
    if (allocs.length !== 1) return;
    const bill = bills.find(b => b.id === allocs[0].bill_id);
    if (!bill?.description) return;
    setForm(f => {
      if (f.description && f.description !== autoFilledDescRef.current) return f;
      autoFilledDescRef.current = bill.description;
      return { ...f, description: bill.description };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.billAllocations?.length === 1 ? form.billAllocations[0].bill_id : null]);
  const projectsForCompany = (cid) => projects.filter(p => !cid || p.company_id === cid);
  // The Company field is optional (picking a Project already implies one) — derive
  // from the project so the Bank Account list is always scoped correctly, not just
  // when someone happens to touch the Company dropdown first.
  const formCompanyId = form.company_id || projectById(form.project_id)?.company_id;
  const eligibleBanks = bankAccounts.filter(b => !formCompanyId || b.company_id === formCompanyId);
  useEffect(() => {
    if (!form.bank_account_id && formCompanyId) {
      const active = eligibleBanks.filter(b => b.status !== 'inactive');
      if (active.length === 1) setForm(f => ({ ...f, bank_account_id: active[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formCompanyId, form.bank_account_id, bankAccounts.length]);

  const projectClientMap = useMemo(() => new Map(projects.map(p => [p.id, clientIdOfProject(p, masters.department_client)])), [projects, masters.department_client]);
  const clientOptions = useMemo(() =>
    (masters.department_client || [])
      .filter(m => allCompaniesSelected || (m.company_ids||[]).some(cid => companyFilterIds.includes(cid)))
      .slice().sort((a,b)=>a.name.localeCompare(b.name)),
    [masters.department_client, allCompaniesSelected, companyFilterIds]);
  const incomeQuery = search.trim().toLowerCase();
  const visibleItems = items
    .filter(r => allCompaniesSelected || companyFilterIds.includes(projectById(r.project_id)?.company_id))
    .filter(r => projectF.matches(r.project_id))
    .filter(r => clientF.matches(projectClientMap.get(r.project_id)))
    .filter(r => !fromDate || (r.receipt_date || '') >= fromDate)
    .filter(r => !toDate || (r.receipt_date || '') <= toDate)
    // User-reported: no sortable column headers on this page's table --
    // same _derived_field convention Company Expenses already uses so a
    // column showing a resolved/derived value (Project name, Company
    // name, Bank Account label) can still be sorted, not just the raw
    // stored id.
    .map(r => ({ ...r, _project_name: projectById(r.project_id)?.project_name || '', _company_name: companyName(projectById(r.project_id)?.company_id), _bank_name: bankNameDisplay(r.bank_account_id) || r.bank_account || '' }))
    .filter(r => !incomeQuery || [r._project_name, projectById(r.project_id)?.client_name, r.description, r.notes, r._bank_name, (r.receipt_type || '').replace(/_/g, ' '), String(r.amount ?? '')].some(v => String(v || '').toLowerCase().includes(incomeQuery)));
  const dateFilterActive = !!(fromDate || toDate);
  const incomeGroups = useMemo(() => sortIncomeGroups(groupIncomeByProject(visibleItems, projectById), cardSort), [visibleItems, projects, cardSort]);
  const viewProjectEntries = (pid) => { projectF.only(pid); setView('table'); };
  const { sorted: sortedItems, sortKey, sortDir, toggleSort } = useSortableRows(visibleItems, 'receipt_date', 'desc');
  const visibleProjectsForFilter = allCompaniesSelected ? projects : projects.filter(p => companyFilterIds.includes(p.company_id));
  const companyFilterSubtitle = !allCompaniesSelected
    ? (companyFilterIds.length === 1 ? companies.find(c=>c.id===companyFilterIds[0])?.name : `${companyFilterIds.length} companies`)
    : 'All Companies';
  // If the company selection changes out from under an already-picked
  // project/client filter, drop the stale selection — same guard
  // Reports/BillsHub already apply to their own project pickers.
  useEffect(() => {
    projectF.prune(visibleProjectsForFilter.map(p => p.id));
    clientF.prune(clientOptions.map(c => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyFilterIds, allCompaniesSelected]);
  // Mirrors the backend's CALC_ELIGIBLE_AUDIT_STATUSES exactly (matched/final)
  // — the same set _compute_from_rows uses for total_receipts everywhere else
  // (Dashboard, Project Detail, every Reports-page report). The audit found
  // this page previously summed every receipt regardless of status, so a
  // project's "Total Amount" here could disagree with what its own P&L
  // showed one click away. Pending/discrepancy amounts are shown as their
  // own KPI instead of silently folded into the headline total.
  const totals = visibleItems.reduce((acc, r) => ({
    amount: acc.amount + (CALC_ELIGIBLE_AUDIT_STATUSES.has(r.audit_status) ? Number(r.amount || 0) : 0),
    pendingAmount: acc.pendingAmount + (CALC_ELIGIBLE_AUDIT_STATUSES.has(r.audit_status) ? 0 : Number(r.amount || 0)),
    pending: acc.pending + (r.audit_status === 'pending_review' ? 1 : 0),
  }), { amount: 0, pendingAmount: 0, pending: 0 });

  const openAdd = () => setForm({
    receipt_date: new Date().toISOString().slice(0, 10), receipt_type: 'customer_payment',
    ...(!allCompaniesSelected && companyFilterIds.length === 1 ? { company_id: companyFilterIds[0] } : {}), ...(projectF.soleId ? { project_id: projectF.soleId } : {}),
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    if (!form.project_id) { toast.error('Project is required.'); return; }
    if (!form.receipt_date) { toast.error('Date is required.'); return; }
    if (!form.amount) { toast.error('Amount is required.'); return; }
    const allocs = form.billAllocations || [];
    if (allocs.length > 0) {
      const total = Math.round(allocs.reduce((s,a)=>s+Number(a.amount||0),0) * 100) / 100;
      if (!allocs.every(a => Number(a.amount) > 0)) { toast.error('Enter an amount for every selected bill.'); return; }
      if (Math.abs(total - Number(form.amount||0)) > 0.01) { toast.error(`The bill amounts (${fmtINR(total)}) must add up to the receipt's own Amount (${fmtINR(Number(form.amount||0))}).`); return; }
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount || 0), bank_account: form.bank_account_id ? bankName(form.bank_account_id) : null };
      delete payload.billAllocations;
      if (allocs.length > 0) {
        payload.bill_allocations = allocs.map(a => ({ bill_id: a.bill_id, amount: Number(a.amount) }));
        payload.bill_id = allocs.length === 1 ? allocs[0].bill_id : null;
      } else {
        payload.bill_allocations = null;
        payload.bill_id = null;
      }
      delete payload.company_id;
      await api.post('/receipts', payload);
      const dedFailed = await saveReceiptDeductionLines(form.deductionLines, form.project_id, form.receipt_date);
      load();
      // User-requested: stay open and reset to the same opening seed
      // instead of closing, so several income entries can be added in a
      // row without re-clicking "Add Income" each time.
      openAdd();
      toast.success(dedFailed ? `Saved — ${dedFailed} deduction line(s) failed` : 'Saved — ready for the next one');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this income entry?')) return;
    try { await api.delete(`/receipts/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // User-requested: same PDF export every other ledger/statement view in
  // the app already has -- this page only ever had Print.
  const exportPDF = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text('Income', 14, 18);
    doc.setFontSize(9); doc.text(`Generated: ${fmtGeneratedAt()}`, 14, 26);
    doc.text(companyFilterSubtitle + (dateFilterActive ? ` · ${fromDate ? fmtDate(fromDate) : 'Start'} – ${toDate ? fmtDate(toDate) : 'Today'}` : ''), 14, 31);
    autoTable(doc, {
      startY: 36,
      head: [['Date','Project','Type','Bank Account','Amount','Audit']],
      body: visibleItems.map(r => [fmtDate(r.receipt_date), projectById(r.project_id)?.project_name || '—', (r.receipt_type||'').replace(/_/g,' '), bankNameDisplay(r.bank_account_id) || r.bank_account || '—', fmtINRPdf(r.amount), (r.audit_status||'').replace(/_/g,' ')]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
    });
    const finalY = doc.lastAutoTable?.finalY || 100;
    doc.setFontSize(10);
    doc.text(`Confirmed Amount: ${fmtINRPdf(totals.amount)}`, 14, finalY + 10);
    if (totals.pendingAmount > 0) doc.text(`Pending Review: ${fmtINRPdf(totals.pendingAmount)}`, 14, finalY + 16);
    doc.save(`income-${Date.now()}.pdf`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="section-eyebrow mb-2">Bank Credits & Customer Payments</div>
          <h1 className="serif text-4xl">Income</h1>
          <div className="text-sm text-muted mt-1 max-w-2xl">Record a payment against any project in one place — pick the project, log the amount, done. Same record as that project's own Receipts tab.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {needsPicker && <GrantPicker
            items={companies}
            selectedIds={companyFilterIds}
            allAccess={allCompaniesSelected}
            onToggle={cid => setCompanyFilterIds(cur => cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid])}
            onSetAllAccess={v => { setAllCompaniesSelected(v); if (v) setCompanyFilterIds([]); }}
            allLabel="All Companies"
            noneWidth={200}
            data-testid="income-filter-company"
          />}
          <MultiFilterPicker filter={projectF} items={visibleProjectsForFilter.map(p => ({ id: p.id, name: p.project_name }))} allLabel="All Projects" width={200} testId="income-filter-project"/>
          {clientOptions.length > 0 && <MultiFilterPicker filter={clientF} items={clientOptions.map(c => ({ id: c.id, name: c.name }))} allLabel="All Clients" width={180} testId="income-filter-client"/>}
          <ClearFiltersButton filters={[projectF, clientF]} extraActive={!allCompaniesSelected} onClear={() => { setAllCompaniesSelected(true); setCompanyFilterIds([]); }}/>
          {/* User-requested: filter by date range, same plain From/To
              convention the Company Fund Ledger and Bank Account Ledger
              filters already use. */}
          <div className="vendor-search">
            <SearchIcon size={14}/>
            <input className="input" placeholder="Search project, client, bank, amount…" value={search} onChange={e => setSearch(e.target.value)} data-testid="income-search"/>
          </div>
          <PeriodPicker from={fromDate} to={toDate} onChange={(f, t) => { setFromDate(f); setToDate(t); }} testId="income-period"/>
          {view === 'cards' && <select className="input" style={{ width: 190 }} value={cardSort} onChange={e => setCardSort(e.target.value)} data-testid="income-card-sort" title="Order of the project cards">
            <option value="received">Sort: Most received</option>
            <option value="latest">Sort: Latest receipt</option>
            <option value="pending">Sort: Most pending</option>
            <option value="name">Sort: Name A–Z</option>
          </select>}
          <div className="inline-flex gap-1" role="group" aria-label="View">
            <button className={"tab btn-small inline-flex items-center gap-1 " + (view === 'cards' ? 'active' : '')} onClick={() => setView('cards')} data-testid="income-view-cards"><LayoutGrid size={13}/>By project</button>
            <button className={"tab btn-small inline-flex items-center gap-1 " + (view === 'table' ? 'active' : '')} onClick={() => setView('table')} data-testid="income-view-table"><ListIcon size={13}/>Entries</button>
          </div>
          {visibleItems.length > 0 && <button onClick={exportPDF} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Export PDF</button>}
          {visibleItems.length > 0 && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>}
          {canEdit && <button onClick={() => { openAdd(); setOpen(true); }} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-income"><Plus size={14} />Add Income</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 no-print" data-testid="income-kpis">
        <KpiCard label="Entries" value={visibleItems.length} icon={TrendingUp} hint={`${incomeGroups.length} project${incomeGroups.length === 1 ? '' : 's'}`} />
        <KpiCard label="Confirmed Amount" value={fmtINR(totals.amount)} tone="green" hint="Matched/final receipts — matches this project's own P&L" />
        <KpiCard label="Pending Review" value={totals.pending} tone="amber" hint={totals.pendingAmount > 0 ? `${fmtINR(totals.pendingAmount)} not yet confirmed` : undefined} />
        <KpiCard label="Top project" value={(() => { const t = [...incomeGroups].sort((a, b) => b.confirmed - a.confirmed)[0]; return t && t.confirmed > 0 ? fmtINR(t.confirmed) : '—'; })()} tone="gold" hint={(() => { const t = [...incomeGroups].sort((a, b) => b.confirmed - a.confirmed)[0]; return t && t.confirmed > 0 ? incomeGroupName(t) : 'No confirmed income yet'; })()} />
      </div>

      {view === 'cards' && (
        <div className="no-print" data-testid="income-cards">
          <div className="text-xs text-muted mb-3">Income by project — <b>{incomeGroups.length}</b> project{incomeGroups.length === 1 ? '' : 's'} with receipts, {visibleItems.length} of {items.length} entries{visibleItems.length !== items.length ? ' (filtered)' : ''}. Only matched/final receipts count as received.</div>
          {incomeGroups.length === 0
            ? <div className="card card-dark text-center text-muted py-10">{items.length === 0 ? 'No income recorded yet.' : 'No entries match these filters.'}</div>
            : <IncomeCards groups={incomeGroups} needsPicker={needsPicker} companyName={companyName} onViewEntries={viewProjectEntries}/>}
        </div>
      )}

      <div className={"card card-dark overflow-x-auto print-area " + (view === 'cards' ? 'vendor-table-print-only' : '')} data-testid="income-table">
        <div className="print-header">
          <div className="print-company">{companyFilterSubtitle}</div>
          <div className="print-report-title">Income</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}{dateFilterActive ? ` · Period: ${fromDate ? fmtDate(fromDate) : 'Start'} – ${toDate ? fmtDate(toDate) : 'Today'}` : ''}</div>
        </div>
        <table className="tbl">
          <thead><tr>
            <SortTh label="Date" sortKeyName="receipt_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Project" sortKeyName="_project_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {needsPicker && <SortTh label="Company" sortKeyName="_company_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>}
            <SortTh label="Type" sortKeyName="receipt_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Bank Account" sortKeyName="_bank_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Amount" sortKeyName="amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
            <SortTh label="Audit" sortKeyName="audit_status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {isMasterAdmin(user) && <th className="no-print"></th>}
          </tr></thead>
          <tbody>
            {visibleItems.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-8">{items.length === 0 ? 'No income recorded yet.' : 'No entries match these filters.'}</td></tr>}
            {sortedItems.map(r => {
              const proj = projectById(r.project_id);
              // User-requested: hover the row to see the linked bill's
              // description (or every linked bill's, for a multi-bill
              // receipt) -- same pattern as the Bills page's own row
              // hover and the per-project Receipts tab.
              const allocBills = (allocMap[r.id] || []).map(a => allBills.find(b => b.id === a.bill_id)).filter(Boolean);
              const linkedBillTitle = allocBills.length === 0 ? undefined :
                allocBills.map(b => `${b.bill_number || fmtDate(b.bill_date)}${b.description ? ': ' + b.description : ''}`).join(' | ');
              return (
                <tr key={r.id} title={linkedBillTitle}>
                  <td className="text-xs">{fmtDate(r.receipt_date)}</td>
                  <td>{proj ? <Link to={`/app/projects/${proj.id}`} className="hover:text-[color:var(--gold)]">{proj.project_name}</Link> : '—'}</td>
                  {needsPicker && <td className="text-xs">{companyName(proj?.company_id)}</td>}
                  <td className="text-xs">{(r.receipt_type || '').replace(/_/g, ' ')}</td>
                  <td className="text-xs">{bankNameDisplay(r.bank_account_id) || r.bank_account || '—'}</td>
                  <td className="mono text-right text-green-400">{fmtINR(r.amount)}</td>
                  <td><AuditStatusChip status={r.audit_status} /></td>
                  {isMasterAdmin(user) && <td className="text-right no-print"><button aria-label="Delete" onClick={() => del(r.id)} className="tab btn-small text-red-300"><Trash2 size={12} /></button></td>}
                </tr>
              );
            })}
          </tbody>
          {/* User-requested: this page's only total was the "Confirmed
              Amount" KPI card above, which deliberately excludes anything
              not yet matched/final -- unlike every other amount list in
              the app (Bills/BillsHub/Expenses/Receipts...), the table
              itself never had a total-of-what's-shown footer. Same
              tfoot convention as BillsHub, respecting whatever
              company/project/client filters are active. */}
          {visibleItems.length > 0 && <tfoot><tr className="font-semibold">
            <td colSpan={needsPicker ? 5 : 4} className="text-right text-muted uppercase text-xs">Total</td>
            <td className="mono text-right text-green-400">{fmtINR(visibleItems.reduce((a,r)=>a+Number(r.amount||0),0))}</td>
            <td colSpan={isMasterAdmin(user) ? 2 : 1}></td>
          </tr></tfoot>}
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Income" maxW={720}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {needsPicker && <Field label="Company"><select className="input" value={form.company_id || ''} onChange={e => setForm({ ...form, company_id: e.target.value, project_id: '', bank_account_id: '' })}>
            <option value="">— select —</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></Field>}
          {/* User-requested: keep this picker dry/clean and free of dead
              ends -- a closed/settled/profit-approved project already
              rejects a new receipt server-side (lock check), so listing
              one here just invites picking something that will fail on
              Save. Reports' own project picker deliberately keeps every
              status, since reviewing a closed project's real history is
              the normal reason to look at Reports at all -- this filter is
              specific to CREATE flows like this one, not viewing/history. */}
          <Field label="Project *"><select className="input" value={form.project_id || ''} onChange={e => setForm({ ...form, project_id: e.target.value, billAllocations: [], bank_account_id: '' })} data-testid="income-project">
            <option value="">— select —</option>
            {/* User-requested: when a Client filter is active on the page,
                narrow this to that client's own projects too -- same
                "pick the anchor first" narrowing Company already does. */}
            {projectsForCompany(form.company_id).filter(p => !isProjectLocked(p)).filter(p => clientF.matches(clientIdOfProject(p, masters.department_client))).map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select></Field>
          <Field label="Date *"><input type="date" className="input" value={form.receipt_date || ''} onChange={e => setForm({ ...form, receipt_date: e.target.value })} /></Field>
          <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount || ''} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="income-amount" /></Field>
          <Field label="Type"><select className="input" value={form.receipt_type || 'customer_payment'} onChange={e => setForm({ ...form, receipt_type: e.target.value })}>
            {RCP_TYPES.map(x => <option key={x} value={x}>{x.replace(/_/g, ' ')}</option>)}
          </select></Field>
          {form.project_id && <BillAllocationPicker bills={openBills} amount={form.amount} allocations={form.billAllocations||[]}
            setAllocations={(a)=>setForm({...form, billAllocations:a})} billLabel={billLabel}/>}
          {form.project_id && <ReceiptDeductionsPicker bills={openBills} allocations={form.billAllocations||[]} lines={form.deductionLines||[]}
            setLines={(l)=>setForm({...form, deductionLines:l})} masters={masters} companyId={formCompanyId} isEdit={!!form.id}/>}
          <Field label="Bank Account (money credited to)"><select className="input" value={form.bank_account_id || ''} onChange={e => setForm({ ...form, bank_account_id: e.target.value })}>
            <option value="">— select —</option>
            {eligibleBanks.map(b => <option key={b.id} value={b.id}>{b.bank_name}{needsPicker ? ` — ${companyName(b.company_id)}` : ''}</option>)}
          </select></Field>
          <Field label="Bank Reference"><input className="input" value={form.bank_reference || ''} onChange={e => setForm({ ...form, bank_reference: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Description"><input className="input" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={() => setOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="income-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

// ============= PROJECT DETAIL =============
// Real-usage-gap finding: this used to be a fixed, uneditable spend-type
// list shared by both project and company expenses. Replaced by the
// project/company-scoped "expense_categories" masters (Settings > Expense
// Categories) -- see ExpenseCategoryIn.scope on the backend. The same 20
// values now live there as the seed data (_DEFAULT_EXPENSE_CATEGORIES,
// server.py) so nothing changes for existing users until they edit the
// list themselves.
const INV_TYPES = ['capital_investment','partner_loan','other'];
// User-requested: this tab's own Type dropdown showed raw enum values,
// mismatched with the plain-language labels PARTNER_TXN_QUICK_CATEGORIES
// already uses for the exact same two concepts (capital_in/loan_in) on the
// newer quick-add form -- same words here so a reviewer sees one
// consistent vocabulary for "money coming in" everywhere in the app.
const INV_TYPE_LABELS = { capital_investment: 'Capital Investment', partner_loan: 'Loan from Partner', other: 'Other' };
const RCP_TYPES = ['customer_payment','running_bill','final_payment','advance','other'];
// User-requested (full-app audit): receipt_type showed the raw enum value
// (underscores and all) in several places -- same plain-language treatment
// already applied to every other enum in the app.
const RCP_TYPE_LABELS = { customer_payment: 'Customer Payment', running_bill: 'Running Bill', final_payment: 'Final Payment', advance: 'Advance', other: 'Other' };
const DED_TYPES = ['security_deposit','retention','tds','gst','gst_differential','labour_cess','cnc_royalty','other_deduction'];
// User-requested (full-app audit): bill status showed the raw enum value
// (e.g. "partially_received") in both Bills pages' dropdowns and status
// badges, while the PDF export of the identical field already humanized
// it -- extracted here once instead of the same raw array repeated 3
// times inline across BillsPanel/BillsHub.
const BILL_STATUSES = ['draft','billed','partially_received','received','disputed'];
const BILL_STATUS_LABELS = { draft: 'Draft', billed: 'Billed', partially_received: 'Partially Received', received: 'Received', disputed: 'Disputed' };
// not_refundable listed first, matching the backend's default — so
// whatever a plain <select> visually pre-selects (its first option, before
// the user has touched it) is never out of sync with what actually saves.
const DEDUCTION_CLASSIFICATION_LABELS = { not_refundable: 'Not Refundable', refundable: 'Refundable', future_liability: 'Future Liability' };
const GST_SLABS = [0, 2, 5, 6, 8, 12, 16, 18, 28];
// Production hardening: capital_investment/partner_loan/personal_expense
// removed -- those are real, correctly-calculated concepts, but only as
// investments.investment_type on the Investments tab. A partner_transactions
// row with one of these types looked identical to a real entry but was
// never read by any total/pool/settlement calculation. Capital
// contribution to a project -> Investments tab; everything left here is a
// partner payment/loan-repayment/advance/etc. against money already
// accounted for elsewhere.
const TXN_TYPES = ['capital_return','loan_repayment','profit_payment','expense_reimbursement','withdrawal','other','interest_credit','interest_charge'];
// User-requested: every raw type dropdown for this table showed the bare
// enum string, mismatched with the plain-language wording the newer
// "Partner Transaction" quick-add and the project-page Transactions tab
// both already use for the same 6 "money out" types (PARTNER_TXN_QUICK_
// CATEGORIES) -- same short names here, plus the 2 interest types those
// two forms deliberately don't offer at all (Partner Settlement's own Add
// Transaction modal and the Audit Queue's edit-in-queue modal both do).
const TXN_TYPE_LABELS = {
  capital_return: 'Capital Return', loan_repayment: 'Loan Repayment', profit_payment: 'Profit Payment',
  expense_reimbursement: 'Reimbursement', withdrawal: 'Withdrawal', other: 'Other',
  interest_credit: 'Interest Credit (owed to partner)', interest_charge: 'Interest Charge (owed by partner)',
};
// The Partner Ledger's own "type" column carries whichever of these its
// row's own source doesn't already have a plain-language label for
// (personal_expense/profit_entitlement/paid_on_behalf/capital_contribution
// aren't investment_type, transaction_type, or a security instrument type
// -- see the ledger table's own lookup chain).
const LEDGER_ENTRY_TYPE_LABELS = {
  personal_expense: 'Personal Expense', profit_entitlement: 'Profit Entitlement',
  paid_on_behalf: 'Paid on Behalf', capital_contribution: 'Capital Contribution',
};

// User-requested: a single, plain-language "Partner Transaction" quick-add
// entry point (Company Expenses page) for daily data entry by someone
// without deep accounting/classification knowledge -- one form, category
// options in everyday language instead of raw type names. Under the hood
// each category routes to whichever of the 3 real tables already carries
// that kind of partner money movement (investments / company_capital_
// contributions / partner_transactions), so it shows up in Partner
// Settlement automatically through the existing ledger engine -- no new
// calculation code, just correct routing at entry time. Interest Credit/
// Charge are deliberately left off this list -- those are admin-reviewed
// Partner Current Account figures, not everyday entries; still available
// on the Transactions tab and Partner Current Account screen.
// User-requested: a soft warning (never a hard block) before recording a
// Profit Payment that would pay out more than a project has actually
// collected in cash -- "cash position" here is exactly the project's own
// cash_received - cash_paid (GET /projects/{id}/financials), which
// already includes every prior profit payment already made, so this
// naturally accounts for partial payments made over time, not just a
// single lump sum. Only checked when the payment is tied to a specific
// project -- there's no single project's cash position to check against
// for a company-wide (no project) payment, and this deliberately never
// blocks the save on its own failure (a network hiccup on this one extra
// read should never be the reason a real payment can't be recorded).
async function confirmProfitPaymentCash(projectId, amount, partnerId) {
  if (!projectId || !amount) return true;
  try {
    const fin = (await api.get(`/projects/${projectId}/financials`)).data;
    // Cash-backed check: how much of THIS partner's profit has actually been collected from the client (costs recovered
    // first). Paying more than that pays profit ahead of collection, and the money has to come from somewhere else.
    const part = partnerId && fin?.cash_backing ? (fin.cash_backing.partners || []).find(x => x.partner_id === partnerId) : null;
    if (part && amount > part.payable_now + 0.5) {
      // Not a question any more: the server refuses it too (see _assert_profit_payment_within_cash). Explained here before it is sent.
      toast.error(`Profit payment blocked: only ${fmtINR(part.payable_now)} of ${part.partner_name}'s profit on this project can be paid now (the client has paid ${fmtINR(fin.cash_backing.receipts)} of ${fmtINR(fin.cash_backing.billed)} billed, and costs are recovered first). The rest becomes payable as the client pays. If this is an advance, record it as a Withdrawal.`, { duration: 9000 });
      return false;
    }
    // Full-app audit finding: this warning used to compare against the
    // raw cash_position, which includes any receipt earmarked for the
    // shared Company Fund (opt-in, via a Security & Refundables recovery)
    // -- money this project shouldn't treat as freely available for a
    // partner profit payment, since it's separately credited to the Fund
    // Ledger too. Net it out so the warning reflects what's actually
    // spendable by this project.
    const cashPosition = Number(fin?.totals?.cash_position ?? 0) - Number(fin?.totals?.cash_earmarked_company_fund ?? 0);
    const shortfall = Math.round((amount - cashPosition) * 100) / 100;
    if (shortfall > 0.5) {
      return window.confirm(`This project's current cash position is ${fmtINR(cashPosition)}, but this Profit Payment is ${fmtINR(amount)} — paying it would leave the project ${fmtINR(shortfall)} short of what's actually been collected so far. Pay anyway?`);
    }
    return true;
  } catch (e) { return true; }
}
const PARTNER_TXN_QUICK_CATEGORIES = [
  { key: 'capital_in', label: 'Capital Investment — partner is putting money IN', direction: 'in',
    hint: 'Pick a Project if this investment is for that project specifically, or leave Project blank for a company-wide investment.' },
  { key: 'loan_in', label: 'Loan from Partner — partner is lending money to a project', direction: 'in', projectRequired: true,
    hint: 'A Project must be selected — loans are tracked against a specific project.' },
  { key: 'capital_return', label: 'Capital Return — paying back a partner’s invested capital', direction: 'out', type: 'capital_return' },
  { key: 'loan_repayment', label: 'Loan Repayment — paying back a partner’s loan', direction: 'out', type: 'loan_repayment' },
  { key: 'profit_payment', label: 'Profit Payment — paying a partner their profit share', direction: 'out', type: 'profit_payment' },
  { key: 'reimbursement', label: 'Reimbursement — paying back a partner who personally paid a bill/expense', direction: 'out', type: 'expense_reimbursement' },
  { key: 'withdrawal', label: 'Withdrawal — partner is taking money out / an advance', direction: 'out', type: 'withdrawal' },
  { key: 'other', label: 'Other — doesn’t fit any of the above', direction: 'out', type: 'other' },
];
// User-requested: the Audit Queue's edit-in-queue modal exists
// specifically to correct an already-entered mistake -- unlike the
// quick-add/Partner Settlement forms (everyday data entry, where
// Interest Credit/Charge are deliberately hidden so they're never
// picked by accident), an audit correction tool needs every type
// selectable, including reclassifying a row into or out of Interest.
// Same plain-language wording as PARTNER_TXN_QUICK_CATEGORIES for the 6
// everyday types (borrowed here rather than duplicated), TXN_TYPE_LABELS'
// own wording for the 2 interest ones it has no equivalent for.
const AUDIT_TXN_TYPE_LABELS = Object.fromEntries(TXN_TYPES.map(t => [
  t, PARTNER_TXN_QUICK_CATEGORIES.find(c => c.direction === 'out' && c.type === t)?.label || TXN_TYPE_LABELS[t],
]));

const PROJECT_TAB_NAMES = ['overview','expenses','receipts','investments','bills','deductions','transactions','adjustments','reconciliation','partners','final','settlement','audit'];

// Keeps a screen's money figures current without a manual refresh: when the browser tab/app comes back into view (after a
// receipt was entered on another tab, a phone, or by a colleague), the screen re-reads its own data -- quietly, at most
// once every 20 seconds, never while the page is hidden. `fn` must be a silent loader (no spinner, no state reset).
function useLiveRefresh(fn) {
  const ref = useRef(fn); ref.current = fn;
  useEffect(() => {
    let last = Date.now();
    const wake = () => { if (document.visibilityState === 'visible' && Date.now() - last > 20000) { last = Date.now(); try { ref.current(); } catch (e) { /* silent */ } } };
    window.addEventListener('focus', wake); document.addEventListener('visibilitychange', wake);
    return () => { window.removeEventListener('focus', wake); document.removeEventListener('visibilitychange', wake); };
  }, []);
}

function ProjectDetail() {
  const { id } = useParams();
  const vendors = useVendors();
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  // The open tab lives in the URL (?tab=expenses), so Back/Forward step
  // through tabs, a refresh keeps the place, and Back from here lands on the
  // list the project was opened from instead of skipping past it.
  const [tabParams, setTabParams] = useSearchParams();
  const tab = tabParams.get('tab') || 'overview';
  const setTab = (t) => setTabParams(t === 'overview' ? {} : { tab: t });
  const [fin, setFin] = useState(null);
  const [partners, setPartners] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [ptxns, setPtxns] = useState([]);
  // User-requested: let a manually-entered deduction reference a specific
  // bill (Deductions tab's own Bill field) -- BillsPanel manages its own
  // separate `bills` state, so this is a lightweight fetch just for that
  // picker rather than lifting BillsPanel's state up.
  const [projectBills, setProjectBills] = useState([]);
  const [roles, setRoles] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [projectExpenseCategories, setProjectExpenseCategories] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  // Real-usage-gap finding: "Move into Project" only ever existed on the
  // ExtraWorks list page's row action -- opening a bucket from there gave
  // no way back to it short of navigating back to that list. Same modal,
  // now reachable from here too.
  const [moveOpen, setMoveOpen] = useState(false);
  const masters = useMasters();
  const statusOptions = (masters.project_status?.map(m=>m.name)) || DEFAULT_PROJECT_STATUSES;

  // Round 18 finding: navigating from one project's page to another
  // before the first load resolves let the OLD project's data land after
  // the new page had already started rendering (React Router doesn't
  // remount this component on a route-param change alone) -- loadToken
  // tags each load; a resolving call only applies its result if it's
  // still the most recent one issued for the current `id`.
  const loadToken = useRef(0);
  const loadAll = async () => {
    const myToken = ++loadToken.current;
    try {
      const [f, p, i, e, r, d, t, ro, ec, b] = await Promise.all([
        api.get(`/projects/${id}/financials`),
        api.get('/partners'),
        api.get('/investments', { params: { project_id: id } }),
        api.get('/expenses', { params: { project_id: id } }),
        api.get('/receipts', { params: { project_id: id } }),
        api.get('/deductions', { params: { project_id: id } }),
        api.get('/partner-transactions', { params: { project_id: id } }),
        api.get(`/projects/${id}/roles`),
        api.get('/expense-categories', { params: { scope: 'project' } }),
        api.get('/bills', { params: { project_id: id } }),
      ]);
      if (loadToken.current !== myToken) return;
      setFin(f.data); setPartners(mastersForCompany(p.data, f.data.project.company_id)); setInvestments(i.data); setExpenses(e.data);
      setReceipts(r.data); setDeductions(d.data); setPtxns(t.data); setRoles(ro.data);
      setProjectExpenseCategories(mastersForCompany(ec.data, f.data.project.company_id));
      setProjectBills(b.data);
    } catch (e) {
      if (loadToken.current !== myToken) return;
      setLoadError(e.response?.data?.detail || 'Could not load this project');
      toast.error(e.response?.data?.detail || 'Could not load this project');
    }
  };
  useEffect(() => { loadAll(); }, [id]);
  useLiveRefresh(() => loadAll());
  useEffect(() => { if (fin?.project?.company_id) api.get('/bank-accounts').then(r=>setBankAccounts(r.data.filter(b=>b.company_id===fin.project.company_id))); }, [fin?.project?.company_id]);
  useEffect(() => { if (fin?.project?.company_id) api.get('/staff').then(r=>setStaffList(forCompany(r.data, fin.project.company_id))); }, [fin?.project?.company_id]);
  // User-requested: a reopened project (financial_review, but was locked
  // before) previously only got a reminder that it needs re-approval
  // INSIDE the Settlement tab (SettlementPanel's own amber note) — anyone
  // working a different tab (fixing the expense/receipt that prompted the
  // reopen in the first place) never saw it, so a reopened project could
  // sit unapproved indefinitely with no page-level signal. This mirrors
  // that same /projects/{id}/distributions check at the page header
  // instead, visible regardless of which tab is open.
  const [reopenedPriorApproval, setReopenedPriorApproval] = useState(null);
  useEffect(() => {
    if (fin?.project?.status !== 'financial_review') { setReopenedPriorApproval(null); return; }
    api.get(`/projects/${id}/distributions`).then(r => setReopenedPriorApproval((r.data||[]).find(d=>d.approved) || null)).catch(()=>{});
  }, [id, fin?.project?.status]);

  if (loadError) return <div className="max-w-2xl mx-auto px-6 py-16 text-center"><div className="text-lg text-red-400 mb-2">{loadError}</div><Link to="/app/projects" className="text-sm text-muted hover:text-[color:var(--gold)]">← Back to Projects</Link></div>;
  if (!fin) return <Loading/>;
  const p = fin.project; const t = fin.totals;
  const partnerName = (pid) => partners.find(x=>x.id===pid)?.name || '—';
  const changeStatus = async (newStatus) => {
    if (newStatus === p.status) return;
    if (newStatus === 'closed' && !window.confirm(`Close "${p.project_name}"?\n\nThis marks the tender fully wound up. It can still be reopened later if needed.`)) return;
    // Once locked, this dropdown only ever offers forward moves within the
    // locked lifecycle (see lockAdvanceOptions) — those go through
    // advance-status, not the plain status endpoint, which now refuses any
    // direct write on a locked project (see POST /projects/{id}/status).
    const endpoint = isProjectLocked(p) ? 'advance-status' : 'status';
    try { await api.post(`/projects/${id}/${endpoint}`, { status: newStatus }); loadAll(); toast.success('Status updated'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const doReopen = async () => {
    if (!reopenReason.trim()) { toast.error('A reason is required to reopen a locked project'); return; }
    if (reopening) return;
    setReopening(true);
    try {
      await api.post(`/projects/${id}/reopen`, { reason: reopenReason.trim() });
      setReopenOpen(false); setReopenReason(''); loadAll();
      toast.success('Project reopened for editing');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setReopening(false); }
  };
  const locked = isProjectLocked(p);
  const advanceOpts = locked ? lockAdvanceOptions(p) : [];
  // Locked: only the current status plus whatever's still ahead in the
  // locked lifecycle (advance-status enforces the same forward-only rule
  // server-side) — everything else requires Reopen first.
  const statusSelectOptions = locked ? [p.status, ...advanceOpts] : statusOptions;
  // Real-usage-gap finding: an Extra Work bucket (verbal-order/unawarded
  // work, tracked as a project with is_extra_work=true until formally
  // moved into a real tender) is meant to be a narrow "just get bills out"
  // workflow -- receipts, deductions, partner transactions/roles, profit
  // adjustments, reconciliation, and settlement all belong to the real
  // project it eventually moves into, not the bucket itself. Once merged
  // (merged_into_project_id set) or for a normal project, the full tab set
  // applies as before. Mirrored server-side (see the create endpoints'
  // _assert_not_unmoved_extra_work guard) so this is a genuine
  // restriction, not just a hidden button -- gates both the tab button
  // list and each restricted tab's own content render below.
  const tabHiddenForExtraWork = p.is_extra_work && !p.merged_into_project_id;
  const EXTRA_WORK_ALLOWED_TABS = ['overview','expenses','investments','bills','audit'];
  // A stale or hand-edited ?tab= (unknown name, or one this project type
  // hides, e.g. Final Calculation on an Extra Work) would render a blank
  // page -- drop it and show the Overview instead.
  if (tab !== 'overview' && (!PROJECT_TAB_NAMES.includes(tab) || (tabHiddenForExtraWork && !EXTRA_WORK_ALLOWED_TABS.includes(tab)))) {
    return <Navigate to={`/app/projects/${id}`} replace/>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <Link to="/app/projects" className="text-xs text-muted hover:text-[color:var(--gold)]">← Back to Projects</Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mt-3 mb-6">
        <div>
          <div className="section-eyebrow mb-1">Project</div>
          <h1 className="serif text-4xl">{p.project_name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            <MetaChip icon={Layers} label="Work Category" value={p.work_category} tone="blue"/>
            <MetaChip icon={FileText} label="Tender No." value={p.tender_number} tone="gold"/>
            <MetaChip icon={Users} label="Client" value={p.client_name} tone="blue"/>
            <MetaChip icon={Building2} label="Department" value={p.department} tone="green"/>
            <MetaChip icon={ListChecks} label="Work Order" value={p.work_order_number} tone="amber"/>
            <MetaChip icon={Calendar} label="Financial Year" value={p.financial_year} tone="gold"/>
          </div>
        </div>
        {/* Mobile audit fix: 2 badges + a 180px status select + a Reopen
            button can exceed a narrow screen's width even after the OUTER
            row (already flex-wrap) drops this whole group below the
            title -- this group needed its own wrap too. */}
        <div className="flex flex-wrap items-center gap-2">
          <ProjectStatusBadge status={p.status}/>
          <ProfitLossBadge profit={t.project_profit}/>
          {isAdmin(user) && (
            <select
              className="input"
              style={{width:180}}
              value={p.status}
              onChange={e=>changeStatus(e.target.value)}
              disabled={locked && advanceOpts.length === 0}
              title={locked ? (advanceOpts.length ? 'Locked — can only move forward, or use Reopen (with a reason) to unlock' : 'Locked and fully closed — use Reopen (with a reason) to unlock') : undefined}
              data-testid="status-select"
            >
              {statusSelectOptions.map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </select>
          )}
          {isAdmin(user) && isProjectLocked(p) && (
            <button onClick={()=>setReopenOpen(true)} className="btn-amber btn-small inline-flex items-center gap-1" data-testid="reopen-btn"><Unlock size={14}/>Reopen</button>
          )}
        </div>
      </div>

      {p.is_extra_work && !p.merged_into_project_id && (
        <div className="card card-dark mb-6" style={{borderColor:'rgba(201,168,76,0.4)'}}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <Layers size={16} className="text-[color:var(--gold)] flex-shrink-0"/>
              <span><b>Extra Work bucket</b> — not yet tied to a named Tender/Voucher Payment project. Move it once identified; every expense, receipt, and rupee of profit moves with it.</span>
            </div>
            {isAdmin(user)
              ? <button onClick={()=>setMoveOpen(true)} className="btn-gold btn-small inline-flex items-center gap-1 flex-shrink-0" data-testid="move-into-project-btn"><GitMerge size={14}/>Move into Project</button>
              : <span className="text-xs text-muted flex-shrink-0" title="Moving into a project absorbs this bucket with no undo — restricted to admin roles.">Move: admin only</span>}
          </div>
        </div>
      )}
      {moveOpen && <MoveExtraWorkModal project={p} onClose={()=>setMoveOpen(false)} onMoved={loadAll}/>}

      {reopenedPriorApproval && (
        <div className="card card-dark mb-6" style={{borderColor:'rgba(230,180,80,0.4)'}}>
          <div className="flex items-center gap-2 text-sm text-amber-400">
            <Unlock size={16} className="flex-shrink-0"/>
            <span>This project was previously approved on {fmtDate(reopenedPriorApproval.approved_at)} by {reopenedPriorApproval.approved_by}, then reopened for edits — it needs to be re-approved (Settlement tab) before it can lock again with the updated figures.</span>
          </div>
        </div>
      )}

      <Modal open={reopenOpen} onClose={()=>{setReopenOpen(false); setReopenReason('');}} title="Reopen Project">
        <p className="text-xs text-muted mb-4">This unlocks "{p.project_name}" for editing again (moves it to Financial Review). The approved settlement stays in history — re-approving afterward writes a fresh one on top. A reason is required and recorded in the audit log.</p>
        <Field label="Reason *"><textarea className="input" rows={3} value={reopenReason} onChange={e=>setReopenReason(e.target.value)} placeholder="e.g. Client disputed a receipt amount, correcting before final settlement" data-testid="reopen-reason"/></Field>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={()=>{setReopenOpen(false); setReopenReason('');}} className="btn-outline btn-small">Cancel</button>
          <button onClick={doReopen} disabled={!reopenReason.trim()||reopening} className="btn-gold btn-small" data-testid="reopen-confirm">{reopening ? 'Reopening…' : 'Reopen'}</button>
        </div>
      </Modal>

      <div className="flex flex-wrap gap-2 mb-6">
        {/* Ordered by how often an accountant actually opens each tab day to
            day — overview first as the landing view, then the routine
            data-entry tabs (expenses/receipts/investments/bills/deductions/
            transactions get touched constantly), then occasional upkeep
            (adjustments/reconciliation/partners), then end-of-lifecycle or
            rarely-visited views (final calculation, settlement, audit). */}
        {PROJECT_TAB_NAMES
          .filter(x => !tabHiddenForExtraWork || EXTRA_WORK_ALLOWED_TABS.includes(x))
          .filter(x => x!=='partners' || ['partner','admin','master_admin'].includes(user.role))
          // Whole-app final-check finding: GET /audit-logs is admin/
          // accountant-tier only (require_roles("admin","accountant"),
          // master_admin auto-passes) -- this tab was shown to every
          // role regardless, so a partner or viewer always got a
          // permanently empty "No audit entries." table with no
          // indication it was actually an access restriction, not real
          // history. Hidden the same way the partners tab above already
          // is for roles that can't use it.
          .filter(x => x!=='audit' || ['admin','accountant','master_admin'].includes(user.role))
          .map(x => (
          <button key={x} className={"tab " + (tab===x?'active':'')} onClick={()=>setTab(x)} data-testid={`tab-${x}`}>{x==='final'?'Final Calculation':x}</button>
        ))}
      </div>

      {tab==='overview' && (
        <div className="grid md:grid-cols-4 gap-4">
          <div className="md:col-span-4"><EntityFlagsBar entityType="project" entityId={id} canEdit={canEdit}/></div>
          {(p.needs_follow_up || Object.keys(PROJECT_STATUS_AXES).some(k=>p[k])) && (
            <div className="md:col-span-4 card card-dark">
              <div className="flex items-center justify-between mb-2">
                <div className="serif text-lg text-[color:var(--gold)]">Tender Lifecycle</div>
                {p.needs_follow_up && <span className="badge status-financial_review">Needs Follow-up</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(PROJECT_STATUS_AXES).filter(([k])=>p[k]).map(([k,{label}], i) => (
                  <MetaChip key={k} label={label} value={axisLabel(p[k])} tone={META_CHIP_TONE_CYCLE[i % META_CHIP_TONE_CYCLE.length]}/>
                ))}
              </div>
            </div>
          )}
          <KpiCard label="Contract (Net)" value={fmtINR(t.net_contract_value)} tone="gold"/>
          <KpiCard label="Investments" value={fmtINR(t.total_investments)} tone="blue"/>
          <KpiCard label="Expenses" value={fmtINR(t.total_expenses)} tone="red" hint={t.expenses_pending_review>0 ? `+ ${fmtINR(t.expenses_pending_review)} pending review, not counted` : null}/>
          <KpiCard label="Receipts" value={fmtINR(t.total_receipts)} tone="green" hint={t.receipts_pending_review>0 ? `+ ${fmtINR(t.receipts_pending_review)} pending review, not counted` : null}/>
          <KpiCard label="Receivable" value={fmtINR(t.outstanding_receivable)} tone="amber" hint="Billed minus received minus deductions"/>
          <KpiCard label="Deductions" value={fmtINR(t.total_deductions)} hint={`Recoverable: ${fmtINR(t.total_deductions_recoverable)} · Non-recoverable: ${fmtINR(t.total_deductions_non_recoverable)}`}/>
          <KpiCard label="Project Profit" value={fmtINR(t.project_profit)} tone={Math.abs(t.project_profit)<0.5?'gold':(t.project_profit>0?'green':'red')} hint={[t.total_deductions_non_recoverable>0 ? `Net of ${fmtINR(t.total_deductions_non_recoverable)} non-recoverable deductions` : null, t.total_security_forfeited>0 ? `Net of ${fmtINR(t.total_security_forfeited)} forfeited security` : null].filter(Boolean).join(' · ') || null}/>
          {/* Full-app audit finding: cash_position previously counted a
              receipt earmarked for the shared Company Fund (recorded via
              a Security & Refundables recovery) at full value with no way
              to tell it apart from ordinary spendable project cash --
              risking the same rupee being treated as available here AND
              via its separate Company Fund Ledger credit. Same hint
              pattern every other KPI card on this row already uses. */}
          <KpiCard label="Cash Position" value={fmtINR(t.cash_position)} tone="blue" hint={t.cash_earmarked_company_fund>0 ? `of which ${fmtINR(t.cash_earmarked_company_fund)} is earmarked for the Company Fund, not freely spendable by this project` : null}/>
          <div className="md:col-span-4 card card-dark">
            <div className="serif text-xl mb-2 text-[color:var(--gold)]">P&L Breakdown</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              <div>Contract (Net): <span className="mono">{fmtINR(t.net_contract_value)}</span></div>
              <div>− Expenses: <span className="mono">{fmtINR(t.total_expenses)}</span></div>
              {t.total_deductions_non_recoverable > 0 && <div>− Non-Recoverable Deductions: <span className="mono">{fmtINR(t.total_deductions_non_recoverable)}</span></div>}
              {t.total_security_forfeited > 0 && <div>− Forfeited Security: <span className="mono">{fmtINR(t.total_security_forfeited)}</span></div>}
              <div>= Project Profit: <span className={"mono " + profitColorClass(t.project_profit)}>{fmtINR(t.project_profit)}</span></div>
              <div>+ Adjustment: <span className="mono">{fmtINR(t.profit_adjustment)}</span></div>
              <div>= Distributable: <span className="mono text-[color:var(--gold)]">{fmtINR(t.distributable_profit)}</span></div>
            </div>
          </div>
        </div>
      )}

      {tab==='final' && !tabHiddenForExtraWork && <FinalCalculationPanel fin={fin} projectId={id} reload={loadAll} canApprove={isAdmin(user)} user={user}/>}

      {tab==='investments' && <CrudPanel title="Investments" items={investments} reload={loadAll} testPrefix="inv"
        hint="A partner's capital contribution to this project. For payments, loans, advances, or reimbursements to/from a partner, use the Transactions tab instead."
        columns={[['transaction_date','Date',fmtDate],['partner_id','Partner',v=>partnerName(v)],['investment_type','Type',v=>INV_TYPE_LABELS[v]||v],['amount','Amount',fmtINR],['voucher_number','Voucher No.',v=>v||'—'],['notes','Notes']]}
        fields={[
          {k:'voucher_number',l:'Voucher No. *'},
          {k:'transaction_date',l:'Date *',t:'date'},
          // User-reported bug: this used to be every company partner
          // unfiltered, so an investor mapped to a DIFFERENT project (or a
          // company-wide investor, who never personally invests per-
          // project at all) showed up here too. Now filtered the same way
          // every other partner picker in the app is, via
          // eligiblePaidByPartners's project-scoping -- keepSelectedId
          // (now safe: opts can see the live form, see CrudPanel above)
          // still shows an already-saved partner even if they've since
          // been unmapped from this project.
          {k:'partner_id',l:'Partner *',t:'select',opts:(form)=>eligiblePaidByPartners(partners, p.company_id, id, false, form.partner_id).map(x=>[x.id,partnerOptionLabel(x)])},
          {k:'investment_type',l:'Type',t:'select',opts:INV_TYPES.map(x=>[x,INV_TYPE_LABELS[x]||x])},
          {k:'amount',l:'Amount *',t:'number'},
          // A project-tied investment doesn't always mean the cash pooled
          // into the shared Company Fund/Reserve — it may have gone
          // straight into this project's own spend instead. Opt-in,
          // mirroring paid_from_company_fund on the expense/transaction
          // side — only ticked rows count as a Fund Ledger credit.
          {k:'paid_to_company_fund',l:'Added to Company Reserve / Common Fund',t:'checkbox'},
          {k:'notes',l:'Notes'},
        ]} endpoint="/investments" projectId={id} canEdit={canEdit} canDelete={isMasterAdmin(user)} project={p} amountKey="amount"/>}

      {tab==='expenses' && <CrudPanel title="Expenses" items={expenses} reload={loadAll} testPrefix="exp"
        columns={[['expense_date','Date',fmtDate],['category','Category'],['description','Description'],['paid_by_partner_id','Paid By',v=>v?partnerName(v):'Company'],['bank_account_id','Bank Account',v=>v?(bankAccounts.find(b=>b.id===v)?.bank_name||'—'):'—'],['amount','Amount',fmtINR],['voucher_number','Voucher No.',v=>v||'—'],['audit_status','Audit',v=><AuditStatusChip status={v}/>]]}
        fields={[
          // User-requested: same field order as the "Add Expense" quick-add
          // form on the Projects list page (Voucher No. right up top, then
          // Date/Category/Amount/Paid-By/Bank/Vendor/Payment Type/Staff,
          // Description last) -- this tab's own Expenses form had drifted
          // to a different order (Voucher No. buried near the bottom,
          // Description up near the top) even though both post to the
          // same /expenses endpoint with the same fields. Payment Method
          // and Reference (not present on that list-page form at all) stay
          // right before Description, since they're this tab's own extra
          // fields, not part of the sequence being matched.
          {k:'voucher_number',l:'Voucher No. *'},
          {k:'expense_date',l:'Date *',t:'date'},
          {k:'category',l:'Category *',t:'select',opts:projectExpenseCategories.map(c=>[c.name,c.name])},
          {k:'amount',l:'Amount *',t:'number'},
          // Investor partners are hidden by default from Paid By pickers
          // elsewhere in the app via eligiblePaidByPartners. This field
          // used to be a deliberate exception -- CrudPanel's opts were a
          // static array computed before the modal opens, with no way to
          // see which partner was already selected on Edit, so filtering
          // risked silently blanking out a real, already-saved value.
          // Now that opts can be a function of the live form (see
          // CrudPanel above, added for the same user-reported "investor
          // shows in every project" bug on the Investments tab's Partner
          // field), keepSelectedId closes that gap and this field can use
          // the same filtering as everywhere else.
          {k:'paid_by_partner_id',l:'Paid By Partner',t:'select',opts:(form)=>[['','— none, paid from bank instead —'], ...eligiblePaidByPartners(partners, p.company_id, id, false, form.paid_by_partner_id).map(x=>[x.id,partnerOptionLabel(x)])]},
          {k:'bank_account_id',l:'Or Bank Account',t:'select',opts:[['','— none, paid by partner instead —'], ...bankAccounts.map(b=>[b.id,b.bank_name])]},
          {k:'vendor_name',l:'Vendor',reqIf:(form)=>vendorIsRequired(form, form.category),t:'select',opts:(form)=>{ const req = isVendorCategory(form.category); const names = vendorNamesFor(vendors, p.company_id); return [[req || form.id ? '' : '', req ? '— choose vendor —' : (form.id ? 'Not a vendor payment' : '— choose —')], ...(req ? [] : (form.id ? [] : [[NO_VENDOR, 'Not a vendor payment']])), ...(form.vendor_name && form.vendor_name !== NO_VENDOR && !names.includes(form.vendor_name) ? [[form.vendor_name, `${form.vendor_name} (not in vendor list)`]] : []), ...names.map(n=>[n,n])]; }},
          // Payment Type comes first and drives the requirement -- picking
          // one makes Staff Member mandatory (see extraRequired below).
          {k:'staff_payment_type',l:'Payment Type',t:'select',opts:Object.entries(STAFF_PAYMENT_TYPE_LABELS).map(([k,l])=>[k,l])},
          {k:'staff_id',l:'Staff Member',reqIf:(form)=>staffIsRequired(form, form.category),t:'select',opts:(form)=>[['',staffNoneLabel(form.staff_payment_type)], ...staffPickList(staffList, form.staff_payment_type, form.staff_id).map(s=>[s.id,s.name])]},
          {k:'payment_method',l:'Payment Method'},
          {k:'reference_number',l:'Reference'},
          {k:'description',l:'Description',full:true},
        ]} endpoint="/expenses" projectId={id} canEdit={canEdit} canDelete={isMasterAdmin(user)} bulkImportEntity="expenses" project={p} flagEntityType="expense"
        extraRequired={(form) => {
          if (!form.paid_by_partner_id && !form.bank_account_id) return 'Paid By is required — choose the partner who personally paid this, or the bank account it was paid from.';
          if (form.staff_payment_type && !form.staff_id) return 'Staff Member is required when a Payment Type is set.';
          return vendorStaffProblem(form, form.category) || false;
        }} amountKey="amount" canMove/>}

      {tab==='receipts' && !tabHiddenForExtraWork && <ReceiptsPanel projectId={id} project={p} bankAccounts={bankAccounts} canEdit={canEdit} canDelete={isMasterAdmin(user)} reload={loadAll} masters={masters}/>}

      {tab==='bills' && <BillsPanel projectId={id} canEdit={canEdit} canDelete={isMasterAdmin(user)} project={p} reload={loadAll}/>}

      {tab==='adjustments' && !tabHiddenForExtraWork && <AdjustmentsPanel projectId={id} canEdit={canEdit} canDelete={isMasterAdmin(user)} reload={loadAll} project={p}/>}

      {tab==='deductions' && !tabHiddenForExtraWork && <CrudPanel title="Deductions" items={deductions} reload={loadAll} testPrefix="ded" hideAdd
        hint="A bill's own withholding fields (legacy TDS/security deposit/retention on older bills) still sync into this list automatically. A deduction entered against a bill from the Receipts tab shows up here too — this list is read-only for new entries; add one from Receipts instead."
        columns={[['deduction_date','Date',fmtDate],['type','Type'],['percentage','%',v=>v?`${v}%`:'—'],['amount','Amount',fmtINR],['classification','Classification',v=>DEDUCTION_CLASSIFICATION_LABELS[v]||v],['reference_bill_id','Bill',v=>v?(projectBills.find(b=>b.id===v)?.bill_number||'—'):'—'],['expected_recovery_date','Expected Recovery',fmtDate]]}
        fields={[
          // User-requested: Date is only mandatory for a refundable/
          // future_liability deduction -- a Not Refundable one is a
          // permanent write-off, its exact date is often unknown or not
          // worth tracking down. CrudPanel's generic ' *' label
          // convention can't express "required depending on another
          // field", so the asterisk stays off the label and the real
          // check lives in extraRequired below (same mechanism Expenses'
          // own Paid By requirement already uses).
          {k:'deduction_date',l:'Date',t:'date'},
          {k:'type',l:'Type *',t:'select',opts:(mastersForCompany(masters.deduction_type, p.company_id).length ? mastersForCompany(masters.deduction_type, p.company_id).map(m=>[m.name,m.name]) : DED_TYPES.map(x=>[x,x])),
            // Deduction Type masters can carry a Default Classification
            // (Settings > Masters > Deduction) -- pick it up automatically
            // when a matching type is chosen, same as the Deductions tab
            // already offers manually. Only overrides Classification when
            // that type actually has one configured; otherwise leaves
            // whatever's already selected alone.
            onSelect:(v, next) => {
              const m = mastersForCompany(masters.deduction_type, p.company_id).find(x=>x.name===v);
              return m?.default_classification ? {...next, classification:m.default_classification} : next;
            }},
          {k:'base_amount',l:'Base Amount (for % calc)',t:'number'},
          {k:'percentage',l:'Percentage (slab or custom, optional)',t:'percent',slabs:GST_SLABS},
          {k:'amount',l:'Amount (overridden if % + Base set)',t:'number'},
          {k:'classification',l:'Classification',t:'select',opts:Object.entries(DEDUCTION_CLASSIFICATION_LABELS)},
          // User-requested: scope this deduction to a specific bill --
          // reduces that bill's own Outstanding the same way its
          // structured TDS/retention/etc. fields already do (see
          // list_bills' linked_deduction_amount), and a Refundable one
          // still auto-creates its own Security & Refundables entry
          // exactly as it already does without a bill picked.
          {k:'reference_bill_id',l:'Bill (optional)',t:'select',opts:[['','— none —'], ...projectBills.map(b=>[b.id, `${b.bill_number||fmtDate(b.bill_date)} — ${fmtINR(b.gross_amount)}`])]},
          {k:'expected_recovery_date',l:'Expected Recovery / Refund Due Date',t:'date'},
          {k:'notes',l:'Notes'},
        ]}
        endpoint="/deductions" projectId={id} canEdit={canEdit} canDelete={isMasterAdmin(user)} project={p} amountKey="amount"
        extraRequired={(form) => {
          if (form.classification !== 'not_refundable' && !form.deduction_date) return 'Date is required for a refundable or future-liability deduction — only Not Refundable can leave it blank.';
          return false;
        }}/>}

      {/* User-requested: this form had drifted from the newer, plain-
          language "Partner Transaction" quick-add (Company Expenses page)
          -- mapped to match within what this tab can actually do (Company/
          Project are already fixed by the page itself, so neither is
          asked again). "What Happened?" reuses PARTNER_TXN_QUICK_CATEGORIES'
          own "out" half directly (single source of truth for the label
          text) -- its "in" half (Capital Investment/Loan) is deliberately
          excluded here, same as before: this tab's own hint already
          pointed a capital contribution at the Investments tab instead.
          Interest Credit/Charge are also excluded (always company-level,
          never project-linked, so a row of either type could never
          actually appear in this project-scoped list to begin with --
          confirmed via the endpoint's own project_id filter) -- created
          instead via "Post Interest as Transaction" on the partner's own
          Current Account tab. Paid By Partner is new here (parity with
          the quick-add) -- CrudPanel's static opts can't see the
          currently-selected Partner to exclude it from this list the way
          the bespoke quick-add form does, so the backend's own "must be a
          different partner" check is the backstop, same tradeoff already
          accepted for Expenses' identical Paid By field in this same
          component. */}
      {tab==='transactions' && !tabHiddenForExtraWork && <CrudPanel title="Partner Transactions" items={ptxns} reload={loadAll} testPrefix="ptxn" lockExempt
        confirmBeforeSave={async (f) => (f.transaction_type !== 'profit_payment' || f.id) ? true : await confirmProfitPaymentCash(id, Number(f.amount || 0), f.partner_id)}
        hint="Payments, repayments, profit, reimbursements, and withdrawals to/from a partner — not a capital contribution. To record a partner putting money into this project, use the Investments tab instead."
        columns={[['transaction_date','Date',fmtDate],['partner_id','Partner',v=>partnerName(v)],['transaction_type','What Happened',v=>PARTNER_TXN_QUICK_CATEGORIES.find(c=>c.type===v)?.label.split(' — ')[0] || v],['amount','Amount',fmtINR],['voucher_number','Voucher No.',v=>v||'—'],['paid_by_partner_id','Paid By',v=>v?partnerName(v):'—'],['description','Description']]}
        fields={[
          // Same user-reported bug and fix as the Investments tab's
          // Partner field above: was every company partner unfiltered,
          // now scoped via eligiblePaidByPartners (keepSelectedId keeps
          // an already-saved value visible even if since unmapped).
          {k:'partner_id',l:'Partner *',t:'select',opts:(form)=>eligiblePaidByPartners(partners, p.company_id, id, false, form.partner_id).map(x=>[x.id,partnerOptionLabel(x)])},
          {k:'voucher_number',l:'Voucher No. *'},
          {k:'transaction_date',l:'Date *',t:'date'},
          {k:'amount',l:'Amount *',t:'number'},
          {k:'transaction_type',l:'What Happened? *',t:'select',opts:PARTNER_TXN_QUICK_CATEGORIES.filter(c=>c.direction==='out').map(c=>[c.type, c.label])},
          {k:'paid_by_partner_id',l:'Paid By Partner (if not this partner / company funds)',t:'select',opts:(form)=>[['','— none —'], ...eligiblePaidByPartners(partners, p.company_id, id, false, form.paid_by_partner_id).map(x=>[x.id,partnerOptionLabel(x)])]},
          // Mirrors Partner Settlement's own paid_from_company_fund
          // checkbox: this project-tied transaction can also be a draw
          // against the shared Company Fund (e.g. repaying a partner's
          // project-scoped capital contribution out of the reserve), now
          // that the Fund Ledger reads project-tied partner_transactions
          // too, not just company-level ones. transaction_type here never
          // includes interest_credit/interest_charge (see the select's own
          // opts above), so no need to hide this for that case.
          {k:'paid_from_company_fund',l:'Paid from Company Reserve / Common Fund',t:'checkbox'},
          {k:'description',l:'Description',full:true},
          {k:'reference',l:'Reference'},
        ]}
        endpoint="/partner-transactions" projectId={id} canEdit={canEdit} canDelete={isMasterAdmin(user)} project={p} amountKey="amount"
        extraRequired={(form) => {
          if (!/^\d+$/.test(form.voucher_number||'')) return 'Voucher No. must contain digits only.';
          return false;
        }}/>}

      {tab==='partners' && !tabHiddenForExtraWork && ['partner','admin','master_admin'].includes(user.role) && <RolesPanel projectId={id} partners={partners} roles={roles} reload={loadAll} model={p.partnership_model} modelBType={p.model_b_type} capitalBonusBase={p.capital_bonus_base} workAllocation={p.work_allocation} canEdit={canEdit} project={p}/>}

      {tab==='settlement' && !tabHiddenForExtraWork && <SettlementPanel fin={fin} projectId={id} reload={loadAll} canApprove={isAdmin(user)} user={user}/>}

      {tab==='reconciliation' && !tabHiddenForExtraWork && <ReconciliationPanel projectId={id} canEdit={canEdit}/>}

      {tab==='audit' && <AuditPanel entityId={id}/>}
    </div>
  );
}

function CrudPanel({ title, items, columns, fields, endpoint, projectId, reload, canEdit, canDelete, testPrefix, bulkImportEntity, project, hint, flagEntityType, extraRequired, amountKey, canMove, hideAdd, confirmBeforeSave, lockExempt }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [flagItem, setFlagItem] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // User-requested: let a master admin move rows (currently opted in
  // only by the Expenses tab, via canMove) to a different project in the
  // same company -- previously the only way was delete-and-re-add by
  // hand. Backend already supported moving project_id on this shared
  // PATCH (used internally for other purposes) but had no master_admin
  // restriction on doing so; added one there (see _crud_endpoints'
  // _update) since this changes profit-sharing math on both the old and
  // new project. Gated here too so the control itself is invisible to
  // anyone it would just 403 for. Multi-select, same shape as Bills' own
  // bulk move -- a checkbox column + "Move N Selected", not a per-row
  // button, so several rows can be reassigned in one pass.
  const { user: moveUser } = useAuth();
  const showMove = canMove && isMasterAdmin(moveUser);
  const [rowMove, setRowMove] = useState(null);
  const [rowMoveMode, setRowMoveMode] = useState('convert');
  const [selected, setSelected] = useState(() => new Set());
  const toggleSelect = (id) => setSelected(s => { const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveProjects, setMoveProjects] = useState([]);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [moving, setMoving] = useState(false);
  const openMoveSelected = () => {
    if (!selected.size) return;
    setMoveTargetId('');
    api.get('/projects').then(r => setMoveProjects(r.data.filter(p => p.company_id === project?.company_id && p.id !== projectId && !isProjectLocked(p))));
    setMoveOpen(true);
  };
  const doMoveSelected = async () => {
    if (moving || !moveTargetId) return;
    const target = moveProjects.find(p => p.id === moveTargetId);
    if (!window.confirm(`Move ${selected.size} selected ${title.toLowerCase()} to "${target?.project_name || 'the selected project'}"? This changes profit-sharing on both projects.`)) return;
    setMoving(true);
    const failed = [];
    for (const iid of selected) {
      const it = items.find(x => x.id === iid);
      if (!it) continue;
      try {
        // Full-object PATCH, same convention every save() in this app
        // uses -- only project_id actually changes.
        await api.patch(`${endpoint}/${iid}`, { ...it, project_id: moveTargetId });
      } catch (e) {
        failed.push({ id: iid, detail: e.response?.data?.detail || 'Error' });
      }
    }
    const succeededCount = selected.size - failed.length;
    if (failed.length === 0) toast.success(`Moved ${succeededCount}`);
    else if (succeededCount === 0) toast.error(`None moved — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    else toast.warning(`Moved ${succeededCount}, ${failed.length} skipped — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    setSelected(new Set(failed.map(f => f.id)));
    setMoveOpen(false);
    setMoving(false);
    reload();
  };
  const [flagsMap, reloadFlags] = useEntityFlagsMap(flagEntityType || 'expense', flagEntityType ? items.map(i=>i.id) : []);
  const flaggedCount = flagEntityType ? items.filter(i=>(flagsMap[i.id]||[]).length > 0).length : 0;
  const visibleItems = flagEntityType && flaggedOnly ? items.filter(i=>(flagsMap[i.id]||[]).length > 0) : items;
  // Generic hook, same shape as the onSelect/`_duplicate_warning` hooks
  // above -- lets one specific CrudPanel usage (e.g. Expenses' "paid by
  // partner OR bank account, at least one") express a cross-field
  // requirement CrudPanel's own generic per-field ' *' convention can't,
  // without hardcoding that entity-specific rule into this shared component.
  // Returns a specific error message string when unmet, or a falsy value
  // when satisfied -- a plain `true` still works (falls back to a generic
  // message below) for backward compatibility with any caller that only
  // needs a boolean.
  const extraRequiredMsg = extraRequired ? extraRequired(form) : false;
  const missingFieldLabels = fields.filter(f => f.l.endsWith(' *') && !form[f.k]).map(f => f.l.replace(/\s*\*$/, ''));
  const save = async () => {
    if (saving) return;
    // User-requested: clicking Save with a mandatory field still blank
    // used to just do nothing (the button was disabled, with no
    // explanation) -- now it's always clickable and names exactly what's
    // missing, same as every hand-written Add form's own validation.
    if (missingFieldLabels.length > 0) { toast.error(`${missingFieldLabels.join(', ')} ${missingFieldLabels.length===1?'is':'are'} required.`); return; }
    if (extraRequiredMsg) { toast.error(typeof extraRequiredMsg === 'string' ? extraRequiredMsg : 'Please fill in the required field(s) above before saving.'); return; }
    if (confirmBeforeSave && !(await confirmBeforeSave(form))) return;
    setSaving(true);
    try {
      const payload = { ...form, project_id: projectId };
      fields.forEach(f => { if ((f.t==='number'||f.t==='percent') && payload[f.k]!==undefined && payload[f.k]!=='') payload[f.k] = Number(payload[f.k]); if (payload[f.k]==='') payload[f.k]=null; });
      if (payload.vendor_name === NO_VENDOR) payload.vendor_name = null;
      const res = form.id ? await api.patch(`${endpoint}/${form.id}`, payload) : await api.post(endpoint, payload);
      setOpen(false); setForm({}); reload(); toast.success('Saved');
      // Generic hook: any CrudPanel-backed entity can flag a soft
      // duplicate warning this way without this component needing to
      // know what kind of entity it is — currently used by Deductions
      // to warn about a manual entry duplicating a bill-linked one.
      if (res?.data?._duplicate_warning) toast.warning(res.data._duplicate_warning);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?') || !(lockExempt || confirmUnlockedEdit(project))) return;
    try { await api.delete(`${endpoint}/${id}`); reload(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openAdd = () => { if (!(lockExempt || confirmUnlockedEdit(project))) return; setForm({}); setOpen(true); };
  const openEdit = (it) => { if (!(lockExempt || confirmUnlockedEdit(project))) return; setForm(it); setOpen(true); };
  // Real-usage-requested: sortable column headers "wherever any list of
  // data appears" -- CrudPanel backs ~10 different entity tables
  // (Investments, Project Expenses, Deductions, Partner Transactions,
  // ...), so this one change reaches all of them at once. Sorts by each
  // column's raw underlying value, not its display formatter -- correct
  // for date/amount/text columns; a column whose formatter maps an id to
  // a name (e.g. Paid By) sorts by that raw id instead, a known, low-
  // stakes limitation rather than something worth a bigger per-column
  // "sort value" config for.
  // User-requested follow-up: every call site's own leftmost column is a
  // date field (transaction_date/expense_date/deduction_date/...) -- a
  // header with no default meant the column showed no active sort arrow
  // at all until clicked, even though the natural expectation is "newest
  // first" like every hand-written table elsewhere already defaults to.
  // Defaulting to that first column, descending, needs no per-call-site
  // change since the convention already holds everywhere this is used.
  const { sorted: sortedItems, sortKey, sortDir, toggleSort } = useSortableRows(visibleItems, columns[0][0], 'desc');
  return (
    <div className="card card-dark print-area">
      {/* User-requested: print, same as the main Projects page already
          has -- this component backs Expenses/Investments/Deductions/
          Partner Transactions inside ANY project's own tabs, Extra Work
          buckets included (they're the same is_extra_work=true rows
          through the exact same component, no separate code path), so
          this one change covers both at once. */}
      <div className="print-header">
        <div className="print-company">{project?.project_name || title}</div>
        <div className="print-report-title">{title}</div>
        <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
      </div>
      {/* Mobile check (user-requested): this toolbar had no flex-wrap,
          unlike every other toolbar in the app -- fine with 3 buttons, but
          the new Print button made a title like "Partner Transactions"
          plus 4 buttons genuinely overflow a narrow screen instead of
          wrapping. Added flex-wrap + gap-y so it drops to a second line
          on mobile instead of squeezing or clipping. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1 no-print">
        <div className="serif text-xl text-[color:var(--gold)] flex items-center gap-2">{title}{isProjectLocked(project) && <span title="Profit approved & signed — editing needs confirmation"><Lock size={14} className="text-muted"/></span>}</div>
        <div className="flex flex-wrap gap-2">
          {flaggedCount > 0 && <button onClick={()=>setFlaggedOnly(v=>!v)} className={"tab btn-small "+(flaggedOnly?'active':'')}>Flagged ({flaggedCount})</button>}
          {showMove && selected.size > 0 && <button onClick={openMoveSelected} className="btn-outline btn-small inline-flex items-center gap-1"><ArrowRightLeft size={14}/>Move {selected.size} Selected</button>}
          {canEdit && bulkImportEntity && <button onClick={()=>setBulkOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1"><Upload size={14}/>Bulk Import</button>}
          {visibleItems.length > 0 && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>}
          {canEdit && !hideAdd && <button onClick={openAdd} className="btn-gold btn-small inline-flex items-center gap-1" data-testid={`${testPrefix}-add`}><Plus size={14}/>Add</button>}
        </div>
      </div>
      {/* User-requested: a total amount under Expenses/Investments/etc.,
          same MetaChip presentation already used for Audit Queue's own
          filtered totals -- opt-in via `amountKey` so a CrudPanel usage
          with no real "amount" field (there isn't one currently) just
          doesn't show a total, rather than guessing at a column. */}
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <div className="text-xs text-muted no-print">{visibleItems.length} entr{visibleItems.length===1?'y':'ies'}{flaggedOnly && ` (${items.length} total)`}</div>
        {amountKey && <MetaChip icon={Calculator} label="Total of the entries currently shown" value={visibleItems.length ? fmtINR(visibleItems.reduce((a,i)=>a+(Number(i[amountKey])||0),0)) : null} tone="amber"/>}
      </div>
      <div className={"text-xs text-muted no-print " + (hint ? "mb-4" : "mb-3")}>{hint}</div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr>{showMove && <th className="no-print"></th>}{columns.map(c=><SortTh key={c[0]} label={c[1]} sortKeyName={c[0]} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>)}{flagEntityType && <th className="no-print">Flags</th>}{canEdit && <th className="no-print"></th>}</tr></thead>
        <tbody>
          {visibleItems.length===0 && <tr><td colSpan={columns.length+1+(flagEntityType?1:0)+(showMove?1:0)} className="text-center text-muted py-6">{items.length===0 ? 'No entries yet.' : 'No flagged entries.'}</td></tr>}
          {sortedItems.map(it => (
            <tr key={it.id} data-testid={`${testPrefix}-${it.id}`}>
              {/* User-requested: multi-select "Move to another project",
                  master_admin only -- same checkbox + bulk-button shape
                  as Bills' own move. Bill-linked rows (auto-synced,
                  read-only here) are excluded, same as Edit/Delete. */}
              {showMove && <td className="no-print">{!it.linked_bill_id && <input type="checkbox" checked={selected.has(it.id)} onChange={()=>toggleSelect(it.id)}/>}</td>}
              {columns.map((c,ci) => <td key={c[0]} className={typeof it[c[0]]==='number' ? 'mono':''}>{c[2] ? c[2](it[c[0]]) : (it[c[0]] ?? '—')}{ci===0 && it.linked_bill_id && <span className="chip ml-1" style={{fontSize:'10px'}} title="Auto-synced from a bill — edit the bill to change this">From Bill</span>}</td>)}
              {flagEntityType && <td className="no-print">
                <button aria-label="Flags" title="Mark attention with a flag" onClick={()=>setFlagItem(it)} className="tab btn-small mr-1"><Flag size={12}/></button>
                <FlagDots flags={flagsMap[it.id]}/>
              </td>}
              {canEdit && <td className="text-right whitespace-nowrap no-print">
                <button aria-label="History" title="View change history" onClick={()=>setHistoryItem(it)} className="tab btn-small mr-1"><Clock3 size={12}/></button>
                {showMove && endpoint==='/expenses' && !it.linked_bill_id && project && !isProjectLocked(project) && <ExpenseMoveButtons id={it.id} projectId={it.project_id} onOpen={(m)=>{setRowMoveMode(m); setRowMove({ id: it.id, project_id: it.project_id, project_name: project.project_name, company_id: project.company_id, amount: it.amount, voucher_number: it.voucher_number, date: it.expense_date, audit_status: it.audit_status, expense_head: it.category, _edit: { paid_from_company_fund: it.paid_from_company_fund } });}}/>}
                {!it.linked_bill_id && <button aria-label="Edit" onClick={()=>openEdit(it)} className="tab btn-small mr-1"><Edit3 size={12}/></button>}
                {!it.linked_bill_id && canDelete && <button aria-label="Delete" onClick={()=>del(it.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <Modal open={!!historyItem} onClose={()=>setHistoryItem(null)} title={`History — ${title.replace(/s$/,'')}`} maxW={720}>
        {historyItem && <AuditPanel entityId={historyItem.id}/>}
      </Modal>
      <Modal open={!!flagItem} onClose={()=>{setFlagItem(null); reloadFlags();}} title={`Flags — ${title.replace(/s$/,'')}`}>
        {flagItem && <EntityFlagsBar entityType={flagEntityType} entityId={flagItem.id} canEdit={canEdit}/>}
      </Modal>
      {rowMove && <ExpenseMoveModal row={rowMove} mode={rowMoveMode} onClose={()=>setRowMove(null)} onSaved={reload}/>}
      <Modal open={moveOpen} onClose={()=>setMoveOpen(false)} title={`Move ${selected.size} ${title} to Another Project`}>
        <div className="text-sm text-muted mb-3">Master admin only — moving these changes profit-sharing math on both the current and the destination project.</div>
        <Field label="Destination Project *">
          <select className="input" value={moveTargetId} onChange={e=>setMoveTargetId(e.target.value)}>
            <option value="">— select —</option>
            {moveProjects.map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select>
        </Field>
        {moveProjects.length === 0 && <div className="text-xs text-muted mt-2">No other project found in this company.</div>}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={()=>setMoveOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={doMoveSelected} disabled={moving || !moveTargetId} className="btn-gold btn-small">{moving ? 'Moving…' : 'Move'}</button>
        </div>
      </Modal>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?`Edit ${title.replace(/s$/,'')}`:`Add ${title.replace(/s$/,'')}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map(f => (
            // User-requested: Description on the Expenses tab should be
            // full-width, matching the Projects list's own "Add Expense"
            // quick-add form -- f.full is a new, optional field-config key
            // (every other CrudPanel-backed entity leaves it unset, so
            // this is non-breaking for all of them).
            <div key={f.k} className={f.full ? 'col-span-2' : undefined}>
            <Field label={f.reqIf && f.reqIf(form) ? f.l + ' *' : f.l}>
              {f.t==='select' ? (
                <select className="input" value={form[f.k] ?? ''} onChange={e=>{
                  const v = e.target.value;
                  // Generic hook: a select field can derive other fields
                  // from the picked value (e.g. Deductions' Type -> a
                  // default Classification pulled from that type's
                  // master) without CrudPanel needing to know what kind
                  // of entity this is.
                  setForm(prev => { const next = {...prev, [f.k]:v}; return f.onSelect ? f.onSelect(v, next) : next; });
                }} data-testid={`${testPrefix}-${f.k}`}>
                  <option value="">-- select --</option>
                  {/* User-reported bug: a project-based investor showed up
                      in the Partner picker of every project, not just the
                      one(s) they're actually mapped to -- these opts were
                      always a static array with no visibility into which
                      row is being edited, so callers couldn't safely apply
                      the same eligiblePaidByPartners filtering used
                      elsewhere without risking a silent blank-out of an
                      already-saved value on Edit. f.opts can now also be a
                      function of the live form, so a caller can filter
                      AND keep whatever's already selected visible. */}
                  {(typeof f.opts === 'function' ? f.opts(form) : f.opts).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ) : f.t==='checkbox' ? (
                <div className="flex items-center gap-2 py-2"><input type="checkbox" checked={!!form[f.k]} onChange={e=>setForm({...form, [f.k]:e.target.checked})}/> <span className="text-sm">{f.l}</span></div>
              ) : f.t==='percent' ? (
                <>
                  <input className="input mono" type="number" step="0.01" min="0" max="100" list={f.slabs ? `${testPrefix}-${f.k}-slabs` : undefined}
                    placeholder="slab or custom %" value={form[f.k] ?? ''} onChange={e=>setForm({...form, [f.k]:e.target.value})} data-testid={`${testPrefix}-${f.k}`}/>
                  {f.slabs && <datalist id={`${testPrefix}-${f.k}-slabs`}>{f.slabs.map(s => <option key={s} value={s}/>)}</datalist>}
                </>
              ) : (
                <input className="input mono" type={f.t||'text'} min={f.t==='number' ? 0 : undefined} value={form[f.k] ?? ''} onChange={e=>setForm({...form, [f.k]:e.target.value})} data-testid={`${testPrefix}-${f.k}`}/>
              )}
            </Field>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid={`${testPrefix}-save`}>{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      {bulkOpen && bulkImportEntity && (
        <Modal open={true} onClose={()=>setBulkOpen(false)} title={`Bulk Import — ${title}`} maxW={860}>
          <ExcelImportWizard lockedProject={project} lockedEntity={bulkImportEntity}/>
        </Modal>
      )}
    </div>
  );
}

function RolesPanel({ projectId, partners, roles, reload, model, modelBType, capitalBonusBase, workAllocation, canEdit, project }) {
  const { user } = useAuth();
  const [drafts, setDrafts] = useState({});
  // Full-app audit finding: DELETE /projects/{pid}/roles/{partner_id} was
  // backend-complete with no UI caller -- the only way to remove a
  // wrongly-assigned partner's role row entirely (as opposed to just
  // zeroing out its checkboxes/percentage via the POST upsert above,
  // which leaves a real row behind) was direct DB/API access. The delete
  // endpoint is master_admin-only (stricter than this panel's own
  // canEdit, which POST's require_roles("admin") also allows) -- gated
  // separately so a plain admin doesn't get a late 403 with no warning.
  const [removingId, setRemovingId] = useState(null);
  const removeRole = async (partnerId) => {
    if (removingId) return;
    if (!window.confirm('Remove this partner\'s role from this project entirely? This deletes their role row (not just clearing the checkboxes) — they can be re-added later if needed.')) return;
    setRemovingId(partnerId);
    try { await api.delete(`/projects/${projectId}/roles/${partnerId}`); reload(); toast.success('Role removed'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setRemovingId(null); }
  };
  // User-requested: one Save for the whole table instead of a separate
  // button per partner row -- previously each row saved independently
  // (Round 19's own per-row double-click guard, now replaced by a
  // single in-flight flag since there's only one save action left).
  const [savingAll, setSavingAll] = useState(false);
  const rolesByPartner = useMemo(() => {
    const map = {};
    for (const r of roles) map[r.partner_id] = r;
    return map;
  }, [roles]);
  useEffect(() => { setDrafts(rolesByPartner); }, [rolesByPartner]);
  const toggle = (partnerId, field) => {
    const cur = drafts[partnerId] || { partner_id: partnerId, project_id: projectId };
    setDrafts({ ...drafts, [partnerId]: { ...cur, [field]: !cur[field] } });
  };
  const setVar = (partnerId, val) => {
    const cur = drafts[partnerId] || { partner_id: partnerId, project_id: projectId };
    setDrafts({ ...drafts, [partnerId]: { ...cur, variable_share_percentage: val } });
  };
  // Dirty-check: only a partner whose draft actually differs from the
  // last-loaded role gets saved -- avoids blindly re-upserting (and
  // re-auditing) every partner in the table just because one changed.
  const ROLE_FIELDS = ['is_money_partner', 'is_work_partner', 'is_equal_partner', 'variable_share_percentage'];
  const isDirty = (partnerId) => {
    const d = drafts[partnerId] || {};
    const orig = rolesByPartner[partnerId] || {};
    return ROLE_FIELDS.some(f => f === 'variable_share_percentage'
      ? Number(d[f] || 0) !== Number(orig[f] || 0)
      : !!d[f] !== !!orig[f]);
  };
  const dirtyPartnerIds = partners.map(p => p.id).filter(isDirty);
  const saveAll = async () => {
    if (savingAll || dirtyPartnerIds.length === 0) return;
    if (!confirmUnlockedEdit(project)) return;
    setSavingAll(true);
    const failed = [];
    for (const partnerId of dirtyPartnerIds) {
      const d = drafts[partnerId] || { partner_id: partnerId, project_id: projectId };
      try {
        await api.post(`/projects/${projectId}/roles`, { ...d, partner_id: partnerId, project_id: projectId, variable_share_percentage: Number(d.variable_share_percentage || 0) });
      } catch (e) { failed.push({ partnerId, detail: e.response?.data?.detail || 'Error' }); }
    }
    const succeeded = dirtyPartnerIds.length - failed.length;
    if (failed.length === 0) toast.success(`Saved ${succeeded} role${succeeded === 1 ? '' : 's'}`);
    else if (succeeded === 0) toast.error(`None saved — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    else toast.warning(`Saved ${succeeded}, ${failed.length} failed — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    // Bug caught in this session's own review pass: reload() re-syncs
    // `drafts` from the freshly-reloaded `roles` prop for EVERY partner,
    // including ones that just failed to save -- calling it unconditionally
    // would silently wipe a failed partner's still-unsaved edit back to
    // its old value, losing what the user typed with no way to recover it.
    // The old per-row Save only ever reloaded on that row's own success
    // (inside its try block); this only reloads when the whole batch
    // succeeded, so a partial failure leaves every draft (succeeded and
    // failed alike) exactly as typed, ready to retry.
    if (failed.length === 0) reload();
    setSavingAll(false);
  };
  // Which pool (if any) is on "variable" allocation for this model —
  // drives both which %-column shows and which partners' %'s the
  // running-total warning below should sum.
  const variablePool = model==='money_work' ? (workAllocation==='variable' ? 'is_work_partner' : null)
    : model==='equal_capital_bonus' ? (capitalBonusBase==='variable' ? 'is_equal_partner' : null)
    : modelBType==='variable' ? 'is_equal_partner' /* equal_variable's "variable" mode has no checkbox gate — every partner counts */ : null;
  const variableTotalPct = variablePool ? Object.entries(drafts)
    .filter(([,d]) => model==='equal_variable' || d[variablePool])
    .reduce((sum,[,d])=>sum+Number(d.variable_share_percentage||0),0) : null;
  const modelLabel = PARTNERSHIP_MODEL_LABELS[model] || model;
  return (
    <div className="card card-dark">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="serif text-xl text-[color:var(--gold)] flex items-center gap-2">Partner Roles — {modelLabel}{isProjectLocked(project) && <Lock size={14} className="text-muted"/>}</div>
        {canEdit && <button onClick={saveAll} disabled={savingAll || dirtyPartnerIds.length === 0} className="btn-gold btn-small" data-testid="role-save-all">{savingAll ? 'Saving…' : dirtyPartnerIds.length ? `Save (${dirtyPartnerIds.length})` : 'Save'}</button>}
      </div>
      {variableTotalPct !== null && Math.abs(variableTotalPct - 100) > 0.01 && (
        <div className="mb-4 p-3 rounded surface-2 text-sm text-[color:var(--amber,#d7a548)]">
          Variable shares add up to {variableTotalPct.toFixed(2)}%, not 100% — {variableTotalPct < 100 ? `${(100-variableTotalPct).toFixed(2)}% of that pool won't be assigned to anyone` : `settlement will pay out ${(variableTotalPct-100).toFixed(2)}% more than that pool actually has`} until this is corrected.
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Partner</th>
          {model==='money_work' ? <><th>Money</th><th>Work</th>{workAllocation==='variable' && <th>Work %</th>}</> :
           model==='equal_capital_bonus' ? <><th>Equal Partner</th>{capitalBonusBase==='variable' && <th>Base %</th>}</> :
           (modelBType==='equal' ? <th>Equal Partner</th> : <th>Variable %</th>)}
          {isMasterAdmin(user) && <th className="no-print"></th>}
          </tr></thead>
        <tbody>
          {partners.map(p => {
            const d = drafts[p.id] || {};
            return (
              <tr key={p.id}>
                {/* User-requested (full-app audit): an investor assigned a
                    profit-sharing role here showed unlabeled, same gap as
                    every other partner list in the app already fixed. */}
                <td>{partnerOptionLabel(p)}</td>
                {model==='money_work' ? <>
                  <td><input type="checkbox" checked={!!d.is_money_partner} onChange={()=>toggle(p.id,'is_money_partner')} disabled={!canEdit} aria-label={`${p.name} — Money partner`} data-testid={`role-money-${p.id}`}/></td>
                  <td><input type="checkbox" checked={!!d.is_work_partner} onChange={()=>toggle(p.id,'is_work_partner')} disabled={!canEdit} aria-label={`${p.name} — Work partner`} data-testid={`role-work-${p.id}`}/></td>
                  {workAllocation==='variable' && <td><input className="input mono" style={{width:100}} type="number" min="0" max="100" value={d.variable_share_percentage ?? ''} onChange={e=>setVar(p.id, e.target.value)} disabled={!canEdit} aria-label={`${p.name} — Work %`} data-testid={`role-work-pct-${p.id}`}/></td>}
                </> : model==='equal_capital_bonus' ? <>
                  <td><input type="checkbox" checked={!!d.is_equal_partner} onChange={()=>toggle(p.id,'is_equal_partner')} disabled={!canEdit} aria-label={`${p.name} — Equal partner`} data-testid={`role-equal-${p.id}`}/></td>
                  {capitalBonusBase==='variable' && <td><input className="input mono" style={{width:100}} type="number" min="0" max="100" value={d.variable_share_percentage ?? ''} onChange={e=>setVar(p.id, e.target.value)} disabled={!canEdit} aria-label={`${p.name} — Base %`} data-testid={`role-base-pct-${p.id}`}/></td>}
                </> : (modelBType==='equal' ?
                  <td><input type="checkbox" checked={!!d.is_equal_partner} onChange={()=>toggle(p.id,'is_equal_partner')} disabled={!canEdit} aria-label={`${p.name} — Equal partner`} data-testid={`role-equal-${p.id}`}/></td> :
                  <td><input className="input mono" style={{width:120}} type="number" min="0" max="100" value={d.variable_share_percentage ?? ''} onChange={e=>setVar(p.id, e.target.value)} disabled={!canEdit} aria-label={`${p.name} — Variable %`} data-testid={`role-var-${p.id}`}/></td>
                )}
                {isMasterAdmin(user) && <td className="no-print">
                  {rolesByPartner[p.id] && !isProjectLocked(project) && <button aria-label={`Remove ${p.name}'s role`} onClick={()=>removeRole(p.id)} disabled={removingId===p.id} className="tab btn-small text-red-300" data-testid={`role-remove-${p.id}`}><Trash2 size={12}/></button>}
                </td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function SettlementPanel({ fin, projectId, reload, canApprove, user }) {
  const s = fin.settlements; const p = fin.project;
  const [distributions, setDistributions] = useState([]);
  const [companyName, setCompanyName] = useState('');
  useEffect(() => { api.get(`/projects/${projectId}/distributions`).then(r=>setDistributions(r.data)).catch(()=>{}); }, [projectId, p?.status]);
  useEffect(() => { if (p?.company_id) api.get('/companies').then(r => { const co = r.data.find(c=>c.id===p.company_id); if (co) setCompanyName(co.legal_name || co.name); }); }, [p?.company_id]);

  const [approving, setApproving] = useState(false);
  const approve = async () => {
    if (approving) return;
    const cbw = fin.cash_backing && fin.cash_backing.totals.deferred > 0.5 ? `Note: ${fmtINR(fin.cash_backing.totals.deferred)} of this profit is not yet backed by cash (the client has paid ${fmtINR(fin.cash_backing.receipts)} of ${fmtINR(fin.cash_backing.billed)} billed). Partners can be paid ${fmtINR(fin.cash_backing.totals.cash_backed)} now and the rest as the client pays.\n\n` : '';
    if (!window.confirm(cbw + 'Approve & lock this project\'s profit distribution?\n\nThis signs off the settlement shown below and locks expenses, receipts, investments, deductions, and partner roles against further changes. An admin can still reopen it later (with a reason) if a correction is needed.')) return;
    setApproving(true);
    try { await api.post('/profit-distributions/approve', { project_id: projectId }); reload(); toast.success('Profit approved & signed'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setApproving(false); }
  };

  const exportPDF = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`${companyName || 'Partner Settlement'}${companyName ? ' — Partner Settlement' : ''}`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Project: ${p.project_name}`, 14, 28);
    doc.text(`Tender: ${p.tender_number || '—'}`, 14, 34);
    doc.text(`Financial Year: ${p.financial_year}`, 14, 40);
    // User-requested polish: this used to print the raw internal model
    // key (e.g. "equal_capital_bonus") instead of the same friendly
    // label already shown on-screen -- a document partners actually
    // sign should read the same way the app does.
    doc.text(`Model: ${PARTNERSHIP_MODEL_LABELS[p.partnership_model] || p.partnership_model}${p.model_b_type?` (${p.model_b_type})`:''}`, 14, 46);
    doc.text(`Distributable Profit: ${fmtINRPdf(fin.totals.distributable_profit)}`, 14, 52);

    // Round 21 finding: this used to hand-roll `Rs ${Number(x).toLocaleString
    // ('en-IN')}` (uncapped, defaults to 3 decimal places) and .toFixed(2)
    // for percentages -- both diverging from the on-screen table just
    // above (fmtINR caps at 2dp; percentages there use .toFixed(4)) for
    // the exact same numbers, on a document partners actually sign off on.
    let head, rows;
    if (p.partnership_model === 'money_work') {
      head = [['Partner','Investment','Money %','Money Profit','Work %','Work Profit','Total','Paid','Balance']];
      rows = s.map(x => [x.partner_name, fmtINRPdf(x.investment), `${(x.money_share_pct||0).toFixed(4)}%`, fmtINRPdf(x.money_profit), `${(x.work_share_pct||0).toFixed(4)}%`, fmtINRPdf(x.work_profit), fmtINRPdf(x.total_profit), fmtINRPdf(x.profit_paid), fmtINRPdf(x.balance)]);
    } else if (p.partnership_model === 'equal_capital_bonus') {
      head = [['Partner','Investment', p.capital_bonus_base==='variable' ? 'Base Share' : 'Equal Share','Capital Bonus','Total','Paid','Balance']];
      rows = s.map(x => [x.partner_name, fmtINRPdf(x.investment), fmtINRPdf(x.equal_share), fmtINRPdf(x.capital_bonus), fmtINRPdf(x.total_profit), fmtINRPdf(x.profit_paid), fmtINRPdf(x.balance)]);
    } else {
      head = [['Partner','Share %','Profit Entitlement','Paid','Balance']];
      rows = s.map(x => [x.partner_name, `${(x.share_pct||0).toFixed(4)}%`, fmtINRPdf(x.total_profit), fmtINRPdf(x.profit_paid), fmtINRPdf(x.balance)]);
    }
    // Company Fund reserve now applies to every model (not just Money &
    // Work) -- show it on the export whenever one applies, or a signed
    // PDF's partner rows would mysteriously not sum to Distributable
    // Profit shown just above with no explanation.
    if (fin.totals.company_fund_pct>0) doc.text(`Company Fund Retained (${fin.totals.company_fund_pct}%): ${fmtINRPdf(fin.totals.company_fund_pool)}`, 14, 58);
    autoTable(doc, { startY: 60, head, body: rows, styles: {fontSize: 9}, headStyles: {fillColor: [201,168,76], textColor: [20,20,20]} });

    let finalY = doc.lastAutoTable?.finalY || 100;
    // Cash-backed payout + bill/receipt mapping: the same section the screen shows, so a printed or exported settlement says how
    // much of the billed-basis profit the client has actually paid for and what each partner can be paid now.
    const cbk = fin.cash_backing;
    if (cbk) {
      const pageH = doc.internal.pageSize.getHeight();
      const need = (h) => { if (finalY + h > pageH - 20) { doc.addPage(); finalY = 14; } };
      need(40);
      doc.setFontSize(11); doc.text('Cash-backed payout — what can actually be paid now', 14, finalY + 10);
      doc.setFontSize(8.5);
      doc.text(`Billed ${fmtINRPdf(cbk.billed)}  ·  Received ${fmtINRPdf(cbk.receipts)}${cbk.collection_pct != null ? ` (${cbk.collection_pct}%)` : ''}  ·  Profit on billed basis ${fmtINRPdf(cbk.distributable_profit)}  ·  Profit realised in cash ${fmtINRPdf(cbk.cash_realised_profit)}  ·  Still to be collected ${fmtINRPdf(cbk.uncollected_profit)}`, 14, finalY + 16, { maxWidth: 182 });
      doc.text('Profit realised in cash = receipts - costs - forfeited security + adjustments (costs are recovered first).', 14, finalY + 26, { maxWidth: 182 });
      const ct = cbk.totals;
      autoTable(doc, { startY: finalY + 30, head: [['Partner', 'Entitlement (billed)', 'Cash-backed', 'Paid', 'Payable now', 'Paid ahead', 'Awaiting collection']],
        body: cbk.partners.map(x => [x.partner_name, fmtINRPdf(x.entitlement), fmtINRPdf(x.cash_backed), fmtINRPdf(x.paid), fmtINRPdf(x.payable_now), x.paid_ahead > 0.5 ? fmtINRPdf(x.paid_ahead) : '-', x.deferred > 0.5 ? fmtINRPdf(x.deferred) : '-']),
        foot: cbk.partners.length ? [['Total', fmtINRPdf(ct.entitlement), fmtINRPdf(ct.cash_backed), fmtINRPdf(ct.paid), fmtINRPdf(ct.payable_now), fmtINRPdf(ct.paid_ahead), fmtINRPdf(ct.deferred)]] : undefined,
        styles: { fontSize: 8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] }, footStyles: { fillColor: [240, 232, 205], textColor: [20, 20, 20], fontStyle: 'bold' },
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } } });
      finalY = doc.lastAutoTable?.finalY || finalY + 40;
      if (ct.paid_ahead > 0.5) { need(14); doc.setTextColor(180, 40, 40); doc.text(`${fmtINRPdf(ct.paid_ahead)} has been paid to partners beyond what the client has paid; it is being funded from the Company Fund / partners' capital until collection.`, 14, finalY + 8, { maxWidth: 182 }); doc.setTextColor(0, 0, 0); finalY += 12; }
      try {
        const mp = (await api.get(`/projects/${projectId}/receipt-mapping`)).data; const mt = mp.totals;
        need(36);
        doc.setFontSize(11); doc.text('Bill and receipt mapping', 14, finalY + 10); doc.setFontSize(8.5);
        doc.text(`${mt.bills} bills billed ${fmtINRPdf(mt.billed_gross)}  ·  received against bills ${fmtINRPdf(mt.received_mapped_to_bills)}  ·  outstanding on bills ${fmtINRPdf(mt.bills_outstanding)}  ·  receipts not mapped to a bill ${fmtINRPdf(mt.receipts_counted_unmapped)}  ·  ${mt.bills_settled} settled / ${mt.bills_part_paid} part-paid / ${mt.bills_unpaid} unpaid`, 14, finalY + 16, { maxWidth: 182 });
        const open = mp.bills.filter(b => b.outstanding_amount > 0.5);
        if (open.length) {
          autoTable(doc, { startY: finalY + 24, head: [['Bill with amount still outstanding', 'Date', 'Billed', 'Received', 'Outstanding']],
            body: open.slice(0, 40).map(b => [b.bill_number || '-', fmtDate(b.bill_date), fmtINRPdf(b.gross_amount), fmtINRPdf(b.allocated_amount), fmtINRPdf(b.outstanding_amount)]),
            styles: { fontSize: 8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] }, columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } } });
          finalY = doc.lastAutoTable?.finalY || finalY + 40;
          if (open.length > 40) { doc.text(`... and ${open.length - 40} more bills with an outstanding amount`, 14, finalY + 6); finalY += 8; }
        } else finalY += 22;
      } catch (e) { /* the mapping is a supplement; the settlement PDF stands without it */ }
    }
    if (finalY > doc.internal.pageSize.getHeight() - 55) { doc.addPage(); finalY = 14; }
    // Signature stamp if approved. Production hardening: distributions
    // rows keep approved=true after a reopen (deliberate -- the prior
    // signed history stays visible), but that meant a reopened,
    // freely-editable project could still export a PDF stamped APPROVED
    // with the original signature even though the numbers above it are
    // no longer what was signed off. Also requiring the project's
    // current status to still be locked closes that gap without
    // touching the history-preservation behavior itself.
    const anyApproved = distributions.some(d => d.approved) && isProjectLocked(p);
    const sigUrl = distributions[0]?.approved_signature_url || (anyApproved ? user?.signature_url : null);
    doc.setFontSize(9);
    doc.text('_______________________', 140, finalY + 30);
    doc.text('Approved by (Signature)', 140, finalY + 42);
    if (sigUrl && sigUrl.startsWith('data:image')) {
      try { doc.addImage(sigUrl, 'PNG', 140, finalY + 8, 50, 20); } catch (e) {}
    }
    if (anyApproved) {
      doc.setTextColor(150,120,50);
      doc.text('APPROVED', 14, finalY + 30);
      doc.setTextColor(0,0,0);
    }
    doc.save(`settlement-${p.project_name}-${Date.now()}.pdf`);
  };

  return (
    <div className="card card-dark">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Partner Settlement</div>
          {p.partnership_model==='money_work' && fin.totals.money_pool_pct!=null && (
            <div className="text-xs text-muted mt-1">Split: {fin.totals.money_pool_pct}% Money / {fin.totals.work_pool_pct}% Work{fin.totals.company_fund_pct>0 && ` / ${fin.totals.company_fund_pct}% Company Fund (${fmtINR(fin.totals.company_fund_pool)} retained)`}{!fin.totals.profit_rule_id && ' (default — no rule configured for this project)'}</div>
          )}
          {p.partnership_model==='equal_capital_bonus' && (
            <div className="text-xs text-muted mt-1">{p.capital_bonus_base==='variable' ? 'Custom base split' : 'Equal base split'} + a capital-investment-ratio bonus on the "extra" (N+1)th share{fin.totals.company_fund_pct>0 && ` — ${fin.totals.company_fund_pct}% Company Fund (${fmtINR(fin.totals.company_fund_pool)} retained)`}</div>
          )}
          {p.partnership_model==='equal_variable' && fin.totals.company_fund_pct>0 && (
            <div className="text-xs text-muted mt-1">{fin.totals.company_fund_pct}% Company Fund ({fmtINR(fin.totals.company_fund_pool)} retained)</div>
          )}
          {fin.totals.unassigned_pool != null && (
            <div className="text-xs text-red-400 mt-1">
              {fin.totals.unassigned_pool > 0
                ? `${fmtINR(fin.totals.unassigned_pool)} of the distributable profit isn't assigned to any partner yet`
                : `Partner shares add up to ${fmtINR(-fin.totals.unassigned_pool)} more than the actual profit`} — fix partner roles before approving.
            </div>
          )}
        </div>
        <div className="flex gap-2 no-print">
          <button onClick={exportPDF} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="export-pdf-btn"><FileText size={14}/>Export PDF</button>
          {/* User-reported: this button stayed live and clickable even on
              an already-approved/locked project -- clicking it correctly
              got rejected server-side (the backend has always blocked a
              second approval), but showing an active "Approve & Lock"
              button that's guaranteed to error is worse UX than just
              reflecting the project's real state. Reopening a locked
              project (Settings/wherever that action lives) flips status
              back out of isProjectLocked, so this naturally reverts to
              the real button again once there's actually something to
              approve.
              User-requested follow-up: shown to EVERY role now, not just
              canApprove -- a Partner/Viewer looking at a locked project's
              Settlement tab previously saw nothing at all in this slot
              (blank space, since the old button was gated behind
              canApprove); the confirmation itself is read-only status,
              not an action, so there's no reason to hide it from anyone
              who can already see this whole panel. */}
          {isProjectLocked(p)
            ? <span className="btn-gold-static btn-small inline-flex items-center gap-1" style={{cursor:'default'}} data-testid="approve-btn-locked"><Lock size={14}/>Approved &amp; Locked</span>
            : canApprove && <button onClick={approve} disabled={fin.totals.unassigned_pool != null || approving} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="approve-btn"><CheckCircle size={14}/>{approving ? 'Approving…' : 'Approve & Lock'}</button>}
        </div>
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        {p.partnership_model==='money_work' ? <>
          <thead><tr><th>Partner</th><th>Investment</th><th>Money %</th><th>Money Profit</th><th>Work %</th><th>Work Profit</th><th>Total Profit</th><th>Paid</th><th>Balance</th></tr></thead>
          <tbody>
            {s.length===0 && <tr><td colSpan="9" className="text-center text-muted py-6">Assign partner roles first.</td></tr>}
            {s.map(x => (
              <tr key={x.partner_id}>
                <td>{x.partner_name}</td>
                <td className="mono">{fmtINR(x.investment)}</td>
                <td className="mono">{x.money_share_pct?.toFixed(4)}%</td>
                <td className="mono">{fmtINR(x.money_profit)}</td>
                <td className="mono">{x.work_share_pct?.toFixed(4)}%</td>
                <td className="mono">{fmtINR(x.work_profit)}</td>
                <td className="mono text-[color:var(--gold)]">{fmtINR(x.total_profit)}</td>
                <td className="mono">{fmtINR(x.profit_paid)}</td>
                <td className={"mono " + (x.balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(x.balance)}</td>
              </tr>
            ))}
          </tbody>
          {/* User-requested: a footer total for each partner column, same
              tfoot convention Reports already uses. Money %/Work % are
              percentages of the same pool, not additive across partners
              (they sum to ~100% each on their own), so those two stay
              blank rather than showing a meaningless "200%"-style sum. */}
          {s.length > 0 && (
            <tfoot><tr className="font-semibold">
              <td>Total</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.investment||0),0))}</td>
              <td></td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.money_profit||0),0))}</td>
              <td></td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.work_profit||0),0))}</td>
              <td className="mono text-[color:var(--gold)]">{fmtINR(s.reduce((a,x)=>a+Number(x.total_profit||0),0))}</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.profit_paid||0),0))}</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.balance||0),0))}</td>
            </tr></tfoot>
          )}
        </> : p.partnership_model==='equal_capital_bonus' ? <>
          <thead><tr><th>Partner</th><th>Investment</th><th>{p.capital_bonus_base==='variable' ? 'Base Share' : 'Equal Share'}</th><th>Capital Bonus</th><th>Total Profit</th><th>Paid</th><th>Balance</th></tr></thead>
          <tbody>
            {s.length===0 && <tr><td colSpan="7" className="text-center text-muted py-6">Assign partner roles first.</td></tr>}
            {s.map(x => (
              <tr key={x.partner_id}>
                <td>{x.partner_name}</td>
                <td className="mono">{fmtINR(x.investment)}</td>
                <td className="mono">{fmtINR(x.equal_share)}</td>
                <td className="mono">{fmtINR(x.capital_bonus)}</td>
                <td className="mono text-[color:var(--gold)]">{fmtINR(x.total_profit)}</td>
                <td className="mono">{fmtINR(x.profit_paid)}</td>
                <td className={"mono " + (x.balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(x.balance)}</td>
              </tr>
            ))}
          </tbody>
          {s.length > 0 && (
            <tfoot><tr className="font-semibold">
              <td>Total</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.investment||0),0))}</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.equal_share||0),0))}</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.capital_bonus||0),0))}</td>
              <td className="mono text-[color:var(--gold)]">{fmtINR(s.reduce((a,x)=>a+Number(x.total_profit||0),0))}</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.profit_paid||0),0))}</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.balance||0),0))}</td>
            </tr></tfoot>
          )}
        </> : <>
          <thead><tr><th>Partner</th><th>Share %</th><th>Profit Entitlement</th><th>Paid</th><th>Balance</th></tr></thead>
          <tbody>
            {s.length===0 && <tr><td colSpan="5" className="text-center text-muted py-6">Assign partner roles first.</td></tr>}
            {s.map(x => (
              <tr key={x.partner_id}>
                <td>{x.partner_name}</td>
                <td className="mono">{x.share_pct?.toFixed(4)}%</td>
                <td className="mono text-[color:var(--gold)]">{fmtINR(x.total_profit)}</td>
                <td className="mono">{fmtINR(x.profit_paid)}</td>
                <td className={"mono " + (x.balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(x.balance)}</td>
              </tr>
            ))}
          </tbody>
          {s.length > 0 && (
            <tfoot><tr className="font-semibold">
              <td>Total</td>
              <td></td>
              <td className="mono text-[color:var(--gold)]">{fmtINR(s.reduce((a,x)=>a+Number(x.total_profit||0),0))}</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.profit_paid||0),0))}</td>
              <td className="mono">{fmtINR(s.reduce((a,x)=>a+Number(x.balance||0),0))}</td>
            </tr></tfoot>
          )}
        </>}
      </table>
      </div>
      <CashBackingCard fin={fin} projectId={projectId}/>
      {distributions.length > 0 && distributions.some(d=>d.approved) && (
        isProjectLocked(p) ? (
          <div className="mt-4 text-xs text-muted">
            Approved on {fmtDate(distributions[0].approved_at)} by {distributions[0].approved_by}
            {distributions[0].approved_signature_url && <span> · signed</span>}
          </div>
        ) : (
          // Production hardening: profit_distributions intentionally keeps
          // approved=true after a reopen (history is preserved on purpose),
          // but the project itself is no longer locked -- the numbers above
          // can now differ from what was actually signed off. Say so plainly
          // instead of implying the current numbers are still approved.
          <div className="mt-4 text-xs text-amber-400">
            Previously approved on {fmtDate(distributions[0].approved_at)} by {distributions[0].approved_by} — project was reopened since, figures above are not yet re-approved.
          </div>
        )
      )}
    </div>
  );
}

// Cash-backed payout: the settlement above is on the BILLED basis. This shows how much of it the client has actually paid for
// (costs recovered first), what each partner can be paid now, and anything already paid ahead of collection. Read-only.
function CashBackingCard({ fin, projectId }) {
  const cb = fin.cash_backing;
  const [showMap, setShowMap] = useState(false);
  if (!cb) return null;
  const tt = cb.totals;
  const noProfit = !(cb.distributable_profit > 0);
  return (
    <div className="mt-5 rounded-xl p-4 print-keep" style={{ border: '1px solid rgba(217,164,65,0.35)', background: 'rgba(217,164,65,0.06)' }} data-testid="cash-backing">
      <div className="serif text-lg text-[color:var(--gold)]">Cash-backed payout — what can actually be paid now</div>
      <div className="text-xs text-muted mt-1 max-w-3xl">The table above is calculated on what has been billed. Partners can only be paid from what the client has actually paid: costs are recovered first, and only the profit left after that is in cash.</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3 text-sm">
        <div><div className="text-xs text-muted">Billed</div><div className="mono">{fmtINR(cb.billed)}</div></div>
        <div><div className="text-xs text-muted">Received (reviewed){cb.collection_pct != null ? ` · ${cb.collection_pct}%` : ''}</div><div className="mono">{fmtINR(cb.receipts)}</div></div>
        <div><div className="text-xs text-muted">Profit on billed basis</div><div className="mono">{fmtINR(cb.distributable_profit)}</div></div>
        <div><div className="text-xs text-muted">Profit realised in cash</div><div className={'mono ' + (cb.cash_realised_profit < 0 ? 'text-red-400' : 'text-[color:var(--gold)]')}>{fmtINR(cb.cash_realised_profit)}</div></div>
        <div><div className="text-xs text-muted">Still to be collected</div><div className="mono text-amber-400">{fmtINR(cb.uncollected_profit)}</div></div>
      </div>
      <div className="text-xs text-muted mt-1">Profit realised in cash = receipts − costs − forfeited security + adjustments. Project cash in hand (incl. capital &amp; loans, after payouts): <span className="mono">{fmtINR(cb.project_cash_position)}</span>.</div>
      {noProfit ? <div className="text-sm text-muted mt-3">There is no profit to distribute on this project yet.</div> : (
        <div className="overflow-x-auto mt-3">
          <table className="tbl">
            <thead><tr><th>Partner</th><th className="text-right">Entitlement (billed basis)</th><th className="text-right">Cash-backed</th><th className="text-right">Paid</th><th className="text-right">Payable now</th><th className="text-right">Paid ahead of cash</th><th className="text-right">Awaiting collection</th></tr></thead>
            <tbody>
              {cb.partners.length === 0 && <tr><td colSpan="7" className="text-center text-muted py-4">Assign partner roles first.</td></tr>}
              {cb.partners.map(x => (
                <tr key={x.partner_id}>
                  <td>{x.partner_name}</td>
                  <td className="mono text-right text-muted">{fmtINR(x.entitlement)}</td>
                  <td className="mono text-right">{fmtINR(x.cash_backed)}</td>
                  <td className="mono text-right">{fmtINR(x.paid)}</td>
                  <td className="mono text-right font-semibold text-[color:var(--gold)]">{fmtINR(x.payable_now)}</td>
                  <td className={'mono text-right ' + (x.paid_ahead > 0.5 ? 'text-red-400 font-semibold' : '')}>{x.paid_ahead > 0.5 ? fmtINR(x.paid_ahead) : '—'}</td>
                  <td className="mono text-right text-amber-400">{x.deferred > 0.5 ? fmtINR(x.deferred) : '—'}</td>
                </tr>
              ))}
            </tbody>
            {cb.partners.length > 0 && <tfoot><tr className="font-semibold"><td>Total</td><td className="mono text-right">{fmtINR(tt.entitlement)}</td><td className="mono text-right">{fmtINR(tt.cash_backed)}</td><td className="mono text-right">{fmtINR(tt.paid)}</td><td className="mono text-right text-[color:var(--gold)]">{fmtINR(tt.payable_now)}</td><td className="mono text-right">{fmtINR(tt.paid_ahead)}</td><td className="mono text-right">{fmtINR(tt.deferred)}</td></tr></tfoot>}
          </table>
        </div>
      )}
      {tt.paid_ahead > 0.5 && <div className="mt-3 text-sm text-red-400" data-testid="cash-backing-ahead">{fmtINR(tt.paid_ahead)} has already been paid to partners beyond what the client has paid. Until the client pays, that money is coming from somewhere else (the Company Fund or a partner's own capital) — make sure that source is recorded.</div>}
      {!noProfit && tt.deferred > 0.5 && <div className="mt-2 text-xs text-muted">{fmtINR(tt.deferred)} of the partners' profit is still waiting on collection. It becomes payable automatically as receipts are recorded and reviewed — nothing here needs to be re-approved.</div>}
      <div className="mt-3 no-print"><button type="button" className="tab btn-small" onClick={() => setShowMap(v => !v)} data-testid="receipt-map-toggle">{showMap ? 'Hide' : 'Show'} bill ↔ receipt mapping</button></div>
      {showMap && <ReceiptMappingPanel projectId={projectId} locked={isProjectLocked(fin.project)} refreshKey={[cb.receipts, cb.billed].join('|')}/>}
    </div>
  );
}

// Which receipts settle which bills: every bill with what has been received and what is still outstanding, and any receipt not yet
// mapped to a bill. Read-only; to map or re-map a receipt, edit it on the Receipts tab (choose the bill(s) it settles).
function ReceiptMappingPanel({ projectId, refreshKey, locked }) {
  const { user } = useAuth();
  const canMap = !locked && ['admin', 'accountant', 'master_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [bump, setBump] = useState(0);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let alive = true;
    api.get(`/projects/${projectId}/receipt-mapping`).then(r => { if (alive) { setData(r.data); setErr(''); } }).catch(e => { if (alive) setErr(e.response?.data?.detail || 'Could not load the mapping'); });
    return () => { alive = false; };
  }, [projectId, refreshKey, bump]);
  const runAutoMap = async (dry) => {
    setBusy(dry ? 'preview' : 'apply');
    try {
      const r = (await api.post(`/projects/${projectId}/auto-map-receipts`, { dry_run: dry })).data;
      if (dry) setPreview(r);
      else { setPreview(null); setBump(b => b + 1); toast.success(`${r.applied} receipt${r.applied === 1 ? '' : 's'} mapped to bills`); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(''); }
  };
  if (err) return <div className="text-sm text-red-400 mt-3">{err}</div>;
  if (!data) return <div className="mt-3"><LoadingMark size={20}/></div>;
  const t = data.totals;
  const chip = (b) => (b.outstanding_amount <= 0.5) ? <span className="badge" style={{ color: '#86efac', borderColor: 'rgba(134,239,172,0.4)' }}>Settled</span>
    : (b.allocated_amount + b.linked_deduction_amount > 0.01) ? <span className="badge" style={{ color: '#c9a84c', borderColor: 'rgba(201,168,76,0.5)' }}>Part-paid</span> : <span className="badge" style={{ color: '#7dd3fc', borderColor: 'rgba(125,211,252,0.35)' }}>Unpaid</span>;
  return (
    <div className="mt-3" data-testid="receipt-mapping">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><div className="text-xs text-muted">Billed ({t.bills} bills)</div><div className="mono">{fmtINR(t.billed_gross)}</div></div>
        <div><div className="text-xs text-muted">Received against bills</div><div className="mono text-green-400">{fmtINR(t.received_mapped_to_bills)}</div></div>
        <div><div className="text-xs text-muted">Still outstanding on bills</div><div className="mono text-amber-400">{fmtINR(t.bills_outstanding)}</div></div>
        <div><div className="text-xs text-muted">Receipts not mapped to a bill</div><div className={'mono ' + (t.receipts_counted_unmapped > 0.5 ? 'text-amber-400' : '')}>{fmtINR(t.receipts_counted_unmapped)}</div></div>
      </div>
      <div className="text-xs text-muted mt-1">{t.bills_settled} settled · {t.bills_part_paid} part-paid · {t.bills_unpaid} unpaid. Receipts counted: <span className="mono">{fmtINR(t.receipts_counted)}</span>{t.receipts_pending_review > 0 ? <> · <span className="mono">{fmtINR(t.receipts_pending_review)}</span> awaiting review (not counted)</> : null}.</div>
      {data.unmapped_receipts.length > 0 && (
        <div className="mt-3">
          <div className="text-sm font-semibold mb-1">Receipts not mapped to a bill — {data.unmapped_receipts.length}</div>
          <div className="overflow-x-auto"><table className="tbl">
            <thead><tr><th>Date</th><th>Type</th><th className="text-right">Amount</th><th className="text-right">Not mapped</th><th>Status</th></tr></thead>
            <tbody>{data.unmapped_receipts.map(r => <tr key={r.id}><td className="text-xs">{fmtDate(r.receipt_date)}</td><td className="text-xs">{(r.receipt_type || '').replace(/_/g, ' ')}{r.description ? ` — ${r.description}` : ''}</td><td className="mono text-right">{fmtINR(r.amount)}</td><td className="mono text-right text-amber-400">{fmtINR(r.unmapped_amount)}</td><td className="text-xs">{r.counted ? 'Counted' : 'Awaiting review (not counted)'}</td></tr>)}</tbody>
          </table></div>
          <div className="text-xs text-muted mt-1">To map one by hand, open the <Link to={`/app/projects/${projectId}?tab=receipts`} className="underline">Receipts tab</Link>, edit the receipt and choose the bill(s) it settles. Money received but not mapped still counts as cash — mapping only tells you which bill it paid.</div>
          {canMap && data.unmapped_receipts.some(r => r.counted) && <div className="mt-2 no-print"><button type="button" className="btn-gold btn-small" disabled={!!busy} onClick={() => runAutoMap(true)} data-testid="auto-map-preview">{busy === 'preview' ? 'Checking…' : 'Auto-map to bills — oldest bill first'}</button> <span className="text-xs text-muted">Shows you what it would do first; nothing is saved until you confirm.</span></div>}
          {preview && (
            <div className="mt-3 rounded-lg p-3 no-print" style={{ border: '1px solid rgba(217,164,65,0.4)' }} data-testid="auto-map-plan">
              <div className="text-sm font-semibold">{preview.to_map === 0 ? 'Nothing can be mapped automatically.' : `${preview.to_map} receipt${preview.to_map === 1 ? '' : 's'} (${fmtINR(preview.amount_to_map)}) would be mapped:`}</div>
              {preview.plan.map(it => <div key={it.receipt_id} className="text-xs mt-1"><b>{fmtDate(it.receipt_date)} · {fmtINR(it.amount)}</b> → {it.allocations.map(a => `Bill ${a.bill_number || fmtDate(a.bill_date)} ${fmtINR(a.amount)}`).join(' + ')}</div>)}
              {preview.skipped.length > 0 && <div className="text-xs text-muted mt-2">Left for you: {preview.skipped.map(x => `${fmtDate(x.receipt_date)} ${fmtINR(x.amount)} — ${x.reason}`).join(' · ')}</div>}
              <div className="text-xs text-muted mt-2">Only the receipt↔bill link is saved. No amount, date, review status or profit figure changes. Receipts go to the oldest bills with room left, never to a bill raised after the receipt date.</div>
              <div className="mt-2 flex gap-2">
                {preview.to_map > 0 && <button type="button" className="btn-gold btn-small" disabled={!!busy} onClick={() => runAutoMap(false)} data-testid="auto-map-apply">{busy === 'apply' ? 'Saving…' : 'Confirm and map'}</button>}
                <button type="button" className="btn-outline btn-small" onClick={() => setPreview(null)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="mt-3 overflow-x-auto"><table className="tbl">
        <thead><tr><th>Bill</th><th>Date</th><th className="text-right">Billed</th><th className="text-right">Received</th><th className="text-right">Deductions</th><th className="text-right">Outstanding</th><th>Status</th></tr></thead>
        <tbody>
          {data.bills.length === 0 && <tr><td colSpan="7" className="text-center text-muted py-4">No bills raised yet.</td></tr>}
          {data.bills.map(b => <tr key={b.id}><td>{b.bill_number || '—'}</td><td className="text-xs">{fmtDate(b.bill_date)}</td><td className="mono text-right">{fmtINR(b.gross_amount)}</td><td className="mono text-right">{fmtINR(b.allocated_amount)}{b.pending_audit_amount > 0 ? <div className="text-xs text-muted">+ {fmtINR(b.pending_audit_amount)} awaiting review</div> : null}</td><td className="mono text-right">{b.linked_deduction_amount ? fmtINR(b.linked_deduction_amount) : '—'}</td><td className={'mono text-right ' + (b.outstanding_amount > 0.5 ? 'text-amber-400' : '')}>{fmtINR(b.outstanding_amount)}</td><td>{chip(b)}</td></tr>)}
        </tbody>
      </table></div>
    </div>
  );
}

// The project's single closing-statement view: contract → expenses →
// deductions → income actually credited (by bank/cash account) → outstanding
// receivable → distributable profit → partner settlement, all in one place.
// Everything here is read from totals _compute_from_rows already produces —
// this panel adds no new math, it just gives "the final calculation" a
// dedicated, printable home instead of leaving it spread across tabs.
function FinalCalculationPanel({ fin, projectId, reload, canApprove, user }) {
  const t = fin.totals; const p = fin.project;
  const [banks, setBanks] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [includeSettlement, setIncludeSettlement] = useState(true);
  useEffect(() => { api.get('/bank-accounts').then(r=>setBanks(r.data)).catch(()=>{}); }, []);
  useEffect(() => { if (p?.company_id) api.get('/companies').then(r => { const co = r.data.find(c=>c.id===p.company_id); if (co) setCompanyName(co.legal_name || co.name); }); }, [p?.company_id]);
  const bankLabel = (row) => {
    if (row.bank_account_id) {
      const b = banks.find(x=>x.id===row.bank_account_id);
      if (b) return `${b.bank_name}${b.account_number ? ' ••' + String(b.account_number).slice(-4) : ''}`;
    }
    return row.label;
  };
  const receiptsByBank = t.receipts_by_bank || [];
  const rows = [
    { item: 'Contract Value (Net of GST)', v: t.net_contract_value },
    { item: '− Total Expenses', v: -t.total_expenses },
    ...(t.total_deductions_non_recoverable > 0 ? [{ item: '− Non-Recoverable Deductions', v: -t.total_deductions_non_recoverable }] : []),
    ...(t.total_security_forfeited > 0 ? [{ item: '− Forfeited Security', v: -t.total_security_forfeited }] : []),
    { item: '= Project Profit', v: t.project_profit, bold: true },
    { item: '+ Profit Adjustment', v: t.profit_adjustment },
    { item: '= Distributable Profit', v: t.distributable_profit, bold: true, gold: true },
    ...(fin.cash_backing ? [
      { item: 'Of which realised in cash (receipts − costs)', v: fin.cash_backing.cash_realised_profit },
      { item: 'Of which still to be collected', v: fin.cash_backing.uncollected_profit },
    ] : []),
  ];
  return (
    <>
    <div className="space-y-4 print-area">
      <div className="card card-dark">
        <div className="print-header">
          <div className="print-company">{companyName || '—'}</div>
          <div className="print-report-title">Final Calculation — {p.project_name}</div>
        </div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="serif text-xl text-[color:var(--gold)]">Final Calculation</div>
            <div className="text-xs text-muted">Closing statement for {p.project_name} — income shown below reflects amounts actually credited, not just billed.</div>
          </div>
          <div className="flex items-center gap-3 no-print">
            <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" checked={includeSettlement} onChange={e=>setIncludeSettlement(e.target.checked)}/>Include Partner Settlement in print</label>
            <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Print</button>
          </div>
        </div>

        <div className="overflow-x-auto mb-2">
          <table className="tbl">
            <tbody>
              {rows.map(r => (
                <tr key={r.item}>
                  <td className={r.bold ? 'font-semibold' : ''}>{r.item}</td>
                  <td className={"mono text-right " + (r.bold ? 'font-semibold ' : '') + (r.gold ? 'text-[color:var(--gold)]' : '')}>{fmtINR(r.v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* User-requested: printed for a partner to read on paper, so this
            can't be a hover tooltip -- Distributable Profit above already
            includes Recoverable Deductions as accrued, not-a-loss profit
            (see the Deductions box below), but "not a loss" isn't the same
            as "already in hand." Shown directly under the number it
            qualifies, only when it's actually non-zero, so a partner
            reading a printed copy sees this without needing to ask. */}
        {t.total_recoverable_deductions_outstanding > 0 && (
          <div className="text-xs mb-6 p-2 rounded" style={{background:'rgba(217,164,65,0.1)', color:'var(--accent-amber)'}}>
            Note: {fmtINR(t.total_recoverable_deductions_outstanding)} of the Distributable Profit above is still tied up in deductions (TDS / retention / security deposit) not yet recovered from the client — it isn't lost, but it isn't received in cash yet either.
          </div>
        )}
        {t.total_recoverable_deductions_outstanding <= 0 && <div className="mb-6"/>}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-semibold mb-2 flex items-center gap-1.5">Deductions{isProjectLocked(p) && <span title="Profit approved & signed — deductions are frozen until this project is reopened"><Lock size={12} className="text-muted"/></span>}</div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted">Total Deductions</span><span className="mono">{fmtINR(t.total_deductions)}</span></div>
              <div className="flex justify-between"><span className="text-muted">Recoverable (not a loss)</span><span className="mono">{fmtINR(t.total_deductions_recoverable)}</span></div>
              {t.total_recoverable_deductions_outstanding > 0 && <div className="flex justify-between text-xs"><span className="text-muted pl-3">— of which, not yet recovered</span><span className="mono" style={{color:'var(--accent-amber)'}}>{fmtINR(t.total_recoverable_deductions_outstanding)}</span></div>}
              <div className="flex justify-between"><span className="text-muted">Non-Recoverable (reduces profit)</span><span className="mono">{fmtINR(t.total_deductions_non_recoverable)}</span></div>
              {t.total_security_forfeited > 0 && <div className="flex justify-between"><span className="text-muted">Forfeited Security (reduces profit)</span><span className="mono">{fmtINR(t.total_security_forfeited)}</span></div>}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold mb-2">Receivable Position</div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted">Total Receipts (Credited)</span><span className="mono">{fmtINR(t.total_receipts)}</span></div>
              <div className="flex justify-between"><span className="text-muted">Outstanding Receivable</span><span className="mono">{fmtINR(t.outstanding_receivable)}</span></div>
              <div className="text-xs text-muted">Billed minus received minus deductions — withheld amounts aren't counted as still owed.</div>
              {t.receipts_pending_review > 0 && <div className="flex justify-between text-xs text-muted"><span>+ pending review, not counted</span><span className="mono">{fmtINR(t.receipts_pending_review)}</span></div>}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-sm font-semibold mb-2">Income by Bank / Cash Account</div>
          {receiptsByBank.length === 0 && <div className="text-sm text-muted">No credited receipts recorded yet.</div>}
          {receiptsByBank.length > 0 && (
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead><tr><th>Account</th><th className="text-right">Credits</th><th className="text-right">Count</th></tr></thead>
                <tbody>
                  {receiptsByBank.map((row,i) => (
                    <tr key={i}>
                      <td>{bankLabel(row)}</td>
                      <td className="mono text-right">{fmtINR(row.amount)}</td>
                      <td className="mono text-right">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className={includeSettlement ? '' : 'no-print'}>
        <SettlementPanel fin={fin} projectId={projectId} reload={reload} canApprove={canApprove} user={user}/>
      </div>

    </div>

      {!isProjectLocked(p) && <InterimSettlementPanel projectId={projectId} project={p} refreshKey={[t.total_receipts, t.total_expenses, t.net_contract_value, t.total_deductions, t.receipts_pending_review].join('|')}/>}
      {!isProjectLocked(p) && <PeriodicSettlementPanel projectId={projectId} project={p} refreshKey={[t.total_receipts, t.total_expenses, t.net_contract_value, t.total_deductions, t.receipts_pending_review].join('|')}/>}
    </>
  );
}

// User-requested: a project billed on a monthly/quarterly cycle (e.g.
// Hsk_PS Rural) wanted periodic visibility into partner shares without
// waiting for the project's real, one-time Approve & Lock at actual
// completion. Recommended and built as a read-only, non-locking "as of
// [date]" report instead of true phased/partial settlement -- no Approve
// button here at all, purely informational. Only rendered for a project
// that isn't locked yet (see the caller above); the backend independently
// rejects the same case, this is just so the section doesn't even appear
// for a project that's already really settled.
// Today's date where the user is (not UTC, which is still "yesterday" for the first hours of an Indian morning).
const todayLocalISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
// The most recent half-year / year-end cut-offs (30 Sep, 31 Mar) that have already passed, newest first, plus today.
const recentCutoffs = () => {
  const today = todayLocalISO(), y = Number(today.slice(0, 4));
  const c = [];
  for (let yy = y; yy >= y - 2; yy--) { c.push({ date: `${yy}-09-30`, label: `30 Sep ${yy}` }); c.push({ date: `${yy}-03-31`, label: `31 Mar ${yy}` }); }
  return [{ date: today, label: 'Today' }, ...c.filter(x => x.date < today).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3)];
};
function InterimSettlementPanel({ projectId, project, refreshKey }) {
  const [asOf, setAsOf] = useState(todayLocalISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const generate = async (silent) => {
    if (!asOf) { if (!silent) toast.error('Pick a date.'); return; }
    if (!silent) setLoading(true);
    try { setData((await api.get(`/projects/${projectId}/interim-calculation`, { params: { as_of: asOf } })).data); }
    catch (e) { if (!silent) { toast.error(e.response?.data?.detail || 'Error'); setData(null); } }
    finally { if (!silent) setLoading(false); }
  };
  const interimPdf = async () => {
    if (!data) return;
    try {
      const num = (n) => fmtINRPdf(n);
      const cb = data.cash_backing;
      const sections = [
        { heading: 'Profit as of this date', head: ['Item', 'Amount'], body: rows.map(r => [r.item, num(r.v)]), columnStyles: { 1: { halign: 'right' } } },
        { heading: 'Cash position as of this date', head: ['Item', 'Amount'], body: [
            ['Receipts received (reviewed)', num(t.total_receipts)], ['Deductions withheld by the client', num(t.total_deductions)], ['Outstanding receivable', num(t.outstanding_receivable)],
            ...(t.receipts_pending_review > 0 ? [['Receipts pending review (not counted)', num(t.receipts_pending_review)]] : []),
            ...(cb ? [['Profit realised in cash (receipts - costs)', num(cb.cash_realised_profit)], ["Partners' profit payable now (cash-backed, after payouts)", num(cb.totals.payable_now)], ["Partners' profit awaiting collection", num(cb.totals.deferred)], ...(cb.totals.paid_ahead > 0.5 ? [['Already paid ahead of collection', num(cb.totals.paid_ahead)]] : [])] : []),
          ], columnStyles: { 1: { halign: 'right' } } },
        ...(cb && cb.partners.length ? [{ heading: 'Partners — cash-backed', head: ['Partner', 'Entitlement (billed)', 'Cash-backed', 'Paid', 'Payable now', 'Paid ahead', 'Awaiting collection'],
            body: cb.partners.map(x => [x.partner_name, num(x.entitlement), num(x.cash_backed), num(x.paid), num(x.payable_now), x.paid_ahead > 0.5 ? num(x.paid_ahead) : '-', x.deferred > 0.5 ? num(x.deferred) : '-']),
            columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } } }] : []),
        { heading: 'Partner shares (as of this date)', head: shareHead, body: shareRows, columnStyles: Object.fromEntries(shareHead.slice(1).map((_, i) => [i + 1, { halign: 'right' }])) },
      ];
      await gstExportPdf({ title: `Interim settlement — ${project.project_name}`, company: `As of ${fmtDate(data.as_of)}`, filters: `${data.billed_as_of_note} Informational only — not an approved settlement.`, sections, filename: `interim-settlement-${gstSeg(project.project_name)}-${data.as_of}.pdf`, landscape: false });
    } catch (e) { toast.error('Could not prepare the PDF'); }
  };
  // Always current: shown as soon as the tab opens, and re-read whenever the project's own figures change (a receipt,
  // expense, bill or deduction was saved) or the browser tab regains focus. Pressing Generate is only needed to change the date.
  const dataRef = useRef(null); dataRef.current = data;
  useEffect(() => { generate(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey, projectId]);
  useEffect(() => { const f = () => { if (dataRef.current) generate(true); }; window.addEventListener('focus', f); return () => window.removeEventListener('focus', f); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [asOf, projectId]);
  const t = data?.totals;
  const s = data?.settlements || [];
  const rows = t ? [
    ...(data.revenue_basis === 'contract'
      ? [{ item: 'Net Contract Value (full contract)', v: t.net_contract_value }]
      : [{ item: 'Revenue — bills raised up to this date', v: t.billed_amount }]),
    { item: '− Total Expenses', v: -t.total_expenses },
    ...(t.total_deductions_non_recoverable > 0 ? [{ item: '− Non-Recoverable Deductions', v: -t.total_deductions_non_recoverable }] : []),
    ...(t.total_security_forfeited > 0 ? [{ item: '− Forfeited Security', v: -t.total_security_forfeited }] : []),
    { item: '= Project Profit', v: t.project_profit, bold: true },
    { item: '+ Profit Adjustment', v: t.profit_adjustment },
    { item: '= Distributable Profit', v: t.distributable_profit, bold: true, gold: true },
  ] : [];
  // Same per-model column shape SettlementPanel's own PDF export already
  // uses -- kept in sync deliberately rather than reusing that whole
  // component, which also fetches real distribution history and shows
  // Approve/reopen messaging that has no place in a read-only interim view.
  let shareHead = null, shareRows = [];
  if (data) {
    const p = data.project;
    if (p.partnership_model === 'money_work') {
      shareHead = ['Partner','Investment','Money %','Money Profit','Work %','Work Profit','Total','Paid','Balance'];
      shareRows = s.map(x => [x.partner_name, fmtINR(x.investment), `${(x.money_share_pct||0).toFixed(2)}%`, fmtINR(x.money_profit), `${(x.work_share_pct||0).toFixed(2)}%`, fmtINR(x.work_profit), fmtINR(x.total_profit), fmtINR(x.profit_paid), fmtINR(x.balance)]);
    } else if (p.partnership_model === 'equal_capital_bonus') {
      shareHead = ['Partner','Investment', p.capital_bonus_base==='variable' ? 'Base Share' : 'Equal Share','Capital Bonus','Total','Paid','Balance'];
      shareRows = s.map(x => [x.partner_name, fmtINR(x.investment), fmtINR(x.equal_share), fmtINR(x.capital_bonus), fmtINR(x.total_profit), fmtINR(x.profit_paid), fmtINR(x.balance)]);
    } else {
      shareHead = ['Partner','Share %','Profit Entitlement','Paid','Balance'];
      shareRows = s.map(x => [x.partner_name, `${(x.share_pct||0).toFixed(2)}%`, fmtINR(x.total_profit), fmtINR(x.profit_paid), fmtINR(x.balance)]);
    }
  }
  return (
    <div className="card card-dark mt-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2 no-print">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Interim Settlement</div>
          <div className="text-xs text-muted max-w-xl">A point-in-time snapshot for a project billed periodically (monthly/quarterly/FY-wise) — purely informational, never locks the project or changes any real number. Pick any date.</div>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" className="input" value={asOf} onChange={e=>setAsOf(e.target.value)}/>
          {recentCutoffs().map(c => <button key={c.date} type="button" className={'tab btn-small ' + (asOf === c.date ? 'active' : '')} onClick={()=>setAsOf(c.date)} title={c.date}>{c.label}</button>)}
          <button onClick={()=>generate(false)} disabled={loading} className="btn-gold btn-small">{loading ? 'Generating…' : 'Generate'}</button>
          {data && <button onClick={()=>printOnly('interim', false)} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Print</button>}
          {data && <button onClick={interimPdf} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="interim-pdf"><Download size={14}/>Download PDF</button>}
        </div>
      </div>
      {data && (
        <div className="print-area ps-interim">
          <div className="print-header">
            <div className="print-report-title">Interim Settlement as of {fmtDate(data.as_of)} — {project.project_name}</div>
            <div className="print-meta">Generated: {fmtGeneratedAt()} — informational only, not an approved settlement</div>
          </div>
          <div className="overflow-x-auto mb-4">
            <table className="tbl">
              <tbody>
                {rows.map(r => (
                  <tr key={r.item}>
                    <td className={r.bold ? 'font-semibold' : ''}>{r.item}</td>
                    <td className={"mono text-right " + (r.bold ? 'font-semibold ' : '') + (r.gold ? 'text-[color:var(--gold)]' : '')}>{fmtINR(r.v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-sm font-semibold mb-2">Cash position (as of this date)</div>
          <div className="overflow-x-auto mb-2">
            <table className="tbl">
              <tbody>
                <tr><td>Receipts received (reviewed)</td><td className="mono text-right">{fmtINR(t.total_receipts)}</td></tr>
                <tr><td>Deductions withheld by the client</td><td className="mono text-right">{fmtINR(t.total_deductions)}</td></tr>
                <tr><td className="font-semibold">Outstanding receivable</td><td className="mono text-right font-semibold">{fmtINR(t.outstanding_receivable)}</td></tr>
                {t.receipts_pending_review > 0 && <tr><td className="text-muted">+ receipts pending review (not counted)</td><td className="mono text-right text-muted">{fmtINR(t.receipts_pending_review)}</td></tr>}
                {data.cash_backing && <>
                  <tr><td>Profit realised in cash (receipts − costs)</td><td className={'mono text-right ' + (data.cash_backing.cash_realised_profit < 0 ? 'text-red-400' : '')}>{fmtINR(data.cash_backing.cash_realised_profit)}</td></tr>
                  <tr><td className="font-semibold">Partners' profit payable now (cash-backed, after payouts)</td><td className="mono text-right font-semibold text-[color:var(--gold)]">{fmtINR(data.cash_backing.totals.payable_now)}</td></tr>
                  <tr><td>Partners' profit awaiting collection</td><td className="mono text-right text-amber-400">{fmtINR(data.cash_backing.totals.deferred)}</td></tr>
                  {data.cash_backing.totals.paid_ahead > 0.5 && <tr><td className="text-red-400">Already paid ahead of collection</td><td className="mono text-right text-red-400">{fmtINR(data.cash_backing.totals.paid_ahead)}</td></tr>}
                </>}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-muted mb-2">Outstanding receivable = revenue billed − receipts − deductions. Receipts affect cash and the receivable, not profit.</div>
          <div className="text-xs text-muted mb-4">{data.billed_as_of_note}</div>
          <div className="text-sm font-semibold mb-2">Partner Shares (as of this date)</div>
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead><tr>{shareHead.map(h=><th key={h} className={h==='Partner'?'':'text-right'}>{h}</th>)}</tr></thead>
              <tbody>
                {shareRows.length===0 && <tr><td colSpan={shareHead.length} className="text-center text-muted py-4">No partners assigned yet.</td></tr>}
                {shareRows.map((row,i) => (
                  <tr key={i}>{row.map((cell,ci) => <td key={ci} className={ci===0?'':'mono text-right'}>{cell}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Half-yearly / quarterly / monthly settlement statement for one financial year: the position at every period end and what
// each period added, for the project and for every partner. Read-only; the numbers come from the same calculation as the
// Interim Settlement above.
const PERIOD_KINDS = { half: 'Half-yearly', quarter: 'Quarterly', month: 'Monthly' };
// Both settlement reports can be on screen at once; print only the one whose button was pressed.
function printOnly(which, landscape) {
  document.body.dataset.printOnly = which;
  const clear = () => { delete document.body.dataset.printOnly; window.removeEventListener('afterprint', clear); };
  window.addEventListener('afterprint', clear);
  setTimeout(() => (landscape ? printLandscape() : window.print()), 50);
}
function PeriodicSettlementPanel({ projectId, project, refreshKey }) {
  const [fy, setFy] = useState(gstCurrentFy());
  const [kind, setKind] = useState('half');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const generate = async (silent) => {
    if (!silent) setLoading(true);
    try { setData((await api.get(`/projects/${projectId}/periodic-settlement`, { params: { fy, granularity: kind } })).data); }
    catch (e) { if (!silent) { toast.error(e.response?.data?.detail || 'Error'); setData(null); } }
    finally { if (!silent) setLoading(false); }
  };
  // Once a statement is on screen, keep it current when the project's figures change or the browser tab regains focus.
  const dataRef = useRef(null); dataRef.current = data;
  useEffect(() => { if (dataRef.current) generate(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey]);
  useEffect(() => { const f = () => { if (dataRef.current) generate(true); }; window.addEventListener('focus', f); return () => window.removeEventListener('focus', f); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fy, kind, projectId]);
  const P = data?.periods || [];
  const partnerList = [];
  P.forEach(per => per.partners.forEach(x => { if (!partnerList.find(q => q.partner_id === x.partner_id)) partnerList.push({ partner_id: x.partner_id, partner_name: x.partner_name }); }));
  const pOf = (per, id) => per.partners.find(x => x.partner_id === id) || { period_entitlement: 0, cumulative_entitlement: 0, cumulative_paid: 0, balance: 0 };
  const sign = (n) => (n > 0 ? '+' : '') + fmtINR(n);
  const revLabel = data?.revenue_basis === 'bills_to_date' ? 'Billed' : 'Billed (bills)';
  const pdf = async () => {
    if (pdfBusy || !data) return; setPdfBusy(true);
    try {
      const num = (n) => fmtINRPdf(n);
      const right = (f, t) => Object.fromEntries(Array.from({ length: t - f + 1 }, (_, i) => [f + i, { halign: 'right' }]));
      const sections = [{
        heading: `${PERIOD_KINDS[kind]} settlement — ${data.financial_year}`,
        head: ['Period', 'As of', 'Billed', 'Receipts', 'Expenses', 'Profit', 'Distributable (cumulative)', 'This period', 'Outstanding receivable', 'Profit in cash'],
        body: [['Opening', fmtDate(data.opening_as_of), num(data.opening.billed_amount), num(data.opening.total_receipts), num(data.opening.total_expenses), num(data.opening.project_profit), num(data.opening.distributable_profit), '—', num(data.opening.outstanding_receivable), num(data.opening.cash_realised_profit)],
          ...P.map(per => [per.label + (per.partial ? ' (to date)' : ''), fmtDate(per.as_of), num(per.cumulative.billed_amount), num(per.cumulative.total_receipts), num(per.cumulative.total_expenses), num(per.cumulative.project_profit), num(per.cumulative.distributable_profit), (per.period.distributable_profit > 0 ? '+' : '') + num(per.period.distributable_profit), num(per.cumulative.outstanding_receivable), num(per.cumulative.cash_realised_profit)])],
        columnStyles: right(2, 9),
      }];
      if (partnerList.length) sections.push({
        heading: 'Partners — entitlement this period and cumulative',
        head: ['Period', ...partnerList.flatMap(q => [`${q.partner_name} — this period`, `${q.partner_name} — cumulative`, `${q.partner_name} — balance`])],
        body: P.map(per => [per.label + (per.partial ? ' (to date)' : ''), ...partnerList.flatMap(q => { const x = pOf(per, q.partner_id); return [(x.period_entitlement > 0 ? '+' : '') + num(x.period_entitlement), num(x.cumulative_entitlement), num(x.balance)]; })]),
        columnStyles: right(1, partnerList.length * 3),
      }, {
        heading: 'Partners — cash-backed position at each period end',
        head: ['Period', ...partnerList.flatMap(q => [`${q.partner_name} — cash-backed`, `${q.partner_name} — payable now`, `${q.partner_name} — paid ahead`])],
        body: P.map(per => [per.label + (per.partial ? ' (to date)' : ''), ...partnerList.flatMap(q => { const x = pOf(per, q.partner_id); return [num(x.cumulative_cash_backed || 0), num(x.payable_now || 0), x.paid_ahead > 0.5 ? num(x.paid_ahead) : '-']; })]),
        columnStyles: right(1, partnerList.length * 3),
      });
      await gstExportPdf({ title: `Settlement statement — ${project.project_name}`, company: `${PERIOD_KINDS[kind]} · ${data.financial_year}`, filters: 'Informational only — not an approved settlement. Cumulative = from the project start; "this period" = what the period itself added.',
        sections, filename: `settlement-${gstSeg(project.project_name)}-${gstSeg(data.financial_year)}-${kind}.pdf`, landscape: true });
    } catch (e) { toast.error('Could not prepare the PDF'); }
    finally { setPdfBusy(false); }
  };
  return (
    <div className="card card-dark mt-4" data-testid="periodic-settlement">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2 no-print">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Settlement by period — half-yearly / quarterly / monthly</div>
          <div className="text-xs text-muted max-w-xl">The position at the end of every half-year (or quarter, or month) of a financial year, and what each period added — for the project and for each partner. Purely informational; nothing is locked or paid.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="input" style={{ width: 140 }} value={fy} onChange={e => setFy(e.target.value)} data-testid="period-fy">{gstFyOptions().map(f => <option key={f} value={f}>{f}</option>)}</select>
          <select className="input" style={{ width: 130 }} value={kind} onChange={e => setKind(e.target.value)} data-testid="period-kind">{Object.entries(PERIOD_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <button onClick={()=>generate(false)} disabled={loading} className="btn-gold btn-small" data-testid="period-generate">{loading ? 'Generating…' : 'Generate'}</button>
          {data && <><button onClick={() => printOnly('periodic', true)} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>
            <button onClick={pdf} disabled={pdfBusy} className="btn-outline btn-small inline-flex items-center gap-1"><Download size={14}/>{pdfBusy ? 'Preparing…' : 'Download PDF'}</button></>}
        </div>
      </div>
      {data && (
        <div className="print-area ps-periodic">
          <div className="print-header">
            <div className="print-company">{project.project_name}</div>
            <div className="print-report-title">{PERIOD_KINDS[kind]} settlement statement — {data.financial_year}</div>
            <div className="print-meta">Generated: {fmtGeneratedAt()} — informational only, not an approved settlement</div>
          </div>
          <div className="overflow-x-auto mb-4">
            <table className="tbl">
              <thead><tr><th>Period</th><th>As of</th><th className="text-right">{revLabel}</th><th className="text-right">Receipts</th><th className="text-right">Expenses</th><th className="text-right">Profit</th><th className="text-right">Distributable (cumulative)</th><th className="text-right">This period</th><th className="text-right">Outstanding receivable</th><th className="text-right">Profit in cash</th></tr></thead>
              <tbody>
                <tr style={{ opacity: .8 }}><td>Opening</td><td className="text-xs">{fmtDate(data.opening_as_of)}</td><td className="mono text-right">{fmtINR(data.opening.billed_amount)}</td><td className="mono text-right">{fmtINR(data.opening.total_receipts)}</td><td className="mono text-right">{fmtINR(data.opening.total_expenses)}</td><td className="mono text-right">{fmtINR(data.opening.project_profit)}</td><td className="mono text-right">{fmtINR(data.opening.distributable_profit)}</td><td className="mono text-right">—</td><td className="mono text-right">{fmtINR(data.opening.outstanding_receivable)}</td><td className="mono text-right">{fmtINR(data.opening.cash_realised_profit)}</td></tr>
                {P.length === 0 && <tr><td colSpan="10" className="text-center text-muted py-4">This financial year has not started yet.</td></tr>}
                {P.map(per => (
                  <tr key={per.from}><td>{per.label}{per.partial && <span className="text-xs text-muted"> (to date)</span>}</td><td className="text-xs">{fmtDate(per.as_of)}</td>
                    <td className="mono text-right">{fmtINR(per.cumulative.billed_amount)}</td><td className="mono text-right">{fmtINR(per.cumulative.total_receipts)}</td><td className="mono text-right">{fmtINR(per.cumulative.total_expenses)}</td><td className="mono text-right">{fmtINR(per.cumulative.project_profit)}</td>
                    <td className="mono text-right text-[color:var(--gold)]">{fmtINR(per.cumulative.distributable_profit)}</td>
                    <td className={'mono text-right font-semibold ' + (per.period.distributable_profit < 0 ? 'text-red-400' : '')}>{sign(per.period.distributable_profit)}</td>
                    <td className="mono text-right">{fmtINR(per.cumulative.outstanding_receivable)}</td><td className="mono text-right">{fmtINR(per.cumulative.cash_realised_profit)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {partnerList.length > 0 && (
            <>
              <div className="text-sm font-semibold mb-2">Partners — entitlement</div>
              <div className="overflow-x-auto">
                <table className="tbl">
                  <thead><tr><th>Period</th>{partnerList.map(q => <th key={q.partner_id} className="text-right">{q.partner_name}</th>)}</tr></thead>
                  <tbody>
                    {P.map(per => (
                      <tr key={per.from}><td>{per.label}{per.partial && <span className="text-xs text-muted"> (to date)</span>}</td>
                        {partnerList.map(q => { const x = pOf(per, q.partner_id); return <td key={q.partner_id} className="mono text-right"><div className="font-semibold">{sign(x.period_entitlement)}</div><div className="text-xs text-muted">cumulative {fmtINR(x.cumulative_entitlement)} · balance {fmtINR(x.balance)}</div><div className="text-xs text-muted">cash-backed {fmtINR(x.cumulative_cash_backed)} · payable now {fmtINR(x.payable_now)}{x.paid_ahead > 0.5 ? <span className="text-red-400"> · paid ahead {fmtINR(x.paid_ahead)}</span> : null}</div></td>; })}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="text-xs text-muted mt-3">{data.note}</div>
        </div>
      )}
    </div>
  );
}

function ReconciliationPanel({ projectId, canEdit }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    try { const r = await api.get(`/projects/${projectId}/reconcile`); setData(r.data); }
    catch (e) { setLoadError(e.response?.data?.detail || 'Could not load reconciliation data'); }
  };
  useEffect(() => { load(); }, [projectId]);
  const takeSnapshot = async () => {
    try {
      // snapshot current computed as baseline
      const r = await api.get(`/projects/${projectId}/financials`);
      await api.post(`/projects/${projectId}/snapshot`, { imported_totals: r.data.totals });
      toast.success('Baseline snapshot saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save baseline'); }
  };
  if (loadError) return <div className="card card-dark text-center text-red-400 py-8">{loadError}</div>;
  if (!data) return <Loading/>;
  return (
    <div className="card card-dark">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Reconciliation</div>
          <div className="text-xs text-muted">{data.has_snapshot ? 'Compare imported totals vs current computed values.' : 'No import snapshot found. Save a baseline to compare against future changes.'}</div>
        </div>
        {canEdit && <button onClick={takeSnapshot} className="btn-outline btn-small inline-flex items-center gap-1"><Scale size={14}/>Save Baseline</button>}
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Metric</th><th className="text-right">Imported</th><th className="text-right">Current</th><th className="text-right">Δ Difference</th><th>Status</th></tr></thead>
        <tbody>
          {data.rows.map(r => (
            <tr key={r.metric}>
              <td>{r.metric.replaceAll('_',' ')}</td>
              {/* Round 27 finding: a metric with no recorded baseline (status
                  NO_BASELINE — imported/difference come back null from the
                  backend) used to render as imported ₹0 vs. a red MISMATCH,
                  which looked like a real discrepancy on every project that
                  simply never had a baseline saved yet. Show '—' instead of
                  fmtINR(null)'s misleading '₹0', and a neutral badge instead
                  of the danger color. */}
              <td className="mono text-right">{r.status==='NO_BASELINE' ? '—' : fmtINR(r.imported)}</td>
              <td className="mono text-right">{fmtINR(r.current)}</td>
              <td className={"mono text-right " + (r.status==='NO_BASELINE' ? '' : (Math.abs(r.difference) < 0.5 ? '' : 'text-red-400'))}>{r.status==='NO_BASELINE' ? '—' : fmtINR(r.difference)}</td>
              <td><span className={"badge " + (r.status==='MATCH' ? 'status-running' : r.status==='NO_BASELINE' ? 'status-closed' : 'status-completed')}>{r.status==='NO_BASELINE' ? 'NO BASELINE' : r.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// Mirrors backend _RESTORABLE_ENTITY_TABLES exactly -- keeps the Restore
// button from appearing (and 400ing) on entity types the endpoint
// deliberately won't restore (profiles -- tied to Supabase auth, not a
// plain row; companies -- can't be deleted at all; bulk-operation audit
// entries like master_type/bulk_import/import/backup).
const RESTORABLE_ENTITY_TYPES = new Set([
  'project','investment','expense','receipt','partner-transaction','deduction',
  'bill','partner','vendor','vendor_contract_term','vendor_transaction','master',
  'security_instrument','profit_adjustment','profit_sharing_rule','bank_account',
  'expense_category','fixed_asset','flag','role',
]);

function AuditPanel({ entityId }) {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const load = () => api.get('/audit-logs', { params: entityId ? { entity_id: entityId, limit: 500 } : { limit: 500 } }).then(r => setLogs(r.data));
  useEffect(() => { load(); }, [entityId]);
  const restore = async (l) => {
    if (!window.confirm(`Restore this deleted ${l.entity_type.replace(/_/g,' ').replace(/-/g,' ')}? It will come back exactly as it was right before deletion.`)) return;
    try { await api.post(`/audit-logs/${l.id}/restore`); toast.success('Restored'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not restore'); }
  };
  return (
    <div className="card card-dark overflow-x-auto">
      <div className="serif text-xl mb-4 text-[color:var(--gold)]">Audit Trail</div>
      <table className="tbl">
        <thead><tr><th>When</th><th>User</th><th>Entity</th><th>Action</th>{isMasterAdmin(user) && <th></th>}</tr></thead>
        <tbody>
          {logs.length===0 && <tr><td colSpan="5" className="text-center text-muted py-6">No audit entries.</td></tr>}
          {logs.map(l => (
            <tr key={l.id}>
              <td className="text-xs">{fmtDate(l.created_at)}</td><td>{l.user_email||'—'}</td><td className="text-xs">{l.entity_type}</td><td><span className="chip">{l.action}</span></td>
              {isMasterAdmin(user) && <td className="text-right">
                {l.action === 'delete' && RESTORABLE_ENTITY_TYPES.has(l.entity_type) && <button onClick={()=>restore(l)} className="tab btn-small inline-flex items-center gap-1" data-testid={`restore-${l.id}`}><Undo2 size={12}/>Restore</button>}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// User-requested: a receipt can now settle several bills at once, each for
// its own amount, instead of the old one-receipt-one-bill dropdown. Shared
// between the Income page and the project-scoped Receipts tab so both get
// the same picker/validation instead of two hand-built copies.
// allocations: [{bill_id, amount}]. billLabel(b) formats one bill's row.
function BillAllocationPicker({ bills, amount, allocations, setAllocations, billLabel }) {
  const selectedIds = new Set(allocations.map(a => a.bill_id));
  // User-requested: ticking a bill pre-fills its box with that bill's own
  // outstanding amount (the common case is settling it in full) instead
  // of leaving it blank -- still a plain number input, so it's changed
  // exactly like any other typed amount whenever it's a partial payment
  // or more than one bill is ticked at once.
  //
  // Bug fix (user-reported, "Supply of Padestal fan-10pc 2nd lot" Bill
  // #11): defaulting to the bill's full Outstanding ignored the
  // receipt's own Amount above even when it was already typed in and
  // smaller -- e.g. because some of it will be withheld as a deduction
  // recorded separately below. The one thing that must ALWAYS be true
  // at save (this picker's own "must sum to Amount" check further down,
  // enforced server-side too) is that a single ticked bill's allocation
  // equals the receipt's Amount exactly -- there's no legitimate reason
  // it should ever be less, since nothing else absorbs the difference
  // for a single-bill receipt. So for the common single-bill case, this
  // box now stays continuously equal to Amount (capped at this bill's
  // own Outstanding, so a receipt that's clearly for MORE than this one
  // bill is left alone rather than guessed at) -- both at the moment of
  // ticking and afterward, so changing Amount later doesn't leave a
  // stale box behind either. Ticking a SECOND bill turns off this
  // auto-sync entirely (an ambiguous multi-way split is for the user to
  // enter by hand, same as before).
  useEffect(() => {
    if (allocations.length !== 1) return;
    const only = allocations[0];
    const target = Number(amount || 0);
    if (target <= 0) return;
    const bill = bills.find(b => b.id === only.bill_id);
    const outstanding = bill ? Number(bill.outstanding_amount ?? bill.net_bank_credit ?? 0) : null;
    if (outstanding != null && target > outstanding + 0.01) return;
    if (Math.abs(Number(only.amount || 0) - target) > 0.005) {
      setAllocations([{ ...only, amount: target }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, allocations.length === 1 ? allocations[0].bill_id : null]);
  const toggle = (billId) => {
    if (selectedIds.has(billId)) setAllocations(allocations.filter(a => a.bill_id !== billId));
    else {
      const bill = bills.find(b => b.id === billId);
      const outstanding = bill ? Number(bill.outstanding_amount ?? bill.net_bank_credit ?? 0) : 0;
      const receiptAmount = Number(amount || 0);
      const defaultAmount = (allocations.length === 0 && receiptAmount > 0 && receiptAmount <= outstanding + 0.01)
        ? receiptAmount
        : (bill ? (bill.outstanding_amount ?? bill.net_bank_credit ?? '') : '');
      setAllocations([...allocations, { bill_id: billId, amount: defaultAmount }]);
    }
  };
  const setAmount = (billId, val) => setAllocations(allocations.map(a => a.bill_id === billId ? { ...a, amount: val } : a));
  const totalAllocated = allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
  const target = Number(amount || 0);
  const diff = Math.round((target - totalAllocated) * 100) / 100;
  const ok = Math.abs(diff) < 0.01 && allocations.length > 0;
  return (
    <div className="col-span-2">
      <div className="text-xs text-muted mb-1">Settles Bill(s) (optional) — select one or more, and how much of this receipt goes to each</div>
      <div className="rounded border p-2" style={{ borderColor: 'var(--border)', maxHeight: 180, overflowY: 'auto' }}>
        {bills.length === 0 && <div className="text-xs text-muted py-1">No open bills on this project.</div>}
        {bills.map(b => (
          <label key={b.id} className="flex items-center gap-2 text-xs py-1 cursor-pointer">
            <input type="checkbox" checked={selectedIds.has(b.id)} onChange={() => toggle(b.id)} />
            {/* User-requested: a bill's own Description (captured on the
                Bill form, shown nowhere else) is genuinely useful here
                while deciding which bill a new receipt actually settles
                -- a plain hover tooltip rather than a permanent extra
                line, so the picker stays compact for the common case of
                a bill with no description at all. */}
            <span className="flex-1" title={b.description || undefined}>{billLabel(b)}</span>
            {/* User-requested: same "Pending Audit" signal shown on the
                Bills tables themselves -- useful right here too, so
                picking a bill to link a new receipt to shows there's
                already a payment against it awaiting review. */}
            {b.pending_audit_amount > 0 && <span title={`${fmtINR(b.pending_audit_amount)} received but not yet reviewed in the Audit Queue`}><AuditStatusChip status="pending_review"/></span>}
            {selectedIds.has(b.id) && (
              <input className="input mono" style={{ width: 110 }} type="number" min="0"
                value={allocations.find(a => a.bill_id === b.id)?.amount ?? ''}
                onClick={e => e.stopPropagation()}
                onChange={e => setAmount(b.id, e.target.value)} />
            )}
          </label>
        ))}
      </div>
      {allocations.length > 0 && (
        <div className="text-xs mt-1" style={{ color: ok ? 'var(--accent-green)' : 'var(--accent-amber)' }}>
          Allocated {fmtINR(totalAllocated)} / {fmtINR(target)}
          {ok ? ' ✓' : diff > 0 ? ` — ${fmtINR(diff)} still unallocated` : ` — ${fmtINR(-diff)} over the receipt amount`}
        </div>
      )}
    </div>
  );
}

// User-requested: TDS/Labour Cess/Security Deposit/Retention/GST-reserved
// etc. moved OFF the Bill form and onto the Receipt form instead -- what a
// client actually withholds is usually only known once the payment lands,
// not when the bill is raised. Each line here becomes a real `deductions`
// row with `reference_bill_id` set (the same mechanism already built for
// a manually-entered deduction to reduce a specific bill's Outstanding —
// see project_anvid_deduction_bill_scope memory), created via separate
// POST /deductions calls right after the receipt itself saves. Scoped to
// whichever bill(s) this receipt is actually allocated to, so a multi-bill
// receipt can withhold against each one separately.
function ReceiptDeductionsPicker({ bills, allocations, lines, setLines, masters, companyId, isEdit }) {
  const typeMasters = mastersForCompany(masters.deduction_type, companyId);
  const typeOpts = typeMasters.length ? typeMasters.map(m=>[m.name,m.name]) : DED_TYPES.map(x=>[x,x]);
  const scopedBills = (allocations||[]).length ? bills.filter(b => allocations.some(a=>a.bill_id===b.id)) : bills;
  // Bug fix (user-reported, "deduction option is not visible"): this
  // whole section used to vanish with zero explanation whenever there
  // were no open bills to scope it against -- indistinguishable from a
  // broken/missing feature. Same explicit "nothing to show, here's why"
  // treatment BillAllocationPicker already uses just above this, instead
  // of silently returning null.
  if (scopedBills.length === 0) {
    return (
      <div className="col-span-2">
        <div className="text-xs text-muted mb-1">Deductions withheld against a bill (optional)</div>
        <div className="text-xs text-muted py-1">No open bills on this project yet — nothing to withhold against. Tick a bill above under "Settles Bill(s)", or add a Bill first.</div>
      </div>
    );
  }
  const lineAmount = (l) => {
    const base = Number(l.base_amount||0);
    return (l.percentage !== '' && l.percentage != null) ? base * Number(l.percentage) / 100 : Number(l.amount||0);
  };
  // Round 2 fix (this replaces an earlier, wrong attempt): the previous
  // version auto-set a bill's allocation to Outstanding minus this
  // receipt's own deductions -- but the real, pre-existing hard rule
  // (BillAllocationPicker's own "must sum to the receipt's Amount" check,
  // enforced at save) has nothing to do with deductions at all: an
  // allocation is what portion of the receipt's own cash Amount goes to
  // this bill, full stop. Deductions are separate, withheld money, never
  // part of that sum. That formula fought the real constraint instead of
  // satisfying it -- live-caught (Supply of Padestal fan-10pc 2nd lot,
  // Bill #11) showing "Allocated ₹48,750.05 / ₹47,301 -- over the
  // receipt amount" the moment Amount was set to less than the bill's
  // full value, even with zero deductions entered yet. The real fix
  // belongs in BillAllocationPicker itself (keeping a single ticked
  // bill's allocation continuously equal to the receipt's own Amount) --
  // see its own comment. Deductions no longer touch the allocation at
  // all here; these are back to plain, unlinked line handlers.
  const addLine = () => {
    // Bug fix: when there's only one scoped bill, bill_id was pre-filled
    // but base_amount was left blank (only ever set from the Bill
    // select's own onChange, which never fires for an already-selected
    // value) -- a % entered on this fresh row had nothing to multiply
    // against, so the auto-calculated Amount silently showed 0.00.
    const defaultBill = scopedBills.length===1 ? scopedBills[0] : null;
    setLines([...(lines||[]), { bill_id: defaultBill?.id||'', type:'', classification:'', percentage:'', base_amount: defaultBill?.gross_amount||'', amount:'', notes:'' }]);
  };
  const updateLine = (i, patch) => setLines(lines.map((l,idx)=> idx===i ? {...l, ...patch} : l));
  const removeLine = (i) => setLines(lines.filter((_,idx)=>idx!==i));
  // Live reconciliation -- reworked twice after real user reports.
  // Round 1 compared THIS receipt's own Received+Deducted against the
  // bill's already-stored Outstanding (a snapshot from BEFORE this
  // transaction) -- wrong, since a bill already fully settled by an
  // earlier receipt correctly shows Outstanding 0, and treating a normal
  // partial payment as "over the bill's Outstanding" was never right
  // either way.
  // Round 2 (this version) compares against the bill's own stable total
  // value (net_bank_credit) instead, adding this receipt's Received/
  // Deducted on top of the bill's own allocated_amount/linked_deduction_
  // amount -- correct for a brand-new receipt, but a real user report
  // (HSK_PS Rural, editing an existing 1-Oct-2024 receipt) traced a
  // deeper issue: when EDITING an already-saved receipt, that bill's
  // allocated_amount/linked_deduction_amount ALREADY include this same
  // receipt's own prior contribution -- adding the form's current
  // amounts on top double-counts it, so it can never show as settled no
  // matter what's entered. Deductions have no column recording which
  // receipt created them, so there's no reliable way to subtract this
  // receipt's own already-saved share back out. Rather than show a
  // number that's silently wrong on every edit, this whole reconciliation
  // is now scoped to a brand-new receipt only (isEdit false) -- the one
  // case where "the bill's own totals + this form" is unambiguous.
  const touchedBillIds = isEdit ? [] : [...new Set([...(allocations||[]).map(a=>a.bill_id), ...(lines||[]).filter(l=>l.bill_id).map(l=>l.bill_id)])];
  const reconciliation = touchedBillIds.map(bid => {
    const bill = bills.find(b=>b.id===bid);
    if (!bill) return null;
    const newReceived = (allocations||[]).filter(a=>a.bill_id===bid).reduce((s,a)=>s+Number(a.amount||0),0);
    const newDeducted = (lines||[]).filter(l=>l.bill_id===bid).reduce((s,l)=>s+lineAmount(l),0);
    const totalReceived = Number(bill.allocated_amount || 0) + newReceived;
    const totalDeducted = Number(bill.linked_deduction_amount || 0) + newDeducted;
    const billValue = Number(bill.net_bank_credit || 0);
    const remaining = Math.round((billValue - totalReceived - totalDeducted) * 100) / 100;
    return { bill, totalReceived, totalDeducted, billValue, remaining, settled: Math.abs(remaining) < 0.01, over: remaining < -0.01 };
  }).filter(Boolean);
  return (
    <div className="col-span-2">
      <div className="text-xs text-muted mb-1">Deductions withheld against a bill (optional) — TDS, Labour Cess, Security Deposit, Retention, GST reserved, etc. Each reduces that bill's own Outstanding, and a Refundable type still auto-creates a Security & Refundables entry.</div>
      {(lines||[]).map((l, i) => {
        const pctAmount = lineAmount(l);
        return (
          // User-reported: the % field had "no space to write, a button
          // in the middle" -- the browser's own native number-spinner
          // control rendering inside this narrow column, same class of
          // issue as the iPad date-field fix above (a native control
          // that doesn't respect a custom-styled narrow box). Spinners
          // suppressed app-wide for every .input number field, see
          // index.css -- reclaims the full width for the value itself.
          <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-end mb-2 rounded border p-2" style={{borderColor:'var(--border)'}}>
            <div className="col-span-1 sm:col-span-3">
              <div className="text-[10px] text-muted mb-0.5">Bill</div>
              <select className="input" style={{fontSize:12}} value={l.bill_id||''} onChange={e=>{
                const b = bills.find(x=>x.id===e.target.value);
                updateLine(i,{bill_id:e.target.value, base_amount: b?.gross_amount||l.base_amount});
              }}>
                <option value="">— select —</option>
                {scopedBills.map(b=><option key={b.id} value={b.id}>{b.bill_number||fmtDate(b.bill_date)}</option>)}
              </select>
            </div>
            <div className="col-span-1 sm:col-span-2">
              <div className="text-[10px] text-muted mb-0.5">Type</div>
              <select className="input" style={{fontSize:12}} value={l.type||''} onChange={e=>{
                const m = typeMasters.find(x=>x.name===e.target.value);
                updateLine(i, {type:e.target.value, classification: m?.default_classification || l.classification});
              }}>
                <option value="">— select —</option>
                {typeOpts.map(([v,lab])=><option key={v} value={v}>{lab}</option>)}
              </select>
            </div>
            <div className="col-span-1 sm:col-span-2">
              <div className="text-[10px] text-muted mb-0.5">Base (for %)</div>
              <input className="input mono" style={{fontSize:12}} type="number" min="0" value={l.base_amount||''} onChange={e=>updateLine(i,{base_amount:e.target.value})}/>
            </div>
            <div className="col-span-1 sm:col-span-1">
              <div className="text-[10px] text-muted mb-0.5">%</div>
              <input className="input mono" style={{fontSize:12}} type="number" step="0.01" min="0" max="100" value={l.percentage??''} onChange={e=>updateLine(i,{percentage:e.target.value})}/>
            </div>
            <div className="col-span-1 sm:col-span-3">
              <div className="text-[10px] text-muted mb-0.5">{l.percentage ? 'Amount (auto)' : 'Amount'}</div>
              <input className="input mono" style={{fontSize:12}} type="number" min="0" disabled={!!l.percentage} value={l.percentage ? pctAmount.toFixed(2) : (l.amount||'')} onChange={e=>updateLine(i,{amount:e.target.value})}/>
            </div>
            <div className="col-span-2 sm:col-span-1 flex justify-end">
              <button type="button" aria-label="Remove" onClick={()=>removeLine(i)} className="tab btn-small text-red-300"><X size={12}/></button>
            </div>
          </div>
        );
      })}
      <button type="button" onClick={addLine} className="btn-outline btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Deduction</button>
      {isEdit ? (
        <div className="text-xs text-muted mt-2">Editing an existing receipt — the bill's own totals already include this receipt's prior amounts, so a live match check isn't shown here to avoid double-counting. Check the Bill's own Outstanding on its own tab instead.</div>
      ) : reconciliation.length > 0 && (
        // User-requested: the "still outstanding" (neither settled nor
        // over) state used var(--mid-gray) -- read as dimmed/de-emphasized
        // next to the green and amber states either side of it, when it's
        // exactly as worth noticing as they are. Gold matches this app's
        // own convention for a plain, attention-worthy computed figure
        // (e.g. Net Bank Credit), distinct from green (good) and amber
        // (a real problem, over-collected).
        <div className="mt-2 space-y-1">
          {reconciliation.map(r => (
            <div key={r.bill.id} className="text-xs" style={{color: r.over ? 'var(--accent-amber)' : r.settled ? 'var(--accent-green)' : 'var(--gold)'}}>
              {r.bill.bill_number || fmtDate(r.bill.bill_date)}: Bill {fmtINR(r.billValue)} — Received {fmtINR(r.totalReceived)} + Deducted {fmtINR(r.totalDeducted)} so far
              {r.settled ? ' — fully settled ✓' : r.over ? ` — ${fmtINR(-r.remaining)} more than the bill is worth, please check` : ` — ${fmtINR(r.remaining)} still outstanding after this`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Fires one POST /deductions per filled-in ReceiptDeductionsPicker line,
// right after the receipt itself has saved. A line needs a Bill, a Type,
// and a non-zero resulting amount to count -- an empty row the user added
// then didn't fill in is silently skipped rather than erroring the whole
// receipt save. Returns the count of lines that failed, so the caller can
// warn (the receipt itself has already saved either way by this point).
async function saveReceiptDeductionLines(lines, projectId, deductionDate) {
  let failed = 0;
  for (const l of (lines || [])) {
    if (!l.bill_id || !l.type) continue;
    const base = Number(l.base_amount || 0);
    const pct = (l.percentage !== '' && l.percentage != null) ? Number(l.percentage) : null;
    const amount = pct != null ? base * pct / 100 : Number(l.amount || 0);
    if (!amount) continue;
    try {
      await api.post('/deductions', {
        project_id: projectId,
        deduction_date: deductionDate || null,
        type: l.type,
        amount,
        percentage: pct,
        base_amount: base || null,
        classification: l.classification || 'not_refundable',
        reference_bill_id: l.bill_id,
        notes: l.notes || null,
      });
    } catch (e) { failed++; }
  }
  return failed;
}

// ============= RECEIPTS PANEL (with Bill linking) =============
function ReceiptsPanel({ projectId, project, bankAccounts, canEdit, canDelete, reload, masters }) {
  const [items, setItems] = useState([]);
  const [bills, setBills] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [flagItem, setFlagItem] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // receiptId -> [{bill_id, amount}], batch-fetched once per project
  // instead of per-row -- see GET /receipts/bill-allocations.
  const [allocMap, setAllocMap] = useState({});
  const load = async () => {
    const [r, b, a] = await Promise.all([
      api.get('/receipts', { params: { project_id: projectId } }),
      api.get('/bills', { params: { project_id: projectId, include_allocation: true } }),
      api.get('/receipts/bill-allocations', { params: { project_id: projectId } }),
    ]);
    setItems(r.data); setBills(b.data); setAllocMap(a.data);
  };
  useEffect(() => { load(); }, [projectId]);
  // Bug fix: `||` treated a genuine 0 outstanding (fully settled) as
  // falsy and fell through to showing the full Net Credit instead.
  const billLabel = (b) => `${b.bill_number || 'Bill'} · ${fmtDate(b.bill_date)} · Net ${fmtINR(b.net_bank_credit)} · Outstanding ${fmtINR(b.outstanding_amount ?? b.net_bank_credit)}`;
  const selectedBillIds = new Set((form.billAllocations||[]).map(a=>a.bill_id));
  const openBills = bills.filter(b => (b.outstanding_amount ?? b.net_bank_credit) > 0.5 || selectedBillIds.has(b.id));
  // Same re-reviewed bill-selection auto-fill as Income()'s identical form
  // -- see its own comment (including the review-pass fix for switching
  // between ticked bills) for why Description, tracked via a ref of the
  // exact text we last auto-set, is the gap being filled here.
  const autoFilledDescRef = useRef(null);
  useEffect(() => {
    const allocs = form.billAllocations || [];
    if (allocs.length !== 1) return;
    const bill = bills.find(b => b.id === allocs[0].bill_id);
    if (!bill?.description) return;
    setForm(f => {
      if (f.description && f.description !== autoFilledDescRef.current) return f;
      autoFilledDescRef.current = bill.description;
      return { ...f, description: bill.description };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.billAllocations?.length === 1 ? form.billAllocations[0].bill_id : null]);
  const bankName = (id) => (bankAccounts||[]).find(b=>b.id===id)?.bank_name;
  const save = async () => {
    if (saving) return;
    if (!form.receipt_date) { toast.error('Date is required.'); return; }
    if (!form.amount) { toast.error('Amount is required.'); return; }
    const allocs = form.billAllocations || [];
    if (allocs.length > 0) {
      const total = Math.round(allocs.reduce((s,a)=>s+Number(a.amount||0),0) * 100) / 100;
      if (!allocs.every(a => Number(a.amount) > 0)) { toast.error('Enter an amount for every selected bill.'); return; }
      if (Math.abs(total - Number(form.amount||0)) > 0.01) { toast.error(`The bill amounts (${fmtINR(total)}) must add up to the receipt's own Amount (${fmtINR(Number(form.amount||0))}).`); return; }
    }
    setSaving(true);
    const isNew = !form.id;
    try {
      const payload = { ...form, project_id: projectId, amount: Number(form.amount||0), bank_account: form.bank_account_id ? bankName(form.bank_account_id) : null };
      delete payload.billAllocations;
      if (allocs.length > 0) {
        payload.bill_allocations = allocs.map(a => ({ bill_id: a.bill_id, amount: Number(a.amount) }));
        payload.bill_id = allocs.length === 1 ? allocs[0].bill_id : null;
      } else {
        payload.bill_allocations = null;
        payload.bill_id = null;
      }
      if (form.id) await api.patch(`/receipts/${form.id}`, payload); else await api.post('/receipts', payload);
      const dedFailed = await saveReceiptDeductionLines(form.deductionLines, projectId, form.receipt_date);
      load(); reload?.();
      // User-requested: after adding a NEW receipt (not editing an
      // existing one), stay open and reset to the same opening seed so
      // several can be entered in a row.
      if (isNew) { setForm({receipt_date:new Date().toISOString().slice(0,10)}); toast.success(dedFailed ? `Saved — ${dedFailed} deduction line(s) failed` : 'Saved — ready for the next one'); }
      else { setOpen(false); setForm({}); toast.success(dedFailed ? `Saved — ${dedFailed} deduction line(s) failed` : 'Saved'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?') || !confirmUnlockedEdit(project)) return;
    try { await api.delete(`/receipts/${id}`); load(); reload?.(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openAdd = () => { if (!confirmUnlockedEdit(project)) return; setForm({receipt_date:new Date().toISOString().slice(0,10)}); setOpen(true); };
  const openEdit = (r) => {
    if (!confirmUnlockedEdit(project)) return;
    const billAllocations = (allocMap[r.id]||[]).map(a => ({ bill_id: a.bill_id, amount: a.amount }));
    setForm({ ...r, billAllocations });
    setOpen(true);
  };
  const [flagsMap, reloadFlags] = useEntityFlagsMap('receipt', items.map(r=>r.id));
  const flaggedCount = items.filter(r=>(flagsMap[r.id]||[]).length > 0).length;
  const visibleItems = flaggedOnly ? items.filter(r=>(flagsMap[r.id]||[]).length > 0) : items;
  return (
    <div className="card card-dark print-area">
      {/* User-requested: print, same as the main Projects page and the
          Expenses/Investments/etc. tabs (CrudPanel) now have -- covers
          Extra Work buckets too, same component, no separate code path. */}
      <div className="print-header">
        <div className="print-company">{project?.project_name || 'Receipts'}</div>
        <div className="print-report-title">Receipts</div>
        <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
      </div>
      {/* Mobile check (user-requested): same flex-wrap fix as CrudPanel's
          toolbar -- title + up to 4 buttons needs to drop to a second
          line on a narrow screen instead of overflowing/squeezing. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4 no-print">
        <div>
          <div className="serif text-xl text-[color:var(--gold)] flex items-center gap-2">Receipts{isProjectLocked(project) && <Lock size={14} className="text-muted"/>}</div>
          <div className="text-xs text-muted">Link each receipt to a bill so outstanding is tracked automatically.</div>
          {/* User-requested: total amount shown under Receipts too, same
              treatment as Expenses/Investments/etc. and the same MetaChip
              style Audit Queue's own totals already use. */}
          <MetaChip icon={Calculator} label="Total of the entries currently shown" value={visibleItems.length ? fmtINR(visibleItems.reduce((a,r)=>a+(Number(r.amount)||0),0)) : null} tone="green"/>
        </div>
        <div className="flex flex-wrap gap-2">
          {flaggedCount > 0 && <button onClick={()=>setFlaggedOnly(v=>!v)} className={"tab btn-small "+(flaggedOnly?'active':'')}>Flagged ({flaggedCount})</button>}
          {canEdit && <button onClick={()=>setBulkOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1"><Upload size={14}/>Bulk Import</button>}
          {visibleItems.length > 0 && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>}
          {canEdit && <button onClick={openAdd} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="rec-add"><Plus size={14}/>Add Receipt</button>}
        </div>
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Date</th><th>Type</th><th>Bill</th><th>Bank Account</th><th>Bank Ref</th><th className="text-right">Amount</th><th className="no-print">Flags</th>{canEdit && <th className="no-print"></th>}</tr></thead>
        <tbody>
          {visibleItems.length===0 && <tr><td colSpan={canEdit?8:7} className="text-center text-muted py-6">{items.length===0 ? 'No receipts yet.' : 'No flagged receipts.'}</td></tr>}
          {visibleItems.map(r => {
            // A receipt split across several bills has bill_id=null (the
            // legacy single-bill column only ever holds a value when
            // there's exactly one) -- allocMap is the real source of
            // truth for both the single- and multi-bill case alike.
            const allocs = allocMap[r.id] || [];
            const allocBills = allocs.map(a => bills.find(b=>b.id===a.bill_id)).filter(Boolean);
            // User-requested: hover anywhere on the row to see the linked
            // bill's description (or every linked bill's, for a multi-bill
            // receipt) -- same "hover the row, not just one cell" pattern
            // as the Bills page's own bill-description tooltip.
            const linkedBillTitle = allocBills.length === 0 ? undefined :
              allocBills.map(b => `${b.bill_number || fmtDate(b.bill_date)}${b.description ? ': ' + b.description : ''}`).join(' | ');
            return (
              <tr key={r.id} title={linkedBillTitle}>
                <td className="text-xs">{fmtDate(r.receipt_date)}</td>
                <td className="text-xs">{RCP_TYPE_LABELS[r.receipt_type] || r.receipt_type}</td>
                <td>{allocBills.length === 0 ? <span className="text-muted text-xs">Unlinked</span> : allocBills.length === 1
                  ? <span className="chip">{allocBills[0].bill_number || fmtDate(allocBills[0].bill_date)}</span>
                  : <span className="chip">{allocBills.length} bills</span>}</td>
                <td className="text-xs">{bankName(r.bank_account_id) || r.bank_account || '—'}</td>
                <td>{r.bank_reference||'—'}</td>
                <td className="mono text-right text-[color:var(--gold)]">{fmtINR(r.amount)}</td>
                <td className="no-print">
                  <button aria-label="Flags" title="Mark attention with a flag" onClick={()=>setFlagItem(r)} className="tab btn-small mr-1"><Flag size={12}/></button>
                  <FlagDots flags={flagsMap[r.id]}/>
                </td>
                {canEdit && <td className="text-right whitespace-nowrap no-print">
                  <button aria-label="Edit" onClick={()=>openEdit(r)} className="tab btn-small mr-1"><Edit3 size={12}/></button>
                  {canDelete && <button aria-label="Delete" onClick={()=>del(r.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                </td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Receipt':'Add Receipt'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Date *"><input type="date" className="input" value={form.receipt_date||''} onChange={e=>setForm({...form, receipt_date:e.target.value})}/></Field>
          <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})} data-testid="rec-amount"/></Field>
          <Field label="Type">
            <select className="input" value={form.receipt_type||'customer_payment'} onChange={e=>setForm({...form, receipt_type:e.target.value})}>
              {RCP_TYPES.map(x=><option key={x} value={x}>{RCP_TYPE_LABELS[x]||x}</option>)}
            </select>
          </Field>
          <BillAllocationPicker bills={openBills} amount={form.amount} allocations={form.billAllocations||[]}
            setAllocations={(a)=>setForm({...form, billAllocations:a})} billLabel={billLabel}/>
          <ReceiptDeductionsPicker bills={openBills} allocations={form.billAllocations||[]} lines={form.deductionLines||[]}
            setLines={(l)=>setForm({...form, deductionLines:l})} masters={masters} companyId={project?.company_id} isEdit={!!form.id}/>
          <Field label="Bank Reference"><input className="input" value={form.bank_reference||''} onChange={e=>setForm({...form, bank_reference:e.target.value})}/></Field>
          <Field label="Bank Account (money credited to)"><select className="input" value={form.bank_account_id||''} onChange={e=>setForm({...form, bank_account_id:e.target.value})}>
            <option value="">— select —</option>
            {(bankAccounts||[]).map(b=><option key={b.id} value={b.id}>{b.bank_name}</option>)}
          </select></Field>
          <div className="col-span-2"><Field label="Description"><input className="input" value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="rec-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      {bulkOpen && (
        <Modal open={true} onClose={()=>setBulkOpen(false)} title="Bulk Import — Receipts" maxW={860}>
          <ExcelImportWizard lockedProject={project} lockedEntity="receipts"/>
        </Modal>
      )}
      <Modal open={!!flagItem} onClose={()=>{setFlagItem(null); reloadFlags();}} title="Flags — Receipt">
        {flagItem && <EntityFlagsBar entityType="receipt" entityId={flagItem.id} canEdit={canEdit}/>}
      </Modal>
    </div>
  );
}


// Shared by BillsPanel (per-project) and BillsHub (cross-project) — same
// jsPDF/autoTable pattern as every other export in the app (see
// exportLedgerPDF/SettlementPanel.exportPDF), so a printed/downloaded
// bill list looks consistent with every other financial document here.
// `withProject` adds a Project column for the cross-project view.
async function exportBillsPDF(bills, { title, subtitle, withProject = false } = {}) {
  const { jsPDF, autoTable } = await loadPdf();
  const doc = new jsPDF({ orientation: withProject ? 'landscape' : 'portrait' });
  doc.setFontSize(16); doc.text(title || 'Bills', 14, 18);
  doc.setFontSize(9);
  doc.text(`Generated: ${fmtGeneratedAt()}`, 14, 26);
  if (subtitle) doc.text(subtitle, 14, 32);
  const head = [[...(withProject ? ['Project'] : []), 'Date', 'Bill #', 'Gross', 'GST', 'TDS', 'Sec. Deposit', 'Net Credit', 'Received', 'Outstanding', 'Status']];
  const body = bills.map(b => [
    ...(withProject ? [b._projectName || '—'] : []),
    fmtDate(b.bill_date), b.bill_number || '—', fmtINRPdf(b.gross_amount), fmtINRPdf(b.gst_amount),
    fmtINRPdf(b.tds_amount), fmtINRPdf(b.security_deposit_amount), fmtINRPdf(b.net_bank_credit),
    fmtINRPdf(b.allocated_amount ?? 0), fmtINRPdf(b.outstanding_amount ?? b.net_bank_credit), (b.status || '').replace(/_/g, ' '),
  ]);
  autoTable(doc, {
    startY: subtitle ? 38 : 32,
    head, body,
    styles: { fontSize: 8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
  });
  const finalY = doc.lastAutoTable?.finalY || 100;
  const totalGross = bills.reduce((a, b) => a + Number(b.gross_amount || 0), 0);
  const totalOutstanding = bills.reduce((a, b) => a + Number(b.outstanding_amount ?? b.net_bank_credit ?? 0), 0);
  doc.setFontSize(10);
  doc.text(`Total Gross: ${fmtINRPdf(totalGross)}`, 14, finalY + 10);
  doc.text(`Total Outstanding: ${fmtINRPdf(totalOutstanding)}`, 14, finalY + 16);
  doc.save(`bills-${(title || 'export').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now()}.pdf`);
}

// Projects list as a PDF (landscape): the shown projects with Contract and any of Spent / Received / Receivable the user
// ticked, a Totals row, and a line saying which filters were active. rows = [{ p, money: { spent, received, receivable } }]
// (a money value of null = figure not loaded -> "—", and its total shows "—" too).
async function exportProjectsPDF({ rows, totals, printCols, needsPicker, companyName, contractTotal, summary, shown, all }) {
  const { jsPDF, autoTable } = await loadPdf();
  const doc = new jsPDF({ orientation: 'landscape' });
  doc.setFontSize(16); doc.text('Projects', 10, 16);
  doc.setFontSize(9);
  doc.text(`${shown} of ${all} projects   ·   Generated: ${fmtGeneratedAt()}`, 10, 22);
  if (summary) doc.text(`Filters: ${summary}`, 10, 27, { maxWidth: 275 });
  const money = (v) => (v === null || v === undefined ? '—' : fmtINRPdf(v));
  const rightCell = (content) => ({ content, styles: { halign: 'right' } });  // number columns: heading and totals line up with the figures
  const moneyHead = printCols.map(([, label]) => rightCell(label));
  const head = [['Project', 'Client', ...(needsPicker ? ['Company'] : []), rightCell('Contract'), ...moneyHead, 'Category', 'Model', 'Status', 'FY']];
  const body = rows.map(({ p, money: m }) => [
    `${p.project_name}${p.tender_number ? `\n${p.tender_number}` : ''}`,
    p.client_name || '—',
    ...(needsPicker ? [companyName(p.company_id)] : []),
    fmtINRPdf(p.contract_value),
    ...printCols.map(([k]) => money(m[k])),
    p.work_category || '—',
    `${p.partnership_model || ''}${p.model_b_type ? ` (${p.model_b_type})` : ''}` || '—',
    (p.status || '').replace(/_/g, ' '),
    p.financial_year || '—',
  ]);
  const lead = needsPicker ? 3 : 2;
  const foot = [[{ content: 'Totals', colSpan: lead, styles: { halign: 'right' } }, rightCell(fmtINRPdf(contractTotal)), ...printCols.map(([k]) => rightCell(money(totals[k]))), { content: '', colSpan: 4 }]];
  const numStart = lead, numCount = 1 + printCols.length;
  const columnStyles = {};
  for (let i = numStart; i < numStart + numCount; i++) columnStyles[i] = { halign: 'right', cellWidth: 27 };  // wide enough that an amount never wraps
  columnStyles[numStart + numCount + 2] = { cellWidth: 24 };  // Status
  columnStyles[numStart + numCount + 3] = { cellWidth: 19 };  // FY
  autoTable(doc, {
    startY: summary ? 32 : 28, head, body, foot, showFoot: 'lastPage', margin: { left: 10, right: 10 },
    styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
    footStyles: { fillColor: [245, 240, 225], textColor: [20, 20, 20], fontStyle: 'bold' },
    columnStyles,
  });
  doc.save(`projects-${todayLocalISO()}.pdf`);
}

// Extra Works as a PDF, one page (or more) per client: each client's buckets with optional Days open and Spent columns and a
// Spent subtotal. groups = [{ client, rows: [{ project, totals }] }]; days = (project) => whole days open or null.
async function exportExtraWorksPDF({ groups, opts, needsPicker, companyName, days, summary, todayLabel }) {
  const { jsPDF, autoTable } = await loadPdf();
  const doc = new jsPDF({ orientation: 'portrait' });
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  groups.forEach((g, gi) => {
    if (gi > 0) doc.addPage();
    doc.setFontSize(15); doc.text(`Extra Works — ${g.client}`, 10, 15);
    doc.setFontSize(9);
    doc.text(`${g.rows.length} bucket${g.rows.length === 1 ? '' : 's'}   ·   Client ${gi + 1} of ${groups.length}   ·   ${total} buckets in all   ·   Generated: ${fmtGeneratedAt()}`, 10, 21);
    if (summary) doc.text(`Filters: ${summary}`, 10, 26, { maxWidth: 190 });
    const head = [['Work / Place', ...(needsPicker ? ['Company'] : []), 'Started', ...(opts.days ? [{ content: 'Days open', styles: { halign: 'right' } }] : []), ...(opts.spent ? [{ content: 'Spent', styles: { halign: 'right' } }] : []), 'Status']];
    const body = g.rows.map(({ project: p, totals: t }) => [
      `${p.project_name}${p.notes ? `\n${p.notes}` : ''}`,
      ...(needsPicker ? [companyName(p.company_id)] : []),
      p.start_date ? fmtDate(p.start_date) : '—',
      ...(opts.days ? [days(p) === null ? '—' : String(days(p))] : []),
      ...(opts.spent ? [fmtINRPdf(t.total_expenses)] : []),
      (p.status || '').replace(/_/g, ' '),
    ]);
    const lead = 1 + (needsPicker ? 1 : 0) + 1;
    const subtotal = g.rows.reduce((sum, r) => sum + Number(r.totals.total_expenses || 0), 0);
    const foot = opts.spent ? [[{ content: 'Spent — this client', colSpan: lead + (opts.days ? 1 : 0), styles: { halign: 'right' } }, { content: fmtINRPdf(subtotal), styles: { halign: 'right' } }, '']] : null;
    const columnStyles = {}; let ci = lead;
    if (opts.days) columnStyles[ci++] = { halign: 'right', cellWidth: 22 };
    if (opts.spent) columnStyles[ci++] = { halign: 'right', cellWidth: 34 };
    autoTable(doc, {
      startY: summary ? 31 : 27, head, body, ...(foot ? { foot, showFoot: 'lastPage' } : {}), margin: { left: 10, right: 10 },
      styles: { fontSize: 8.5, cellPadding: 2 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
      footStyles: { fillColor: [245, 240, 225], textColor: [20, 20, 20], fontStyle: 'bold' }, columnStyles,
    });
  });
  doc.save(`extra-works-by-client-${todayLabel}.pdf`);
}

// ============= BILLS PANEL =============
function BillsPanel({ projectId, canEdit, canDelete, project, reload }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [detail, setDetail] = useState(null);
  const [detailReceipts, setDetailReceipts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [flagItem, setFlagItem] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // User-requested: master-admin-only bulk "Move to another project" for
  // Bills, mirroring Expenses' own single-row Move (CrudPanel's canMove)
  // but with multi-select since a whole batch of bills often needs
  // reassigning at once. Same backend gate (update_bill now 403s a
  // project change for anyone but master_admin) -- this control is
  // simply invisible to anyone it would just 403 for.
  const { user: billsUser } = useAuth();
  const showMove = isMasterAdmin(billsUser);
  const [selected, setSelected] = useState(() => new Set());
  const toggleSelect = (id) => setSelected(s => { const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveProjects, setMoveProjects] = useState([]);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [moving, setMoving] = useState(false);
  // User-requested: an opt-in checkbox to also move this project's
  // Expenses in the same action -- previously the only way to do both was
  // two separate trips (move the bill(s) here, then go move every expense
  // one at a time from the Expenses tab's own identical multi-select-move
  // feature). Off by default since most bill moves aren't also an expense
  // move; always resets on reopen so it never silently carries over from
  // a previous move.
  const [alsoMoveExpenses, setAlsoMoveExpenses] = useState(false);
  const openMoveSelected = () => {
    if (!selected.size) return;
    setMoveTargetId('');
    setAlsoMoveExpenses(false);
    api.get('/projects').then(r => setMoveProjects(r.data.filter(p => p.company_id === project?.company_id && p.id !== projectId && !isProjectLocked(p))));
    setMoveOpen(true);
  };
  const doMoveSelected = async () => {
    if (moving || !moveTargetId) return;
    const target = moveProjects.find(p => p.id === moveTargetId);
    if (!window.confirm(`Move ${selected.size} selected bill(s)${alsoMoveExpenses ? ' AND every Expense currently on this project' : ''} to "${target?.project_name || 'the selected project'}"? This changes Billed/Outstanding on both projects. A bill with receipts already allocated to it will be skipped.`)) return;
    setMoving(true);
    const failed = [];
    for (const bid of selected) {
      const b = items.find(x => x.id === bid);
      if (!b) continue;
      try {
        await api.patch(`/bills/${bid}`, { ...b, project_id: moveTargetId });
      } catch (e) {
        failed.push({ id: bid, detail: e.response?.data?.detail || 'Error' });
      }
    }
    const succeededCount = selected.size - failed.length;
    if (failed.length === 0) toast.success(`Moved ${succeededCount} bill(s)`);
    else if (succeededCount === 0) toast.error(`None moved — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    else toast.warning(`Moved ${succeededCount} bill(s), ${failed.length} skipped — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    setSelected(new Set(failed.map(f => f.id)));
    // Same full-object PATCH, same per-item try/catch summary convention
    // the bill loop above (and the Expenses tab's own move button) already
    // use — moves every expense currently on the SOURCE project, not just
    // ones related to the bill(s) just moved (expenses carry no bill
    // reference to narrow by).
    if (alsoMoveExpenses) {
      try {
        const exp = (await api.get('/expenses', { params: { project_id: projectId } })).data;
        const expFailed = [];
        for (const e of exp) {
          try { await api.patch(`/expenses/${e.id}`, { ...e, project_id: moveTargetId }); }
          catch (err) { expFailed.push({ id: e.id, detail: err.response?.data?.detail || 'Error' }); }
        }
        const expOk = exp.length - expFailed.length;
        if (exp.length === 0) { /* nothing to move, no toast needed */ }
        else if (expFailed.length === 0) toast.success(`Moved ${expOk} expense(s)`);
        else if (expOk === 0) toast.error(`No expenses moved — ${expFailed[0].detail}${expFailed.length > 1 ? ` (+${expFailed.length - 1} more)` : ''}`);
        else toast.warning(`Moved ${expOk} expense(s), ${expFailed.length} failed — ${expFailed[0].detail}${expFailed.length > 1 ? ` (+${expFailed.length - 1} more)` : ''}`);
      } catch (e) { toast.error(e.response?.data?.detail || 'Could not load this project\'s expenses to move'); }
    }
    setMoveOpen(false);
    setMoving(false);
    load(); reload?.();
  };
  const load = async () => setItems((await api.get('/bills', { params: { project_id: projectId, include_allocation: true } })).data);
  useEffect(() => { load(); }, [projectId]);
  // Same company-name lookup as FinalCalculationPanel's print header —
  // the project row itself only carries company_id, not a display name.
  useEffect(() => { if (project?.company_id) api.get('/companies').then(r => { const co = r.data.find(c=>c.id===project.company_id); if (co) setCompanyName(co.legal_name || co.name); }); }, [project?.company_id]);
  const pctOrFlat = (pctField, flatField) => {
    const g = Number(form.gross_amount||0);
    return (form[pctField] !== undefined && form[pctField] !== null && form[pctField] !== '')
      ? g * Number(form[pctField]) / 100
      : Number(form[flatField]||0);
  };
  const gstPreview = useMemo(() => pctOrFlat('gst_percentage','gst_amount'), [form]);
  const labourCessPreview = useMemo(() => pctOrFlat('labour_cess_percentage','labour_cess_amount'), [form]);
  const tdsPreview = useMemo(() => pctOrFlat('tds_percentage','tds_amount'), [form]);
  const securityDepositPreview = useMemo(() => pctOrFlat('security_deposit_percentage','security_deposit_amount'), [form]);
  const netPreview = useMemo(() => {
    const g = Number(form.gross_amount||0), rt = Number(form.retention_amount||0), o = Number(form.other_deduction_amount||0);
    return g + gstPreview - tdsPreview - labourCessPreview - securityDepositPreview - rt - o;
  }, [form, gstPreview, labourCessPreview, tdsPreview, securityDepositPreview]);
  // TDS/labour cess/security deposit/retention are no longer entered on
  // a NEW bill (moved to the Receipt form, since what a client actually
  // withholds is often only known once the payment lands) -- but an
  // existing bill that already has one of these set still needs a way
  // to view/fix it, so the user can fold it into Other Charges at their
  // own pace without the value going invisible.
  const legacyWithholding = !!(form.id && (Number(form.tds_amount)||Number(form.labour_cess_amount)||Number(form.security_deposit_amount)||Number(form.retention_amount)));
  const save = async () => {
    if (saving) return;
    if (!form.bill_date) { toast.error('Bill Date is required.'); return; }
    if (!form.gross_amount) { toast.error('Gross Amount is required.'); return; }
    setSaving(true);
    const isNew = !form.id;
    try {
      const payload = { ...form, project_id: projectId };
      ['gross_amount','gst_amount','tds_amount','labour_cess_amount','security_deposit_amount','retention_amount','other_deduction_amount'].forEach(k => payload[k] = Number(payload[k]||0));
      for (const pf of ['gst_percentage','labour_cess_percentage','tds_percentage','security_deposit_percentage']) {
        payload[pf] = (form[pf] !== undefined && form[pf] !== null && form[pf] !== '') ? Number(form[pf]) : null;
      }
      if (form.id) await api.patch(`/bills/${form.id}`, payload); else await api.post('/bills', payload);
      load(); if (autoContract) reload?.();
      // User-requested: after adding a NEW bill (not editing an existing
      // one), stay open and reset to the same opening seed so several can
      // be entered in a row.
      if (isNew) { setForm({bill_date:new Date().toISOString().slice(0,10),status:'billed'}); toast.success('Saved — ready for the next one'); }
      else { setOpen(false); setForm({}); toast.success('Saved'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete bill?') || !confirmUnlockedEdit(project)) return;
    try { await api.delete(`/bills/${id}`); load(); if (autoContract) reload?.(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openDetail = async (bill) => {
    setDetail(bill);
    const r = await api.get(`/bills/${bill.id}/receipts`);
    setDetailReceipts(r.data);
  };
  const openAdd = () => { if (!confirmUnlockedEdit(project)) return; setForm({bill_date:new Date().toISOString().slice(0,10),status:'billed'}); setOpen(true); };
  const openEdit = (b) => { if (!confirmUnlockedEdit(project)) return; setForm(b); setOpen(true); };
  const totalBilled = items.reduce((a, b) => a + Number(b.gross_amount || 0), 0);
  // User-reported: an earlier version of this sync silently ASSUMED 18%
  // GST on any bill missing its own GST info, and always added GST into
  // Contract Value with no way to opt out. User-confirmed fix: GST
  // inclusion is now fully opt-in (default OFF below) -- with it off,
  // Contract Value is simply the bill totals, no GST reasoning at all,
  // exactly "only total bill amount should be added." With it on, a bill
  // that genuinely has GST info (gst_percentage set, even to 0, or a
  // manually typed gst_amount) always uses its own real recorded value;
  // only a bill with NEITHER falls back to the assumed rate below --
  // which is now a visible, editable field (default 18, a suggestion,
  // not a silent assumption), not a hardcoded constant.
  const _billHasGstInfo = (b) => (b.gst_percentage !== null && b.gst_percentage !== undefined) || Number(b.gst_amount || 0) > 0;
  const billsWithGstCount = items.filter(_billHasGstInfo).length;
  const totalBilledGstRecorded = items.reduce((a, b) => a + (_billHasGstInfo(b) ? Number(b.gst_amount || 0) : 0), 0);
  const billsMissingGstCount = items.length - billsWithGstCount;
  const [includeGstInContractValue, setIncludeGstInContractValue] = useState(false);
  const [assumedGstPct, setAssumedGstPct] = useState(18);
  const totalBilledGstForSync = items.reduce((a, b) => a + (_billHasGstInfo(b) ? Number(b.gst_amount || 0) : Number(b.gross_amount || 0) * (Number(assumedGstPct||0) / 100)), 0);
  const projectBilledAmount = Number(project?.billed_amount || 0);
  const billedMismatch = items.length > 0 && projectBilledAmount > 0 && Math.abs(totalBilled - projectBilledAmount) > 0.5;
  // User-requested: some projects (e.g. voucher/work-order jobs) don't
  // have a known Contract Value until the final bill is raised -- typing
  // it by hand is error-prone and easy to leave stale as more bills come
  // in, even though it drives Project Profit directly (net_contract_value
  // = contract_value - gst_amount). One-click, optional sync instead:
  // sets the project's real contract_value/gst_amount fields via the
  // normal project update endpoint (so the existing lock/audit protection
  // on those fields applies exactly as it would to a manual edit) to this
  // tab's own bill totals. Never automatic -- only changes the project
  // when explicitly clicked and confirmed.
  //
  // Bug fix (user-reported, "opting wrong data to contract value"):
  // Contract Value is meant to be the GST-INCLUSIVE tender value (the
  // Project form's own "Contract Value - GST Amount = Net" convention),
  // so whenever GST IS included, it's added on top of the bill gross
  // total, not double-subtracted; with GST off (the default now), Net
  // Contract is simply the bill gross total, matching plain "Net Contract"
  // everywhere else in the app.
  const totalBilledInclGst = totalBilled + (includeGstInContractValue ? totalBilledGstForSync : 0);
  // User-requested: an alternative to the default model (GST tracked as
  // its own field, subtracted upfront at Net Contract) -- some users
  // prefer to treat GST as a real Company Expense instead (paid to the
  // government as an actual expense entry, whenever that happens),
  // leaving the project's own GST Amount at 0 so Net Contract = the FULL
  // contract value and GST comes back out later via total_expenses
  // instead. Only meaningful once GST is actually being included above.
  const [gstAsExpense, setGstAsExpense] = useState(false);
  const gstAmountToSync = (includeGstInContractValue && !gstAsExpense) ? totalBilledGstForSync : 0;
  const contractNetFromBills = totalBilledInclGst - gstAmountToSync;
  const projectNetContract = Number(project?.net_contract_value ?? ((Number(project?.contract_value)||0) - (Number(project?.gst_amount)||0)));
  const contractValueMismatch = items.length > 0 && Math.abs(contractNetFromBills - projectNetContract) > 0.5;
  // Monthly-billed project: the server keeps Contract Value equal to the total of these bills by itself (see
  // _sync_contract_from_bills), so the manual sync below is hidden and the page just shows what is in force.
  const autoContract = project?.billing_frequency === 'monthly' && !project?.is_extra_work && !isProjectLocked(project);
  const syncContractValue = async () => {
    if (!confirmUnlockedEdit(project)) return;
    const gstNote = !includeGstInContractValue ? ' — GST not included (bill totals only)' : (gstAsExpense ? ' — GST Amount set to ₹0 since you\'re tracking GST as a separate Company Expense instead' : (billsMissingGstCount > 0 ? ` — ${billsMissingGstCount} bill(s) had no GST entered, so ${assumedGstPct}% was assumed on those for this figure` : ''));
    if (!window.confirm(`Set this project's Contract Value to ${fmtINR(totalBilledInclGst)} (GST ${fmtINR(gstAmountToSync)}, Net ${fmtINR(contractNetFromBills)}) from the ${items.length} bill(s) raised so far?${gstNote} This replaces whatever Contract Value/GST is there now.`)) return;
    try {
      // Full-object PATCH -- ProjectIn.project_name (and other fields) are
      // mandatory on this endpoint, so a payload of only the two changed
      // fields would 422. Spreading the current project supplies the rest
      // unchanged, same convention every other save() in this app uses.
      await api.patch(`/projects/${projectId}`, { ...project, contract_value: totalBilledInclGst, gst_amount: gstAmountToSync });
      toast.success('Contract Value updated from Bills');
      reload?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const [flagsMap, reloadFlags] = useEntityFlagsMap('bill', items.map(b=>b.id));
  const flaggedCount = items.filter(b=>(flagsMap[b.id]||[]).length > 0).length;
  const visibleItems = flaggedOnly ? items.filter(b=>(flagsMap[b.id]||[]).length > 0) : items;
  // User-requested: every other amount-bearing list in the app (Expenses,
  // Investments, Receipts, ...) shows a total footer -- this table never
  // had one beyond the single Gross-only mismatch-check line above.
  // Matches those tables' own convention of totaling whatever's currently
  // shown (respects the Flagged filter), not every bill regardless.
  const visibleTotalGross = visibleItems.reduce((a, b) => a + Number(b.gross_amount || 0), 0);
  const visibleTotalNet = visibleItems.reduce((a, b) => a + Number(b.net_bank_credit || 0), 0);
  const visibleTotalReceived = visibleItems.reduce((a, b) => a + Number(b.allocated_amount || 0), 0);
  const visibleTotalOutstanding = visibleItems.reduce((a, b) => a + Number(b.outstanding_amount ?? b.net_bank_credit ?? 0), 0);
  return (
    <div className="card card-dark print-area">
      <div className="print-header">
        <div className="print-company">{companyName || '—'}</div>
        <div className="print-report-title">Bills — {project?.project_name || 'Project'}</div>
        <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
      </div>
      {/* Mobile audit fix: 4 buttons (Flagged/Export PDF/Print/Add Bill)
          in a non-wrapping row -- same overflow class fixed elsewhere. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)] flex items-center gap-2">Bills — Billed → Deductions → Net → Received{isProjectLocked(project) && <Lock size={14} className="text-muted"/>}</div>
          <div className="text-xs text-muted">Link each receipt to a bill to auto-track outstanding and status.</div>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          {flaggedCount > 0 && <button onClick={()=>setFlaggedOnly(v=>!v)} className={"tab btn-small "+(flaggedOnly?'active':'')}>Flagged ({flaggedCount})</button>}
          {showMove && selected.size > 0 && <button onClick={openMoveSelected} className="btn-outline btn-small inline-flex items-center gap-1"><ArrowRightLeft size={14}/>Move {selected.size} Selected</button>}
          {items.length > 0 && <button onClick={()=>exportBillsPDF(items, {title:`Bills — ${project?.project_name||'Project'}`})} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Export PDF</button>}
          {items.length > 0 && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>}
          {canEdit && <button onClick={openAdd} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Bill</button>}
        </div>
      </div>
      {items.length > 0 && (
        <div className={"text-xs mb-4 " + (billedMismatch ? 'text-amber-400' : 'text-muted')}>
          Bills raised so far: <span className="mono">{fmtINR(totalBilled)}</span> · Project "Billed Amount" (used in Final Calculation): <span className="mono">{fmtINR(projectBilledAmount)}</span>
          {billedMismatch && <> — these don't match. Update the project's Billed Amount so Final Calculation's Outstanding Receivable stays correct.</>}
        </div>
      )}
      {/* User-requested: visibility of what GST is actually already on
          record across this project's bills -- shown regardless of
          whether the sync section below is even touched. */}
      {items.length > 0 && (
        <div className="text-xs text-muted mb-2">
          GST on record: <span className="mono">{billsWithGstCount} of {items.length}</span> bill{items.length===1?'':'s'} have GST entered, totaling <span className="mono">{fmtINR(totalBilledGstRecorded)}</span>{billsMissingGstCount > 0 && <> ({billsMissingGstCount} bill{billsMissingGstCount>1?'s':''} with no GST entered)</>}.
        </div>
      )}
      {/* User-requested: some projects don't have a known Contract Value
          until the final bill is raised -- optional, one-click sync of
          Contract Value/GST from this tab's own bill totals, rather than
          typing (and re-typing, as more bills come in) a figure by hand.
          User-reported real gap: this used to always add GST into
          Contract Value (assuming 18% when a bill had none entered) with
          no way to opt out. GST inclusion is now its own explicit toggle,
          off by default -- "only total bill amount should be added" is
          the plain, no-GST-reasoning-at-all default; ticking it on
          reveals the Company-Expense checkbox and a visible, editable
          assumed-% field (not a hardcoded 18%) for whatever's still on
          the sync figure. */}
      {autoContract && items.length > 0 && (
        <div className="text-xs mb-4" data-testid="bills-auto-contract">
          <span className="text-muted">Monthly billing — Contract Value is automatic: </span><span className="mono text-[color:var(--gold)]">{fmtINR(totalBilled)}</span>
          <span className="text-muted"> = total of these {items.length} bill{items.length === 1 ? '' : 's'}; it updates by itself whenever a bill is added, changed or deleted. Contract (Net) in use for Project Profit: </span><span className={"mono " + (Math.abs(totalBilled - projectNetContract) > 0.5 ? 'text-amber-400' : 'text-emerald-400')}>{fmtINR(projectNetContract)}</span>
        </div>
      )}
      {canEdit && items.length > 0 && !autoContract && (
        <div className="flex flex-col gap-2 mb-4 text-xs">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-muted">
              {includeGstInContractValue ? 'Contract (Net) from bills so far' : 'Contract from bills so far (bill totals only, no GST)'}: <span className="mono text-[color:var(--gold)]">{fmtINR(contractNetFromBills)}</span>
              {' '}· Project Contract (Net) (used in Project Profit): <span className={"mono " + (contractValueMismatch ? 'text-red-400' : 'text-emerald-400')}>{fmtINR(projectNetContract)}</span>
            </span>
            <button onClick={syncContractValue} className="btn-gold btn-small">Use Bills Total as Contract Value</button>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeGstInContractValue} onChange={e=>setIncludeGstInContractValue(e.target.checked)}/>Also include GST in this figure (optional — leave off to sync just the bill amounts)
          </label>
          {includeGstInContractValue && <div className="flex flex-wrap items-center gap-3 pl-5">
            <label className="flex items-center gap-1.5 cursor-pointer px-2.5 py-1 rounded-full" style={{background:'rgba(217,164,65,0.12)', color:'var(--accent-amber)'}} title="Leave GST Amount at ₹0 instead of tracking it here — use this if you'd rather record GST as a real Company Expense whenever it's actually paid, and let Project Profit come out of the full Contract Value instead.">
              <input type="checkbox" checked={gstAsExpense} onChange={e=>setGstAsExpense(e.target.checked)}/>Treat GST as a Company Expense (don't reduce Contract Value)
            </label>
            {billsMissingGstCount > 0 && !gstAsExpense && (
              <span className="flex items-center gap-1.5 text-muted" title="Applied only to bills with no GST % or GST Amount of their own — a bill that already has GST recorded always uses its own real value instead.">
                Assumed GST % for the {billsMissingGstCount} bill{billsMissingGstCount>1?'s':''} with none entered:
                <input className="input mono" type="number" min="0" max="100" step="0.01" style={{width:70}} value={assumedGstPct} onChange={e=>setAssumedGstPct(e.target.value)}/>%
              </span>
            )}
          </div>}
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr>{showMove && <th className="no-print"></th>}<th>Date</th><th>Bill #</th><th>Gross</th><th>GST</th><th>Net Credit</th><th>Received</th><th>Outstanding</th><th>Status</th><th>Flags</th><th></th></tr></thead>
        <tbody>
          {visibleItems.length===0 && <tr><td colSpan={showMove?11:10} className="text-center text-muted py-6">{items.length===0 ? 'No bills yet.' : 'No flagged bills.'}</td></tr>}
          {visibleItems.map(b => {
            const outstanding = b.outstanding_amount ?? Number(b.net_bank_credit||0);
            const allocated = b.allocated_amount ?? 0;
            return (
              <tr key={b.id} className="cursor-pointer hover:bg-white/5" title={b.description || undefined} onClick={()=>openDetail(b)}>
                {/* User-requested: warn upfront rather than only after a
                    failed move attempt -- a bill with a receipt already
                    allocated to it can't be moved (see update_bill's own
                    block, receipt_bill_allocations), so its checkbox is
                    disabled here with a tooltip explaining why, instead
                    of silently letting it be selected and skipped later. */}
                {showMove && <td className="no-print" onClick={e=>e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(b.id)} disabled={allocated > 0} title={allocated > 0 ? 'Has a receipt allocated — unlink or delete that receipt first, then move this bill' : undefined} onChange={()=>toggleSelect(b.id)}/>
                </td>}
                <td className="text-xs">{fmtDate(b.bill_date)}</td>
                <td className="text-xs">{b.bill_number||'—'}</td>
                <td className="mono">{fmtINR(b.gross_amount)}</td>
                <td className="text-xs">{_billHasGstInfo(b) ? <>{b.gst_percentage!=null && `${b.gst_percentage}%`}{b.gst_percentage!=null && Number(b.gst_amount||0)>0 && ' / '}{Number(b.gst_amount||0)>0 && fmtINR(b.gst_amount)}</> : <span className="text-muted">—</span>}</td>
                <td className="mono text-[color:var(--gold)]">{fmtINR(b.net_bank_credit)}</td>
                <td className="mono">{fmtINR(allocated)}</td>
                <td className={"mono " + (outstanding <= 0.5 ? 'text-emerald-400' : 'text-red-400')}>{fmtINR(outstanding)}</td>
                <td>
                  <span className={"badge " + (b.suggested_status==='received'?'status-running':(b.suggested_status==='partially_received'?'status-completed':'status-planning'))}>{BILL_STATUS_LABELS[b.suggested_status || b.status] || b.suggested_status || b.status}</span>
                  {/* User-requested: a receipt entered against this bill
                      but not yet reviewed in the Audit Queue gave zero
                      visual signal here -- same AuditStatusChip already
                      used everywhere else for exactly this status, so a
                      pending amount reads consistently app-wide. */}
                  {b.pending_audit_amount > 0 && <span className="ml-1" title={`${fmtINR(b.pending_audit_amount)} received but not yet reviewed in the Audit Queue`}><AuditStatusChip status="pending_review"/></span>}
                </td>
                <td onClick={e=>e.stopPropagation()}>
                  <button aria-label="Flags" title="Mark attention with a flag" onClick={()=>setFlagItem(b)} className="tab btn-small mr-1"><Flag size={12}/></button>
                  <FlagDots flags={flagsMap[b.id]}/>
                </td>
                <td className="text-right whitespace-nowrap" onClick={e=>e.stopPropagation()}>
                  {/* Round 21 finding: the row's own onClick (allocated
                      receipts, a real feature distinct from Edit) was
                      only reachable by clicking the row itself -- a
                      keyboard-only user had no way to reach it at all,
                      unlike Edit/Delete which already had real buttons.
                      Kept the row-click for mouse convenience and added
                      an explicit button alongside it, rather than making
                      the whole <tr> a role="button" (which would collapse
                      its per-cell data into one opaque control for a
                      screen reader instead of a normal table row). */}
                  <button aria-label="View allocated receipts" onClick={()=>openDetail(b)} className="tab btn-small mr-1"><Eye size={12}/></button>
                  {canEdit && <button aria-label="Edit" onClick={()=>openEdit(b)} className="tab btn-small mr-1"><Edit3 size={12}/></button>}
                  {canDelete && <button aria-label="Delete" onClick={()=>del(b.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                </td>
              </tr>
            );
          })}
        </tbody>
        {visibleItems.length > 0 && <tfoot><tr className="font-semibold">
          {/* The header has a leading (select) column for master admin only (showMove); the label must span it too or every total lands one column left. */}
          <td colSpan={showMove ? 3 : 2} className="text-right text-muted uppercase text-xs">Total</td>
          <td className="mono">{fmtINR(visibleTotalGross)}</td>
          <td className="mono">{fmtINR(visibleItems.reduce((a,b)=>a+(_billHasGstInfo(b)?Number(b.gst_amount||0):0),0))}</td>
          <td className="mono text-[color:var(--gold)]">{fmtINR(visibleTotalNet)}</td>
          <td className="mono">{fmtINR(visibleTotalReceived)}</td>
          <td className="mono">{fmtINR(visibleTotalOutstanding)}</td>
          <td colSpan={3}></td>
        </tr></tfoot>}
      </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Bill':'Add Bill'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Bill Date *"><input type="date" className="input" value={form.bill_date||''} onChange={e=>setForm({...form, bill_date:e.target.value})}/></Field>
          <Field label="Bill Number"><input className="input" value={form.bill_number||''} onChange={e=>setForm({...form, bill_number:e.target.value})}/></Field>
          <Field label="Gross Amount *"><input className="input mono" type="number" min="0" value={form.gross_amount||''} onChange={e=>setForm({...form, gross_amount:e.target.value})}/></Field>
          <Field label="GST % (slab or custom, optional)">
            <input className="input mono" type="number" step="0.01" min="0" max="100" list="bill-gst-slabs" placeholder="e.g. 18"
              value={form.gst_percentage ?? ''} onChange={e=>setForm({...form, gst_percentage:e.target.value})}/>
            <datalist id="bill-gst-slabs">{GST_SLABS.map(s => <option key={s} value={s}/>)}</datalist>
          </Field>
          <Field label={form.gst_percentage ? 'GST Amount (auto from %)' : 'GST Amount'}>
            <input className="input mono" type="number" min="0" value={form.gst_percentage ? gstPreview.toFixed(2) : (form.gst_amount||'')} disabled={!!form.gst_percentage}
              onChange={e=>setForm({...form, gst_amount:e.target.value})}/>
          </Field>
          {/* User-requested: simplified to GST + one catch-all Other
              Charges going forward -- TDS/labour cess/security deposit
              are now expected to be entered against the RECEIPT instead
              (often not known for certain until the payment actually
              lands and the client says what they withheld), not
              pre-declared on the bill. These 4 legacy fields still exist
              on the backend and still correctly reduce Net Bank Credit
              (calculation unchanged) -- kept visible here ONLY when an
              already-existing bill already has one of them set, so real
              historical data stays manageable rather than hidden with no
              way to fix/consolidate it. A brand-new bill never sees them. */}
          {legacyWithholding && <>
            <Field label="TDS % (slab or custom, optional)">
              <input className="input mono" type="number" step="0.01" min="0" max="100" list="bill-tds-slabs" placeholder="e.g. 2"
                value={form.tds_percentage ?? ''} onChange={e=>setForm({...form, tds_percentage:e.target.value})}/>
              <datalist id="bill-tds-slabs">{[1, 2, 10].map(s => <option key={s} value={s}/>)}</datalist>
            </Field>
            <Field label={form.tds_percentage ? 'TDS Amount (auto from %)' : 'TDS Amount'}>
              <input className="input mono" type="number" min="0" value={form.tds_percentage ? tdsPreview.toFixed(2) : (form.tds_amount||'')} disabled={!!form.tds_percentage}
                onChange={e=>setForm({...form, tds_amount:e.target.value})}/>
            </Field>
            <Field label="Labour Cess % (slab or custom, optional)">
              <input className="input mono" type="number" step="0.01" min="0" max="100" list="bill-labour-cess-slabs" placeholder="e.g. 1"
                value={form.labour_cess_percentage ?? ''} onChange={e=>setForm({...form, labour_cess_percentage:e.target.value})}/>
              <datalist id="bill-labour-cess-slabs">{[1, 2].map(s => <option key={s} value={s}/>)}</datalist>
            </Field>
            <Field label={form.labour_cess_percentage ? 'Labour Cess Amount (auto from %)' : 'Labour Cess Amount'}>
              <input className="input mono" type="number" min="0" value={form.labour_cess_percentage ? labourCessPreview.toFixed(2) : (form.labour_cess_amount||'')} disabled={!!form.labour_cess_percentage}
                onChange={e=>setForm({...form, labour_cess_amount:e.target.value})}/>
            </Field>
            <Field label="Security Deposit % (slab or custom, optional)">
              <input className="input mono" type="number" step="0.01" min="0" max="100" list="bill-security-deposit-slabs" placeholder="e.g. 5"
                value={form.security_deposit_percentage ?? ''} onChange={e=>setForm({...form, security_deposit_percentage:e.target.value})}/>
              <datalist id="bill-security-deposit-slabs">{[5, 10].map(s => <option key={s} value={s}/>)}</datalist>
            </Field>
            <Field label={form.security_deposit_percentage ? 'Security Deposit Amount (auto from %)' : 'Security Deposit Amount'}>
              <input className="input mono" type="number" min="0" value={form.security_deposit_percentage ? securityDepositPreview.toFixed(2) : (form.security_deposit_amount||'')} disabled={!!form.security_deposit_percentage}
                onChange={e=>setForm({...form, security_deposit_amount:e.target.value})}/>
            </Field>
            <Field label="Retention"><input className="input mono" type="number" min="0" value={form.retention_amount||''} onChange={e=>setForm({...form, retention_amount:e.target.value})}/></Field>
          </>}
          <Field label="Other Charges"><input className="input mono" type="number" min="0" value={form.other_deduction_amount||''} onChange={e=>setForm({...form, other_deduction_amount:e.target.value})}/></Field>
          <Field label="Status"><select className="input" value={form.status||'billed'} onChange={e=>setForm({...form, status:e.target.value})}>
            {BILL_STATUSES.map(s=><option key={s} value={s}>{BILL_STATUS_LABELS[s]||s}</option>)}
          </select></Field>
          {/* User-requested: make this stand out as its own highlighted
              callout rather than just another inline field buried among
              the rest -- the number that actually matters once GST/
              Other Charges are set. */}
          <div className="col-span-2 rounded p-3 flex items-center justify-between" style={{background:'rgba(201,168,76,0.08)', border:'1px solid var(--gold)'}}>
            <span className="text-xs uppercase tracking-wide text-muted">Net Bank Credit (auto)</span>
            <span className="mono text-lg font-semibold text-[color:var(--gold)]">{fmtINR(netPreview)}</span>
          </div>
          <div className="col-span-2"><Field label="Description"><input className="input" value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field></div>
          <div className="col-span-2"><Field label="Notes"><input className="input" value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      <Modal open={!!detail} onClose={()=>setDetail(null)} title={`Bill ${detail?.bill_number || ''} — Receipt Allocation`} maxW={720}>
        {detail && (
          <>
            {/* Bug fix (user-reported): Outstanding showed the full Net
                Credit instead of 0 on an already-settled bill --
                `detail.outstanding_amount || detail.net_bank_credit`
                treated a genuine 0 as falsy and fell through to the
                fallback. `??` only falls through on null/undefined, so a
                real 0 (fully settled) now displays correctly. Also
                surfaces Deducted (bill-linked deductions) when present,
                so Net Credit = Received + Deducted + Outstanding
                visibly adds up instead of Outstanding looking wrong on
                its own. */}
            <div className={"grid grid-cols-1 gap-3 mb-4 " + ((detail.linked_deduction_amount||0) > 0.5 ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
              <div><div className="text-xs text-muted">Net Credit</div><div className="mono text-lg text-[color:var(--gold)]">{fmtINR(detail.net_bank_credit)}</div></div>
              {/* Same color language used everywhere else in the app --
                  green for money actually received (Income/Receipts own
                  amount columns), amber for withheld/deducted (the
                  reconciliation line + audit chips), green/red for
                  Outstanding based on whether anything's still owed. */}
              <div><div className="text-xs text-muted">Received</div><div className="mono text-lg text-green-400">{fmtINR(detail.allocated_amount || 0)}</div></div>
              {(detail.linked_deduction_amount||0) > 0.5 && <div><div className="text-xs text-muted">Deducted</div><div className="mono text-lg" style={{color:'var(--accent-amber)'}}>{fmtINR(detail.linked_deduction_amount)}</div></div>}
              <div><div className="text-xs text-muted">Outstanding</div><div className={"mono text-lg " + ((detail.outstanding_amount??detail.net_bank_credit) <= 0.5 ? 'text-emerald-400' : 'text-red-400')}>{fmtINR(detail.outstanding_amount ?? detail.net_bank_credit)}</div></div>
            </div>
            {/* User-requested: this modal has real room for it, unlike the
                list row (hover tooltip there instead) -- shown plainly
                when a bill actually has one. */}
            {detail.description && <div className="text-xs text-muted mb-4">{detail.description}</div>}
            {detail.pending_audit_amount > 0 && (
              <div className="flex items-center gap-2 mb-4 text-xs">
                <AuditStatusChip status="pending_review"/>
                <span className="text-muted">{fmtINR(detail.pending_audit_amount)} received but not yet reviewed in the Audit Queue — not counted in Received/Outstanding above until reviewed.</span>
              </div>
            )}
            <table className="tbl">
              <thead><tr><th>Date</th><th>Type</th><th>Bank Ref</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {detailReceipts.length===0 && <tr><td colSpan="4" className="text-center text-muted py-6">No receipts linked yet. Create a receipt in the Receipts tab and pick this bill.</td></tr>}
                {detailReceipts.map(r => (
                  // Shows only the portion of this receipt actually
                  // allocated to THIS bill (r.allocated_amount, set by
                  // GET /bills/{id}/receipts) -- a receipt split across
                  // several bills has a smaller allocated_amount than its
                  // own full amount, shown here rather than the full
                  // amount so this list stays consistent with the
                  // Received KPI right above it.
                  <tr key={r.id}>
                    <td className="text-xs">{fmtDate(r.receipt_date)}</td>
                    <td>{RCP_TYPE_LABELS[r.receipt_type] || r.receipt_type}</td>
                    <td>{r.bank_reference||'—'}</td>
                    <td className="mono text-right">{fmtINR(r.allocated_amount ?? r.amount)}{r.allocated_amount != null && r.allocated_amount !== r.amount && <span className="text-muted text-xs ml-1" title={`Full receipt amount: ${fmtINR(r.amount)} — split across multiple bills`}>*</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Modal>
      <Modal open={!!flagItem} onClose={()=>{setFlagItem(null); reloadFlags();}} title="Flags — Bill">
        {flagItem && <EntityFlagsBar entityType="bill" entityId={flagItem.id} canEdit={canEdit}/>}
      </Modal>
      <Modal open={moveOpen} onClose={()=>setMoveOpen(false)} title={`Move ${selected.size} Bill(s) to Another Project`}>
        <div className="text-sm text-muted mb-3">Master admin only — moving these changes Billed/Outstanding on both the current and the destination project.</div>
        <Field label="Destination Project *">
          <select className="input" value={moveTargetId} onChange={e=>setMoveTargetId(e.target.value)}>
            <option value="">— select —</option>
            {moveProjects.map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select>
        </Field>
        {moveProjects.length === 0 && <div className="text-xs text-muted mt-2">No other project found in this company.</div>}
        <label className="flex items-start gap-2 text-sm mt-4 p-3 rounded surface-2">
          <input type="checkbox" className="mt-0.5" checked={alsoMoveExpenses} onChange={e=>setAlsoMoveExpenses(e.target.checked)}/>
          <span>Also move every Expense currently on <b>{project?.project_name}</b> to the destination project. Optional — leave unchecked to move only the selected bill(s).</span>
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={()=>setMoveOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={doMoveSelected} disabled={moving || !moveTargetId} className="btn-gold btn-small">{moving ? 'Moving…' : 'Move'}</button>
        </div>
      </Modal>
    </div>
  );
}

// Real-usage-gap finding: a standalone, cross-project view for generating
// and tracking bills — previously only reachable one project at a time
// via that project's own "Bills" tab (still there, unchanged, still fully
// functional — this is a second, additive entry point covering every
// visible project including Extra Work buckets, not a replacement).
// ---- Bills page: receivables-by-project card helpers ----
const BILL_STALE_DAYS = 90; // an unpaid bill older than this is flagged "chase"
const billOutstanding = (b) => Number(b.outstanding_amount ?? b.net_bank_credit ?? 0);
// One summary per project from the bills currently shown. Outstanding follows the table's own
// rule (outstanding_amount, falling back to net bank credit when no allocation info exists).
function groupBillsByProject(rows, projectById, todayIso) {
  const m = new Map();
  rows.forEach(b => {
    const key = b.project_id || '__none__';
    let g = m.get(key);
    if (!g) { g = { key, project: projectById(b.project_id) || null, count: 0, gross: 0, net: 0, received: 0, outstanding: 0, pendingAudit: 0, lastDate: null, oldestUnpaid: null }; m.set(key, g); }
    const out = billOutstanding(b);
    g.count += 1; g.gross += Number(b.gross_amount || 0); g.net += Number(b.net_bank_credit || 0);
    g.received += Number(b.allocated_amount || 0); g.outstanding += out; g.pendingAudit += Number(b.pending_audit_amount || 0);
    if (b.bill_date && (!g.lastDate || b.bill_date > g.lastDate)) g.lastDate = b.bill_date;
    if (out > 0.5 && b.bill_date && (!g.oldestUnpaid || b.bill_date < g.oldestUnpaid)) g.oldestUnpaid = b.bill_date;
  });
  return [...m.values()].map(g => ({ ...g, oldestUnpaidDays: g.oldestUnpaid ? Math.max(0, Math.round((new Date(`${todayIso}T00:00:00`) - new Date(`${g.oldestUnpaid}T00:00:00`)) / 86400000)) : null }));
}
const billGroupName = (g) => g.project ? g.project.project_name : '(Unknown project)';
const sortBillGroups = (groups, key) => {
  const c = [...groups];
  const byName = (a, b) => billGroupName(a).localeCompare(billGroupName(b), undefined, { numeric: true, sensitivity: 'base' });
  if (key === 'oldest') c.sort((a, b) => (b.oldestUnpaidDays ?? -1) - (a.oldestUnpaidDays ?? -1) || byName(a, b));
  else if (key === 'billed') c.sort((a, b) => b.net - a.net || byName(a, b));
  else if (key === 'name') c.sort(byName);
  else c.sort((a, b) => b.outstanding - a.outstanding || byName(a, b));
  return c;
};
function BillCards({ groups, needsPicker, companyName, onViewBills }) {
  return (
    <div className="vendor-grid project-grid">
      {groups.map(g => {
        const settled = g.outstanding <= 0.5;
        const tone = settled ? META_CHIP_TONES.green : META_CHIP_TONES.amber;
        const collectedPct = g.net > 0 ? Math.max(0, Math.min(100, Math.round((g.received / g.net) * 100))) : 0;
        const stale = g.oldestUnpaidDays !== null && g.oldestUnpaidDays > BILL_STALE_DAYS;
        return (
          <div key={g.key} className="card card-dark vendor-card" style={{ '--vendor-accent': tone.color }} data-testid={`bill-card-${g.key}`}>
            <div className="min-w-0">
              {g.project
                ? <Link to={`/app/projects/${g.project.id}`} className="serif text-lg leading-tight hover:text-[color:var(--gold)] vendor-name" title={billGroupName(g)}>{billGroupName(g)}</Link>
                : <span className="serif text-lg leading-tight vendor-name">{billGroupName(g)}</span>}
              <div className="text-xs text-muted mt-0.5">{g.project ? `${g.project.client_name || 'No client'}${needsPicker ? ` · ${companyName(g.project.company_id)}` : ''}` : ''}</div>
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-muted">{settled ? 'Fully collected' : 'Still to collect'}</div>
              <div className={"serif mono text-2xl " + (settled ? 'text-emerald-400' : 'text-red-400')} data-testid={`bill-card-out-${g.key}`}>{fmtINR(g.outstanding)}</div>
              <div className="vendor-bar" aria-hidden="true" title={`${collectedPct}% of the net bank credit collected`}><div className="bar-green" style={{ width: `${collectedPct}%` }}/></div>
              <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                <div><div className="text-muted">Billed (net)</div><div className="mono" data-testid={`bill-card-net-${g.key}`}>{fmtINR(g.net)}</div></div>
                <div><div className="text-muted">Received</div><div className="mono text-green-400" data-testid={`bill-card-recv-${g.key}`}>{fmtINR(g.received)}</div></div>
                <div><div className="text-muted">Collected</div><div className="mono" data-testid={`bill-card-pct-${g.key}`}>{collectedPct}%</div></div>
              </div>
            </div>
            {g.pendingAudit > 0 && <div className="vendor-pending mt-3" data-testid={`bill-card-pending-${g.key}`}><Clock3 size={12}/>{fmtINR(g.pendingAudit)} received, awaiting review</div>}
            <div className="mt-3 text-xs flex items-center justify-between gap-2" data-testid={`bill-card-age-${g.key}`}>
              <span className="text-muted">{g.count} bill{g.count === 1 ? '' : 's'}{g.lastDate ? ` · last ${fmtDate(g.lastDate)}` : ''}</span>
              {g.oldestUnpaidDays !== null && <span className={stale ? 'ew-stale' : 'text-muted'} title={stale ? 'The oldest unpaid bill has been open a long time — chase it' : undefined}>oldest unpaid {g.oldestUnpaidDays} day{g.oldestUnpaidDays === 1 ? '' : 's'}{stale ? ' · chase' : ''}</span>}
            </div>
            <div style={{ flex: 1, minHeight: 14 }} aria-hidden="true"/>
            <div className="vendor-card-foot">
              <div className="inline-flex gap-1 flex-shrink-0 ml-auto">
                {g.project && <button onClick={() => onViewBills(g.key)} className="tab btn-small" data-testid={`bill-card-bills-${g.key}`}>View bills</button>}
                {g.project && <Link to={`/app/projects/${g.project.id}`} className="tab btn-small" data-testid={`bill-card-open-${g.key}`}>Open</Link>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BillsHub() {
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const { companies, needsPicker } = useMyCompanies();
  // Multi-company selection (same GrantPicker/company_filter pattern as
  // Reports) — replaces the old single-company dropdown so a caller with
  // several companies can view/print/export bills for any subset of them,
  // not just "one" or "all".
  const [companyFilterIds, setCompanyFilterIds] = usePersistedState('BillsHub.companyFilterIds', []);
  const [allCompaniesSelected, setAllCompaniesSelected] = usePersistedState('BillsHub.allCompaniesSelected', true);
  const projectF = useMultiFilter('BillsHub.project');
  // User-requested: client-wise filter, same dropdown convention as the
  // Projects list and Audit Queue's own Client filter. Bills don't carry
  // a client_name/department_client_id of their own (only project_id),
  // so resolved via a project_id -> client_id map built from the
  // /projects fetch this page already makes for the Project filter --
  // no extra request needed.
  const clientF = useMultiFilter('BillsHub.client');
  const masters = useMasters();
  const statusF = useMultiFilter('BillsHub.status');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [flagItem, setFlagItem] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [search, setSearch] = usePersistedState('BillsHub.search', '');
  const [view, setView] = usePersistedState('BillsHub.view', 'cards');
  const [cardSort, setCardSort] = usePersistedState('BillsHub.cardSort', 'outstanding');

  const companyFilterParam = (!needsPicker || allCompaniesSelected) ? undefined : companyFilterIds.join(',');
  const load = async () => {
    const [b, p] = await Promise.all([
      api.get('/bills', { params: { include_allocation: true, ...(companyFilterParam ? { company_filter: companyFilterParam } : {}) } }),
      api.get('/projects'),
    ]);
    setItems(b.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, [companyFilterParam]);
  const projectById = (id) => projects.find(p => p.id === id);
  const projectName = (id) => projectById(id)?.project_name || '—';
  const projectClientMap = useMemo(() => new Map(projects.map(p => [p.id, clientIdOfProject(p, masters.department_client)])), [projects, masters.department_client]);
  const clientOptions = useMemo(() =>
    (masters.department_client || [])
      .filter(m => allCompaniesSelected || (m.company_ids||[]).some(cid => companyFilterIds.includes(cid)))
      .slice().sort((a,b)=>a.name.localeCompare(b.name)),
    [masters.department_client, allCompaniesSelected, companyFilterIds]);
  // A company selection change can leave a stale Client selection that no
  // longer belongs to it -- clear rather than silently filtering to zero.
  useEffect(() => {
    clientF.prune(clientOptions.map(c => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyFilterIds, allCompaniesSelected]);

  const [flagsMap] = useEntityFlagsMap('bill', items.map(b=>b.id));
  const flaggedCount = items.filter(b=>(flagsMap[b.id]||[]).length > 0).length;
  const billQuery = search.trim().toLowerCase();
  const visibleItems = items
    .filter(b => projectF.matches(b.project_id))
    .filter(b => clientF.matches(projectClientMap.get(b.project_id)))
    .filter(b => statusF.matches(b.status))
    .filter(b => !pendingOnly || (b.outstanding_amount ?? b.net_bank_credit ?? 0) > 0.5)
    .filter(b => !flaggedOnly || (flagsMap[b.id]||[]).length > 0)
    // Round 32 audit finding: the "Project" column displays projectName(b.project_id)
    // but sorting used the raw project_id UUID -- clicking the header produced an
    // order with no relationship to the visible text. Sorting on this derived,
    // display-matching field instead.
    .map(b => ({ ...b, _project_name: projectName(b.project_id) }))
    .filter(b => !billQuery || [b._project_name, projectById(b.project_id)?.client_name, b.bill_number, b.description, String(b.gross_amount ?? ''), String(b.net_bank_credit ?? '')].some(v => String(v || '').toLowerCase().includes(billQuery)));

  const { sorted: sortedItems, sortKey, sortDir, toggleSort } = useSortableRows(visibleItems, 'bill_date', 'desc');
  const todayIso = new Date().toLocaleDateString('en-CA');
  const billGroups = useMemo(() => sortBillGroups(groupBillsByProject(visibleItems, projectById, todayIso), cardSort), [visibleItems, projects, cardSort]);
  const billTotals = visibleItems.reduce((a, b) => ({ net: a.net + Number(b.net_bank_credit || 0), received: a.received + Number(b.allocated_amount || 0), outstanding: a.outstanding + billOutstanding(b) }), { net: 0, received: 0, outstanding: 0 });
  const viewProjectBills = (pid) => { projectF.only(pid); setView('table'); };

  const visibleProjects = (!needsPicker || allCompaniesSelected) ? projects : projects.filter(p => companyFilterIds.includes(p.company_id));
  const companyFilterSubtitle = needsPicker && !allCompaniesSelected
    ? (companyFilterIds.length === 1 ? companies.find(c=>c.id===companyFilterIds[0])?.name : `${companyFilterIds.length} companies`)
    : 'All Companies';

  const pctOrFlat = (pctField, flatField) => {
    const g = Number(form.gross_amount||0);
    return (form[pctField] !== undefined && form[pctField] !== null && form[pctField] !== '')
      ? g * Number(form[pctField]) / 100
      : Number(form[flatField]||0);
  };
  const gstPreview = useMemo(() => pctOrFlat('gst_percentage','gst_amount'), [form]);
  const labourCessPreview = useMemo(() => pctOrFlat('labour_cess_percentage','labour_cess_amount'), [form]);
  const tdsPreview = useMemo(() => pctOrFlat('tds_percentage','tds_amount'), [form]);
  const securityDepositPreview = useMemo(() => pctOrFlat('security_deposit_percentage','security_deposit_amount'), [form]);
  const netPreview = useMemo(() => {
    const g = Number(form.gross_amount||0), rt = Number(form.retention_amount||0), o = Number(form.other_deduction_amount||0);
    return g + gstPreview - tdsPreview - labourCessPreview - securityDepositPreview - rt - o;
  }, [form, gstPreview, labourCessPreview, tdsPreview, securityDepositPreview]);
  // See BillsPanel's identical comment -- these 4 are legacy-only now,
  // entered against the Receipt instead going forward.
  const legacyWithholding = !!(form.id && (Number(form.tds_amount)||Number(form.labour_cess_amount)||Number(form.security_deposit_amount)||Number(form.retention_amount)));

  const save = async () => {
    if (saving) return;
    if (!form.bill_date) { toast.error('Bill Date is required.'); return; }
    if (!form.project_id) { toast.error('Project is required.'); return; }
    if (!form.gross_amount) { toast.error('Gross Amount is required.'); return; }
    setSaving(true);
    const isNew = !form.id;
    try {
      const payload = { ...form };
      ['gross_amount','gst_amount','tds_amount','labour_cess_amount','security_deposit_amount','retention_amount','other_deduction_amount'].forEach(k => payload[k] = Number(payload[k]||0));
      for (const pf of ['gst_percentage','labour_cess_percentage','tds_percentage','security_deposit_percentage']) {
        payload[pf] = (form[pf] !== undefined && form[pf] !== null && form[pf] !== '') ? Number(form[pf]) : null;
      }
      if (form.id) await api.patch(`/bills/${form.id}`, payload); else await api.post('/bills', payload);
      load();
      // User-requested: after adding a NEW bill (not editing an existing
      // one), stay open and reset to the same opening seed so several can
      // be entered in a row.
      if (isNew) { setForm({bill_date:new Date().toISOString().slice(0,10), status:'billed', project_id: projectF.soleId}); toast.success('Saved — ready for the next one'); }
      else { setOpen(false); setForm({}); toast.success('Saved'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const openAdd = () => { setForm({bill_date:new Date().toISOString().slice(0,10), status:'billed', project_id: projectF.soleId}); setOpen(true); };
  const openEdit = (b) => { setForm(b); setOpen(true); };
  // If the company selection changes out from under an already-picked
  // project (its company just got deselected), drop the stale selection
  // — same guard Reports already applies to its own project picker.
  useEffect(() => {
    projectF.prune(visibleProjects.map(p => p.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyFilterIds, allCompaniesSelected]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="section-eyebrow mb-2">Billing</div>
          <h1 className="serif text-4xl">Bills</h1>
          <div className="text-sm text-muted mt-1">Generate and track bills across every project — TDS and Security Deposit can be entered as a flat amount or a %.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {needsPicker && <GrantPicker
            items={companies}
            selectedIds={companyFilterIds}
            allAccess={allCompaniesSelected}
            onToggle={cid => setCompanyFilterIds(cur => cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid])}
            onSetAllAccess={v => { setAllCompaniesSelected(v); if (v) setCompanyFilterIds([]); }}
            allLabel="All Companies"
            noneWidth={200}
          />}
          <MultiFilterPicker filter={projectF} items={visibleProjects.map(p => ({ id: p.id, name: p.project_name }))} allLabel="All Projects" width={200} testId="bills-filter-project"/>
          {clientOptions.length > 0 && <MultiFilterPicker filter={clientF} items={clientOptions.map(c => ({ id: c.id, name: c.name }))} allLabel="All Clients" width={180} testId="bills-filter-client"/>}
          <MultiFilterPicker filter={statusF} items={BILL_STATUSES.map(st => ({ id: st, name: BILL_STATUS_LABELS[st] || st }))} allLabel="All Statuses" width={160} testId="bills-filter-status"/>
          <ClearFiltersButton filters={[projectF, clientF, statusF]} extraActive={needsPicker && !allCompaniesSelected} onClear={() => { setAllCompaniesSelected(true); setCompanyFilterIds([]); }}/>
          <div className="vendor-search">
            <SearchIcon size={14}/>
            <input className="input" placeholder="Search project, bill no., description…" value={search} onChange={e=>setSearch(e.target.value)} data-testid="bills-search"/>
          </div>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={pendingOnly} onChange={e=>setPendingOnly(e.target.checked)}/>Pending only</label>
          {view === 'cards' && <select className="input" style={{width:200}} value={cardSort} onChange={e=>setCardSort(e.target.value)} data-testid="bills-card-sort" title="Order of the project cards">
            <option value="outstanding">Sort: Most outstanding</option>
            <option value="oldest">Sort: Oldest unpaid</option>
            <option value="billed">Sort: Most billed</option>
            <option value="name">Sort: Name A–Z</option>
          </select>}
          <div className="inline-flex gap-1" role="group" aria-label="View">
            <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='cards'?'active':'')} onClick={()=>setView('cards')} data-testid="bills-view-cards"><LayoutGrid size={13}/>By project</button>
            <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='table'?'active':'')} onClick={()=>setView('table')} data-testid="bills-view-table"><ListIcon size={13}/>Bills</button>
          </div>
          {flaggedCount > 0 && <button onClick={()=>setFlaggedOnly(v=>!v)} className={"tab btn-small "+(flaggedOnly?'active':'')}>Flagged ({flaggedCount})</button>}
          {visibleItems.length > 0 && <button onClick={()=>exportBillsPDF(visibleItems.map(b=>({...b, _projectName:projectName(b.project_id)})), {title:'Bills', subtitle: companyFilterSubtitle, withProject:true})} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Export PDF</button>}
          {visibleItems.length > 0 && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>}
          {canEdit && <button onClick={openAdd} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Bill</button>}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 no-print" data-testid="bills-kpis">
        <KpiCard label="Bills" value={visibleItems.length} icon={Receipt} hint={`${billGroups.length} project${billGroups.length === 1 ? '' : 's'}`} />
        <KpiCard label="Billed (net credit)" value={fmtINR(billTotals.net)} tone="gold" />
        <KpiCard label="Received" value={fmtINR(billTotals.received)} tone="green" />
        <KpiCard label="Outstanding" value={fmtINR(billTotals.outstanding)} tone={billTotals.outstanding > 0.5 ? 'red' : 'green'} hint={billTotals.net > 0 ? `${Math.max(0, Math.min(100, Math.round((billTotals.received / billTotals.net) * 100)))}% collected` : undefined} />
      </div>
      {view === 'cards' && (
        <div className="no-print" data-testid="bills-cards">
          <div className="text-xs text-muted mb-3">Receivables by project — <b>{billGroups.length}</b> project{billGroups.length === 1 ? '' : 's'}, {visibleItems.length} of {items.length} bills{visibleItems.length !== items.length ? ' (filtered)' : ''}.</div>
          {billGroups.length === 0
            ? <div className="card card-dark text-center text-muted py-10">{items.length === 0 ? 'No bills yet.' : 'No bills match these filters.'}</div>
            : <BillCards groups={billGroups} needsPicker={needsPicker} companyName={(id) => companies.find(c => c.id === id)?.name || '—'} onViewBills={viewProjectBills}/>}
        </div>
      )}
      <div className={"card card-dark print-area overflow-x-auto " + (view === 'cards' ? 'vendor-table-print-only' : '')} data-testid="bills-table">
        <div className="print-header">
          <div className="print-company">{companyFilterSubtitle}</div>
          <div className="print-report-title">Bills</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        <div className="text-xs text-muted mb-2 no-print">{visibleItems.length} bill{visibleItems.length===1?'':'s'}</div>
        <table className="tbl">
          <thead><tr>
            <SortTh label="Project" sortKeyName="_project_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Date" sortKeyName="bill_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Bill #" sortKeyName="bill_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Gross" sortKeyName="gross_amount" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Net Credit" sortKeyName="net_bank_credit" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Received" sortKeyName="allocated_amount" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Outstanding" sortKeyName="outstanding_amount" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <th className="no-print">Flags</th>
            {canEdit && <th className="no-print"></th>}
          </tr></thead>
          <tbody>
            {visibleItems.length===0 && <tr><td colSpan={canEdit?10:9} className="text-center text-muted py-8">{items.length===0 ? 'No bills yet.' : 'No bills match these filters.'}</td></tr>}
            {sortedItems.map(b => (
              <tr key={b.id} title={b.description || undefined}>
                <td><Link to={`/app/projects/${b.project_id}`} className="hover:text-[color:var(--gold)]">{projectName(b.project_id)}</Link></td>
                <td className="text-xs">{fmtDate(b.bill_date)}</td>
                <td>{b.bill_number||'—'}</td>
                <td className="mono text-right">{fmtINR(b.gross_amount)}</td>
                <td className="mono text-right">{fmtINR(b.net_bank_credit)}</td>
                <td className="mono text-right">{fmtINR(b.allocated_amount||0)}</td>
                <td className={"mono text-right " + ((b.outstanding_amount||0)<=0.5?'text-emerald-400':'text-red-400')}>{fmtINR(b.outstanding_amount ?? b.net_bank_credit)}</td>
                <td>
                  <span className="badge status-running">{BILL_STATUS_LABELS[b.suggested_status||b.status] || b.suggested_status || b.status}</span>
                  {b.pending_audit_amount > 0 && <span className="ml-1" title={`${fmtINR(b.pending_audit_amount)} received but not yet reviewed in the Audit Queue`}><AuditStatusChip status="pending_review"/></span>}
                </td>
                <td className="no-print">
                  <button aria-label="Flags" title="Mark attention with a flag" onClick={()=>setFlagItem(b)} className="tab btn-small mr-1"><Flag size={12}/></button>
                  <FlagDots flags={flagsMap[b.id]}/>
                </td>
                {canEdit && <td className="text-right no-print"><button aria-label="Edit" onClick={()=>openEdit(b)} className="tab btn-small"><Edit3 size={12}/></button></td>}
              </tr>
            ))}
          </tbody>
          {/* User-requested: this cross-project Bills page never had a
              total footer -- only the per-project Bills tab did. Same
              4-column total (Gross/Net Credit/Received/Outstanding) for
              whatever's currently filtered/shown. */}
          {visibleItems.length > 0 && <tfoot><tr className="font-semibold">
            <td colSpan={3} className="text-right text-muted uppercase text-xs">Total</td>
            <td className="mono text-right">{fmtINR(visibleItems.reduce((a,b)=>a+Number(b.gross_amount||0),0))}</td>
            <td className="mono text-right text-[color:var(--gold)]">{fmtINR(visibleItems.reduce((a,b)=>a+Number(b.net_bank_credit||0),0))}</td>
            <td className="mono text-right">{fmtINR(visibleItems.reduce((a,b)=>a+Number(b.allocated_amount||0),0))}</td>
            <td className="mono text-right">{fmtINR(visibleItems.reduce((a,b)=>a+Number(b.outstanding_amount ?? b.net_bank_credit ?? 0),0))}</td>
            <td colSpan={canEdit?3:2}></td>
          </tr></tfoot>}
        </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Bill':'Add Bill'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* User-requested: keep this dry/clean, no dead ends -- a closed/
              settled/profit-approved project already rejects a new bill
              server-side. Still shows the CURRENT project when editing an
              existing bill even if it's since become locked (otherwise
              editing an old bill on a now-closed project would show a
              blank Project field despite the real value still being set). */}
          <div className="col-span-2"><Field label="Project *"><select className="input" value={form.project_id||''} onChange={e=>setForm({...form, project_id:e.target.value})}>
            <option value="">— select —</option>
            {/* User-requested: when a Client filter is active on the page,
                narrow this to that client's own projects too -- same
                narrowing added to Add Income. Still shows the form's own
                already-picked project even if a client filter set after
                opening it would otherwise exclude it. */}
            {visibleProjects.filter(p => !isProjectLocked(p) || p.id === form.project_id).filter(p => clientF.matches(clientIdOfProject(p, masters.department_client)) || p.id === form.project_id).map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select></Field></div>
          <Field label="Bill Date *"><input type="date" className="input" value={form.bill_date||''} onChange={e=>setForm({...form, bill_date:e.target.value})}/></Field>
          <Field label="Bill Number"><input className="input" value={form.bill_number||''} onChange={e=>setForm({...form, bill_number:e.target.value})}/></Field>
          <Field label="Gross Amount *"><input className="input mono" type="number" min="0" value={form.gross_amount||''} onChange={e=>setForm({...form, gross_amount:e.target.value})}/></Field>
          <Field label="GST % (slab or custom, optional)">
            <input className="input mono" type="number" step="0.01" min="0" max="100" list="billshub-gst-slabs" placeholder="e.g. 18"
              value={form.gst_percentage ?? ''} onChange={e=>setForm({...form, gst_percentage:e.target.value})}/>
            <datalist id="billshub-gst-slabs">{GST_SLABS.map(s => <option key={s} value={s}/>)}</datalist>
          </Field>
          <Field label={form.gst_percentage ? 'GST Amount (auto from %)' : 'GST Amount'}>
            <input className="input mono" type="number" min="0" value={form.gst_percentage ? gstPreview.toFixed(2) : (form.gst_amount||'')} disabled={!!form.gst_percentage}
              onChange={e=>setForm({...form, gst_amount:e.target.value})}/>
          </Field>
          {legacyWithholding && <>
            <Field label="TDS % (slab or custom, optional)">
              <input className="input mono" type="number" step="0.01" min="0" max="100" list="billshub-tds-slabs" placeholder="e.g. 2"
                value={form.tds_percentage ?? ''} onChange={e=>setForm({...form, tds_percentage:e.target.value})}/>
              <datalist id="billshub-tds-slabs">{[1, 2, 10].map(s => <option key={s} value={s}/>)}</datalist>
            </Field>
            <Field label={form.tds_percentage ? 'TDS Amount (auto from %)' : 'TDS Amount'}>
              <input className="input mono" type="number" min="0" value={form.tds_percentage ? tdsPreview.toFixed(2) : (form.tds_amount||'')} disabled={!!form.tds_percentage}
                onChange={e=>setForm({...form, tds_amount:e.target.value})}/>
            </Field>
            <Field label="Labour Cess % (slab or custom, optional)">
              <input className="input mono" type="number" step="0.01" min="0" max="100" list="billshub-labour-cess-slabs" placeholder="e.g. 1"
                value={form.labour_cess_percentage ?? ''} onChange={e=>setForm({...form, labour_cess_percentage:e.target.value})}/>
              <datalist id="billshub-labour-cess-slabs">{[1, 2].map(s => <option key={s} value={s}/>)}</datalist>
            </Field>
            <Field label={form.labour_cess_percentage ? 'Labour Cess Amount (auto from %)' : 'Labour Cess Amount'}>
              <input className="input mono" type="number" min="0" value={form.labour_cess_percentage ? labourCessPreview.toFixed(2) : (form.labour_cess_amount||'')} disabled={!!form.labour_cess_percentage}
                onChange={e=>setForm({...form, labour_cess_amount:e.target.value})}/>
            </Field>
            <Field label="Security Deposit % (slab or custom, optional)">
              <input className="input mono" type="number" step="0.01" min="0" max="100" list="billshub-security-deposit-slabs" placeholder="e.g. 5"
                value={form.security_deposit_percentage ?? ''} onChange={e=>setForm({...form, security_deposit_percentage:e.target.value})}/>
              <datalist id="billshub-security-deposit-slabs">{[5, 10].map(s => <option key={s} value={s}/>)}</datalist>
            </Field>
            <Field label={form.security_deposit_percentage ? 'Security Deposit Amount (auto from %)' : 'Security Deposit Amount'}>
              <input className="input mono" type="number" min="0" value={form.security_deposit_percentage ? securityDepositPreview.toFixed(2) : (form.security_deposit_amount||'')} disabled={!!form.security_deposit_percentage}
                onChange={e=>setForm({...form, security_deposit_amount:e.target.value})}/>
            </Field>
            <Field label="Retention"><input className="input mono" type="number" min="0" value={form.retention_amount||''} onChange={e=>setForm({...form, retention_amount:e.target.value})}/></Field>
          </>}
          <Field label="Other Charges"><input className="input mono" type="number" min="0" value={form.other_deduction_amount||''} onChange={e=>setForm({...form, other_deduction_amount:e.target.value})}/></Field>
          <Field label="Status"><select className="input" value={form.status||'billed'} onChange={e=>setForm({...form, status:e.target.value})}>
            {BILL_STATUSES.map(s=><option key={s} value={s}>{BILL_STATUS_LABELS[s]||s}</option>)}
          </select></Field>
          {/* User-requested: make this stand out as its own highlighted
              callout rather than just another inline field buried among
              the rest -- the number that actually matters once GST/
              Other Charges are set. */}
          <div className="col-span-2 rounded p-3 flex items-center justify-between" style={{background:'rgba(201,168,76,0.08)', border:'1px solid var(--gold)'}}>
            <span className="text-xs uppercase tracking-wide text-muted">Net Bank Credit (auto)</span>
            <span className="mono text-lg font-semibold text-[color:var(--gold)]">{fmtINR(netPreview)}</span>
          </div>
          <div className="col-span-2"><Field label="Description"><input className="input" value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field></div>
          <div className="col-span-2"><Field label="Notes"><input className="input" value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      <Modal open={!!flagItem} onClose={()=>setFlagItem(null)} title="Flags — Bill">
        {flagItem && <EntityFlagsBar entityType="bill" entityId={flagItem.id} canEdit={canEdit}/>}
      </Modal>
    </div>
  );
}

// ============= PROFIT ADJUSTMENTS PANEL =============
function AdjustmentsPanel({ projectId, canEdit, canDelete, reload, project }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const load = async () => setItems((await api.get('/profit-adjustments', { params: { project_id: projectId } })).data);
  useEffect(() => { load(); }, [projectId]);
  const total = items.reduce((a, x) => a + Number(x.amount||0), 0);
  const save = async () => {
    if (saving) return;
    if (!form.adjustment_date) { toast.error('Date is required.'); return; }
    if (!form.amount) { toast.error('Amount is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, project_id: projectId, amount: Number(form.amount||0) };
      if (form.id) await api.patch(`/profit-adjustments/${form.id}`, payload); else await api.post('/profit-adjustments', payload);
      setOpen(false); setForm({}); load(); reload?.(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete adjustment?') || !confirmUnlockedEdit(project)) return;
    try { await api.delete(`/profit-adjustments/${id}`); load(); reload?.(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openAdd = () => { if (!confirmUnlockedEdit(project)) return; setForm({adjustment_date:new Date().toISOString().slice(0,10)}); setOpen(true); };
  const openEdit = (a) => { if (!confirmUnlockedEdit(project)) return; setForm(a); setOpen(true); };
  return (
    <div className="card card-dark">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)] flex items-center gap-2">Profit Adjustments{isProjectLocked(project) && <Lock size={14} className="text-muted"/>}</div>
          <div className="text-xs text-muted">Manual adjustments applied to Distributable Profit. Each has a reason and audit trail.</div>
        </div>
        {canEdit && <button onClick={openAdd} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Adjustment</button>}
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Reason</th><th>Approved By</th>{canEdit && <th></th>}</tr></thead>
        <tbody>
          {items.length===0 && <tr><td colSpan={canEdit?6:5} className="text-center text-muted py-6">No adjustments.</td></tr>}
          {items.map(a => (
            <tr key={a.id}><td className="text-xs">{fmtDate(a.adjustment_date)}</td><td className="text-xs">{a.adjustment_type}</td><td className={"mono "+(a.amount>=0?'':'text-red-400')}>{fmtINR(a.amount)}</td><td>{a.reason||'—'}</td><td className="text-xs">{a.approved_by||'—'}</td>
              {canEdit && <td className="text-right"><button aria-label="Edit" onClick={()=>openEdit(a)} className="tab btn-small mr-1"><Edit3 size={12}/></button>{canDelete && <button aria-label="Delete" onClick={()=>del(a.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}</td>}
            </tr>
          ))}
          {items.length>0 && <tr><td colSpan={2} className="text-right text-muted uppercase text-xs">Total</td><td className="mono text-[color:var(--gold)]">{fmtINR(total)}</td><td colSpan={canEdit?3:2}></td></tr>}
        </tbody>
      </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Adjustment':'Add Adjustment'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Date *"><input type="date" className="input" value={form.adjustment_date||''} onChange={e=>setForm({...form, adjustment_date:e.target.value})}/></Field>
          <Field label="Type"><select className="input" value={form.adjustment_type||'other'} onChange={e=>setForm({...form, adjustment_type:e.target.value})}>
            {['prior_period','penalty','exceptional_income','exceptional_expense','partner_adjustment','rounding','other'].map(t=><option key={t} value={t}>{t}</option>)}
          </select></Field>
          <Field label="Amount (positive/negative) *"><input className="input mono" type="number" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
          <Field label="Reason"><input className="input" value={form.reason||''} onChange={e=>setForm({...form, reason:e.target.value})}/></Field>
          <Field label="Approved By"><input className="input" value={form.approved_by||''} onChange={e=>setForm({...form, approved_by:e.target.value})}/></Field>
          <div className="col-span-2"><Field label="Notes"><textarea className="input" rows={2} value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

// ============= EXCEL IMPORT WIZARD =============
const IMPORT_TARGETS = {
  projects: { label: 'Projects', fields: [
    { k: 'project_name', l: 'Project Name', required: true },
    { k: 'work_category', l: 'Work Category (e.g. Tender Work / Voucher Payment / Billed via Vendor)', required: true },
    { k: 'tender_number', l: 'Tender Number' },
    { k: 'client_name', l: 'Client Name' },
    { k: 'contract_value', l: 'Contract Value' },
    { k: 'gst_amount', l: 'GST Amount' },
    { k: 'billed_amount', l: 'Billed Amount' },
    { k: 'start_date', l: 'Start Date' },
    { k: 'status', l: 'Status' },
    { k: 'model', l: 'Model (money/capital/equal — blank defaults to Equal + Capital Bonus)' },
  ] },
  extra_works: { label: 'Extra Works (new buckets)', fields: [
    { k: 'name', l: 'Work Name / Place', required: true },
    { k: 'client_name', l: 'Client Name (must exactly match an existing Settings > Masters > Department / Client entry)', required: true },
    { k: 'start_date', l: 'Start Date' },
    { k: 'notes', l: 'Notes' },
  ] },
  investments: { label: 'Investments', fields: [
    { k: 'project', l: 'Project Name', required: true },
    { k: 'partner', l: 'Partner Name', required: true },
    { k: 'date', l: 'Date', required: true },
    { k: 'amount', l: 'Amount', required: true },
    { k: 'voucher_number', l: 'Voucher Number (digits only, real & unique)', required: true },
    { k: 'type', l: 'Type (capital_investment/partner_loan)' },
    { k: 'paid_to_company_fund', l: 'Added to Company Reserve / Common Fund (yes/no — optional, defaults to no)' },
    { k: 'notes', l: 'Notes' },
  ] },
  expenses: { label: 'Project Expenses', fields: [
    { k: 'project', l: 'Project Name', required: true },
    { k: 'date', l: 'Date', required: true },
    { k: 'category', l: 'Category', required: true },
    { k: 'description', l: 'Description' },
    { k: 'amount', l: 'Amount', required: true },
    { k: 'paid_by', l: 'Paid By Partner Name (must exactly match Settings > Partners — required unless Bank Account is mapped instead)' },
    { k: 'vendor', l: 'Vendor' },
    { k: 'voucher_number', l: 'Voucher Number (digits only, real & unique)', required: true },
    { k: 'bank_account', l: 'Bank Account Name (required unless Paid By is mapped instead)' },
    { k: 'payment_type', l: 'Payment Type (salary/travel_allowance/other — mapping this makes Staff Name mandatory below)' },
    { k: 'staff_name', l: 'Staff Name (must exactly match Settings > Staff; required when Payment Type is mapped, links this to /staff/{id}/payments)' },
  ] },
  company_expenses: { label: 'Company Expenses (overhead, no project)', fields: [
    { k: 'overhead_category', l: 'Overhead Category Name', required: true },
    { k: 'date', l: 'Date', required: true },
    { k: 'description', l: 'Description' },
    { k: 'amount', l: 'Amount', required: true },
    { k: 'voucher_number', l: 'Voucher Number (digits only, real & unique)', required: true },
    { k: 'paid_by', l: 'Paid By Partner Name (must exactly match Settings > Partners — required unless Bank Account or Paid from Company Fund is mapped instead)' },
    { k: 'bank_account', l: 'Bank Account Name (required unless Paid By or Paid from Company Fund is mapped instead)' },
    { k: 'paid_from_company_fund', l: 'Paid from Company Fund (yes/no — required unless Paid By or Bank Account is mapped instead)' },
    { k: 'payment_type', l: 'Payment Type (salary/travel_allowance/other — mapping this makes Staff Name mandatory below)' },
    { k: 'staff_name', l: 'Staff Name (must exactly match Settings > Staff; required when Payment Type is mapped, links this to /staff/{id}/payments)' },
  ] },
  security_instruments: { label: 'Security & Refundables', fields: [
    // User-requested: mandatory columns grouped together in one continuous
    // block (matching the order they're actually checked in, top to
    // bottom, in create_security_instrument's import path), instead of
    // interleaved with the optional ones below.
    { k: 'type', l: 'Instrument Type (emd/fixed_deposit/bank_guarantee/demand_draft (DD)/refundable_advance/other)', required: true },
    { k: 'voucher_number', l: 'Voucher Number (digits only, real & unique)', required: true },
    { k: 'date_lodged', l: 'Date Lodged', required: true },
    { k: 'amount', l: 'Amount', required: true },
    { k: 'party_name', l: 'Party Name', required: true },
    { k: 'borne_by', l: 'Borne By — a Partner Name matching Settings > Partners, or "company" if paid from the Company Fund', required: true },
    { k: 'ledger_folio_number', l: 'Ledger Folio Number', required: true },
    { k: 'project', l: 'Project Name (optional — leave blank for a company-level instrument)' },
    { k: 'tender_reference', l: 'Tender Reference' },
    { k: 'bank_account_name', l: 'Bank / Account Name' },
    { k: 'status', l: 'Status (pending/hold/settlement_required/credited/refunded — defaults to pending)' },
    { k: 'refund_due_date', l: 'Refund Due Date' },
    { k: 'notes', l: 'Notes' },
  ] },
  receipts: { label: 'Receipts', fields: [
    { k: 'project', l: 'Project Name', required: true },
    { k: 'date', l: 'Date', required: true },
    { k: 'amount', l: 'Amount', required: true },
    { k: 'type', l: 'Type' },
    { k: 'reference', l: 'Bank Reference' },
    { k: 'bank_account', l: 'Bank Account Name (optional)' },
    { k: 'description', l: 'Description' },
  ] },
  partner_transactions: { label: 'Partner Transactions', fields: [
    { k: 'partner', l: 'Partner Name', required: true },
    { k: 'project', l: 'Project Name' },
    { k: 'date', l: 'Date', required: true },
    { k: 'type', l: 'Transaction Type', required: true },
    { k: 'amount', l: 'Amount', required: true },
    { k: 'voucher_number', l: 'Voucher Number (digits only, real & unique — required unless Type is Interest Credit/Charge)' },
    { k: 'paid_by', l: 'Paid By Partner Name (if not this partner / company funds — optional, must exactly match Settings > Partners)' },
    { k: 'paid_from_company_fund', l: 'Paid from Company Fund (yes/no — optional, defaults to no)' },
    { k: 'description', l: 'Description' },
    { k: 'reference', l: 'Reference' },
  ] },
  fixed_assets: { label: 'Fixed Assets', fields: [
    { k: 'asset_code', l: 'Asset Code', required: true },
    { k: 'asset_name', l: 'Asset Name', required: true },
    { k: 'purchase_date', l: 'Purchase Date', required: true },
    { k: 'purchase_cost', l: 'Purchase Cost', required: true },
    { k: 'category', l: 'Category (plant_machinery/vehicles/furniture_fixtures/office_equipment/computers_it/tools_equipment/electrical_installations/land_building/others)' },
    { k: 'location', l: 'Location' },
    { k: 'custodian', l: 'Custodian' },
    { k: 'serial_number', l: 'Serial Number' },
    { k: 'quantity', l: 'Quantity' },
    { k: 'invoice_number', l: 'Invoice Number' },
    { k: 'warranty_expiry', l: 'Warranty Expiry' },
    { k: 'depreciation_method', l: 'Depreciation Method (straight_line/wdv/none)' },
    { k: 'useful_life_years', l: 'Useful Life (Years)' },
    { k: 'depreciation_rate', l: 'Depreciation Rate (%)' },
    { k: 'salvage_value', l: 'Salvage Value' },
    { k: 'status', l: 'Status (active/under_maintenance/disposed/written_off)' },
    { k: 'project', l: 'Assigned Project Name (optional)' },
    { k: 'vendor', l: 'Purchased From Vendor Name (optional)' },
    { k: 'notes', l: 'Notes' },
  ] },
};

// User-requested: a total amount shown at the preview step, so a total
// pasted from the source spreadsheet can be sanity-checked against what's
// about to be imported before committing. Each entity's own single money
// figure -- 'extra_works' has none at all (no amount-shaped field), so it's
// left out on purpose and the stat tile below is skipped entirely for it.
const IMPORT_AMOUNT_FIELDS = {
  projects: 'contract_value', investments: 'amount', expenses: 'amount',
  company_expenses: 'amount', security_instruments: 'amount', receipts: 'amount',
  partner_transactions: 'amount', fixed_assets: 'purchase_cost',
};

// lockedProject ({id, project_name, company_id}) + lockedEntity: launched
// from inside a specific project's Receipts/Expenses tab, so the entity and
// project are fixed rather than picked — every imported row belongs to
// that one project automatically, no "Project Name" column needed in the file.
function ExcelImportWizard({ lockedProject, lockedEntity } = {}) {
  const [step, setStep] = useState(1);
  // Real-usage-gap follow-up (audit finding): this commit removed the
  // 'partners' entry from IMPORT_TARGETS (Partners import moved out of
  // the bulk-import wizard, per the commit message) but left this default
  // pointing at the now-nonexistent key -- target below (IMPORT_TARGETS[entity])
  // resolved to undefined and target.fields threw immediately, crashing the
  // standalone "Excel / CSV Import Wizard" panel (Settings > Data & Backup,
  // the app's one bare `<ExcelImportWizard/>` call site with no lockedEntity)
  // on every single open, with no error boundary anywhere in the app to
  // catch it. 'projects' is IMPORT_TARGETS' first real entry and a sane
  // default entity to land on.
  const [entity, setEntity] = useState(lockedEntity || 'projects');
  const [rawRows, setRawRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // { [fieldKey]: string[] of source columns, combined with a space }
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedMappings, setSavedMappings] = useState([]);
  const [mappingName, setMappingName] = useState('');
  // Full-app audit finding: DELETE /import/mappings/{id} was backend-
  // complete with no UI caller at all -- a mistyped or obsolete saved
  // mapping accumulated permanently in the load-mapping dropdown below
  // with no way to clean it up short of a direct DB edit.
  const [selectedMappingId, setSelectedMappingId] = useState('');
  const [deletingMapping, setDeletingMapping] = useState(false);
  const { companies, needsPicker } = useMyCompanies();
  const [companyId, setCompanyId] = useState('');
  const [dupeAck, setDupeAck] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);

  const target = IMPORT_TARGETS[entity];
  // The project column is auto-filled from lockedProject, not mapped from
  // the file — hide it from the mapping UI so there's nothing to pick.
  const visibleFields = lockedProject ? target.fields.filter(f => f.k !== 'project') : target.fields;
  const effectiveCompanyId = lockedProject ? lockedProject.company_id : companyId;

  const loadSavedMappings = async (ent) => {
    const r = await api.get('/import/mappings', { params: { entity: ent } });
    setSavedMappings(r.data);
  };
  useEffect(() => { loadSavedMappings(entity); setSelectedMappingId(''); }, [entity]);

  const onFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const buf = await file.arrayBuffer();
    const XLSX = await loadXlsx();
    const wb = XLSX.read(buf, { type: 'array', cellDates: true });
    const first = wb.SheetNames[0];
    const sheet = wb.Sheets[first];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
    if (!rows.length) { toast.error('No rows found'); return; }
    setRawRows(rows);
    const hdrs = Object.keys(rows[0]);
    setHeaders(hdrs);
    // Best-effort auto-mapping
    const auto = {};
    // Round 24 finding: two fields whose cleaned keys both partially match
    // the same short, plausible header (e.g. a generic "Amount" column
    // matching both gst_amount and billed_amount) used to both silently
    // claim it, giving two different financial fields the exact same
    // value with nothing on the mapping screen distinguishing them. Once
    // a header is claimed by a field, it's removed from the pool so no
    // later field can auto-match onto it too -- first-declared field wins.
    const claimedHeaders = new Set();
    for (const f of visibleFields) {
      const found = hdrs.find(h => {
        if (claimedHeaders.has(h)) return false;
        const hn = h.toLowerCase().replace(/[^a-z0-9]/g,'');
        const kn = f.k.toLowerCase().replace(/[^a-z0-9]/g,'');
        const ln = f.l.toLowerCase().replace(/[^a-z0-9]/g,'');
        const hclean = hn;
        return hclean === kn || hclean === ln || hclean.includes(kn) || kn.includes(hclean);
      });
      if (found) { auto[f.k] = [found]; claimedHeaders.add(found); }
    }
    setMapping(auto);
    setStep(2);
  };

  const applySavedMapping = (id) => {
    const m = savedMappings.find(x => x.id === id);
    if (!m) return;
    // Stored mapping may be from before multi-column support (plain strings) — normalize to arrays.
    // Round 24 finding: a saved mapping's column names were loaded as-is
    // with no check against the CURRENT file's actual headers -- if this
    // file's columns differ from the one the mapping was saved against,
    // buildRows() silently produced undefined for that field on every row
    // (dropping it entirely), and the user only found out via a confusing
    // wall of backend "not found"/missing-field errors with no indication
    // the real cause was a stale saved mapping. Now any column the current
    // file doesn't actually have is dropped from the applied mapping and
    // called out by name, so the affected fields visibly need remapping
    // instead of silently vanishing.
    // Round 25 finding: a mapping can be saved from a context where a field
    // is visible (e.g. "project", from the general, non-project-locked
    // import screen) and later loaded inside a lockedProject context where
    // that same field is deliberately hidden (auto-filled, not mappable —
    // see visibleFields above). If that field's saved column also happened
    // to be stale, this used to fall back to the raw field key and tell the
    // user to "map it manually" for a field with literally no mapping
    // control on screen -- a confusing, unactionable toast. Only fields the
    // user can actually see and fix in this screen's mapping UI are called
    // out now; a stale/hidden field's saved column is just quietly dropped
    // (harmless -- buildRows() only ever reads mapping entries for
    // visibleFields, and a locked/hidden field's value is auto-supplied
    // elsewhere anyway).
    const normalized = {};
    const droppedFields = [];
    for (const [k, v] of Object.entries(m.mapping || {})) {
      const cols = (Array.isArray(v) ? v : [v]).filter(Boolean);
      const validCols = cols.filter(c => headers.includes(c));
      if (validCols.length) normalized[k] = validCols;
      if (validCols.length < cols.length) {
        const f = visibleFields.find(vf => vf.k === k);
        if (f) droppedFields.push(f.l);
      }
    }
    setMapping(normalized);
    if (droppedFields.length) {
      toast.error(`Loaded "${m.name}", but this file is missing the column(s) it expects for: ${droppedFields.join(', ')} — map those manually`);
    } else {
      toast.success(`Loaded mapping "${m.name}"`);
    }
  };

  const saveMappingAs = async () => {
    if (mappingSaving) return;
    if (!mappingName.trim()) { toast.error('Name this mapping first'); return; }
    setMappingSaving(true);
    try {
      await api.post('/import/mappings', { entity, name: mappingName.trim(), mapping, company_id: effectiveCompanyId || undefined });
      setMappingName(''); loadSavedMappings(entity);
      toast.success('Mapping saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setMappingSaving(false); }
  };

  const setFieldColumns = (k, cols) => setMapping({ ...mapping, [k]: cols.filter(Boolean) });
  const addColumnToField = (k) => setFieldColumns(k, [...(mapping[k]||['']), '']);
  const removeColumnFromField = (k, i) => setFieldColumns(k, (mapping[k]||[]).filter((_,idx)=>idx!==i));

  const buildRows = () => rawRows.map(r => {
    const out = {};
    for (const f of visibleFields) {
      const cols = (mapping[f.k] || []).filter(Boolean);
      const parts = cols.map(c => r[c]).filter(v => v !== undefined && v !== '');
      if (parts.length) out[f.k] = parts.join(' ');
    }
    if (lockedProject) out.project = lockedProject.project_name;
    return out;
  });

  const doPreview = async () => {
    if (needsPicker && !lockedProject && !companyId) { toast.error('Select a company first'); return; }
    const missingReq = visibleFields.filter(f => f.required && !(mapping[f.k]||[]).some(Boolean));
    if (missingReq.length) { toast.error(`Missing required mapping: ${missingReq.map(f=>f.l).join(', ')}`); return; }
    setBusy(true);
    try {
      const rows = buildRows();
      const r = await api.post('/import/bulk', { entity, rows, dry_run: true, company_id: effectiveCompanyId || undefined, locked_project_id: lockedProject?.id });
      setPreview(r.data);
      setDupeAck(false);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const rows = buildRows();
      const r = await api.post('/import/bulk', { entity, rows, dry_run: false, company_id: effectiveCompanyId || undefined, locked_project_id: lockedProject?.id });
      // Real-usage finding: a fully-rejected import (every row skipped via
      // a WARNING, not necessarily an "error" -- e.g. a missing/non-
      // matching mandatory field like Extra Work's client_name) still got
      // this toast.success + Step 4's "Import Complete" checkmark, since
      // that step's own rejected-state check only looked at errors.length,
      // never warnings.length. inserted===0 is what actually means nothing
      // was saved, regardless of which array explains why.
      if (r.data.inserted === 0 && (r.data.errors.length > 0 || r.data.warnings.length > 0)) {
        toast.error(`Import rejected — 0 rows inserted (${r.data.errors.length} errors, ${r.data.warnings.length} warnings). See details below.`);
      } else {
        toast.success(`Inserted ${r.data.inserted} rows · ${r.data.errors.length} errors · ${r.data.warnings.length} warnings`);
      }
      setPreview(r.data); setStep(4);
    } catch (e) { toast.error(e.response?.data?.detail || 'Commit failed'); }
    finally { setBusy(false); }
  };

  const reset = () => { setStep(1); setRawRows([]); setHeaders([]); setMapping({}); setPreview(null); setDupeAck(false); };

  return (
    <div>
      {/* User-reported (real device): the 4-step tracker below had no
          wrap protection -- 4 pills + 3 chevron separators with no
          flex-wrap either overflowed the modal horizontally on a phone or
          squeezed unreadably thin, since nothing here could shrink below
          its own text content. flex-wrap lets it break onto a second line
          instead, same as every other toolbar in the app already does. */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
        {['Upload','Map','Preview','Done'].map((s,i) => (
          <React.Fragment key={s}>
            <div className={"px-3 py-1 rounded-full border " + ((i+1)<=step ? 'border-[color:var(--gold)] text-[color:var(--gold)]' : 'border-subtle text-muted')}>{i+1}. {s}</div>
            {i<3 && <ChevronRight size={12} className="text-muted"/>}
          </React.Fragment>
        ))}
      </div>

      {step===1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lockedProject ? (
            <div className="col-span-2 p-3 rounded surface-2 text-sm">Importing {target.label.toLowerCase()} for <span className="text-[color:var(--gold)]">{lockedProject.project_name}</span> — every row in this file will be recorded against this project.</div>
          ) : <>
            {needsPicker && <Field label="Company *"><select className="input" value={companyId} onChange={e=>setCompanyId(e.target.value)} data-testid="excel-company">
              <option value="">— select —</option>
              {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select></Field>}
            {!lockedEntity && <Field label="What are you importing?">
              <select className="input" value={entity} onChange={e=>setEntity(e.target.value)} data-testid="excel-entity">
                {Object.entries(IMPORT_TARGETS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>}
          </>}
          <Field label="Choose .xlsx / .csv file">
            <input type="file" accept=".xlsx,.xls,.csv" className="input" onChange={onFile} data-testid="excel-file"/>
          </Field>
          <div className="col-span-2 p-3 rounded surface-2">
            <div className="text-xs text-muted mb-2">Fields {target.label} expects — arrange your spreadsheet's columns to match before uploading (any header names are fine, you'll map them next):</div>
            <div className="flex flex-wrap gap-1.5">
              {visibleFields.map(f => <span key={f.k} className="chip" style={f.required?{color:'#e66767',background:'rgba(230,103,103,0.12)'}:undefined}>{f.l}{f.required?' *':''}</span>)}
            </div>
          </div>
          <div className="col-span-2 text-xs text-muted">
            Tip: Column headers in your file can be any label — you'll map them in the next step. Dates support YYYY-MM-DD, D/M/YYYY or D/M/YY (day and month can be 1 or 2 digits either way, e.g. 5/1/2026 or 05/01/26 — dashes work the same as slashes), and D Mon YYYY (e.g. 5 Jan 2026). Fields marked <span className="text-red-400">*</span> are required.
          </div>
        </div>
      )}

      {step===2 && (() => {
        // Round 24 finding: nothing on this screen distinguished two
        // different fields silently pointing at the same source column
        // (whether from auto-detect or a manual edit) — each dropdown
        // looks independently correct, so a non-technical user reviewing
        // the mapping is very likely to miss it, and both fields then get
        // the exact same value for every row.
        const columnUsage = {};
        for (const f of visibleFields) {
          // Round 25 finding: iterating mapping[f.k] directly counted a
          // field combining the SAME column with itself (e.g. "+ combine
          // column" then picking that column again by mistake) as if two
          // DIFFERENT fields collided on it — the banner below would fire
          // with a confusing "X" -> "Field" AND "Field" line. Deduping a
          // field's own column list first means only a genuine cross-field
          // collision (this field's own multi-column combine is still
          // completely unaffected) trips the warning.
          const ownCols = new Set(mapping[f.k] || []);
          for (const c of ownCols) {
            if (!c) continue;
            (columnUsage[c] = columnUsage[c] || []).push(f.l);
          }
        }
        const collidingColumns = Object.entries(columnUsage).filter(([,fields]) => fields.length > 1);
        return (
        <div>
          <div className="text-sm text-muted mb-3">Detected {rawRows.length} rows with {headers.length} columns. Map each required field — click "+ combine column" to join two source columns into one (e.g. a date + time column).</div>

          {collidingColumns.length > 0 && (
            <div className="card card-dark mb-3" style={{border:'1px solid rgba(230,103,103,0.4)'}}>
              <div className="text-xs text-red-400 mb-1 font-semibold">Same column mapped to more than one field:</div>
              <div className="text-xs mono space-y-1">
                {collidingColumns.map(([col, fields]) => <div key={col}>"{col}" → {fields.join(' AND ')}</div>)}
              </div>
              <div className="text-xs text-muted mt-2">Each of those fields will get the exact same value from that column for every row — check this is really intended before continuing.</div>
            </div>
          )}

          {savedMappings.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded surface-2">
              <span className="text-xs text-muted">Saved mappings for {target.label}:</span>
              <select className="input" style={{width:220}} value={selectedMappingId} onChange={e=>{ setSelectedMappingId(e.target.value); if (e.target.value) applySavedMapping(e.target.value); }} data-testid="load-mapping">
                <option value="">— load a saved mapping —</option>
                {savedMappings.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              {selectedMappingId && <button
                onClick={async () => {
                  if (!window.confirm('Delete this saved mapping? This cannot be undone.')) return;
                  setDeletingMapping(true);
                  try {
                    await api.delete(`/import/mappings/${selectedMappingId}`);
                    setSelectedMappingId('');
                    loadSavedMappings(entity);
                    toast.success('Mapping deleted');
                  } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                  finally { setDeletingMapping(false); }
                }}
                disabled={deletingMapping} className="tab btn-small text-red-300" aria-label="Delete saved mapping" data-testid="delete-mapping"><Trash2 size={12}/></button>}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {/* Real-usage finding: the field's own label (e.g. "Project
                Name") and the spreadsheet column now filling it (e.g.
                "ProjectTitle") rendered in the same plain input styling —
                nothing distinguished "this is the app's field" from "this
                is what your file actually filled it with," so scanning a
                long mapping list for what's done vs. still needs attention
                meant reading every dropdown's text individually. Each
                field now gets a colored left accent (green = mapped, amber
                = still-required, red = colliding with another field) and
                the select itself highlights the same way once it holds a
                real value, so mapped/unmapped/conflicting state reads at a
                glance instead of by re-reading every row. */}
            {visibleFields.map(f => {
              const cols = mapping[f.k]?.length ? mapping[f.k] : [''];
              const isMapped = cols.some(Boolean);
              const isColliding = cols.some(c => c && columnUsage[c]?.length > 1);
              const isMissingRequired = f.required && !isMapped;
              const cardAccent = isColliding ? 'var(--accent-red)' : isMissingRequired ? 'var(--accent-amber)' : isMapped ? 'var(--accent-green)' : 'var(--border-strong)';
              const cardBg = isColliding ? 'rgba(230,103,103,0.06)' : isMissingRequired ? 'rgba(201,133,0,0.06)' : isMapped ? 'rgba(25,158,112,0.06)' : 'transparent';
              return (
                <div key={f.k} className="p-3 rounded" style={{borderLeft:`3px solid ${cardAccent}`, background:cardBg}}>
                  <Field label={f.l + (f.required?' *':'')}>
                    {cols.map((col, i) => {
                      const collides = col && columnUsage[col]?.length > 1;
                      const filled = !!col;
                      return (
                        <div key={i} className="flex items-center gap-1 mb-1">
                          <select className="input" style={filled ? {borderColor: collides?'var(--accent-red)':'var(--accent-green)', color: collides?'var(--accent-red)':'var(--accent-green)', fontWeight:600, background: collides?'rgba(230,103,103,0.1)':'rgba(25,158,112,0.1)'} : undefined} value={col} onChange={e=>{ const next=[...cols]; next[i]=e.target.value; setFieldColumns(f.k, next); }} data-testid={i===0?`map-${f.k}`:undefined}>
                            <option value="">— skip —</option>
                            {headers.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                          {cols.length > 1 && <button type="button" onClick={()=>removeColumnFromField(f.k, i)} className="tab btn-small text-red-300"><X size={12}/></button>}
                        </div>
                      );
                    })}
                    <button type="button" onClick={()=>addColumnToField(f.k)} className="text-xs text-muted hover:text-[color:var(--gold)]">+ combine column</button>
                  </Field>
                  {isMapped && !isColliding && <div className="text-xs mt-1.5 flex items-center gap-1" style={{color:'var(--accent-green)'}}><CheckCircle size={12}/>Mapped</div>}
                  {isColliding && <div className="text-xs mt-1.5 flex items-center gap-1" style={{color:'var(--accent-red)'}}><X size={12}/>Same column used by another field too</div>}
                  {isMissingRequired && <div className="text-xs mt-1.5" style={{color:'var(--accent-amber)'}}>Required — not yet mapped</div>}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded surface-2">
            <input className="input" style={{width:220}} placeholder="Name this mapping to save it" value={mappingName} onChange={e=>setMappingName(e.target.value)} data-testid="mapping-name"/>
            <button onClick={saveMappingAs} disabled={mappingSaving} className="btn-outline btn-small">{mappingSaving ? 'Saving…' : 'Save Mapping'}</button>
          </div>

          <div className="flex justify-between">
            <button onClick={reset} className="btn-outline btn-small inline-flex items-center gap-1"><ArrowLeft size={14}/>Back</button>
            <button onClick={doPreview} disabled={busy || (needsPicker && !lockedProject && !companyId)} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="excel-preview">{busy?'…':<>Preview<ArrowRight size={14}/></>}</button>
          </div>
        </div>
        );
      })()}

      {step===3 && preview && (() => {
        const dupeWarnings = preview.warnings.filter(w => /duplicate|already exists|repeated within this file/i.test(w));
        const otherWarnings = preview.warnings.filter(w => !dupeWarnings.includes(w));
        const needsAck = dupeWarnings.length > 0 && !dupeAck;
        // User-requested: a total the user can sanity-check against the
        // source spreadsheet's own total before committing -- summed from
        // every mapped row in the file (buildRows(), not just the ones
        // that would actually insert, since preview carries no per-row
        // success/failure list to filter by, only aggregate counts). Non-
        // numeric/blank amounts count as 0 rather than making the whole
        // total NaN, matching how a real mismapped column would show up
        // as an error on that row anyway.
        const amountKey = IMPORT_AMOUNT_FIELDS[entity];
        const totalAmount = amountKey ? buildRows().reduce((sum, r) => { const n = parseFloat(r[amountKey]); return sum + (isNaN(n) ? 0 : n); }, 0) : null;
        return (
        <div>
          <div className={"grid grid-cols-1 gap-3 mb-4 " + (amountKey ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
            <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Would insert</div><div className="serif text-2xl text-[color:var(--gold)]">{preview.inserted}</div></div>
            <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Errors</div><div className="serif text-2xl">{preview.errors.length}</div></div>
            <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Warnings</div><div className="serif text-2xl">{preview.warnings.length}</div></div>
            {amountKey && <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Total {IMPORT_TARGETS[entity].fields.find(f=>f.k===amountKey)?.l || 'Amount'} (this file)</div><div className="serif text-2xl text-[color:var(--gold)] mono">{fmtINR(totalAmount)}</div></div>}
          </div>
          {preview.errors.length > 0 && (
            <div className="card card-dark mb-3">
              <div className="text-xs text-red-400 mb-2">Errors (rows will be skipped):</div>
              <div className="text-xs mono max-h-32 overflow-y-auto space-y-1">{preview.errors.slice(0,20).map((e,i)=><div key={i}>{e}</div>)}</div>
              {/* Round 24 finding: the stat tile above shows the true count, but this list silently capped at 20 with nothing telling the user more exist — a real import with 20+ problem rows (very plausible after rounds of added per-row validation) let a user believe they'd reviewed everything. */}
              {preview.errors.length > 20 && <div className="text-xs text-muted mt-1">+{preview.errors.length - 20} more not shown</div>}
            </div>
          )}
          {dupeWarnings.length > 0 && (
            <div className="card card-dark mb-3" style={{border:'1px solid rgba(230,167,103,0.4)'}}>
              <div className="text-xs text-[color:var(--gold)] mb-2 font-semibold">Possible duplicates found ({dupeWarnings.length}) — clean the source data first if these aren't intentional:</div>
              <div className="text-xs mono max-h-32 overflow-y-auto space-y-1">{dupeWarnings.slice(0,20).map((w,i)=><div key={i}>{w}</div>)}</div>
              {dupeWarnings.length > 20 && <div className="text-xs text-muted mt-1">+{dupeWarnings.length - 20} more not shown</div>}
              <label className="flex items-center gap-2 text-xs mt-3 pt-2 cursor-pointer" style={{borderTop:'1px solid var(--border-subtle)'}}>
                <input type="checkbox" checked={dupeAck} onChange={e=>setDupeAck(e.target.checked)} data-testid="dupe-ack"/>
                I've reviewed these duplicates and want to import anyway
              </label>
            </div>
          )}
          {otherWarnings.length > 0 && (
            <div className="card card-dark mb-3">
              <div className="text-xs text-[color:var(--gold)] mb-2">Warnings:</div>
              <div className="text-xs mono max-h-32 overflow-y-auto space-y-1">{otherWarnings.slice(0,20).map((w,i)=><div key={i}>{w}</div>)}</div>
              {otherWarnings.length > 20 && <div className="text-xs text-muted mt-1">+{otherWarnings.length - 20} more not shown</div>}
            </div>
          )}
          <div className="flex justify-between">
            <button onClick={()=>setStep(2)} className="btn-outline btn-small inline-flex items-center gap-1"><ArrowLeft size={14}/>Adjust Mapping</button>
            <button onClick={commit} disabled={busy || preview.inserted===0 || needsAck} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="excel-commit" title={needsAck ? 'Acknowledge the duplicate warnings above first' : undefined}>{busy?'Importing…':<><CheckCircle size={14}/>Confirm & Import</>}</button>
          </div>
        </div>
        );
      })()}

      {step===4 && preview && (
        <div>
          <div className="text-center py-6">
            {preview.inserted === 0 && (preview.errors.length > 0 || preview.warnings.length > 0) ? (
              <>
                <X size={40} className="mx-auto text-red-400 mb-3"/>
                <div className="serif text-2xl mb-2">Import Rejected</div>
                <div className="text-muted">No partial import — every row in this file had a problem (see the reasons below), so nothing was saved.</div>
              </>
            ) : (
              <>
                <CheckCircle size={40} className="mx-auto text-[color:var(--gold)] mb-3"/>
                <div className="serif text-2xl mb-2">Import Complete</div>
                <div className="text-muted">{preview.inserted} rows inserted · {preview.errors.length} errors · {preview.warnings.length} warnings</div>
              </>
            )}
          </div>
          {(preview.errors.length > 0 || preview.warnings.length > 0) && (
            <div className="text-xs mono space-y-1 p-3 rounded surface-2 mb-4 max-h-64 overflow-y-auto">
              {preview.errors.map((e,i) => <div key={'e'+i} className="text-red-400">{e}</div>)}
              {preview.warnings.map((w,i) => <div key={'w'+i} className="text-amber-400">{w}</div>)}
            </div>
          )}
          <div className="flex justify-center gap-2"><button onClick={reset} className="btn-outline btn-small">Import Another</button></div>
        </div>
      )}
    </div>
  );
}


// ============= SETTINGS (Companies + Bank Accounts) =============
function Settings() {
  const [tab, setTab] = usePersistedState('Settings.tab', 'companies');
  return (
    <div className="max-w-6xl mx-auto px-6 py-8 fade-up">
      <div className="section-eyebrow mb-2">Master Data</div>
      <h1 className="serif text-4xl mb-6">Settings</h1>
      <div className="flex gap-2 mb-6">
        <button className={"tab "+(tab==='companies'?'active':'')} onClick={()=>setTab('companies')}>Companies</button>
        <button className={"tab "+(tab==='banks'?'active':'')} onClick={()=>setTab('banks')}>Bank Accounts</button>
        <button className={"tab "+(tab==='masters'?'active':'')} onClick={()=>setTab('masters')}>Masters</button>
        <button className={"tab "+(tab==='profitrules'?'active':'')} onClick={()=>setTab('profitrules')}>Profit Sharing</button>
        <button className={"tab "+(tab==='expensecats'?'active':'')} onClick={()=>setTab('expensecats')}>Expense Categories</button>
        {/* Real-usage-gap finding: partners are master data (name/phone/
            email/company grants, no financial figures) — moved here from
            its own top-level nav item; profit/ledger data now lives at
            the new "Partner Settlement" tab instead. */}
        <button className={"tab "+(tab==='partners'?'active':'')} onClick={()=>setTab('partners')}>Partners</button>
      </div>
      {tab==='companies' && <CompaniesPanel/>}
      {tab==='banks' && <BankAccountsPanel/>}
      {tab==='masters' && <MastersPanel/>}
      {tab==='profitrules' && <ProfitSharingRulesPanel/>}
      {tab==='expensecats' && <SettingsExpenseCategoriesPanel/>}
      {tab==='partners' && <Partners/>}
    </div>
  );
}

const MASTER_TYPE_LABELS = {
  department_client: 'Department / Client',
  deduction_type: 'Deduction',
  work_category: 'Work Category',
};
// A custom type (anything beyond the 3 above + Flags) has no hardcoded
// label — humanize its raw slug for display instead.
const masterTypeLabel = (t) => MASTER_TYPE_LABELS[t] || t.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase());

function MastersPanel() {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [type, setType] = useState('department_client');
  const [customTypes, setCustomTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [q, setQ] = useState('');
  const [newTypeOpen, setNewTypeOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteCompanyId, setPasteCompanyId] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [companyFilter, setCompanyFilter] = useState('');
  const [pendingType, setPendingType] = useState(null); // a brand-new type not yet saved anywhere
  const [priorType, setPriorType] = useState(null); // where to snap back to if the first entry is cancelled
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [saving, setSaving] = useState(false);
  const { companies, needsPicker } = useMyCompanies();
  const isDeptClient = type === 'department_client';
  const isDeductionType = type === 'deduction_type';
  const isCustomType = customTypes.includes(type);
  const load = async () => setItems((await api.get('/masters', { params: { type } })).data);
  const loadTypes = async () => setCustomTypes((await api.get('/masters/types')).data.custom);
  useEffect(() => { if (type !== 'flags') load(); setSelected([]); }, [type]);
  useEffect(() => { loadTypes(); }, []);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        type, name: form.name, status: form.status || 'active', company_id: form.company_id,
        company_ids: form.company_ids || [],
        // Audit finding: this hand-built payload never included parent_id
        // (used for a hierarchy like expense_head -> expense_sub_head; see
        // update_master), even though openEdit loads the FULL row
        // (setForm(m)) which carries it when set. Any master given a
        // parent had that link silently severed the next time it was
        // renamed or had its status flipped, with no error and no UI
        // control to even see or restore it -- same full-object-PATCH bug
        // class as the company_ids fix just above. `form.parent_id` is
        // undefined for every row that never had one, so this is a no-op
        // for the common case.
        parent_id: form.parent_id ?? null,
      };
      if (isDeptClient) {
        payload.client_code = form.client_code || null;
        payload.client_organisation = form.client_organisation || null;
        payload.phone = form.phone || null;
      }
      if (isDeductionType) payload.default_classification = form.default_classification || null;
      if (form.id) {
        // Whole-app final-check finding: same as Partners/Admin -- /api/masters
        // now correctly narrows company_ids to what THIS caller can see, but
        // the backend treats an included array as authoritative (omitted =
        // untouched). Resending the loaded (narrowed) array on every edit
        // silently stripped a shared master's access to companies the
        // editing admin couldn't see. Only send it when actually changed.
        const original = items.find(i => i.id === form.id);
        const origIds = original?.company_ids || [];
        const formIds = form.company_ids || [];
        const sameIds = origIds.length === formIds.length && origIds.every(x => formIds.includes(x));
        if (sameIds) delete payload.company_ids;
        await api.patch(`/masters/${form.id}`, payload);
      } else await api.post('/masters', payload);
      setOpen(false); setForm({}); setPendingType(null); load(); loadTypes(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const closeAddModal = () => {
    setOpen(false);
    // Cancelling a brand-new type's very first entry — nothing was ever
    // saved, so snap the tab bar back to wherever the user actually was
    // instead of leaving `type` pointed at a slug with no tab to show it.
    if (pendingType && type === pendingType) {
      setType(priorType || 'department_client');
      setPendingType(null);
    }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/masters/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} selected ${masterTypeLabel(type).toLowerCase()} entries? This cannot be undone.`)) return;
    setBulkDeleting(true);
    // Whole-app final-check finding: a single shared try/catch around the
    // whole loop meant one reference-blocked entry (e.g. still set as a
    // project's Department/Client) stopped everything after it dead --
    // already-deleted entries stayed silently gone with no record of
    // which, remaining entries were never even attempted, and
    // setSelected([])/load() (both inside the same try) never ran, so the
    // list kept showing all of them, still checked, as if nothing had
    // happened -- a retry would then immediately 404 on the ones already
    // deleted and stop again at the same point. Each delete now runs
    // independently so one failure can't block the rest, and the list
    // always refreshes with an accurate summary of what actually happened.
    const failed = [];
    for (const id of selected) {
      try { await api.delete(`/masters/${id}`); }
      catch (e) { failed.push({ id, detail: e.response?.data?.detail || 'Error' }); }
    }
    const succeededCount = selected.length - failed.length;
    if (failed.length === 0) toast.success(`Deleted ${succeededCount}`);
    else if (succeededCount === 0) toast.error(`None deleted — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    else toast.warning(`Deleted ${succeededCount}, ${failed.length} blocked — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    setSelected(failed.map(f => f.id));
    load();
    setBulkDeleting(false);
  };
  const pasteMultiple = async () => {
    const names = [...new Set(pasteText.split('\n').map(s=>s.trim()).filter(Boolean))];
    if (!names.length) { toast.error('Paste at least one name, one per line'); return; }
    if (needsPicker && !pasteCompanyId) { toast.error('Choose a company first'); return; }
    const existing = new Set(items.map(m=>m.name.toLowerCase()));
    const toCreate = names.filter(n => !existing.has(n.toLowerCase()));
    const skipped = names.length - toCreate.length;
    setPasteBusy(true);
    let created = 0;
    const failed = [];
    for (const name of toCreate) {
      try {
        await api.post('/masters', { type, name, status: 'active', company_id: pasteCompanyId || companies[0]?.id });
        created++;
      } catch (e) { failed.push({ name, detail: e.response?.data?.detail || 'Error' }); }
    }
    setPasteBusy(false);
    setPasteOpen(false); setPasteText(''); load();
    const skippedMsg = skipped ? `, skipped ${skipped} duplicate${skipped===1?'':'s'}` : '';
    if (!failed.length) toast.success(`Added ${created}${skippedMsg}`);
    else toast.warning(`Added ${created}${skippedMsg}, ${failed.length} failed — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
  };
  const createType = () => {
    const slug = newTypeName.trim().toLowerCase().replace(/[^a-z0-9]+/g,'_').replace(/^_+|_+$/g,'');
    if (!slug) { toast.error('Enter a name for the new master list'); return; }
    if ([...Object.keys(MASTER_TYPE_LABELS), ...customTypes, 'flags'].includes(slug)) { toast.error('A master list with that name already exists'); return; }
    setNewTypeOpen(false); setNewTypeName('');
    setPriorType(type); setPendingType(slug);
    setType(slug);
    setForm({status:'active'}); setOpen(true);
  };
  const doRename = async () => {
    const slug = renameValue.trim().toLowerCase().replace(/[^a-z0-9]+/g,'_').replace(/^_+|_+$/g,'');
    if (!slug) { toast.error('Enter a name'); return; }
    try {
      await api.patch(`/masters/types/${type}`, { new_type: slug });
      setRenameOpen(false); setType(slug); loadTypes();
      toast.success('Renamed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteType = async () => {
    if (!window.confirm(`Delete the entire "${masterTypeLabel(type)}" master list and all ${items.length} of its entries? This cannot be undone.`)) return;
    try {
      await api.delete(`/masters/types/${type}`);
      toast.success('Master list deleted');
      setType('department_client'); loadTypes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  const filtered = items
    .filter(m => !companyFilter || (m.company_ids||[]).includes(companyFilter))
    .filter(m => {
      if (!q.trim()) return true;
      const s = q.trim().toLowerCase();
      return m.name?.toLowerCase().includes(s) || m.client_code?.toLowerCase().includes(s) || m.client_organisation?.toLowerCase().includes(s) || m.phone?.includes(s);
    });
  return (
    <div className="card card-dark">
      {/* Mobile audit fix: up to 5 buttons here (Add/Paste Multiple/Rename
          List/Delete List/New Master List) in a non-wrapping row -- same
          overflow class as the CrudPanel/ReceiptsPanel toolbar bug fixed
          earlier, worse here since there are more buttons. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Masters</div>
          <div className="text-xs text-muted mt-1">Shared across your company's business group. Used as the pick-lists for project and expense entry.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && type !== 'flags' && <button onClick={()=>{setForm({status:'active', company_ids: companies.length===1?[companies[0].id]:[]});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add</button>}
          {canEdit && !isDeptClient && type !== 'flags' && <button onClick={()=>{setPasteCompanyId(companies.length===1?companies[0].id:'');setPasteOpen(true);}} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="paste-multiple"><Upload size={14}/>Paste Multiple</button>}
          {canEdit && isCustomType && <button onClick={()=>{setRenameValue(masterTypeLabel(type));setRenameOpen(true);}} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="rename-master-type"><Edit3 size={14}/>Rename List</button>}
          {isMasterAdmin(user) && isCustomType && <button onClick={deleteType} className="btn-outline btn-small text-red-300 inline-flex items-center gap-1" data-testid="delete-master-type"><Trash2 size={14}/>Delete List</button>}
          {canEdit && <button onClick={()=>setNewTypeOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="new-master-type"><Plus size={14}/>New Master List</button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.keys(MASTER_TYPE_LABELS).map(t => (
          <button key={t} className={"tab btn-small "+(type===t?'active':'')} onClick={()=>setType(t)}>{masterTypeLabel(t)}</button>
        ))}
        {customTypes.map(t => (
          <button key={t} className={"tab btn-small "+(type===t?'active':'')} onClick={()=>setType(t)}>{masterTypeLabel(t)}</button>
        ))}
        {pendingType && !customTypes.includes(pendingType) && (
          <button key={pendingType} className="tab btn-small active" style={{opacity:0.75}} title="Not saved yet — add its first entry to create it">{masterTypeLabel(pendingType)} (new)</button>
        )}
        <button className={"tab btn-small "+(type==='flags'?'active':'')} onClick={()=>setType('flags')}>Flags</button>
      </div>
      {type==='flags' ? <FlagsPanel/> : (
      <>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {items.length > 6 && <input className="input" style={{maxWidth:320}} placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)} data-testid="masters-search"/>}
        {needsPicker && <select className="input" style={{width:170}} value={companyFilter} onChange={e=>setCompanyFilter(e.target.value)} data-testid="masters-filter-company">
          <option value="">All Companies</option>
          {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>}
        {isMasterAdmin(user) && selected.length > 0 && <button onClick={bulkDelete} disabled={bulkDeleting} className="btn-small tab text-red-300 inline-flex items-center gap-1"><Trash2 size={12}/>Delete {selected.length} selected</button>}
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr>
          {isMasterAdmin(user) && <th></th>}
          <th>{isDeptClient ? 'Client Name' : 'Name'}</th>
          {isDeptClient && <><th>Client Code</th><th>Organisation</th><th>Phone</th></>}
          {isDeductionType && <th>Default Classification</th>}
          {needsPicker && <th>Companies</th>}
          <th>Status</th>{canEdit && <th></th>}
        </tr></thead>
        <tbody>
          {filtered.length===0 && <tr><td colSpan={8} className="text-center text-muted py-6">{(q || companyFilter) ? 'No matches.' : 'None yet.'}</td></tr>}
          {filtered.map(m => (
            <tr key={m.id}>
              {isMasterAdmin(user) && <td><input type="checkbox" checked={selected.includes(m.id)} onChange={()=>toggleSelect(m.id)} aria-label={`Select ${m.name}`}/></td>}
              <td>{m.name}</td>
              {isDeptClient && <>
                <td className="text-xs">{m.client_code||'—'}</td>
                <td className="text-xs">{m.client_organisation||'—'}</td>
                <td className="text-xs">{m.phone||'—'}</td>
              </>}
              {isDeductionType && <td className="text-xs">{DEDUCTION_CLASSIFICATION_LABELS[m.default_classification]||'—'}</td>}
              {needsPicker && <td className="text-xs">{(m.company_ids||[]).map(companyName).join(', ')||'—'}</td>}
              <td><span className={"badge "+(m.status==='active'?'status-running':'status-closed')}>{m.status}</span></td>
              {canEdit && <td className="text-right whitespace-nowrap">
                <button aria-label="Edit" onClick={()=>{setForm(m);setOpen(true);}} className="tab btn-small mr-1"><Edit3 size={12}/></button>
                {isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(m.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <Modal open={open} onClose={closeAddModal} title={`${form.id?'Edit':'Add'} — ${masterTypeLabel(type)}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {needsPicker ? (
            <div className="col-span-2"><Field label="Companies *"><div data-testid="master-companies">
              <GrantPicker
                items={companies}
                selectedIds={form.company_ids || []}
                allAccess={false}
                onToggle={cid => setForm(f => { const cur = f.company_ids||[]; return {...f, company_ids: cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid]}; })}
                onSetAllAccess={()=>{}}
                canGrantAll={false}
                allLabel="All Companies"
              />
            </div></Field></div>
          ) : (
            <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker}/>
          )}
          <Field label={isDeptClient ? 'Client Name *' : 'Name *'}><input className="input" value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
          {isDeptClient && <>
            <Field label="Client Code"><input className="input" value={form.client_code||''} onChange={e=>setForm({...form, client_code:e.target.value})}/></Field>
            <Field label="Client Organisation"><input className="input" value={form.client_organisation||''} onChange={e=>setForm({...form, client_organisation:e.target.value})}/></Field>
            <Field label="Phone"><input className="input" value={form.phone||''} onChange={e=>setForm({...form, phone:e.target.value})}/></Field>
          </>}
          {isDeductionType && <Field label="Default Classification"><select className="input" value={form.default_classification||''} onChange={e=>setForm({...form, default_classification:e.target.value})}>
            <option value="">— none —</option>
            {Object.entries(DEDUCTION_CLASSIFICATION_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
          </select></Field>}
          <Field label="Status"><select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}>
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select></Field>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={closeAddModal} className="btn-outline btn-small">Cancel</button>
          <button onClick={save} disabled={saving || !form.name?.trim() || !(form.company_ids||[]).length} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      </>
      )}
      <Modal open={newTypeOpen} onClose={()=>setNewTypeOpen(false)} title="New Master List">
        <p className="text-xs text-muted mb-3">Creates a brand-new pick-list (e.g. "Vendor Grade") — it'll show up as its own tab here, and its first entry opens right after.</p>
        <Field label="Master List Name *"><input className="input" value={newTypeName} onChange={e=>setNewTypeName(e.target.value)} placeholder="e.g. Vendor Grade" autoFocus/></Field>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setNewTypeOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={createType} disabled={!newTypeName.trim()} className="btn-gold btn-small">Create</button></div>
      </Modal>
      <Modal open={renameOpen} onClose={()=>setRenameOpen(false)} title={`Rename "${masterTypeLabel(type)}"`}>
        <p className="text-xs text-muted mb-3">Renames the whole list — every entry in it keeps its data, only the list's name changes.</p>
        <Field label="New Name *"><input className="input" value={renameValue} onChange={e=>setRenameValue(e.target.value)} autoFocus/></Field>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setRenameOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={doRename} disabled={!renameValue.trim()} className="btn-gold btn-small">Rename</button></div>
      </Modal>
      <Modal open={pasteOpen} onClose={()=>setPasteOpen(false)} title={`Paste Multiple — ${masterTypeLabel(type)}`}>
        <p className="text-xs text-muted mb-3">One name per line. Existing names (case-insensitive) are skipped automatically.</p>
        {needsPicker && <div className="mb-3"><Field label="Company *"><select className="input" value={pasteCompanyId} onChange={e=>setPasteCompanyId(e.target.value)}>
          <option value="">— select —</option>
          {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select></Field></div>}
        <Field label="Names"><textarea className="input" rows={8} placeholder={"Labour Cess\nSite Survey\nWatchman Wages"} value={pasteText} onChange={e=>setPasteText(e.target.value)} data-testid="paste-textarea"/></Field>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setPasteOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={pasteMultiple} disabled={pasteBusy || !pasteText.trim()} className="btn-gold btn-small" data-testid="paste-submit">{pasteBusy?'Adding…':'Add All'}</button></div>
      </Modal>
    </div>
  );
}

const FLAG_SWATCHES = ['#c9a84c','#e5484d','#3e9b5c','#4a90d9','#a855f7','#f59e0b','#64748b'];

function FlagsPanel() {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  // Full-app audit finding: every other delete in this app (bank accounts,
  // masters, expense categories, security instruments, bills, partners,
  // vendors, staff, projects, profiles, ...) is master_admin-only -- Flags
  // was the sole outlier at plain admin. Split from canEdit (which still
  // gates Add/Edit, unchanged) so a Company Admin keeps managing flags day
  // to day but can no longer permanently delete a definition shared across
  // companies.
  const canDelete = isMasterAdmin(user);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { companies, needsPicker } = useMyCompanies();
  const load = async () => setItems((await api.get('/flags')).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description || null, color: form.color || '#c9a84c', status: form.status || 'active', company_ids: form.company_ids || [] };
      if (form.id) await api.patch(`/flags/${form.id}`, payload); else await api.post('/flags', payload);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this flag?')) return;
    try { await api.delete(`/flags/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs text-muted mt-1">Attach any number of these to a project, bill, expense, or receipt — e.g. Urgent, Disputed, Needs Follow-Up — to mark attention. Add a description so anyone ticking one knows exactly what it means.</div>
        </div>
        {canEdit && <button onClick={()=>{setForm({status:'active', color:'#c9a84c', company_ids: companies.length===1?[companies[0].id]:[]});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Flag</button>}
      </div>
      <div className="flex flex-wrap gap-2">
        {items.length===0 && <div className="text-muted text-sm">No flags yet.</div>}
        {items.map(f => (
          <div key={f.id} className="flex items-center gap-2 px-3 py-2 rounded-full border border-subtle" style={{opacity: f.status==='active'?1:0.5}} title={f.description || undefined}>
            <span style={{width:10,height:10,borderRadius:'50%',background:f.color,display:'inline-block'}}/>
            <span className="text-sm">{f.name}</span>
            {f.description && <span className="text-xs text-muted">— {f.description}</span>}
            {needsPicker && <span className="text-xs text-muted">({(f.company_ids||[]).map(cid=>companies.find(c=>c.id===cid)?.name).filter(Boolean).join(', ')||'—'})</span>}
            {canEdit && <button aria-label="Edit" onClick={()=>{setForm(f);setOpen(true);}} className="text-muted hover:text-[color:var(--gold)]"><Edit3 size={12}/></button>}
            {canDelete && <button aria-label="Delete" onClick={()=>del(f.id)} className="text-muted hover:text-red-400"><Trash2 size={12}/></button>}
          </div>
        ))}
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Flag':'Add Flag'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {needsPicker ? (
            <div className="col-span-2"><Field label="Companies *"><div data-testid="flag-companies">
              <GrantPicker
                items={companies}
                selectedIds={form.company_ids || []}
                allAccess={false}
                onToggle={cid => setForm(f => { const cur = f.company_ids||[]; return {...f, company_ids: cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid]}; })}
                onSetAllAccess={()=>{}}
                canGrantAll={false}
                allLabel="All Companies"
              />
            </div></Field></div>
          ) : (
            <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker}/>
          )}
          <Field label="Name *"><input className="input" placeholder="e.g. URGENT, Need Follow-Up" value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
          <Field label="Status"><select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}>
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select></Field>
          <div className="col-span-2"><Field label="Description"><textarea className="input" rows={2} placeholder={'What does ticking this flag mean? e.g. "Needs same-day review before payment."'} value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field></div>
          <div className="col-span-2"><Field label="Colour">
            <div className="flex gap-2 items-center flex-wrap">
              {FLAG_SWATCHES.map(c => <button key={c} onClick={()=>setForm({...form, color:c})} style={{width:26,height:26,borderRadius:'50%',background:c,border: form.color===c ? '2px solid var(--ink,#fff)' : '2px solid transparent', cursor:'pointer'}}/>)}
              <input type="color" value={form.color||'#c9a84c'} onChange={e=>setForm({...form, color:e.target.value})} style={{width:34,height:26,padding:0,border:'none',background:'none'}}/>
            </div>
          </Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving || !form.name?.trim() || !(form.company_ids||[]).length} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

// Real-usage-gap finding: generalized from the original company-only
// "Overhead Categories" panel so the same CRUD/UI now also backs the
// project- and company-expense spend-type lists (previously a fixed,
// uneditable EXPENSE_CATS array) — see the backend's
// ExpenseCategoryIn.scope comment for why these are 3 separate,
// non-overlapping scopes. scope='overhead' is the original, unchanged
// concept (still linked via expenses/receipts.overhead_category_id);
// scope='project'/'company' are the new spend-type lists.
function ExpenseCategoriesPanel({ scope }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  // Multi-company assignment (like Partners/Masters) applies to every
  // scope, including 'overhead' -- a category (Overhead or spend-type)
  // can be shared across more than one of your companies.
  const multiCompany = true;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [companyFilter, setCompanyFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteCompanyId, setPasteCompanyId] = useState('');
  const [pasteCompanyIds, setPasteCompanyIds] = useState([]);
  const [pasteBusy, setPasteBusy] = useState(false);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  const load = async () => setItems((await api.get('/expense-categories', { params: { scope } })).data);
  useEffect(() => { load(); }, [scope]);
  const visibleItems = items.filter(c => !companyFilter || (multiCompany ? (c.company_ids||[]).includes(companyFilter) : c.company_id === companyFilter));
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      let payload = { name: form.name, description: form.description||null, status: form.status||'active', scope };
      if (multiCompany) {
        payload.company_ids = form.company_ids;
        // Same "only send if actually changed" guard as Partners/Masters --
        // an admin with narrower visibility than the category's full grant
        // set must never silently strip companies they can't even see.
        if (form.id) {
          const original = items.find(i => i.id === form.id);
          const origIds = original?.company_ids || [];
          const formIds = form.company_ids || [];
          const sameIds = origIds.length === formIds.length && origIds.every(x => formIds.includes(x));
          if (sameIds) payload.company_ids = undefined; else payload.company_id = (form.company_ids||[])[0];
        } else {
          payload.company_id = (form.company_ids||[])[0];
        }
      } else {
        payload.company_id = form.company_id;
      }
      if (form.id) await api.patch(`/expense-categories/${form.id}`, payload); else await api.post('/expense-categories', payload);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await api.delete(`/expense-categories/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  // Same "one name per line, skip live duplicates" pattern as MastersPanel's
  // own Paste Multiple, extended for multi-company scopes: pick several
  // companies at once and each pasted name becomes ONE category shared
  // across all of them (company_ids), matching how the Add Category form's
  // own GrantPicker already works — not one duplicate row per company.
  const pasteMultiple = async () => {
    const names = [...new Set(pasteText.split('\n').map(s=>s.trim()).filter(Boolean))];
    if (!names.length) { toast.error('Paste at least one name, one per line'); return; }
    const cids = multiCompany ? pasteCompanyIds : (pasteCompanyId ? [pasteCompanyId] : []);
    if (needsPicker && !cids.length) { toast.error('Choose at least one company first'); return; }
    const targetCids = cids.length ? cids : [companies[0]?.id];
    // Skip a name only if it already exists sharing ANY of the target
    // companies — same "don't create a confusing overlapping duplicate"
    // intent as the single-company version.
    const existing = new Set(
      items.filter(c => (multiCompany ? (c.company_ids||[]) : [c.company_id]).some(id => targetCids.includes(id))).map(c=>c.name.toLowerCase())
    );
    const toCreate = names.filter(n => !existing.has(n.toLowerCase()));
    const skipped = names.length - toCreate.length;
    setPasteBusy(true);
    let created = 0;
    const failed = [];
    for (const name of toCreate) {
      try {
        await api.post('/expense-categories', multiCompany ? { name, scope, status: 'active', company_ids: targetCids } : { name, scope, status: 'active', company_id: targetCids[0] });
        created++;
      } catch (e) { failed.push({ name, detail: e.response?.data?.detail || 'Error' }); }
    }
    setPasteBusy(false);
    setPasteOpen(false); setPasteText(''); load();
    const skippedMsg = skipped ? `, skipped ${skipped} duplicate${skipped===1?'':'s'}` : '';
    if (!failed.length) toast.success(`Added ${created}${skippedMsg}`);
    else toast.warning(`Added ${created}${skippedMsg}, ${failed.length} failed — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
  };
  return (
    <div className="card card-dark">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">{scope==='project' ? 'Project Expense Categories' : scope==='company' ? 'Company Expense Categories' : 'Overhead Categories'}</div>
          <div className="text-xs text-muted mt-1">{scope==='project'
            ? "The Category options offered when entering an expense against a project (material, labour, transport, etc.) — edit, rename, or add your own. A category can be shared across more than one of your companies."
            : scope==='company'
            ? "The Category options offered when entering a Company Expense (material, labour, transport, etc.) — edit, rename, or add your own. A category can be shared across more than one of your companies."
            : "Which overhead bucket a Company Expense belongs to (Legal, R&D, Salary, Tax Filing, Misc.) — not tied to a project, and never counted against a project's profit."}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {needsPicker && <select className="input" style={{width:170}} value={companyFilter} onChange={e=>setCompanyFilter(e.target.value)} data-testid="oc-filter-company">
            <option value="">All Companies</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>}
          {canEdit && multiCompany && <button onClick={()=>{setPasteCompanyIds(companies.length===1?[companies[0].id]:[]);setPasteOpen(true);}} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="expense-category-paste-multiple"><Upload size={14}/>Paste Multiple</button>}
          {canEdit && <button onClick={()=>{setForm({status:'active', company_ids: companies.length===1?[companies[0].id]:[]});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Category</button>}
        </div>
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Name</th><th>Description</th>{needsPicker && <th>{multiCompany ? 'Companies' : 'Company'}</th>}<th>Status</th>{canEdit && <th></th>}</tr></thead>
        <tbody>
          {visibleItems.length===0 && <tr><td colSpan={4} className="text-center text-muted py-6">{items.length===0 ? 'None yet.' : 'No categories match this filter.'}</td></tr>}
          {visibleItems.map(c => (
            <tr key={c.id}>
              <td>{c.name}</td><td className="text-xs text-muted">{c.description||'—'}</td>
              {needsPicker && <td className="text-xs">{multiCompany ? ((c.company_ids||[]).map(companyName).join(', ')||'—') : companyName(c.company_id)}</td>}
              <td><span className={"badge "+(c.status==='active'?'status-running':'status-closed')}>{c.status}</span></td>
              {canEdit && <td className="text-right whitespace-nowrap">
                <button aria-label="Edit" onClick={()=>{setForm(c);setOpen(true);}} className="tab btn-small mr-1"><Edit3 size={12}/></button>
                {isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(c.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Category':'Add Category'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {multiCompany && needsPicker ? (
            <div className="col-span-2"><Field label="Companies *"><div data-testid="expense-category-companies">
              <GrantPicker
                items={companies}
                selectedIds={form.company_ids || []}
                allAccess={false}
                onToggle={cid => setForm(f => { const cur = f.company_ids||[]; return {...f, company_ids: cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid]}; })}
                onSetAllAccess={()=>{}}
                canGrantAll={false}
                allLabel="All Companies"
              />
            </div></Field></div>
          ) : (
            // Renders nothing when !needsPicker (single-company login) —
            // form.company_ids/company_id are pre-seeded on Add and already
            // present on the loaded row on Edit, so there's nothing to pick.
            <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker}/>
          )}
          <Field label="Name *"><input className="input" value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
          <Field label="Status"><select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}>
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select></Field>
          <div className="col-span-2"><Field label="Description"><input className="input" value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving || !form.name || (multiCompany && needsPicker && !(form.company_ids||[]).length)} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      <Modal open={pasteOpen} onClose={()=>setPasteOpen(false)} title={`Paste Multiple — ${scope==='project' ? 'Project Expense Categories' : 'Overhead Categories'}`}>
        {needsPicker && (multiCompany ? (
          <div className="mb-3"><Field label="Companies *"><div data-testid="expense-category-paste-companies">
            <GrantPicker
              items={companies}
              selectedIds={pasteCompanyIds}
              allAccess={false}
              onToggle={cid => setPasteCompanyIds(cur => cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid])}
              onSetAllAccess={()=>{}}
              canGrantAll={false}
              allLabel="All Companies"
            />
          </div></Field></div>
        ) : (
          <div className="mb-3"><Field label="Company *"><select className="input" value={pasteCompanyId} onChange={e=>setPasteCompanyId(e.target.value)}>
            <option value="">— select —</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select></Field></div>
        ))}
        <Field label="Names"><textarea className="input" rows={8} placeholder={"Cement\nSteel\nSite Transport"} value={pasteText} onChange={e=>setPasteText(e.target.value)} data-testid="expense-category-paste-textarea"/></Field>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setPasteOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={pasteMultiple} disabled={pasteBusy || !pasteText.trim()} className="btn-gold btn-small" data-testid="expense-category-paste-submit">{pasteBusy?'Adding…':'Add All'}</button></div>
      </Modal>
    </div>
  );
}

// Thin wrapper preserving the original name/call sites (the "Manage
// Categories" button on Company Expenses) — same component, original
// (now named 'overhead') scope, so it still shows only the admin-managed
// overhead buckets, not the new spend-type lists.
function OverheadCategoriesPanel() {
  return <ExpenseCategoriesPanel scope="overhead"/>;
}

// Project (spend-type, per project expense entries) and Overhead (which
// bucket a Company Expense belongs to -- the ONLY categorization Company
// Expenses has; the separate company-scope spend-type list was removed).
// Overhead here is the exact same list/component Company Expenses' own
// "Manage Categories" button opens, so both are managing one connected
// list, not two parallel ones.
function SettingsExpenseCategoriesPanel() {
  const [scope, setScope] = useState('project');
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button className={"tab "+(scope==='project'?'active':'')} onClick={()=>setScope('project')}>Project</button>
        <button className={"tab "+(scope==='overhead'?'active':'')} onClick={()=>setScope('overhead')}>Overhead</button>
      </div>
      <ExpenseCategoriesPanel scope={scope}/>
    </div>
  );
}

// ============= PROFIT SHARING RULES =============
function ProfitSharingRulesPanel() {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { companies, needsPicker } = useMyCompanies();
  const today = new Date().toISOString().slice(0,10);
  const load = async () => {
    const [r, p] = await Promise.all([api.get('/profit-sharing-rules'), api.get('/projects')]);
    setItems(r.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);
  const projectName = (id) => id ? (projects.find(p=>p.id===id)?.project_name || '—') : 'Company default';
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { project_id: form.project_id||null, company_id: form.company_id, rule_name: form.rule_name, money_pool_percentage: Number(form.money_pool_percentage??50), company_fund_percentage: Number(form.company_fund_percentage||0), interest_rate_percentage: Number(form.interest_rate_percentage||0), effective_from: form.effective_from||today, effective_to: form.effective_to||null, status: form.status||'active' };
      if (form.id) await api.patch(`/profit-sharing-rules/${form.id}`, payload); else await api.post('/profit-sharing-rules', payload);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await api.delete(`/profit-sharing-rules/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const moneyPct = form.money_pool_percentage!==undefined && form.money_pool_percentage!=='' ? Number(form.money_pool_percentage) : 50;
  const fundPct = form.company_fund_percentage!==undefined && form.company_fund_percentage!=='' ? Number(form.company_fund_percentage) : 0;
  const workPct = Math.max(0, 100 - moneyPct - fundPct);
  const RULE_TEMPLATES = [
    { label: '50 / 50 — Money + Work', money: 50, fund: 0 },
    { label: '60 / 40 — Money + Work', money: 60, fund: 0 },
    { label: '40 / 40 / 20 — + Company Fund', money: 40, fund: 20 },
  ];
  // The Money/Work split itself only ever does anything for a Money & Work
  // project — see _resolve_money_work_split, only called when
  // model === "money_work". Company Fund %, however, is a reserve that
  // now applies to EVERY partnership model (see _resolve_company_fund_pct)
  // — a project-targeted rule needs to say plainly which of the two
  // actually applies before showing fields that would otherwise look live
  // but do nothing.
  const targetProject = form.project_id ? projects.find(p => p.id === form.project_id) : null;
  const targetModel = targetProject ? (targetProject.partnership_model || 'equal_capital_bonus') : null;
  const showMoneyWorkFields = !targetProject || targetModel === 'money_work';
  return (
    <div className="card card-dark">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Profit Sharing Rules</div>
          <div className="text-xs text-muted mt-1">Company Fund % (a reserve held back before the partner split, toward company overhead) applies to every Partnership Model. The Money/Work split itself only applies to the Money & Work model — the other two models set their own split per-project on the project's own form. A project-specific rule overrides the company default; with no rule at all, there's no reserve and Money & Work falls back to 50/50.</div>
        </div>
        {canEdit && <button onClick={()=>{setForm({status:'active', effective_from:today, money_pool_percentage:50, company_fund_percentage:0});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Rule</button>}
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Rule Name</th><th>Scope</th><th>Money / Work / Fund Split</th><th>Effective</th><th>Status</th>{canEdit && <th></th>}</tr></thead>
        <tbody>
          {items.length===0 && <tr><td colSpan={6} className="text-center text-muted py-6">No rules configured — all projects use the 50/50 fallback.</td></tr>}
          {items.map(r => {
            const pools = r.pool_definitions||[];
            const moneyP = pools.find(x=>x.pool_name==='money')?.pool_percentage;
            const workP = pools.find(x=>x.pool_name==='work')?.pool_percentage;
            const fundP = pools.find(x=>x.pool_name==='company_fund')?.pool_percentage || 0;
            return (
              <tr key={r.id}>
                <td>{r.rule_name}</td>
                <td className="text-xs">{projectName(r.project_id)}</td>
                <td className="mono">{moneyP}% / {workP}%{fundP>0 && ` / ${fundP}% fund`}</td>
                <td className="text-xs">{fmtDate(r.effective_from)}{r.effective_to ? ` → ${fmtDate(r.effective_to)}` : ''}</td>
                <td><span className={"badge "+(r.status==='active'?'status-running':(r.status==='draft'?'status-planning':'status-closed'))}>{r.status}</span></td>
                {canEdit && <td className="text-right whitespace-nowrap">
                  <button aria-label="Edit" onClick={()=>{setForm({...r, money_pool_percentage: moneyP, company_fund_percentage: fundP});setOpen(true);}} className="tab btn-small mr-1"><Edit3 size={12}/></button>
                  {isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(r.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                </td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Rule':'Add Rule'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker} clear={['project_id']}/>
          <div className="col-span-2"><Field label="Rule Name *"><input className="input" value={form.rule_name||''} onChange={e=>setForm({...form, rule_name:e.target.value})}/></Field></div>
          <div className="col-span-2"><Field label="Applies To"><select className="input" value={form.project_id||''} onChange={e=>setForm({...form, project_id:e.target.value})}>
            <option value="">— Company default (all projects without their own rule) —</option>
            {forCompany(projects, form.company_id).map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select></Field></div>
          {targetProject ? (
            <div className={"col-span-2 p-3 rounded surface-2 text-xs "+(showMoneyWorkFields ? "" : "border border-[color:var(--gold)]/30")}>
              <span className="text-muted">Partnership Model:</span> <span className="text-[color:var(--gold)]">{PARTNERSHIP_MODEL_LABELS[targetModel]}</span>
              {showMoneyWorkFields ? (
                <div className="text-muted mt-1">Uses a Money/Work rule — set the split below. Company Fund % also applies.</div>
              ) : (
                <div className="mt-1">
                  <div className="text-muted">{PARTNERSHIP_MODEL_HELP[targetModel]} This model doesn't use a Money/Work split — its own split is set on {targetProject.project_name}'s own form. Company Fund % below still applies to it.</div>
                  <Link to={`/app/projects/${targetProject.id}`} className="inline-block mt-1.5 text-[color:var(--gold)] hover:underline">Open {targetProject.project_name} to change its Partnership Model →</Link>
                </div>
              )}
            </div>
          ) : (
            <div className="col-span-2 text-xs text-muted -mt-1">Company Fund % applies to every project on this company sharing the default. The Money/Work split only applies to Money & Work projects that don't have their own rule.</div>
          )}
          {showMoneyWorkFields && <>
          <div className="col-span-2">
            <div className="text-xs text-muted mb-1.5 uppercase tracking-wider">Quick Templates</div>
            <div className="flex gap-2 flex-wrap">
              {RULE_TEMPLATES.map(t => <button key={t.label} type="button" onClick={()=>setForm({...form, money_pool_percentage:t.money, company_fund_percentage:t.fund})} className="tab btn-small">{t.label}</button>)}
            </div>
          </div>
          <Field label="Money Pool %"><input className="input mono" type="number" min="0" max="100" value={form.money_pool_percentage??50} onChange={e=>setForm({...form, money_pool_percentage:e.target.value})}/></Field>
          </>}
          <Field label="Company Fund % (optional, reserved before the partner split, toward company overhead)"><input className="input mono" type="number" min="0" max="100" value={form.company_fund_percentage??0} onChange={e=>setForm({...form, company_fund_percentage:e.target.value})}/></Field>
          {showMoneyWorkFields && <Field label="Work Pool % (auto)"><input className="input mono" disabled value={`${workPct.toFixed(2)}%`}/></Field>}
          {/* Full-app audit finding: this field existed on the backend
              model and was already set by the separate "Interest Rate %"
              quick-widget on a partner's own Current Account tab (both
              patch the SAME company-wide default rule row) -- but this
              general form's own save() never included it in its payload,
              so saving ANY unrelated change here (rename, status, money
              split) silently reset a real, already-configured interest
              rate back to 0% with no warning. Only meaningful on a
              company-wide default rule (project_id null) -- the backend
              itself rejects a nonzero value on a project-specific rule. */}
          {!form.project_id && <Field label="Partner Current Account Interest Rate % (annual, optional)"><input className="input mono" type="number" min="0" value={form.interest_rate_percentage??0} onChange={e=>setForm({...form, interest_rate_percentage:e.target.value})}/></Field>}
          <Field label="Effective From"><input type="date" className="input" value={form.effective_from||today} onChange={e=>setForm({...form, effective_from:e.target.value})}/></Field>
          <Field label="Effective To (optional)"><input type="date" className="input" value={form.effective_to||''} onChange={e=>setForm({...form, effective_to:e.target.value})}/></Field>
          <Field label="Status"><select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}>
            <option value="draft">Draft</option><option value="active">Active</option><option value="superseded">Superseded</option>
          </select></Field>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={save} disabled={saving || !form.rule_name} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

// User-requested: the Company Fund Ledger needed to appear on the Reports
// page too (alongside Bank Account Ledger), "kept connected" to the
// existing view inside Company Expenses rather than forked into a second
// copy -- extracted out of CompanyExpensesPanel into its own self-fetching
// component so both places render the exact same data/logic. Read-only
// (canEdit/onAddContribution both optional) so the Reports page can embed
// it without also exposing the Add Capital Contribution write action,
// which stays a Settings-only affordance.
function CompanyFundLedgerCard({ companyId, companyLabel, canEdit, onAddContribution, printable }) {
  const [fundLedger, setFundLedger] = useState(null);
  useEffect(() => {
    if (!companyId) { setFundLedger(null); return; }
    let cancelled = false;
    api.get(`/companies/${companyId}/fund-ledger`).then(r => { if (!cancelled) setFundLedger(r.data); }).catch(() => { if (!cancelled) setFundLedger(null); });
    return () => { cancelled = true; };
  }, [companyId]);

  // User-requested: same FY/date-range filtering BankLedgerModal already
  // has -- filtered client-side for the identical reason (the endpoint
  // already returns every entry with a correctly-cumulative running
  // `balance` computed from full history, so a filtered view just needs
  // an opening balance carried forward from the last entry BEFORE the
  // range, not a re-computation).
  const [fyFilter, setFyFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const allEntries = fundLedger?.entries || [];
  const availableFYs = distinctFYs(allEntries.map(e => fyFromDateStr(e.date)));
  const filteredEntries = allEntries.filter(e =>
    (!fyFilter || fyFromDateStr(e.date) === fyFilter) &&
    (!fromDate || e.date >= fromDate) &&
    (!toDate || e.date <= toDate)
  );
  const filterActive = !!(fyFilter || fromDate || toDate);
  const rangeLowerBound = fromDate || (fyFilter ? (/FY (\d{4})-/.exec(fyFilter)?.[1] + '-04-01') : null);
  const beforeRange = rangeLowerBound ? allEntries.filter(e => e.date < rangeLowerBound) : [];
  const openingBalance = rangeLowerBound ? (beforeRange.length ? beforeRange[beforeRange.length - 1].balance : 0) : 0;
  const closingBalance = filteredEntries.length ? filteredEntries[filteredEntries.length - 1].balance : openingBalance;
  const rangeCredited = filteredEntries.reduce((a,e)=>a+(e.credit||0),0);
  const rangeDebited = filteredEntries.reduce((a,e)=>a+(e.debit||0),0);
  const clearFilters = () => { setFyFilter(''); setFromDate(''); setToDate(''); };

  const exportPDF = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(`Company Fund Ledger${companyLabel ? ' — '+companyLabel : ''}`, 14, 18);
    doc.setFontSize(9); doc.text(`Generated: ${fmtGeneratedAt()}`, 14, 26);
    if (filterActive) doc.text(`Range: ${fyFilter || `${fromDate||'…'} to ${toDate||'…'}`}`, 14, 31);
    autoTable(doc, {
      startY: filterActive ? 37 : 32,
      head: [['Date','Type','Description','Credit','Debit','Balance']],
      body: filteredEntries.map(e => [fmtDate(e.date), e.type.replace(/_/g,' '), (e.description||'').slice(0,40), fmtINRPdf(e.credit), fmtINRPdf(e.debit), fmtINRPdf(e.balance)]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
    });
    const finalY = doc.lastAutoTable?.finalY || 100;
    doc.setFontSize(10);
    if (filterActive) doc.text(`Opening Balance: ${fmtINRPdf(openingBalance)}`, 14, finalY + 10);
    doc.text(`Total Credited: ${fmtINRPdf(filterActive ? rangeCredited : fundLedger.totals.total_credit)}`, 14, finalY + (filterActive?16:10));
    doc.text(`Total Debited: ${fmtINRPdf(filterActive ? rangeDebited : fundLedger.totals.total_debit)}`, 14, finalY + (filterActive?22:16));
    doc.text(`${filterActive?'Closing':'Fund'} Balance: ${fmtINRPdf(closingBalance)}`, 14, finalY + (filterActive?28:22));
    doc.save(`company-fund-ledger-${(companyLabel||'company').toLowerCase().replace(/[^a-z0-9]+/g,'-')}-${Date.now()}.pdf`);
  };

  if (!companyId) return null;
  if (!fundLedger) return <Loading/>;
  return (
    <div className={"card card-dark mb-6 " + (printable ? "print-area" : "")}>
      {printable && <div className="print-header">
        <div className="print-company">{companyLabel || 'Company'}</div>
        <div className="print-report-title">Company Fund Ledger{filterActive ? ` — ${fyFilter || `${fromDate||'…'} to ${toDate||'…'}`}` : ''}</div>
        <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
      </div>}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Company Fund Ledger{companyLabel ? ` — ${companyLabel}` : ''}</div>
          <div className="text-xs text-muted mt-1 max-w-xl">The running position of the common fund — credited by the Overhead Reserve % (as each project is approved) and by partners contributing capital directly, debited by any Company Expense marked "Paid from Company Reserve / Common Fund". Tracked, never blocking — a negative position is a signal, not an error.</div>
        </div>
        <div className="flex gap-2 no-print">
          {printable && filteredEntries.length > 0 && <button onClick={exportPDF} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Export PDF</button>}
          {printable && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>}
          {canEdit && onAddContribution && <button onClick={onAddContribution} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="add-capital-contribution"><Plus size={14}/>Add Capital Contribution</button>}
        </div>
      </div>
      {printable && (
        <div className="flex flex-wrap items-end gap-2 mt-3 no-print">
          <Field label="Financial Year"><FySelect value={fyFilter} onChange={v=>{setFyFilter(v); setFromDate(''); setToDate('');}} years={availableFYs} style={{width:150}}/></Field>
          <Field label="From"><input type="date" className="input" value={fromDate} onChange={e=>{setFromDate(e.target.value); setFyFilter('');}} style={{width:150}}/></Field>
          <Field label="To"><input type="date" className="input" value={toDate} onChange={e=>{setToDate(e.target.value); setFyFilter('');}} style={{width:150}}/></Field>
          {filterActive && <button onClick={clearFilters} className="tab btn-small">Clear Range</button>}
        </div>
      )}
      {fundLedger.reopened_pending_reapproval.length > 0 && (
        <div className="text-xs text-amber-400 mt-3">{fundLedger.reopened_pending_reapproval.length} project(s) reopened for edits — their reserve isn't counted below until re-approved: {fundLedger.reopened_pending_reapproval.map(p=>p.project_name).join(', ')}</div>
      )}
      <div className={"grid grid-cols-1 gap-4 mt-3 " + (filterActive ? 'sm:grid-cols-2 md:grid-cols-4' : 'sm:grid-cols-3')}>
        {filterActive && <KpiCard label="Opening Balance" value={fmtINR(openingBalance)}/>}
        <KpiCard label={filterActive ? 'Closing Balance' : 'Fund Position'} value={fmtINR(closingBalance)} tone={closingBalance<0?'red':'gold'} hint={closingBalance<0?'Fund is overdrawn':undefined}/>
        <KpiCard label="Credited (range)" value={fmtINR(filterActive ? rangeCredited : fundLedger.totals.total_credit)}/>
        <KpiCard label="Debited (range)" value={fmtINR(filterActive ? rangeDebited : fundLedger.totals.total_debit)}/>
      </div>
      {filteredEntries.length > 0 ? (
        <div className="overflow-x-auto mt-4">
          <table className="tbl">
            <thead><tr><th>Date</th><th>Type</th><th>Description</th><th className="text-right">Credit</th><th className="text-right">Debit</th><th className="text-right">Balance</th></tr></thead>
            <tbody>
              {filteredEntries.map((e,i) => (
                <tr key={i}>
                  <td className="text-xs">{fmtDate(e.date)}</td>
                  <td className="text-xs">{e.type.replace(/_/g,' ')}</td>
                  <td>{e.project_id ? <Link to={`/app/projects/${e.project_id}`} className="hover:text-[color:var(--gold)]">{e.description}</Link> : e.description}</td>
                  <td className="mono text-right">{e.credit ? fmtINR(e.credit) : '—'}</td>
                  <td className="mono text-right">{e.debit ? fmtINR(e.debit) : '—'}</td>
                  <td className={"mono text-right " + (e.balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(e.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : filterActive && <div className="text-sm text-muted text-center py-6">No entries in this range.</div>}
    </div>
  );
}

// ============= COMPANY EXPENSES =============
// ---- Type tiles (Company Expenses by expense type, Security & Refundables by security type) ----
// Pure re-grouping of figures the pages already show; no number is recalculated or reinterpreted.
const TILE_TONE_KEYS = ['gold', 'blue', 'green', 'amber', 'violet'];
const SECURITY_TYPE_TILE_TONES = { emd: 'blue', fixed_deposit: 'green', bank_guarantee: 'violet', security_deposit: 'amber', refundable_advance: 'gold' };
const stableTileTone = (key) => {
  if (SECURITY_TYPE_TILE_TONES[key]) return META_CHIP_TONES[SECURITY_TYPE_TILE_TONES[key]]; let h = 0; String(key).split('').forEach(ch => { h = (h * 31 + ch.charCodeAt(0)) >>> 0; }); return META_CHIP_TONES[TILE_TONE_KEYS[h % TILE_TONE_KEYS.length]]; };
// Company Expenses: one group per expense type (overhead category). Confirmed = matched/final only,
// exactly the rule behind the table's "Confirmed Total"; everything else is the "Pending Review" amount.
function groupCompanyExpensesByType(rows, catName) {
  const m = new Map();
  rows.forEach(e => {
    const key = e.overhead_category_id || '__none__';
    let g = m.get(key);
    if (!g) { g = { key, name: e.overhead_category_id ? catName(e.overhead_category_id) : '(No category)', confirmed: 0, pendingAmount: 0, pendingCount: 0, count: 0, lastDate: null }; m.set(key, g); }
    const amt = Number(e.amount || 0);
    g.count += 1;
    if (CALC_ELIGIBLE_AUDIT_STATUSES.has(e.audit_status)) g.confirmed += amt; else { g.pendingAmount += amt; g.pendingCount += 1; }
    if (e.expense_date && (!g.lastDate || e.expense_date > g.lastDate)) g.lastDate = e.expense_date;
  });
  return [...m.values()].sort((a, b) => b.confirmed - a.confirmed || a.name.localeCompare(b.name));
}
function ExpenseTypeTiles({ groups, activeKeys, onToggle, onClear }) {
  const maxConfirmed = Math.max(0, ...groups.map(g => g.confirmed));
  return (
    <div className="mb-6 no-print" data-testid="cexp-type-tiles">
      <div className="flex items-center justify-between mb-2">
        <div className="serif text-xl text-[color:var(--gold)]">By expense type</div>
        {activeKeys.length > 0 && <button onClick={onClear} className="tab btn-small" data-testid="cexp-tile-clear">Show all types</button>}
      </div>
      <div className="vendor-grid">
        {groups.map(g => {
          const tone = stableTileTone(g.key); const active = activeKeys.includes(g.key);
          return (
            <button key={g.key} type="button" onClick={() => onToggle(g.key)} disabled={g.key === '__none__'} className={"card card-dark vendor-card text-left" + (active ? ' type-tile-active' : '')} aria-pressed={active}
              style={{ '--vendor-accent': tone.color, cursor: g.key === '__none__' ? 'default' : 'pointer', padding: 14 }} data-testid={`cexp-tile-${g.key}`}>
              <div className="serif text-base leading-tight vendor-name" title={g.name}>{g.name}</div>
              <div className="serif mono text-xl mt-2" style={{ color: 'var(--gold)' }} data-testid={`cexp-tile-amt-${g.key}`}>{fmtINR(g.confirmed)}</div>
              <div className="vendor-bar" aria-hidden="true"><div style={{ width: `${maxConfirmed > 0 ? Math.max(g.confirmed > 0 ? 2 : 0, Math.round((g.confirmed / maxConfirmed) * 100)) : 0}%`, background: tone.color }}/></div>
              <div className="text-xs text-muted mt-2" data-testid={`cexp-tile-meta-${g.key}`}>{g.count} entr{g.count === 1 ? 'y' : 'ies'}{g.lastDate ? ` · last ${fmtDate(g.lastDate)}` : ''}</div>
              {g.pendingCount > 0 && <div className="vendor-pending mt-2" data-testid={`cexp-tile-pending-${g.key}`}><Clock3 size={12}/>{fmtINR(g.pendingAmount)} pending</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CompanyExpensesPanel() {
  const { user } = useAuth();
  const entryOnly = isDataEntry(user); // Data Entry: the add forms only, no list / reserve / ledgers
  const [recentEntries, setRecentEntries] = useState([]);
  const vendors = useVendors();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [companyFilter, setCompanyFilter] = usePersistedState('CompanyExpensesPanel.companyFilter', '');
  // Category (and the expense-type tiles) can hold several values. Company stays single here on purpose: it also
  // selects the ONE company the Fund Ledger / bank ledger / reserve and the add-form defaults refer to.
  const categoryF = useMultiFilter('CompanyExpensesPanel.category');
  const [manageOpen, setManageOpen] = useState(false);
  const { companies, needsPicker } = useMyCompanies();
  const [staffList, setStaffList] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  // User-requested: same "Add Security Instrument" shortcut as the
  // Extra Works page's own cross-page Add Expenses button, reusing
  // SecurityInstrumentModal (shared with Security & Refundables' own
  // Add/Edit) so both places use the exact same form.
  const [siOpen, setSiOpen] = useState(false);
  // User-requested: an Investor is hidden from Paid By by default -- this
  // reveals them for the rare genuine one-off exception.
  const [showAllPartners, setShowAllPartners] = useState(false);
  // User-requested: "access company ledgers" from this page -- the Company
  // Fund Ledger is already shown inline below (CompanyFundLedgerCard); this
  // adds the other half, individual Bank Account Ledgers, via the same
  // reused, self-contained BankLedgerModal + company-scoped picker pattern
  // already used on Settings > Bank Accounts, Reports, and Partner
  // Settlement (see project-anvid-partner-settlement-bank-ledger memory).
  const [ledgerBankAccountId, setLedgerBankAccountId] = useState('');
  const load = async () => {
    const [e, c, s, b] = await Promise.all([
      entryOnly ? Promise.resolve({ data: [] }) : api.get('/expenses'),
      api.get('/expense-categories', { params: { scope: 'overhead' } }),
      api.get('/staff'),
      api.get('/bank-accounts'),
    ]);
    setItems(e.data.filter(x => !x.project_id));
    setCategories(c.data);
    setStaffList(s.data);
    setBankAccounts(b.data);
  };
  useEffect(() => { load(); }, []);
  const catName = (id) => categories.find(c=>c.id===id)?.name || '—';
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  const partnerName = (id) => partners.find(p=>p.id===id)?.name || '—';

  // Company Overhead Reserve -- a % of every project's profit held back
  // before the partner split, toward this company's real overhead (see
  // _resolve_company_fund_pct in the backend). Scoped to exactly one
  // company at a time, same as the rest of this page's filter.
  const activeCompanyId = !needsPicker ? companies[0]?.id : (companyFilter || null);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveRule, setReserveRule] = useState(null);
  const [reservePct, setReservePct] = useState('');
  const [reserveSaving, setReserveSaving] = useState(false);
  const loadReserve = async (cid) => {
    if (!cid || entryOnly) { setReserveRule(null); return; }
    try {
      const r = await api.get('/profit-sharing-rules');
      const companyWide = (r.data || []).find(x => x.company_id === cid && !x.project_id && isProfitRuleCurrentlyActive(x));
      setReserveRule(companyWide || null);
    } catch { setReserveRule(null); }
  };
  useEffect(() => { loadReserve(activeCompanyId); }, [activeCompanyId]);
  const reserveFundPct = reserveRule ? ((reserveRule.pool_definitions||[]).find(x=>x.pool_name==='company_fund')?.pool_percentage || 0) : 0;
  const openReserve = () => { setReservePct(reserveFundPct || ''); setReserveOpen(true); };
  const saveReserve = async () => {
    if (reserveSaving || !activeCompanyId) return;
    setReserveSaving(true);
    try {
      const pct = Number(reservePct || 0);
      if (reserveRule) {
        // Preserve every other field on the existing rule, changing only
        // company_fund_percentage -- never hardcode money_pool_percentage
        // here, or an existing Money/Work split this company's own
        // money_work projects rely on would silently reset to 0.
        const moneyPct = (reserveRule.pool_definitions||[]).find(x=>x.pool_name==='money')?.pool_percentage ?? 50;
        await api.patch(`/profit-sharing-rules/${reserveRule.id}`, {
          project_id: null, company_id: activeCompanyId, rule_name: reserveRule.rule_name,
          money_pool_percentage: moneyPct, company_fund_percentage: pct,
          effective_from: reserveRule.effective_from, effective_to: reserveRule.effective_to, status: reserveRule.status,
        });
      } else {
        // No existing company-wide rule -- money_pool_percentage: 0 here
        // (not the form default of 50) so a fresh reserve-only setup
        // never hits the backend's money+fund<=100 validation ceiling
        // for a field this company isn't even using yet.
        await api.post('/profit-sharing-rules', {
          project_id: null, company_id: activeCompanyId, rule_name: 'Company Overhead Reserve',
          money_pool_percentage: 0, company_fund_percentage: pct,
          effective_from: new Date().toISOString().slice(0,10), status: 'active',
        });
      }
      setReserveOpen(false); loadReserve(activeCompanyId); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setReserveSaving(false); }
  };

  // Coverage: reserved (already carved out of approved projects this FY)
  // vs. actual overhead recorded this FY -- pure visibility, doesn't feed
  // back into any calculation automatically.
  const currentFY = fyFromDateStr(new Date().toISOString());
  const [coverageFy, setCoverageFy] = useState(currentFY);
  const [coverage, setCoverage] = useState(null);
  useEffect(() => {
    if (!activeCompanyId || !coverageFy || entryOnly) { setCoverage(null); return; }
    api.get('/company-overhead-coverage', { params: { company_id: activeCompanyId, financial_year: coverageFy } })
      .then(r => setCoverage(r.data)).catch(() => setCoverage(null));
  }, [activeCompanyId, coverageFy]);
  const fyOptions = distinctFYs([currentFY, ...items.map(x => fyFromDateStr(x.expense_date))]);

  // Company Fund Ledger card is now a standalone, self-fetching component
  // (CompanyFundLedgerCard) shared with the Reports page — bumping this
  // token remounts it (forcing a fresh fetch) after a contribution save,
  // same effect loadFundLedger's own re-call used to have.
  const [fundLedgerReloadToken, setFundLedgerReloadToken] = useState(0);
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get('/partners').then(r => setPartners(r.data)).catch(() => {}); }, []);
  const companyPartners = mastersForCompany(partners, activeCompanyId);
  // For the "Partner Transaction" quick-add's optional Project field
  // (Capital Investment / Loan categories only) -- this panel otherwise
  // never needs a projects list.
  const [ptxnQuickProjects, setPtxnQuickProjects] = useState([]);
  useEffect(() => { api.get('/projects').then(r => setPtxnQuickProjects(r.data)).catch(() => {}); }, []);
  // Just the closing balance, for the "Paid from Company Reserve" checkbox's
  // hint below -- kept as its own tiny fetch rather than re-coupling to
  // CompanyFundLedgerCard's internal state, which this panel no longer owns.
  const [fundPosition, setFundPosition] = useState(null);
  useEffect(() => {
    if (!activeCompanyId || entryOnly) { setFundPosition(null); return; }
    api.get(`/companies/${activeCompanyId}/fund-ledger`).then(r => setFundPosition(r.data.totals.closing_balance)).catch(() => setFundPosition(null));
  }, [activeCompanyId, fundLedgerReloadToken]);

  const [contribOpen, setContribOpen] = useState(false);
  const [contribForm, setContribForm] = useState({});
  const [contribSaving, setContribSaving] = useState(false);
  const openContrib = () => { setContribForm({ contribution_date: new Date().toISOString().slice(0,10), partnerIds: [], totalAmount: '' }); setContribOpen(true); };
  const contribPartnerCount = (contribForm.partnerIds || []).length;
  const contribEqualShare = contribPartnerCount ? Number(contribForm.totalAmount || 0) / contribPartnerCount : 0;
  const saveContrib = async () => {
    if (contribSaving || !activeCompanyId || !contribPartnerCount || !Number(contribForm.totalAmount || 0)) return;
    if (!/^\d+$/.test(contribForm.voucher_number||'')) { toast.error('Voucher number is required and must contain digits only.'); return; }
    setContribSaving(true);
    try {
      // One row per selected partner, split equally -- a convenience over
      // the raw POST /company-capital-contributions, not a new bulk
      // endpoint, matching the "Paste Multiple" sequential-POST pattern
      // already used elsewhere in this app. All rows from one physical
      // voucher share the same voucher_number (it's ONE payment split
      // across several partners' shares, not several separate payments).
      for (const pid of contribForm.partnerIds) {
        await api.post('/company-capital-contributions', {
          company_id: activeCompanyId, partner_id: pid,
          contribution_date: contribForm.contribution_date,
          amount: contribEqualShare, voucher_number: contribForm.voucher_number, notes: contribForm.notes || null,
        });
      }
      setContribOpen(false); setFundLedgerReloadToken(t=>t+1); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setContribSaving(false); }
  };

  // "Partner Transaction" quick-add -- one simplified form, plain-language
  // Payment Category instead of a raw type/table name, for daily entry by
  // someone without deep accounting knowledge. Routes to whichever of the
  // 3 real tables (investments / company_capital_contributions /
  // partner_transactions) that category actually belongs to -- see
  // PARTNER_TXN_QUICK_CATEGORIES' own comment for why. Deliberately its
  // own local modal (not a shared component like SecurityInstrumentModal)
  // since only this page asked for it.
  const [ptxnQuickOpen, setPtxnQuickOpen] = useState(false);
  const [ptxnQuickForm, setPtxnQuickForm] = useState({});
  const [ptxnQuickSaving, setPtxnQuickSaving] = useState(false);
  const [ptxnQuickShowAllPartners, setPtxnQuickShowAllPartners] = useState(false);
  const openPtxnQuick = () => {
    setPtxnQuickForm({ company_id: companyFilter || (!needsPicker && companies.length===1 ? companies[0].id : ''), txn_date: new Date().toISOString().slice(0,10) });
    setPtxnQuickShowAllPartners(false);
    setPtxnQuickOpen(true);
  };
  const ptxnQuickCategory = PARTNER_TXN_QUICK_CATEGORIES.find(c => c.key === ptxnQuickForm.category);
  // The Data Entry dashboard tiles link here as /app/company-expenses?add=company|security|partner
  // and land straight in the matching form (once, then the parameter is cleared).
  const [searchParams, setSearchParams] = useSearchParams();
  const autoAddDone = useRef(false);
  useEffect(() => {
    const what = searchParams.get('add');
    if (!what || autoAddDone.current || !companies.length || !(canEdit || isDataEntry(user))) return;
    autoAddDone.current = true;
    if (what === 'company') { setForm({ expense_date: new Date().toISOString().slice(0,10), category: 'miscellaneous', company_id: companyFilter || (!needsPicker && companies.length===1 ? companies[0].id : '') }); setOpen(true); }
    else if (what === 'security') setSiOpen(true);
    else if (what === 'partner') openPtxnQuick();
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, companies.length]);
  const savePtxnQuick = async () => {
    if (ptxnQuickSaving) return;
    if (!ptxnQuickForm.company_id) { toast.error('Company is required.'); return; }
    if (!ptxnQuickForm.partner_id) { toast.error('Partner is required.'); return; }
    if (!/^\d+$/.test(ptxnQuickForm.voucher_number||'')) { toast.error('Voucher number is required and must contain digits only.'); return; }
    if (!ptxnQuickForm.txn_date) { toast.error('Date is required.'); return; }
    if (!ptxnQuickForm.amount || Number(ptxnQuickForm.amount) <= 0) { toast.error('Amount is required and must be greater than 0.'); return; }
    if (!ptxnQuickCategory) { toast.error('Payment Category is required.'); return; }
    if (ptxnQuickCategory.projectRequired && !ptxnQuickForm.project_id) { toast.error('Project is required for a Loan from a Partner.'); return; }
    const amount = Number(ptxnQuickForm.amount || 0);
    if (ptxnQuickCategory.type === 'profit_payment' && !entryOnly && !(await confirmProfitPaymentCash(ptxnQuickForm.project_id, amount, ptxnQuickForm.partner_id))) return; // the cash-position check reads project financials, which a Data Entry login can't see
    setPtxnQuickSaving(true);
    try {
      if (ptxnQuickCategory.direction === 'in' && !ptxnQuickForm.project_id) {
        // Capital Investment, company-wide (no project picked).
        await api.post('/company-capital-contributions', {
          company_id: ptxnQuickForm.company_id, partner_id: ptxnQuickForm.partner_id,
          contribution_date: ptxnQuickForm.txn_date, amount, voucher_number: ptxnQuickForm.voucher_number,
          notes: ptxnQuickForm.description || null,
        });
      } else if (ptxnQuickCategory.direction === 'in') {
        // Capital Investment tied to a project, or a Loan (always project-tied).
        await api.post('/investments', {
          project_id: ptxnQuickForm.project_id, partner_id: ptxnQuickForm.partner_id,
          transaction_date: ptxnQuickForm.txn_date, amount,
          investment_type: ptxnQuickCategory.key === 'loan_in' ? 'partner_loan' : 'capital_investment',
          voucher_number: ptxnQuickForm.voucher_number, notes: ptxnQuickForm.description || null,
          paid_to_company_fund: !!ptxnQuickForm.paid_to_company_fund,
        });
      } else {
        await api.post('/partner-transactions', {
          company_id: ptxnQuickForm.company_id, project_id: ptxnQuickForm.project_id || null,
          partner_id: ptxnQuickForm.partner_id, transaction_type: ptxnQuickCategory.type,
          transaction_date: ptxnQuickForm.txn_date, amount, voucher_number: ptxnQuickForm.voucher_number,
          paid_by_partner_id: ptxnQuickForm.paid_by_partner_id || null, description: ptxnQuickForm.description || null,
          paid_from_company_fund: !!ptxnQuickForm.paid_from_company_fund,
        });
      }
      if (entryOnly) setRecentEntries(r => [`Partner transaction · voucher ${ptxnQuickForm.voucher_number} · ${ptxnQuickCategory.label.split(' — ')[0]} · ${fmtINR(amount)}`, ...r].slice(0, 15));
      // User-requested motive is fast repeated daily entry -- stays open,
      // resets to the same company so several entries in a row don't
      // require re-picking it each time.
      setPtxnQuickForm({ company_id: ptxnQuickForm.company_id, txn_date: ptxnQuickForm.txn_date });
      setFundLedgerReloadToken(t=>t+1);
      toast.success('Saved — ready for the next one');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setPtxnQuickSaving(false); }
  };

  // Real bug, user-reported: every row under a shared Overhead Category
  // (one granted to more than one company, e.g. Om Shankar AND Royal
  // Buddha) was displayed/filtered under that category's own single
  // DEFAULT company (here: Easeprime) -- ignoring the row's own stored
  // company_id, which is the authoritative field once a category is
  // shared (see ExpenseIn.company_id's own comment). Same root cause as
  // the 2026-09-08 Audit Queue mis-attribution fix (_row_company_id in
  // server.py already prefers the row's own company_id) -- that backend
  // fix never had a frontend twin here, so this page kept using the old,
  // category-only logic even after the Audit Queue started showing the
  // correct company per row for the exact same data. A legacy row with
  // no company_id of its own (predates the column) still falls back to
  // the category's default, unchanged.
  const companyOfExpense = (e) => e.company_id || categories.find(c=>c.id===e.overhead_category_id)?.company_id;
  const companyScoped = items.filter(e => !companyFilter || companyOfExpense(e) === companyFilter);
  const visibleItems = companyScoped
    .filter(e => categoryF.matches(e.overhead_category_id))
    // Round 32 audit finding: the "Category" column displays
    // catName(e.overhead_category_id) but sorting used e.category (a
    // different, unrelated free-text field, e.g. "miscellaneous") --
    // clicking the header sorted by text never shown in that column.
    // Sorting on this derived, display-matching field instead.
    .map(e => ({ ...e, _overhead_category_name: catName(e.overhead_category_id) }));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    if (needsPicker && !form.company_id) { toast.error('Company is required.'); return; }
    if (!form.overhead_category_id) { toast.error('Overhead Category is required.'); return; }
    if (!form.expense_date) { toast.error('Date is required.'); return; }
    if (!form.amount) { toast.error('Amount is required.'); return; }
    if (!/^\d+$/.test(form.voucher_number||'')) { toast.error('Voucher number is required and must contain digits only.'); return; }
    if (!form.paid_by_partner_id && !form.bank_account_id && !form.paid_from_company_fund) { toast.error('Paid By is required — choose the partner, the bank account, or tick Paid from Company Fund.'); return; }
    if (form.staff_payment_type && !form.staff_id) { toast.error('Staff Member is required when a Payment Type is set.'); return; }
    { const vp = vendorStaffProblem(form, (categories.find(c => c.id === form.overhead_category_id) || {}).name); if (vp) { toast.error(vp); return; } }
    if (!form.id && isStaffDecisionCategory((categories.find(c => c.id === form.overhead_category_id) || {}).name) && !form.staff_payment_type && form.staff_decision !== 'none') { toast.error(STAFF_DECISION_MESSAGE); return; }
    setSaving(true);
    const isNew = !form.id;
    try {
      const payload = { ...form, project_id: null, vendor_name: cleanVendor(form.vendor_name), amount: Number(form.amount||0) };
      delete payload.staff_decision;
      if (form.id) await api.patch(`/expenses/${form.id}`, payload); else await api.post('/expenses', payload);
      if (entryOnly && !form.id) setRecentEntries(r => [`Company expense · voucher ${form.voucher_number} · ${catName(form.overhead_category_id)} · ${fmtINR(payload.amount)}`, ...r].slice(0, 15));
      load();
      // User-requested: after adding a NEW company expense (not editing an
      // existing one), stay open and reset to the same opening seed so
      // several can be entered in a row.
      if (isNew) { setForm({expense_date:new Date().toISOString().slice(0,10),category:'miscellaneous',company_id: companyFilter || (!needsPicker && companies.length===1 ? companies[0].id : '')}); toast.success('Saved — ready for the next one'); }
      else { setOpen(false); setForm({}); toast.success('Saved'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/expenses/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  // Same audit_status workflow as project expenses (default pending_review
  // on create) applies to overhead-category expenses too — the "Income"
  // page already had this exact gap fixed (see CALC_ELIGIBLE_AUDIT_STATUSES
  // above), and this total footer had the same one, unfixed: it was
  // summing every row regardless of status, so a not-yet-reviewed company
  // expense inflated the headline total shown here.
  const typeGroups = groupCompanyExpensesByType(companyScoped, catName);
  const eligibleItems = visibleItems.filter(x => CALC_ELIGIBLE_AUDIT_STATUSES.has(x.audit_status));
  const total = eligibleItems.reduce((a,x)=>a+Number(x.amount||0),0);
  const pendingTotal = visibleItems.reduce((a,x)=>a+(CALC_ELIGIBLE_AUDIT_STATUSES.has(x.audit_status)?0:Number(x.amount||0)),0);
  // Company is now picked first in the Add/Edit form itself (see the
  // Modal below), so it's always known directly -- falls back to
  // activeCompanyId only for a single-company caller who never sees that
  // field at all.
  const expenseCompanyId = form.company_id || activeCompanyId;
  const staffOptions = forCompany(staffList, expenseCompanyId);
  const partnerOptions = eligiblePaidByPartners(partners, expenseCompanyId, null, showAllPartners, form.paid_by_partner_id);
  const bankOptions = bankAccounts.filter(b => !expenseCompanyId || b.company_id === expenseCompanyId);
  const bankName = (id) => bankAccounts.find(b=>b.id===id)?.bank_name;
  const { sorted: sortedItems, sortKey, sortDir, toggleSort } = useSortableRows(visibleItems, 'expense_date', 'desc');
  // Scoped the same way the Company filter above already scopes the
  // expenses table itself -- All Companies shows every bank account this
  // user can see, a single company narrows it to that company's accounts.
  const ledgerBankOptions = bankAccounts.filter(b => !companyFilter || b.company_id === companyFilter);
  const ledgerBankAccount = bankAccounts.find(b => b.id === ledgerBankAccountId);
  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <div>
          <div className="section-eyebrow mb-2">Not Project-Linked</div>
          <h1 className="serif text-4xl">Company Expenses</h1>
          {entryOnly ? <div className="text-sm text-muted mt-1">Use the buttons below to enter a company expense, a security instrument or a partner transaction.</div> : (
          <div className="text-sm text-muted mt-1">Legal, R&D, salary, tax filing, misc. — company-level spend that doesn't touch any one project's own profit calculation directly. Set a Company Overhead Reserve below to hold back a % of every project's profit so partners jointly cover it instead.</div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {needsPicker && <select className="input" style={{width:170}} value={companyFilter} onChange={e=>setCompanyFilter(e.target.value)} data-testid="cexp-filter-company">
            <option value="">All Companies</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>}
          {!entryOnly && ledgerBankOptions.length > 0 && <select className="input" style={{width:190}} value={ledgerBankAccountId} onChange={e=>setLedgerBankAccountId(e.target.value)} data-testid="cexp-bank-ledger-picker">
            <option value="">Bank Ledger — select account</option>
            {/* User-reported: a bare bank name is ambiguous once "All
                Companies" is selected -- more than one real company here
                names its account the same generic thing (e.g. "Hdfc
                Bank"). Only needed here, not in the Add-form dropdown
                below (that one narrows to one company as soon as it's
                picked) -- this picker can genuinely span every company at
                once whenever companyFilter is blank. */}
            {ledgerBankOptions.map(b=><option key={b.id} value={b.id}>{b.bank_name}{!companyFilter ? ` — ${companyName(b.company_id)}` : ''}</option>)}
          </select>}
          {canEdit && <button onClick={()=>setManageOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="manage-categories"><PenLine size={14}/>Manage Categories</button>}
          {!entryOnly && <>
          <MultiFilterPicker filter={categoryF} items={categories.map(c=>({id:c.id, name:c.name}))} allLabel="All Categories" width={180} testId="cexp-filter-category"/>
          <ClearFiltersButton filters={[categoryF]} extraActive={!!companyFilter} onClear={()=>setCompanyFilter('')}/>
          <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>
          </>}
          {/* User-requested: a Data Entry login can Add on these 3 too
              (backend already allows it, see _crud_endpoints'
              extra_create_roles / create_security_instrument's own role
              list), even though it fails the regular canEdit check that
              also gates Edit/Delete/Manage Categories elsewhere here. */}
          {(canEdit || isDataEntry(user)) && <button onClick={()=>{setForm({expense_date:new Date().toISOString().slice(0,10),category:'miscellaneous',company_id: companyFilter || (!needsPicker && companies.length===1 ? companies[0].id : '')});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Company Expense</button>}
          {(canEdit || isDataEntry(user)) && <button onClick={()=>setSiOpen(true)} className="btn-blue btn-small inline-flex items-center gap-1" data-testid="cexp-add-security"><Plus size={14}/>Add Security Instrument</button>}
          {/* User-requested: a single simplified entry point for daily
              partner money-movement entry by someone without deep
              accounting knowledge -- see PARTNER_TXN_QUICK_CATEGORIES. */}
          {(canEdit || isDataEntry(user)) && <button onClick={openPtxnQuick} className="btn-violet btn-small inline-flex items-center gap-1" data-testid="cexp-add-partner-txn"><Plus size={14}/>Partner Transaction</button>}
        </div>
      </div>

      {entryOnly && <div className="card card-dark mb-6" data-testid="de-cexp-note"><div className="text-sm">Fill in each form exactly as written on the voucher. The form stays open after each save so you can enter the next one. Saved entries can't be edited here — see the Audit Queue for their review status.</div><DataEntryRecent items={recentEntries}/></div>}
      {!entryOnly && (activeCompanyId ? (
        <div className="card card-dark mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="serif text-xl text-[color:var(--gold)]">Company Overhead Reserve{needsPicker ? ` — ${companyName(activeCompanyId)}` : ''}</div>
              <div className="text-xs text-muted mt-1 max-w-xl">A % of every project's profit, held back before the partner split, toward this company's real overhead — applies to every Partnership Model. This is a target rate you set, not a live calculation off the expenses below; use the Coverage comparison to see if it's enough and adjust over time.</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-3xl serif text-[color:var(--gold)]">{reserveFundPct || 0}%</div>
                <div className="text-xs text-muted">current reserve</div>
              </div>
              {canEdit && <button onClick={openReserve} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="edit-reserve-pct"><Edit3 size={14}/>Edit</button>}
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted">Overhead Coverage</div>
            <FySelect value={coverageFy} onChange={setCoverageFy} years={fyOptions} style={{width:150}}/>
          </div>
          {coverage && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
              <KpiCard label="Reserved (approved this FY)" value={fmtINR(coverage.reserved)} gold/>
              <KpiCard label="Actual Overhead Recorded" value={fmtINR(coverage.actual_overhead)} tone="red"/>
              <KpiCard label="Difference" value={fmtINR(coverage.difference)} tone={coverage.difference<0?'red':'green'} hint={coverage.difference<0?'Reserve is short of real overhead — consider raising it':'Reserve covers real overhead for this FY'}/>
            </div>
          )}
        </div>
      ) : needsPicker && (
        <div className="card card-dark mb-6 text-sm text-muted">Select a single company above to manage its Overhead Reserve % and see Coverage.</div>
      ))}

      {!entryOnly && activeCompanyId && <CompanyFundLedgerCard key={fundLedgerReloadToken} companyId={activeCompanyId} companyLabel={needsPicker ? companyName(activeCompanyId) : ''} canEdit={canEdit} onAddContribution={openContrib}/>}

      {!entryOnly && typeGroups.length > 0 && <ExpenseTypeTiles groups={typeGroups} activeKeys={categoryF.all ? [] : categoryF.ids} onToggle={key => { if (categoryF.all) categoryF.only(key); else if (categoryF.ids.includes(key) && categoryF.ids.length === 1) categoryF.reset(); else categoryF.toggle(key); }} onClear={categoryF.reset}/>}
      {!entryOnly && (
      <div className="card card-dark overflow-x-auto print-area">
        <div className="print-header">
          <div className="print-company">Company Expenses</div>
          <div className="print-report-title">{visibleItems.length} of {items.length} entries</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        <div className="text-xs text-muted mb-2 no-print">Showing <b>{visibleItems.length}</b> of <b>{items.length}</b> entries{visibleItems.length !== items.length ? ' (filtered)' : ''}</div>
        <table className="tbl">
          <thead><tr>
            <SortTh label="Date" sortKeyName="expense_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Category" sortKeyName="_overhead_category_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {needsPicker && <th>Company</th>}
            <SortTh label="Description" sortKeyName="description" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Voucher No." sortKeyName="voucher_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <th>Paid By</th>
            <SortTh label="Amount" sortKeyName="amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
            <SortTh label="Audit" sortKeyName="audit_status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {canEdit && <th className="no-print"></th>}
          </tr></thead>
          <tbody>
            {visibleItems.length===0 && <tr><td colSpan={9} className="text-center text-muted py-8">{items.length===0 ? 'No company expenses yet.' : 'No expenses match these filters.'}</td></tr>}
            {sortedItems.map(e => (
              <tr key={e.id}>
                <td className="text-xs">{fmtDate(e.expense_date)}</td>
                <td className="text-xs">{catName(e.overhead_category_id)}</td>
                {needsPicker && <td className="text-xs">{companyName(companyOfExpense(e))}</td>}
                <td>{e.description||'—'}</td>
                <td className="text-xs">{e.voucher_number||'—'}</td>
                <td className="text-xs">{e.paid_by_partner_id ? partnerName(e.paid_by_partner_id) : (e.bank_account_id ? (bankName(e.bank_account_id) || '—') : 'Company')}</td>
                <td className="mono text-right">{fmtINR(e.amount)}</td>
                <td><AuditStatusChip status={e.audit_status}/></td>
                {canEdit && <td className="text-right whitespace-nowrap no-print">
                  <button aria-label="Edit" onClick={()=>{setForm(e);setOpen(true);}} className="tab btn-small mr-1"><Edit3 size={12}/></button>
                  {isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(e.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                </td>}
              </tr>
            ))}
            {visibleItems.length>0 && <tr><td colSpan={needsPicker?6:5} className="text-right text-muted uppercase text-xs">Confirmed Total (matched/final)</td><td className="mono text-right text-[color:var(--gold)]">{fmtINR(total)}</td><td colSpan={canEdit?2:1}></td></tr>}
            {pendingTotal>0 && <tr><td colSpan={needsPicker?6:5} className="text-right text-muted uppercase text-xs">Pending Review, not counted above</td><td className="mono text-right text-amber-400">{fmtINR(pendingTotal)}</td><td colSpan={canEdit?2:1}></td></tr>}
          </tbody>
        </table>
      </div>
      )}
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Company Expense':'Add Company Expense'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* User-reported gap: this form derived its company purely from
              whichever Overhead Category got picked (and only ever asked
              explicitly when that category happened to be shared across
              more than one company) -- with no direct Company field at
              all, it wasn't obvious which company a manually entered
              expense would land under. Company is now picked FIRST, same
              "pick the anchor first" cascade every other form in this app
              already uses (Project Expenses, Security & Refundables,
              Partner Settlement) -- Overhead Category then only shows
              that company's own categories. */}
          {needsPicker && (
            <Field label="Company *"><select className="input" value={form.company_id||''} onChange={e=>setForm({...form, company_id:e.target.value||'', overhead_category_id:'', paid_by_partner_id:null, bank_account_id:null, staff_id:null, staff_payment_type:null})} data-testid="cexp-company">
              <option value="">— select —</option>
              {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select></Field>
          )}
          {/* User-requested: Voucher No. right after Company. Real-usage-
              requested rule: a voucher number must come off the real
              physical voucher/bill, digits only, unique -- no placeholder/
              bypass value. Enforced for real server-side
              (_validate_expense_scope); this is just matching client-side
              feedback. */}
          <Field label="Voucher No. *"><input className="input" inputMode="numeric" placeholder="e.g. 1042" value={form.voucher_number||''} onChange={e=>setForm({...form, voucher_number:e.target.value})}/></Field>
          <Field label="Date *"><input type="date" className="input" value={form.expense_date||''} onChange={e=>setForm({...form, expense_date:e.target.value})}/></Field>
          <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
          <Field label="Overhead Category *"><select className="input" disabled={needsPicker && !form.company_id} value={form.overhead_category_id||''} onChange={e=>setForm({...form, overhead_category_id:e.target.value})}>
            <option value="">{needsPicker && !form.company_id ? '— select a company first —' : '— select —'}</option>
            {(needsPicker ? mastersForCompany(categories, form.company_id) : categories).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select></Field>
          {/* Real-usage-gap finding: this field already existed on Project
              Expenses (and the backend/ledger logic already fully support
              it here too) but was missing from this form entirely, forcing
              a manual off-app tally of which partner personally paid a
              company/overhead expense. Same field, same behavior — flows
              straight into the partner's Ledger/Position/Current Account
              as a credit once the expense is reviewed (matched/final). */}
          {/* User-requested: Paid By is now mandatory -- every expense must
              record either the partner who personally paid it, or the
              company bank account it was paid from (same rule the backend
              enforces in _validate_expense_scope). Bank Account is new on
              this form -- it previously had no way at all to represent "paid
              directly from a company account, no partner involved", which
              would otherwise be impossible to save once the rule went in. */}
          <Field label="Paid By Partner *"><select className="input" value={form.paid_by_partner_id||''} onChange={e=>setForm({...form, paid_by_partner_id:e.target.value||null, bank_account_id: e.target.value ? null : form.bank_account_id, paid_from_company_fund: e.target.value ? false : form.paid_from_company_fund})}>
            <option value="">— none, paid from bank instead —</option>
            {partnerOptions.map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={showAllPartners} onChange={e=>setShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
          </Field>
          <Field label="Or Bank Account *"><select className="input" value={form.bank_account_id||''} onChange={e=>setForm({...form, bank_account_id:e.target.value||null, paid_by_partner_id: e.target.value ? null : form.paid_by_partner_id, paid_from_company_fund: e.target.value ? false : form.paid_from_company_fund})}>
            <option value="">— none, paid by partner instead —</option>
            {/* Same ambiguity fix as the Bank Ledger picker above -- this
                dropdown falls back to showing every company's accounts
                unfiltered until a Company is actually picked in this
                form (see bankOptions' own comment). */}
            {bankOptions.map(b=><option key={b.id} value={b.id}>{b.bank_name}{needsPicker && !expenseCompanyId ? ` — ${companyName(b.company_id)}` : ''}</option>)}
          </select></Field>
          <div className="col-span-2"><Field label="Description"><input className="input" value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field></div>
          {/* Staff & Vendor: optional link so this payment shows up on the
              staff member's own auto-totaled Payments view (GET
              /staff/:id/payments) -- payments still get entered here,
              exactly as before, this is just the link. User-requested:
              Payment Type now comes first and drives the requirement --
              picking one (anything other than "not a staff payment")
              makes Staff Member mandatory, rather than the old order
              where Payment Type only appeared once a staff member was
              already picked. */}
          <Field label={vendorIsRequired(form, (categories.find(c => c.id === form.overhead_category_id) || {}).name) ? 'Vendor *' : 'Vendor'}><VendorSelect vendors={vendors} companyId={expenseCompanyId} value={form.vendor_name} isNew={!form.id} categoryName={(categories.find(c => c.id === form.overhead_category_id) || {}).name} onChange={v=>setForm({...form, vendor_name:v})}/></Field>
          {(() => {
            const staffDecision = !form.id && isStaffDecisionCategory((categories.find(c => c.id === form.overhead_category_id) || {}).name);
            return (
              <Field label={staffDecision ? 'Payment Type *' : 'Payment Type'}><select className="input" data-testid="staff-type-select" value={form.staff_payment_type || (form.staff_decision === 'none' ? '__none__' : '')} onChange={e=>{
                const v = e.target.value;
                setForm(v === '__none__' ? {...form, staff_payment_type: null, staff_id: null, staff_decision: 'none'} : {...form, staff_payment_type: v || null, staff_id: staffAfterTypeChange(staffOptions, v || null, form.staff_id), staff_decision: undefined});
              }}>
                {staffDecision ? <><option value="">— choose —</option><option value="__none__">Not a staff payment</option></> : <option value="">— not a staff payment —</option>}
                {Object.entries(STAFF_PAYMENT_TYPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
              </select></Field>
            );
          })()}
          <Field label={staffIsRequired(form, (categories.find(c => c.id === form.overhead_category_id) || {}).name) ? "Staff Member *" : "Staff Member"}><select className="input" value={form.staff_id||''} onChange={e=>setForm({...form, staff_id:e.target.value||null})}>
            <option value="">{staffNoneLabel(form.staff_payment_type)}</option>
            {staffPickList(staffOptions, form.staff_payment_type, form.staff_id).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select></Field>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.paid_from_company_fund} onChange={e=>setForm({...form, paid_from_company_fund:e.target.checked, paid_by_partner_id: e.target.checked ? null : form.paid_by_partner_id, bank_account_id: e.target.checked ? null : form.bank_account_id})}/>
              Paid from Company Reserve / Common Fund
            </label>
            {form.paid_from_company_fund && fundPosition !== null && <div className="text-xs text-muted mt-1">Current fund position: {fmtINR(fundPosition)} — not blocking, tracked only.</div>}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      {manageOpen && (
        <Modal open={true} onClose={()=>{setManageOpen(false); load();}} title="Manage Overhead Categories" maxW={780}>
          <OverheadCategoriesPanel/>
        </Modal>
      )}
      <SecurityInstrumentModal open={siOpen} onClose={()=>setSiOpen(false)} onSaved={()=>{}} onCreated={x => { if (entryOnly) setRecentEntries(r => [`Security instrument · voucher ${x.voucher_number} · ${x.instrument_type} · ${fmtINR(x.amount)}`, ...r].slice(0, 15)); }} initialForm={{date_lodged: new Date().toISOString().slice(0,10), instrument_type: 'emd', status: 'pending', company_id: activeCompanyId || ''}}/>
      <Modal open={reserveOpen} onClose={()=>setReserveOpen(false)} title="Company Overhead Reserve %">
        <p className="text-xs text-muted mb-4">Held back from every project's profit before the partner split, applies to every Partnership Model. A specific project can still be given its own override rate under Settings → Profit Sharing Rules.</p>
        <Field label="Reserve %"><input className="input mono" type="number" min="0" max="100" value={reservePct} onChange={e=>setReservePct(e.target.value)} autoFocus/></Field>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setReserveOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={saveReserve} disabled={reserveSaving || reservePct===''} className="btn-gold btn-small" data-testid="save-reserve-pct">{reserveSaving?'Saving…':'Save'}</button></div>
      </Modal>
      <Modal open={contribOpen} onClose={()=>setContribOpen(false)} title="Add Capital Contribution">
        <p className="text-xs text-muted mb-4">Partners contributing capital directly to the company's common fund, not tied to any project. Select more than one partner to split the total equally between them, or one partner for a single, unequal contribution.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Date *"><input type="date" className="input" value={contribForm.contribution_date||''} onChange={e=>setContribForm({...contribForm, contribution_date:e.target.value})}/></Field>
          <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={contribForm.voucher_number||''} onChange={e=>setContribForm({...contribForm, voucher_number:e.target.value})}/></Field>
          <Field label="Total Amount (₹) *"><input className="input mono" type="number" min="0" value={contribForm.totalAmount||''} onChange={e=>setContribForm({...contribForm, totalAmount:e.target.value})}/></Field>
          <div className="col-span-2">
            <div className="text-xs text-muted mb-1.5 uppercase tracking-wider">Partners *</div>
            <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
              {companyPartners.length===0 && <div className="text-xs text-muted">No partners on this company.</div>}
              {companyPartners.map(p => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={(contribForm.partnerIds||[]).includes(p.id)} onChange={()=>setContribForm({...contribForm, partnerIds: (contribForm.partnerIds||[]).includes(p.id) ? contribForm.partnerIds.filter(x=>x!==p.id) : [...(contribForm.partnerIds||[]), p.id]})}/>
                  {partnerOptionLabel(p)}
                </label>
              ))}
            </div>
            {contribPartnerCount > 1 && <div className="text-xs text-muted mt-2">Split equally: {fmtINR(contribEqualShare)} each × {contribPartnerCount} partners</div>}
          </div>
          <div className="col-span-2"><Field label="Notes"><input className="input" value={contribForm.notes||''} onChange={e=>setContribForm({...contribForm, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setContribOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={saveContrib} disabled={contribSaving || !contribPartnerCount || !Number(contribForm.totalAmount||0) || !contribForm.contribution_date} className="btn-gold btn-small" data-testid="save-capital-contribution">{contribSaving?'Saving…':'Save'}</button></div>
        {/* Save itself validates the voucher number and toasts a specific
            error, matching this app's established pattern -- the button's
            own disabled condition intentionally stays limited to the
            checks above (amount/partners/date), not duplicated here. */}
      </Modal>

      <Modal open={ptxnQuickOpen} onClose={()=>setPtxnQuickOpen(false)} title="Partner Transaction">
        <p className="text-xs text-muted mb-4">Pick who, then what happened — the rest of the form fills in only once that's chosen.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company *"><select className="input" value={ptxnQuickForm.company_id||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, company_id:e.target.value, partner_id:'', project_id:''})}>
            <option value="">— select —</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select></Field>
          <Field label="Partner *"><select className="input" disabled={!ptxnQuickForm.company_id} value={ptxnQuickForm.partner_id||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, partner_id:e.target.value})}>
            <option value="">{ptxnQuickForm.company_id ? '— select —' : '— select a company first —'}</option>
            {mastersForCompany(partners, ptxnQuickForm.company_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
          </select></Field>
          <div className="col-span-2">
            <Field label="What happened? *">
              <select className="input" disabled={!ptxnQuickForm.partner_id} value={ptxnQuickForm.category||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, category:e.target.value, project_id:'', paid_by_partner_id:''})}>
                <option value="">{ptxnQuickForm.partner_id ? '— select —' : '— select company and partner first —'}</option>
                {/* Full-app audit finding: a data_entry login could see and
                    fully fill out Capital Investment / Loan from Partner
                    (money IN) here, but both route to endpoints that are
                    admin/accountant-only server-side -- the save always
                    403'd with no earlier warning. Only the 6 "money out"
                    categories that data_entry can actually save are
                    offered to that role, matching what the Partner
                    Transactions tab elsewhere already restricts it to. */}
                {PARTNER_TXN_QUICK_CATEGORIES.filter(c=>!isDataEntry(user) || c.direction==='out').map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
            {ptxnQuickCategory?.hint && <div className="text-xs text-muted mt-1.5">{ptxnQuickCategory.hint}</div>}
          </div>
          {/* Everything below only appears once a category is picked --
              user-requested simplification: don't show the full field set
              up front, reveal it step by step so there's less on screen
              to figure out at once. */}
          {ptxnQuickCategory && <>
            <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={ptxnQuickForm.voucher_number||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, voucher_number:e.target.value})} autoFocus/></Field>
            <Field label="Date *"><input type="date" className="input" value={ptxnQuickForm.txn_date||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, txn_date:e.target.value})}/></Field>
            <Field label="Amount *"><input className="input mono" type="number" min="0" value={ptxnQuickForm.amount||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, amount:e.target.value})}/></Field>
            {/* Project only matters for money coming IN (a Loan is always
                project-tied; a Capital Investment optionally is) -- the 6
                "money out" categories don't show it at all, one less field
                for the common case. */}
            {(ptxnQuickCategory.direction === 'in' || ptxnQuickCategory.type === 'profit_payment') && (
              <Field label={ptxnQuickCategory.projectRequired ? 'Project *' : 'Project (optional)'}>
                <select className="input" value={ptxnQuickForm.project_id||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, project_id:e.target.value})}>
                  <option value="">{ptxnQuickCategory.projectRequired ? '— select a project —' : '— company-wide, no project —'}</option>
                  {forCompany(ptxnQuickProjects, ptxnQuickForm.company_id).filter(p => ptxnQuickCategory.type === 'profit_payment' || !isProjectLocked(p)).map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
                </select>
                {ptxnQuickCategory.type === 'profit_payment' && <div className="text-xs text-muted mt-1">Pick the project this profit is from: the payment is then limited to that partner's cash-backed share, and counts against it. An approved project can still be paid.</div>}
              </Field>
            )}
            {/* Only meaningful once a project is picked -- a company-wide
                (no project) Capital Investment already posts to Company
                Capital Contributions instead, which unconditionally counts
                toward the Fund Ledger, same as before this whole feature
                existed. A project-tied investment doesn't always mean the
                cash pooled into the shared reserve, so it needs this
                explicit opt-in. */}
            {ptxnQuickCategory.direction === 'in' && ptxnQuickForm.project_id && (
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!ptxnQuickForm.paid_to_company_fund} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, paid_to_company_fund:e.target.checked})}/>
                  Added to Company Reserve / Common Fund
                </label>
              </div>
            )}
            {ptxnQuickCategory.direction === 'out' && (
              <Field label="Paid By Partner (if not this partner / company funds)">
                <select className="input" value={ptxnQuickForm.paid_by_partner_id||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, paid_by_partner_id:e.target.value||null, paid_from_company_fund: e.target.value ? false : ptxnQuickForm.paid_from_company_fund})}>
                  <option value="">— none —</option>
                  {eligiblePaidByPartners(partners, ptxnQuickForm.company_id, ptxnQuickForm.project_id, ptxnQuickShowAllPartners, ptxnQuickForm.paid_by_partner_id).filter(p=>p.id!==ptxnQuickForm.partner_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={ptxnQuickShowAllPartners} onChange={e=>setPtxnQuickShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
              </Field>
            )}
            {/* Mirrors CompanyExpensesPanel's own paid_from_company_fund
                checkbox — this quick-add only ever creates company-level
                (project_id null) transactions, so a fund-draw here is
                always the simple, unambiguous case the Fund Ledger already
                reads. */}
            {ptxnQuickCategory.direction === 'out' && (
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!ptxnQuickForm.paid_from_company_fund} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, paid_from_company_fund:e.target.checked, paid_by_partner_id: e.target.checked ? null : ptxnQuickForm.paid_by_partner_id})}/>
                  Paid from Company Reserve / Common Fund
                </label>
              </div>
            )}
            <div className="col-span-2"><Field label="Description"><input className="input" value={ptxnQuickForm.description||''} onChange={e=>setPtxnQuickForm({...ptxnQuickForm, description:e.target.value})}/></Field></div>
          </>}
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setPtxnQuickOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={savePtxnQuick} disabled={ptxnQuickSaving || !ptxnQuickCategory} className="btn-gold btn-small" data-testid="save-partner-txn-quick">{ptxnQuickSaving?'Saving…':'Save'}</button></div>
      </Modal>
      {ledgerBankAccount && <BankLedgerModal account={ledgerBankAccount} companyName={companyName(ledgerBankAccount.company_id)} canEdit={canEdit} onClose={()=>setLedgerBankAccountId('')}/>}
    </div>
  );
}

// ============= SECURITY & REFUNDABLES =============
// User-requested: "Security Deposit" renamed to "Demand Draft (DD)"
// everywhere on this page -- the underlying stored value stays
// `security_deposit` (no schema/data change), since it's also the type
// a Bills withholding of the same name syncs into here (see
// _sync_deduction_security_link) -- only how it displays changes.
const SECURITY_TYPE_LABELS = { emd: 'EMD', fixed_deposit: 'Fixed Deposit', bank_guarantee: 'Bank Guarantee', security_deposit: 'Demand Draft (DD)', refundable_advance: 'Refundable Advance', other: 'Other' };
const PARTNER_REPORT_CATEGORY_LABELS = { reopen_request: 'Request to Reopen', miscalculation: 'Suspected Miscalculation', other: 'Other Comment' };
const SECURITY_STATUS_LABELS = { pending: 'Pending', hold: 'On Hold', settlement_required: 'Settlement Required', credited: 'Credited (not returned)', refunded: 'Refunded' };
// User-requested, generalized from an earlier Security-Deposit-only fix:
// ANY refundable deduction taken directly off a Receipt (not tied to a
// Bill) auto-syncs into a real security_instruments row via
// _sync_deduction_security_link, precisely so it can be tracked and
// claimed back the same way an EMD/FD/DD already is. That sync sets
// instrument_type from the deduction's own free-text type master (TDS,
// Security Deposit, GST Reserve, Labour Cess, ...), which essentially
// never matches SECURITY_INSTRUMENT_TYPES' fixed 6-value enum, so the
// sync's own `else "other"` fallback always fires -- live-confirmed 64
// real rows (53 "ITD/TDS" + 11 "Security Deposit") indistinguishable
// from genuine "Other" instruments, with no way to filter or trace any
// of them back to what they were actually withheld for.
// Fixed generally rather than one type at a time: the deduction's own
// original type name is recovered from the row's own auto-generated
// `purpose` field ("Auto-linked from deduction: <type>", a fixed
// template -- see _sync_deduction_security_link) and used as BOTH the
// display label and a dedicated Type filter option, built dynamically
// off whichever deduction types actually have synced rows right now --
// so a new deduction type added later (Settings > Masters > Deduction
// Type) is traced automatically, with no further code change here.
const DEDUCTION_SYNC_PURPOSE_RE = /^auto-linked from deduction:\s*(.+)$/i;
const deductionSyncedTypeName = (s) => {
  if (!s.linked_deduction_id) return null;
  const m = DEDUCTION_SYNC_PURPOSE_RE.exec((s.purpose || '').trim());
  return m ? m[1].trim() : null;
};
// A stable filter-select value derived from the type name, distinct from
// any real instrument_type enum value (e.g. a deduction type literally
// named "Other" can never collide with the fixed "other" option).
const deductionSyncFilterValue = (typeName) => `deduction:${typeName}`;
const securityTypeLabel = (s) => deductionSyncedTypeName(s) || SECURITY_TYPE_LABELS[s.instrument_type] || s.instrument_type;

// User-requested: shared between Security & Refundables' own "Add
// Instrument"/Edit action and a same-shaped "Add Security Instrument"
// shortcut on the Company Expenses page (see CompanyExpensesPanel) --
// self-contained (fetches its own projects/partners) so either caller
// only needs to pass open/onClose/onSaved/initialForm, matching the
// AddExtraWorkExpenseModal pattern already used for the same kind of
// cross-page reuse. Field order: Company > Voucher No. right after it >
// Date Lodged > Type right before Amount > Amount, per explicit request.
// A capital lock only applies to an instrument paid from the Company Fund;
// stale lock values left behind by switching to "Borne By" are dropped.
// Party = a Client of the company (Settings > Clients). Picking one sets party_client_id and the client's own name as
// party_name, so two spellings of one client can't exist. An older row whose party has no client record keeps its text,
// shown as "(not in client list)" until a client is picked.
function SecurityPartyField({ form, setForm, clients, companyId, testId }) {
  const list = (clients || []).filter(c => !companyId || (c.company_ids || []).includes(companyId));
  const legacy = !form.party_client_id && (form.party_name || '').trim();
  const legacyMatches = legacy && list.some(c => c.name.trim().toLowerCase() === form.party_name.trim().toLowerCase());
  return (
    <Field label="Party (Client) *">
      <select className="input" value={form.party_client_id || (legacy && !legacyMatches ? '__legacy__' : '')} data-testid={testId || 'si-party'}
        onChange={e => {
          const v = e.target.value;
          if (v === '__legacy__') return;
          const c = list.find(x => x.id === v);
          setForm({ ...form, party_client_id: v || null, party_name: c ? c.name : '' });
        }}>
        <option value="">— choose the client —</option>
        {legacy && !legacyMatches && <option value="__legacy__">{form.party_name} (not in client list)</option>}
        {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {list.length === 0 && <div className="text-xs mt-1" style={{color:'var(--accent-amber)'}}>No clients set up for this company yet — add them under Settings → Clients.</div>}
    </Field>
  );
}

const normalizeCapitalLock = (form) => {
  const on = !!form.paid_from_company_fund && !!form.capital_locked_partner_id;
  return {
    capital_locked_partner_id: on ? form.capital_locked_partner_id : null,
    capital_locked_amount: on && form.capital_locked_amount !== undefined && form.capital_locked_amount !== null && form.capital_locked_amount !== '' ? Number(form.capital_locked_amount) : null,
  };
};
function CapitalLockField({ form, setForm, partners, companyId, projectId }) {
  if (!form.paid_from_company_fund) return null;
  const list = eligiblePaidByPartners(partners, companyId, projectId, true, form.capital_locked_partner_id);
  return (
    <div className="col-span-2 rounded p-3" style={{background:'rgba(217,164,65,0.08)'}} data-testid="capital-lock-field">
      <div className="text-sm font-medium mb-2">Funded from a partner's capital? <span className="text-xs text-muted font-normal">(optional)</span></div>
      <div className="grid grid-cols-2 gap-3">
        <select className="input" value={form.capital_locked_partner_id||''} onChange={e=>setForm({...form, capital_locked_partner_id:e.target.value||null, capital_locked_amount: e.target.value ? (form.capital_locked_amount ?? '') : null})} data-testid="capital-lock-partner">
          <option value="">— no capital lock —</option>
          {list.map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
        </select>
        {form.capital_locked_partner_id && <input className="input mono" type="number" min="0" placeholder={`Amount locked (blank = full ${form.amount ? fmtINR(form.amount) : 'amount'})`} value={form.capital_locked_amount ?? ''} onChange={e=>setForm({...form, capital_locked_amount:e.target.value})} data-testid="capital-lock-amount"/>}
      </div>
      <div className="text-xs text-muted mt-2">That much of the partner's capital stays locked until this instrument is refunded or credited. Payments made to the partner meanwhile are never counted as returning it; anything paid beyond their free capital is treated as a refund of their personal expenses.</div>
    </div>
  );
}

function SecurityInstrumentModal({ open, onClose, onSaved, onCreated, initialForm }) {
  const { user } = useAuth();
  const { companies, needsPicker } = useMyCompanies();
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // User-requested: an Investor is hidden from Borne By by default -- this
  // reveals them for the rare genuine one-off exception.
  const [showAllPartners, setShowAllPartners] = useState(false);
  const [partyClients, setPartyClients] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm(initialForm || {});
    setShowAllPartners(false);
    Promise.all([api.get('/projects'), api.get('/partners')]).then(([p, pt]) => {
      setProjects(p.data); setPartners(pt.data);
    });
    api.get('/security-instruments/party-clients').then(r => setPartyClients(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // User-requested: default Refund Due Date to 180 days after this
  // project's own most recent payment received (the practical, always-
  // available stand-in for "date of final payment" -- no receipt carries
  // an explicit "this is the final one" marker; see the backend's
  // _default_refund_due_date for the same rule applied to deduction-
  // synced rows). Only fills when still blank; never overwrites a real
  // date the user typed. autoFilledRefundDueRef tracks the exact value
  // this effect itself last set (same pattern as the Add Receipt
  // Description auto-fill) so switching which Project is selected
  // updates the default again, but a value the user then edited by hand
  // is never clobbered.
  const autoFilledRefundDueRef = useRef(null);
  useEffect(() => {
    if (!open || !form.project_id || isDataEntry(user)) return; // Data Entry has no access to receipts
    api.get('/receipts', { params: { project_id: form.project_id } }).then(r => {
      const dates = (r.data || []).map(x => x.receipt_date).filter(Boolean).sort();
      if (!dates.length) return;
      const due = new Date(dates[dates.length - 1]);
      due.setUTCDate(due.getUTCDate() + 180);
      const dueStr = due.toISOString().slice(0, 10);
      setForm(f => {
        if (f.refund_due_date && f.refund_due_date !== autoFilledRefundDueRef.current) return f;
        autoFilledRefundDueRef.current = dueStr;
        return { ...f, refund_due_date: dueStr };
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.project_id]);

  // User-requested (lightweight reconciliation): mirrors the backend's
  // _require_forfeiture_note exactly -- a real, non-zero loss (Credited/
  // not-returned, or Refunded for less than the full lodged amount)
  // needs a note explaining why, so a shortfall isn't left unexplained
  // for someone to puzzle over months later.
  const forfeitureAmount = form.status === 'credited' ? Number(form.amount||0)
    : (form.status === 'refunded' && form.settled_amount !== undefined && form.settled_amount !== '') ? Math.max(0, Number(form.amount||0) - Number(form.settled_amount||0))
    : 0;
  const save = async () => {
    if (saving) return;
    if (!form.instrument_type) { toast.error('Type is required.'); return; }
    if (!form.date_lodged) { toast.error('Date Lodged is required.'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Amount is required and must be greater than 0.'); return; }
    if (!/^\d+$/.test(form.voucher_number||'')) { toast.error('Voucher number is required and must contain digits only.'); return; }
    if (!(form.ledger_folio_number||'').trim()) { toast.error('Ledger Folio Number is required.'); return; }
    if (!form.party_client_id && !(form.party_name||'').trim()) { toast.error('Party is required — choose the client the instrument is lodged with.'); return; }
    if (!form.borne_by_partner_id && !form.paid_from_company_fund) { toast.error('Borne By is required — choose the partner who personally bore this, or tick Paid from Company Fund.'); return; }
    if (form.refund_due_date && form.date_lodged && form.refund_due_date < form.date_lodged) { toast.error('Refund Due Date can\'t be before the Date Lodged.'); return; }
    if (form.status === 'refunded' && !isDataEntry(user)) {
      if (form.settled_amount === undefined || form.settled_amount === '' || form.settled_amount === null) { toast.error('Amount Actually Received Back is required for a Refunded instrument.'); return; }
      if (Number(form.settled_amount) > Number(form.amount||0) + 0.005) { toast.error('Amount Actually Received Back can\'t be more than the lodged amount.'); return; }
      if (!form.settlement_date) { toast.error('Settlement Date is required for a Refunded instrument.'); return; }
      if (form.date_lodged && form.settlement_date < form.date_lodged) { toast.error('Settlement Date can\'t be before the Date Lodged.'); return; }
    }
    if (forfeitureAmount > 0.005 && !(form.notes||'').trim()) { toast.error(`This settles for ${fmtINR(forfeitureAmount)} less than the full lodged amount (or is marked Credited/not returned) — add a note explaining the shortfall before saving.`); return; }
    setSaving(true);
    const isNew = !form.id;
    try {
      const lock = normalizeCapitalLock(form);
      if (lock.capital_locked_amount !== null && lock.capital_locked_amount > Number(form.amount||0)) { toast.error('The locked capital can\'t be more than the instrument amount.'); setSaving(false); return; }
      const payload = { ...form, ...lock, amount: Number(form.amount||0), settled_amount: form.settled_amount!==undefined && form.settled_amount!=='' ? Number(form.settled_amount) : null, project_id: form.project_id || null };
      if (form.id) await api.patch(`/security-instruments/${form.id}`, payload); else await api.post('/security-instruments', payload);
      if (isNew && onCreated) onCreated(payload);
      onSaved();
      // User-requested: after adding a NEW instrument (not editing an
      // existing one), stay open and reset to the same opening seed
      // (whatever the caller passed as initialForm) so several can be
      // entered in a row without re-clicking the Add button each time.
      if (isNew) { setForm(initialForm || {}); toast.success('Saved — ready for the next one'); }
      else { onClose(); toast.success('Saved'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={form.id?'Edit Security Instrument':'Add Security Instrument'} maxW={860}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker} clear={['project_id','borne_by_partner_id']}/>
        {/* User-requested: Voucher No. right after Company, mandatory,
            same digits-only rule already enforced on expenses. */}
        <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={form.voucher_number||''} onChange={e=>setForm({...form, voucher_number:e.target.value})}/></Field>
        <Field label="Date Lodged *"><input type="date" className="input" max={new Date().toISOString().slice(0,10)} value={form.date_lodged||''} onChange={e=>setForm({...form, date_lodged:e.target.value})}/></Field>
        {/* User-requested: Type right before Amount. */}
        <Field label="Type *"><select className="input" value={form.instrument_type||'emd'} onChange={e=>setForm({...form, instrument_type:e.target.value})}>
          {Object.entries(SECURITY_TYPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
        </select></Field>
        {/* User-requested: Amount is now mandatory (was optional/defaulted
            to 0). */}
        <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
        <Field label="Project (optional)"><select className="input" value={form.project_id||''} onChange={e=>setForm({...form, project_id:e.target.value})}>
          <option value="">— company-level / not project-specific —</option>
          {forCompany(projects, form.company_id).filter(p=>!isProjectLocked(p) || p.id===form.project_id).map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
        </select></Field>
        <SecurityPartyField form={form} setForm={setForm} clients={partyClients} companyId={form.company_id}/>
        <Field label="Tender Reference"><input className="input" value={form.tender_reference||''} onChange={e=>setForm({...form, tender_reference:e.target.value})}/></Field>
        <Field label="Bank Account"><input className="input" value={form.bank_account_name||''} onChange={e=>setForm({...form, bank_account_name:e.target.value})}/></Field>
        <Field label="Borne By Partner * (or tick Company Fund below)"><select className="input" value={form.borne_by_partner_id||''} onChange={e=>setForm({...form, borne_by_partner_id:e.target.value, paid_from_company_fund: e.target.value ? false : form.paid_from_company_fund})}>
          <option value="">— none —</option>
          {eligiblePaidByPartners(partners, form.company_id, form.project_id, showAllPartners, form.borne_by_partner_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={showAllPartners} onChange={e=>setShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
        </Field>
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.paid_from_company_fund} onChange={e=>setForm({...form, paid_from_company_fund:e.target.checked, borne_by_partner_id: e.target.checked ? null : form.borne_by_partner_id})}/>
            Paid from Company Reserve / Common Fund
          </label>
          {!form.borne_by_partner_id && !form.paid_from_company_fund && <div className="text-xs mt-1" style={{color:'var(--accent-amber)'}}>Choose a partner above, or tick this — one is required.</div>}
        </div>
        {!isDataEntry(user) && <CapitalLockField form={form} setForm={setForm} partners={partners} companyId={form.company_id} projectId={form.project_id}/>}
        {/* User-reported real gap, real data corrected: "Settled Amount"
            reads as "how much came back" to this code (see
            _security_loss_amount below), but the label alone doesn't say
            that -- a caller can easily read it as "how much was settled/
            deducted" instead, and enter 0 meaning "nothing lost" when
            they mean the opposite. Live-confirmed cost: two real ₹1,18,590
            EMDs marked Refunded with Settled Amount left at 0 showed as a
            full ₹2,37,180 loss (Forfeited) instead of the fully-recovered
            zero-loss they actually were. Two changes: the label now says
            plainly what the number means, and switching Status to
            Refunded auto-fills it with the full lodged Amount (the common
            "got it all back" case needs zero typing) -- only on that
            transition, so it never silently overwrites a value someone
            already typed for a genuine partial refund. */}
        {!isDataEntry(user) && <Field label="Status"><select className="input" value={form.status||'pending'} onChange={e=>{
          const status = e.target.value;
          const justBecameRefunded = status === 'refunded' && form.status !== 'refunded';
          setForm({...form, status, ...(justBecameRefunded && (form.settled_amount===undefined || form.settled_amount==='') ? {settled_amount: form.amount} : {})});
        }}>
          {Object.entries(SECURITY_STATUS_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
        </select></Field>}
        <Field label="Refund Due Date"><input type="date" className="input" min={form.date_lodged||undefined} value={form.refund_due_date||''} onChange={e=>setForm({...form, refund_due_date:e.target.value})}/></Field>
        {form.status==='refunded' && !isDataEntry(user) && <>
          <Field label="Settlement Date *"><input type="date" className="input" min={form.date_lodged||undefined} max={new Date().toISOString().slice(0,10)} value={form.settlement_date||''} onChange={e=>setForm({...form, settlement_date:e.target.value})}/></Field>
          <Field label="Amount Actually Received Back *"><input className="input mono" type="number" min="0" value={form.settled_amount??''} onChange={e=>setForm({...form, settled_amount:e.target.value})}/>
            <div className="text-xs text-muted mt-1">The full lodged amount if fully refunded — a lower figure means the difference is being declared a loss.</div>
          </Field>
          <Field label="Credited To"><input className="input" value={form.credited_to||''} onChange={e=>setForm({...form, credited_to:e.target.value})}/></Field>
        </>}
        <Field label="Ledger Folio No. *"><input className="input" value={form.ledger_folio_number||''} onChange={e=>setForm({...form, ledger_folio_number:e.target.value})}/></Field>
        <div className="col-span-2">
          <Field label={forfeitureAmount>0.005 ? 'Notes * (explain the shortfall)' : 'Notes'}><input className="input" value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field>
          {forfeitureAmount>0.005 && <div className="text-xs mt-1" style={{color:'var(--accent-amber)'}}>This settles for {fmtINR(forfeitureAmount)} less than the full lodged amount — explain why (e.g. client under-deducted, rounding difference, TDS certificate mismatch).</div>}
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2"><button onClick={onClose} className="btn-outline btn-small">Cancel</button>
        <button onClick={save} disabled={saving} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
    </Modal>
  );
}

// User-requested: recording that a client-withheld security amount has
// actually come back needed two separate manual steps before this -- a
// Receipt for the real cash inflow, and separately updating whatever it
// was withheld against, since the Security & Refundables row for it was
// either entirely read-only (a Bill's own structured TDS/DD/Retention
// field) or, for a receipt-level Deduction, just a label with no cash
// artifact behind it at all. Doing only one of the two silently leaves
// outstanding_receivable wrong (bill case) or leaves the "refund" purely
// cosmetic (deduction case) -- this does both together, in one request,
// for either kind:
//   - bill-linked (row.source==='deduction', has linked_bill_id): POST
//     /bills/{id}/record-recovery, which also reduces that bill's own
//     withholding field. `row` here is one of the read-only "From Bill"
//     rows this list synthesizes (instrument_type, linked_bill_id, amount
//     = currently still-withheld, company_id).
//   - deduction-linked (a real row, linked_deduction_id set, no
//     linked_bill_id): POST /security-instruments/{id}/record-recovery,
//     which also flips this row's own status/settled_amount to reflect
//     the recovery -- plus an explicit opt-in to route the recovered cash
//     into the shared Company Fund instead of staying ordinary project
//     cash (receipts.paid_to_company_fund, mirrors Investments' own
//     opt-in), since recovered client money is NOT automatically shared
//     reserve cash by default.
function RecordRecoveryModal({ open, onClose, onSaved, row }) {
  const [form, setForm] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const isBillLinked = !!(row && row.source === 'deduction' && row.linked_bill_id);
  // A bill-linked row's own `amount` field IS "currently still withheld"
  // (synthesized fresh from the bill every load). A real deduction-linked
  // row instead carries its full original amount plus whatever's already
  // been settled from a prior partial recovery (settled_amount is only
  // ever non-null while status==='refunded' -- see
  // _normalize_security_settlement).
  const remaining = row ? (isBillLinked ? Number(row.amount||0) : Math.max(0, Number(row.amount||0) - (row.status==='refunded' ? Number(row.settled_amount||0) : 0))) : 0;

  useEffect(() => {
    if (!open || !row) return;
    setForm({ amount: remaining, receipt_date: new Date().toISOString().slice(0,10), bank_account_id: null, description: '', add_to_company_fund: false });
    api.get('/bank-accounts').then(r => setBankAccounts((r.data||[]).filter(b => b.company_id === row.company_id))).catch(() => setBankAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.id]);

  if (!row) return null;
  const label = deductionSyncedTypeName(row) || SECURITY_TYPE_LABELS[row.instrument_type] || row.instrument_type;

  // User-requested (lightweight reconciliation): mirrors the backend's
  // own shortfall_note requirement in record_security_instrument_recovery
  // -- only meaningful for the deduction-linked path, since a partial
  // BILL recovery has no forfeiture concept at all (the remaining balance
  // just stays outstanding for a later recovery, nothing is ever declared
  // permanently lost the way a deduction-linked instrument's own
  // status/settled_amount can be).
  const isShortfall = !isBillLinked && Number(form.amount||0) > 0 && Number(form.amount||0) < remaining - 0.005;
  // User-requested: recovering into the Company Fund on a locked project's
  // instrument skips the reopen requirement server-side, but ONLY for a
  // full recovery (see record_security_instrument_recovery's own comment
  // for why a partial one still genuinely needs reopen+re-approve). Caught
  // here too so the error reads in terms of this form, not a raw 423 from
  // the lock check the backend falls back to.
  const isLockedFundRecovery = !isBillLinked && !!row.project_locked && !!form.add_to_company_fund;
  const save = async () => {
    if (saving) return;
    if (!Number(form.amount) || Number(form.amount) <= 0) { toast.error('Amount Recovered is required and must be greater than 0.'); return; }
    if (Number(form.amount) > remaining + 0.01) { toast.error(`Amount Recovered can't exceed what's still outstanding (${fmtINR(remaining)}).`); return; }
    if (!form.receipt_date) { toast.error('Date is required.'); return; }
    if (isShortfall && !(form.shortfall_note||'').trim()) { toast.error(`This leaves ${fmtINR(remaining - Number(form.amount||0))} unaccounted for — add a note explaining the shortfall.`); return; }
    if (isLockedFundRecovery && Number(form.amount) < remaining - 0.01) { toast.error(`This project is locked — recovering into the Company Fund without reopening only works for the full outstanding amount (${fmtINR(remaining)}). Recover the full amount, or reopen the project (Settlement tab) first for a partial recovery.`); return; }
    setSaving(true);
    try {
      if (isBillLinked) {
        await api.post(`/bills/${row.linked_bill_id}/record-recovery`, {
          withholding_type: row.instrument_type,
          amount: Number(form.amount || 0),
          receipt_date: form.receipt_date,
          bank_account_id: form.bank_account_id || null,
          description: form.description || null,
        });
      } else {
        await api.post(`/security-instruments/${row.id}/record-recovery`, {
          amount: Number(form.amount || 0),
          receipt_date: form.receipt_date,
          bank_account_id: form.bank_account_id || null,
          description: form.description || null,
          add_to_company_fund: !!form.add_to_company_fund,
          shortfall_note: form.shortfall_note || null,
        });
      }
      onClose(); toast.success('Recovery recorded'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Record Recovery — ${label}`}>
      <p className="text-xs text-muted mb-4">Records the amount actually received back as a Receipt on this project{isBillLinked ? <> and reduces this Bill's own {label} withholding by the same amount</> : ''}. Currently outstanding: {fmtINR(remaining)}.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Amount Recovered *"><input className="input mono" type="number" min="0" max={remaining} value={form.amount??''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
        <Field label="Date *"><input type="date" className="input" value={form.receipt_date||''} onChange={e=>setForm({...form, receipt_date:e.target.value})}/></Field>
        <Field label="Bank Account"><select className="input" value={form.bank_account_id||''} onChange={e=>setForm({...form, bank_account_id:e.target.value||null})}>
          <option value="">— none / cash —</option>
          {bankAccounts.map(b=><option key={b.id} value={b.id}>{b.bank_name}</option>)}
        </select></Field>
        <div className="col-span-2"><Field label="Description"><input className="input" placeholder="Auto-filled if left blank" value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field></div>
        {!isBillLinked && (
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.add_to_company_fund} onChange={e=>setForm({...form, add_to_company_fund:e.target.checked})}/>
              Add this recovered amount to the Company Fund
            </label>
            <div className="text-xs text-muted mt-1">Leave unticked to keep it as ordinary project cash (counts toward this project's own receipts/profit as usual).</div>
            {row.project_locked && (
              form.add_to_company_fund
                ? <div className="text-xs mt-1" style={{color:'var(--accent-amber)'}}>This project is locked, but a full recovery routed to the Company Fund doesn't require reopening it — recover the full {fmtINR(remaining)} to proceed. A partial recovery still needs Reopen (Settlement tab) first.</div>
                : <div className="text-xs text-muted mt-1">This project is locked — recording this recovery normally requires reopening it first (Settlement tab), unless you tick the box above and recover the full amount.</div>
            )}
          </div>
        )}
        {isShortfall && (
          <div className="col-span-2">
            <Field label="Reason for Shortfall *"><input className="input" value={form.shortfall_note||''} onChange={e=>setForm({...form, shortfall_note:e.target.value})} placeholder="e.g. TDS certificate shows less than what was recorded as withheld"/></Field>
            <div className="text-xs mt-1" style={{color:'var(--accent-amber)'}}>This leaves {fmtINR(remaining - Number(form.amount||0))} unaccounted for — explain why before saving.</div>
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2"><button onClick={onClose} className="btn-outline btn-small">Cancel</button>
        <button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="recovery-save">{saving?'Saving…':'Save'}</button></div>
    </Modal>
  );
}

// Security & Refundables: one group per security type -- the SAME key the type filter uses (a deduction-synced
// row groups under "deduction:<name>", everything else under its instrument type). Figures are the table's own:
// total = sum of amounts shown, pending = the table's "Pending Review" rule, overdue = the row's own overdue test.
function groupSecurityByType(rows, typeKeyOf, typeLabelOf, todayIso) {
  const m = new Map();
  rows.forEach(s => {
    const key = typeKeyOf(s);
    let g = m.get(key);
    if (!g) { g = { key, name: typeLabelOf(s), total: 0, count: 0, refundedAmount: 0, refundedCount: 0, overdueCount: 0, overdueAmount: 0, pendingAmount: 0, pendingCount: 0 }; m.set(key, g); }
    const amt = Number(s.amount || 0);
    g.total += amt; g.count += 1;
    if (s.status === 'refunded') { g.refundedAmount += amt; g.refundedCount += 1; }
    else if (s.refund_due_date && s.refund_due_date < todayIso) { g.overdueCount += 1; g.overdueAmount += amt; }
    if (!s.read_only && !CALC_ELIGIBLE_AUDIT_STATUSES.has(s.audit_status)) { g.pendingAmount += amt; g.pendingCount += 1; }
  });
  return [...m.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}
function SecurityTypeTiles({ groups, activeKeys, onToggle, onClear }) {
  const maxTotal = Math.max(0, ...groups.map(g => g.total));
  return (
    <div className="mb-6 no-print" data-testid="si-type-tiles">
      <div className="flex items-center justify-between mb-2">
        <div className="serif text-xl text-[color:var(--gold)]">By security type</div>
        {activeKeys.length > 0 && <button onClick={onClear} className="tab btn-small" data-testid="si-tile-clear">Show all types</button>}
      </div>
      <div className="vendor-grid">
        {groups.map(g => {
          const tone = stableTileTone(g.key); const active = activeKeys.includes(g.key);
          return (
            <button key={g.key} type="button" onClick={() => onToggle(g.key)} className={"card card-dark vendor-card text-left" + (active ? ' type-tile-active' : '')} aria-pressed={active}
              style={{ '--vendor-accent': tone.color, cursor: 'pointer', padding: 14 }} data-testid={`si-tile-${g.key}`}>
              <div className="serif text-base leading-tight vendor-name" title={g.name}>{g.name}</div>
              <div className="serif mono text-xl mt-2" style={{ color: 'var(--gold)' }} data-testid={`si-tile-amt-${g.key}`}>{fmtINR(g.total)}</div>
              <div className="vendor-bar" aria-hidden="true"><div style={{ width: `${maxTotal > 0 ? Math.max(g.total > 0 ? 2 : 0, Math.round((g.total / maxTotal) * 100)) : 0}%`, background: tone.color }}/></div>
              <div className="text-xs text-muted mt-2" data-testid={`si-tile-meta-${g.key}`}>{g.count} instrument{g.count === 1 ? '' : 's'}{g.refundedCount > 0 ? ` · ${g.refundedCount} refunded` : ''}</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {g.overdueCount > 0 && <span className="text-xs text-red-400" data-testid={`si-tile-overdue-${g.key}`}>{g.overdueCount} overdue · {fmtINR(g.overdueAmount)}</span>}
                {g.pendingCount > 0 && <span className="vendor-pending" data-testid={`si-tile-pending-${g.key}`}><Clock3 size={12}/>{fmtINR(g.pendingAmount)} pending review</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Security & Refundables print / PDF: one section per party (the party name is the section heading), the rows inside a
// section arranged by type (EMD, Fixed Deposit, Bank Guarantee, ... in the same order as the type filter) then by date.
const SECURITY_PRINT_DEFAULTS = { type: false, party: false, borne: true, audit: true };
// "Overdue" is not a stored status: an instrument that is not refunded and whose refund due date has passed (same rule as the Overdue Refund tile).
const SECURITY_OVERDUE_FILTER_ID = 'overdue';
const isSecurityOverdue = (s, today) => s.status !== 'refunded' && !!s.refund_due_date && s.refund_due_date < today;
function groupSecurityForPrint(rows, typeKeyOf, typeLabelOf) {
  const typeOrder = Object.keys(SECURITY_TYPE_LABELS);
  const rank = (s) => { const i = typeOrder.indexOf(s.instrument_type); return typeKeyOf(s).startsWith('deduction:') ? 100 : (i < 0 ? 99 : i); };
  const byParty = new Map();
  rows.forEach(s => { const k = (s.party_name || '').trim() || '\u0000'; if (!byParty.has(k)) byParty.set(k, []); byParty.get(k).push(s); });
  return [...byParty.entries()]
    .map(([k, list]) => ({
      party: k === '\u0000' ? 'No party named' : k,
      rows: list.slice().sort((a, b) => rank(a) - rank(b) || typeLabelOf(a).localeCompare(typeLabelOf(b)) || (a.date_lodged || '').localeCompare(b.date_lodged || '')),
      total: list.reduce((n, s) => n + Number(s.amount || 0), 0),
      none: k === '\u0000',
    }))
    .map(g => {
      // Same order, split into one block per type so the type can be a heading instead of a column.
      const types = [];
      g.rows.forEach(s => { const label = typeLabelOf(s); const last = types[types.length - 1]; if (last && last.label === label) last.rows.push(s); else types.push({ label, rows: [s] }); });
      types.forEach(t => { t.total = t.rows.reduce((n, s) => n + Number(s.amount || 0), 0); });
      return { ...g, types };
    })
    .sort((a, b) => (a.none - b.none) || a.party.localeCompare(b.party));
}
async function exportSecurityPDF({ groups, opts, companyLabel, summary, todayLabel, grandTotal, count, typeLabelOf, partnerName, projectName, today }) {
  const { jsPDF, autoTable } = await loadPdf();
  const doc = new jsPDF({ orientation: 'landscape' });
  doc.setFontSize(15); doc.text('Security & Refundables', 10, 14);
  doc.setFontSize(9);
  doc.text(`${companyLabel}   ·   ${count} instrument${count === 1 ? '' : 's'} in ${groups.length} part${groups.length === 1 ? 'y' : 'ies'}   ·   Generated: ${fmtGeneratedAt()}`, 10, 20);
  if (summary) doc.text(`Filters: ${summary}`, 10, 25, { maxWidth: 270 });
  const head = [['Date Lodged', ...(opts.type ? ['Type'] : []), ...(opts.party ? ['Party'] : []), 'Project', ...(opts.borne ? ['Borne By'] : []), { content: 'Amount', styles: { halign: 'right' } }, 'Refund Due', 'Status', ...(opts.audit ? ['Audit'] : [])]];
  const ncols = head[0].length;
  const body = [];
  groups.forEach(g => {
    body.push([{ content: `${g.party}   —   ${g.rows.length} item${g.rows.length === 1 ? '' : 's'}   ·   ${fmtINRPdf(g.total)}`, colSpan: ncols, styles: { fontStyle: 'bold', fillColor: [240, 232, 205], textColor: [20, 20, 20] } }]);
    g.types.forEach(t => {
    body.push([{ content: `${t.label}   —   ${t.rows.length} item${t.rows.length === 1 ? '' : 's'}   ·   ${fmtINRPdf(t.total)}`, colSpan: ncols, styles: { fontStyle: 'bold', fillColor: [250, 246, 232], textColor: [60, 50, 20] } }]);
    t.rows.forEach(s => {
      const overdue = s.status !== 'refunded' && s.refund_due_date && s.refund_due_date < today;
      body.push([
        s.date_lodged ? fmtDate(s.date_lodged) : '—',
        ...(opts.type ? [typeLabelOf(s)] : []),
        ...(opts.party ? [s.party_name || '—'] : []),
        s.project_id ? projectName(s.project_id) : '—',
        ...(opts.borne ? [s.borne_by_partner_id ? partnerName(s.borne_by_partner_id) : (s.paid_from_company_fund ? 'Company Fund' : '—')] : []),
        { content: fmtINRPdf(s.amount), styles: { halign: 'right' } },
        s.refund_due_date ? `${fmtDate(s.refund_due_date)}${overdue ? ' (overdue)' : ''}` : '—',
        SECURITY_STATUS_LABELS[s.status] || s.status,
        ...(opts.audit ? [s.read_only ? '—' : (s.audit_status || '').replace('_', ' ')] : []),
      ]);
    });
    });
  });
  body.push([{ content: 'Total', colSpan: ncols - (opts.audit ? 4 : 3), styles: { halign: 'right', fontStyle: 'bold' } }, { content: fmtINRPdf(grandTotal), styles: { halign: 'right', fontStyle: 'bold' } }, { content: '', colSpan: opts.audit ? 3 : 2 }]);
  autoTable(doc, {
    startY: summary ? 30 : 25, head, body, margin: { left: 10, right: 10 },
    styles: { fontSize: 8.5, cellPadding: 2 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
    didDrawPage: () => { doc.setFontSize(8); doc.text(`Page ${doc.internal.getNumberOfPages()}`, 287, 205, { align: 'right' }); },
  });
  doc.save(`security-and-refundables-${todayLabel}.pdf`);
}

// ---- FY-wise ITD / TDS refunds -------------------------------------------------------------------------------
// TDS a client withheld comes back from the Income Tax Department one financial year at a time. One refund received is
// recorded once, allocated across the TDS instruments of that FY (each gets its project receipt, as Record Recovery does)
// and stays linked to them.
function ItdTdsYearsCard({ data, onRecord, canEdit }) {
  const [open, setOpen] = useState(false);
  const [openRows, setOpenRows] = useState({});
  const years = (data && data.years) || [];
  if (!years.length) return null;
  const multiCompany = new Set(years.map(y => y.company_id)).size > 1;
  const key = (y) => `${y.company_id}|${y.financial_year}`;
  return (
    <div className="card card-dark mb-6 no-print" data-testid="itd-years-card">
      <button onClick={() => setOpen(v => !v)} className="flex items-center justify-between w-full text-left">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">ITD / TDS refunds — by financial year</div>
          <div className="text-xs text-muted mt-1">TDS is refunded by the Income Tax Department year by year. Record each refund once and it settles — and stays linked to — the TDS entries of that year.</div>
        </div>
        {open ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
      </button>
      {open && (
        <div className="overflow-x-auto mt-4">
          <table className="tbl">
            <thead><tr><th>Financial year</th>{multiCompany && <th>Company</th>}<th className="text-right">TDS deducted</th><th className="text-right">Refunded</th><th className="text-right">Shortfall</th><th className="text-right">Still to refund</th><th>Refunds</th><th className="no-print"></th></tr></thead>
            <tbody>
              {years.map(y => (
                <React.Fragment key={key(y)}>
                  <tr data-testid={`itd-year-${y.financial_year}`}>
                    <td>{y.financial_year}</td>
                    {multiCompany && <td className="text-xs">{y.company_name}</td>}
                    <td className="mono text-right">{fmtINR(y.deducted)}</td>
                    <td className="mono text-right">{fmtINR(y.refunded)}</td>
                    <td className="mono text-right">{y.shortfall > 0 ? <span className="text-red-400">{fmtINR(y.shortfall)}</span> : '—'}</td>
                    <td className="mono text-right text-[color:var(--gold)]">{fmtINR(y.outstanding)}{y.outstanding_count > 0 && <span className="text-xs text-muted"> · {y.outstanding_count}</span>}</td>
                    <td className="text-xs">{y.refunds.length ? <button className="tab btn-small" onClick={() => setOpenRows(o => ({ ...o, [key(y)]: !o[key(y)] }))}>{y.refunds.length} recorded {openRows[key(y)] ? '▴' : '▾'}</button> : '—'}</td>
                    <td className="text-right no-print">{canEdit && y.outstanding_count > 0 && <button className="btn-gold btn-small" onClick={() => onRecord(y)} data-testid={`itd-record-${y.financial_year}`}>Record refund</button>}</td>
                  </tr>
                  {openRows[key(y)] && y.refunds.map(rf => (
                    <tr key={rf.id}><td colSpan={multiCompany ? 8 : 7} className="text-xs" style={{ background: 'rgba(217,164,65,0.06)' }}>
                      <div><b>{fmtDate(rf.refund_date)}</b> · {fmtINR(rf.amount_received)} received{rf.reference_no ? ` · ref ${rf.reference_no}` : ''}{rf.interest_amount > 0 ? ` · interest ${fmtINR(rf.interest_amount)} (reference only)` : ''}{rf.notes ? ` · ${rf.notes}` : ''}</div>
                      <div className="text-muted mt-1">{rf.links.map(l => `${l.project_name || l.party_name || 'instrument'} ${fmtINR(l.allocated_amount)}`).join('  ·  ')}</div>
                    </td></tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ItdRefundModal({ open, onClose, onSaved, data, initial }) {
  const [companyId, setCompanyId] = useState('');
  const [fy, setFy] = useState('');
  const [rows, setRows] = useState({});          // security id -> { on, amount, note }
  const [refundDate, setRefundDate] = useState('');
  const [received, setReceived] = useState('');
  const [bankId, setBankId] = useState('');
  const [reference, setReference] = useState('');
  const [interest, setInterest] = useState('');
  const [notes, setNotes] = useState('');
  const [toFund, setToFund] = useState(false);
  const [banks, setBanks] = useState([]);
  const [saving, setSaving] = useState(false);
  const years = (data && data.years) || [];
  const bucket = years.find(y => y.company_id === companyId && y.financial_year === fy);
  const candidates = (bucket && bucket.candidates) || [];
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!open) return;
    const y = initial || years.find(x => x.outstanding_count > 0) || years[0];
    setCompanyId(y ? y.company_id : ''); setFy(y ? y.financial_year : '');
    setRefundDate(today); setReceived(''); setBankId(''); setReference(''); setInterest(''); setNotes(''); setToFund(false);
    api.get('/bank-accounts').then(r => setBanks(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // default allocation: every instrument of that FY (not on a locked project) at its full amount
  useEffect(() => {
    if (!open) return;
    const next = {};
    candidates.forEach(c => { next[c.id] = { on: !c.project_locked, amount: String(c.amount), note: '' }; });
    setRows(next);
    setReceived(String(Math.round(candidates.filter(c => !c.project_locked).reduce((a, c) => a + c.amount, 0) * 100) / 100 || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, fy]);

  const picked = candidates.filter(c => rows[c.id] && rows[c.id].on);
  const allocTotal = Math.round(picked.reduce((a, c) => a + (Number(rows[c.id].amount) || 0), 0) * 100) / 100;
  const diff = Math.round(((Number(received) || 0) - allocTotal) * 100) / 100;
  const shortRows = picked.filter(c => (Number(rows[c.id].amount) || 0) < c.amount - 0.005);
  const missingNote = shortRows.some(c => !(rows[c.id].note || '').trim());
  const overRows = picked.filter(c => (Number(rows[c.id].amount) || 0) > c.amount + 0.005);
  const lockedPicked = picked.filter(c => c.project_locked);
  const problem = !companyId || !fy ? 'Choose the financial year.'
    : !picked.length ? 'Tick at least one instrument.'
    : !(Number(received) > 0) ? 'Enter the amount received.'
    : Math.abs(diff) > 0.01 ? `Amount received differs from the ticked allocations by ${fmtINR(Math.abs(diff))}.`
    : overRows.length ? 'An allocation is more than that instrument\'s amount.'
    : missingNote ? 'Add a reason for every instrument allocated less than its full amount.'
    : (lockedPicked.length && !toFund) ? 'An instrument on an approved & locked project needs "also add to Company Fund" ticked (full amount only).'
    : !refundDate ? 'Refund date is required.' : null;
  const autoFill = () => {
    let left = Number(received) || 0; const next = { ...rows };
    candidates.filter(c => !c.project_locked).forEach(c => {
      const give = Math.min(c.amount, Math.max(0, left)); left = Math.round((left - give) * 100) / 100;
      next[c.id] = { ...(next[c.id] || { note: '' }), on: give > 0, amount: String(give || c.amount) };
    });
    setRows(next);
  };
  const save = async () => {
    if (saving || problem) return;
    setSaving(true);
    try {
      const r = (await api.post('/itd-tds-refunds', {
        company_id: companyId, financial_year: fy, refund_date: refundDate, amount_received: Number(received), bank_account_id: bankId || null,
        reference_no: reference.trim() || null, interest_amount: Number(interest) || 0, notes: notes.trim() || null, add_to_company_fund: toFund,
        allocations: picked.map(c => ({ security_id: c.id, amount: Number(rows[c.id].amount), shortfall_note: (rows[c.id].note || '').trim() || null })),
      })).data;
      toast.success(`Refund recorded and linked to ${r.linked} instrument${r.linked === 1 ? '' : 's'}`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record the refund'); }
    finally { setSaving(false); }
  };
  const companiesInData = [...new Map(years.map(y => [y.company_id, y.company_name])).entries()];
  const fysForCompany = years.filter(y => y.company_id === companyId && y.outstanding_count > 0).map(y => y.financial_year);
  return (
    <Modal open={open} onClose={onClose} title="Record ITD / TDS refund" maxW={900}>
      <div data-testid="itd-refund-modal">
        <p className="text-sm text-muted mb-4">Enter the refund the Income Tax Department paid for one financial year (the year the TDS was <b>deducted</b>), tick the TDS entries it covers and split the amount between them. Each ticked entry gets a receipt on its project and becomes Refunded; all of them stay linked to this refund.</p>
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          {companiesInData.length > 1 && <Field label="Company"><select className="input" value={companyId} onChange={e => { setCompanyId(e.target.value); setFy(''); }}>{companiesInData.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></Field>}
          <Field label="Financial year *"><select className="input" value={fy} onChange={e => setFy(e.target.value)} data-testid="itd-fy"><option value="">— choose —</option>{[...new Set([...fysForCompany, fy].filter(Boolean))].map(f => <option key={f} value={f}>{f}</option>)}</select></Field>
          <Field label="Refund date *"><input type="date" className="input" max={today} value={refundDate} onChange={e => setRefundDate(e.target.value)}/></Field>
          <Field label="Amount received *"><input className="input mono" type="number" min="0" value={received} onChange={e => setReceived(e.target.value)} data-testid="itd-received"/></Field>
          <Field label="Received in bank account"><select className="input" value={bankId} onChange={e => setBankId(e.target.value)}><option value="">— not specified —</option>{banks.filter(b => b.company_id === companyId).map(b => <option key={b.id} value={b.id}>{b.bank_name}{b.account_number ? ` · ${String(b.account_number).slice(-4)}` : ''}</option>)}</select></Field>
          <Field label="ITD reference / refund order no."><input className="input" value={reference} onChange={e => setReference(e.target.value)} data-testid="itd-reference"/></Field>
          <Field label="Interest paid by ITD (reference only)"><input className="input mono" type="number" min="0" value={interest} onChange={e => setInterest(e.target.value)}/></Field>
          <Field label="Notes"><input className="input" value={notes} onChange={e => setNotes(e.target.value)}/></Field>
        </div>
        {fy && candidates.length === 0 && <div className="text-sm text-muted py-4">Nothing left to refund for {fy}.</div>}
        {candidates.length > 0 && (
          <div className="overflow-x-auto">
            <div className="flex items-center justify-between mb-2"><div className="text-sm">Instruments with TDS deducted in <b>{fy}</b> still to be refunded</div><button className="btn-outline btn-small" onClick={autoFill} data-testid="itd-autofill">Fill from amount received (oldest first)</button></div>
            <table className="tbl">
              <thead><tr><th></th><th>Project</th><th>Deducted</th><th className="text-right">TDS amount</th><th className="text-right">Refunded now</th><th>Reason if less</th></tr></thead>
              <tbody>
                {candidates.map(c => {
                  const r = rows[c.id] || { on: false, amount: '', note: '' };
                  const lessThan = r.on && (Number(r.amount) || 0) < c.amount - 0.005;
                  return (
                    <tr key={c.id} style={c.project_locked && !toFund ? { opacity: 0.55 } : undefined}>
                      <td><input type="checkbox" checked={!!r.on} disabled={c.project_locked && !toFund} onChange={e => setRows({ ...rows, [c.id]: { ...r, on: e.target.checked } })}/></td>
                      <td className="text-xs">{c.project_name || c.party_name || '—'}{c.project_locked && <div style={{ color: 'var(--accent-amber)' }}>project approved &amp; locked</div>}</td>
                      <td className="text-xs">{fmtDate(c.date_lodged)}</td>
                      <td className="mono text-right">{fmtINR(c.amount)}</td>
                      <td className="text-right"><input className="input mono" style={{ width: 110, textAlign: 'right' }} type="number" min="0" disabled={!r.on} value={r.amount} onChange={e => setRows({ ...rows, [c.id]: { ...r, amount: e.target.value } })}/></td>
                      <td>{lessThan ? <input className="input" placeholder="why less? (required)" value={r.note} onChange={e => setRows({ ...rows, [c.id]: { ...r, note: e.target.value } })}/> : <span className="text-muted text-xs">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <label className="flex items-center gap-2 text-xs mt-3 cursor-pointer"><input type="checkbox" checked={toFund} onChange={e => setToFund(e.target.checked)}/>Also add the money to the shared Company Fund (needed for entries on a locked project; only for the full amount)</label>
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">Ticked <b className="mono">{fmtINR(allocTotal)}</b> of <b className="mono">{fmtINR(Number(received) || 0)}</b> received{Math.abs(diff) > 0.01 && <span className="text-red-400"> · difference {fmtINR(diff)}</span>}</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-outline btn-small">Cancel</button>
            <button onClick={save} disabled={saving || !!problem} title={problem || ''} className="btn-gold btn-small" data-testid="itd-save">{saving ? 'Recording…' : 'Record refund'}</button>
          </div>
        </div>
        {problem && picked.length > 0 && <div className="text-xs mt-2" style={{ color: 'var(--accent-amber)' }}>{problem}</div>}
      </div>
    </Modal>
  );
}

function SecurityInstrumentsPanel() {
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const canDelete = isMasterAdmin(user);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editSeed, setEditSeed] = useState(null);
  const [recoveryRow, setRecoveryRow] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  // User-requested: multi-select so several instruments can be marked
  // Received (Refunded) together instead of one Edit modal at a time.
  // Excludes read-only bill-linked rows (their own separate "Record
  // Recovery" flow both records the receipt AND reduces the bill's
  // withholding field, so a plain status flip here would leave that
  // reduction undone) and rows already Refunded (nothing left to mark).
  const [selected, setSelected] = useState(() => new Set());
  const toggleSelect = (id) => setSelected(s => { const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const [markingReceived, setMarkingReceived] = useState(false);
  const companyF = useMultiFilter('SecurityInstrumentsPanel.company');
  const typeF = useMultiFilter('SecurityInstrumentsPanel.type');
  const statusF = useMultiFilter('SecurityInstrumentsPanel.status');
  // User-requested: same client-wise filter Projects/Audit Queue/Bills Hub/
  // Income already have -- resolved via a project_id -> client_id map
  // built off this page's own /projects fetch, no extra request needed.
  // A company-level instrument (no project_id) naturally won't match any
  // client, same as those other pages' own unlinked rows.
  const clientF = useMultiFilter('SecurityInstrumentsPanel.client');
  // User-requested: FY filter, same distinctFYs/FySelect convention every
  // other list page in the app already uses -- a dropdown of years that
  // actually exist in this data, not a free-text box to mistype.
  const fyF = useMultiFilter('SecurityInstrumentsPanel.fy');
  const masters = useMasters();
  const { companies, needsPicker } = useMyCompanies();
  const load = async () => {
    const [i, s, p, pt] = await Promise.all([
      api.get('/security-instruments'), api.get('/security-instruments/summary'),
      api.get('/projects'), api.get('/partners'),
    ]);
    setItems(i.data); setSummary(s.data); setProjects(p.data); setPartners(pt.data);
  };
  useEffect(() => { load(); }, []);
  const projectName = (id) => projects.find(p=>p.id===id)?.project_name || '—';
  const partnerName = (id) => partners.find(p=>p.id===id)?.name || '—';
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  const today = new Date().toISOString().slice(0,10);
  const projectClientMap = useMemo(() => new Map(projects.map(p => [p.id, clientIdOfProject(p, masters.department_client)])), [projects, masters.department_client]);
  const clientOptions = useMemo(() =>
    (masters.department_client || [])
      .filter(m => companyF.all || (m.company_ids||[]).some(cid => companyF.ids.includes(cid)))
      .slice().sort((a,b)=>a.name.localeCompare(b.name)),
    [masters.department_client, companyF.all, companyF.ids]);
  // A company filter change can leave a stale Client selection that no
  // longer belongs to it -- clear rather than silently filtering to zero,
  // same guard the other client-filter pages already apply.
  useEffect(() => {
    clientF.prune(clientOptions.map(c => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyF.all, companyF.ids]);
  // Built off the rows actually loaded, not a hardcoded list -- any
  // deduction type that currently has at least one receipt-synced row
  // gets its own filter option automatically (see deductionSyncedTypeName
  // above), with no future code change needed when a new deduction type
  // is added under Settings > Masters.
  const deductionTypeOptions = useMemo(() =>
    [...new Set(items.map(deductionSyncedTypeName).filter(Boolean))].sort((a,b)=>a.localeCompare(b)),
    [items]);
  const securityTypeKey = (s) => deductionSyncedTypeName(s) ? deductionSyncFilterValue(deductionSyncedTypeName(s)) : s.instrument_type;
  // Everything except the type filter, so the type tiles keep showing every type while one is selected.
  const baseItems = items
    .filter(s => companyF.matches(s.company_id))
    .filter(s => statusF.all || statusF.ids.includes(s.status) || (statusF.ids.includes(SECURITY_OVERDUE_FILTER_ID) && isSecurityOverdue(s, today)))
    .filter(s => clientF.matches(projectClientMap.get(s.project_id)))
    .filter(s => fyF.matches(fyFromDateStr(s.date_lodged)));
  const visibleItems = baseItems
    // A deduction-synced row's own type filter (e.g. "Security Deposit",
    // "ITD/TDS") and the fixed enum options (EMD/FD/BG/DD/Advance/Other)
    // are mutually exclusive here, even when they'd otherwise share the
    // same raw instrument_type -- see deductionSyncedTypeName/
    // securityTypeLabel above.
    .filter(s => typeF.matches(securityTypeKey(s)));
  const typeTileGroups = groupSecurityByType(baseItems, securityTypeKey, securityTypeLabel, today);
  const fyOptions = useMemo(() => distinctFYs(items.map(s => fyFromDateStr(s.date_lodged))), [items]);
  // User-requested: Security & Refundables now goes through the same
  // Audit Queue review workflow as expenses/receipts -- a still-pending
  // instrument doesn't yet count toward project_profit's forfeiture
  // figure or a partner's owed balance. Deduction-synced rows (read_only)
  // have no real audit_status of their own, so they're excluded here.
  const pendingSecurityTotal = visibleItems.reduce((a,s)=>a+((!s.read_only && !CALC_ELIGIBLE_AUDIT_STATUSES.has(s.audit_status))?Number(s.amount||0):0),0);
  // User-requested: same "Confirmed Total" footer row the sibling
  // Company Expenses / Profit Adjustments lists already have -- this one
  // only ever showed the Pending-Review subset, never an overall total
  // of everything currently shown.
  const securityTotal = visibleItems.reduce((a,s)=>a+Number(s.amount||0),0);
  // User-requested: a running total for whatever's currently checked, same
  // "Selected Total" convenience the bulk-action toolbar's count already
  // hints at but never surfaced in rupees -- lets someone tally an
  // arbitrary subset (e.g. "how much across these 3 EMDs") without adding
  // them up by hand.
  const selectedTotal = visibleItems.reduce((a,s)=>a+(selected.has(s.id)?Number(s.amount||0):0),0);
  const { sorted: sortedItems, sortKey, sortDir, toggleSort } = useSortableRows(visibleItems, 'date_lodged', 'desc');
  // Print / PDF: grouped by party, optional Party / Borne By / Audit columns, no Company column.
  const [printOpen, setPrintOpen] = useState(false);
  const [itdData, setItdData] = useState(null);
  const [itdOpen, setItdOpen] = useState(false);
  const [itdInitial, setItdInitial] = useState(null);
  const loadItd = () => { if (canEdit) api.get('/itd-tds-refunds/overview').then(r => setItdData(r.data)).catch(() => {}); };
  useEffect(() => { loadItd(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [canEdit]);
  const [printOpts, setPrintOpts] = usePersistedState('Security.printOpts', SECURITY_PRINT_DEFAULTS);
  const printGroups = groupSecurityForPrint(visibleItems, securityTypeKey, securityTypeLabel);
  const printCompanyLabel = needsPicker ? (companyF.all ? 'All Companies' : companyF.ids.map(companyName).join(', ')) : companies[0]?.name;
  const printFilterSummary = [
    !typeF.all ? `Type: ${typeF.ids.map(k => SECURITY_TYPE_LABELS[k] || String(k).replace(/^deduction:/, '')).join(', ') || 'none'}` : null,
    !statusF.all ? `Status: ${statusF.ids.map(k => k === SECURITY_OVERDUE_FILTER_ID ? 'Overdue' : (SECURITY_STATUS_LABELS[k] || k)).join(', ') || 'none'}` : null,
    !clientF.all ? `Client: ${clientF.ids.map(id => clientOptions.find(c => c.id === id)?.name || '—').join(', ') || 'none'}` : null,
    !fyF.all ? `Year: ${fyF.ids.join(', ') || 'none'}` : null,
  ].filter(Boolean).join(' · ');
  const downloadSecurityPdf = async () => {
    setPrintOpen(false);
    try {
      await exportSecurityPDF({ groups: printGroups, opts: printOpts, companyLabel: printCompanyLabel, summary: printFilterSummary, todayLabel: today, grandTotal: securityTotal, count: visibleItems.length, typeLabelOf: securityTypeLabel, partnerName, projectName, today });
    } catch (e) { toast.error('Could not build the PDF'); }
  };
  const doSecurityPrint = () => { setPrintOpen(false); setTimeout(() => printLandscape(), 150); };
  // A real deduction-linked row's own amount owing, net of whatever a
  // prior partial Record Recovery already settled -- settled_amount is
  // only ever non-null while status==='refunded' (_normalize_security_
  // settlement), matching RecordRecoveryModal's own `remaining` calc for
  // the non-bill-linked case.
  const remainingToRecover = (s) => Math.max(0, Number(s.amount||0) - (s.status==='refunded' ? Number(s.settled_amount||0) : 0));
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/security-instruments/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  // User-requested: bulk marking used to blindly set Settled Amount to
  // the full lodged amount for every row with no way to say otherwise --
  // exactly the TDS-certificate-vs-actual-credit gap discussed for the
  // single-row flow can just as easily span several rows marked at once.
  // This modal reviews each selected row with an editable Amount Received
  // (defaulting to the full amount, so the common "all received in full"
  // case is still zero extra typing), and requires a per-row reason the
  // moment any one row's entered amount falls short -- same
  // _require_forfeiture_note rule the backend already enforces per-row,
  // so this needs no backend change at all.
  const [bulkReceiveOpen, setBulkReceiveOpen] = useState(false);
  const [bulkDrafts, setBulkDrafts] = useState({});
  const [bulkDate, setBulkDate] = useState('');
  const openBulkReceive = () => {
    const drafts = {};
    for (const id of selected) {
      const row = items.find(x => x.id === id);
      if (row) drafts[id] = { amount: row.amount, note: '' };
    }
    setBulkDrafts(drafts);
    setBulkDate(today);
    setBulkReceiveOpen(true);
  };
  const setBulkDraft = (id, field, value) => setBulkDrafts(d => ({ ...d, [id]: { ...d[id], [field]: value } }));
  const saveBulkReceive = async () => {
    if (markingReceived) return;
    const ids = Object.keys(bulkDrafts);
    for (const id of ids) {
      const row = items.find(x => x.id === id);
      const d = bulkDrafts[id];
      if (!row || !d) continue;
      if (!d.amount || Number(d.amount) <= 0) { toast.error(`Amount Received is required for ${securityTypeLabel(row)}.`); return; }
      if (Number(d.amount) > Number(row.amount) + 0.01) { toast.error(`Amount Received for ${securityTypeLabel(row)} can't exceed the lodged amount (${fmtINR(row.amount)}).`); return; }
      if (Number(d.amount) < Number(row.amount) - 0.005 && !(d.note||'').trim()) { toast.error(`${securityTypeLabel(row)} settles for less than the full lodged amount — add a reason before saving.`); return; }
    }
    setMarkingReceived(true);
    const failed = [];
    for (const id of ids) {
      const row = items.find(x => x.id === id);
      const d = bulkDrafts[id];
      if (!row || !d) continue;
      try {
        const notes = (d.note||'').trim() ? (row.notes ? `${row.notes}\n${d.note.trim()}` : d.note.trim()) : row.notes;
        await api.patch(`/security-instruments/${id}`, { ...row, status: 'refunded', settlement_date: bulkDate, settled_amount: Number(d.amount), notes });
      } catch (e) { failed.push({ id, detail: e.response?.data?.detail || 'Error' }); }
    }
    const succeeded = ids.length - failed.length;
    if (failed.length === 0) toast.success(`Marked ${succeeded} as Received`);
    else if (succeeded === 0) toast.error(`None updated — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    else toast.warning(`Marked ${succeeded}, ${failed.length} failed — ${failed[0].detail}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ''}`);
    setSelected(new Set(failed.map(f => f.id)));
    setMarkingReceived(false);
    if (failed.length === 0) setBulkReceiveOpen(false);
    load();
  };
  // Same eligibility a checkbox itself is gated on below -- used to drive
  // the toolbar's Select All state without duplicating the condition.
  const selectableIds = visibleItems.filter(s => !s.read_only && s.status !== 'refunded').map(s => s.id);
  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="section-eyebrow mb-2">EMD · FD · Demand Draft (DD) · Refundable Advance</div>
          <h1 className="serif text-4xl">Security & Refundables</h1>
          <div className="text-sm text-muted mt-1">Money held in trust, not spend or income — kept separate from expenses and never counted against project profit.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {needsPicker && <MultiFilterPicker filter={companyF} items={companies.map(c=>({id:c.id, name:c.name}))} allLabel="All Companies" width={170} testId="si-filter-company"/>}
          <MultiFilterPicker filter={typeF} items={[...Object.entries(SECURITY_TYPE_LABELS).map(([k,l])=>({id:k, name:l})), ...deductionTypeOptions.map(t=>({id:deductionSyncFilterValue(t), name:t}))]} allLabel="All Types" width={150} testId="si-filter-type"/>
          <MultiFilterPicker filter={statusF} items={[...Object.entries(SECURITY_STATUS_LABELS).map(([k,l])=>({id:k, name:l})), {id:SECURITY_OVERDUE_FILTER_ID, name:'Overdue (refund date passed)'}]} allLabel="All Statuses" width={160} testId="si-filter-status"/>
          {clientOptions.length > 0 && <MultiFilterPicker filter={clientF} items={clientOptions.map(c=>({id:c.id, name:c.name}))} allLabel="All Clients" width={170} testId="si-filter-client"/>}
          <MultiFilterPicker filter={fyF} items={fyOptions.map(y=>({id:y, name:y}))} allLabel="All Years" width={150} testId="si-filter-fy"/>
          <ClearFiltersButton filters={[companyF, typeF, statusF, clientF, fyF]}/>
          {canEdit && <button onClick={()=>setBulkOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="si-bulk-import"><Upload size={14}/>Bulk Import</button>}
          {canEdit && itdData && (itdData.years || []).length > 0 && <button onClick={()=>{ setItdInitial(null); setItdOpen(true); }} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="si-itd-refund">ITD/TDS Refund</button>}
          {selected.size > 0 && <span className="text-xs text-muted" data-testid="si-selected-total">{selected.size} selected — <span className="mono text-[color:var(--gold)]">{fmtINR(selectedTotal)}</span></span>}
          {canEdit && selected.size > 0 && <button onClick={openBulkReceive} disabled={markingReceived} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="si-mark-received">{markingReceived ? 'Marking…' : `Mark ${selected.size} Received`}</button>}
          {visibleItems.length > 0 && <button onClick={()=>setPrintOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="si-print"><Printer size={14}/>Print</button>}
          {canEdit && <button onClick={()=>{setEditSeed({date_lodged:today,instrument_type:'emd',status:'pending'});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Instrument</button>}
        </div>
      </div>

      {summary && (
        <div className="grid md:grid-cols-4 gap-4 mb-6 no-print">
          <KpiCard label="Outstanding Security" value={fmtINR(summary.outstanding_security)} hint={`${summary.outstanding_count} instrument(s)`} gold/>
          <KpiCard label="Pending Refund" value={fmtINR(summary.pending_refund)} hint={`${summary.pending_refund_count} tracked with a due date`}/>
          <KpiCard label="Overdue Refund" value={fmtINR(summary.overdue_refund)} hint={summary.overdue_count>0 ? `${summary.overdue_count} past due — chase these` : 'None overdue'}/>
          <KpiCard label="Returned Amount" value={fmtINR(summary.returned_amount)} hint={`${summary.returned_count} refunded`}/>
        </div>
      )}

      {canEdit && <ItdTdsYearsCard data={itdData} canEdit={canEdit} onRecord={(y) => { setItdInitial(y); setItdOpen(true); }}/>}
      {typeTileGroups.length > 0 && <SecurityTypeTiles groups={typeTileGroups} activeKeys={typeF.all ? [] : typeF.ids} onToggle={key => { if (typeF.all) typeF.only(key); else if (typeF.ids.includes(key) && typeF.ids.length === 1) typeF.reset(); else typeF.toggle(key); }} onClear={typeF.reset}/>}
      <div className="print-area sec-print-only" data-testid="si-print-groups">
        <div className="print-header">
          <div className="print-company">{printCompanyLabel}</div>
          <div className="print-report-title">Security & Refundables</div>
          <div className="print-meta">{visibleItems.length} instrument{visibleItems.length === 1 ? '' : 's'} · {printGroups.length} part{printGroups.length === 1 ? 'y' : 'ies'} · Generated: {fmtGeneratedAt()}{printFilterSummary ? ` · Filters: ${printFilterSummary}` : ''}</div>
        </div>
        {printGroups.map(g => (
          <div key={g.party} className="sec-party">
            <div className="sec-party-head" data-testid="si-print-party">{g.party} <span className="sec-party-sub">— {g.rows.length} item{g.rows.length === 1 ? '' : 's'} · {fmtINR(g.total)}</span></div>
            <table className="tbl">
              <thead><tr><th>Date Lodged</th>{printOpts.type && <th data-testid="sec-col-type">Type</th>}{printOpts.party && <th data-testid="sec-col-party">Party</th>}<th>Project</th>{printOpts.borne && <th data-testid="sec-col-borne">Borne By</th>}<th className="text-right">Amount</th><th>Refund Due</th><th>Status</th>{printOpts.audit && <th data-testid="sec-col-audit">Audit</th>}</tr></thead>
              <tbody>
                {g.types.map(t => (<React.Fragment key={t.label}>
                <tr className="sec-type-row" data-testid="si-print-type"><td colSpan={5 + (printOpts.type ? 1 : 0) + (printOpts.party ? 1 : 0) + (printOpts.borne ? 1 : 0) + (printOpts.audit ? 1 : 0)}>{t.label} <span className="sec-party-sub">— {t.rows.length} item{t.rows.length === 1 ? '' : 's'} · {fmtINR(t.total)}</span></td></tr>
                {t.rows.map(s => {
                  const overdue = s.status !== 'refunded' && s.refund_due_date && s.refund_due_date < today;
                  return (
                    <tr key={s.id}>
                      <td className="text-xs">{fmtDate(s.date_lodged)}</td>
                      {printOpts.type && <td className="text-xs">{securityTypeLabel(s)}</td>}
                      {printOpts.party && <td className="text-xs">{s.party_name || '—'}</td>}
                      <td className="text-xs">{s.project_id ? projectName(s.project_id) : '—'}</td>
                      {printOpts.borne && <td className="text-xs">{s.borne_by_partner_id ? partnerName(s.borne_by_partner_id) : (s.paid_from_company_fund ? 'Company Fund' : '—')}</td>}
                      <td className="mono text-right">{fmtINR(s.amount)}</td>
                      <td className="text-xs">{s.refund_due_date ? `${fmtDate(s.refund_due_date)}${overdue ? ' (overdue)' : ''}` : '—'}</td>
                      <td className="text-xs">{SECURITY_STATUS_LABELS[s.status] || s.status}</td>
                      {printOpts.audit && <td className="text-xs">{s.read_only ? '—' : (s.audit_status || '').replace('_', ' ')}</td>}
                    </tr>
                  );
                })}
                </React.Fragment>))}
              </tbody>
            </table>
          </div>
        ))}
        <div className="sec-print-total">Total: <b>{fmtINR(securityTotal)}</b></div>
      </div>
      <ItdRefundModal open={itdOpen} onClose={() => setItdOpen(false)} data={itdData} initial={itdInitial} onSaved={() => { load(); loadItd(); }}/>
      <Modal open={printOpen} onClose={() => setPrintOpen(false)} title="Print Security & Refundables" maxW={520}>
        <div data-testid="si-print-dialog">
          <p className="text-sm text-muted mb-4">Prints the <b>{visibleItems.length}</b> instrument{visibleItems.length === 1 ? '' : 's'} shown now (filters apply), grouped under each <b>party's name</b>, with the <b>type</b> as a heading inside it. The company name is not repeated on every row. Tick the columns you want on each row.</p>
          {[['type', 'Type', 'repeat the type (EMD, Fixed Deposit …) on every row — it is already a heading inside each party'], ['party', 'Party', 'repeat the party name on every row (it is already the heading of each section)'], ['borne', 'Borne By', 'the partner who personally bore it, or Company Fund'], ['audit', 'Audit status', 'pending review / matched / final']].map(([k, label, hint]) => (
            <label key={k} className="flex items-start gap-3 mb-3 cursor-pointer">
              <input type="checkbox" className="mt-1" checked={!!printOpts[k]} onChange={e => setPrintOpts({ ...printOpts, [k]: e.target.checked })} data-testid={`si-print-opt-${k}`}/>
              <span><span className="text-sm">{label}</span><span className="block text-xs text-muted">{hint}</span></span>
            </label>
          ))}
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button onClick={() => setPrintOpen(false)} className="btn-outline btn-small">Cancel</button>
            <button onClick={downloadSecurityPdf} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="si-print-pdf"><Download size={14}/>Download PDF</button>
            <button onClick={doSecurityPrint} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="si-print-go"><Printer size={14}/>Print</button>
          </div>
        </div>
      </Modal>
      <div className="card card-dark overflow-x-auto no-print">
        <div className="print-header">
          <div className="print-company">{printCompanyLabel}</div>
          <div className="print-report-title">Security & Refundables</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        <table className="tbl">
          <thead><tr>{canEdit && <th className="no-print">
            {selectableIds.length > 0 && <input type="checkbox" checked={selectableIds.every(id=>selected.has(id))} onChange={e=>setSelected(e.target.checked ? new Set(selectableIds) : new Set())} aria-label="Select all"/>}
          </th>}<SortTh label="Date Lodged" sortKeyName="date_lodged" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/><th>Type</th>{needsPicker && <th>Company</th>}<th>Party</th><th>Project</th><th>Borne By</th><SortTh label="Amount" sortKeyName="amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/><th>Refund Due</th><th>Status</th><th>Audit</th>{canEdit && <th className="no-print"></th>}</tr></thead>
          <tbody>
            {visibleItems.length===0 && <tr><td colSpan={12} className="text-center text-muted py-8">{items.length===0 ? 'No security instruments recorded yet.' : 'No instruments match these filters.'}</td></tr>}
            {sortedItems.map(s => {
              const overdue = s.status!=='refunded' && s.refund_due_date && s.refund_due_date < today;
              return (
                <tr key={s.id}>
                  {canEdit && <td className="no-print">{!s.read_only && s.status!=='refunded' && <input type="checkbox" checked={selected.has(s.id)} onChange={()=>toggleSelect(s.id)} aria-label={`Select ${securityTypeLabel(s)} — ${fmtINR(s.amount)}`}/>}</td>}
                  <td className="text-xs">{fmtDate(s.date_lodged)}</td>
                  <td className="text-xs">{securityTypeLabel(s)}{s.source==='deduction' && <span className="chip ml-1" style={{fontSize:'10px'}} title="Withheld from a bill — edit the bill to change this">From Bill</span>}</td>
                  {needsPicker && <td className="text-xs">{companyName(s.company_id)}</td>}
                  <td className="text-xs">{s.party_name||'—'}</td>
                  <td className="text-xs">{s.project_id ? (s.source==='deduction' ? <Link to={`/app/projects/${s.project_id}`} className="hover:text-[color:var(--gold)]">{projectName(s.project_id)}</Link> : projectName(s.project_id)) : '—'}</td>
                  <td className="text-xs">{s.borne_by_partner_id ? partnerName(s.borne_by_partner_id) : '—'}
                    {(s.itd_refunds || []).length > 0 && <div><span className="chip" style={{fontSize:'10px', background:'rgba(134,239,172,0.15)'}} title={s.itd_refunds.map(l => `${l.financial_year} refund of ${fmtINR(l.allocated_amount)} on ${fmtDate(l.refund_date)}${l.reference_no ? ' (ref ' + l.reference_no + ')' : ''}`).join('\n')}>ITD refund {s.itd_refunds[0].financial_year}</span></div>}
                    {s.capital_locked_partner_id && <div><span className="chip" style={{fontSize:'10px', background: ['pending','hold','settlement_required'].includes(s.status) ? 'rgba(217,164,65,0.18)' : undefined}} title={`${fmtINR(s.capital_locked_amount)} of ${partnerName(s.capital_locked_partner_id)}'s capital is tied up in this instrument` + (['pending','hold','settlement_required'].includes(s.status) ? ' — locked until it is refunded.' : ' — released (this instrument is resolved).')}>{['pending','hold','settlement_required'].includes(s.status) ? 'Locked:' : 'Released:'} {partnerName(s.capital_locked_partner_id)} capital {fmtINR(s.capital_locked_amount)}</span></div>}
                  </td>
                  <td className="mono text-right">{fmtINR(s.amount)}</td>
                  <td className="text-xs">{s.refund_due_date ? <span className={overdue?'text-red-400':''}>{fmtDate(s.refund_due_date)}{overdue && ' (overdue)'}</span> : '—'}</td>
                  <td><span className={"badge "+(s.status==='refunded'?'status-running':(overdue?'status-financial_review':'status-planning'))}>{SECURITY_STATUS_LABELS[s.status]||s.status}</span>
                    {/* Full-app audit finding: a recovered instrument
                        flips to Refunded the instant Record Recovery
                        runs, before its own recovery Receipt has actually
                        cleared Audit Queue review -- display-only qualifier,
                        no calculation is affected either way (the receipt
                        itself is already correctly excluded from every
                        total until reviewed). */}
                    {s.pending_recovery_review && <span className="text-xs text-amber-400 ml-1" title="The receipt recording this recovery hasn't cleared Audit Queue review yet">(pending review)</span>}</td>
                  <td>{s.read_only ? <span className="text-muted text-xs">—</span> : <AuditStatusChip status={s.audit_status}/>}</td>
                  {canEdit && <td className="text-right whitespace-nowrap no-print">
                    {/* User-requested: bill-withheld amounts (TDS, Demand
                        Draft/DD, Retention, etc.) were entirely read-only
                        here with no way to record the money coming back --
                        recording it needed a separate Receipt AND a manual
                        Bill edit, easy to do only one of and leave
                        outstanding_receivable wrong. This does both in one
                        step. Only ever shown for a bill-linked row still in
                        this list at all (which, since a fully-recovered
                        withholding's row disappears once the bill's field
                        hits 0, means there's necessarily still something
                        outstanding here). */}
                    {s.source==='deduction' && s.linked_bill_id && <button onClick={()=>setRecoveryRow(s)} className="tab btn-small mr-1" data-testid={`record-recovery-${s.id}`}>Record Recovery</button>}
                    {/* User-requested: the same Record Recovery affordance,
                        now for the OTHER kind of client-withheld security --
                        a real row synced from a plain receipt-level
                        Deduction (linked_deduction_id, not a bill). Marking
                        one of these Refunded via plain Edit was previously
                        just a label change with no cash artifact at all.
                        Hidden once fully recovered (nothing left owing) or
                        terminally lost ('credited'). */}
                    {s.source==='instrument' && s.linked_deduction_id && s.status!=='credited' && remainingToRecover(s)>0.01 && <button onClick={()=>setRecoveryRow(s)} className="tab btn-small mr-1" data-testid={`record-recovery-${s.id}`}>Record Recovery</button>}
                    {s.source==='deduction' && s.project_id && <Link to={`/app/projects/${s.project_id}`} className="tab btn-small mr-1">Open Project</Link>}
                    {!s.read_only && <button aria-label="Edit" onClick={()=>{setEditSeed(s);setOpen(true);}} className="tab btn-small mr-1"><Edit3 size={12}/></button>}
                    {!s.read_only && canDelete && <button aria-label="Delete" onClick={()=>del(s.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                  </td>}
                </tr>
              );
            })}
            {visibleItems.length>0 && <tr><td colSpan={(needsPicker?6:5)+(canEdit?1:0)} className="text-right text-muted uppercase text-xs">Total</td><td className="mono text-right text-[color:var(--gold)]">{fmtINR(securityTotal)}</td><td colSpan={3+(canEdit?1:0)}></td></tr>}
            {pendingSecurityTotal>0 && <tr><td colSpan={(needsPicker?6:5)+(canEdit?1:0)} className="text-right text-muted uppercase text-xs">Pending Review, not yet counted toward project profit</td><td className="mono text-right text-amber-400">{fmtINR(pendingSecurityTotal)}</td><td colSpan={3+(canEdit?1:0)}></td></tr>}
            {selected.size>0 && <tr className="no-print"><td colSpan={(needsPicker?6:5)+(canEdit?1:0)} className="text-right text-muted uppercase text-xs">Selected Total ({selected.size} item{selected.size===1?'':'s'})</td><td className="mono text-right text-[color:var(--gold)]">{fmtINR(selectedTotal)}</td><td colSpan={3+(canEdit?1:0)}></td></tr>}
          </tbody>
        </table>
      </div>

      <SecurityInstrumentModal open={open} onClose={()=>setOpen(false)} onSaved={load} initialForm={editSeed}/>
      <RecordRecoveryModal open={!!recoveryRow} onClose={()=>setRecoveryRow(null)} onSaved={load} row={recoveryRow}/>
      {bulkReceiveOpen && (
        <Modal open={true} onClose={()=>setBulkReceiveOpen(false)} title={`Mark ${Object.keys(bulkDrafts).length} Received`} maxW={860}>
          <p className="text-xs text-muted mb-4">Review the amount actually received for each — defaults to the full lodged amount. Enter less if the actual credit fell short (e.g. a TDS certificate showing less than what was recorded as withheld); a reason is required whenever it does.</p>
          <Field label="Settlement Date *"><input type="date" className="input" style={{width:180}} value={bulkDate} onChange={e=>setBulkDate(e.target.value)}/></Field>
          <div className="overflow-x-auto mt-4">
            <table className="tbl">
              <thead><tr><th>Type</th><th>Party</th><th className="text-right">Lodged</th><th className="text-right">Amount Received *</th><th>Reason (if short)</th></tr></thead>
              <tbody>
                {Object.keys(bulkDrafts).map(id => {
                  const row = items.find(x => x.id === id);
                  if (!row) return null;
                  const d = bulkDrafts[id];
                  const isShort = Number(d.amount||0) < Number(row.amount||0) - 0.005;
                  return (
                    <tr key={id}>
                      <td className="text-xs">{securityTypeLabel(row)}</td>
                      <td className="text-xs">{row.party_name||'—'}</td>
                      <td className="mono text-right">{fmtINR(row.amount)}</td>
                      <td><input className="input mono text-right" style={{width:130}} type="number" min="0" max={row.amount} value={d.amount??''} onChange={e=>setBulkDraft(id,'amount',e.target.value)} data-testid={`bulk-amount-${id}`}/></td>
                      <td>{isShort && <input className="input" style={{width:220}} value={d.note||''} onChange={e=>setBulkDraft(id,'note',e.target.value)} placeholder="Explain the shortfall" data-testid={`bulk-note-${id}`}/>}</td>
                    </tr>
                  );
                })}
              </tbody>
              {/* User-requested: a running comparison between what's
                  lodged and what's actually being marked received across
                  the WHOLE batch, not just per-row -- catches a
                  data-entry slip (e.g. you know 43,500 landed in the
                  bank total for this batch, but the rows as edited still
                  add up to something else) before saving. */}
              {(() => {
                const totalLodged = Object.keys(bulkDrafts).reduce((a,id)=>a+Number(items.find(x=>x.id===id)?.amount||0),0);
                const totalReceived = Object.values(bulkDrafts).reduce((a,d)=>a+Number(d.amount||0),0);
                const diff = Math.round((totalLodged-totalReceived)*100)/100;
                return (
                  <tfoot><tr className="font-semibold">
                    <td colSpan={2} className="text-right text-muted uppercase text-xs">Total</td>
                    <td className="mono text-right">{fmtINR(totalLodged)}</td>
                    <td className="mono text-right" style={diff>0.005?{color:'var(--accent-amber)'}:undefined}>{fmtINR(totalReceived)}</td>
                    <td className="text-xs" style={diff>0.005?{color:'var(--accent-amber)'}:undefined}>{diff>0.005 ? `${fmtINR(diff)} short across this batch` : ''}</td>
                  </tr></tfoot>
                );
              })()}
            </table>
          </div>
          <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setBulkReceiveOpen(false)} className="btn-outline btn-small">Cancel</button>
            <button onClick={saveBulkReceive} disabled={markingReceived} className="btn-gold btn-small" data-testid="bulk-receive-save">{markingReceived ? 'Saving…' : 'Save'}</button></div>
        </Modal>
      )}
      {bulkOpen && (
        <Modal open={true} onClose={()=>{setBulkOpen(false);load();}} title="Bulk Import — Security & Refundables" maxW={860}>
          {/* User-requested: same Import History visibility Extra Works/
              Data & Backup already have, scoped to this entity -- lets
              a user check what actually happened (and whether a file was
              already imported) before re-running one. */}
          <ImportHistoryPanel entityFilter="security_instruments" title="Security & Refundables Import History"/>
          <ExcelImportWizard lockedEntity="security_instruments" />
        </Modal>
      )}
    </div>
  );
}

function CompaniesPanel() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [defaultId, setDefaultId] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const r = await api.get('/companies', { params: { include_inactive: true } }); setItems(r.data);
    const me = await api.get('/auth/me'); setDefaultId(me.data.company_id);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (form.id) await api.patch(`/companies/${form.id}`, form); else await api.post('/companies', form);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const toggleStatus = async (c) => {
    try {
      await api.patch(`/companies/${c.id}`, { ...c, status: c.status === 'active' ? 'inactive' : 'active' });
      load(); toast.success(c.status === 'active' ? 'Marked inactive' : 'Marked active');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  // Every login can now see multiple companies at once (or all), combined —
  // there's no more single "active" company to switch. This just sets which
  // one pre-fills the Company field on Add/Edit forms, for any role.
  const setAsDefault = async (cid) => { await api.patch('/profiles/me', { company_id: cid }); await refresh(); setDefaultId(cid); toast.success('Set as default company'); };
  const isMaster = user.role === 'master_admin';
  // Once a field already carries a value, only a Master Admin may change it —
  // a Company Admin can still fill in whatever's still blank. Evaluated
  // live off `form`, which only ever mutates fields the user is actually
  // allowed to touch, so an untouched locked field keeps reading its
  // original loaded value for the life of the modal.
  const isLocked = (field) => !!(form.id && !isMaster && form[field]);
  const lockedLabel = (base, field) => {
    if (!isLocked(field)) return base;
    const required = base.endsWith(' *');
    const clean = required ? base.slice(0, -2) : base;
    return `${clean} (locked) ${required ? '*' : ''}`.trim();
  };
  return (
    <div className="card card-dark">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Companies</div>
          <div className="text-xs text-muted mt-1">{isMaster ? 'You see every company you have access to at once — set one as default to pre-fill the Company field when adding records.' : 'Only a Master Admin can create companies. You can still set your default among the companies you have access to.'}</div>
        </div>
        {isMaster && <button onClick={()=>{setForm({status:'active'});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-company"><Plus size={14}/>Add Company</button>}
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Name</th><th>Legal Name</th><th>PAN</th><th>GST</th><th>Account Manager</th><th>Status</th><th>Default</th><th></th></tr></thead>
        <tbody>
          {items.length===0 && <tr><td colSpan={8} className="text-center text-muted py-8">No companies yet.</td></tr>}
          {items.map(c => (
            <tr key={c.id} style={{opacity: c.status==='active'?1:0.55}}>
              <td className="serif text-base">{c.name}</td><td>{c.legal_name||'—'}</td><td>{c.pan||'—'}</td><td>{c.gst_number||'—'}</td>
              <td>{c.account_manager_name || <span className="text-red-400 text-xs">missing</span>}</td>
              <td><button onClick={()=>toggleStatus(c)} className={"badge "+(c.status==='active'?'status-running':'status-closed')} style={{cursor:'pointer', border:'1px solid'}}>{c.status||'active'}</button></td>
              <td>{defaultId===c.id ? <span className="chip">Default</span> : <button onClick={()=>setAsDefault(c.id)} className="tab btn-small">Set default</button>}</td>
              <td><button aria-label="Edit" onClick={()=>{setForm(c);setOpen(true);}} className="tab btn-small"><Edit3 size={12}/></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Company':'Add Company'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={lockedLabel('Name *','name')}><input className="input" value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})} disabled={isLocked('name')} title={isLocked('name')?'Already set — only a Master Admin can change this':undefined} data-testid="company-name"/></Field>
          <Field label={lockedLabel('Legal Name','legal_name')}><input className="input" value={form.legal_name||''} onChange={e=>setForm({...form, legal_name:e.target.value})} disabled={isLocked('legal_name')} title={isLocked('legal_name')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label={lockedLabel('PAN','pan')}><input className="input" value={form.pan||''} onChange={e=>setForm({...form, pan:e.target.value})} disabled={isLocked('pan')} title={isLocked('pan')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label={lockedLabel('GST Number','gst_number')}><input className="input" value={form.gst_number||''} onChange={e=>setForm({...form, gst_number:e.target.value})} disabled={isLocked('gst_number')} title={isLocked('gst_number')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label={lockedLabel('Phone','phone')}><input className="input" value={form.phone||''} onChange={e=>setForm({...form, phone:e.target.value})} disabled={isLocked('phone')} title={isLocked('phone')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label={lockedLabel('Email','email')}><input className="input" value={form.email||''} onChange={e=>setForm({...form, email:e.target.value})} disabled={isLocked('email')} title={isLocked('email')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label={lockedLabel('Account Manager Name','account_manager_name')}><input className="input" value={form.account_manager_name||''} onChange={e=>setForm({...form, account_manager_name:e.target.value})} disabled={isLocked('account_manager_name')} title={isLocked('account_manager_name')?'Already set — only a Master Admin can change this':undefined} data-testid="company-account-manager"/></Field>
          <Field label="Status"><select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}>
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select></Field>
          <div className="col-span-2"><Field label={lockedLabel('Address','address')}><textarea className="input" rows={2} value={form.address||''} onChange={e=>setForm({...form, address:e.target.value})} disabled={isLocked('address')} title={isLocked('address')?'Already set — only a Master Admin can change this':undefined}/></Field></div>
        </div>
        {/* Round 19 finding: account_manager_name is genuinely optional on
            the backend (CompanyIn.account_manager_name: Optional[str]) --
            requiring it here blocked saving ANY edit (even an unrelated
            field) on a company that doesn't have one yet, with no way to
            fix it short of inventing a name. */}
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving||!form.name} className="btn-gold btn-small" data-testid="company-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

function BankAccountsPanel() {
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const canDelete = isMasterAdmin(user);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [ledgerFor, setLedgerFor] = useState(null);
  const [companyFilter, setCompanyFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  const load = async () => setItems((await api.get('/bank-accounts')).data);
  useEffect(() => { load(); }, []);
  const visibleItems = items.filter(b => !companyFilter || b.company_id === companyFilter);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (form.id) await api.patch(`/bank-accounts/${form.id}`, form); else await api.post('/bank-accounts', form);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/bank-accounts/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  // Same already-filled-fields-are-Master-Admin-only rule as CompaniesPanel.
  const isLocked = (field) => !!(form.id && user.role !== 'master_admin' && form[field]);
  const lockedLabel = (base, field) => {
    if (!isLocked(field)) return base;
    const required = base.endsWith(' *');
    const clean = required ? base.slice(0, -2) : base;
    return `${clean} (locked) ${required ? '*' : ''}`.trim();
  };
  return (
    <div className="card card-dark">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Bank Accounts</div>
          <div className="text-xs text-muted mt-1">Ledger is a read-only view across every project. To record a credit or payment, use the Receipts or Expenses tab on the relevant project.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {needsPicker && <select className="input" style={{width:170}} value={companyFilter} onChange={e=>setCompanyFilter(e.target.value)} data-testid="bank-filter-company">
            <option value="">All Companies</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>}
          {canEdit && <button onClick={()=>{setForm({status:'active'});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-bank"><Plus size={14}/>Add Bank</button>}
        </div>
      </div>
      <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Bank</th><th>A/C Holder</th><th>A/C No.</th><th>Branch</th><th>IFSC</th>{needsPicker && <th>Company</th>}<th>Status</th><th></th>{canEdit && <th></th>}</tr></thead>
        <tbody>
          {visibleItems.length===0 && <tr><td colSpan="8" className="text-center text-muted py-6">{items.length===0 ? 'No bank accounts yet.' : 'No bank accounts match this filter.'}</td></tr>}
          {visibleItems.map(b => (
            <tr key={b.id}>
              <td>{b.bank_name}</td><td>{b.account_holder||'—'}</td><td className="mono">{b.account_number||'—'}</td><td>{b.branch||'—'}</td><td>{b.ifsc||'—'}</td>
              {needsPicker && <td className="text-xs">{companyName(b.company_id)}</td>}
              <td><span className="badge status-running">{b.status}</span></td>
              <td><button onClick={()=>setLedgerFor(b)} className="tab btn-small inline-flex items-center gap-1" data-testid={`bank-ledger-${b.id}`}><Landmark size={12}/>Ledger</button></td>
              {canEdit && <td className="text-right"><button aria-label="Edit" onClick={()=>{setForm(b);setOpen(true);}} className="tab btn-small mr-1"><Edit3 size={12}/></button>{canDelete && <button aria-label="Delete" onClick={()=>del(b.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id?'Edit Bank Account':'Add Bank Account'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker}/>
          <Field label={lockedLabel('Bank Name *','bank_name')}><input className="input" value={form.bank_name||''} onChange={e=>setForm({...form, bank_name:e.target.value})} disabled={isLocked('bank_name')} title={isLocked('bank_name')?'Already set — only a Master Admin can change this':undefined} data-testid="bank-name"/></Field>
          <Field label={lockedLabel('A/C Holder','account_holder')}><input className="input" value={form.account_holder||''} onChange={e=>setForm({...form, account_holder:e.target.value})} disabled={isLocked('account_holder')} title={isLocked('account_holder')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label={lockedLabel('Account Number','account_number')}><input className="input mono" value={form.account_number||''} onChange={e=>setForm({...form, account_number:e.target.value})} disabled={isLocked('account_number')} title={isLocked('account_number')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label={lockedLabel('Branch','branch')}><input className="input" value={form.branch||''} onChange={e=>setForm({...form, branch:e.target.value})} disabled={isLocked('branch')} title={isLocked('branch')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label={lockedLabel('IFSC','ifsc')}><input className="input mono" value={form.ifsc||''} onChange={e=>setForm({...form, ifsc:e.target.value})} disabled={isLocked('ifsc')} title={isLocked('ifsc')?'Already set — only a Master Admin can change this':undefined}/></Field>
          <Field label="Status"><select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}><option value="active">active</option><option value="inactive">inactive</option></select></Field>
          <div className="col-span-2"><Field label={lockedLabel('Notes','notes')}><textarea className="input" rows={2} value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})} disabled={isLocked('notes')} title={isLocked('notes')?'Already set — only a Master Admin can change this':undefined}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving || !form.bank_name} className="btn-gold btn-small" data-testid="bank-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
      {ledgerFor && <BankLedgerModal account={ledgerFor} companyName={companyName(ledgerFor.company_id)} canEdit={canEdit} onClose={()=>setLedgerFor(null)}/>}
    </div>
  );
}

function BankLedgerModal({ account, companyName, canEdit, onClose }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [mapping, setMapping] = useState({}); // { [entryId]: projectId being chosen }
  // User-requested: print/export with date-range or FY filters. Filtering
  // client-side (not a new backend query param) since the endpoint already
  // returns every entry with its own correctly-cumulative running_balance
  // computed from full history -- slicing here needs no re-computation,
  // just an "opening balance" figure carried forward from whatever the
  // last entry BEFORE the visible range left the account at, so a
  // filtered statement still opens on a real, correct balance rather than
  // restarting at zero.
  const [fyFilter, setFyFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Round 20 finding: no guard against a rapid double-click double-
  // submitting a row's "Map" action -- the backend's own check-then-update
  // (is this entry already mapped?) is a real TOCTOU window, so two
  // near-simultaneous clicks could both pass the check before either
  // commits. Keyed by entry id since each row maps independently.
  const [mappingIds, setMappingIds] = useState(() => new Set());
  const load = async () => {
    try { setData((await api.get(`/bank-accounts/${account.id}/ledger`)).data); }
    catch (e) { setLoadError(e.response?.data?.detail || 'Could not load this ledger'); }
  };
  useEffect(() => { load(); api.get('/projects').then(r=>setProjects(r.data)); }, [account.id]);

  const mapEntry = async (entry) => {
    if (mappingIds.has(entry.id)) return;
    const pid = mapping[entry.id];
    if (!pid) { toast.error('Choose a project first'); return; }
    setMappingIds(prev => new Set(prev).add(entry.id));
    try {
      await api.post(`/${entry.kind==='credit'?'receipts':'expenses'}/${entry.id}/map`, { project_id: pid });
      toast.success('Mapped to project');
      setMapping(m => ({ ...m, [entry.id]: '' })); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setMappingIds(prev => { const next = new Set(prev); next.delete(entry.id); return next; }); }
  };

  const allEntries = data?.entries || [];
  const availableFYs = distinctFYs(allEntries.map(e => fyFromDateStr(e.date)));
  const filteredEntries = allEntries.filter(e =>
    (!fyFilter || fyFromDateStr(e.date) === fyFilter) &&
    (!fromDate || e.date >= fromDate) &&
    (!toDate || e.date <= toDate)
  );
  const filterActive = !!(fyFilter || fromDate || toDate);
  // Opening balance = the real cumulative balance immediately before the
  // range's lower bound -- computed from the boundary DATE, not from
  // filteredEntries' own first element, since a range with genuinely zero
  // transactions (a real, valid case: an account carried a balance into
  // an FY with no activity) would otherwise have no "first entry" to
  // anchor off and wrongly show an opening balance of 0 instead of the
  // real carried-forward figure.
  const rangeLowerBound = fromDate || (fyFilter ? (/FY (\d{4})-/.exec(fyFilter)?.[1] + '-04-01') : null);
  const beforeRange = rangeLowerBound ? allEntries.filter(e => e.date < rangeLowerBound) : [];
  const openingBalance = rangeLowerBound ? (beforeRange.length ? beforeRange[beforeRange.length - 1].running_balance : 0) : 0;
  const closingBalance = filteredEntries.length ? filteredEntries[filteredEntries.length - 1].running_balance : openingBalance;
  const rangeCredited = filteredEntries.filter(e => e.kind === 'credit').reduce((a, e) => a + e.amount, 0);
  const rangePaid = filteredEntries.filter(e => e.kind === 'payment').reduce((a, e) => a + e.amount, 0);
  const rangeLabel = fyFilter || (fromDate || toDate ? `${fromDate ? fmtDate(fromDate) : 'Start'} – ${toDate ? fmtDate(toDate) : 'Today'}` : '');

  const exportLedgerPDF = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    const acctLabel = `${account.bank_name}${account.account_number ? ' ••'+String(account.account_number).slice(-4) : ''}`;
    doc.setFontSize(16); doc.text(`Bank Account Ledger — ${acctLabel}`, 14, 18);
    doc.setFontSize(9);
    if (companyName) doc.text(companyName, 14, 26);
    doc.text(`Generated: ${fmtGeneratedAt()}`, 14, companyName ? 32 : 26);
    if (rangeLabel) doc.text(`Period: ${rangeLabel}`, 14, companyName ? 38 : 32);
    doc.text(`Opening Balance: ${fmtINRPdf(openingBalance)}`, 14, (companyName ? 38 : 32) + (rangeLabel ? 6 : 0));
    autoTable(doc, {
      startY: (companyName ? 38 : 32) + (rangeLabel ? 6 : 0) + 6,
      head: [['Date','Kind','Type','Reference','Project','Amount','Balance']],
      body: filteredEntries.map(e => [
        fmtDate(e.date), e.kind === 'credit' ? 'Credit' : 'Payment', e.type, e.reference || '—',
        e.mapped ? (e.project_name || 'Company expense') : 'Unmapped',
        (e.kind === 'credit' ? '+' : '−') + fmtINRPdf(e.amount), fmtINRPdf(e.running_balance),
      ]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
    });
    const finalY = doc.lastAutoTable?.finalY || 100;
    doc.setFontSize(10);
    doc.text(`Total Credited: ${fmtINRPdf(rangeCredited)}`, 14, finalY + 10);
    doc.text(`Total Paid: ${fmtINRPdf(rangePaid)}`, 14, finalY + 16);
    doc.text(`Closing Balance: ${fmtINRPdf(closingBalance)}`, 14, finalY + 22);
    doc.save(`bank-ledger-${account.bank_name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}-${Date.now()}.pdf`);
  };

  return (
    <Modal open={true} onClose={onClose} title={`Ledger — ${account.bank_name}${account.account_number ? ' ••'+String(account.account_number).slice(-4) : ''}${companyName ? ` (${companyName})` : ''}`} maxW={860}>
      {loadError ? <div className="text-center text-red-400 py-8">{loadError}</div> : !data ? <Loading/> : (
        <div className="print-area">
          <div className="print-header">
            <div className="print-company">{companyName || account.bank_name}</div>
            <div className="print-report-title">Bank Account Ledger — {account.bank_name}{account.account_number ? ' ••'+String(account.account_number).slice(-4) : ''}</div>
            <div className="print-meta">Generated: {fmtGeneratedAt()}{rangeLabel ? ` · Period: ${rangeLabel}` : ''}</div>
          </div>
          <p className="text-xs text-muted mb-3 no-print">Read-only view across every project — to record a credit or payment against this account, use the Receipts or Expenses tab on the relevant project.</p>
          <div className="flex flex-wrap items-center gap-2 mb-4 no-print">
            <FySelect value={fyFilter} onChange={v=>{setFyFilter(v); setFromDate(''); setToDate('');}} years={availableFYs} style={{width:150}}/>
            <input className="input" style={{width:150}} type="date" value={fromDate} onChange={e=>{setFromDate(e.target.value); setFyFilter('');}} title="From date"/>
            <input className="input" style={{width:150}} type="date" value={toDate} onChange={e=>{setToDate(e.target.value); setFyFilter('');}} title="To date"/>
            {filterActive && <button onClick={()=>{setFyFilter('');setFromDate('');setToDate('');}} className="tab btn-small">Clear</button>}
            <button onClick={exportLedgerPDF} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Export PDF</button>
            <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>
          </div>
          <div className="flex flex-wrap gap-3 mb-4">
            {filterActive && <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Opening Balance</div><div className="serif text-xl">{fmtINR(openingBalance)}</div></div>}
            <div className="p-3 rounded surface-2"><div className="text-xs text-muted">{filterActive ? 'Credited (period)' : 'Total Credited'}</div><div className="serif text-xl text-[color:var(--gold)]">{fmtINR(filterActive ? rangeCredited : data.total_credited)}</div></div>
            <div className="p-3 rounded surface-2"><div className="text-xs text-muted">{filterActive ? 'Paid (period)' : 'Total Paid'}</div><div className="serif text-xl text-red-400">{fmtINR(filterActive ? rangePaid : data.total_paid)}</div></div>
            <div className="p-3 rounded surface-2"><div className="text-xs text-muted">{filterActive ? 'Closing Balance' : 'Balance'}</div><div className="serif text-xl">{fmtINR(filterActive ? closingBalance : data.balance)}</div></div>
            {data.unmapped_count > 0 && <div className="p-3 rounded surface-2 no-print"><div className="text-xs text-muted">Awaiting Mapping</div><div className="serif text-xl text-[color:var(--amber,#d7a548)]">{data.unmapped_count}</div></div>}
          </div>

          <div className="overflow-x-auto">
            <table className="tbl">
              <thead><tr><th>Date</th><th>Kind</th><th>Type</th><th>Reference</th><th>Project</th><th className="text-right">Amount</th><th className="text-right">Balance</th>{canEdit && <th className="no-print">Map</th>}</tr></thead>
              <tbody>
                {filteredEntries.length===0 && <tr><td colSpan={8} className="text-center text-muted py-6">{allEntries.length===0 ? 'No transactions recorded against this account yet.' : 'No transactions in this period.'}</td></tr>}
                {filteredEntries.map(e => (
                  <tr key={`${e.kind}-${e.id}`}>
                    <td className="text-xs">{fmtDate(e.date)}</td>
                    <td className="text-xs">{e.kind==='credit' ? <span className="text-green-400">Credit</span> : <span className="text-red-400">Payment</span>}</td>
                    <td className="text-xs">{e.type}</td>
                    <td className="text-xs">{e.reference||'—'}</td>
                    <td className="text-xs">{e.mapped ? (e.project_name || 'Company expense') : <span className="text-[color:var(--amber,#d7a548)]">Unmapped</span>}</td>
                    <td className={"mono text-right " + (e.kind==='credit' ? 'text-green-400' : 'text-red-400')}>{e.kind==='credit'?'+':'−'}{fmtINR(e.amount)}</td>
                    <td className="mono text-right">{fmtINR(e.running_balance)}</td>
                    {canEdit && <td className="no-print">
                      {!e.mapped && (
                        <div className="flex gap-1">
                          <select className="input" style={{width:150}} value={mapping[e.id]||''} onChange={ev=>setMapping(m=>({...m,[e.id]:ev.target.value}))}>
                            <option value="">— project —</option>
                            {projects.map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
                          </select>
                          <button onClick={()=>mapEntry(e)} disabled={mappingIds.has(e.id)} className="tab btn-small">{mappingIds.has(e.id) ? 'Mapping…' : 'Map'}</button>
                        </div>
                      )}
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ============= PARTNER DETAIL (Ledger + Position) =============
// User-requested: with Badal personally acting as the company's bank
// until now, once a project is locked he needs one glance at every
// partner's Net Position side-by-side to decide who to actually pay and
// how much -- the partner-picker grid below already shows each card's own
// net_position, but one at a time isn't enough for that decision. This is
// company-wide (every company the caller can see, GET /reports/partner-
// payouts-summary), collapsed by default since it's a periodic "decide
// payouts" tool, not something glanced at every visit to this page.
// Partner Payouts Summary: the backend returns every component of a
// partner's Net Position; these group the smaller ones into catch-all
// columns so each row adds up on screen:
//   Capital + Personal Expenses + Security + Other Credits + Profit
//     - Capital Returned - Refunded Against Expenses - Other Paid Out = Net Position
// Capital returned beyond what a partner introduced (per company) is shown
// as a refund against their personal expenses instead -- see the
// reconciliation block partner_position returns.
const payoutsOtherCredits = (p) => (p.loan||0) + (p.investment_other||0) + (p.interest_credited||0) + (p.paid_on_behalf||0);
const payoutsOtherPaidOut = (p) => (p.profit_paid||0) + (p.loan_repaid||0) + (p.withdrawals||0) + (p.other_debits||0) + (p.interest_charged||0);
const payoutsBreakdownTitle = (parts) => {
  const shown = parts.filter(([, v]) => v);
  return shown.length ? shown.map(([label, v]) => `${label}: ${fmtINR(v)}`).join('\n') : 'Nothing recorded';
};
const payoutsOtherCreditsTitle = (p) => payoutsBreakdownTitle([['Loans', p.loan], ['Other investments', p.investment_other], ['Interest credited', p.interest_credited], ['Paid on behalf of others', p.paid_on_behalf]]);
const payoutsOtherPaidOutTitle = (p) => payoutsBreakdownTitle([['Profit paid', p.profit_paid], ['Loans repaid', p.loan_repaid], ['Withdrawals', p.withdrawals], ['Other debits', p.other_debits], ['Interest charged', p.interest_charged]]);
const payoutsCapitalReturnedTitle = (p) => {
  const r = p.reconciliation || {};
  return r.capital_return_excess > 0
    ? `Capital returns recorded: ${fmtINR(r.capital_returned)}\nOf which ${fmtINR(r.capital_return_excess)} is more than the capital introduced in that company, so it is counted as an expense refund (next column).`
    : 'Capital returns recorded, all within the capital introduced.';
};
const payoutsRefundedTitle = (p) => {
  const r = p.reconciliation || {};
  return payoutsBreakdownTitle([['Expense reimbursements paid', r.expenses_reimbursed], ['Capital returned beyond capital introduced', r.capital_return_excess]]);
};

function PartnerPayoutsSummary() {
  const { companies } = useMyCompanies();
  const [allPartners, setAllPartners] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  useEffect(() => { api.get('/partners').then(r=>setAllPartners(r.data)).catch(()=>{}); }, []);
  useEffect(() => { api.get('/projects').then(r=>setAllProjects(r.data)).catch(()=>{}); }, []);
  // allAccess must be its own independent state, not derived from
  // companyIds.length===0 -- GrantPicker disables every individual
  // checkbox while allAccess is true, so a derived allAccess re-locks
  // them the instant the last one is unticked, with no way back in.
  // Same proven allCompaniesSelected pattern ExtraWorks/BillsPage already
  // use. Live-reported bug from getting this wrong the first time.
  const [allCompaniesSelected, setAllCompaniesSelected] = useState(true);
  const [companyIds, setCompanyIds] = useState([]);
  const [allPartnersSelected, setAllPartnersSelected] = useState(true);
  const [partnerIds, setPartnerIds] = useState([]);
  // User-reported real gap: the Partner picker listed every partner
  // regardless of which company(ies) were selected above -- same
  // company-narrows-the-list convention every other company+partner (or
  // company+master-data) filter pair in this app already follows (see
  // e.g. Projects' own department_client filter, `allCompaniesSelected ||
  // (m.company_ids||[]).some(cid => companyFilterIds.includes(cid))`).
  const visiblePartnersForFilter = allCompaniesSelected
    ? allPartners
    : allPartners.filter(p => (p.company_ids && p.company_ids.length ? p.company_ids : (p.company_id ? [p.company_id] : [])).some(cid => companyIds.includes(cid)));
  // A company deselected out from under an already-picked partner would
  // otherwise leave a selected id that's no longer even shown in the
  // (now narrower) list -- same stale-selection guard Reports/Bills
  // already apply to their own project pickers when their company filter
  // changes.
  useEffect(() => {
    setPartnerIds(cur => cur.filter(pid => visiblePartnersForFilter.some(p => p.id === pid)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCompaniesSelected, companyIds]);
  const [fy, setFy] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  // User-reported real gap: this can genuinely take a while to compute on
  // real data (it recomputes every distinct project's full financials to
  // get a real collection ratio, not a cheap lookup) -- with no loading
  // state at all, expanding the panel looked like nothing had happened
  // until it finally populated. Explicit "Loading…" now covers that gap.
  const load = () => {
    setLoading(true);
    const params = {};
    if (companyIds.length) params.company_ids = companyIds.join(',');
    if (partnerIds.length) params.partner_ids = partnerIds.join(',');
    if (fy) params.financial_year = fy;
    api.get('/reports/partner-payouts-summary', { params }).then(r=>setData(r.data)).catch(()=>setData({partners:[], company_fund_balances:[]})).finally(()=>setLoading(false));
  };
  useEffect(() => { if (!collapsed) load(); }, [collapsed, allCompaniesSelected, companyIds, allPartnersSelected, partnerIds, fy]);
  // User-reported real gap: Net Position is accrual-basis -- it can
  // include profit locked in against a project's full contract value
  // even when only part of that has actually been collected ("future
  // coming money"). "Real Payable Now" (net_position_cash_basis, from
  // the backend) scales each project's profit contribution by that
  // project's own real collection ratio instead -- this is the figure
  // that answers "how much can I actually pay right now," not the
  // accrual one. Both are shown, side by side, so the gap itself is
  // visible rather than one number silently replacing the other.
  const totalRealPayable = (data?.partners||[]).reduce((a,p)=>a+Math.max(0,p.net_position_cash_basis||0),0);
  const totalAccrualPayable = (data?.partners||[]).reduce((a,p)=>a+Math.max(0,p.net_position||0),0);
  const totalReceivable = (data?.partners||[]).reduce((a,p)=>a+Math.max(0,-(p.net_position_cash_basis||0)),0);
  return (
    <div className="card card-dark mb-6" data-testid="partner-payouts-summary">
      <button onClick={()=>setCollapsed(v=>!v)} className="flex items-center justify-between w-full text-left">
        <div>
          <div className="serif text-xl text-[color:var(--gold)]">Partner Payouts Summary</div>
          <div className="text-xs text-muted mt-1">Every partner's Net Position side-by-side, company-wide — "Real Payable Now" limits each project's profit to what the client has actually paid for (costs are recovered first), so it doesn't count money still on paper. Doesn't include individual bank account balances or cash held outside this app.</div>
        </div>
        {collapsed ? <ChevronRight size={18}/> : <ChevronDown size={18}/>}
      </button>
      {!collapsed && loading && !data && <div className="text-sm text-muted text-center py-8 mt-4">Loading — this recomputes every project's real collection figures, so it can take a moment…</div>}
      {!collapsed && data && <div className="mt-4">
        {loading && <div className="text-xs text-muted mb-3">Refreshing…</div>}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <GrantPicker
            items={companies.map(c=>({id:c.id, name:c.name}))}
            selectedIds={companyIds}
            allAccess={allCompaniesSelected}
            onToggle={cid => setCompanyIds(cur => cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid])}
            onSetAllAccess={v => { setAllCompaniesSelected(v); if (v) setCompanyIds([]); }}
            allLabel="All Companies"
            noneWidth={170}
          />
          <GrantPicker
            items={visiblePartnersForFilter.map(p=>({id:p.id, name:p.name}))}
            selectedIds={partnerIds}
            allAccess={allPartnersSelected}
            onToggle={pid => setPartnerIds(cur => cur.includes(pid) ? cur.filter(x=>x!==pid) : [...cur, pid])}
            onSetAllAccess={v => { setAllPartnersSelected(v); if (v) setPartnerIds([]); }}
            allLabel="All Partners"
            noneWidth={170}
          />
          <FySelect value={fy} onChange={setFy} years={distinctFYs(allProjects.map(p=>p.financial_year))} style={{width:150}}/>
        </div>
        {fy && <div className="text-xs text-muted mb-4">FY filter narrows Real Payable/profit figures to projects starting in {fy} only — Capital/Security (already real cash, not tied to one year) stay all-time regardless.</div>}
        {data.company_fund_balances.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {data.company_fund_balances.map(c => (
              <span key={c.company_id} className="chip" style={{background:'rgba(217,164,65,0.14)', color:'var(--accent-amber)'}} title="Company Fund closing balance — reserve credits + capital contributions minus company-fund-paid expenses/withdrawals recorded in this app">
                {c.company_name} Fund: {fmtINR(c.fund_balance)}
              </span>
            ))}
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <KpiCard label="Total Real Payable Now" value={fmtINR(totalRealPayable)} tone="gold" hint="Cash-basis — only counts profit backed by money actually collected"/>
          <KpiCard label="Total Payable (Accrual)" value={fmtINR(totalAccrualPayable)} hint="Includes profit not yet collected — for comparison only"/>
          <KpiCard label="Total Receivable from Partners" value={fmtINR(totalReceivable)} tone="red"/>
        </div>
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead><tr><th>Partner</th><th>Capital Introduced</th><th title="Capital paid back, up to what this partner introduced in that company">Capital Returned</th><th title="Company expenses this partner paid out of their own pocket — owed back to them">Personal Expenses Borne</th><th title="Expense reimbursements, plus any capital returned beyond the capital introduced — money already received against personal expenses">Refunded Against Expenses</th><th>Security Borne</th><th title="Loans, other investments, interest credited and payments made on another partner's behalf">Other Credits</th><th>Profit (Accrual)</th><th title="Profit paid, loans repaid, withdrawals, other debits and interest charged">Other Paid Out</th><th>Net Position (Accrual)</th><th>Real Payable Now</th></tr></thead>
            <tbody>
              {data.partners.length===0 && <tr><td colSpan="11" className="text-center text-muted py-6">No partners match these filters.</td></tr>}
              {data.partners.map(p => {
                // User-requested: name the reason for a <100% ratio
                // directly (deductions still uncollected) instead of
                // leaving it as an unexplained percentage -- built from
                // this partner's own per-project breakdown, so it's exact
                // to the same rupee the backend already computed, not a
                // re-derived guess.
                const shortfallProjects = (p.projects||[]).filter(pr => (pr.cash_backed_pct ?? pr.collection_pct) < 100);
                const realPayableTooltip = shortfallProjects.length === 0
                  ? 'Fully cash-backed — every project counted here has been collected in full.'
                  : shortfallProjects.map(pr => `${pr.project_name}: ${pr.cash_backed_pct ?? pr.collection_pct}% of the profit is backed by cash (${pr.collection_pct}% of the contract collected; costs are recovered first)` + (pr.deductions_outstanding > 0 ? ` — ${fmtINR(pr.deductions_outstanding)} in deductions not yet recovered` : ' — balance of the contract not yet billed/collected')).join('\n');
                return (
                <tr key={p.partner_id}>
                  <td>{p.partner_name}</td>
                  <td className="mono">{fmtINR(p.capital)}</td>
                  <td className="mono" title={payoutsCapitalReturnedTitle(p)}>{fmtINR(p.reconciliation ? p.reconciliation.capital_returned_within_capital : p.capital_returned)}</td>
                  <td className="mono">{fmtINR(p.personal_expense)}</td>
                  <td className="mono" title={payoutsRefundedTitle(p)}>{fmtINR(p.reconciliation ? p.reconciliation.expenses_refunded : p.reimbursements)}</td>
                  <td className="mono">{fmtINR(p.security_borne)}</td>
                  <td className="mono" title={payoutsOtherCreditsTitle(p)}>{fmtINR(payoutsOtherCredits(p))}</td>
                  <td className="mono text-muted" title="Locked in at approval against the full contract value — may include money not yet collected">{fmtINR(p.profit_entitlement)}</td>
                  <td className="mono" title={payoutsOtherPaidOutTitle(p)}>{fmtINR(payoutsOtherPaidOut(p))}</td>
                  <td className="mono text-muted">{fmtINR(p.net_position)}</td>
                  <td className={"mono font-semibold " + ((p.net_position_cash_basis||0)>=0?'text-[color:var(--gold)]':'text-red-400')} title={realPayableTooltip}>{fmtINR(p.net_position_cash_basis)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-xs text-muted mt-3">Each row adds up: Capital Introduced + Personal Expenses Borne + Security Borne + Other Credits + Profit − Capital Returned − Refunded Against Expenses − Other Paid Out = Net Position. Capital returned beyond what a partner introduced (company by company) is counted against their personal expenses, not as capital.</div>
        </div>
      </div>}
    </div>
  );
}

// Real-usage-gap finding: replaces the old PartnerDetail (reachable only
// by clicking a partner in the Partners list, which has now moved to
// Settings as plain master data) with a top-level, dropdown-driven
// dashboard -- "Profit Distribution & Partner Settlement" in the nav.
// Reuses the exact same Position/Ledger computation (partner_ledger/
// partner_position, unchanged) and adds the one thing that was missing:
// recording a partner transaction directly from here, instead of only
// from inside one specific project's own Transactions tab.
function PartnerSettlementDashboard() {
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const { companies } = useMyCompanies();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('partner') || '';
  const [allPartners, setAllPartners] = useState([]);
  // User-requested: the new "Open Partner Reports" Dashboard card links
  // straight here with ?tab=distributions so clicking through lands on
  // the report itself, not the default Position tab.
  const [tab, setTab] = useState(() => { const t = searchParams.get('tab') || 'position'; return (user.role === 'partner' && t === 'bankledger') ? 'position' : t; });
  const [ledger, setLedger] = useState(null);
  const [position, setPosition] = useState(null);
  const [fy, setFy] = useState('');
  // User-reported: a project-based filter/picker here didn't work well --
  // a partner can have real activity (investments, personal expenses,
  // security instruments) on a project with no formal project_partner_
  // roles entry, so a role-gated project list came back empty far too
  // often. Replaced with a company multi-select instead -- simpler, and
  // matches how the rest of this page (Current Account's own company
  // picker) already scopes things.
  const [companyIds, setCompanyIds] = useState([]);
  // One tick for everything project-wise on this page: it decides whether the per-project tables are in the PRINTOUT and in the Statement PDF
  // (totals and KPI cards always print). The screen keeps showing everything.
  const [includeBreakdown, setIncludeBreakdown] = usePersistedState('PartnerSettlement.includeBreakdown', true);
  // User-requested: same Bank Account Ledger already available under
  // Settings > Bank Accounts and Reports, added here too so a bank
  // statement can be pulled up without leaving a partner's settlement
  // view. Reuses BankLedgerModal (opening/closing balance, FY/date
  // filters, Export PDF, Print, unmapped-entry mapping) completely
  // unchanged -- narrowed to this partner's own companies, same
  // partnerCompanyIds scoping the Company/Current Account pickers above
  // already use.
  const [bankAccounts, setBankAccounts] = useState([]);
  const [ledgerBankAccountId, setLedgerBankAccountId] = useState('');
  useEffect(() => { api.get('/bank-accounts').then(r=>setBankAccounts(r.data)); }, []);
  // User-requested: search + sort on the Running Ledger. Sorting is
  // deliberately Date-only (the Balance column is a running total, only
  // meaningful in true chronological order -- sorting by Credit/Debit/
  // Type would leave a Balance column that no longer makes sense against
  // the rows shown above/below it) -- see the ledger table below.
  const [ledgerSearch, setLedgerSearch] = useState('');
  // User-requested follow-up: FY alone was the only way to narrow this
  // list -- these three add a finer date range (within the selected FY,
  // same convention as Income's own From/To), a single Project, and an
  // entry Type, all applied client-side against the already-fetched FY-
  // scoped `ledger.entries` (same as ledgerSearch above), so none of them
  // need a new backend call. Deliberately don't recompute Total Credit/
  // Debit/Closing against the filtered rows -- those stay whole-ledger
  // figures, same as ledgerSearch already leaves them, since a "closing
  // balance" only means anything across the full chronological ledger,
  // not one project or type sliced out of it.
  const [ledgerFromDate, setLedgerFromDate] = useState('');
  const [ledgerToDate, setLedgerToDate] = useState('');
  const [ledgerProjectFilter, setLedgerProjectFilter] = useState('');
  const [ledgerTypeFilter, setLedgerTypeFilter] = useState('');
  const [txnOpen, setTxnOpen] = useState(false);
  const [txnForm, setTxnForm] = useState({});
  const [txnSaving, setTxnSaving] = useState(false);
  // Capital returned beyond capital introduced -> expense refund (master
  // admin only): preview data, the ticked transactions, and a saving flag.
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertData, setConvertData] = useState(null);
  const [convertSel, setConvertSel] = useState([]);
  const [convertSaving, setConvertSaving] = useState(false);
  const openConvert = async () => {
    setConvertData(null); setConvertSel([]); setConvertOpen(true);
    try {
      const d = (await api.get(`/partners/${id}/capital-return-excess`)).data;
      setConvertData(d);
      setConvertSel(d.companies.flatMap(c => c.suggested_ids));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the capital returns'); setConvertOpen(false); }
  };
  const saveConvert = async () => {
    setConvertSaving(true);
    try {
      const r = (await api.post(`/partners/${id}/capital-return-excess/reclassify`, { transaction_ids: convertSel })).data;
      toast.success(`${r.reclassified} payment(s) re-filed as expense refunds — Net Position unchanged`);
      setConvertOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not convert these payments'); }
    finally { setConvertSaving(false); }
  };
  // User-requested: "Paid By Partner" on a transaction, same idea as an
  // expense's own Paid By -- an Investor is hidden from it by default.
  const [txnShowAllPartners, setTxnShowAllPartners] = useState(false);
  useEffect(() => { api.get('/partners').then(r=>setAllPartners(r.data)); }, []);
  // Full project list (same company-scoped-to-caller access as every
  // other project dropdown in the app) -- used only to populate the Add
  // Transaction modal's Project field, filtered client-side to whichever
  // company is selected there. Not used for the top filter above, which
  // is company-based now.
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get('/projects').then(r=>setProjects(r.data)); }, []);
  const [loadError, setLoadError] = useState(null);

  // User-requested: the "All Partners" picker was just a bare name + phone/
  // email, with nothing to actually glance at before clicking in -- fetches
  // each partner's own net_position (a handful of partners in real use, so
  // this stays cheap) so the picker itself is informative, not just a
  // directory. A network hiccup on one partner degrades to "—" for that
  // card only, never blocks the rest of the grid from showing.
  const [positionsByPartner, setPositionsByPartner] = useState({});
  useEffect(() => {
    if (!allPartners.length) return;
    let cancelled = false;
    Promise.all(allPartners.map(p => api.get(`/partners/${p.id}/position`).then(r => [p.id, r.data.totals?.net_position]).catch(() => [p.id, null])))
      .then(pairs => { if (!cancelled) setPositionsByPartner(Object.fromEntries(pairs)); });
    // The cash-backed version of every card, from ONE request (one bulk calculation, not one per partner); it appears on the
    // cards a few seconds after the plain figure, and a failure just leaves the cards as they were.
    api.get('/partners/cash-positions').then(r => { if (!cancelled) setCashByPartner(r.data.partners || {}); }).catch(() => {});
    return () => { cancelled = true; };
  }, [allPartners]);
  const [cashByPartner, setCashByPartner] = useState({});
  const [partnerSearch, setPartnerSearch] = useState('');
  const filteredPartners = allPartners.filter(p => !partnerSearch.trim() || p.name.toLowerCase().includes(partnerSearch.trim().toLowerCase()));

  // Current Account -- company-wide (not per-project) balance + optional
  // interest, formalizing what the Ledger/Position tabs already track.
  // Deliberately its own fetch/company-select, separate from the
  // project-filtered ledger/position above.
  const [caCompanyId, setCaCompanyId] = useState('');
  const [caFy, setCaFy] = useState('');
  const [currentAccount, setCurrentAccount] = useState(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [ratePct, setRatePct] = useState('');
  const [rateRule, setRateRule] = useState(null);
  const [rateSaving, setRateSaving] = useState(false);
  // Same stale-response guard as ProjectDetail.loadAll — see its comment.
  const loadToken = useRef(0);
  const load = async () => {
    if (!id) { setLedger(null); setPosition(null); setLoadError(null); return; }
    const myToken = ++loadToken.current;
    const ledgerParams = {};
    if (fy) ledgerParams.financial_year = fy;
    if (companyIds.length) ledgerParams.company_ids = companyIds.join(',');
    const positionParams = {};
    if (companyIds.length) positionParams.company_ids = companyIds.join(',');
    positionParams.include_cash = true;      // cash-backed view of the same position (what the client has actually paid for)
    try {
      const [l, p] = await Promise.all([
        api.get(`/partners/${id}/ledger`, { params: ledgerParams }),
        api.get(`/partners/${id}/position`, { params: positionParams })
      ]);
      if (loadToken.current !== myToken) return;
      setLedger(l.data); setPosition(p.data); setLoadError(null);
    } catch (e) {
      if (loadToken.current !== myToken) return;
      setLoadError(e.response?.data?.detail || 'Could not load this partner');
      toast.error(e.response?.data?.detail || 'Could not load this partner');
    }
  };
  useEffect(() => { load(); }, [id, fy, companyIds]);
  const partnerCompanyIds = ledger?.partner ? (ledger.partner.company_ids && ledger.partner.company_ids.length ? ledger.partner.company_ids : (ledger.partner.company_id ? [ledger.partner.company_id] : [])) : [];
  useEffect(() => { if (partnerCompanyIds.length && !partnerCompanyIds.includes(caCompanyId)) setCaCompanyId(partnerCompanyIds[0]); }, [ledger?.partner?.id]);
  const loadCurrentAccount = async () => {
    if (!id || !caCompanyId) { setCurrentAccount(null); return; }
    try {
      const params = { company_id: caCompanyId };
      if (caFy) params.financial_year = caFy;
      setCurrentAccount((await api.get(`/partners/${id}/current-account`, { params })).data);
    } catch { setCurrentAccount(null); }
  };
  useEffect(() => { loadCurrentAccount(); }, [id, caCompanyId, caFy]);

  // User-requested: Position/Ledger/Current Account are all running
  // totals -- none of them show a partner their individual signed profit
  // distributions, one per project, in one place. This is that view --
  // own fetch/FY-filter, same convention as Current Account's caFy above
  // (the shared `fy` control is gated to the Ledger tab only, see its own
  // comment), reusing the shared company-filter (companyIds) at the top
  // since that one already applies across every tab.
  const [distributions, setDistributions] = useState(null);
  const [distFy, setDistFy] = useState('');
  const loadDistributions = async () => {
    if (!id) { setDistributions(null); return; }
    try {
      const params = {};
      if (companyIds.length) params.company_ids = companyIds.join(',');
      if (distFy) params.financial_year = distFy;
      setDistributions((await api.get(`/partners/${id}/profit-distributions`, { params })).data);
    } catch { setDistributions(null); }
  };
  useEffect(() => { loadDistributions(); }, [id, companyIds, distFy]);
  const distTotals = (distributions||[]).reduce((acc,d)=>({
    entitlement: acc.entitlement + (d.total_profit_entitlement||0),
    entitlementCashBasis: acc.entitlementCashBasis + (d.total_profit_entitlement_cash_basis??d.total_profit_entitlement??0),
    paid: acc.paid + (d.profit_paid||0),
    balance: acc.balance + (d.balance||0),
    payableNow: acc.payableNow + (d.payable_now_cash_basis||0),
  }), {entitlement:0, entitlementCashBasis:0, paid:0, balance:0, payableNow:0});

  // User-requested: the Profit Distribution view above is read-only --
  // this is the one channel back the other way, for a partner login
  // specifically (a partner can flag a suspected miscalculation, ask for
  // a reopen, or leave any other comment; an admin/accountant/master_admin
  // viewing this same page resolves it with a note). Only a partner-role
  // login can file one (isPartnerLogin below); everyone who can see this
  // page sees the resulting list, same as the backend's own
  // _check_partner_ownership gate.
  const isPartnerLogin = user.role === 'partner';
  const [reports, setReports] = useState([]);
  const loadReports = async () => {
    if (!id) { setReports([]); return; }
    try { setReports((await api.get(`/partners/${id}/reports`)).data); } catch { setReports([]); }
  };
  useEffect(() => { loadReports(); }, [id]);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportForm, setReportForm] = useState({});
  const [reportSaving, setReportSaving] = useState(false);
  const openReport = (dist) => {
    setReportForm({
      category: dist ? 'miscalculation' : 'other',
      message: '',
      project_id: dist?.project_id || null,
      profit_distribution_id: dist?.id || null,
      _project_name: dist?.project_name || null,
    });
    setReportOpen(true);
  };
  const saveReport = async () => {
    if (reportSaving || !reportForm.message?.trim()) return;
    setReportSaving(true);
    try {
      await api.post(`/partners/${id}/reports`, reportForm);
      setReportOpen(false);
      toast.success('Sent — the office will follow up here');
      loadReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setReportSaving(false); }
  };
  const [resolvingId, setResolvingId] = useState(null);
  const [resolveNoteById, setResolveNoteById] = useState({});
  const resolveReport = async (reportId) => {
    setResolvingId(reportId);
    try {
      await api.patch(`/partners/${id}/reports/${reportId}`, { resolution_note: resolveNoteById[reportId] || null });
      toast.success('Marked resolved');
      loadReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setResolvingId(null); }
  };
  const loadRateRule = async (cid) => {
    if (!cid) { setRateRule(null); return; }
    try {
      const r = await api.get('/profit-sharing-rules');
      setRateRule((r.data || []).find(x => x.company_id === cid && !x.project_id && isProfitRuleCurrentlyActive(x)) || null);
    } catch { setRateRule(null); }
  };
  useEffect(() => { loadRateRule(caCompanyId); }, [caCompanyId]);
  const openRate = () => { setRatePct(currentAccount?.interest_rate_pct || ''); setRateOpen(true); };
  const saveRate = async () => {
    if (rateSaving || !caCompanyId) return;
    setRateSaving(true);
    try {
      const pct = Number(ratePct || 0);
      if (rateRule) {
        const moneyPct = rateRule.pool_definitions?.find(x=>x.pool_name==='money')?.pool_percentage ?? 50;
        const fundPct = rateRule.pool_definitions?.find(x=>x.pool_name==='company_fund')?.pool_percentage ?? 0;
        const today = new Date().toISOString().slice(0,10);
        const rateChanged = Number(rateRule.interest_rate_percentage || 0) !== pct;
        if (!rateChanged) { setRateOpen(false); setRateSaving(false); return; }
        // User-requested enhancement: a real rate change now closes out
        // the OLD rule (effective_to = yesterday) and creates a genuinely
        // NEW one, instead of mutating interest_rate_percentage in place
        // on the same row. Mutating in place silently discarded the
        // previous rate with no history at all, so a mid-year change
        // could never be correctly prorated -- see the backend's
        // _accrued_interest_over_period, built specifically to read this
        // history. Only the interest rate itself changes; money/company
        // fund % carry forward unchanged onto the new rule. If this
        // rule's own effective_from is today or later (just created, or
        // scheduled to start in the future), there's no real history to
        // preserve yet -- falls back to the old in-place update so it
        // doesn't create an inverted (effective_to < effective_from) range.
        if (rateRule.effective_from && rateRule.effective_from < today) {
          const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0,10);
          await api.patch(`/profit-sharing-rules/${rateRule.id}`, {
            project_id: null, company_id: caCompanyId, rule_name: rateRule.rule_name,
            money_pool_percentage: moneyPct, company_fund_percentage: fundPct,
            interest_rate_percentage: rateRule.interest_rate_percentage,
            effective_from: rateRule.effective_from, effective_to: yesterday, status: rateRule.status,
          });
          await api.post('/profit-sharing-rules', {
            project_id: null, company_id: caCompanyId, rule_name: rateRule.rule_name,
            money_pool_percentage: moneyPct, company_fund_percentage: fundPct,
            interest_rate_percentage: pct,
            effective_from: today, effective_to: null, status: 'active',
          });
        } else {
          await api.patch(`/profit-sharing-rules/${rateRule.id}`, {
            project_id: null, company_id: caCompanyId, rule_name: rateRule.rule_name,
            money_pool_percentage: moneyPct, company_fund_percentage: fundPct,
            interest_rate_percentage: pct,
            effective_from: rateRule.effective_from, effective_to: rateRule.effective_to, status: rateRule.status,
          });
        }
      } else {
        await api.post('/profit-sharing-rules', {
          project_id: null, company_id: caCompanyId, rule_name: 'Current Account Interest',
          money_pool_percentage: 0, company_fund_percentage: 0, interest_rate_percentage: pct,
          effective_from: new Date().toISOString().slice(0,10), status: 'active',
        });
      }
      setRateOpen(false); loadRateRule(caCompanyId); loadCurrentAccount(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setRateSaving(false); }
  };
  const postInterest = () => {
    if (!currentAccount) return;
    const amt = Math.abs(currentAccount.accrued_interest);
    setTxnForm({
      transaction_date: new Date().toISOString().slice(0,10),
      transaction_type: currentAccount.accrued_interest >= 0 ? 'interest_credit' : 'interest_charge',
      amount: amt, company_id: caCompanyId,
      description: `Current Account interest — ${currentAccount.financial_year} (avg balance ${fmtINR(currentAccount.avg_balance_this_fy)} @ ${currentAccount.interest_rate_pct}%)`,
    });
    setTxnOpen(true);
  };
  const selectPartner = (pid) => setSearchParams(pid ? { partner: pid } : {});
  // User-requested: this page's own Add Transaction only ever offered the
  // 6 "money out" categories (paying a partner) -- the same plain-language
  // "in or out" picker the Company Expenses page's quick-add already has
  // (PARTNER_TXN_QUICK_CATEGORIES) was missing its "in" half here, so
  // recording a partner's Capital Investment or Loan from this partner's
  // own settlement page wasn't possible; it silently required leaving this
  // page for the quick-add or a specific project's own Investments tab.
  // Not a backend gap -- partner_transactions genuinely has no "in" type
  // at all (see TXN_TYPES); a "money in" category routes to a different
  // real table (investments / company_capital_contributions), exactly as
  // the quick-add already does. `category` (a PARTNER_TXN_QUICK_CATEGORIES
  // key) drives the picker for a NEW entry; an edit of an existing (always
  // "out" or interest) row never touches it, matching the quick-add's own
  // create-only "in" support.
  const txnCategory = PARTNER_TXN_QUICK_CATEGORIES.find(c => c.key === txnForm.category);
  const saveTxn = async () => {
    if (txnSaving) return;
    if (!txnForm.transaction_date) { toast.error('Date is required.'); return; }
    if (!txnForm.transaction_type && !txnForm.category) { toast.error('Type is required.'); return; }
    if (!txnForm.amount) { toast.error('Amount is required.'); return; }
    if (!txnForm.company_id) { toast.error('Company is required.'); return; }
    // Interest Credit/Charge are admin-computed Current Account figures,
    // never sourced from a physical voucher -- exempt, matching the
    // backend's own exception in _validate_partner_txn_type.
    if (!['interest_credit','interest_charge'].includes(txnForm.transaction_type) && !/^\d+$/.test(txnForm.voucher_number||'')) {
      toast.error('Voucher number is required and must contain digits only.'); return;
    }
    if (txnCategory?.projectRequired && !txnForm.project_id) { toast.error('Project is required for a Loan from a Partner.'); return; }
    if (txnForm.transaction_type === 'profit_payment' && !(await confirmProfitPaymentCash(txnForm.project_id, Number(txnForm.amount||0), txnForm.partner_id))) return;
    const amount = Number(txnForm.amount||0);
    setTxnSaving(true);
    try {
      if (!txnForm.id && txnCategory?.direction === 'in' && !txnForm.project_id) {
        // Capital Investment, company-wide (no project picked) -- same
        // routing as the quick-add's identical branch.
        await api.post('/company-capital-contributions', {
          company_id: txnForm.company_id, partner_id: id,
          contribution_date: txnForm.transaction_date, amount, voucher_number: txnForm.voucher_number,
          notes: txnForm.description || null,
        });
      } else if (!txnForm.id && txnCategory?.direction === 'in') {
        // Capital Investment tied to a project, or a Loan (always project-tied).
        await api.post('/investments', {
          project_id: txnForm.project_id, partner_id: id,
          transaction_date: txnForm.transaction_date, amount,
          investment_type: txnCategory.key === 'loan_in' ? 'partner_loan' : 'capital_investment',
          voucher_number: txnForm.voucher_number, notes: txnForm.description || null,
          paid_to_company_fund: !!txnForm.paid_to_company_fund,
        });
      } else {
        // Company is now always picked explicitly in the form (before
        // Project, which is filtered by it) -- the backend cross-checks a
        // supplied project_id against a supplied company_id and rejects a
        // mismatch, so sending both together here is safe; they always
        // agree since Project's own options are already filtered to this
        // company. `category` is a picker-only field, never a real column
        // on partner_transactions -- stripped out rather than spread.
        const { category, ...rest } = txnForm;
        const payload = { ...rest, partner_id: id, project_id: txnForm.project_id || null, amount };
        // User-requested: edit right for a company admin (and master admin)
        // on a transaction added from this page -- previously create-only.
        if (txnForm.id) await api.patch(`/partner-transactions/${txnForm.id}`, payload);
        else await api.post('/partner-transactions', payload);
      }
      setTxnOpen(false); setTxnForm({}); load(); toast.success(txnForm.id ? 'Saved' : 'Transaction recorded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setTxnSaving(false); }
  };
  const deleteTxn = async (entry) => {
    if (!window.confirm('Delete this transaction? This cannot be undone.')) return;
    try {
      await api.delete(`/partner-transactions/${entry.id}`);
      load();
      toast.success('Deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  // A ledger row is always a real "out"/interest partner_transaction (see
  // the comment above txnCategory) -- category is derived by reverse-
  // matching its transaction_type back to an "out" quick-category, purely
  // so the picker below shows the right selection; the saved
  // transaction_type itself still comes straight from entry._edit.
  const openEditTxn = (entry) => {
    const cat = PARTNER_TXN_QUICK_CATEGORIES.find(c => c.direction === 'out' && c.type === entry._edit.transaction_type);
    setTxnForm({ id: entry.id, ...entry._edit, category: cat?.key });
    setTxnOpen(true);
  };

  const companyName = (cid) => companies.find(c=>c.id===cid)?.name || '—';

  if (!id) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
        <div className="section-eyebrow mb-1">Profit Distribution & Partner Settlement</div>
        <h1 className="serif text-4xl mb-6">Partner Settlement</h1>
        {canEdit && <PartnerPayoutsSummary/>}
        <div className="card card-dark">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="text-sm text-muted">Select a partner to see their consolidated position across every project, running ledger, and record a transaction — investments, advances, and profit shares are pulled in and totalled automatically.</div>
            {allPartners.length > 4 && <input className="input" style={{width:220}} placeholder="Search partners…" value={partnerSearch} onChange={e=>setPartnerSearch(e.target.value)} data-testid="partner-search"/>}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {filteredPartners.map(p => {
              const net = positionsByPartner[p.id];
              return (
                <button key={p.id} onClick={()=>selectPartner(p.id)} className="card card-dark partner-pick-card text-left" style={{cursor:'pointer'}} data-testid={`partner-pick-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="serif text-lg">{p.name}</div>
                        {/* User-requested: label an Investor partner right
                            here as Project- or Company-based, same
                            wording INVESTOR_SCOPE_LABELS already uses on
                            Settings > Partners. */}
                        {p.investor_scope && <span className="chip" style={{fontSize:10, background:'rgba(217,164,65,0.14)', color:'var(--accent-amber)'}}>{INVESTOR_SCOPE_LABELS[p.investor_scope]}</span>}
                      </div>
                      <div className="text-xs text-muted mt-0.5">{p.phone || p.email || 'No contact info'}</div>
                    </div>
                    {net !== undefined && (
                      <div className="text-right flex-shrink-0">
                        <div className={"mono text-sm " + (net == null ? 'text-muted' : net >= 0 ? 'text-[color:var(--gold)]' : 'text-red-400')}>{net == null ? '—' : fmtINR(net)}</div>
                        <div className="text-xs text-muted">{net == null ? '' : net >= 0 ? 'Payable' : 'Receivable'}</div>
                        {cashByPartner[p.id] && net != null && Math.abs((cashByPartner[p.id].net_position_cash_basis ?? net) - net) > 0.5 && (
                          <div className="mt-1" data-testid={`partner-cash-${p.id}`} title="The same position with each project's profit limited to what the client has actually paid for (costs recovered first)">
                            <div className="mono text-sm font-semibold" style={{ color: 'var(--accent-amber)' }}>{fmtINR(cashByPartner[p.id].net_position_cash_basis)}</div>
                            <div className="text-xs text-muted">cash-backed</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {(p.company_ids||[]).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(p.company_ids||[]).map(cid => <span key={cid} className="chip" style={{fontSize:10}}>{companyName(cid)}</span>)}
                    </div>
                  )}
                </button>
              );
            })}
            {allPartners.length===0 && <div className="text-muted text-sm">No partners yet — add one under Settings &gt; Partners.</div>}
            {allPartners.length>0 && filteredPartners.length===0 && <div className="text-muted text-sm">No partners match "{partnerSearch}".</div>}
          </div>
        </div>
      </div>
    );
  }
  if (loadError) return <div className="max-w-2xl mx-auto px-6 py-16 text-center"><div className="text-lg text-red-400 mb-2">{loadError}</div><button onClick={()=>selectPartner(null)} className="text-sm text-muted hover:text-[color:var(--gold)]">← All Partners</button></div>;
  if (!ledger || !position) return <Loading/>;

  // partner_position now takes the same company_ids filter as
  // partner_ledger and applies it server-side (including correctly
  // scoping company-level, no-project_id rows by their own company) --
  // no client-side re-filtering needed here any more.
  const visiblePositionProjects = position.projects;
  const visibleTotals = position.totals;

  const exportLedgerPDF = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(`Partner Statement — ${ledger.partner.name}`, 14, 18);
    doc.setFontSize(9);
    doc.text(`Generated: ${fmtGeneratedAt()}`, 14, 26);
    if (fy) doc.text(`Financial Year: ${fy}`, 14, 32);
    autoTable(doc, {
      startY: 38,
      head: [['Date','Type','Description','Credit','Debit','Balance']],
      body: ledger.entries.map(e => [fmtDate(e.date), e.type, (e.description||'').slice(0,40), fmtINRPdf(e.credit), fmtINRPdf(e.debit), fmtINRPdf(e.balance)]),
      styles: {fontSize:8}, headStyles: {fillColor:[201,168,76], textColor:[20,20,20]}
    });
    const finalY = doc.lastAutoTable?.finalY || 100;
    doc.setFontSize(10);
    doc.text(`Total Credit: ${fmtINRPdf(ledger.totals.total_credit)}`, 14, finalY+10);
    doc.text(`Total Debit:  ${fmtINRPdf(ledger.totals.total_debit)}`, 14, finalY+16);
    doc.text(`Closing Balance: ${fmtINRPdf(ledger.totals.closing_balance)}`, 14, finalY+22);
    if (position?.totals?.net_position_cash_basis !== undefined) {
      const pt = position.totals;
      doc.text(`Net Position: ${fmtINRPdf(pt.net_position)} (billed basis)  ·  ${fmtINRPdf(pt.net_position_cash_basis)} cash-backed`, 14, finalY+28);
      doc.text(`Profit payable now (cash-backed): ${fmtINRPdf(pt.payable_now)}  ·  awaiting collection: ${fmtINRPdf(pt.awaiting_collection)}  ·  paid ahead: ${fmtINRPdf(pt.paid_ahead)}`, 14, finalY+34);
    }
    // Cash-backed view of the profit this partner has been approved for -- the same figures as the Profit Distribution tab.
    const dl = distributions || [];
    if (dl.length && includeBreakdown) {
      let y2 = finalY + 42;
      if (y2 > doc.internal.pageSize.getHeight() - 50) { doc.addPage(); y2 = 16; }
      doc.setFontSize(11); doc.text('Profit distributions — cash-backed', 14, y2);
      doc.setFontSize(8); doc.text('Entitlement is on the billed basis. Cash-backed = the part the client has actually paid for (costs recovered first). Payable now = cash-backed minus what has been paid.', 14, y2 + 5, { maxWidth: 182 });
      autoTable(doc, { startY: y2 + 10, head: [['Project', 'Entitlement', 'Cash-backed', 'Paid', 'Payable now', 'Balance']],
        body: dl.map(d => [d.project_name, fmtINRPdf(d.total_profit_entitlement), fmtINRPdf(d.total_profit_entitlement_cash_basis ?? d.total_profit_entitlement), fmtINRPdf(d.profit_paid), fmtINRPdf(d.payable_now_cash_basis ?? 0), fmtINRPdf(d.balance)]),
        foot: [['Total', fmtINRPdf(distTotals.entitlement), fmtINRPdf(distTotals.entitlementCashBasis), fmtINRPdf(distTotals.paid), fmtINRPdf(distTotals.payableNow), fmtINRPdf(distTotals.balance)]],
        styles: {fontSize:8}, headStyles: {fillColor:[201,168,76], textColor:[20,20,20]}, footStyles: {fillColor:[240,232,205], textColor:[20,20,20], fontStyle:'bold'},
        columnStyles: { 1: {halign:'right'}, 2: {halign:'right'}, 3: {halign:'right'}, 4: {halign:'right'}, 5: {halign:'right'} } });
    }
    doc.save(`partner-statement-${ledger.partner.name}-${Date.now()}.pdf`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <button onClick={()=>selectPartner(null)} className="text-xs text-muted hover:text-[color:var(--gold)] no-print">← All Partners</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mt-3 mb-6 no-print">
        <div>
          <div className="section-eyebrow mb-1">Profit Distribution & Partner Settlement</div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="serif text-4xl">{ledger.partner.name}</h1>
            {ledger.partner.investor_scope && <span className="chip" style={{background:'rgba(217,164,65,0.14)', color:'var(--accent-amber)'}}>{INVESTOR_SCOPE_LABELS[ledger.partner.investor_scope]}</span>}
          </div>
          <div className="text-sm text-muted mt-1">{ledger.partner.phone || ledger.partner.email || 'No contact info'}</div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div data-testid="partner-company-filter">
            <GrantPicker
              items={companies.filter(c=>partnerCompanyIds.includes(c.id)).map(c=>({id:c.id, name:c.name}))}
              selectedIds={companyIds}
              allAccess={false}
              onToggle={cid => setCompanyIds(cur => cur.includes(cid) ? cur.filter(x=>x!==cid) : [...cur, cid])}
              onSetAllAccess={()=>{}}
              canGrantAll={false}
              allLabel="All Companies"
              noneWidth={200}
            />
          </div>
          {/* Audit finding: this control sat above the tab bar (visible +
              interactive on all 4 tabs) but `fy` only ever fed the Ledger
              tab's own fetch -- Position has no financial_year param at
              all (it's a deliberate all-time cumulative balance, not a
              per-year one), and Current Account / Bank Ledger each
              already have their OWN separate, tab-scoped FY control
              (caFy below; BankLedgerModal's own internal filter) that
              actually does something. Picking a year on Position or Bank
              Ledger changed nothing with no indication it was ignored.
              Gated to the one tab it actually affects, same convention
              AuditQueuePage already uses for its own per-tab-only
              filters. */}
          {tab==='ledger' && <FySelect value={fy} onChange={setFy} years={distinctFYs(projects.filter(p=>partnerCompanyIds.includes(p.company_id)).map(p=>p.financial_year))} style={{width:160}}/>}
          {tab==='distributions' && <FySelect value={distFy} onChange={setDistFy} years={distinctFYs((distributions||[]).map(d=>d.financial_year))} style={{width:160}}/>}
          {(tab==='position' || tab==='distributions') && <label className="flex items-center gap-1.5 text-xs cursor-pointer" title="Applies to Print and to the Statement PDF"><input type="checkbox" checked={includeBreakdown} onChange={e=>setIncludeBreakdown(e.target.checked)} data-testid="include-breakdown"/>Include project-wise breakdown</label>}
          <button onClick={exportLedgerPDF} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Statement PDF</button>
          <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>
          {canEdit && <button onClick={()=>{setTxnForm({transaction_date:new Date().toISOString().slice(0,10), company_id: partnerCompanyIds.length===1 ? partnerCompanyIds[0] : ''});setTxnOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-partner-txn"><Plus size={14}/>Add Transaction</button>}
        </div>
      </div>
      <div className="flex gap-2 mb-6 no-print">
        <button className={"tab "+(tab==='position'?'active':'')} onClick={()=>setTab('position')} data-testid="tab-position">Position</button>
        <button className={"tab "+(tab==='ledger'?'active':'')} onClick={()=>setTab('ledger')} data-testid="tab-ledger">Ledger</button>
        <button className={"tab "+(tab==='currentaccount'?'active':'')} onClick={()=>setTab('currentaccount')} data-testid="tab-currentaccount">Current Account</button>
        {user.role !== 'partner' && <button className={"tab "+(tab==='bankledger'?'active':'')} onClick={()=>setTab('bankledger')} data-testid="tab-bankledger">Bank Ledger</button>}
        <button className={"tab "+(tab==='distributions'?'active':'')} onClick={()=>setTab('distributions')} data-testid="tab-distributions">Profit Distribution</button>
      </div>

      {tab==='position' && (
        <div className="print-area">
          <div className="print-header">
            <div className="print-company">{ledger.partner.name}</div>
            <div className="print-report-title">Partner Position{companyIds.length ? ` — ${companyIds.length} company(ies) selected` : ' — All Companies'}</div>
            <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
          </div>
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Capital Introduced" value={fmtINR(visibleTotals.capital)}/>
            <KpiCard label="Loans" value={fmtINR(visibleTotals.loan)}/>
            <KpiCard label="Personal Expenses Borne" value={fmtINR(visibleTotals.personal_expense)}/>
            <KpiCard label="Security Instruments Borne" value={fmtINR(visibleTotals.security_borne)} hint="EMD/BG/FD funded personally — owed back, less anything forfeited"/>
            <KpiCard label="Profit Entitlement (Approved)" value={fmtINR(visibleTotals.profit_entitlement)} gold hint="Approved projects only. The Dashboard estimate also counts running projects."/>
            <KpiCard label="Profit Paid" value={fmtINR(visibleTotals.profit_paid)}/>
            <KpiCard label="Capital Returned" value={fmtINR(visibleTotals.capital_returned)}/>
            <KpiCard label="Withdrawals" value={fmtINR(visibleTotals.withdrawals)}/>
            <KpiCard label="Net Position" value={fmtINR(visibleTotals.net_position)} gold hint={(visibleTotals.net_position>=0?'Payable to Partner':'Receivable from Partner') + ' · includes personal expenses and security borne, so it is larger than the Dashboard profit balance'}/>
          </div>
          {visibleTotals.net_position_cash_basis !== undefined && (
            <div className="card card-dark mb-6" data-testid="partner-cash-backed">
              <div className="serif text-xl mb-1 text-[color:var(--gold)]">Cash-backed position — what can actually be paid now</div>
              <div className="text-xs text-muted mb-4 max-w-3xl">The figures above count each approved project's profit on the billed basis. A client may not have paid for all of it yet. Here each project's profit is limited to what the client has actually paid for (costs are recovered first); capital, loans, expenses borne and payments already made are real cash and are unchanged.</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 text-sm">
                <div><div className="text-xs text-muted">Profit entitlement (billed basis)</div><div className="mono">{fmtINR(visibleTotals.profit_entitlement)}</div></div>
                <div><div className="text-xs text-muted">Cash-backed entitlement</div><div className="mono text-[color:var(--gold)]">{fmtINR(visibleTotals.profit_entitlement_cash_basis)}</div></div>
                <div title="Profit-making projects only: a loss share is real but is not something that can be paid out"><div className="text-xs text-muted">Payable now (after payments)</div><div className="mono font-semibold text-[color:var(--gold)]">{fmtINR(visibleTotals.payable_now)}</div></div>
                <div><div className="text-xs text-muted">Awaiting collection</div><div className="mono text-amber-400">{fmtINR(visibleTotals.awaiting_collection)}</div></div>
                <div><div className="text-xs text-muted">Paid ahead of collection</div><div className={'mono ' + (visibleTotals.paid_ahead > 0.5 ? 'text-red-400' : '')}>{fmtINR(visibleTotals.paid_ahead)}</div></div>
              </div>
              <div className="text-sm mb-3">Net Position: <span className="mono text-muted">{fmtINR(visibleTotals.net_position)}</span> on the billed basis · <span className="mono font-semibold text-[color:var(--gold)]">{fmtINR(visibleTotals.net_position_cash_basis)}</span> cash-backed ({(visibleTotals.net_position_cash_basis >= 0 ? 'payable to the partner' : 'receivable from the partner')}).</div>
              <div className={"overflow-x-auto" + (includeBreakdown ? '' : ' no-print')}>
                <table className="tbl">
                  <thead><tr><th>Project</th><th className="text-right">Entitlement</th><th className="text-right">Cash-backed %</th><th className="text-right">Cash-backed</th><th className="text-right">Paid</th><th className="text-right">Payable now</th><th className="text-right">Paid ahead</th><th className="text-right">Awaiting collection</th></tr></thead>
                  <tbody>
                    {(visiblePositionProjects || []).filter(x => Number(x.profit_entitlement) || Number(x.profit_paid)).length === 0 && <tr><td colSpan="8" className="text-center text-muted py-4">No approved project profit yet.</td></tr>}
                    {(visiblePositionProjects || []).filter(x => Number(x.profit_entitlement) || Number(x.profit_paid)).map((x, i) => (
                      <tr key={x.project_id || i}><td>{x.project_name || 'Company-level'}</td><td className="mono text-right text-muted">{fmtINR(x.profit_entitlement)}</td><td className="mono text-right">{x.cash_backed_pct != null ? `${x.cash_backed_pct}%` : '—'}</td><td className="mono text-right">{fmtINR(x.profit_entitlement_cash_basis)}</td><td className="mono text-right">{fmtINR(x.profit_paid)}</td><td className="mono text-right font-semibold text-[color:var(--gold)]">{fmtINR(x.payable_now)}</td><td className={'mono text-right ' + (x.paid_ahead > 0.5 ? 'text-red-400' : '')}>{x.paid_ahead > 0.5 ? fmtINR(x.paid_ahead) : '—'}</td><td className="mono text-right text-amber-400">{x.awaiting_collection > 0.5 ? fmtINR(x.awaiting_collection) : '—'}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {position.reconciliation && (() => {
            const rec = position.reconciliation;
            const Row = ({label, value, strong, hint}) => (
              <div className="flex items-baseline justify-between gap-3 py-1 text-sm" title={hint}>
                <span className={strong ? 'font-semibold' : 'text-muted'}>{label}</span>
                <span className={"mono " + (strong ? 'font-semibold text-[color:var(--gold)]' : '')}>{fmtINR(value)}</span>
              </div>
            );
            return (
              <div className="card card-dark mb-6" data-testid="capital-expense-account">
                <div className="serif text-xl mb-1 text-[color:var(--gold)]">Capital &amp; Expenses Account</div>
                <div className="text-xs text-muted mb-4">Net Position above is the single figure that decides what is owed. This splits it into the two things a partner can be owed back, so money already received is set against the right one. Worked out company by company.</div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div data-testid="capital-account">
                    <div className="text-xs uppercase tracking-wider text-muted mb-1">Capital</div>
                    <Row label="Capital introduced" value={rec.capital_introduced}/>
                    <Row label="Capital returned (up to what was introduced)" value={rec.capital_returned_within_capital}/>
                    {rec.capital_locked > 0 && <Row label="of which locked in refundable securities" value={rec.capital_locked} hint="Capital tied up in Security & Refundables items that are not refunded yet. Payments to the partner are never counted as returning it."/>}
                    {rec.capital_locked > 0 && <Row label="of which free to be returned now" value={rec.capital_free_still_owed}/>}
                    <Row label="Capital still owed to partner" value={rec.capital_still_owed} strong/>
                  </div>
                  <div data-testid="expense-account">
                    <div className="text-xs uppercase tracking-wider text-muted mb-1">Personal expenses</div>
                    <Row label="Personal expenses borne" value={rec.expenses_borne}/>
                    <Row label="Expense reimbursements paid" value={rec.expenses_reimbursed}/>
                    <Row label="Capital returned beyond capital introduced" value={rec.capital_return_excess} hint="Money paid back as 'capital return' that is more than the capital this partner actually put into that company — in substance, a refund of expenses they paid."/>
                    <Row label="Expenses still owed to partner" value={rec.expenses_still_owed} strong/>
                  </div>
                </div>
                {(rec.locks||[]).length > 0 && (
                  <div className="mt-4 text-xs" data-testid="capital-locks-list">
                    <div className="uppercase tracking-wider text-muted mb-1">Capital locked until refunded</div>
                    {rec.locks.map(l => (
                      <div key={l.security_id} className="flex justify-between gap-3 py-0.5">
                        <span>{(SECURITY_TYPE_LABELS[l.instrument_type] || l.instrument_type)}{l.party_name ? ` — ${l.party_name}` : ''} <span className="text-muted">({SECURITY_STATUS_LABELS[l.status] || l.status})</span></span>
                        <span className="mono">{fmtINR(l.locked_amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {rec.capital_return_excess > 0 && (
                  <div className="mt-4 p-3 rounded text-sm" style={{background:'rgba(217,164,65,0.12)'}} data-testid="capital-excess-notice">
                    {fmtINR(rec.capital_return_excess)} was paid back as “Capital Return” but is more than the capital introduced, so it is counted above as a refund of personal expenses. Net Position is the same either way.
                    {user.role === 'master_admin'
                      ? <> <button onClick={openConvert} className="btn-gold btn-small ml-2 no-print" data-testid="open-convert-capital-returns">Review &amp; re-file these payments as expense refunds</button></>
                      : <> Ask a master admin to re-file these payments if the records should say so.</>}
                  </div>
                )}
                {rec.overpaid_beyond_expenses > 0 && (
                  <div className="mt-3 p-3 rounded text-sm text-red-400" style={{background:'rgba(220,80,80,0.10)'}} data-testid="capital-overpaid-notice">
                    {fmtINR(rec.overpaid_beyond_expenses)} has been paid back beyond both the capital introduced and every personal expense borne — this partner has received more than they put in. It is already reducing Net Position.
                  </div>
                )}
              </div>
            );
          })()}
          <div className={"card card-dark overflow-x-auto" + (includeBreakdown ? '' : ' no-print')}>
            <div className="serif text-xl mb-4 text-[color:var(--gold)]">Project-wise Breakdown</div>
            <table className="tbl">
              <thead><tr><th>Project</th><th>Capital</th><th>Loan</th><th>Personal Exp</th><th>Security Borne</th><th>Profit Entitlement</th><th>Profit Paid</th><th>Returned/Withdrawn</th><th>Net</th></tr></thead>
              <tbody>
                {visiblePositionProjects.length===0 && <tr><td colSpan="9" className="text-center text-muted py-6">No projects.</td></tr>}
                {visiblePositionProjects.map(p => (
                  <tr key={p.project_id}>
                    <td>{p.project_id ? <Link to={`/app/projects/${p.project_id}`} className="hover:text-[color:var(--gold)]">{p.project_name}</Link> : p.project_name}</td>
                    <td className="mono">{fmtINR(p.capital)}</td>
                    <td className="mono">{fmtINR(p.loan)}</td>
                    <td className="mono">{fmtINR(p.personal_expense)}</td>
                    <td className="mono">{fmtINR(p.security_borne)}</td>
                    <td className="mono">{fmtINR(p.profit_entitlement)}</td>
                    <td className="mono">{fmtINR(p.profit_paid)}</td>
                    <td className="mono">{fmtINR(p.capital_returned + p.loan_repaid + p.withdrawals + p.reimbursements + p.other_debits)}</td>
                    <td className={"mono " + (p.net_position>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(p.net_position)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab==='ledger' && (() => {
        // Filters first (none of them touch each entry's own already-
        // computed balance, just which rows are shown), then the Date-
        // only sort -- see the state comment above for why Balance can't
        // be a sortable column here.
        const q = ledgerSearch.trim().toLowerCase();
        const typeLabelOf = e => TXN_TYPE_LABELS[e.type] || INV_TYPE_LABELS[e.type] || SECURITY_TYPE_LABELS[e.type] || LEDGER_ENTRY_TYPE_LABELS[e.type] || e.type || '';
        let filteredEntries = ledger.entries;
        if (ledgerFromDate) filteredEntries = filteredEntries.filter(e => (e.date||'') >= ledgerFromDate);
        if (ledgerToDate) filteredEntries = filteredEntries.filter(e => (e.date||'') <= ledgerToDate);
        if (ledgerProjectFilter) filteredEntries = filteredEntries.filter(e => ledgerProjectFilter === '__none__' ? !e.project_id : e.project_id === ledgerProjectFilter);
        if (ledgerTypeFilter) filteredEntries = filteredEntries.filter(e => e.type === ledgerTypeFilter);
        if (q) filteredEntries = filteredEntries.filter(e =>
          typeLabelOf(e).toLowerCase().includes(q)
          || (e.description||'').toLowerCase().includes(q)
          || (e.voucher_number||'').toLowerCase().includes(q));
        // Options built from what's actually IN this partner's ledger
        // (not every project/type in the app), so the dropdowns only ever
        // offer choices that could plausibly return a row.
        const ledgerProjectIds = [...new Set(ledger.entries.map(e=>e.project_id).filter(Boolean))];
        const ledgerHasNoProjectEntry = ledger.entries.some(e=>!e.project_id);
        const ledgerTypes = [...new Set(ledger.entries.map(e=>e.type).filter(Boolean))];
        const filtersActive = ledgerFromDate || ledgerToDate || ledgerProjectFilter || ledgerTypeFilter;
        return (
        <div className="card card-dark overflow-x-auto print-area">
          <div className="print-header">
            <div className="print-company">{ledger.partner.name}</div>
            <div className="print-report-title">Partner Ledger{companyIds.length ? ` — ${companyIds.length} company(ies) selected` : ' — All Companies'}</div>
            <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
          </div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2 no-print">
            <div className="flex items-center gap-2 flex-wrap">
              <input type="date" className="input" style={{width:140}} title="From date" value={ledgerFromDate} onChange={e=>setLedgerFromDate(e.target.value)}/>
              <span className="text-xs text-muted">to</span>
              <input type="date" className="input" style={{width:140}} title="To date" value={ledgerToDate} onChange={e=>setLedgerToDate(e.target.value)}/>
              <select className="input" style={{width:170}} value={ledgerProjectFilter} onChange={e=>setLedgerProjectFilter(e.target.value)}>
                <option value="">All Projects</option>
                {ledgerHasNoProjectEntry && <option value="__none__">— Company-level (no project) —</option>}
                {ledgerProjectIds.map(pid=><option key={pid} value={pid}>{projects.find(p=>p.id===pid)?.project_name || pid}</option>)}
              </select>
              <select className="input" style={{width:170}} value={ledgerTypeFilter} onChange={e=>setLedgerTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                {ledgerTypes.map(t=><option key={t} value={t}>{TXN_TYPE_LABELS[t] || INV_TYPE_LABELS[t] || SECURITY_TYPE_LABELS[t] || LEDGER_ENTRY_TYPE_LABELS[t] || t}</option>)}
              </select>
              {filtersActive && <button className="btn-outline btn-small" onClick={()=>{setLedgerFromDate('');setLedgerToDate('');setLedgerProjectFilter('');setLedgerTypeFilter('');}}>Clear</button>}
            </div>
          </div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="serif text-xl text-[color:var(--gold)]">Running Ledger</div>
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <input className="input no-print" style={{width:220}} placeholder="Search type, description, voucher…" value={ledgerSearch} onChange={e=>setLedgerSearch(e.target.value)}/>
              <span>Total Credit: <span className="mono text-[color:var(--gold)]">{fmtINR(ledger.totals.total_credit)}</span></span>
              <span>Total Debit: <span className="mono">{fmtINR(ledger.totals.total_debit)}</span></span>
              <span>Closing: <span className="mono text-[color:var(--gold)]">{fmtINR(ledger.totals.closing_balance)}</span></span>
            </div>
          </div>
          <PartnerLedgerTable entries={filteredEntries} canEdit={canEdit} isMaster={isMasterAdmin(user)} openEditTxn={openEditTxn} deleteTxn={deleteTxn} emptyLabel={(q || filtersActive) ? 'No entries match your search/filters.' : 'No entries.'}/>
        </div>
        );
      })()}

      {tab==='currentaccount' && (
        <div className="card card-dark no-print">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <div className="serif text-xl text-[color:var(--gold)]">Current Account</div>
              <div className="text-xs text-muted mt-1 max-w-xl">Company-wide balance — every way this partner has funded the company (investments, personal expenses paid, profit entitlements, Company Fund contributions) minus every way they've taken money out. An optional interest rate rewards a positive balance and symmetrically charges a negative one.</div>
            </div>
            <div className="flex gap-2 items-center">
              {partnerCompanyIds.length > 1 && <select className="input" style={{width:170}} value={caCompanyId} onChange={e=>setCaCompanyId(e.target.value)}>
                {partnerCompanyIds.map(cid=><option key={cid} value={cid}>{companies.find(c=>c.id===cid)?.name || cid}</option>)}
              </select>}
              <FySelect value={caFy} onChange={setCaFy} years={distinctFYs([fyFromDateStr(new Date().toISOString()), ...(currentAccount ? [currentAccount.financial_year] : [])])} style={{width:150}}/>
              {canEdit && <button onClick={openRate} className="btn-outline btn-small inline-flex items-center gap-1"><Edit3 size={14}/>Interest Rate %</button>}
            </div>
          </div>
          {currentAccount ? (
            <>
              <div className="grid md:grid-cols-4 gap-4">
                <KpiCard label="Balance" value={fmtINR(currentAccount.balance)} tone={currentAccount.balance<0?'red':'gold'} hint={currentAccount.balance>=0?'Owed to partner':'Owed by partner'}/>
                <KpiCard label={`Avg Balance — ${currentAccount.financial_year}`} value={fmtINR(currentAccount.avg_balance_this_fy)}/>
                <KpiCard label="Interest Rate" value={`${currentAccount.interest_rate_pct}%`}/>
                <KpiCard label="Accrued Interest" value={fmtINR(Math.abs(currentAccount.accrued_interest))} tone={currentAccount.accrued_interest<0?'red':'green'} hint={currentAccount.accrued_interest>=0?'Owed to partner':(currentAccount.accrued_interest<0?'Owed by partner':undefined)}/>
              </div>
              {canEdit && currentAccount.interest_rate_pct > 0 && currentAccount.accrued_interest !== 0 && (
                <div className="mt-4 flex justify-end">
                  <button onClick={postInterest} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="post-interest"><Plus size={14}/>Post Interest as Transaction</button>
                </div>
              )}
            </>
          ) : <div className="text-sm text-muted py-6 text-center">{partnerCompanyIds.length ? <LoadingMark size={22}/> : 'This partner has no company assigned.'}</div>}
        </div>
      )}

      {tab==='bankledger' && (() => {
        const scopedBankAccounts = bankAccounts.filter(b => partnerCompanyIds.includes(b.company_id));
        const acct = scopedBankAccounts.find(b => b.id === ledgerBankAccountId);
        return (
          <div className="card card-dark no-print">
            <div className="serif text-xl text-[color:var(--gold)] mb-1">Bank Account Ledger</div>
            <div className="text-xs text-muted mb-4 max-w-xl">Full credit/payment history for any bank account this partner's company(ies) hold — opening/closing balance, FY or date-range filters, Export PDF/Print, same as Settings &gt; Bank Accounts and Reports.</div>
            <select className="input" style={{width:320}} value={ledgerBankAccountId} onChange={e=>setLedgerBankAccountId(e.target.value)} data-testid="partner-bank-account">
              <option value="">-- select bank account --</option>
              {scopedBankAccounts.map(b => <option key={b.id} value={b.id}>{b.bank_name}{b.account_number ? ' ••'+String(b.account_number).slice(-4) : ''}{partnerCompanyIds.length > 1 ? ` — ${companies.find(c=>c.id===b.company_id)?.name || ''}` : ''}</option>)}
            </select>
            {scopedBankAccounts.length===0 && <div className="text-sm text-muted mt-4">No bank accounts on this partner's company(ies) yet.</div>}
            {acct && <BankLedgerModal account={acct} companyName={companies.find(c=>c.id===acct.company_id)?.name} canEdit={canEdit} onClose={()=>setLedgerBankAccountId('')}/>}
          </div>
        );
      })()}

      {tab==='distributions' && (
        <div className="print-area">
          <div className="print-header">
            <div className="print-company">{ledger.partner.name}</div>
            <div className="print-report-title">Profit Distribution{companyIds.length ? ` — ${companyIds.length} company(ies) selected` : ' — All Companies'}{distFy ? ` — ${distFy}` : ''}</div>
            <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
          </div>
          <div className="flex items-start justify-between gap-4 mb-4 no-print">
            <div className="text-xs text-muted max-w-2xl">Every project this partner's profit distribution has been approved on, one row per project, filterable by company (above) and financial year. A project shown as "Reopened" below is being corrected right now — its figures here are the last signed snapshot, not necessarily final.</div>
            {isPartnerLogin && <button onClick={()=>openReport(null)} className="btn-amber btn-small inline-flex items-center gap-1 flex-shrink-0" data-testid="report-issue"><Flag size={14}/>Report an Issue</button>}
          </div>
          <div className="grid md:grid-cols-5 gap-4 mb-6">
            <KpiCard label="Total Profit Entitlement" value={fmtINR(distTotals.entitlement)} gold hint="Approved projects only, so it can differ from the Dashboard estimate."/>
            <KpiCard label="Real Entitlement Now" value={fmtINR(distTotals.entitlementCashBasis)} hint="Cash-backed — the part of each project's profit the client has actually paid for (costs recovered first)"/>
            <KpiCard label="Payable Now" value={fmtINR(distTotals.payableNow)} gold hint="Cash-backed entitlement not yet paid out"/>
            <KpiCard label="Profit Paid" value={fmtINR(distTotals.paid)}/>
            <KpiCard label="Balance" value={fmtINR(distTotals.balance)} tone={distTotals.balance<0?'red':undefined}/>
          </div>
          <div className={"card card-dark overflow-x-auto" + (includeBreakdown ? '' : ' no-print')}>
            <table className="tbl">
              <thead><tr><th>Project</th><th>Company</th><th>FY</th><th>Model</th><th>Money %</th><th>Work %</th><th>Money Profit</th><th>Work Profit</th><th>Total Entitlement</th><th>Real Entitlement Now</th><th>Paid</th><th>Payable Now</th><th>Balance</th><th>Approved By</th><th>Approved At</th>{isPartnerLogin && <th className="no-print"></th>}</tr></thead>
              <tbody>
                {distributions===null && <tr><td colSpan="16" className="text-center text-muted py-6"><LoadingMark size={22}/></td></tr>}
                {distributions!==null && distributions.length===0 && <tr><td colSpan="16" className="text-center text-muted py-6">No approved profit distributions yet.</td></tr>}
                {(distributions||[]).map(d => {
                  const realTooltip = (d.cash_backed_pct ?? d.collection_pct) >= 100
                    ? 'Fully cash-backed — this project has been collected in full.'
                    : `${d.cash_backed_pct ?? d.collection_pct}% of this profit is backed by cash (${d.collection_pct}% of the contract collected; costs are recovered first)` + (d.deductions_outstanding > 0 ? ` — ${fmtINR(d.deductions_outstanding)} in deductions not yet recovered` : ' — balance of the contract not yet billed/collected');
                  return (
                  <tr key={d.id}>
                    <td>
                      {d.project_id ? <Link to={`/app/projects/${d.project_id}`} className="hover:text-[color:var(--gold)]">{d.project_name}</Link> : d.project_name}
                      {!isProjectLocked({status:d.project_status}) && <span className="chip ml-1.5" style={{background:'rgba(217,164,65,0.14)', color:'var(--accent-amber)'}} title="This project has been reopened for correction since this distribution was approved">Reopened</span>}
                    </td>
                    <td>{d.company_name || '—'}</td>
                    <td>{d.financial_year || '—'}</td>
                    <td>{PARTNERSHIP_MODEL_LABELS?.[d.distribution_model] || d.distribution_model || '—'}</td>
                    <td className="mono">{d.money_share_percentage ? `${d.money_share_percentage}%` : '—'}</td>
                    <td className="mono">{d.work_share_percentage ? `${d.work_share_percentage}%` : '—'}</td>
                    <td className="mono">{fmtINR(d.money_profit)}</td>
                    <td className="mono">{fmtINR(d.work_profit)}</td>
                    <td className="mono text-muted" title="Locked in at approval against the full contract value — may include money not yet collected">{fmtINR(d.total_profit_entitlement)}</td>
                    <td className="mono font-semibold text-[color:var(--gold)]" title={realTooltip}>{fmtINR(d.total_profit_entitlement_cash_basis ?? d.total_profit_entitlement)}</td>
                    <td className="mono">{fmtINR(d.profit_paid)}</td>
                    <td className="mono font-semibold text-[color:var(--gold)]" title="Cash-backed entitlement minus what has already been paid">{fmtINR(d.payable_now_cash_basis ?? 0)}</td>
                    <td className={"mono " + (d.balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(d.balance)}</td>
                    <td>{d.approved_by || '—'}</td>
                    <td>{d.approved_at ? fmtDate(d.approved_at) : '—'}</td>
                    {isPartnerLogin && <td className="no-print"><button onClick={()=>openReport(d)} className="tab btn-small" title="Flag this distribution"><Flag size={12}/></button></td>}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card card-dark overflow-x-auto mt-6 no-print" data-testid="partner-reports">
            <div className="serif text-xl mb-1 text-[color:var(--gold)]">Reports</div>
            <div className="text-xs text-muted mb-4 max-w-xl">{isPartnerLogin ? 'Anything you\'ve flagged or reported — a reopen request, a suspected miscalculation, or any other comment — and its status here once the office responds.' : 'Anything this partner has flagged or reported — resolve with a note once it\'s been looked into.'}</div>
            <table className="tbl">
              <thead><tr><th>Date</th><th>Category</th><th>Project</th><th>Message</th><th>Status</th><th>Resolution</th>{canEdit && <th>Resolved By</th>}</tr></thead>
              <tbody>
                {reports.length===0 && <tr><td colSpan={canEdit?7:6} className="text-center text-muted py-6">Nothing reported yet.</td></tr>}
                {reports.map(r => (
                  <tr key={r.id}>
                    <td className="text-xs">{fmtDate(r.created_at)}</td>
                    <td className="text-xs">{PARTNER_REPORT_CATEGORY_LABELS[r.category] || r.category}</td>
                    <td className="text-xs">{r.project_name || '—'}</td>
                    <td className="text-xs" style={{maxWidth:280, whiteSpace:'normal'}}>{r.message}</td>
                    <td><span className={"badge "+(r.status==='resolved'?'status-closed':'status-financial_review')}>{r.status}</span></td>
                    <td className="text-xs" style={{maxWidth:220, whiteSpace:'normal'}}>
                      {r.status==='resolved' ? (r.resolution_note || <span className="text-muted">—</span>) : (canEdit ? (
                        <div className="flex gap-1 items-start">
                          <input className="input" style={{width:160}} placeholder="Optional note" value={resolveNoteById[r.id]||''} onChange={e=>setResolveNoteById(m=>({...m,[r.id]:e.target.value}))}/>
                          <button onClick={()=>resolveReport(r.id)} disabled={resolvingId===r.id} className="btn-gold btn-small">{resolvingId===r.id?'…':'Resolve'}</button>
                        </div>
                      ) : <span className="text-muted">Open</span>)}
                    </td>
                    {canEdit && r.status==='resolved' && <td className="text-xs text-muted">{r.resolved_by}</td>}
                    {canEdit && r.status!=='resolved' && <td></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={reportOpen} onClose={()=>setReportOpen(false)} title="Report an Issue">
        <div className="grid grid-cols-1 gap-4">
          {reportForm._project_name && <div className="text-xs text-muted">Regarding: <span className="text-[color:var(--gold)]">{reportForm._project_name}</span></div>}
          <Field label="Category *"><select className="input" value={reportForm.category||'other'} onChange={e=>setReportForm({...reportForm, category:e.target.value})}>
            <option value="reopen_request">Request to Reopen</option>
            <option value="miscalculation">Suspected Miscalculation</option>
            <option value="other">Other Comment</option>
          </select></Field>
          <Field label="Message *"><textarea className="input" rows={4} value={reportForm.message||''} onChange={e=>setReportForm({...reportForm, message:e.target.value})} placeholder="Describe what looks wrong or what you'd like reviewed…" autoFocus/></Field>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={()=>setReportOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={saveReport} disabled={reportSaving || !reportForm.message?.trim()} className="btn-gold btn-small">{reportSaving?'Sending…':'Send'}</button>
        </div>
      </Modal>

      <Modal open={rateOpen} onClose={()=>setRateOpen(false)} title="Current Account Interest Rate %">
        <p className="text-xs text-muted mb-4">Applied to this partner's average Current Account balance for a financial year — company-wide, not tied to any project. A negative average balance accrues interest owed TO the company, symmetrically.</p>
        <Field label="Interest Rate % (annual)"><input className="input mono" type="number" min="0" value={ratePct} onChange={e=>setRatePct(e.target.value)} autoFocus/></Field>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setRateOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={saveRate} disabled={rateSaving || ratePct===''} className="btn-gold btn-small">{rateSaving?'Saving…':'Save'}</button></div>
      </Modal>

      <Modal open={convertOpen} onClose={()=>setConvertOpen(false)} title="Re-file capital returns as expense refunds" maxW={860}>
        {!convertData ? <Loading/> : (() => {
          const allTx = convertData.companies.flatMap(c => c.transactions.map(t => ({...t, company_id: c.company_id})));
          const selTotalFor = (cid) => allTx.filter(t => t.company_id === cid && convertSel.includes(t.id)).reduce((a, t) => a + t.amount, 0);
          const problems = convertData.companies.filter(c => {
            const sel = allTx.filter(t => t.company_id === c.company_id && convertSel.includes(t.id));
            const largest = sel.length ? Math.max(...sel.map(t => t.amount)) : 0;
            return selTotalFor(c.company_id) > c.excess + largest + 0.005;
          });
          return (
            <div>
              <p className="text-sm text-muted mb-3">These “Capital Return” payments are more than the capital {convertData.reconciliation ? 'this partner' : 'the partner'} introduced in that company. Re-filing them as <b>Expense Reimbursements</b> changes only their type — amount, date, voucher number, who paid and audit status stay exactly as they are, and the partner's Net Position is checked before and after (the change is undone if it moves by even a paisa). The newest payments are ticked first so the oldest stay as genuine capital returns.</p>
              {convertData.companies.length === 0 && <div className="text-sm text-muted py-4">Nothing to re-file — no company has capital returned beyond capital introduced.</div>}
              {convertData.companies.map(c => {
                const picked = selTotalFor(c.company_id);
                return (
                  <div key={c.company_id} className="mb-5" data-testid={`convert-company-${c.company_id}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                      <div className="serif text-lg">{c.company_name}</div>
                      <div className="text-xs text-muted">Introduced {fmtINR(c.capital_introduced)}{c.capital_locked > 0 ? ` (of which ${fmtINR(c.capital_locked)} locked in securities)` : ''} · returned {fmtINR(c.capital_returned)} · <span className="text-[color:var(--gold)]">beyond free capital {fmtINR(c.excess)}</span> · personal expenses {fmtINR(c.expenses_borne)}</div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="tbl">
                        <thead><tr><th></th><th>Date</th><th>Voucher</th><th>Description</th><th style={{textAlign:'right'}}>Amount</th></tr></thead>
                        <tbody>
                          {c.transactions.map(t => (
                            <tr key={t.id}>
                              <td><input type="checkbox" checked={convertSel.includes(t.id)} onChange={e => setConvertSel(cur => e.target.checked ? [...cur, t.id] : cur.filter(x => x !== t.id))} data-testid={`convert-tx-${t.id}`}/></td>
                              <td>{fmtDate(t.transaction_date)}</td>
                              <td className="mono">{t.voucher_number || '—'}</td>
                              <td>{t.description || '—'}</td>
                              <td className="mono" style={{textAlign:'right'}}>{fmtINR(t.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-xs mt-1 text-muted">Ticked: <span className="mono text-[color:var(--gold)]">{fmtINR(picked)}</span> of {fmtINR(c.excess)} beyond capital{picked > c.excess + 0.005 && picked <= c.excess + Math.max(0, ...allTx.filter(t => t.company_id === c.company_id && convertSel.includes(t.id)).map(t => t.amount)) + 0.005 ? ` — ${fmtINR(picked - c.excess)} over, because a whole payment cannot be split; that much will show as capital still owed.` : ''}</div>
                  </div>
                );
              })}
              {problems.length > 0 && <div className="text-sm text-red-400 mb-3">Too many ticked for {problems.map(c => c.company_name).join(', ')} — that would relabel genuine capital returns. Untick some.</div>}
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={()=>setConvertOpen(false)} className="btn-outline btn-small">Cancel</button>
                <button onClick={saveConvert} disabled={convertSaving || convertSel.length === 0 || problems.length > 0} className="btn-gold btn-small" data-testid="confirm-convert-capital-returns">{convertSaving ? 'Converting…' : `Re-file ${convertSel.length} payment(s) as expense refunds`}</button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal open={txnOpen} onClose={()=>setTxnOpen(false)} title={txnForm.id ? 'Edit Partner Transaction' : 'Add Partner Transaction'}>
        {/* User-requested: this form's field ORDER and step-by-step reveal
            now mirror the Company Expenses page's Partner Transaction
            quick-add exactly (Company -> What happened? -> everything
            else, hidden until a category is picked) -- previously this
            page showed every field at once in a different order
            (Date/Type/Amount/Voucher/Company/Project/...), the one real
            remaining mismatch after the wording itself was already
            unified. User-requested follow-up: Capital Investment/Loan
            (money IN) are now offered here too, routed to the same
            investments/company_capital_contributions tables the quick-add
            posts to -- see txnCategory's own comment above for why this
            needed a separate `category` field rather than reusing
            transaction_type directly. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Company now comes before everything else and drives it --
              narrowed to partnerCompanyIds (this partner's own companies,
              not every company the caller can see), so a transaction
              can't be booked against a company this partner has nothing
              to do with. Changing company clears any already-picked
              project, since that project almost certainly belongs to the
              OLD company. */}
          <div className="col-span-2">
            <Field label="Company *"><select className="input" value={txnForm.company_id||''} onChange={e=>setTxnForm({...txnForm, company_id:e.target.value, project_id:''})}>
              <option value="">— select —</option>
              {companies.filter(c=>partnerCompanyIds.includes(c.id)).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select></Field>
          </div>
          {/* Same plain-language "What happened?" picker the quick-add
              uses (PARTNER_TXN_QUICK_CATEGORIES, both directions now).
              Interest Credit/Charge stay off this picker (same
              "admin-reviewed, not an everyday entry" reasoning as the
              quick-add) -- reachable here only via "Post Interest as
              Transaction" (which pre-sets the type directly) or by
              editing an existing interest-type row, both shown as a fixed
              value instead of a free choice. */}
          {['interest_credit','interest_charge'].includes(txnForm.transaction_type) ? (
            <div className="col-span-2"><Field label="Type"><div className="input" style={{background:'transparent'}}>{TXN_TYPE_LABELS[txnForm.transaction_type]}</div></Field></div>
          ) : (
            <div className="col-span-2">
              <Field label="What happened? *">
                <select className="input" disabled={!txnForm.company_id} value={txnForm.category||''} onChange={e=>{
                  const cat = PARTNER_TXN_QUICK_CATEGORIES.find(c=>c.key===e.target.value);
                  setTxnForm({...txnForm, category: e.target.value, transaction_type: cat?.direction==='out' ? cat.type : undefined, project_id: '', paid_by_partner_id: ''});
                }}>
                  <option value="">{txnForm.company_id ? '— select —' : '— select a company first —'}</option>
                  {PARTNER_TXN_QUICK_CATEGORIES.map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </Field>
              {txnCategory?.hint && <div className="text-xs text-muted mt-1.5">{txnCategory.hint}</div>}
            </div>
          )}
          {/* Everything below only appears once a category is picked --
              same step-by-step reveal the quick-add uses, rather than
              showing the full field set up front. Gated on txnCategory
              (not transaction_type directly) so an "in" pick -- which
              deliberately leaves transaction_type unset, see txnCategory's
              own comment -- still reveals the rest of the form; an
              interest-type edit has neither set and never reaches this
              select at all (shown as fixed text above instead). */}
          {(txnCategory || txnForm.transaction_type) && <>
            {!['interest_credit','interest_charge'].includes(txnForm.transaction_type) &&
              <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={txnForm.voucher_number||''} onChange={e=>setTxnForm({...txnForm, voucher_number:e.target.value})} autoFocus/></Field>}
            <Field label="Date *"><input type="date" className="input" value={txnForm.transaction_date||''} onChange={e=>setTxnForm({...txnForm, transaction_date:e.target.value})}/></Field>
            <Field label="Amount *"><input className="input mono" type="number" min="0" value={txnForm.amount||''} onChange={e=>setTxnForm({...txnForm, amount:e.target.value})}/></Field>
            {/* Deliberately kept for "out"/interest, unlike the quick-add
                (which only ever creates company-level transactions): this
                page's own "Edit" flow needs to show/change an already
                project-tied row's project, and a genuinely project-tied
                "money out" transaction is a real, supported case here
                (e.g. repaying a partner's project-scoped capital out of
                the fund). For "in" it mirrors the quick-add exactly
                instead: required for a Loan, optional for a Capital
                Investment (company-wide when left blank, via Company
                Capital Contributions -- see saveTxn). */}
            <Field label={txnCategory?.direction === 'in' ? (txnCategory.projectRequired ? 'Project *' : 'Project (optional)') : 'Project (optional)'}>
              <select className="input" disabled={!txnForm.company_id} value={txnForm.project_id||''} onChange={e=>setTxnForm({...txnForm, project_id:e.target.value})}>
                <option value="">{!txnForm.company_id ? '— select a company first —' : (txnCategory?.direction === 'in' && txnCategory.projectRequired) ? '— select a project —' : '— company-level, no project —'}</option>
                {projects.filter(p=>p.company_id===txnForm.company_id && (!isProjectLocked(p) || p.id===txnForm.project_id || txnForm.transaction_type==='profit_payment')).map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
              </select>
            </Field>
            {/* Only meaningful once a project is picked -- a company-wide
                (no project) Capital Investment already posts to Company
                Capital Contributions instead, which unconditionally counts
                toward the Fund Ledger. Mirrors the quick-add's identical
                checkbox exactly. */}
            {txnCategory?.direction === 'in' && txnForm.project_id && (
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!txnForm.paid_to_company_fund} onChange={e=>setTxnForm({...txnForm, paid_to_company_fund:e.target.checked})}/>
                  Added to Company Reserve / Common Fund
                </label>
              </div>
            )}
            {/* User-requested: save entering the same payment on two
                partners' records separately -- if someone else personally
                fronted the cash for THIS partner's transaction, one entry
                here updates both ledgers (this one as usual, plus a
                credit on the payer's own ledger for having covered it).
                Excludes this page's own partner (id) -- Paid By must be
                someone else, enforced server-side too. Not shown for "in"
                -- an investing/lending partner is always the one putting
                the money in themselves, there's no "paid by" concept on
                that side (investments/company_capital_contributions have
                no such column). */}
            {txnCategory?.direction !== 'in' && (
              <Field label="Paid By Partner (if not this partner / company funds)">
                <select className="input" disabled={!txnForm.company_id} value={txnForm.paid_by_partner_id||''} onChange={e=>setTxnForm({...txnForm, paid_by_partner_id:e.target.value||null, paid_from_company_fund: e.target.value ? false : txnForm.paid_from_company_fund})}>
                  <option value="">— none —</option>
                  {eligiblePaidByPartners(allPartners, txnForm.company_id, txnForm.project_id, txnShowAllPartners, txnForm.paid_by_partner_id).filter(p=>p.id!==id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={txnShowAllPartners} onChange={e=>setTxnShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
              </Field>
            )}
            {/* Round 32 finding: paying a partner back out of the Company
                Fund had no way to debit the Fund Ledger at all -- mirrors
                CompanyExpensesPanel's own paid_from_company_fund checkbox
                exactly. User-requested follow-up: originally hidden
                whenever a Project was selected (fund draws were treated
                as company-level-only) -- now available either way, since
                a project-tied capital_return/withdrawal repaying money
                that itself came from the fund (see the Fund Ledger's own
                updated partner_transactions source) is just as real a
                fund draw. Still never shown for interest_credit (only
                books what's owed, doesn't pay out cash by itself) or for
                "in" (that direction's own Added to Company Reserve
                checkbox above covers it instead). */}
            {txnForm.transaction_type !== 'interest_credit' && txnCategory?.direction !== 'in' && (
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!txnForm.paid_from_company_fund} onChange={e=>setTxnForm({...txnForm, paid_from_company_fund:e.target.checked, paid_by_partner_id: e.target.checked ? null : txnForm.paid_by_partner_id})}/>
                  Paid from Company Reserve / Common Fund
                </label>
              </div>
            )}
            <div className="col-span-2"><Field label="Description"><input className="input" value={txnForm.description||''} onChange={e=>setTxnForm({...txnForm, description:e.target.value})}/></Field></div>
            {txnCategory?.direction !== 'in' && <Field label="Reference"><input className="input" value={txnForm.reference||''} onChange={e=>setTxnForm({...txnForm, reference:e.target.value})}/></Field>}
          </>}
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setTxnOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={saveTxn} disabled={txnSaving} className="btn-gold btn-small">{txnSaving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

// User-requested: search + sort on the Running Ledger. Extracted out of
// PartnerSettlementDashboard so the Date-only sort state doesn't get
// reset by that component's own frequent re-renders (partner switch,
// filter changes, etc.) -- entries is already the search-filtered list
// by the time it gets here (see the ledger tab above), this component
// only ever adds the Date-order toggle on top of that. Deliberately NOT
// sortable by Credit/Debit/Type/Balance -- Balance is a running total,
// only meaningful in true chronological order; sorting by anything else
// would leave a Balance column that no longer makes sense against the
// rows shown around it.
function PartnerLedgerTable({ entries, canEdit, isMaster, openEditTxn, deleteTxn, emptyLabel }) {
  // User-requested: this table's own Date header showed no active sort
  // arrow at all until clicked, even though `entries` already arrives in
  // true chronological order (the only order the running Balance column
  // means anything in -- see the comment on ledgerSearch's own sort
  // restriction above). Defaulting to 'date'/'asc' matches that existing
  // order exactly, so this only fixes the header's own indicator/toggle
  // starting point, not the row order itself.
  const { sorted, sortKey, sortDir, toggleSort } = useSortableRows(entries, 'date', 'asc');
  return (
    <table className="tbl">
      <thead><tr>
        <SortTh label="Date" sortKeyName="date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
        <th>Type</th><th>Description</th><th>Voucher No.</th><th className="text-right">Credit</th><th className="text-right">Debit</th><th className="text-right">Balance</th>{(canEdit || isMaster) && <th className="no-print"></th>}
      </tr></thead>
      <tbody>
        {sorted.length===0 && <tr><td colSpan="8" className="text-center text-muted py-6">{emptyLabel}</td></tr>}
        {sorted.map((e,i) => (
          <tr key={i}>
            <td className="text-xs">{fmtDate(e.date)}</td>
            {/* User-requested: this column mixed several raw type
                vocabularies (investment_type, transaction_type, a
                security instrument's own type, plus a few fixed
                source labels) -- same plain-language lookup chain
                as every dropdown now uses, so the ledger itself
                reads in the same words as the forms that fed it. */}
            <td className="text-xs">{TXN_TYPE_LABELS[e.type] || INV_TYPE_LABELS[e.type] || SECURITY_TYPE_LABELS[e.type] || LEDGER_ENTRY_TYPE_LABELS[e.type] || e.type}</td>
            {/* Real-usage-gap finding: "link it with expenses" — a
                ledger row already carries the project_id it came
                from (personal expenses paid by this partner,
                investments, security instruments, etc.); jump
                straight to that project instead of leaving the
                user to search for it. */}
            <td>{e.project_id ? <Link to={`/app/projects/${e.project_id}`} className="hover:text-[color:var(--gold)]">{e.description || '—'}</Link> : (e.description || '—')}</td>
            {/* User-requested: a voucher number here lets a real
                physical voucher/bill be matched straight against a
                running-balance entry, instead of having to go find
                the source row elsewhere first. Profit distributions
                have no voucher (a system-computed approval record,
                not sourced from a physical document) -- shown as
                "—", same as any other blank cell here. */}
            <td className="mono text-xs">{e.voucher_number || '—'}</td>
            <td className="mono text-right">{e.credit ? fmtINR(e.credit) : '—'}</td>
            <td className="mono text-right">{e.debit ? fmtINR(e.debit) : '—'}</td>
            <td className={"mono text-right " + (e.balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(e.balance)}</td>
            {/* User-requested: edit right for a company admin (and
                master admin), edit+delete for master admin, on a
                transaction added right here on this page. Only
                source==='partner_transaction' rows are genuinely
                editable/deletable through this endpoint -- every
                other source (investment/expense/security instrument/
                profit distribution) is a different entity with its
                own edit path elsewhere, not something this page
                should try to act on directly. */}
            {(canEdit || isMaster) && (
              <td className="text-right no-print">
                {e.source === 'partner_transaction' && canEdit && <button aria-label="Edit" onClick={()=>openEditTxn(e)} className="tab btn-small mr-1"><Edit3 size={12}/></button>}
                {e.source === 'partner_transaction' && isMaster && <button aria-label="Delete" onClick={()=>deleteTxn(e)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Old /app/partners/:id URLs (bookmarks, links elsewhere in the app) keep
// working — redirected to the new dashboard with the partner pre-selected.
function PartnerDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/partner-settlement?partner=${id}`} replace/>;
}

// ============= VENDORS =============
const VENDOR_TYPE_LABELS = { labour_supply: 'Labour Supply', material_supplier: 'Material Supplier', contractor: 'Contractor', other: 'Other' };
const VENDOR_TXN_LABELS = { bill: 'Bill', payment: 'Payment', advance: 'Advance', adjustment: 'Adjustment' };

const VENDOR_TYPE_TONES = { labour_supply: 'green', material_supplier: 'blue', contractor: 'violet', other: 'amber' };
const vendorInitials = (name) => {
  const parts = String(name || '?').replace(/\(.*?\)/g, ' ').split(/\s+/).filter(Boolean);
  return ((parts[0] || '?')[0] + (parts.length > 1 ? parts[1][0] : '')).toUpperCase();
};
function VendorAvatar({ name, type, size = 44 }) {
  const t = META_CHIP_TONES[VENDOR_TYPE_TONES[type]] || META_CHIP_TONES.gold;
  return <div className="vendor-avatar" style={{ width: size, height: size, fontSize: size * 0.36, color: t.color, background: t.soft, borderColor: t.border }} aria-hidden="true">{vendorInitials(name)}</div>;
}

function Vendors() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const companyF = useMultiFilter('Vendors.company');
  const statusF = useMultiFilter('Vendors.status');
  const typeF = useMultiFilter('Vendors.type');
  const [search, setSearch] = usePersistedState('Vendors.search', '');
  const [view, setView] = usePersistedState('Vendors.view', 'cards');
  const [cardSort, setCardSort] = usePersistedState('Vendors.cardSort', 'name');
  const [saving, setSaving] = useState(false);
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';

  const load = async () => {
    setItems((await api.get('/vendors')).data); setLoaded(true);
    // The payment summary is a nice-to-have: if it fails, the list still works.
    try { setStats((await api.get('/vendors-stats')).data); } catch (e) { /* cards just omit the figures */ }
  };
  useEffect(() => { load(); }, []);
  const q = search.trim().toLowerCase();
  const filteredItems = items
    .map(v => ({ ...v, ...(stats[v.id] || {}) }))
    .filter(v => companyF.matches(v.company_id))
    .filter(v => statusF.matches(v.status))
    .filter(v => typeF.matches(v.vendor_type || '__none__'))
    .filter(v => !q || [v.name, v.phone, v.email, VENDOR_TYPE_LABELS[v.vendor_type]].some(x => String(x || '').toLowerCase().includes(q)));
  const { sorted: tableItems, sortKey, sortDir, toggleSort } = useSortableRows(filteredItems, 'name', 'asc');
  const cardItems = useMemo(() => {
    const c = [...filteredItems];
    if (cardSort === 'paid') c.sort((a, b) => (b.paid_total || 0) - (a.paid_total || 0) || a.name.localeCompare(b.name));
    else if (cardSort === 'recent') c.sort((a, b) => String(b.last_payment_date || '').localeCompare(String(a.last_payment_date || '')) || a.name.localeCompare(b.name));
    else c.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    return c;
  }, [filteredItems, cardSort]);
  const visibleItems = view === 'cards' ? cardItems : tableItems;
  const totalPaid = filteredItems.reduce((t, v) => t + (v.paid_total || 0), 0);
  const totalPending = filteredItems.reduce((t, v) => t + (v.pending_total || 0), 0);
  const topVendor = filteredItems.reduce((best, v) => (v.paid_total || 0) > (best?.paid_total || 0) ? v : best, null);
  const maxPaid = Math.max(0, ...filteredItems.map(v => v.paid_total || 0));
  const activeCount = filteredItems.filter(v => v.status === 'active').length;
  const hasFilter = !!(companyF.active || statusF.active || typeF.active || q);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Only the form fields -- never the computed payment figures merged in for display.
      const { paid_total, paid_count, pending_total, pending_count, last_payment_date, project_count, ...rest } = form;
      if (rest.id) await api.patch(`/vendors/${rest.id}`, rest);
      else await api.post('/vendors', rest);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete vendor?')) return;
    try { await api.delete(`/vendors/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const statsReady = Object.keys(stats).length > 0;
  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <div>
          <div className="section-eyebrow mb-2">Suppliers & Contractors</div>
          <h1 className="serif text-4xl">Vendors</h1>
          <div className="text-sm text-muted mt-1">One record per vendor, so a supplier billed under five spellings becomes one running account.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>
          {canEdit && <button onClick={()=>{setForm({status:'active'});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="vendor-add"><Plus size={14}/>Add Vendor</button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 no-print" data-testid="vendor-kpis">
        <KpiCard label="Vendors" tone="blue" icon={Truck} value={filteredItems.length} hint={`${activeCount} active${filteredItems.length - activeCount ? ` · ${filteredItems.length - activeCount} inactive` : ''}`}/>
        <KpiCard label="Paid to vendors" tone="gold" value={statsReady ? fmtINR(totalPaid) : '—'} hint="Reviewed expenses only"/>
        <KpiCard label="Awaiting review" tone="amber" value={statsReady ? fmtINR(totalPending) : '—'} hint="Not counted until audited"/>
        <KpiCard label="Largest vendor" tone="green" icon={Award} value={topVendor && topVendor.paid_total ? fmtINR(topVendor.paid_total) : '—'} hint={topVendor && topVendor.paid_total ? topVendor.name : 'No payments yet'}/>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 no-print">
        <div className="vendor-search">
          <SearchIcon size={14}/>
          <input className="input" placeholder="Search name, phone, email, type…" value={search} onChange={e=>setSearch(e.target.value)} data-testid="vendor-search"/>
        </div>
        {needsPicker && <MultiFilterPicker filter={companyF} items={companies.map(c=>({id:c.id, name:c.name}))} allLabel="All Companies" width={170} testId="vendor-filter-company"/>}
        <MultiFilterPicker filter={typeF} items={[...Object.entries(VENDOR_TYPE_LABELS).map(([k,l])=>({id:k, name:l})), {id:'__none__', name:'Unspecified'}]} allLabel="All Types" width={170} testId="vendor-filter-type"/>
        <MultiFilterPicker filter={statusF} items={[{id:'active', name:'active'},{id:'inactive', name:'inactive'}]} allLabel="All Statuses" width={140} testId="vendor-filter-status"/>
        {view === 'cards' && <select className="input" style={{width:170}} value={cardSort} onChange={e=>setCardSort(e.target.value)} data-testid="vendor-card-sort" title="Order of the cards">
          <option value="name">Sort: Name A–Z</option>
          <option value="paid">Sort: Most paid</option>
          <option value="recent">Sort: Latest payment</option>
        </select>}
        {hasFilter && <button onClick={()=>{setSearch('');typeF.reset();statusF.reset();companyF.reset();}} className="tab btn-small" data-testid="vendor-clear">Clear</button>}
        <div className="ml-auto inline-flex gap-1" role="group" aria-label="View">
          <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='cards'?'active':'')} onClick={()=>setView('cards')} data-testid="vendor-view-cards"><LayoutGrid size={13}/>Cards</button>
          <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='table'?'active':'')} onClick={()=>setView('table')} data-testid="vendor-view-table"><ListIcon size={13}/>Table</button>
        </div>
      </div>
      <div className="text-xs text-muted mb-3 no-print">Showing <b>{visibleItems.length}</b> of <b>{items.length}</b> vendors{visibleItems.length !== items.length ? ' (filtered)' : ''}</div>

      {view === 'cards' && (
        <div className="no-print" data-testid="vendor-cards">
          {loaded && visibleItems.length === 0 && <div className="card card-dark text-center text-muted py-10">{items.length === 0 ? 'No vendors yet.' : 'No vendors match these filters.'}</div>}
          <div className="vendor-grid">
            {visibleItems.map(v => {
              const tone = META_CHIP_TONES[VENDOR_TYPE_TONES[v.vendor_type]] || META_CHIP_TONES.gold;
              const pct = maxPaid > 0 ? Math.max(2, Math.round(((v.paid_total || 0) / maxPaid) * 100)) : 0;
              return (
                <div key={v.id} className={"card card-dark vendor-card " + (v.status === 'active' ? '' : 'vendor-card-inactive')} style={{ '--vendor-accent': tone.color }} data-testid={`vendor-card-${v.id}`}>
                  <div className="flex items-start gap-3">
                    <VendorAvatar name={v.name} type={v.vendor_type}/>
                    <div className="min-w-0 flex-1">
                      <Link to={`/app/vendors/${v.id}`} className="serif text-lg leading-tight hover:text-[color:var(--gold)] vendor-name" title={v.name}>{v.name}</Link>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="meta-chip" style={{ '--chip-color': tone.color, '--chip-soft': tone.soft, '--chip-border': tone.border, '--chip-hover': tone.hover }}>{VENDOR_TYPE_LABELS[v.vendor_type] || 'Unspecified'}</span>
                        <span className={"badge "+(v.status==='active'?'status-running':'status-closed')}>{v.status}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-widest text-muted">Paid (reviewed)</div>
                    <div className="serif mono text-2xl" style={{ color: 'var(--gold)' }} data-testid={`vendor-card-paid-${v.id}`}>{statsReady ? fmtINR(v.paid_total || 0) : '—'}</div>
                    <div className="vendor-bar" aria-hidden="true"><div style={{ width: `${pct}%` }}/></div>
                    <div className="text-xs text-muted mt-2">
                      {statsReady ? <>{v.paid_count || 0} payment{(v.paid_count || 0) === 1 ? '' : 's'}{v.project_count ? ` · ${v.project_count} project${v.project_count === 1 ? '' : 's'}` : ''}{v.last_payment_date ? ` · last ${fmtDate(v.last_payment_date)}` : ''}</> : ' '}
                    </div>
                    {v.pending_total > 0 && <div className="vendor-pending mt-2" data-testid={`vendor-card-pending-${v.id}`}><Clock3 size={12}/>{fmtINR(v.pending_total)} awaiting review</div>}
                  </div>
                  {needsPicker && <div className="text-xs text-muted mt-3">{companyName(v.company_id)}</div>}
                  <div style={{ flex: 1, minHeight: 14 }} aria-hidden="true"/>
                  <div className="vendor-card-foot">
                    <div className="inline-flex items-center gap-2 min-w-0">
                      {v.phone ? <a href={`tel:${String(v.phone).replace(/\s+/g,'')}`} className="vendor-contact" title={v.phone}><Phone size={13}/><span className="vendor-contact-text">{v.phone}</span></a> : null}
                      {v.email ? <a href={`mailto:${v.email}`} className="vendor-contact" title={v.email} aria-label={`Email ${v.name}`}><Mail size={13}/>{!v.phone && <span className="vendor-contact-text">{v.email}</span>}</a> : null}
                      {!v.phone && !v.email && <span className="text-xs text-muted">No contact info</span>}
                    </div>
                    <div className="inline-flex gap-1 flex-shrink-0">
                      <Link to={`/app/vendors/${v.id}`} className="tab btn-small">View</Link>
                      {canEdit && <button aria-label="Edit" onClick={()=>{setForm(v);setOpen(true);}} className="tab btn-small"><Edit3 size={12}/></button>}
                      {canEdit && isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(v.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={"card card-dark overflow-x-auto print-area " + (view === 'cards' ? 'vendor-table-print-only' : '')} data-testid="vendor-table">
        <div className="print-header">
          <div className="print-company">Vendors</div>
          <div className="print-report-title">{visibleItems.length} of {items.length} vendors</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        <table className="tbl">
          <thead><tr>
            <SortTh label="Name" sortKeyName="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Type" sortKeyName="vendor_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Phone" sortKeyName="phone" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Email" sortKeyName="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {needsPicker && <th>Company</th>}
            <SortTh label="Paid" sortKeyName="paid_total" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
            <SortTh label="Last payment" sortKeyName="last_payment_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <th className="no-print"></th>
          </tr></thead>
          <tbody>
            {tableItems.length === 0 && <tr><td colSpan="9" className="text-center text-muted py-8">{items.length === 0 ? 'No vendors yet.' : 'No vendors match these filters.'}</td></tr>}
            {tableItems.map(v => (
              <tr key={v.id}>
                <td className="serif text-base"><Link to={`/app/vendors/${v.id}`} className="hover:text-[color:var(--gold)]">{v.name}</Link></td>
                <td className="text-xs">{VENDOR_TYPE_LABELS[v.vendor_type]||'—'}</td>
                <td>{v.phone || '—'}</td>
                <td>{v.email || '—'}</td>
                {needsPicker && <td className="text-xs">{companyName(v.company_id)}</td>}
                <td className="mono text-right">{statsReady ? fmtINR(v.paid_total || 0) : '—'}{v.pending_total > 0 && <div className="text-xs text-muted">+{fmtINR(v.pending_total)} pending</div>}</td>
                <td className="text-xs whitespace-nowrap">{v.last_payment_date ? fmtDate(v.last_payment_date) : '—'}</td>
                <td><span className={"badge "+(v.status==='active'?'status-running':'status-closed')}>{v.status}</span></td>
                <td className="text-right whitespace-nowrap no-print">
                  <Link to={`/app/vendors/${v.id}`} className="tab btn-small mr-2">View</Link>
                  {canEdit && <>
                    <button aria-label="Edit" onClick={()=>{setForm(v);setOpen(true);}} className="tab btn-small mr-2"><Edit3 size={12}/></button>
                    {isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(v.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id ? 'Edit Vendor' : 'Add Vendor'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker}/>
          <Field label="Name *"><input className="input" value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
          <Field label="Type"><select className="input" value={form.vendor_type||''} onChange={e=>setForm({...form, vendor_type:e.target.value})}>
            <option value="">— unspecified —</option>
            {Object.entries(VENDOR_TYPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
          </select></Field>
          <Field label="Phone"><input className="input" value={form.phone||''} onChange={e=>setForm({...form, phone:e.target.value})}/></Field>
          <Field label="Email"><input className="input" value={form.email||''} onChange={e=>setForm({...form, email:e.target.value})}/></Field>
          <Field label="Status"><select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}>
            <option value="active">active</option><option value="inactive">inactive</option>
          </select></Field>
          <div className="col-span-2"><Field label="Notes"><textarea className="input" rows={2} value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving || !form.name} className="btn-gold btn-small">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

function VendorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const canDelete = isMasterAdmin(user);
  const [tab, setTab] = useState('ledger');
  const [ledger, setLedger] = useState(null);
  const [expPay, setExpPay] = useState(null);
  const tabTouched = useRef(false);
  const [epFy, setEpFy] = useState('');
  const [epProject, setEpProject] = useState('');
  const [terms, setTerms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [txnOpen, setTxnOpen] = useState(false);
  const [txnForm, setTxnForm] = useState({});
  const [termOpen, setTermOpen] = useState(false);
  const [termForm, setTermForm] = useState({});
  const [txnSaving, setTxnSaving] = useState(false);
  const [termSaving, setTermSaving] = useState(false);

  const [loadError, setLoadError] = useState(null);
  // Same stale-response guard as ProjectDetail.loadAll — see its comment.
  const loadToken = useRef(0);
  const load = async () => {
    const myToken = ++loadToken.current;
    try {
      const [l, t, p, ep] = await Promise.all([
        api.get(`/vendors/${id}/ledger`),
        api.get('/vendor-contract-terms', { params: { vendor_id: id } }),
        api.get('/projects', { params: { include_extra_work: true } }),
        api.get(`/vendors/${id}/expense-payments`),
      ]);
      if (loadToken.current !== myToken) return;
      setLedger(l.data); setTerms(t.data); setProjects(p.data); setExpPay(ep.data);
      // A vendor paid only through Expenses has an empty manual ledger --
      // open on the tab that actually has their payments.
      if (!tabTouched.current && l.data.entries.length === 0 && ep.data.entries.length > 0) setTab('expenses');
    } catch (e) {
      if (loadToken.current !== myToken) return;
      setLoadError(e.response?.data?.detail || 'Could not load this vendor');
      toast.error(e.response?.data?.detail || 'Could not load this vendor');
    }
  };
  useEffect(() => { load(); }, [id]);
  // User-requested: sortable headers -- Date-only, same reasoning as
  // PartnerLedgerTable's identical restriction: Balance is a running
  // total, only meaningful in the true chronological order the backend
  // already returns (vendor_ledger orders by transaction_date before
  // computing it) -- sorting by any other column would leave a Balance
  // column that no longer makes sense against the rows shown above/below
  // it. Build error caught this in review: this was previously called
  // inside an IIFE further down (a hook inside a nested function, and
  // conditionally on tab==='ledger') -- moved here, unconditional, before
  // either early-return guard, with a safe [] fallback while `ledger` is
  // still null during the initial load.
  const { sorted: sortedLedger, sortKey, sortDir, toggleSort } = useSortableRows(ledger?.entries || [], 'transaction_date', 'asc');
  // Filters for the Expense Payments tab: Financial Year and Project (or Company
  // expenses). Totals, the per-project summary, print and PDF all follow them.
  const epFiltered = (expPay?.entries || []).filter(e =>
    (!epFy || fyFromDateStr(e.date) === epFy) &&
    (!epProject || (epProject === '__company__' ? !e.project_id : e.project_id === epProject)));
  const { sorted: sortedExpPay, sortKey: epSortKey, sortDir: epSortDir, toggleSort: epToggleSort } = useSortableRows(epFiltered, 'date', 'asc');
  if (loadError) return <div className="max-w-2xl mx-auto px-6 py-16 text-center"><div className="text-lg text-red-400 mb-2">{loadError}</div><Link to="/app/vendors" className="text-sm text-muted hover:text-[color:var(--gold)]">← Back to Vendors</Link></div>;
  if (!ledger) return <Loading/>;

  const projectName = (pid) => pid ? (projects.find(p=>p.id===pid)?.project_name || '—') : '—';
  const epAll = expPay?.entries || [];
  const epFyOptions = distinctFYs(epAll.map(e => fyFromDateStr(e.date)));
  const epProjectIds = [...new Set(epAll.filter(e => e.project_id).map(e => e.project_id))].sort((a, b) => projectName(a).localeCompare(projectName(b)));
  const epHasCompany = epAll.some(e => !e.project_id);
  const epProjectLabel = !epProject ? '' : epProject === '__company__' ? 'Company expenses' : projectName(epProject);
  const epCounted = epFiltered.filter(e => e.counted);
  const epTotal = epCounted.reduce((t, e) => t + e.amount, 0);
  const epPending = epFiltered.filter(e => !e.counted).reduce((t, e) => t + e.amount, 0);
  const epByProject = Object.values(epCounted.reduce((m, e) => {
    const k = e.project_id || '__company__';
    (m[k] = m[k] || { key: k, name: e.project_id ? projectName(e.project_id) : 'Company expenses', count: 0, total: 0 });
    m[k].count += 1; m[k].total += e.amount; return m;
  }, {})).sort((a, b) => b.total - a.total);
  const epFilterText = [epFy, epProjectLabel].filter(Boolean).join(' · ');
  const exportExpensePaymentsPDF = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(`Payments via Expenses — ${ledger.vendor.name}`, 14, 18);
    doc.setFontSize(9); doc.text(`Generated: ${fmtGeneratedAt()}`, 14, 26);
    if (epFilterText) doc.text(`Filter: ${epFilterText}`, 14, 32);
    autoTable(doc, {
      startY: epFilterText ? 38 : 34,
      head: [['Date','Voucher','Project','Category','Description','Status','Amount']],
      body: sortedExpPay.map(e => [fmtDate(e.date), e.voucher_number || '—', e.project_id ? projectName(e.project_id) : 'Company expense', e.category || '—', (e.description || '').slice(0, 30), (e.audit_status || '').replace('_', ' '), fmtINRPdf(e.amount)]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
    });
    const y = doc.lastAutoTable?.finalY || 100;
    doc.setFontSize(10);
    doc.text(`Total (reviewed): ${fmtINRPdf(epTotal)}`, 14, y + 10);
    if (epPending > 0) doc.text(`Awaiting review (not counted): ${fmtINRPdf(epPending)}`, 14, y + 16);
    doc.save(`vendor-expense-payments-${ledger.vendor.name}-${Date.now()}.pdf`);
  };

  const saveTxn = async () => {
    if (txnSaving) return;
    setTxnSaving(true);
    try {
      const payload = { ...txnForm, vendor_id: id, amount: Number(txnForm.amount||0), project_id: txnForm.project_id||null };
      if (txnForm.id) await api.patch(`/vendor-transactions/${txnForm.id}`, payload);
      else await api.post('/vendor-transactions', payload);
      setTxnOpen(false); setTxnForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setTxnSaving(false); }
  };
  const delTxn = async (tid) => {
    if (!window.confirm('Delete entry?')) return;
    try { await api.delete(`/vendor-transactions/${tid}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveTerm = async () => {
    if (termSaving) return;
    setTermSaving(true);
    try {
      const payload = { ...termForm, vendor_id: id, amount: termForm.amount!==undefined && termForm.amount!=='' ? Number(termForm.amount) : null };
      if (termForm.id) await api.patch(`/vendor-contract-terms/${termForm.id}`, payload);
      else await api.post('/vendor-contract-terms', payload);
      setTermOpen(false); setTermForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setTermSaving(false); }
  };
  const delTerm = async (tid) => {
    if (!window.confirm('Delete contract term?')) return;
    try { await api.delete(`/vendor-contract-terms/${tid}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const exportStatementPDF = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(`Vendor Statement — ${ledger.vendor.name}`, 14, 18);
    doc.setFontSize(9); doc.text(`Generated: ${fmtGeneratedAt()}`, 14, 26);
    autoTable(doc, {
      startY: 34,
      head: [['Date','Type','Reference','Description','Amount','Balance']],
      body: ledger.entries.map(e => [fmtDate(e.transaction_date), VENDOR_TXN_LABELS[e.transaction_type]||e.transaction_type, e.reference||'—', (e.description||'').slice(0,40), fmtINRPdf(e.amount), fmtINRPdf(e.running_balance)]),
      styles: {fontSize:8}, headStyles: {fillColor:[201,168,76], textColor:[20,20,20]}
    });
    const finalY = doc.lastAutoTable?.finalY || 100;
    doc.setFontSize(10);
    doc.text(`Total Billed: ${fmtINRPdf(ledger.totals.total_billed)}`, 14, finalY+10);
    doc.text(`Total Paid: ${fmtINRPdf(ledger.totals.total_paid)}`, 14, finalY+16);
    doc.text(`Outstanding Balance: ${fmtINRPdf(ledger.totals.outstanding_balance)}`, 14, finalY+22);
    doc.save(`vendor-statement-${ledger.vendor.name}-${Date.now()}.pdf`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <Link to="/app/vendors" className="text-xs text-muted hover:text-[color:var(--gold)]">← Back to Vendors</Link>
      <div className="card card-dark vendor-hero mt-3 mb-6" data-testid="vendor-hero">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <VendorAvatar name={ledger.vendor.name} type={ledger.vendor.vendor_type} size={64}/>
            <div className="min-w-0">
              <div className="section-eyebrow mb-1">Vendor</div>
              <h1 className="serif text-3xl md:text-4xl" style={{ wordBreak: 'break-word' }}>{ledger.vendor.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {(() => { const t = META_CHIP_TONES[VENDOR_TYPE_TONES[ledger.vendor.vendor_type]] || META_CHIP_TONES.gold; return <span className="meta-chip" style={{ '--chip-color': t.color, '--chip-soft': t.soft, '--chip-border': t.border, '--chip-hover': t.hover }}>{VENDOR_TYPE_LABELS[ledger.vendor.vendor_type] || 'Unspecified type'}</span>; })()}
                <span className={"badge "+(ledger.vendor.status==='active'?'status-running':'status-closed')}>{ledger.vendor.status || 'active'}</span>
                {ledger.vendor.phone && <a href={`tel:${String(ledger.vendor.phone).replace(/\s+/g,'')}`} className="vendor-contact"><Phone size={13}/>{ledger.vendor.phone}</a>}
                {ledger.vendor.email && <a href={`mailto:${ledger.vendor.email}`} className="vendor-contact"><Mail size={13}/>{ledger.vendor.email}</a>}
                {!ledger.vendor.phone && !ledger.vendor.email && <span className="text-xs text-muted">No contact info</span>}
              </div>
              {ledger.vendor.notes && <div className="text-xs text-muted mt-2" style={{ maxWidth: 560 }}>{ledger.vendor.notes}</div>}
            </div>
          </div>
          <button onClick={exportStatementPDF} className="btn-outline btn-small inline-flex items-center gap-1 no-print"><FileText size={14}/>Statement PDF</button>
        </div>
      </div>
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <KpiCard tone="gold" icon={Receipt} label={epFilterText ? `Paid via Expenses (${epFilterText})` : 'Paid via Expenses'} value={fmtINR(epTotal)} hint={`${epCounted.length} reviewed expense${epCounted.length === 1 ? '' : 's'}${epPending ? ` · ${fmtINR(epPending)} pending review` : ''}`}/>
        <KpiCard tone="blue" icon={FileText} label="Total Billed" value={fmtINR(ledger.totals.total_billed)}/>
        <KpiCard tone="green" icon={CheckCircle} label="Total Paid" value={fmtINR(ledger.totals.total_paid)}/>
        <KpiCard tone={ledger.totals.outstanding_balance>0?'amber':'gold'} icon={Scale} label="Outstanding Balance" value={fmtINR(ledger.totals.outstanding_balance)} hint={ledger.totals.outstanding_balance>=0?'Payable to Vendor':'Overpaid / Vendor owes us'}/>
      </div>
      <div className="flex gap-2 mb-6">
        <button className={"tab "+(tab==='expenses'?'active':'')} onClick={()=>{tabTouched.current=true; setTab('expenses');}} data-testid="vendor-tab-expenses">Expense Payments ({expPay?.entries.length || 0})</button>
        <button className={"tab "+(tab==='ledger'?'active':'')} onClick={()=>{tabTouched.current=true; setTab('ledger');}}>Ledger</button>
        <button className={"tab "+(tab==='terms'?'active':'')} onClick={()=>{tabTouched.current=true; setTab('terms');}}>Contract Terms</button>
      </div>

      {tab==='expenses' && (
        <div className="card card-dark overflow-x-auto print-area" data-testid="vendor-expense-payments">
          <div className="print-header">
            <div className="print-company">{ledger.vendor.name}</div>
            <div className="print-report-title">Payments recorded as Expenses{epFilterText ? ` — ${epFilterText}` : ''}</div>
            <div className="print-meta">Total (reviewed): {fmtINR(epTotal)}{epPending ? ` · Awaiting review (not counted): ${fmtINR(epPending)}` : ''} · Generated: {fmtGeneratedAt()}</div>
          </div>
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
            <div className="serif text-xl text-[color:var(--gold)] no-print">Payments recorded as Expenses</div>
            <div className="text-sm">Total <span className="mono text-[color:var(--gold)]">{fmtINR(epTotal)}</span>{epPending ? <span className="text-muted"> · {fmtINR(epPending)} awaiting review (not counted)</span> : null}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3 no-print">
            <FySelect value={epFy} onChange={setEpFy} years={epFyOptions} style={{width:150}} testId="vendor-ep-fy"/>
            <select className="input" style={{width:240}} value={epProject} onChange={e=>setEpProject(e.target.value)} data-testid="vendor-ep-project">
              <option value="">All projects</option>
              {epHasCompany && <option value="__company__">Company expenses</option>}
              {epProjectIds.map(pid => <option key={pid} value={pid}>{projectName(pid)}</option>)}
            </select>
            {(epFy || epProject) && <button onClick={()=>{setEpFy(''); setEpProject('');}} className="tab btn-small">Clear filters</button>}
            <div className="flex gap-2 ml-auto">
              <button onClick={()=>window.print()} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="vendor-ep-print"><Printer size={14}/>Print</button>
              <button onClick={exportExpensePaymentsPDF} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>PDF</button>
            </div>
          </div>
          <div className="text-xs text-muted mb-4 no-print">Every Project and Company Expense tagged to this vendor. Kept separate from the manual Ledger so a payment entered in both places is never counted twice.{expPay && expPay.name_variants.length > 1 ? ` Matched under ${expPay.name_variants.length} spellings of the name.` : ''} Showing {epFiltered.length} of {epAll.length}.</div>
          {epByProject.length > 1 && (
            <table className="tbl mb-4" data-testid="vendor-ep-by-project">
              <thead><tr><th>Project</th><th className="text-right">Payments</th><th className="text-right">Total (reviewed)</th></tr></thead>
              <tbody>{epByProject.map(g => <tr key={g.key}><td className="text-xs">{g.name}<div className="vendor-bar no-print" style={{ maxWidth: 260 }} aria-hidden="true"><div style={{ width: `${Math.max(2, Math.round((g.total / (epByProject[0].total || 1)) * 100))}%` }}/></div></td><td className="mono text-right text-xs">{g.count}</td><td className="mono text-right">{fmtINR(g.total)}</td></tr>)}</tbody>
            </table>
          )}
          <table className="tbl">
            <thead><tr>
              <SortTh label="Date" sortKeyName="date" sortKey={epSortKey} sortDir={epSortDir} onSort={epToggleSort}/>
              <SortTh label="Voucher" sortKeyName="voucher_number" sortKey={epSortKey} sortDir={epSortDir} onSort={epToggleSort}/>
              <th>Project</th><th>Category</th><th>Description</th><th>Status</th>
              <SortTh label="Amount" sortKeyName="amount" sortKey={epSortKey} sortDir={epSortDir} onSort={epToggleSort} right/>
            </tr></thead>
            <tbody>
              {sortedExpPay.length===0 && <tr><td colSpan={7} className="text-center text-muted py-6">{epAll.length ? 'No payments match these filters.' : 'No expenses are tagged to this vendor yet.'}</td></tr>}
              {sortedExpPay.map(e => (
                <tr key={e.expense_id} className={e.counted ? '' : 'opacity-70'}>
                  <td className="text-xs whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="mono text-xs">{e.voucher_number || '—'}</td>
                  <td className="text-xs">{e.project_id ? <Link to={`/app/projects/${e.project_id}`} className="hover:text-[color:var(--gold)]">{projectName(e.project_id)}</Link> : '(Company expense)'}</td>
                  <td className="text-xs">{e.category || '—'}</td>
                  <td className="text-xs">{e.description || '—'}</td>
                  <td><AuditStatusChip status={e.audit_status}/></td>
                  <td className="mono text-right">{fmtINR(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab==='ledger' && (
        <div className="card card-dark overflow-x-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="serif text-xl text-[color:var(--gold)]">Running Ledger</div>
            {canEdit && <button onClick={()=>{setTxnForm({transaction_date:new Date().toISOString().slice(0,10), transaction_type:'bill'});setTxnOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Entry</button>}
          </div>
          <table className="tbl">
            <thead><tr><SortTh label="Date" sortKeyName="transaction_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/><th>Type</th><th>Project</th><th>Reference</th><th>Description</th><th className="text-right">Amount</th><th className="text-right">Balance</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {sortedLedger.length===0 && <tr><td colSpan={8} className="text-center text-muted py-6">No entries yet.</td></tr>}
              {sortedLedger.map(e => (
                <tr key={e.id}>
                  <td className="text-xs">{fmtDate(e.transaction_date)}</td>
                  <td className="text-xs">{VENDOR_TXN_LABELS[e.transaction_type]||e.transaction_type}</td>
                  <td className="text-xs">{projectName(e.project_id)}</td>
                  <td className="mono text-xs">{e.reference || '—'}</td>
                  <td>{e.description || '—'}</td>
                  <td className="mono text-right">{fmtINR(e.amount)}</td>
                  <td className={"mono text-right " + (e.running_balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(e.running_balance)}</td>
                  {canEdit && <td className="text-right whitespace-nowrap">
                    <button aria-label="Edit" onClick={()=>{setTxnForm(e);setTxnOpen(true);}} className="tab btn-small mr-1"><Edit3 size={12}/></button>
                    {canDelete && <button aria-label="Delete" onClick={()=>delTxn(e.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab==='terms' && (
        <div className="card card-dark overflow-x-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="serif text-xl text-[color:var(--gold)]">Contract Terms</div>
            {canEdit && <button onClick={()=>{setTermForm({effective_from:new Date().toISOString().slice(0,10)});setTermOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1"><Plus size={14}/>Add Term</button>}
          </div>
          <table className="tbl">
            <thead><tr><th>Effective</th><th>Rate</th><th>Amount</th><th>Frequency</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {terms.length===0 && <tr><td colSpan={5} className="text-center text-muted py-6">No contract terms recorded.</td></tr>}
              {terms.map(t => (
                <tr key={t.id}>
                  <td className="text-xs">{fmtDate(t.effective_from)}{t.effective_to ? ` → ${fmtDate(t.effective_to)}` : ''}</td>
                  <td>{t.rate_description}</td>
                  <td className="mono">{t.amount!=null ? fmtINR(t.amount) : '—'}</td>
                  <td className="text-xs">{t.frequency || '—'}</td>
                  {canEdit && <td className="text-right whitespace-nowrap">
                    <button aria-label="Edit" onClick={()=>{setTermForm(t);setTermOpen(true);}} className="tab btn-small mr-1"><Edit3 size={12}/></button>
                    {canDelete && <button aria-label="Delete" onClick={()=>delTerm(t.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={txnOpen} onClose={()=>setTxnOpen(false)} title={txnForm.id?'Edit Entry':'Add Entry'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Type *"><select className="input" value={txnForm.transaction_type||'bill'} onChange={e=>setTxnForm({...txnForm, transaction_type:e.target.value})}>
            {Object.entries(VENDOR_TXN_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
          </select></Field>
          <Field label="Date *"><input type="date" className="input" value={txnForm.transaction_date||''} onChange={e=>setTxnForm({...txnForm, transaction_date:e.target.value})}/></Field>
          <Field label="Amount *"><input className="input mono" type="number" min={txnForm.transaction_type==='adjustment'?undefined:0} value={txnForm.amount||''} onChange={e=>setTxnForm({...txnForm, amount:e.target.value})}/></Field>
          <Field label="Project (optional)"><select className="input" value={txnForm.project_id||''} onChange={e=>setTxnForm({...txnForm, project_id:e.target.value})}>
            <option value="">— none —</option>
            {forCompany(projects, ledger.vendor.company_id).filter(p=>!isProjectLocked(p) || p.id===txnForm.project_id).map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select></Field>
          <div className="col-span-2"><Field label="Description"><input className="input" value={txnForm.description||''} onChange={e=>setTxnForm({...txnForm, description:e.target.value})}/></Field></div>
          <Field label="Reference"><input className="input" value={txnForm.reference||''} onChange={e=>setTxnForm({...txnForm, reference:e.target.value})}/></Field>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setTxnOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={saveTxn} disabled={!txnForm.amount||!txnForm.transaction_date||txnSaving} className="btn-gold btn-small">{txnSaving ? 'Saving…' : 'Save'}</button></div>
      </Modal>

      <Modal open={termOpen} onClose={()=>setTermOpen(false)} title={termForm.id?'Edit Contract Term':'Add Contract Term'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Effective From *"><input type="date" className="input" value={termForm.effective_from||''} onChange={e=>setTermForm({...termForm, effective_from:e.target.value})}/></Field>
          <Field label="Effective To (optional)"><input type="date" className="input" value={termForm.effective_to||''} onChange={e=>setTermForm({...termForm, effective_to:e.target.value})}/></Field>
          <div className="col-span-2"><Field label="Rate Description *"><input className="input" placeholder="e.g. 85000/Month, or 95000 fixed on satisfactory bill" value={termForm.rate_description||''} onChange={e=>setTermForm({...termForm, rate_description:e.target.value})}/></Field></div>
          <Field label="Amount (optional)"><input className="input mono" type="number" min="0" value={termForm.amount??''} onChange={e=>setTermForm({...termForm, amount:e.target.value})}/></Field>
          <Field label="Frequency"><select className="input" value={termForm.frequency||''} onChange={e=>setTermForm({...termForm, frequency:e.target.value})}>
            <option value="">— unspecified —</option>
            <option value="monthly">Monthly</option><option value="per_job">Per Job</option><option value="other">Other</option>
          </select></Field>
          <div className="col-span-2"><Field label="Notes"><input className="input" value={termForm.notes||''} onChange={e=>setTermForm({...termForm, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setTermOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={saveTerm} disabled={!termForm.rate_description||!termForm.effective_from||termSaving} className="btn-gold btn-small">{termSaving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}


// ============= STAFF (Staff & Vendor) =============
// Staff salary/travel-allowance payments continue to be recorded as
// ordinary Expenses (Project or Company) -- this page reads and
// auto-totals those rows per staff member via GET /staff/:id/payments,
// it is not a new payment-entry mechanism.
const STAFF_PAYMENT_TYPE_LABELS = { salary: 'Salary', travel_allowance: 'Travel Allowance', other: 'Other' };
// A Travel Allowance payment can only go to staff ticked "Travel allowance allowed" on their record; the person already
// chosen on an existing payment stays listed so editing it never blanks the field.
// (a list that carries no travel_allowed value at all is shown in full rather than emptied; the server still refuses a wrong choice)
const staffPickList = (list, type, selectedId) => type === 'travel_allowance' ? (list || []).filter(x => x.travel_allowed === undefined || x.travel_allowed || x.id === selectedId) : (list || []);
const staffAfterTypeChange = (list, type, staffId) => (type === 'travel_allowance' && staffId && !(list || []).find(x => x.id === staffId && (x.travel_allowed || x.travel_allowed === undefined))) ? null : staffId;
const staffNoneLabel = (type) => type === 'travel_allowance' ? '— choose (travel-allowed staff only) —' : '— none —';

function StaffAndVendor() {
  const [tab, setTab] = useState('staff');
  return (
    <div className="max-w-7xl mx-auto px-6 pt-8 fade-up">
      <div className="flex gap-2 mb-2">
        <button className={"tab "+(tab==='staff'?'active':'')} onClick={()=>setTab('staff')} data-testid="tab-staff">Staff</button>
        <button className={"tab "+(tab==='vendors'?'active':'')} onClick={()=>setTab('vendors')} data-testid="tab-vendors">Vendors</button>
        <button className={"tab "+(tab==='attendance'?'active':'')} onClick={()=>setTab('attendance')} data-testid="tab-attendance">Attendance</button>
        <button className={"tab "+(tab==='holidays'?'active':'')} onClick={()=>setTab('holidays')} data-testid="tab-holidays">Holidays</button>
        <button className={"tab "+(tab==='payroll'?'active':'')} onClick={()=>setTab('payroll')} data-testid="tab-payroll">Payroll</button>
        <button className={"tab "+(tab==='travel'?'active':'')} onClick={()=>setTab('travel')} data-testid="tab-travel">Travel</button>
      </div>
      {tab==='staff' && <StaffPanel/>}
      {tab==='vendors' && <Vendors/>}
      {tab==='attendance' && <AttendancePanel/>}
      {tab==='holidays' && <HolidaysPanel/>}
      {tab==='payroll' && <PayrollPanel/>}
      {tab==='travel' && <TravelAccountPanel/>}
    </div>
  );
}

const STAFF_SALARY_TONES = { fixed_monthly: 'blue', per_day: 'green' };
const STAFF_SALARY_LABELS = { fixed_monthly: 'Monthly (regular)', per_day: 'Daily wage' };
function StaffPanel() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const companyF = useMultiFilter('Staff.company');
  const statusF = useMultiFilter('Staff.status');
  const salaryF = useMultiFilter('Staff.salary');
  const [search, setSearch] = usePersistedState('Staff.search', '');
  const [view, setView] = usePersistedState('Staff.view', 'cards');
  const [cardSort, setCardSort] = usePersistedState('Staff.cardSort', 'name');
  const [saving, setSaving] = useState(false);
  const [payRepOpen, setPayRepOpen] = useState(false);
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';

  const load = async () => {
    setItems((await api.get('/staff')).data); setLoaded(true);
    // The payment summary is a nice-to-have: if it fails, the list still works.
    try { setStats((await api.get('/staff-stats')).data); } catch (e) { /* cards just omit the figures */ }
  };
  useEffect(() => { load(); }, []);
  const q = search.trim().toLowerCase();
  const filteredItems = items
    .map(x => ({ ...x, ...(stats[x.id] || {}) }))
    .filter(x => companyF.matches(x.company_id))
    .filter(x => statusF.matches(x.status))
    .filter(x => salaryF.matches(x.salary_amount > 0 ? x.salary_type : '__none__'))
    .filter(x => !q || [x.name, x.designation, x.phone, x.email].some(v => String(v || '').toLowerCase().includes(q)));
  const { sorted: tableItems, sortKey, sortDir, toggleSort } = useSortableRows(filteredItems, 'name', 'asc');
  const cardItems = useMemo(() => {
    const c = [...filteredItems];
    if (cardSort === 'paid') c.sort((a, b) => (b.paid_total || 0) - (a.paid_total || 0) || a.name.localeCompare(b.name));
    else if (cardSort === 'recent') c.sort((a, b) => String(b.last_payment_date || '').localeCompare(String(a.last_payment_date || '')) || a.name.localeCompare(b.name));
    else c.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    return c;
  }, [filteredItems, cardSort]);
  const visibleItems = view === 'cards' ? cardItems : tableItems;
  const activeStaff = filteredItems.filter(x => x.status === 'active');
  const fixedBill = activeStaff.filter(x => x.salary_type !== 'per_day').reduce((t, x) => t + (Number(x.salary_amount) || 0), 0);
  const perDayCount = activeStaff.filter(x => x.salary_type === 'per_day').length;
  const totalPaid = filteredItems.reduce((t, x) => t + (x.paid_total || 0), 0);
  const totalPending = filteredItems.reduce((t, x) => t + (x.pending_total || 0), 0);
  const maxPaid = Math.max(0, ...filteredItems.map(x => x.paid_total || 0));
  const statsReady = Object.keys(stats).length > 0;
  const hasFilter = !!(companyF.active || statusF.active || salaryF.active || q);
  const salaryText = (x) => x.salary_amount > 0 ? `${fmtINR(x.salary_amount)}${x.salary_type === 'per_day' ? ' / day' : ' / month'}` : '—';

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Only the form fields -- never the computed payment figures merged in for display.
      const { paid_total, paid_count, pending_total, pending_count, last_payment_date, ...rest } = form;
      if (rest.id) await api.patch(`/staff/${rest.id}`, rest);
      else await api.post('/staff', rest);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete staff member?')) return;
    try { await api.delete(`/staff/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <div>
          <div className="section-eyebrow mb-2">Employees</div>
          <h1 className="serif text-4xl">Staff</h1>
          <div className="text-sm text-muted mt-1" style={{ maxWidth: 760 }}>Salary and Travel Allowance payments are recorded as normal Expenses, linked to a staff member here — open a staff record to see their auto-totaled payments.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>
          {canEdit && <button onClick={()=>setPayRepOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="staff-payment-details-btn"><Download size={14}/>Payment details</button>}
          {canEdit && <button onClick={()=>{setForm({status:'active'});setOpen(true);}} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-staff-btn"><Plus size={14}/>Add Staff</button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 no-print" data-testid="staff-kpis">
        <KpiCard label="Staff" tone="blue" icon={Users} value={filteredItems.length} hint={`${activeStaff.length} active${filteredItems.length - activeStaff.length ? ` · ${filteredItems.length - activeStaff.length} inactive` : ''}`}/>
        {canEdit
          ? <KpiCard label="Monthly salary bill" tone="gold" value={fmtINR(fixedBill)} hint={`Active fixed-monthly staff${perDayCount ? ` · ${perDayCount} per-day not included` : ''}`}/>
          : <KpiCard label="Per-day staff" tone="gold" icon={Clock3} value={perDayCount} hint="Active, paid by the day"/>}
        <KpiCard label="Paid to staff" tone="green" icon={CheckCircle} value={statsReady ? fmtINR(totalPaid) : '—'} hint="Reviewed expenses only"/>
        <KpiCard label="Awaiting review" tone="amber" value={statsReady ? fmtINR(totalPending) : '—'} hint="Not counted until audited"/>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 no-print">
        <div className="vendor-search">
          <SearchIcon size={14}/>
          <input className="input" placeholder="Search name, designation, phone…" value={search} onChange={e=>setSearch(e.target.value)} data-testid="staff-search"/>
        </div>
        {needsPicker && <MultiFilterPicker filter={companyF} items={companies.map(c=>({id:c.id, name:c.name}))} allLabel="All Companies" width={170} testId="staff-filter-company"/>}
        <MultiFilterPicker filter={statusF} items={[{id:'active', name:'active'},{id:'inactive', name:'inactive'}]} allLabel="All Statuses" width={140} testId="staff-filter-status"/>
        {canEdit && <MultiFilterPicker filter={salaryF} items={[{id:'fixed_monthly', name:'Fixed monthly'},{id:'per_day', name:'Per day'},{id:'__none__', name:'No salary set'}]} allLabel="All Pay Types" width={170} testId="staff-filter-salary"/>}
        {view === 'cards' && <select className="input" style={{width:170}} value={cardSort} onChange={e=>setCardSort(e.target.value)} data-testid="staff-card-sort" title="Order of the cards">
          <option value="name">Sort: Name A–Z</option>
          <option value="paid">Sort: Most paid</option>
          <option value="recent">Sort: Latest payment</option>
        </select>}
        {hasFilter && <button onClick={()=>{setSearch('');salaryF.reset();statusF.reset();companyF.reset();}} className="tab btn-small" data-testid="staff-clear">Clear</button>}
        <div className="ml-auto inline-flex gap-1" role="group" aria-label="View">
          <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='cards'?'active':'')} onClick={()=>setView('cards')} data-testid="staff-view-cards"><LayoutGrid size={13}/>Cards</button>
          <button className={"tab btn-small inline-flex items-center gap-1 "+(view==='table'?'active':'')} onClick={()=>setView('table')} data-testid="staff-view-table"><ListIcon size={13}/>Table</button>
        </div>
      </div>
      <div className="text-xs text-muted mb-3 no-print">Showing <b>{visibleItems.length}</b> of <b>{items.length}</b> staff{visibleItems.length !== items.length ? ' (filtered)' : ''}</div>

      {view === 'cards' && (
        <div className="no-print" data-testid="staff-cards">
          {loaded && visibleItems.length === 0 && <div className="card card-dark text-center text-muted py-10">{items.length === 0 ? 'No staff yet.' : 'No staff match these filters.'}</div>}
          <div className="vendor-grid">
            {visibleItems.map(x => {
              const tone = META_CHIP_TONES[STAFF_SALARY_TONES[x.salary_type] && x.salary_amount > 0 ? STAFF_SALARY_TONES[x.salary_type] : 'gold'];
              const pct = maxPaid > 0 ? Math.max(2, Math.round(((x.paid_total || 0) / maxPaid) * 100)) : 0;
              return (
                <div key={x.id} className={"card card-dark vendor-card " + (x.status === 'active' ? '' : 'vendor-card-inactive')} style={{ '--vendor-accent': tone.color }} data-testid={`staff-card-${x.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="vendor-avatar" style={{ width: 44, height: 44, fontSize: 16, color: tone.color, background: tone.soft, borderColor: tone.border }} aria-hidden="true">{vendorInitials(x.name)}</div>
                    <div className="min-w-0 flex-1">
                      <Link to={`/app/staff/${x.id}`} className="serif text-lg leading-tight hover:text-[color:var(--gold)] vendor-name" title={x.name}>{x.name}</Link>
                      <div className="text-xs text-muted mt-0.5">{x.designation || 'No designation'}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {canEdit && x.salary_amount > 0 && <span className="meta-chip" style={{ '--chip-color': tone.color, '--chip-soft': tone.soft, '--chip-border': tone.border, '--chip-hover': tone.hover }} data-testid={`staff-card-salary-${x.id}`}>{STAFF_SALARY_LABELS[x.salary_type] || 'Salary'} · {salaryText(x).split(' ')[0]}</span>}
                        <span className={"badge "+(x.status==='active'?'status-running':'status-closed')}>{x.status}</span>
                        {x.attendance_tracked === false && <span className="badge" style={{ borderColor: 'var(--border-strong)' }} title="Not marked in the Attendance register — paid by payroll only" data-testid={`staff-card-payroll-only-${x.id}`}>Payroll only</span>}
                        {canEdit && x.travel_allowed && <span className="badge" style={{ borderColor: 'var(--border-strong)' }} title="Can be paid Travel Allowance; has a travel account under Staff → Travel" data-testid={`staff-card-travel-${x.id}`}>Travel allowed</span>}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-widest text-muted">Paid (reviewed)</div>
                    <div className="serif mono text-2xl" style={{ color: 'var(--gold)' }} data-testid={`staff-card-paid-${x.id}`}>{statsReady ? fmtINR(x.paid_total || 0) : '—'}</div>
                    <div className="vendor-bar" aria-hidden="true"><div style={{ width: `${pct}%` }}/></div>
                    <div className="text-xs text-muted mt-2">
                      {statsReady ? <>{x.paid_count || 0} payment{(x.paid_count || 0) === 1 ? '' : 's'}{x.last_payment_date ? ` · last ${fmtDate(x.last_payment_date)}` : ''}</> : ' '}
                    </div>
                    {x.pending_total > 0 && <div className="vendor-pending mt-2" data-testid={`staff-card-pending-${x.id}`}><Clock3 size={12}/>{fmtINR(x.pending_total)} awaiting review</div>}
                  </div>
                  {needsPicker && <div className="text-xs text-muted mt-3">{companyName(x.company_id)}</div>}
                  <div style={{ flex: 1, minHeight: 14 }} aria-hidden="true"/>
                  <div className="vendor-card-foot">
                    <div className="inline-flex items-center gap-2 min-w-0">
                      {x.phone ? <a href={`tel:${String(x.phone).replace(/\s+/g,'')}`} className="vendor-contact" title={x.phone}><Phone size={13}/><span className="vendor-contact-text">{x.phone}</span></a> : null}
                      {x.email ? <a href={`mailto:${x.email}`} className="vendor-contact" title={x.email} aria-label={`Email ${x.name}`}><Mail size={13}/>{!x.phone && <span className="vendor-contact-text">{x.email}</span>}</a> : null}
                      {!x.phone && !x.email && <span className="text-xs text-muted">No contact info</span>}
                    </div>
                    <div className="inline-flex gap-1 flex-shrink-0">
                      <Link to={`/app/staff/${x.id}`} className="tab btn-small">View</Link>
                      {canEdit && <button aria-label="Edit" onClick={()=>{setForm(x);setOpen(true);}} className="tab btn-small"><Edit3 size={12}/></button>}
                      {canEdit && isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(x.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={"card card-dark overflow-x-auto print-area " + (view === 'cards' ? 'vendor-table-print-only' : '')} data-testid="staff-table">
        <div className="print-header">
          <div className="print-company">Staff</div>
          <div className="print-report-title">{visibleItems.length} of {items.length} staff</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        <table className="tbl">
          <thead><tr>
            <SortTh label="Name" sortKeyName="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Designation" sortKeyName="designation" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Phone" sortKeyName="phone" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Email" sortKeyName="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {needsPicker && <th>Company</th>}
            {canEdit && <SortTh label="Salary" sortKeyName="salary_amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>}
            <SortTh label="Paid" sortKeyName="paid_total" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
            <SortTh label="Last payment" sortKeyName="last_payment_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <th className="no-print"></th>
          </tr></thead>
          <tbody>
            {tableItems.length === 0 && <tr><td colSpan="10" className="text-center text-muted py-8">{items.length === 0 ? 'No staff yet.' : 'No staff match these filters.'}</td></tr>}
            {tableItems.map(x => (
              <tr key={x.id}>
                <td className="serif text-base"><Link to={`/app/staff/${x.id}`} className="hover:text-[color:var(--gold)]">{x.name}</Link></td>
                <td className="text-xs">{x.designation || '—'}</td>
                <td>{x.phone || '—'}</td>
                <td>{x.email || '—'}</td>
                {needsPicker && <td className="text-xs">{companyName(x.company_id)}</td>}
                {canEdit && <td className="mono text-right text-xs">{salaryText(x)}</td>}
                <td className="mono text-right">{statsReady ? fmtINR(x.paid_total || 0) : '—'}{x.pending_total > 0 && <div className="text-xs text-muted">+{fmtINR(x.pending_total)} pending</div>}</td>
                <td className="text-xs whitespace-nowrap">{x.last_payment_date ? fmtDate(x.last_payment_date) : '—'}</td>
                <td><span className={"badge "+(x.status==='active'?'status-running':'status-closed')}>{x.status}</span>{x.attendance_tracked === false && <span className="badge ml-1" style={{ borderColor: 'var(--border-strong)' }} title="Not marked in the Attendance register — paid by payroll only">Payroll only</span>}</td>
                <td className="text-right whitespace-nowrap no-print">
                  <Link to={`/app/staff/${x.id}`} className="tab btn-small mr-2">View</Link>
                  {canEdit && <>
                    <button aria-label="Edit" onClick={()=>{setForm(x);setOpen(true);}} className="tab btn-small mr-2"><Edit3 size={12}/></button>
                    {isMasterAdmin(user) && <button aria-label="Delete" onClick={()=>del(x.id)} className="tab btn-small text-red-300"><Trash2 size={12}/></button>}
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={payRepOpen} onClose={()=>setPayRepOpen(false)} title="Staff payment details — download" maxW={1180}>
        {payRepOpen && <StaffPaymentReport embedded/>}
      </Modal>
      <Modal open={open} onClose={()=>setOpen(false)} title={form.id ? 'Edit Staff' : 'Add Staff'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker}/>
          <Field label="Name *"><input className="input" value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
          <Field label="Designation"><input className="input" value={form.designation||''} onChange={e=>setForm({...form, designation:e.target.value})}/></Field>
          <Field label="Phone"><input className="input" value={form.phone||''} onChange={e=>setForm({...form, phone:e.target.value})}/></Field>
          <Field label="Email"><input className="input" value={form.email||''} onChange={e=>setForm({...form, email:e.target.value})}/></Field>
          <Field label="Status"><select className="input" value={form.status||'active'} onChange={e=>setForm({...form, status:e.target.value})}>
            <option value="active">active</option><option value="inactive">inactive</option>
          </select></Field>
          {/* Attendance & Payroll: per staff member, either a fixed monthly
              salary (attendance doesn't change it) or a per-day rate (pay =
              rate x days present, half-day = 0.5) -- a construction company
              typically has both office staff and daily-wage site labour on
              the same Staff list, so this is set per person, not company-wide. */}
          <Field label="Salary Type"><select className="input" value={form.salary_type||'fixed_monthly'} onChange={e=>setForm({...form, salary_type:e.target.value, ...(e.target.value==='per_day' ? { attendance_tracked: true } : {})})}>
            <option value="fixed_monthly">Monthly salary (regular employee)</option>
            <option value="per_day">Daily wage (paid per day worked)</option>
          </select></Field>
          <Field label={form.salary_type==='per_day' ? 'Daily Wage (per day)' : 'Monthly Salary'}><input className="input mono" type="number" min="0" value={form.salary_amount??''} onChange={e=>setForm({...form, salary_amount:e.target.value})}/></Field>
          {/* Some staff are never marked in the Attendance register -- they are paid by payroll only (their pay is
              just the fixed monthly salary). Off = hidden from attendance; a Per Day Rate person is always on
              attendance because their pay is worked out from it. */}
          <div className="col-span-2">
            <label className="flex items-start gap-3 cursor-pointer" style={form.salary_type==='per_day' ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}>
              <input type="checkbox" className="mt-1" checked={form.attendance_tracked !== false} disabled={form.salary_type==='per_day'} onChange={e=>setForm({...form, attendance_tracked:e.target.checked})} data-testid="staff-attendance-tracked"/>
              <span>
                <span className="text-sm">Mark attendance for this person</span>
                <span className="block text-xs text-muted mt-0.5">{form.salary_type==='per_day'
                  ? 'Daily-wage staff are always on attendance — their pay is days worked x rate. Holidays are unpaid unless you switch a holiday to paid.'
                  : 'Ticked: full salary every month, minus a cut only for days marked Absent (Half-day = half; Leave, holidays and weekly off are always paid). Untick for staff paid by payroll only: they are never marked in the register and always get the full salary.'}</span>
              </span>
            </label>
          </div>
          <div className="col-span-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="mt-1" checked={!!form.travel_allowed} onChange={e=>setForm({...form, travel_allowed:e.target.checked})} data-testid="staff-travel-allowed"/>
              <span>
                <span className="text-sm">Travel allowance allowed</span>
                <span className="block text-xs text-muted mt-0.5">Only staff ticked here can be chosen when a Travel Allowance payment is recorded, and only they get a travel account under Staff → Travel. Leave unticked for everyone else.</span>
              </span>
            </label>
          </div>
          <div className="col-span-2"><Field label="Notes"><textarea className="input" rows={2} value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setOpen(false)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving || !form.name} className="btn-gold btn-small" data-testid="staff-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>
    </div>
  );
}

// Attendance & Payroll: no month-picker precedent exists anywhere else in
// the app (confirmed) -- a plain <input type="month"> is the simplest
// correct choice, native browser support, no extra component needed.
const currentPeriod = () => new Date().toISOString().slice(0, 7);
const ATTENDANCE_STATUS_LABELS = { present: 'P', absent: 'A', half_day: 'H', leave: 'L' };
const ATTENDANCE_STATUS_FULL = { present: 'Present', absent: 'Absent', half_day: 'Half-Day', leave: 'Leave' };
const daysInPeriod = (period) => {
  const [y, m] = period.split('-').map(Number);
  const count = new Date(y, m, 0).getDate();
  return Array.from({ length: count }, (_, i) => {
    const day = i + 1;
    const iso = `${period}-${String(day).padStart(2, '0')}`;
    const weekday = new Date(y, m - 1, day).toLocaleDateString('en-IN', { weekday: 'short' });
    return { date: iso, weekday };
  });
};

const ATTENDANCE_STATUSES_UI = ['present', 'half_day', 'absent', 'leave'];

// Wide printouts (register, salary sheet): switch the page to landscape for the
// duration of this print only, then put it back.
function printLandscape() {
  const st = document.createElement('style');
  st.textContent = '@page { size: A4 landscape; margin: 10mm; }';
  document.head.appendChild(st);
  const cleanup = () => { st.remove(); window.removeEventListener('afterprint', cleanup); };
  window.addEventListener('afterprint', cleanup);
  window.print();
}
// ---- Attendance register: all staff x all days of a month on ONE screen ----
const ATT_CYCLE = ['', 'present', 'half_day', 'absent', 'leave'];
const nextAttendanceStatus = (cur) => ATT_CYCLE[(ATT_CYCLE.indexOf(cur || '') + 1) % ATT_CYCLE.length];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// The company's weekly-off day (0 = Sunday ... 6 = Saturday, null = none).
const isWeeklyOffIso = (iso, weeklyOffDay) => weeklyOffDay !== null && weeklyOffDay !== undefined && new Date(`${iso}T00:00:00`).getDay() === weeklyOffDay;
// A person's own weekly-off day: their override if they have one (-1 = none at
// all, 0..6 = that weekday), otherwise the company default.
const staffOffDay = (st, companyDay) => (st && st.weekly_off_override !== null && st.weekly_off_override !== undefined)
  ? (st.weekly_off_override === -1 ? null : st.weekly_off_override)
  : companyDay;
const todayLocalIso = () => new Date().toLocaleDateString('en-CA');
// Company holiday roster: staff_ids empty/null = everyone, otherwise only those staff.
const holidayAppliesTo = (h, staffId) => !h.staff_ids || h.staff_ids.length === 0 || h.staff_ids.includes(staffId);
const holidayOnDate = (holidays, date, staffId) => (holidays || []).find(h => h.date === date && holidayAppliesTo(h, staffId)) || null;
const ATT_CELL_STYLE = {
  present: { color: '#34aa6e', background: 'rgba(52,170,110,0.18)' },
  half_day: { color: '#d4a853', background: 'rgba(212,168,83,0.22)' },
  absent: { color: '#e66767', background: 'rgba(230,103,103,0.2)' },
  leave: { color: '#5b9be8', background: 'rgba(57,135,229,0.2)' },
};
// Fill every BLANK day (up to today, skipping days isOff(date, staffRow) flags) as Present,
// for all unlocked staff or one staff member. Marks already made are never changed.
function fillBlanksPresent(marks, staffRows, grid, todayIso, isOff, onlyStaffId) {
  const next = { ...marks };
  staffRows.forEach(st => {
    if (st.locked || (onlyStaffId && st.id !== onlyStaffId)) return;
    const row = { ...(next[st.id] || {}) };
    grid.forEach(({ date }) => {
      if (date > todayIso || (isOff && isOff(date, st))) return;
      if (!row[date]) row[date] = 'present';
    });
    next[st.id] = row;
  });
  return next;
}
function diffAttendance(saved, marks) {
  const changes = [];
  const ids = new Set([...Object.keys(saved), ...Object.keys(marks)]);
  ids.forEach(sid => {
    const a = saved[sid] || {}, b = marks[sid] || {};
    new Set([...Object.keys(a), ...Object.keys(b)]).forEach(date => {
      if ((a[date] || '') !== (b[date] || '')) changes.push({ staff_id: sid, date, status: b[date] || null });
    });
  });
  return changes;
}
function countAttendance(row, grid) {
  const c = { present: 0, half_day: 0, absent: 0, leave: 0 };
  grid.forEach(({ date }) => { const v = row?.[date]; if (v && c[v] !== undefined) c[v] += 1; });
  return c;
}

function AttendanceRegister() {
  const { user } = useAuth();
  const canEdit = ['admin', 'accountant', 'master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const [companyId, setCompanyId] = useState(needsPicker ? '' : undefined);
  const [period, setPeriod] = useState(currentPeriod());
  const [staffRows, setStaffRows] = useState([]);
  const [saved, setSaved] = useState({});
  const [marks, setMarks] = useState({});
  const [skipWeeklyOff, setSkipWeeklyOff] = useState(true);
  const [weeklyOffDay, setWeeklyOffDay] = useState(0);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Master Admin only: correct the attendance of a month whose payroll is already finalized (the payroll itself is never changed
  // behind your back -- it is flagged "attendance changed after payroll" and you review it in Payroll).
  const isMaster = user.role === 'master_admin';
  const [editFinalized, setEditFinalized] = useState(false);
  const lockedFor = (st) => !!st.locked && !(isMaster && editFinalized);
  useEffect(() => { if (!needsPicker && companies.length === 1) setCompanyId(companies[0].id); }, [companies, needsPicker]);
  const grid = period ? daysInPeriod(period) : [];
  const changes = diffAttendance(saved, marks);
  const companyName = (companies.find(c => c.id === companyId) || {}).name || '';

  const load = async (cid = companyId, per = period) => {
    if (!cid || !per) { setStaffRows([]); setSaved({}); setMarks({}); return; }
    setLoading(true);
    try {
      const r = await api.get('/attendance/register', { params: { company_id: cid, period: per } });
      const by = {};
      r.data.days.forEach(d => { (by[d.staff_id] = by[d.staff_id] || {})[d.date] = d.status; });
      setStaffRows(r.data.staff); setSaved(by); setMarks(JSON.parse(JSON.stringify(by)));
      setWeeklyOffDay(r.data.weekly_off_day === undefined ? 0 : r.data.weekly_off_day);
      setHolidays(r.data.holidays || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the register'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [companyId, period]);

  const holidayFor = (date, st) => holidayOnDate(holidays, date, st && st.id);
  // A rest day for this person: their weekly off, or a company holiday that applies to them.
  const isOff = (date, st) => isWeeklyOffIso(date, staffOffDay(st, weeklyOffDay)) || !!holidayFor(date, st);
  const hasOverrides = staffRows.some(st => st.weekly_off_override !== null && st.weekly_off_override !== undefined);
  const changeStaffOff = async (st, v) => {
    const next = v === '' ? null : Number(v);
    try {
      await api.patch('/attendance/staff-weekly-off', { staff_id: st.id, weekly_off_override: next });
      setStaffRows(rows => rows.map(r => r.id === st.id ? { ...r, weekly_off_override: next } : r));
      toast.success(next === null ? `${st.name} follows the company weekly off` : next === -1 ? `${st.name} has no weekly off` : `${st.name}'s weekly off set to ${WEEKDAY_NAMES[next]}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const changeWeeklyOff = async (v) => {
    const next = v === '' ? null : Number(v);
    try {
      await api.patch('/attendance/weekly-off', { company_id: companyId, weekly_off_day: next });
      setWeeklyOffDay(next);
      toast.success(next === null ? 'No weekly off for this company' : `Weekly off set to ${WEEKDAY_NAMES[next]}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const guard = (fn) => () => { if (changes.length && !window.confirm('You have unsaved changes in this register. Discard them?')) return; fn(); };
  const cycle = (sid, date) => setMarks(m => ({ ...m, [sid]: { ...(m[sid] || {}), [date]: nextAttendanceStatus((m[sid] || {})[date]) } }));
  const fillDay = (date) => setMarks(m => {
    const next = { ...m };
    staffRows.forEach(st => { if (!lockedFor(st) && !(next[st.id] || {})[date]) next[st.id] = { ...(next[st.id] || {}), [date]: 'present' }; });
    return next;
  });
  const save = async () => {
    if (saving || !changes.length) return;
    // changes that touch a finalized (paid) month need an explicit yes: the pay itself stays as it was, only flagged for review
    const lockedHit = staffRows.filter(st => st.locked && changes.some(c => c.staff_id === st.id));
    const override = isMaster && editFinalized && lockedHit.length > 0;
    if (override && !window.confirm(`You are changing attendance for ${lockedHit.map(x => x.name).join(', ')} — payroll for ${period} is already finalized.\n\nTheir payroll will NOT change automatically. They are marked "attendance changed after payroll" in Payroll, where you can see the difference and recalculate.\n\nSave these changes?`)) return;
    setSaving(true);
    try {
      const r = await api.post('/attendance/register', { company_id: companyId, period, changes, ...(override ? { override_locked: true } : {}) });
      toast.success(`Saved — ${r.data.saved} marked, ${r.data.cleared} cleared`);
      if (r.data.payroll_flagged > 0) toast.warning(`${r.data.payroll_flagged} finalized payroll entr${r.data.payroll_flagged === 1 ? 'y is' : 'ies are'} now marked "attendance changed" — review in Payroll.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-center mb-3 no-print">
        {needsPicker && <select className="input" style={{ width: 170 }} value={companyId || ''} onChange={e => guard(() => setCompanyId(e.target.value))()}>
          <option value="">— select company —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>}
        <input type="month" className="input" style={{ width: 160 }} value={period} onChange={e => { const v = e.target.value; guard(() => setPeriod(v))(); }} />
        {isMaster && companyId && staffRows.some(st => st.locked) && <label className="text-xs inline-flex items-center gap-1 cursor-pointer px-2 py-1 rounded" style={{ border: '1px solid rgba(212,168,83,0.5)', color: '#d4a853' }} title="Master Admin only. The pay of a finalized month is not changed; it is flagged for your review in Payroll."><input type="checkbox" checked={editFinalized} onChange={e => setEditFinalized(e.target.checked)} data-testid="reg-edit-finalized" />Edit finalized months (Master Admin)</label>}
        {canEdit && companyId && <button onClick={() => setMarks(m => fillBlanksPresent(m, staffRows.map(st => ({ ...st, locked: lockedFor(st) })), grid, todayLocalIso(), skipWeeklyOff ? isOff : null))} className="btn-outline btn-small" data-testid="reg-fill-all">Mark all blanks Present (up to today)</button>}
        {companyId && <label className="text-xs text-muted inline-flex items-center gap-1">Weekly off
          <select className="input" style={{ width: 130, padding: '4px 8px' }} value={weeklyOffDay === null ? '' : String(weeklyOffDay)} disabled={!canEdit} onChange={e => changeWeeklyOff(e.target.value)} data-testid="reg-weekly-off">
            <option value="">None</option>
            {WEEKDAY_NAMES.map((n, i) => <option key={n} value={i}>{n}</option>)}
          </select></label>}
        {canEdit && companyId && <label className="text-xs text-muted inline-flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={skipWeeklyOff} onChange={e => setSkipWeeklyOff(e.target.checked)} data-testid="reg-skip-off" />Skip {weeklyOffDay !== null ? `${WEEKDAY_NAMES[weeklyOffDay]}s & holidays` : 'holidays'} when filling</label>}
        <div className="flex gap-2 ml-auto items-center">
          {changes.length > 0 && <span className="text-xs" style={{ color: 'var(--gold)' }} data-testid="reg-unsaved">{changes.length} unsaved change{changes.length === 1 ? '' : 's'}</span>}
          {changes.length > 0 && <button onClick={() => setMarks(JSON.parse(JSON.stringify(saved)))} className="tab btn-small">Undo changes</button>}
          <button onClick={() => printLandscape()} disabled={!staffRows.length} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14} />Print</button>
          {canEdit && <button onClick={save} disabled={saving || !changes.length} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="reg-save"><CheckCircle size={14} />{saving ? 'Saving…' : 'Save'}</button>}
        </div>
      </div>
      <div className="text-xs text-muted mb-3 no-print">Tap a cell to cycle <b>P</b>resent → <b>H</b>alf-day → <b>A</b>bsent → <b>L</b>eave → blank. For regular monthly staff only <b>A</b>bsent (unapproved) cuts pay — a Half-day cuts half a day, <b>L</b>eave (approved) is fully paid. Daily-wage staff are paid for days Present only. Tap a day number to mark everyone Present for that day. The shaded <b>–</b> days are the weekly off: they're not counted as absence and are skipped when filling, but you can still mark someone Present if they worked. Gold <b>★</b> days are company holidays (set under Staff & Vendor › Holidays), handled the same way. Rows with a lock are already paid for this month.</div>
      {!companyId ? <div className="card card-dark text-center text-muted py-10">Select a company to open its attendance register.</div>
        : loading ? <Loading />
        : !staffRows.length ? <div className="card card-dark text-center text-muted py-10">No active staff in this company yet. Add staff in the Staff tab first.</div>
        : (
          <div className="card card-dark overflow-x-auto print-area" data-testid="attendance-register">
            <div className="print-header">
              <div className="print-company">{companyName || 'Attendance'}</div>
              <div className="print-report-title">Attendance Register — {period}</div>
              <div className="print-meta">P = Present · H = Half-day · A = Absent · L = Leave{holidays.length ? ' · ★ = holiday' : ''}{weeklyOffDay !== null ? ` · – = weekly off (${WEEKDAY_NAMES[weeklyOffDay]}${hasOverrides ? ', or the own day shown by a name' : ''})` : (hasOverrides ? ' · – = weekly off (own day shown by name)' : '')} · Generated: {fmtGeneratedAt()}</div>
            </div>
            <table className="tbl register-tbl" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th className="reg-sticky" style={{ position: 'sticky', left: 0, zIndex: 1, minWidth: 150 }}>Staff</th>
                  {grid.map(({ date, weekday }) => (
                    <th key={date} className="text-center" style={{ padding: '4px 2px', minWidth: 28, background: holidays.some(h => h.date === date && !(h.staff_ids && h.staff_ids.length)) ? 'rgba(201,168,76,0.28)' : isWeeklyOffIso(date, weeklyOffDay) ? 'rgba(128,128,128,0.14)' : undefined }} title={(holidays.find(h => h.date === date) || {}).name || undefined} data-testid={`reg-head-${date}`}>
                      <button type="button" disabled={!canEdit} onClick={() => fillDay(date)} title={`Mark everyone Present on ${fmtDate(date)}`} data-testid={`reg-day-${date}`} style={{ background: 'none', border: 'none', color: 'inherit', cursor: canEdit ? 'pointer' : 'default', padding: 0, lineHeight: 1.1 }}>
                        <div>{Number(date.slice(8))}</div><div className="text-muted" style={{ fontSize: 9 }}>{weekday.slice(0, 1)}</div>
                      </button>
                    </th>
                  ))}
                  <th className="text-center" title="Present days">P</th><th className="text-center" title="Half-days">H</th><th className="text-center" title="Absent days">A</th><th className="text-center" title="Leave days">L</th>
                  <th className="no-print"></th>
                </tr>
              </thead>
              <tbody>
                {staffRows.map(st => {
                  const row = marks[st.id] || {};
                  const c = countAttendance(row, grid);
                  return (
                    <tr key={st.id}>
                      <td className="reg-sticky" style={{ position: 'sticky', left: 0, zIndex: 1 }}>
                        {st.locked && <Lock size={11} className="inline mr-1" style={{ color: lockedFor(st) ? 'var(--muted, #888)' : '#d4a853' }} title={lockedFor(st) ? 'Payroll finalized for this month' : 'Payroll finalized — you are editing as Master Admin'} />}{st.changed_after_payroll && <span className="badge mr-1" style={{ color: '#d4a853', borderColor: 'rgba(212,168,83,0.5)', fontSize: 9 }} title="Attendance was changed after this month's payroll was finalized — review it in Payroll" data-testid={`reg-changed-${st.id}`}>changed after payroll</span>}
                        <span>{st.name}</span>{st.designation && <span className="text-muted reg-designation" style={{ fontSize: 10 }}> · {st.designation}</span>}
                        {st.weekly_off_override !== null && st.weekly_off_override !== undefined && <span className="text-muted" style={{ fontSize: 10 }} data-testid={`reg-own-off-${st.id}`}> · off: {st.weekly_off_override === -1 ? 'none' : WEEKDAY_NAMES[st.weekly_off_override].slice(0, 3)}</span>}
                      </td>
                      {grid.map(({ date }) => {
                        const v = row[date] || '';
                        return (
                          <td key={date} style={{ padding: 1, textAlign: 'center', background: holidayFor(date, st) ? 'rgba(201,168,76,0.2)' : isOff(date, st) ? 'rgba(128,128,128,0.12)' : undefined }}>
                            <button type="button" disabled={!canEdit || lockedFor(st)} onClick={() => cycle(st.id, date)} data-testid={`reg-cell-${st.id}-${date}`} title={v ? ATTENDANCE_STATUS_FULL[v] : (holidayFor(date, st) ? `Holiday: ${holidayFor(date, st).name}` : isOff(date, st) ? 'Weekly off' : 'Not marked')}
                              style={{ width: 26, height: 26, borderRadius: 6, border: v ? '1px solid transparent' : '1px dashed rgba(150,150,150,0.35)', fontSize: 11, fontWeight: 600, cursor: (!canEdit || lockedFor(st)) ? 'default' : 'pointer', ...(v ? ATT_CELL_STYLE[v] : { background: 'transparent', color: 'inherit' }), opacity: st.locked ? 0.7 : 1 }}>
                              {v ? ATTENDANCE_STATUS_LABELS[v] : (holidayFor(date, st) ? '★' : isOff(date, st) ? '–' : '')}
                            </button>
                          </td>
                        );
                      })}
                      <td className="mono text-center">{c.present}</td><td className="mono text-center">{c.half_day}</td><td className="mono text-center">{c.absent}</td><td className="mono text-center">{c.leave}</td>
                      <td className="no-print text-right" style={{ whiteSpace: 'nowrap' }}>
                        {canEdit && <select className="input" style={{ width: 92, padding: '2px 4px', fontSize: 11 }} value={st.weekly_off_override === null || st.weekly_off_override === undefined ? '' : String(st.weekly_off_override)} onChange={e => changeStaffOff(st, e.target.value)} title={`Weekly off for ${st.name}: follow the company setting, none, or a specific day`} data-testid={`reg-off-${st.id}`}>
                          <option value="">Off: company</option>
                          <option value="-1">Off: none</option>
                          {WEEKDAY_NAMES.map((n, i) => <option key={n} value={i}>Off: {n.slice(0, 3)}</option>)}
                        </select>}
                        {canEdit && !lockedFor(st) && <button type="button" className="tab btn-small" style={{ padding: '2px 8px', marginLeft: 4 }} onClick={() => setMarks(m => fillBlanksPresent(m, staffRows, grid, todayLocalIso(), skipWeeklyOff ? isOff : null, st.id))} title="Mark this person's blank days Present (up to today)">Fill</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="reg-sticky text-muted text-xs" style={{ position: 'sticky', left: 0 }}>Present each day</td>
                  {grid.map(({ date }) => <td key={date} className="mono text-center text-muted" style={{ fontSize: 10, padding: 1 }}>{staffRows.filter(st => (marks[st.id] || {})[date] === 'present').length || ''}</td>)}
                  <td colSpan={5}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
    </div>
  );
}

function AttendancePanel() {
  const [view, setView] = usePersistedState('Attendance.view', 'register');
  return (
    <div className="pb-8">
      <div className="mb-4">
        <div className="section-eyebrow mb-2">Employees</div>
        <h1 className="serif text-4xl">Attendance</h1>
        <div className="text-sm text-muted mt-1">Mark the whole team on one screen. A Fixed Monthly staff member's pay doesn't change with attendance (it's kept as a record); a Per Day Rate staff member is paid for their Present days plus half for Half-days. Staff who are paid by payroll only (untick "Mark attendance" on their Staff record) aren't listed here.</div>
      </div>
      <div className="flex gap-2 mb-4 no-print">
        <button className={"tab " + (view === 'register' ? 'active' : '')} onClick={() => setView('register')} data-testid="att-view-register">Register (all staff)</button>
        <button className={"tab " + (view === 'staff' ? 'active' : '')} onClick={() => setView('staff')} data-testid="att-view-staff">By staff member</button>
      </div>
      {view === 'register' ? <AttendanceRegister /> : <AttendanceByStaff />}
    </div>
  );
}

function AttendanceByStaff() {
  const { user } = useAuth();
  const canEdit = ['admin', 'accountant', 'master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const [companyId, setCompanyId] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [period, setPeriod] = useState(currentPeriod());
  const [days, setDays] = useState({}); // { [date]: {status, notes} }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/staff').then(r => setStaffList(r.data.filter(s => s.status === 'active' && s.attendance_tracked !== false))); }, []);
  const companyStaff = forCompany(staffList, companyId);

  const load = async () => {
    if (!staffId || !period) { setDays({}); return; }
    setLoading(true);
    try {
      const r = await api.get('/attendance', { params: { staff_id: staffId, period } });
      const byDate = {};
      r.data.days.forEach(d => { byDate[d.date] = { status: d.status, notes: d.notes || '' }; });
      setDays(byDate);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load attendance'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [staffId, period]);

  const setDayStatus = (date, status) => setDays(d => ({ ...d, [date]: { ...(d[date] || {}), status } }));
  const setDayNotes = (date, notes) => setDays(d => ({ ...d, [date]: { ...(d[date] || {}), notes } }));

  const save = async () => {
    if (saving) return;
    const dayList = Object.entries(days).filter(([, v]) => v.status).map(([date, v]) => ({ date, status: v.status, notes: v.notes || null }));
    if (!dayList.length) { toast.error('Mark at least one day before saving.'); return; }
    setSaving(true);
    try {
      let r;
      try { r = await api.post('/attendance/bulk', { staff_id: staffId, period, days: dayList }); }
      catch (e) {
        // finalized month: only the Master Admin may correct it, and only after an explicit yes
        if (e.response?.status === 423 && user.role === 'master_admin' && window.confirm(`Payroll for ${period} is already finalized for this staff member.\n\nAs Master Admin you can still correct the attendance. Their payroll will NOT change automatically — they are marked "attendance changed after payroll" in Payroll, where you can see the difference and recalculate.\n\nSave anyway?`)) {
          r = await api.post('/attendance/bulk', { staff_id: staffId, period, days: dayList, override_locked: true });
        } else throw e;
      }
      toast.success('Attendance saved');
      if (r.data.payroll_flagged > 0) toast.warning('This month\'s finalized payroll is now marked "attendance changed" — review it in Payroll.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const staffName = companyStaff.find(s => s.id === staffId)?.name;
  const grid = period ? daysInPeriod(period) : [];

  return (
    <div className="pb-8">
      <div className="flex flex-wrap gap-2 items-center mb-4">
        {needsPicker && <select className="input" style={{ width: 170 }} value={companyId} onChange={e => { setCompanyId(e.target.value); setStaffId(''); }}>
          <option value="">— select company —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>}
        <select className="input" style={{ width: 220 }} disabled={needsPicker && !companyId} value={staffId} onChange={e => setStaffId(e.target.value)}>
          <option value="">{needsPicker && !companyId ? '— select a company first —' : '— select staff —'}</option>
          {companyStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input type="month" className="input" style={{ width: 160 }} value={period} onChange={e => setPeriod(e.target.value)} />
        {canEdit && staffId && <button onClick={save} disabled={saving} className="btn-gold btn-small inline-flex items-center gap-1"><CheckCircle size={14} />{saving ? 'Saving…' : 'Save Month'}</button>}
      </div>
      {!staffId ? (
        <div className="card card-dark text-center text-muted py-10">Select a staff member and month to mark attendance.</div>
      ) : loading ? <Loading /> : (
        <div className="card card-dark overflow-x-auto">
          <div className="serif text-xl text-[color:var(--gold)] mb-4">{staffName} — {period}</div>
          <table className="tbl">
            <thead><tr><th>Date</th><th>Day</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>
              {grid.map(({ date, weekday }) => {
                const cur = days[date]?.status;
                return (
                  <tr key={date}>
                    <td className="text-xs">{fmtDate(date)}</td>
                    <td className="text-xs text-muted">{weekday}</td>
                    <td>
                      <div className="flex gap-1">
                        {ATTENDANCE_STATUSES_UI.map(s => (
                          <button key={s} disabled={!canEdit} title={ATTENDANCE_STATUS_FULL[s]} onClick={() => setDayStatus(date, s)}
                            className={"btn-small " + (cur === s ? "btn-gold" : "btn-outline")} style={{ width: 30, padding: '2px 0' }}>
                            {ATTENDANCE_STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td><input className="input" style={{ minWidth: 160 }} disabled={!canEdit} value={days[date]?.notes || ''} onChange={e => setDayNotes(date, e.target.value)} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
// ---- Company holiday roster (Holidays tab) ----
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Fixed-date national holidays only; festival dates move every year, so those are added by hand.
const COMMON_FIXED_HOLIDAYS = [
  { key: 'republic', name: 'Republic Day', md: '01-26' },
  { key: 'labour', name: 'Labour Day', md: '05-01' },
  { key: 'independence', name: 'Independence Day', md: '08-15' },
  { key: 'gandhi', name: 'Gandhi Jayanti', md: '10-02' },
  { key: 'christmas', name: 'Christmas', md: '12-25' },
];
const isRealIsoDate = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return false;
  const d = new Date(`${iso}T00:00:00`);
  return !isNaN(d) && d.getFullYear() === Number(iso.slice(0, 4)) && d.getMonth() + 1 === Number(iso.slice(5, 7)) && d.getDate() === Number(iso.slice(8, 10));
};
// Every date from..to inclusive (max 62 days, so a typo can't create a year of holidays).
const holidayRangeDates = (from, to) => {
  if (!isRealIsoDate(from)) return [];
  if (!to || to <= from) return [from];
  if (!isRealIsoDate(to)) return [from];
  const out = []; const d = new Date(`${from}T00:00:00`);
  while (out.length < 62) {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (iso > to) break;
    out.push(iso); d.setDate(d.getDate() + 1);
  }
  return out;
};
// Same day-and-month in another year (29 Feb silently drops in a non-leap year).
const shiftHolidaysToYear = (list, toYear) => list
  .map(h => ({ ...h, holiday_date: `${toYear}${h.holiday_date.slice(4)}` }))
  .filter(h => isRealIsoDate(h.holiday_date));
const holidayWeekday = (iso) => WEEKDAY_NAMES[new Date(`${iso}T00:00:00`).getDay()].slice(0, 3);

function HolidaysPanel() {
  const { user } = useAuth();
  const canEdit = ['admin', 'accountant', 'master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const [companyId, setCompanyId] = useState(needsPicker ? '' : undefined);
  const [year, setYear] = usePersistedState('Holidays.year', new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState(0);
  const [items, setItems] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [quick, setQuick] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!needsPicker && companies.length === 1) setCompanyId(companies[0].id); }, [companies, needsPicker]);
  const companyName = (companies.find(c => c.id === companyId) || {}).name || '';

  const load = async (cid = companyId, y = year) => {
    if (!cid) { setItems([]); setStaffList([]); return; }
    setLoading(true);
    try {
      const [h, st] = await Promise.all([api.get('/holidays', { params: { company_id: cid, year: y } }), api.get('/staff')]);
      setItems(h.data);
      setStaffList(st.data.filter(x => x.company_id === cid && x.status === 'active' && x.attendance_tracked !== false));   // holidays belong to attendance: staff who are not marked for attendance are not offered
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load holidays'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [companyId, year]);

  const staffName = (id) => (staffList.find(x => x.id === id) || {}).name || 'Former staff';
  const byMonth = MONTH_NAMES.map((n, i) => ({ month: i + 1, name: n, rows: items.filter(h => Number(h.holiday_date.slice(5, 7)) === i + 1) }));
  const shownMonths = byMonth.filter(m => m.rows.length && (!monthFilter || m.month === monthFilter));
  const paidCount = items.filter(h => h.paid_for_per_day).length;
  const specificCount = items.filter(h => h.staff_ids && h.staff_ids.length).length;

  const openNew = () => setForm({ name: '', date: `${year}-01-01`, to_date: '', applies: 'all', staff_ids: [], paid: false, notes: '' });
  const openEdit = (h) => setForm({ id: h.id, name: h.name, date: h.holiday_date, to_date: '', applies: h.staff_ids && h.staff_ids.length ? 'selected' : 'all', staff_ids: h.staff_ids || [], paid: !!h.paid_for_per_day, notes: h.notes || '' });
  const save = async () => {
    if (saving || !form) return;
    if (!form.name.trim()) { toast.error('Give the holiday a name.'); return; }
    if (!isRealIsoDate(form.date)) { toast.error('Pick a valid date.'); return; }
    if (form.applies === 'selected' && !form.staff_ids.length) { toast.error('Pick at least one staff member, or choose All staff.'); return; }
    const base = { company_id: companyId, name: form.name.trim(), staff_ids: form.applies === 'selected' ? form.staff_ids : null, paid_for_per_day: form.paid, notes: form.notes || null };
    setSaving(true);
    try {
      if (form.id) {
        await api.patch(`/holidays/${form.id}`, { ...base, holiday_date: form.date });
        toast.success('Holiday updated');
      } else {
        const dates = holidayRangeDates(form.date, form.to_date);
        if (dates.length > 1) {
          const r = await api.post('/holidays/bulk', { company_id: companyId, skip_existing: true, items: dates.map((d, i) => ({ ...base, name: `${base.name} (Day ${i + 1})`, holiday_date: d })) });
          toast.success(`${r.data.created.length} day${r.data.created.length === 1 ? '' : 's'} added${r.data.skipped.length ? ` · ${r.data.skipped.length} already had a holiday` : ''}`);
        } else {
          await api.post('/holidays', { ...base, holiday_date: dates[0] });
          toast.success('Holiday added');
        }
        if (Number(form.date.slice(0, 4)) !== Number(year)) setYear(Number(form.date.slice(0, 4)));
      }
      setForm(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (h) => {
    if (!window.confirm(`Delete "${h.name}" on ${fmtDate(h.holiday_date)}?`)) return;
    try { await api.delete(`/holidays/${h.id}`); toast.success('Deleted'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openQuick = () => setQuick({ picked: COMMON_FIXED_HOLIDAYS.map(c => c.key), paid: false });
  const saveQuick = async () => {
    if (saving || !quick) return;
    const chosen = COMMON_FIXED_HOLIDAYS.filter(c => quick.picked.includes(c.key) && !items.some(h => h.holiday_date === `${year}-${c.md}`));
    if (!chosen.length) { toast.error('Tick at least one holiday.'); return; }
    setSaving(true);
    try {
      const r = await api.post('/holidays/bulk', { company_id: companyId, skip_existing: true, items: chosen.map(c => ({ company_id: companyId, name: c.name, holiday_date: `${year}-${c.md}`, staff_ids: null, paid_for_per_day: quick.paid })) });
      toast.success(`${r.data.created.length} added${r.data.skipped.length ? ` · ${r.data.skipped.length} already on the roster` : ''}`);
      setQuick(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const copyFromLastYear = async () => {
    if (saving) return;
    try {
      const prev = (await api.get('/holidays', { params: { company_id: companyId, year: year - 1 } })).data;
      if (!prev.length) { toast.error(`There are no holidays in ${year - 1} to copy.`); return; }
      const shifted = shiftHolidaysToYear(prev, year).filter(h => !items.some(x => x.holiday_date === h.holiday_date));
      if (!shifted.length) { toast.error(`Everything from ${year - 1} is already on the ${year} roster.`); return; }
      if (!window.confirm(`Copy ${shifted.length} holiday${shifted.length === 1 ? '' : 's'} from ${year - 1} to ${year} on the same dates? Festival dates change every year — review them after copying.`)) return;
      setSaving(true);
      const r = await api.post('/holidays/bulk', { company_id: companyId, skip_existing: true, items: shifted.map(h => ({ company_id: companyId, name: h.name, holiday_date: h.holiday_date, staff_ids: h.staff_ids && h.staff_ids.length ? h.staff_ids : null, paid_for_per_day: !!h.paid_for_per_day, notes: h.notes || null })) });
      toast.success(`Copied ${r.data.created.length} holiday${r.data.created.length === 1 ? '' : 's'} — check the festival dates`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const toggleStaff = (id) => setForm(f => ({ ...f, staff_ids: f.staff_ids.includes(id) ? f.staff_ids.filter(x => x !== id) : [...f.staff_ids, id] }));

  return (
    <div className="pb-8">
      <div className="mb-4">
        <div className="section-eyebrow mb-2">Employees</div>
        <h1 className="serif text-4xl">Holidays</h1>
        <div className="text-sm text-muted mt-1" style={{ maxWidth: 780 }}>Each company keeps its own holiday roster, for all staff or only chosen staff. Holidays show as gold ★ days in the Attendance register and are skipped when blanks are filled. Pay is only affected if you switch a holiday to <b>paid for per-day staff</b> — fixed-monthly pay never changes.</div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4 no-print">
        {needsPicker && <select className="input" style={{ width: 190 }} value={companyId || ''} onChange={e => setCompanyId(e.target.value)} data-testid="hol-company">
          <option value="">— select company —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>}
        <div className="inline-flex items-center gap-1">
          <button className="tab btn-small" onClick={() => { setYear(year - 1); setMonthFilter(0); }} aria-label="Previous year" data-testid="hol-prev"><ArrowLeft size={13} /></button>
          <span className="serif text-xl px-2" data-testid="hol-year">{year}</span>
          <button className="tab btn-small" onClick={() => { setYear(year + 1); setMonthFilter(0); }} aria-label="Next year" data-testid="hol-next"><ArrowRight size={13} /></button>
        </div>
        {canEdit && companyId && <>
          <button onClick={openNew} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="hol-add"><Plus size={14} />Add holiday</button>
          <button onClick={openQuick} className="btn-outline btn-small" data-testid="hol-quick">Add common holidays</button>
          <button onClick={copyFromLastYear} disabled={saving} className="btn-outline btn-small" data-testid="hol-copy">Copy from {year - 1}</button>
        </>}
        <button onClick={() => window.print()} disabled={!items.length} className="btn-outline btn-small inline-flex items-center gap-1 ml-auto"><Printer size={14} />Print</button>
      </div>
      {!companyId ? <div className="card card-dark text-center text-muted py-10">Select a company to see its holiday roster.</div>
        : loading ? <Loading />
        : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4 no-print" data-testid="hol-summary">
              <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Holidays in {year}</div><div className="serif text-2xl mono text-[color:var(--gold)]">{items.length}</div></div>
              <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Also paid to daily-wage staff</div><div className="serif text-2xl mono">{paidCount}</div></div>
              <div className="p-3 rounded surface-2"><div className="text-xs text-muted">For chosen staff only</div><div className="serif text-2xl mono">{specificCount}</div></div>
            </div>
            <div className="flex flex-wrap gap-1 mb-4 no-print" data-testid="hol-months">
              <button className={"tab btn-small " + (!monthFilter ? 'active' : '')} onClick={() => setMonthFilter(0)} data-testid="hol-month-all">All year</button>
              {byMonth.map(m => <button key={m.month} className={"tab btn-small " + (monthFilter === m.month ? 'active' : '')} onClick={() => setMonthFilter(m.month)} data-testid={`hol-month-${m.month}`}>{m.name.slice(0, 3)}{m.rows.length ? <span style={{ color: 'var(--gold)' }}> {m.rows.length}</span> : ''}</button>)}
            </div>
            <div className="card card-dark print-area hol-list" data-testid="hol-list">
              <div className="print-header">
                <div className="print-company">{companyName || 'Holidays'}</div>
                <div className="print-report-title">Holiday roster — {year}{monthFilter ? ` · ${MONTH_NAMES[monthFilter - 1]}` : ''}</div>
                <div className="print-meta">{items.length} holidays · Generated: {fmtGeneratedAt()}</div>
              </div>
              {shownMonths.length === 0 && <div className="text-center text-muted py-8">{items.length ? `No holidays in ${MONTH_NAMES[monthFilter - 1]}.` : `No holidays set for ${year} yet. Add one, add the common fixed-date holidays, or copy last year's roster.`}</div>}
              {shownMonths.map(m => (
                <div key={m.month} className="mb-4" data-testid={`hol-group-${m.month}`}>
                  <div className="serif text-lg text-[color:var(--gold)] mb-1">{m.name} <span className="text-xs text-muted">· {m.rows.length}</span></div>
                  <table className="tbl" style={{ tableLayout: 'fixed' }}>
                    <colgroup><col style={{ width: 170 }} /><col /><col style={{ width: 170 }} /><col style={{ width: 150 }} /><col className="no-print" style={{ width: 90 }} /></colgroup>
                    <tbody>
                      {m.rows.map(h => (
                        <tr key={h.id} data-testid={`hol-row-${h.holiday_date}`}>
                          <td className="whitespace-nowrap" style={{ width: 150 }}><span className="mono">{fmtDate(h.holiday_date)}</span> <span className="text-xs text-muted">{holidayWeekday(h.holiday_date)}</span></td>
                          <td className="serif text-base">{h.name}{h.notes ? <div className="text-xs text-muted" style={{ fontFamily: 'inherit' }}>{h.notes}</div> : null}</td>
                          <td className="text-xs">{h.staff_ids && h.staff_ids.length
                            ? <span title={h.staff_ids.map(staffName).join(', ')}>{h.staff_ids.length <= 2 ? h.staff_ids.map(staffName).join(', ') : `${h.staff_ids.length} staff`}</span>
                            : <span className="text-muted">All staff</span>}</td>
                          <td className="text-xs"><div><span style={{ color: '#34aa6e' }}>✓</span> Monthly staff: paid</div><div className={h.paid_for_per_day ? '' : 'text-muted'} title="Daily-wage workers are paid only for days they work, unless you tick the box on this holiday"><span style={{ color: h.paid_for_per_day ? '#34aa6e' : undefined }}>{h.paid_for_per_day ? '✓' : '–'}</span> Daily wage: {h.paid_for_per_day ? 'paid' : 'unpaid'}</div></td>
                          <td className="text-right whitespace-nowrap no-print">{canEdit && <>
                            <button aria-label="Edit" onClick={() => openEdit(h)} className="tab btn-small mr-1"><Edit3 size={12} /></button>
                            <button aria-label="Delete" onClick={() => del(h)} className="tab btn-small text-red-300"><Trash2 size={12} /></button>
                          </>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </>
        )}

      {form && <Modal open={true} onClose={() => setForm(null)} title={form.id ? 'Edit holiday' : 'Add holiday'} maxW={620}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hol-form">
          <div className="md:col-span-2"><Field label="Holiday name *"><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Diwali" data-testid="hol-name" /></Field></div>
          <Field label={form.id ? 'Date *' : 'From date *'}><input type="date" className="input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="hol-date" /></Field>
          {!form.id && <Field label="To date (multi-day, optional)"><input type="date" className="input" value={form.to_date} min={form.date} onChange={e => setForm({ ...form, to_date: e.target.value })} data-testid="hol-to-date" /></Field>}
          <div className="md:col-span-2">
            <Field label="Applies to">
              <div className="flex gap-4 text-sm">
                <label className="inline-flex items-center gap-1 cursor-pointer"><input type="radio" name="hol-applies" checked={form.applies === 'all'} onChange={() => setForm({ ...form, applies: 'all' })} data-testid="hol-applies-all" />All staff</label>
                <label className="inline-flex items-center gap-1 cursor-pointer"><input type="radio" name="hol-applies" checked={form.applies === 'selected'} onChange={() => setForm({ ...form, applies: 'selected' })} data-testid="hol-applies-selected" />Selected staff only</label>
              </div>
            </Field>
            {form.applies === 'selected' && <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1" style={{ maxHeight: 180, overflowY: 'auto' }} data-testid="hol-staff-pick">
              {staffList.length === 0 && <div className="text-xs text-muted">No active staff in this company.</div>}
              {staffList.map(x => <label key={x.id} className="text-sm inline-flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.staff_ids.includes(x.id)} onChange={() => toggleStaff(x.id)} data-testid={`hol-staff-${x.id}`} />{x.name}{x.designation ? <span className="text-xs text-muted">· {x.designation}</span> : null}</label>)}
            </div>}
          </div>
          <div className="md:col-span-2">
            <label className="inline-flex items-start gap-2 cursor-pointer text-sm"><input type="checkbox" className="mt-1" checked={form.paid} onChange={e => setForm({ ...form, paid: e.target.checked })} data-testid="hol-paid" />
              <span><b>Also pay daily-wage workers for this day</b><br /><span className="text-xs text-muted">Regular monthly staff are always paid for a holiday — nothing to set. Daily-wage workers are paid only for days they work, so leave this OFF unless you want to pay them for this day (they get one paid day, unless you marked them on that day or it is their weekly off).</span></span></label>
          </div>
          <div className="md:col-span-2"><Field label="Notes"><input className="input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="hol-notes" /></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={() => setForm(null)} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="hol-save">{saving ? 'Saving…' : 'Save'}</button></div>
      </Modal>}

      {quick && <Modal open={true} onClose={() => setQuick(null)} title={`Add common holidays — ${year}`} maxW={520}>
        <div className="text-xs text-muted mb-3">Fixed-date national holidays. Festivals that move every year (Holi, Diwali, Eid…) are added by hand. Dates already on the roster are skipped.</div>
        <div className="grid gap-1" data-testid="hol-quick-list">
          {COMMON_FIXED_HOLIDAYS.map(c => {
            const exists = items.some(h => h.holiday_date === `${year}-${c.md}`);
            return <label key={c.key} className={"text-sm inline-flex items-center gap-2 " + (exists ? 'opacity-50' : 'cursor-pointer')}><input type="checkbox" disabled={exists} checked={!exists && quick.picked.includes(c.key)} onChange={() => setQuick(q => ({ ...q, picked: q.picked.includes(c.key) ? q.picked.filter(x => x !== c.key) : [...q.picked, c.key] }))} data-testid={`hol-quick-${c.key}`} />{c.name} <span className="text-xs text-muted">· {fmtDate(`${year}-${c.md}`)}{exists ? ' · already added' : ''}</span></label>;
          })}
        </div>
        <label className="inline-flex items-start gap-2 cursor-pointer text-sm mt-4"><input type="checkbox" className="mt-1" checked={quick.paid} onChange={e => setQuick({ ...quick, paid: e.target.checked })} data-testid="hol-quick-paid" /><span><b>Also pay daily-wage workers</b> <span className="text-xs text-muted">(off = daily-wage unpaid; monthly staff are always paid)</span></span></label>
        <div className="mt-6 flex justify-end gap-2"><button onClick={() => setQuick(null)} className="btn-outline btn-small">Cancel</button><button onClick={saveQuick} disabled={saving} className="btn-gold btn-small" data-testid="hol-quick-save">{saving ? 'Adding…' : 'Add selected'}</button></div>
      </Modal>}
    </div>
  );
}
// ---- Staff → Travel: each staff member's travel allowance account ----
// Advances are paid through the month; the approved amount is typed in after month end. closing = carried in + approved − advances
// + adjustment. Positive = due to the staff member, negative = excess advance they hold; it carries into the next month.
const monthShift = (ym, by) => { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + by, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
function TravelBalanceChip({ value, startsAt }) {
  if (value === null || value === undefined) return <span className="text-xs text-muted" title={`The account starts in ${startsAt ? monthLabelOf(startsAt) : 'the first month you enter'}`}>not started</span>;
  if (Math.abs(value) < 0.005) return <span className="badge status-running">Settled</span>;
  return value > 0
    ? <span className="badge status-completed" style={{ whiteSpace: 'nowrap' }} title="The company owes the staff member this much">Due to staff {fmtINR(value)}</span>
    : <span className="badge" style={{ color: '#e66767', borderColor: 'rgba(230,103,103,0.45)', whiteSpace: 'nowrap' }} title="The staff member holds this much advance beyond what is approved">Advance with staff {fmtINR(-value)}</span>;
}
function TravelLedgerModal({ staffId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/staff/${staffId}/travel-ledger`).then(r => setD(r.data)).catch(e => { toast.error(e.response?.data?.detail || 'Could not load the account'); onClose(); }); }, [staffId]);
  return (
    <Modal open={true} onClose={onClose} title={d ? `Travel account — ${d.staff.name}` : 'Travel account'} maxW={760}>
      {!d ? <Loading /> : (<>
        {d.months.length > 0 && d.totals && <div className="grid grid-cols-3 gap-2 mb-3" data-testid="travel-ledger-totals">
          <div className="p-2 rounded surface-2"><div className="text-xs text-muted">Total received</div><div className="mono serif text-xl">{fmtINR(d.totals.received)}</div></div>
          <div className="p-2 rounded surface-2"><div className="text-xs text-muted">Total approved</div><div className="mono serif text-xl text-[color:var(--gold)]">{fmtINR(d.totals.approved)}</div></div>
          <div className="p-2 rounded surface-2"><div className="text-xs text-muted">{d.totals.balance > 0.004 ? 'Still due to staff' : d.totals.balance < -0.004 ? 'Advance still with staff' : 'Settled'}</div><div className="mono serif text-xl" style={{ color: d.totals.balance < -0.004 ? '#e66767' : undefined }}>{fmtINR(Math.abs(d.totals.balance))}</div></div>
        </div>}
        {d.before > 0 && d.first && <div className="text-xs text-muted mb-2">Advances of {fmtINR(d.before)} dated before {monthLabelOf(d.first)} are carried in at the start of the account.</div>}
        {d.months.length === 0 ? <div className="py-2" data-testid="travel-ledger-empty">
          <div className="text-sm text-muted mb-2">No month has an approved amount yet{d.unsettled_advances > 0 ? ` — ${fmtINR(d.unsettled_advances)} of advances are waiting to be settled. Type the approved amount for a month on the Travel screen to start the account.` : '.'}</div>
          {(d.advance_months || []).length > 0 && <table className="tbl"><thead><tr><th>Month</th><th className="text-right">Payments</th><th className="text-right">Advances paid</th></tr></thead><tbody>{d.advance_months.map(m => <tr key={m.period}><td>{m.period ? monthLabelOf(m.period) : '—'}</td><td className="mono text-right">{m.count}</td><td className="mono text-right">{fmtINR(m.advances_total)}</td></tr>)}</tbody></table>}
        </div> : (
          <div style={{ maxHeight: 420, overflow: 'auto' }}>
            <table className="tbl" data-testid="travel-ledger-table"><thead><tr><th>Month</th><th className="text-right">Carried in</th><th className="text-right">Approved</th><th className="text-right">Advances</th><th className="text-right" title="Total paid to the staff member up to and including this month">Received to date</th><th className="text-right" title="Total approved up to and including this month">Approved to date</th><th className="text-right">Adjust.</th><th className="text-right">Balance</th></tr></thead>
              <tbody>{d.months.map(m => <tr key={m.period}>
                <td>{monthLabelOf(m.period)}{!m.has_record && <span className="text-xs text-muted"> · approved not entered</span>}</td>
                <td className="mono text-right">{fmtINR(m.opening)}</td><td className="mono text-right">{fmtINR(m.approved)}</td><td className="mono text-right">{fmtINR(m.advances_total)}</td>
                <td className="mono text-right text-muted">{fmtINR(m.received_to_date)}</td><td className="mono text-right text-muted">{fmtINR(m.approved_to_date)}</td>
                <td className="mono text-right" title={[m.adjustment_note, m.recovered > 0 ? `${fmtINR(m.recovered)} recovered through payroll` : ''].filter(Boolean).join(' · ') || undefined}>{(m.adjustment + m.recovered) ? fmtINR(m.adjustment + m.recovered) : '—'}</td>
                <td className="text-right"><TravelBalanceChip value={m.closing} /></td></tr>)}</tbody></table>
          </div>)}
      </>)}
      <div className="mt-4 flex justify-end"><button onClick={onClose} className="btn-outline btn-small">Close</button></div>
    </Modal>
  );
}
function TravelAccountPanel() {
  const { user } = useAuth();
  const canEdit = ['admin', 'accountant', 'master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const [companyId, setCompanyId] = useState(needsPicker ? '' : undefined);
  const [period, setPeriod] = useState(currentPeriod());
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState('');
  const [openId, setOpenId] = useState('');
  const [ledgerFor, setLedgerFor] = useState('');
  const loadToken = useRef(0);
  useEffect(() => { if (!needsPicker && companies.length === 1) setCompanyId(companies[0].id); }, [companies, needsPicker]);
  const load = async () => {
    if (!companyId || !period) { setData(null); return; }
    const my = ++loadToken.current;
    setLoading(true);
    try {
      const r = await api.get('/staff-travel', { params: { company_id: companyId, period, include_all: showAll } });
      if (loadToken.current === my) { setData(r.data); setEdits({}); }
    } catch (e) { if (loadToken.current === my) toast.error(e.response?.data?.detail || 'Could not load travel accounts'); }
    finally { if (loadToken.current === my) setLoading(false); }
  };
  useEffect(() => { load(); }, [companyId, period, showAll]);
  const setField = (sid, patch) => setEdits(e => ({ ...e, [sid]: { ...(e[sid] || {}), ...patch } }));
  const valOf = (r, k) => edits[r.staff_id]?.[k] ?? (k === 'approved' ? r.approved : k === 'adjustment' ? r.adjustment : (r.adjustment_note || ''));
  const save = async (r) => {
    if (savingId) return;
    setSavingId(r.staff_id);
    try {
      await api.put('/staff-travel', { staff_id: r.staff_id, period, approved_amount: Number(valOf(r, 'approved') || 0), adjustment: Number(valOf(r, 'adjustment') || 0), adjustment_note: valOf(r, 'adjustment_note') || null });
      toast.success(`Saved ${r.name}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSavingId(''); }
  };
  const moveAdvance = async (adv, p) => {
    try { await api.patch(`/expenses/${adv.id}/travel-month`, { period: p || null }); toast.success(p ? `Counted in ${monthLabelOf(p)}` : 'Back to its own month'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const companyLabel = (companies.find(c => c.id === companyId) || {}).name || '';
  const rows = data?.rows || [];
  const t = data?.totals;
  return (
    <div className="pb-8" data-testid="travel-panel">
      <div className="mb-6">
        <div className="section-eyebrow mb-2">Employees</div>
        <h1 className="serif text-4xl">Travel Allowance</h1>
        <div className="text-sm text-muted mt-1" style={{ maxWidth: 820 }}>Advances are paid through the month as Travel Allowance expenses. After month end, type each person's <b>approved</b> travel allowance here. The account works out the rest: <b>balance = carried in + approved − advances</b>. A balance <b>due to staff</b> or an <b>advance with staff</b> carries into the next month automatically, so nothing is lost or counted twice. Settle it in cash, or with the salary (Payroll shows it on the sheet). Payroll never posts expenses: enter what you actually pay as a Travel Allowance expense and this account updates by itself.</div>
      </div>
      <div className="flex flex-wrap gap-2 items-center mb-4 no-print">
        {needsPicker && <select className="input" style={{ width: 170 }} value={companyId || ''} onChange={e => setCompanyId(e.target.value)}><option value="">— select company —</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
        <button className="tab btn-small" onClick={() => setPeriod(monthShift(period, -1))} aria-label="Previous month" data-testid="travel-prev"><ArrowLeft size={13} /></button>
        <input type="month" className="input" style={{ width: 160 }} value={period} onChange={e => e.target.value && setPeriod(e.target.value)} data-testid="travel-period" />
        <button className="tab btn-small" onClick={() => setPeriod(monthShift(period, 1))} aria-label="Next month" data-testid="travel-next"><ArrowRight size={13} /></button>
        <label className="text-xs text-muted inline-flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} data-testid="travel-show-all" />Show all staff</label>
        <button onClick={() => window.print()} disabled={!rows.length} className="btn-outline btn-small inline-flex items-center gap-1 ml-auto"><Printer size={14} />Print</button>
      </div>
      {t && <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 no-print" data-testid="travel-totals">
        <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Approved for {monthLabelOf(period)}</div><div className="serif text-2xl mono text-[color:var(--gold)]">{fmtINR(t.approved)}</div></div>
        <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Advances paid</div><div className="serif text-2xl mono">{fmtINR(t.advances)}</div></div>
        <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Due to staff</div><div className="serif text-2xl mono" style={{ color: '#d4a853' }}>{fmtINR(t.due_to_staff)}</div></div>
        <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Advance held by staff</div><div className="serif text-2xl mono" style={{ color: t.excess_with_staff ? '#e66767' : undefined }}>{fmtINR(t.excess_with_staff)}</div></div>
      </div>}
      {!companyId ? <div className="card card-dark text-center text-muted py-10">Select a company to see its travel allowance accounts.</div>
        : loading && !data ? <Loading />
        : rows.length === 0 ? <div className="card card-dark text-center text-muted py-10">No travel allowance recorded for {monthLabelOf(period)} yet. Advances appear here once a Travel Allowance payment is recorded against a staff member (Expenses → Staff payment → Travel Allowance). Tick "Show all staff" to enter an approved amount for someone without advances.</div>
        : (
          <div className="card card-dark overflow-x-auto print-area">
            <div className="print-header">
              <div className="print-company">{companyLabel || 'Travel Allowance'}</div>
              <div className="print-report-title">Travel Allowance Account — {monthLabelOf(period)}</div>
              <div className="print-meta">Approved {fmtINR(t.approved)} · Advances {fmtINR(t.advances)} · Due to staff {fmtINR(t.due_to_staff)} · Advance held by staff {fmtINR(t.excess_with_staff)} · Generated: {fmtGeneratedAt()}</div>
            </div>
            <table className="tbl" data-testid="travel-table">
              <thead><tr><th>Staff</th><th className="text-right" title="Balance brought forward from the earlier months">Carried in</th><th className="text-right" title="The total travel allowance approved for the month — typed in after month end">Approved (₹)</th><th className="text-right" title="Travel Allowance payments made in this month">Advances paid</th><th className="text-right" title="Cash returned by the staff member (+) or other correction; recoveries through payroll show here too">Adjust. (₹)</th><th className="text-right">Balance</th><th className="no-print"></th></tr></thead>
              <tbody>
                {rows.map(r => {
                  const dirty = !!edits[r.staff_id];
                  const preview = (r.closing === null) ? null : Math.round((r.opening + Number(valOf(r, 'approved') || 0) - r.advances_total + Number(valOf(r, 'adjustment') || 0) + r.recovered) * 100) / 100;
                  return <React.Fragment key={r.staff_id}>
                    <tr data-testid={`travel-row-${r.staff_id}`}>
                      <td><b>{r.name}</b>{r.designation ? <div className="text-xs text-muted">{r.designation}</div> : null}{!r.has_record && r.advances_total > 0 && !r.before_start && <div className="text-xs" style={{ color: '#d4a853' }}>approved amount not entered</div>}{!r.before_start && r.received_to_date !== undefined && <div className="text-xs text-muted" title="Everything paid to this person so far, and everything approved so far (up to and including this month). The balance is the difference." data-testid={`travel-todate-${r.staff_id}`}>till {monthLabelOf(period)}: received {fmtINR(r.received_to_date)} · approved {fmtINR(r.approved_to_date)}</div>}</td>
                      <td className="mono text-right">{r.before_start ? '—' : fmtINR(r.opening)}</td>
                      <td className="text-right">{canEdit && !r.before_start ? <input className="input mono" style={{ width: 110, textAlign: 'right' }} type="number" min="0" value={valOf(r, 'approved')} onChange={e => setField(r.staff_id, { approved: e.target.value })} data-testid={`travel-approved-${r.staff_id}`} /> : <span className="mono">{fmtINR(r.approved)}</span>}</td>
                      <td className="mono text-right"><button type="button" className="tab btn-small" onClick={() => setOpenId(openId === r.staff_id ? '' : r.staff_id)} title="Show the individual payments" data-testid={`travel-adv-toggle-${r.staff_id}`}>{fmtINR(r.advances_total)} · {r.advances.length}</button></td>
                      <td className="text-right">{canEdit && !r.before_start ? <div className="inline-flex flex-col items-end gap-1"><input className="input mono" style={{ width: 90, textAlign: 'right' }} type="number" value={valOf(r, 'adjustment')} onChange={e => setField(r.staff_id, { adjustment: e.target.value })} title="Cash returned by the staff member (+) or other correction" data-testid={`travel-adj-${r.staff_id}`} />{(Number(valOf(r, 'adjustment')) !== 0 || r.adjustment_note) && <input className="input no-print" style={{ width: 130 }} placeholder="Note" value={valOf(r, 'adjustment_note')} onChange={e => setField(r.staff_id, { adjustment_note: e.target.value })} />}</div> : <span className="mono">{(r.adjustment + r.recovered) ? fmtINR(r.adjustment + r.recovered) : '—'}</span>}{r.recovered > 0 && <div className="text-xs text-muted">{fmtINR(r.recovered)} via payroll</div>}</td>
                      <td className="text-right"><TravelBalanceChip value={dirty ? preview : r.closing} startsAt={r.ledger_starts} />{dirty && <div className="text-xs text-muted">unsaved</div>}</td>
                      <td className="text-right whitespace-nowrap no-print">
                        {canEdit && dirty && <button onClick={() => save(r)} disabled={savingId === r.staff_id} className="btn-gold btn-small mr-1" data-testid={`travel-save-${r.staff_id}`}>{savingId === r.staff_id ? 'Saving…' : 'Save'}</button>}
                        <button onClick={() => setLedgerFor(r.staff_id)} className="tab btn-small" data-testid={`travel-history-${r.staff_id}`}>History</button>
                      </td>
                    </tr>
                    {openId === r.staff_id && <tr className="no-print"><td colSpan={7} style={{ background: 'rgba(236,230,216,0.03)' }}>
                      {r.advances.length === 0 ? <div className="text-xs text-muted py-1">No advances were paid for {monthLabelOf(period)}.</div> : r.advances.map(a => (
                        <div key={a.id} className="flex flex-wrap items-center gap-3 py-1 text-sm" data-testid={`travel-advance-${a.id}`}>
                          <span className="mono">{fmtDate(a.expense_date)}</span><span className="mono">{fmtINR(a.amount)}</span>
                          <span className="text-xs text-muted flex-1">{a.description || ''}{a.voucher_number ? ` · V${a.voucher_number}` : ''}</span>
                          <AuditStatusChip status={a.audit_status} />
                          {canEdit && <span className="inline-flex items-center gap-1 text-xs text-muted">counted in
                            <input type="month" className="input" style={{ width: 140, padding: '2px 6px' }} value={a.payroll_period || (a.expense_date || '').slice(0, 7)} onChange={e => e.target.value && e.target.value !== (a.payroll_period || (a.expense_date || '').slice(0, 7)) && moveAdvance(a, e.target.value)} />
                            {a.payroll_period && <button className="tab btn-small" onClick={() => moveAdvance(a, null)} title="Count it in the month of its own date again">Reset</button>}
                          </span>}
                        </div>))}
                    </td></tr>}
                  </React.Fragment>;
                })}
              </tbody>
              <tfoot><tr className="font-semibold"><td>Total</td><td></td><td className="mono text-right">{fmtINR(t.approved)}</td><td className="mono text-right">{fmtINR(t.advances)}</td><td></td><td className="text-right text-xs">Due {fmtINR(t.due_to_staff)} · Held {fmtINR(t.excess_with_staff)}</td><td className="no-print"></td></tr></tfoot>
            </table>
          </div>)}
      {ledgerFor && <TravelLedgerModal staffId={ledgerFor} onClose={() => setLedgerFor('')} />}
    </div>
  );
}

// What is actually paid with the salary once the month's travel is settled:
// salary - any excess travel advance (never below zero) + any travel still owed.
const payrollPayable = (e) => {
  const net = Number(e.net_amount) || 0, bal = Number(e.travel_balance) || 0;
  if (!e.travel_settle) return Math.round(net * 100) / 100;
  return Math.round((net + Math.max(bal, -net)) * 100) / 100;
};
function PayrollTravelModal({ entry, staffName, onClose, onSaved }) {
  const [settle, setSettle] = useState(!!entry.travel_settle);
  const [saving, setSaving] = useState(false);
  const bal = Number(entry.travel_balance) || 0, net = Number(entry.net_amount) || 0;
  const applied = Math.max(bal, -net);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.put(`/payroll/${entry.id}/travel-settle`, { settle });
      toast.success(settle ? 'Travel will be settled with this salary' : 'Travel will not be settled with this salary');
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title={`Travel allowance — ${staffName} · ${entry.period}`} maxW={560}>
      <div className="text-xs text-muted mb-3">The account comes from Staff → Travel: the approved amount you typed for {entry.period}, the advances already paid, and whatever was carried in from earlier months. Recalculate payroll to refresh these figures.</div>
      <div className="p-3 rounded surface-2 text-sm mb-3" data-testid="travel-summary">
        <div className="flex justify-between"><span>Approved for {entry.period}</span><span className="mono">{fmtINR(entry.travel_actual)}</span></div>
        <div className="flex justify-between"><span>Advances paid for {entry.period}</span><span className="mono">−{fmtINR(entry.travel_advances)}</span></div>
        <div className="flex justify-between" style={{ fontWeight: 600 }}><span>{bal > 0 ? 'Account balance — due to staff (incl. carried forward)' : bal < 0 ? 'Account balance — excess advance with staff (incl. carried forward)' : 'Account balance — settled'}</span><span className="mono">{fmtINR(Math.abs(bal))}</span></div>
      </div>
      <label className="inline-flex items-start gap-2 cursor-pointer text-sm"><input type="checkbox" className="mt-1" checked={settle} disabled={entry.status !== 'draft'} onChange={e => setSettle(e.target.checked)} data-testid="travel-settle" />
        <span><b>Settle this balance with this salary</b><br /><span className="text-xs text-muted">{bal > 0 ? `${fmtINR(bal)} is shown on the payroll sheet as payable to the staff member along with the salary. Nothing is posted to the books; when you pay it, enter it as a Travel Allowance expense and the account updates by itself.` : bal < 0 ? `${fmtINR(Math.min(-bal, net))} is shown as taken out of the salary on the payroll sheet (it counts as recovered on their travel account; the salary you pay is entered as an ordinary Expense)${-bal > net ? `; the remaining ${fmtINR(-bal - net)} stays on their travel account for later` : ''}.` : 'Nothing to settle.'} Leave it unticked to keep the balance carried forward instead.</span></span></label>
      {settle && <div className="flex justify-between mt-3 text-sm"><span>Paid with this salary</span><span className="mono" style={{ fontWeight: 600 }}>{fmtINR(Math.round((net + applied) * 100) / 100)}</span></div>}
      <div className="mt-5 flex justify-end gap-2"><button onClick={onClose} className="btn-outline btn-small">Close</button>{entry.status === 'draft' && <button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="travel-save">{saving ? 'Saving…' : 'Save'}</button>}</div>
    </Modal>
  );
}

// A finalized payroll entry whose month's attendance was corrected afterwards (Master Admin): what it was vs what it would be now.
function PayrollAttendanceChangeModal({ entry, staffName, companyId, onClose, onDone }) {
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/payroll/${entry.id}/recalc-preview`).then(r => setPv(r.data)).catch(e => { toast.error(e.response?.data?.detail || 'Could not load the comparison'); onClose(); }); }, []);
  const apply = async () => {
    if (busy || !pv) return;
    if (!window.confirm(`Un-finalize ${staffName}'s ${entry.period} payroll and recalculate it from the corrected attendance?\n\nThe entry goes back to draft with the new figures (nothing in the books changes). You then finalize it again.`)) return;
    setBusy(true);
    try {
      await api.post(`/payroll/${entry.id}/unfinalize`);
      await api.post('/payroll/generate', { company_id: companyId, period: entry.period });
      toast.success('Back to draft with the corrected figures — check it, then finalize again');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };
  const Row = ({ label, a, b, money }) => <tr><td>{label}</td><td className="mono text-right">{money ? fmtINR(a) : a}</td><td className="mono text-right" style={a !== b ? { color: '#d4a853', fontWeight: 600 } : undefined}>{money ? fmtINR(b) : b}</td></tr>;
  return (
    <Modal open={true} onClose={onClose} title={`Attendance changed after payroll — ${staffName} · ${entry.period}`} maxW={560}>
      {!pv ? <Loading /> : (<>
        <div className="text-xs text-muted mb-3">The attendance of this month was corrected after payroll was finalized{pv.attendance_changed_at ? ` (${fmtDate(String(pv.attendance_changed_at).slice(0, 10))})` : ''}. The finalized payroll figures have <b>not</b> been changed.</div>
        <table className="tbl" data-testid="attchg-table"><thead><tr><th></th><th className="text-right">Finalized</th><th className="text-right">If calculated now</th></tr></thead><tbody>
          <Row label="Days present" a={pv.finalized.present} b={pv.now.present} /><Row label="Half-days" a={pv.finalized.half_day} b={pv.now.half_day} /><Row label="Days absent" a={pv.finalized.absent} b={pv.now.absent} /><Row label="Leave" a={pv.finalized.leave} b={pv.now.leave} />
          <Row label="Pay for the month" a={pv.finalized.gross} b={pv.now.gross} money /><Row label="Absence cut" a={pv.finalized.absence_cut} b={pv.now.absence_cut} money /><Row label="Other deduction" a={pv.finalized.typed_deduction} b={pv.now.typed_deduction} money />
          <tr style={{ fontWeight: 600 }}><td>Net salary</td><td className="mono text-right">{fmtINR(pv.finalized.net)}</td><td className="mono text-right" style={pv.difference ? { color: '#d4a853' } : undefined}>{fmtINR(pv.now.net)}</td></tr></tbody></table>
        <div className="mt-3 text-sm" data-testid="attchg-diff">{Math.abs(pv.difference) < 0.005 ? 'The corrected attendance gives the same salary — nothing to change.' : pv.difference > 0 ? `Corrected attendance means ${fmtINR(pv.difference)} MORE salary is due than was finalized.` : `Corrected attendance means ${fmtINR(-pv.difference)} LESS salary is due than was finalized.`}</div>
        {!pv.can_unfinalize && <div className="mt-2 text-xs" style={{ color: '#e66767' }}>An older Salary Expense that payroll posted for this entry is already “{pv.expense_status}” in the Audit Queue, so it can't be un-finalized yet. Set that expense back to pending review first.</div>}
      </>)}
      <div className="mt-5 flex justify-end gap-2"><button onClick={onClose} className="btn-outline btn-small">Close</button>{pv && Math.abs(pv.difference) >= 0.005 && <button onClick={apply} disabled={busy || !pv.can_unfinalize} className="btn-gold btn-small" data-testid="attchg-apply">{busy ? 'Working…' : 'Un-finalize & recalculate'}</button>}</div>
    </Modal>
  );
}

function PayrollPanel() {
  const { user } = useAuth();
  const canEdit = ['admin', 'accountant', 'master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const [companyId, setCompanyId] = useState(needsPicker ? '' : undefined);
  const [period, setPeriod] = useState(currentPeriod());
  const [entries, setEntries] = useState([]);
  const [staffById, setStaffById] = useState({});
  const [generating, setGenerating] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [finalizingId, setFinalizingId] = useState('');
  const [bulkFinalizing, setBulkFinalizing] = useState(false);
  const [unfinalizing, setUnfinalizing] = useState(false);
  const [travelFor, setTravelFor] = useState(null);
  const [attChgFor, setAttChgFor] = useState(null);
  const [slip, setSlip] = useState(null);
  const [rounding, setRounding] = useState('none');
  const [savingRound, setSavingRound] = useState(false);

  useEffect(() => { api.get('/staff').then(r => setStaffById(Object.fromEntries(r.data.map(s => [s.id, s])))); }, []);
  useEffect(() => {
    if (!companyId) { setRounding('none'); return; }
    api.get('/payroll/settings', { params: { company_id: companyId } }).then(r => setRounding(r.data.rounding || 'none')).catch(() => {});
  }, [companyId]);
  const changeRounding = async (mode) => {
    if (savingRound || !companyId || mode === rounding) return;
    setSavingRound(true);
    try {
      const r = await api.put('/payroll/settings/rounding', { company_id: companyId, mode });
      setRounding(r.data.rounding);
      toast.success(r.data.drafts_updated ? `Rounding saved — ${r.data.drafts_updated} draft entr${r.data.drafts_updated === 1 ? 'y' : 'ies'} updated` : 'Rounding saved');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSavingRound(false); }
  };
  useEffect(() => { if (!needsPicker && companies.length === 1) setCompanyId(companies[0].id); }, [companies, needsPicker]);

  const load = async () => {
    if (!companyId || !period) { setEntries([]); return; }
    try { setEntries((await api.get('/payroll', { params: { company_id: companyId, period } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load payroll'); }
  };
  useEffect(() => { load(); }, [companyId, period]);

  const generate = async () => {
    if (generating || !companyId) return;
        setGenerating(true);
    try {
      const r = await api.post('/payroll/generate', { company_id: companyId, period });
      setEntries(r.data.entries);
      toast.success(`Draft ready for ${r.data.entries.length} staff member(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setGenerating(false); }
  };

  const saveDeduction = async (entry, deduction_amount, deduction_notes) => {
    setSavingId(entry.id);
    try {
      const r = await api.patch(`/payroll/${entry.id}`, { deduction_amount: Number(deduction_amount || 0), deduction_notes: deduction_notes || null });
      setEntries(es => es.map(e => e.id === entry.id ? r.data : e));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSavingId(''); }
  };

  const finalizeOne = async (entry) => {
    const nm = staffById[entry.staff_id]?.name || 'this staff member';
    if (!window.confirm(`Finalize ${nm}'s ${period} payroll (${fmtINR(payrollPayable(entry))})?\n\nThis only locks the figures as the payroll record. Nothing is posted to the books — what you actually pay is entered as an ordinary Expense.`)) return;
    setFinalizingId(entry.id);
    try {
      const r = await api.post(`/payroll/${entry.id}/finalize`);
      setEntries(es => es.map(e => e.id === entry.id ? r.data : e));
      toast.success('Finalized — payroll record locked');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setFinalizingId(''); }
  };

  const finalizeAllDraft = async () => {
    const draftIds = entries.filter(e => e.status === 'draft').map(e => e.id);
    if (!draftIds.length) return;
    if (!window.confirm(`Finalize all ${draftIds.length} draft payroll entries for ${period}? This only locks the payroll record; nothing is posted to the books.`)) return;
    setBulkFinalizing(true);
    try {
      const r = await api.post('/payroll/finalize-bulk', { ids: draftIds });
      await load();
      if (r.data.failed.length) toast.warning(`Finalized ${r.data.ok.length}, ${r.data.failed.length} failed — ${r.data.failed[0].error}${r.data.failed.length > 1 ? ` (+${r.data.failed.length - 1} more)` : ''}`);
      else toast.success(`Finalized ${r.data.ok.length}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBulkFinalizing(false); }
  };

  // Undo a finalize: back to draft. (Entries finalized before payroll became reference-only may still own an auto-posted Salary Expense; that is removed while it is still waiting in the Audit Queue.)
  const unfinalize = async (ids, label) => {
    if (!ids.length || unfinalizing) return;
    if (!window.confirm(`Un-finalize ${label}? Each goes back to draft so you can recalculate and finalize again. If an entry still owns an older auto-posted Salary Expense that is waiting in the Audit Queue, that expense is removed; one already matched/final is refused.`)) return;
    setUnfinalizing(true);
    try {
      const r = await api.post('/payroll/unfinalize-bulk', { ids });
      await load();
      if (r.data.failed.length) toast.warning(`Un-finalized ${r.data.ok.length}, ${r.data.failed.length} could not be — ${r.data.failed[0].error}`);
      else toast.success(`Un-finalized ${r.data.ok.length}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setUnfinalizing(false); }
  };

  const totals = entries.reduce((a, e) => ({ gross: a.gross + e.gross_amount, absence: a.absence + (e.absence_deduction || 0), deduction: a.deduction + e.deduction_amount, net: a.net + payrollPayable(e) }), { gross: 0, absence: 0, deduction: 0, net: 0 });
  const draftCount = entries.filter(e => e.status === 'draft').length;
  const finalIds = entries.filter(e => e.status === 'finalized').map(e => e.id);
  const companyLabel = (companies.find(c => c.id === companyId) || {}).name || '';

  return (
    <div className="pb-8">
      <div className="mb-6">
        <div className="section-eyebrow mb-2">Employees</div>
        <h1 className="serif text-4xl">Payroll</h1>
        <div className="text-sm text-muted mt-1">A reference sheet: it works out each person's pay from this month's Attendance, and finalizing locks the figures. It is kept apart from the books — nothing here is posted as an expense. What you actually pay is entered as ordinary Expenses (and shows under Staff → Salary by month).</div>
        <div className="text-xs text-muted mt-2" data-testid="payroll-rules"><b>How pay is worked out.</b> <b>Monthly (regular) staff</b> get the full salary; holidays, weekly offs and approved Leave are always paid. Only days marked <b>Absent</b> are cut (Half-day = half a day), at <b>monthly salary ÷ 26</b> per day, in every month. A person with no Present / Half-day / Leave at all in the month is cut for the whole month. <b>Daily-wage staff</b> get rate × days Present (Half-day = 0.5); a holiday is unpaid unless you switched it to paid. Anyone not on attendance gets the full salary. Advances and other recoveries go in "Other deduction". <b>Travel:</b> advances are paid through the month as Travel Allowance expenses; after month end type the approved amount under <b>Staff → Travel</b>. Click <b>Travel</b> on a row here to settle that account's balance with the salary (paid if due to staff, recovered if they hold excess advance) — or leave it carried forward. It is shown on the sheet only; record the real payment as an Expense.</div>
      </div>
      <div className="flex flex-wrap gap-2 items-center mb-4">
        {needsPicker && <select className="input" style={{ width: 170 }} value={companyId || ''} onChange={e => setCompanyId(e.target.value)}>
          <option value="">— select company —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>}
        <input type="month" className="input" style={{ width: 160 }} value={period} onChange={e => setPeriod(e.target.value)} />
        {canEdit && companyId && <label className="inline-flex items-center gap-1 text-xs text-muted" title="Rounds each person's salary after cuts. Draft entries follow at once; finalized entries are never changed.">Round net pay
          <select className="input" style={{ width: 150 }} value={rounding} disabled={savingRound} onChange={e => changeRounding(e.target.value)} data-testid="payroll-rounding">
            <option value="none">No rounding</option><option value="rupee">Nearest ₹1</option><option value="ten">Nearest ₹10</option>
          </select></label>}
        {canEdit && companyId && <button onClick={generate} disabled={generating} className="btn-outline btn-small inline-flex items-center gap-1"><RefreshCw size={14} />{generating ? 'Calculating…' : (entries.length ? 'Recalculate from attendance' : 'Calculate payroll')}</button>}
        {entries.length > 0 && <button onClick={() => printLandscape()} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="payroll-print"><Printer size={14} />Print salary sheet</button>}
        {canEdit && finalIds.length > 0 && <button onClick={() => unfinalize(finalIds, `all ${finalIds.length} finalized entries for ${period}`)} disabled={unfinalizing} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="payroll-unfinalize-all">{unfinalizing ? 'Working…' : `Un-finalize all (${finalIds.length})`}</button>}
        {canEdit && draftCount > 0 && <button onClick={() => finalizeAllDraft()} disabled={bulkFinalizing} className="btn-gold btn-small inline-flex items-center gap-1"><CheckCircle size={14} />{bulkFinalizing ? 'Finalizing…' : `Finalize All Draft (${draftCount})`}</button>}
      </div>
      {entries.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Total pay</div><div className="serif text-2xl text-[color:var(--gold)] mono">{fmtINR(totals.gross)}</div></div>
          <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Cuts (absence {fmtINR(totals.absence)} + other {fmtINR(totals.deduction)})</div><div className="serif text-2xl mono">{fmtINR(totals.absence + totals.deduction)}</div></div>
          <div className="p-3 rounded surface-2"><div className="text-xs text-muted">Net payable</div><div className="serif text-2xl text-[color:var(--gold)] mono">{fmtINR(totals.net)}</div></div>
        </div>
      )}
      {!companyId ? (
        <div className="card card-dark text-center text-muted py-10">Select a company to generate payroll.</div>
      ) : entries.length === 0 ? (
        <div className="card card-dark text-center text-muted py-10">No payroll for {period} yet — click "Calculate payroll" to work it out from Attendance. You can recalculate any time before finalizing (finalized entries are never touched, typed deductions are kept).</div>
      ) : (
        <div className={"card card-dark overflow-x-auto print-area" + (slip ? " no-print" : "")} data-testid="payroll-sheet">
          <div className="print-header">
            <div className="print-company">{companyLabel || 'Payroll'}</div>
            <div className="print-report-title">Salary Sheet — {period}</div>
            <div className="print-meta">Pay {fmtINR(totals.gross)} · Absence cut {fmtINR(totals.absence)} · Other deductions {fmtINR(totals.deduction)} · Net payable {fmtINR(totals.net)} · {entries.length} staff · Generated: {fmtGeneratedAt()}</div>
          </div>
          <table className="tbl payroll-tbl">
            <thead><tr><th>Staff</th><th className="text-right" title="Days present">Present</th><th className="text-right" title="Half-days">Half</th><th className="text-right" title="Absent (unapproved) days">Absent</th><th className="text-right" title="Approved leave days (paid)">Leave</th>{entries.some(e => e.days_paid_holiday > 0) && <th className="text-right" title="Paid holidays added for daily-wage staff">Paid hol.</th>}<th className="text-right" title="Salary before any cut">Pay</th><th className="text-right" title="Cut for unapproved absence: monthly salary ÷ 26 × days absent">Absence cut</th><th>Other deduction</th><th className="text-right" title="Travel allowance account settled with this salary: + is paid to the staff member, − is recovered from the salary. Set it with the Travel button; the account itself is under Staff → Travel.">Travel ±</th><th className="text-right" title="Salary after cuts, plus or minus the travel settlement">Net</th><th>Status</th><th className="print-only" style={{minWidth:120}}>Signature</th><th className="no-print"></th></tr></thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td>{staffById[e.staff_id]?.name || '—'}{staffById[e.staff_id]?.attendance_tracked !== false && staffById[e.staff_id]?.salary_type !== 'per_day' && (Number(e.days_present) + Number(e.days_half_day) + Number(e.days_leave)) === 0 && <div className="text-xs" style={{ color: '#e66767' }} title="Nobody was marked Present, Half-day or on approved Leave this month, so the whole month is cut" data-testid={`payroll-noatt-${e.id}`}>no attendance marked — month cut</div>}</td>
                  {staffById[e.staff_id]?.attendance_tracked === false ? (
                    <td className="text-right text-muted text-xs" colSpan={4 + (entries.some(x => x.days_paid_holiday > 0) ? 1 : 0)} title="Paid by payroll only — not marked in the Attendance register">No attendance · full salary</td>
                  ) : (<>
                    <td className="mono text-right">{e.days_present}</td>
                    <td className="mono text-right">{e.days_half_day}</td>
                    <td className="mono text-right">{e.days_absent}</td>
                    <td className="mono text-right">{e.days_leave}</td>
                    {entries.some(x => x.days_paid_holiday > 0) && <td className="mono text-right">{e.days_paid_holiday || 0}</td>}
                  </>)}
                  <td className="mono text-right">{fmtINR(e.gross_amount)}</td>
                  <td className="mono text-right" title={e.absence_deduction > 0 ? `${e.days_deducted} day(s) × ${fmtINR(e.gross_amount)} ÷ 26` : undefined} data-testid={`payroll-cut-${e.id}`}>{e.absence_deduction > 0 ? <span style={{ color: '#e66767' }}>−{fmtINR(e.absence_deduction)}<span className="text-xs text-muted"> ({e.days_deducted} d)</span></span> : <span className="text-muted">—</span>}</td>
                  <td>
                    {e.status === 'draft' ? (
                      <>
                      <span className="print-only-inline">{fmtINR(e.deduction_amount)}{e.deduction_notes ? ` — ${e.deduction_notes}` : ''}</span>
                      <div className="flex items-center gap-1 no-print">
                        <input className="input mono" style={{ width: 90 }} type="number" min="0" defaultValue={e.deduction_amount} disabled={savingId === e.id}
                          onBlur={ev => { const v = ev.target.value; if (Number(v) !== e.deduction_amount) saveDeduction(e, v, e.deduction_notes); }} />
                        <input className="input" style={{ width: 120 }} placeholder="Note" defaultValue={e.deduction_notes || ''} disabled={savingId === e.id}
                          onBlur={ev => { const v = ev.target.value; if (v !== (e.deduction_notes || '')) saveDeduction(e, e.deduction_amount, v); }} />
                      </div>
                      </>
                    ) : <span className="text-xs">{fmtINR(e.deduction_amount)}{e.deduction_notes ? ` — ${e.deduction_notes}` : ''}</span>}
                  </td>
                  <td className="mono text-right" data-testid={`payroll-travel-${e.id}`}>
                    {(e.travel_settle && Number(e.travel_balance) !== 0) ? <span title="Travel account balance settled with this salary (approved − advances, plus anything carried forward)" style={{ color: e.travel_balance < 0 ? '#e66767' : '#34aa6e' }}>{e.travel_balance > 0 ? '+' : '−'}{fmtINR(Math.abs(Math.max(e.travel_balance, -e.net_amount)))}</span> : <span className="text-muted" title={Number(e.travel_balance) !== 0 ? 'Not being settled — the balance carries forward' : undefined}>{Number(e.travel_balance) !== 0 ? 'carried' : '—'}</span>}
                    {canEdit && e.status === 'draft' && <button onClick={() => setTravelFor(e)} className="tab btn-small ml-1 no-print" data-testid={`payroll-travel-btn-${e.id}`}>Travel</button>}
                  </td>
                  <td className="mono text-right text-[color:var(--gold)]">{fmtINR(payrollPayable(e))}{Number(e.rounding_adjustment) !== 0 && <div className="text-xs text-muted" title="Rounded to the nearest rupee / ten, as chosen under Round net pay" data-testid={`payroll-round-${e.id}`}>rounded {Number(e.rounding_adjustment) > 0 ? '+' : '−'}{fmtINR(Math.abs(Number(e.rounding_adjustment)))}</div>}</td>
                  <td><span className={"badge " + (e.status === 'finalized' ? 'status-running' : 'status-planning')}>{e.status}</span>{e.status === 'finalized' && e.attendance_changed_at && <button type="button" className="badge ml-1 cursor-pointer" style={{ color: '#d4a853', borderColor: 'rgba(212,168,83,0.55)', background: 'transparent' }} onClick={() => setAttChgFor(e)} title="Attendance of this month was corrected after payroll was finalized — click to compare" data-testid={`payroll-attchg-${e.id}`}>attendance changed</button>}</td>
                  <td className="print-only"></td>
                  <td className="text-right whitespace-nowrap no-print">
                    <button onClick={() => setSlip(e)} className="tab btn-small mr-1 inline-flex items-center gap-1" title="Print this person's payslip" data-testid={`payslip-${e.id}`}><Printer size={12} />Payslip</button>
                    {canEdit && e.status === 'draft' && <button onClick={() => finalizeOne(e)} disabled={finalizingId === e.id} className="tab btn-small">{finalizingId === e.id ? 'Finalizing…' : 'Finalize'}</button>}
                    {canEdit && e.status === 'finalized' && <button onClick={() => unfinalize([e.id], `${staffById[e.staff_id]?.name || 'this entry'}'s ${period} payroll`)} disabled={unfinalizing} className="tab btn-small" data-testid={`payroll-unfinalize-${e.id}`}>Un-finalize</button>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={{ fontWeight: 600 }}>Total</td><td colSpan={4 + (entries.some(x => x.days_paid_holiday > 0) ? 1 : 0)}></td>
              <td className="mono text-right" style={{ fontWeight: 600 }}>{fmtINR(totals.gross)}</td>
              <td className="mono text-right" style={{ fontWeight: 600 }}>{fmtINR(totals.absence)}</td>
              <td className="mono" style={{ fontWeight: 600 }}>{fmtINR(totals.deduction)}</td>
              <td></td>
              <td className="mono text-right" style={{ fontWeight: 600 }}>{fmtINR(totals.net)}</td>
              <td colSpan={2}></td>
            </tr></tfoot>
          </table>
        </div>
      )}
      {attChgFor && <PayrollAttendanceChangeModal entry={attChgFor} staffName={staffById[attChgFor.staff_id]?.name || 'Staff'} companyId={companyId} onClose={() => setAttChgFor(null)} onDone={() => { setAttChgFor(null); load(); }} />}
      {travelFor && <PayrollTravelModal entry={travelFor} staffName={staffById[travelFor.staff_id]?.name || 'Staff'} onClose={() => setTravelFor(null)} onSaved={(row) => { setEntries(es => es.map(x => x.id === row.id ? row : x)); setTravelFor(null); }} />}
      {slip && <Modal open={true} onClose={() => setSlip(null)} title="Payslip" maxW={560}>
        <div className="print-area" data-testid="payslip">
          <div className="print-header">
            <div className="print-company">{companyLabel}</div>
            <div className="print-report-title">Payslip — {slip.period}</div>
          </div>
          <div className="serif text-2xl mb-1 no-print">{companyLabel}</div>
          <div className="text-xs text-muted mb-3 no-print">Payslip — {slip.period}</div>
          <div className="mb-3"><b>{staffById[slip.staff_id]?.name || '—'}</b>{staffById[slip.staff_id]?.designation ? <span className="text-muted"> · {staffById[slip.staff_id].designation}</span> : null}</div>
          <table className="tbl" style={{ marginBottom: 12 }}>
            <tbody>
              {staffById[slip.staff_id]?.attendance_tracked === false
                ? <tr><td>Basis</td><td className="text-right">Fixed monthly salary (no attendance)</td></tr>
                : <tr><td>Present / half-day / absent / leave (days)</td><td className="mono text-right">{slip.days_present} / {slip.days_half_day} / {slip.days_absent} / {slip.days_leave}</td></tr>}
              {slip.days_paid_holiday > 0 && <tr><td>Paid holidays (added to the days above)</td><td className="mono text-right">{slip.days_paid_holiday}</td></tr>}
              <tr><td>Pay for the month</td><td className="mono text-right">{fmtINR(slip.gross_amount)}</td></tr>
              {slip.absence_deduction > 0 && <tr><td>Absence cut ({slip.days_deducted} day{slip.days_deducted === 1 ? '' : 's'} at {fmtINR(Math.round(Number(slip.gross_amount) / 26 * 100) / 100)} a day, salary ÷ 26)</td><td className="mono text-right">−{fmtINR(slip.absence_deduction)}</td></tr>}
              <tr><td>Other deductions{slip.deduction_notes ? ` (${slip.deduction_notes})` : ''}</td><td className="mono text-right">{fmtINR(slip.deduction_amount)}</td></tr>
              {Number(slip.rounding_adjustment) !== 0 && <tr><td>Rounding</td><td className="mono text-right">{Number(slip.rounding_adjustment) > 0 ? '+' : '−'}{fmtINR(Math.abs(Number(slip.rounding_adjustment)))}</td></tr>}
              <tr><td>Salary after cuts{Number(slip.rounding_adjustment) !== 0 ? ' (rounded)' : ''}</td><td className="mono text-right">{fmtINR(slip.net_amount)}</td></tr>
              {slip.travel_settle && Number(slip.travel_balance) !== 0 && <>
                <tr><td>Travel allowance approved for the month</td><td className="mono text-right">{fmtINR(slip.travel_actual)}</td></tr>
                <tr><td>Travel advances already paid</td><td className="mono text-right">−{fmtINR(slip.travel_advances)}</td></tr>
                <tr><td>{slip.travel_balance < 0 ? 'Excess travel advance recovered (incl. carried forward)' : 'Travel allowance still due, paid now (incl. carried forward)'}</td><td className="mono text-right">{slip.travel_balance < 0 ? '−' : '+'}{fmtINR(Math.abs(Math.max(slip.travel_balance, -slip.net_amount)))}</td></tr>
              </>}
              <tr><td style={{ fontWeight: 600 }}>Net pay</td><td className="mono text-right" style={{ fontWeight: 600 }}>{fmtINR(payrollPayable(slip))}</td></tr>
            </tbody>
          </table>
          <div className="text-xs text-muted">Status: {slip.status === 'finalized' ? 'Finalized' : 'Draft — not yet finalized'}{slip.voucher_number ? ` · Voucher ${slip.voucher_number}` : ''}</div>
          <div className="flex justify-between" style={{ marginTop: 48 }}>
            <div style={{ borderTop: '1px solid currentColor', paddingTop: 4, width: '40%', fontSize: 11 }}>Employee signature</div>
            <div style={{ borderTop: '1px solid currentColor', paddingTop: 4, width: '40%', fontSize: 11 }}>Authorised signatory</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5 no-print">
          <button onClick={() => setSlip(null)} className="btn-outline btn-small">Close</button>
          <button onClick={() => window.print()} className="btn-gold btn-small inline-flex items-center gap-1"><Printer size={14} />Print payslip</button>
        </div>
      </Modal>}
    </div>
  );
}

// Staff detail: what payroll says each month's salary is, against the salary payments that were made FOR that month
// (a payment made in October may be for September: "counted in" re-assigns it; nothing else in the app changes).
function StaffSalaryAccount({ staffId }) {
  const { user } = useAuth();
  const canEdit = ['admin', 'accountant', 'master_admin'].includes(user.role);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState('');
  const load = async () => { try { setD((await api.get(`/staff/${staffId}/salary-account`)).data); } catch (e) { setD(false); } };
  useEffect(() => { load(); }, [staffId]);
  const move = async (pay, period) => {
    try { await api.patch(`/expenses/${pay.id}/for-month`, { period: period || null }); toast.success(period ? `Counted for ${monthLabelOf(period)}` : 'Back to its own month'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  if (!canEdit || d === false || d === null || (d.months || []).length === 0) return null;
  const chip = (m) => m.balance === null ? <span className="text-xs text-muted" title="No payroll was calculated for this month, so there is nothing to compare the payment with">no payroll</span>
    : Math.abs(m.balance) < 0.005 ? <span className="badge status-running">Settled</span>
    : m.balance > 0 ? <span className="badge status-completed" style={{ whiteSpace: 'nowrap' }} title="Payroll says this is still to be paid for the month">To pay {fmtINR(m.balance)}</span>
    : <span className="badge" style={{ color: '#e66767', borderColor: 'rgba(230,103,103,0.45)', whiteSpace: 'nowrap' }} title="More was paid for this month than payroll says. Check for a duplicate, or a payment meant for another month">Paid extra {fmtINR(-m.balance)}</span>;
  const t = d.totals;
  return (
    <div className="card card-dark overflow-x-auto mb-6 no-print" data-testid="salary-account">
      <div className="serif text-xl text-[color:var(--gold)] mb-1">Salary by month</div>
      <div className="text-xs text-muted mb-3" style={{ maxWidth: 760 }}>Payroll's salary for each month against the salary payments made <b>for</b> that month. A payment belongs to the month of its date unless you change it with <b>counted in</b> (e.g. paid in October, but it was September's salary). This only labels the payment for this comparison; no other total in the app changes. Payments still awaiting review are shown apart.</div>
      <table className="tbl" data-testid="salary-account-table">
        <thead><tr><th>Month</th><th className="text-right" title="Net salary from Payroll for the month">Payroll salary</th><th className="text-right" title="Reviewed salary payments counted for this month">Paid</th><th className="text-right" title="Salary payments for this month still waiting in the Audit Queue">Awaiting review</th><th className="text-right">Balance</th><th></th></tr></thead>
        <tbody>{d.months.map(m => <React.Fragment key={m.period}>
          <tr data-testid={`salary-month-${m.period}`}>
            <td>{monthLabelOf(m.period)}{m.payroll_status === 'draft' && <span className="text-xs text-muted"> · payroll draft</span>}</td>
            <td className="mono text-right">{m.payable === null ? '—' : fmtINR(m.payable)}</td>
            <td className="mono text-right">{fmtINR(m.paid_reviewed)}</td>
            <td className="mono text-right" style={m.paid_pending ? { color: '#d4a853' } : undefined}>{m.paid_pending ? fmtINR(m.paid_pending) : '—'}</td>
            <td className="text-right">{chip(m)}</td>
            <td className="text-right"><button type="button" className="tab btn-small" onClick={() => setOpen(open === m.period ? '' : m.period)} data-testid={`salary-toggle-${m.period}`}>{m.payments.length} payment{m.payments.length === 1 ? '' : 's'}</button></td>
          </tr>
          {open === m.period && <tr><td colSpan={6} style={{ background: 'rgba(236,230,216,0.03)' }}>
            {m.payments.length === 0 ? <div className="text-xs text-muted py-1">No salary payment is counted for this month.</div> : m.payments.map(a => (
              <div key={a.id} className="flex flex-wrap items-center gap-3 py-1 text-sm" data-testid={`salary-pay-${a.id}`}>
                <span className="mono">{fmtDate(a.date)}</span><span className="mono">{fmtINR(a.amount)}</span>
                <span className="text-xs text-muted flex-1">{a.description || ''}{a.voucher_number ? ` · V${a.voucher_number}` : ''}</span>
                <AuditStatusChip status={a.audit_status} />
                {a.from_payroll ? <span className="text-xs text-muted">posted by payroll</span> : <span className="inline-flex items-center gap-1 text-xs text-muted">counted in
                  <input type="month" className="input" style={{ width: 140, padding: '2px 6px' }} value={a.for_month || a.own_month} onChange={e => e.target.value && e.target.value !== (a.for_month || a.own_month) && move(a, e.target.value)} data-testid={`salary-formonth-${a.id}`} />
                  {a.for_month && <button className="tab btn-small" onClick={() => move(a, null)} title="Count it in the month of its own date again">Reset</button>}</span>}
              </div>))}
          </td></tr>}
        </React.Fragment>)}</tbody>
        <tfoot><tr className="font-semibold"><td>Months with payroll</td><td className="mono text-right">{fmtINR(t.payable)}</td><td className="mono text-right" colSpan={2}>{fmtINR(t.paid_for_payroll_months)}</td><td className="text-right text-xs" data-testid="salary-total-balance">{t.balance > 0.004 ? `To pay ${fmtINR(t.balance)}` : t.balance < -0.004 ? `Paid extra ${fmtINR(-t.balance)}` : 'Settled'}</td><td></td></tr></tfoot>
      </table>
      {t.paid_without_payroll > 0 && <div className="text-xs text-muted mt-2">{fmtINR(t.paid_without_payroll)} of salary payments are for months with no payroll calculated.</div>}
    </div>
  );
}

function StaffDetail() {
  const { id } = useParams();
  const [payments, setPayments] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const currentFY = fyFromDateStr(new Date().toISOString());
  const [fy, setFy] = useState('');
  const loadToken = useRef(0);
  const load = async () => {
    const myToken = ++loadToken.current;
    try {
      const [p, pr] = await Promise.all([
        api.get(`/staff/${id}/payments`, { params: fy ? { financial_year: fy } : {} }),
        api.get('/projects'),
      ]);
      if (loadToken.current !== myToken) return;
      setPayments(p.data); setProjects(pr.data);
    } catch (e) {
      if (loadToken.current !== myToken) return;
      setLoadError(e.response?.data?.detail || 'Could not load this staff member');
      toast.error(e.response?.data?.detail || 'Could not load this staff member');
    }
  };
  useEffect(() => { load(); }, [id, fy]);
  // User-requested: sortable headers, same convention as every other list.
  // Build error caught this in review: hooks can never be called after an
  // early return (or inside a conditional/IIFE below) -- called here,
  // unconditionally, before either early-return guard, with a safe []
  // fallback while `payments` is still null during the initial load.
  const { sorted: sortedPayments, sortKey, sortDir, toggleSort } = useSortableRows(payments?.entries || [], 'date', 'desc');
  if (loadError) return <div className="max-w-2xl mx-auto px-6 py-16 text-center"><div className="text-lg text-red-400 mb-2">{loadError}</div><Link to="/app/vendors" className="text-sm text-muted hover:text-[color:var(--gold)]">← Back to Staff & Vendor</Link></div>;
  if (!payments) return <Loading/>;

  const projectName = (pid) => pid ? (projects.find(p=>p.id===pid)?.project_name || '—') : 'Company';
  const fyOptions = distinctFYs([currentFY, ...payments.entries.map(e => fyFromDateStr(e.date))]);

  const exportStatementPDF = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(`Staff Payments — ${payments.staff.name}`, 14, 18);
    doc.setFontSize(9); doc.text(`Generated: ${fmtGeneratedAt()}`, 14, 26);
    if (fy) doc.text(`Financial Year: ${fy}`, 14, 32);
    autoTable(doc, {
      startY: fy ? 38 : 34,
      head: [['Date','Type','Project','Voucher No.','Description','Amount']],
      body: payments.entries.map(e => [fmtDate(e.date), STAFF_PAYMENT_TYPE_LABELS[e.payment_type]||e.payment_type, projectName(e.project_id), e.voucher_number||'—', (e.description||'').slice(0,40), fmtINRPdf(e.amount)]),
      styles: {fontSize:8}, headStyles: {fillColor:[201,168,76], textColor:[20,20,20]}
    });
    const finalY = doc.lastAutoTable?.finalY || 100;
    doc.setFontSize(10);
    doc.text(`Salary: ${fmtINRPdf(payments.totals.salary)}`, 14, finalY+10);
    doc.text(`Travel Allowance: ${fmtINRPdf(payments.totals.travel_allowance)}`, 14, finalY+16);
    doc.text(`Other: ${fmtINRPdf(payments.totals.other)}`, 14, finalY+22);
    doc.text(`Total: ${fmtINRPdf(payments.totals.total)}`, 14, finalY+28);
    doc.save(`staff-payments-${payments.staff.name}-${Date.now()}.pdf`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <Link to="/app/vendors" className="text-xs text-muted hover:text-[color:var(--gold)]">← Back to Staff & Vendor</Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mt-3 mb-6">
        <div>
          <div className="section-eyebrow mb-1">Staff</div>
          <h1 className="serif text-4xl">{payments.staff.name}</h1>
          <div className="text-sm text-muted mt-1">{payments.staff.designation || 'No designation'} · {payments.staff.phone || payments.staff.email || 'No contact info'}</div>
        </div>
        <div className="flex gap-2 items-center">
          <FySelect value={fy} onChange={setFy} years={fyOptions} style={{width:150}}/>
          <button onClick={()=>window.print()} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="staff-print"><Printer size={14}/>Print</button>
          <button onClick={exportStatementPDF} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Statement PDF</button>
        </div>
      </div>
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Salary Paid" value={fmtINR(payments.totals.salary)}/>
        <KpiCard label="Travel Allowance Paid" value={fmtINR(payments.totals.travel_allowance)}/>
        <KpiCard label="Other" value={fmtINR(payments.totals.other)}/>
        <KpiCard label="Total Paid" value={fmtINR(payments.totals.total)} gold hint={payments.pending_total>0 ? `${fmtINR(payments.pending_total)} pending review, not counted above` : undefined}/>
      </div>
      <StaffSalaryAccount staffId={id} />
      <div className="card card-dark overflow-x-auto print-area">
        <div className="print-header">
          <div className="print-company">{payments.staff.name}</div>
          <div className="print-report-title">Staff Payments{fy ? ` — ${fy}` : ''}</div>
          <div className="print-meta">Salary {fmtINR(payments.totals.salary)} · Travel Allowance {fmtINR(payments.totals.travel_allowance)} · Other {fmtINR(payments.totals.other)} · Total {fmtINR(payments.totals.total)}{payments.pending_total > 0 ? ` (pending review, not counted: ${fmtINR(payments.pending_total)})` : ''} · Generated: {fmtGeneratedAt()}</div>
        </div>
        <div className="serif text-xl text-[color:var(--gold)] mb-4 no-print">Payments</div>
        <table className="tbl">
          <thead><tr>
            <SortTh label="Date" sortKeyName="date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Type" sortKeyName="payment_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <th>Project</th>
            <SortTh label="Voucher No." sortKeyName="voucher_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <th>Description</th>
            <SortTh label="Amount" sortKeyName="amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
          </tr></thead>
          <tbody>
            {sortedPayments.length===0 && <tr><td colSpan={6} className="text-center text-muted py-6">No payments recorded yet — link an Expense to this staff member to see it here.</td></tr>}
            {sortedPayments.map(e => (
              <tr key={e.expense_id}>
                <td className="text-xs whitespace-nowrap">{fmtDate(e.date)}</td>
                <td className="text-xs">{STAFF_PAYMENT_TYPE_LABELS[e.payment_type]||e.payment_type}</td>
                <td className="text-xs">{e.project_id ? <Link to={`/app/projects/${e.project_id}`} className="hover:text-[color:var(--gold)]">{projectName(e.project_id)}</Link> : 'Company'}</td>
                <td className="mono text-xs">{e.voucher_number || '—'}</td>
                <td>{e.description || '—'}</td>
                <td className="mono text-right">{fmtINR(e.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============= FIXED ASSET REGISTER =============
const ASSET_CATEGORY_LABELS = {
  land_building: 'Land & Building',
  plant_machinery: 'Plant & Machinery',
  vehicles: 'Vehicles',
  furniture_fixtures: 'Furniture & Fixtures',
  office_equipment: 'Office Equipment',
  computers_it: 'Computers & IT Equipment',
  tools_equipment: 'Tools & Equipment',
  electrical_installations: 'Electrical Installations',
  others: 'Others',
};
const ASSET_DEPRECIATION_LABELS = { straight_line: 'Straight Line (SLM)', wdv: 'Written Down Value (WDV)', none: 'No Depreciation' };
const ASSET_STATUSES = ['active', 'under_maintenance', 'disposed', 'written_off'];
const ASSET_STATUS_STYLE = {
  active: { color: '#86efac', borderColor: 'rgba(134,239,172,0.35)' },
  under_maintenance: { color: '#fcd34d', borderColor: 'rgba(252,211,77,0.35)' },
  disposed: { color: 'rgba(236,230,216,0.55)', borderColor: 'rgba(236,230,216,0.16)' },
  written_off: { color: '#e66767', borderColor: 'rgba(230,103,103,0.4)' },
};
function AssetStatusBadge({ status }) {
  const s = ASSET_STATUS_STYLE[status] || ASSET_STATUS_STYLE.active;
  return <span className="badge" style={s}>{(status || 'active').replace(/_/g, ' ')}</span>;
}

// ---- Fixed Assets page: asset card helpers ----
const ASSET_STATUS_TONES = { active: 'green', under_maintenance: 'amber' }; // disposed / written off get no accent
// Share of the purchase cost already depreciated (0-100), null when there is no cost to compare against.
const assetDepreciatedPct = (a) => {
  const cost = Number(a?.purchase_cost || 0);
  if (!(cost > 0)) return null;
  return Math.max(0, Math.min(100, Math.round((Number(a.accumulated_depreciation || 0) / cost) * 100)));
};
// Years owned, one decimal ("2.3"), from the purchase date; null when unknown.
const assetAgeYears = (a, todayIso) => {
  if (!a?.purchase_date) return null;
  const d = (new Date(`${todayIso}T00:00:00`) - new Date(`${a.purchase_date}T00:00:00`)) / 86400000 / 365.25;
  return d < 0 ? 0 : Math.round(d * 10) / 10;
};
const sortAssetCards = (list, key) => {
  const c = [...list];
  const byName = (a, b) => (a.asset_name || '').localeCompare(b.asset_name || '', undefined, { numeric: true, sensitivity: 'base' });
  if (key === 'cost') c.sort((a, b) => Number(b.purchase_cost || 0) - Number(a.purchase_cost || 0) || byName(a, b));
  else if (key === 'book') c.sort((a, b) => Number(b.book_value || 0) - Number(a.book_value || 0) || byName(a, b));
  else if (key === 'newest') c.sort((a, b) => String(b.purchase_date || '').localeCompare(String(a.purchase_date || '')) || byName(a, b));
  else c.sort(byName);
  return c;
};
function AssetCards({ assets, canEdit, canDelete, needsPicker, companyName, onEdit, onDelete, todayIso }) {
  return (
    <div className="vendor-grid project-grid">
      {assets.map(a => {
        const tone = META_CHIP_TONES[ASSET_STATUS_TONES[a.status]] || null;
        const pct = assetDepreciatedPct(a);
        const age = assetAgeYears(a, todayIso);
        return (
          <div key={a.id} className="card card-dark vendor-card" style={{ '--vendor-accent': tone ? tone.color : 'var(--border-strong)' }} data-testid={`asset-card-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="serif text-lg leading-tight vendor-name" title={a.asset_name}>{a.asset_name}</div>
                <div className="text-xs text-muted mt-0.5"><span className="mono">{a.asset_code}</span>{a.serial_number ? ` · S/N ${a.serial_number}` : ''}</div>
              </div>
              <div className="flex-shrink-0"><AssetStatusBadge status={a.status}/></div>
            </div>
            <div className="flex flex-wrap gap-1 mt-3">
              <span className="text-xs" style={{ padding: '1px 8px', borderRadius: 20, border: '1px solid var(--border-subtle)' }}>{ASSET_CATEGORY_LABELS[a.category] || a.category}</span>
              {a.location && <span className="text-xs text-muted" style={{ padding: '1px 4px' }}>{a.location}</span>}
              {needsPicker && <span className="text-xs text-muted" style={{ padding: '1px 4px' }}>{companyName(a.company_id)}</span>}
              {a.source_expense_id && <span className="chip" style={{ fontSize: 10, background: 'rgba(217,164,65,0.14)', color: 'var(--accent-amber)' }} title="Auto-captured from a Company Expense marked Asset Purchase — review and complete, or mark as not a company asset" data-testid={`asset-card-fromexp-${a.id}`}>From Expense</span>}
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-muted">Net book value</div>
              <div className="serif mono text-2xl" style={{ color: 'var(--gold)' }} data-testid={`asset-card-book-${a.id}`}>{fmtINR(a.book_value)}</div>
              {pct !== null && <div className="vendor-bar" aria-hidden="true" title={`${pct}% of the purchase cost depreciated`}><div className="bar-red" style={{ width: `${pct}%` }}/></div>}
              <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                <div><div className="text-muted">Cost</div><div className="mono" data-testid={`asset-card-cost-${a.id}`}>{fmtINR(a.purchase_cost)}</div></div>
                <div><div className="text-muted">Depreciated{pct !== null ? ` (${pct}%)` : ''}</div><div className="mono text-red-400" data-testid={`asset-card-dep-${a.id}`}>{fmtINR(a.accumulated_depreciation)}</div></div>
              </div>
            </div>
            <div className="mt-3 text-xs text-muted" data-testid={`asset-card-bought-${a.id}`}>Bought {a.purchase_date ? fmtDate(a.purchase_date) : '—'}{age !== null ? ` · ${age} yr${age === 1 ? '' : 's'} ago` : ''}</div>
            <div style={{ flex: 1, minHeight: 14 }} aria-hidden="true"/>
            {canEdit && <div className="vendor-card-foot">
              <div className="inline-flex gap-1 ml-auto">
                <button aria-label="Edit" onClick={() => onEdit(a)} className="tab btn-small" data-testid={`asset-card-edit-${a.id}`}><Edit3 size={12}/></button>
                {canDelete && <button aria-label={a.source_expense_id ? 'Not a company asset' : 'Delete'} title={a.source_expense_id ? 'Not a company asset' : 'Delete'} onClick={() => onDelete(a)} className="tab btn-small text-red-300" data-testid={`asset-card-delete-${a.id}`}><Trash2 size={12}/></button>}
              </div>
            </div>}
          </div>
        );
      })}
    </div>
  );
}

function FixedAssets() {
  const { user } = useAuth();
  const canEdit = ['admin', 'accountant', 'master_admin'].includes(user.role);
  const canDelete = isMasterAdmin(user);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c => c.id === id)?.name || '—';

  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const companyF = useMultiFilter('FixedAssets.company');
  const statusF = useMultiFilter('FixedAssets.status');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [search, setSearch] = usePersistedState('FixedAssets.search', '');
  const [view, setView] = usePersistedState('FixedAssets.view', 'cards');
  const [cardSort, setCardSort] = usePersistedState('FixedAssets.cardSort', 'name');
  // Round 19 finding: KPI tiles and the table rendered off items=[] before
  // the first /assets response landed -- a slow load looked identical to
  // "confirmed zero assets" (Total Assets 0, Purchase Cost ₹0, etc.)
  // instead of still loading.
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      // Company and Status are multi-select filters applied in the browser below, so ask for everything.
      const r = await api.get('/assets');
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/vendors').then(r => setVendors(r.data)).catch(() => {});
  }, []);

  const openAdd = () => setForm({ status: 'active', depreciation_method: 'straight_line', quantity: 1, salvage_value: 0, ...(companyF.soleId ? { company_id: companyF.soleId } : {}) });
  const openEdit = (a) => setForm({ ...a });
  const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    const payload = {
      ...form,
      quantity: Number(form.quantity || 1),
      purchase_cost: Number(form.purchase_cost || 0),
      salvage_value: Number(form.salvage_value || 0),
      useful_life_years: numOrNull(form.useful_life_years),
      depreciation_rate: numOrNull(form.depreciation_rate),
      disposal_value: numOrNull(form.disposal_value),
    };
    try {
      if (form.id) await api.patch(`/assets/${form.id}`, payload);
      else await api.post('/assets', payload);
      setOpen(false); setForm({}); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const del = async (a) => {
    // User-requested: an asset auto-captured from a company expense
    // (source_expense_id set -- see the backend's own capture hook) gets
    // a clearer confirm message for this same delete action, since for a
    // stub row this is really "reject, not a real company asset" rather
    // than "delete a fully-entered record".
    const msg = a.source_expense_id
      ? 'Mark this as not a company asset? It will be removed from Fixed Assets (the original expense is untouched).'
      : 'Delete this asset? This cannot be undone.';
    if (!window.confirm(msg)) return;
    try { await api.delete(`/assets/${a.id}`); load(); toast.success(a.source_expense_id ? 'Removed' : 'Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const exportCsv = () => {
    downloadCSV(shownItems.map(a => ({
      asset_code: a.asset_code, asset_name: a.asset_name, category: ASSET_CATEGORY_LABELS[a.category] || a.category,
      company: companyName(a.company_id), location: a.location || '', custodian: a.custodian || '',
      purchase_date: a.purchase_date, purchase_cost: a.purchase_cost, accumulated_depreciation: a.accumulated_depreciation,
      book_value: a.book_value, status: a.status,
    })), `fixed-assets-${Date.now()}.csv`);
  };

  const projectsForCompany = (cid) => projects.filter(p => !cid || p.company_id === cid);
  const vendorsForCompany = (cid) => vendors.filter(v => !cid || v.company_id === cid);
  const isDisposal = ['disposed', 'written_off'].includes(form.status);

  const aq = search.trim().toLowerCase();
  const shownItems = items.filter(a => companyF.matches(a.company_id) && statusF.matches(a.status)).filter(a => !aq || [a.asset_name, a.asset_code, a.serial_number, a.location, a.custodian, ASSET_CATEGORY_LABELS[a.category] || a.category].some(v => String(v || '').toLowerCase().includes(aq)));
  const totals = shownItems.reduce((acc, a) => ({
    cost: acc.cost + Number(a.purchase_cost || 0),
    depreciation: acc.depreciation + Number(a.accumulated_depreciation || 0),
    book: acc.book + Number(a.book_value || 0),
  }), { cost: 0, depreciation: 0, book: 0 });
  // User-requested: sortable headers, same convention as every other list
  // in the app -- defaults to Purchase Date descending (newest first).
  const { sorted: sortedItems, sortKey, sortDir, toggleSort } = useSortableRows(shownItems, 'purchase_date', 'desc');
  const cardAssets = useMemo(() => sortAssetCards(shownItems, cardSort), [shownItems, cardSort]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="section-eyebrow mb-2">Fixed Asset Register</div>
          <h1 className="serif text-4xl">Fixed Assets</h1>
          <div className="text-sm text-muted mt-1">One record per asset — machinery, vehicles, equipment, furniture, IT — with book value computed live as of today.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {needsPicker && <MultiFilterPicker filter={companyF} items={companies.map(c=>({id:c.id, name:c.name}))} allLabel="All Companies" width={180} testId="asset-filter-company"/>}
          <MultiFilterPicker filter={statusF} items={ASSET_STATUSES.map(st=>({id:st, name:st.replace(/_/g,' ')}))} allLabel="All Statuses" width={170} testId="asset-filter-status"/>
          <ClearFiltersButton filters={[companyF, statusF]}/>
          <div className="vendor-search">
            <SearchIcon size={14}/>
            <input className="input" placeholder="Search asset, code, location…" value={search} onChange={e => setSearch(e.target.value)} data-testid="asset-search"/>
          </div>
          {view === 'cards' && <select className="input" style={{ width: 180 }} value={cardSort} onChange={e => setCardSort(e.target.value)} data-testid="asset-card-sort" title="Order of the cards">
            <option value="name">Sort: Name A–Z</option>
            <option value="cost">Sort: Highest cost</option>
            <option value="book">Sort: Highest book value</option>
            <option value="newest">Sort: Newest first</option>
          </select>}
          <div className="inline-flex gap-1" role="group" aria-label="View">
            <button className={"tab btn-small inline-flex items-center gap-1 " + (view === 'cards' ? 'active' : '')} onClick={() => setView('cards')} data-testid="asset-view-cards"><LayoutGrid size={13}/>Cards</button>
            <button className={"tab btn-small inline-flex items-center gap-1 " + (view === 'table' ? 'active' : '')} onClick={() => setView('table')} data-testid="asset-view-table"><ListIcon size={13}/>Table</button>
          </div>
          {items.length > 0 && <button onClick={exportCsv} className="btn-outline btn-small inline-flex items-center gap-1"><Download size={14} />Export CSV</button>}
          {items.length > 0 && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>}
          {canEdit && <button onClick={() => setBulkOpen(true)} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="bulk-import-assets"><Upload size={14} />Bulk Import</button>}
          {canEdit && <button onClick={() => { openAdd(); setOpen(true); }} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="add-asset"><Plus size={14} />Add Asset</button>}
        </div>
      </div>

      {!loading && <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 no-print">
        <KpiCard label="Total Assets" value={shownItems.length} icon={Package} />
        <KpiCard label="Purchase Cost" value={fmtINR(totals.cost)} tone="blue" />
        <KpiCard label="Accumulated Depreciation" value={fmtINR(totals.depreciation)} tone="red" />
        <KpiCard label="Net Book Value" value={fmtINR(totals.book)} tone="gold" />
      </div>}

      {view === 'cards' && (
        <div className="no-print" data-testid="asset-cards">
          <div className="text-xs text-muted mb-3">Showing <b>{shownItems.length}</b> of <b>{items.length}</b> assets{shownItems.length !== items.length ? ' (filtered)' : ''}</div>
          {loading && <div className="card card-dark text-center text-muted py-10">Loading…</div>}
          {!loading && shownItems.length === 0 && <div className="card card-dark text-center text-muted py-10">{items.length === 0 ? 'No assets recorded yet.' : 'No assets match these filters.'}</div>}
          {!loading && shownItems.length > 0 && <AssetCards assets={cardAssets} canEdit={canEdit} canDelete={canDelete} needsPicker={needsPicker} companyName={companyName} todayIso={new Date().toLocaleDateString('en-CA')} onEdit={a => { openEdit(a); setOpen(true); }} onDelete={del}/>}
        </div>
      )}
      <div className={"card card-dark overflow-x-auto print-area " + (view === 'cards' ? 'vendor-table-print-only' : '')} data-testid="asset-table">
        <div className="print-header">
          <div className="print-company">{needsPicker ? (companyF.all ? 'All Companies' : companyF.ids.map(companyName).join(', ')) : companies[0]?.name}</div>
          <div className="print-report-title">Fixed Assets</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        <table className="tbl">
          <thead><tr>
            <SortTh label="Code" sortKeyName="asset_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Asset" sortKeyName="asset_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Category" sortKeyName="category" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {needsPicker && <th>Company</th>}
            <SortTh label="Location" sortKeyName="location" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Purchase Date" sortKeyName="purchase_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Cost" sortKeyName="purchase_cost" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
            <SortTh label="Depreciation" sortKeyName="accumulated_depreciation" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
            <SortTh label="Book Value" sortKeyName="book_value" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
            <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <th className="no-print"></th>
          </tr></thead>
          <tbody>
            {loading && <LoadingRow colSpan="11"/>}
            {!loading && sortedItems.length === 0 && <tr><td colSpan="11" className="text-center text-muted py-8">{items.length === 0 ? 'No assets recorded yet.' : 'No assets match these filters.'}</td></tr>}
            {!loading && sortedItems.map(a => (
              <tr key={a.id} data-testid={`row-asset-${a.id}`}>
                <td className="mono text-xs">{a.asset_code}</td>
                <td><div className="serif text-base flex items-center gap-1.5">{a.asset_name}
                  {/* User-requested: an expense entered under an Asset
                      Purchase category auto-creates this row with most
                      fields blank -- this chip is the only visible sign
                      why, and that it's still waiting on the rest of its
                      details (or a decision that it isn't a real asset). */}
                  {a.source_expense_id && <span className="chip" style={{fontSize:10, background:'rgba(217,164,65,0.14)', color:'var(--accent-amber)'}} title="Auto-captured from a Company Expense marked Asset Purchase — review and complete, or mark as not a company asset">From Expense</span>}
                  </div>{a.serial_number && <div className="text-xs text-muted">S/N {a.serial_number}</div>}</td>
                <td className="text-xs">{ASSET_CATEGORY_LABELS[a.category] || a.category}</td>
                {needsPicker && <td className="text-xs">{companyName(a.company_id)}</td>}
                <td className="text-xs">{a.location || '—'}</td>
                <td className="text-xs">{fmtDate(a.purchase_date)}</td>
                <td className="mono text-right">{fmtINR(a.purchase_cost)}</td>
                <td className="mono text-right text-red-400">{fmtINR(a.accumulated_depreciation)}</td>
                <td className="mono text-right text-[color:var(--gold)]">{fmtINR(a.book_value)}</td>
                <td><AssetStatusBadge status={a.status} /></td>
                {canEdit && <td className="text-right whitespace-nowrap no-print">
                  <button aria-label="Edit" onClick={() => { openEdit(a); setOpen(true); }} className="tab btn-small mr-1"><Edit3 size={12} /></button>
                  {canDelete && <button aria-label={a.source_expense_id ? 'Not a company asset' : 'Delete'} title={a.source_expense_id ? 'Not a company asset' : 'Delete'} onClick={() => del(a)} className="tab btn-small text-red-300"><Trash2 size={12} /></button>}
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? 'Edit Asset' : 'Add Asset'} maxW={860}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker} clear={['project_id', 'vendor_id']} />
          <Field label="Asset Code *"><input className="input mono" value={form.asset_code || ''} onChange={e => setForm({ ...form, asset_code: e.target.value })} data-testid="asset-code" /></Field>
          <Field label="Asset Name *"><input className="input" value={form.asset_name || ''} onChange={e => setForm({ ...form, asset_name: e.target.value })} data-testid="asset-name" /></Field>
          <Field label="Category *"><select className="input" value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="asset-category">
            <option value="">— select —</option>
            {Object.entries(ASSET_CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></Field>
          <Field label="Serial Number"><input className="input" value={form.serial_number || ''} onChange={e => setForm({ ...form, serial_number: e.target.value })} /></Field>
          <Field label="Location"><input className="input" value={form.location || ''} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Site / office / store" /></Field>
          <Field label="Custodian"><input className="input" value={form.custodian || ''} onChange={e => setForm({ ...form, custodian: e.target.value })} placeholder="Person / department responsible" /></Field>
          <Field label="Assigned Project"><select className="input" value={form.project_id || ''} onChange={e => setForm({ ...form, project_id: e.target.value || null })}>
            <option value="">— none —</option>
            {projectsForCompany(form.company_id).map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select></Field>
          <Field label="Purchased From (Vendor)"><select className="input" value={form.vendor_id || ''} onChange={e => setForm({ ...form, vendor_id: e.target.value || null })}>
            <option value="">— none —</option>
            {vendorsForCompany(form.company_id).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select></Field>
          <Field label="Quantity"><input type="number" min="0" step="1" className="input" value={form.quantity ?? 1} onChange={e => setForm({ ...form, quantity: e.target.value })} /></Field>

          <Field label="Purchase Date *"><input type="date" className="input" value={form.purchase_date || ''} onChange={e => setForm({ ...form, purchase_date: e.target.value })} data-testid="asset-purchase-date" /></Field>
          <Field label="Purchase Cost *"><input type="number" min="0" step="0.01" className="input mono" value={form.purchase_cost ?? ''} onChange={e => setForm({ ...form, purchase_cost: e.target.value })} data-testid="asset-purchase-cost" /></Field>
          <Field label="Invoice / Bill No."><input className="input" value={form.invoice_number || ''} onChange={e => setForm({ ...form, invoice_number: e.target.value })} /></Field>
          <Field label="Warranty Expiry"><input type="date" className="input" value={form.warranty_expiry || ''} onChange={e => setForm({ ...form, warranty_expiry: e.target.value })} /></Field>

          <Field label="Depreciation Method"><select className="input" value={form.depreciation_method || 'straight_line'} onChange={e => setForm({ ...form, depreciation_method: e.target.value })}>
            {Object.entries(ASSET_DEPRECIATION_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></Field>
          {form.depreciation_method === 'straight_line' && <Field label="Useful Life (years)"><input type="number" min="0" step="0.5" className="input" value={form.useful_life_years ?? ''} onChange={e => setForm({ ...form, useful_life_years: e.target.value })} /></Field>}
          {form.depreciation_method === 'wdv' && <Field label="Depreciation Rate (% p.a.)"><input type="number" min="0" max="100" step="0.1" className="input" value={form.depreciation_rate ?? ''} onChange={e => setForm({ ...form, depreciation_rate: e.target.value })} /></Field>}
          {form.depreciation_method !== 'none' && <Field label="Salvage Value"><input type="number" min="0" step="0.01" className="input mono" value={form.salvage_value ?? 0} onChange={e => setForm({ ...form, salvage_value: e.target.value })} /></Field>}

          <Field label="Status"><select className="input" value={form.status || 'active'} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="asset-status">
            {ASSET_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select></Field>
          {isDisposal && <Field label="Disposal Date *"><input type="date" className="input" value={form.disposal_date || ''} onChange={e => setForm({ ...form, disposal_date: e.target.value })} /></Field>}
          {isDisposal && <Field label="Disposal Value"><input type="number" min="0" step="0.01" className="input mono" value={form.disposal_value ?? ''} onChange={e => setForm({ ...form, disposal_value: e.target.value })} /></Field>}

          <div className="col-span-2"><Field label="Description"><textarea className="input" rows={2} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></Field></div>
          <div className="col-span-2"><Field label="Notes"><textarea className="input" rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field></div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={() => setOpen(false)} className="btn-outline btn-small">Cancel</button>
          {/* Full-app audit finding: _validate_asset_fields (server-side)
              requires useful_life_years when Straight Line is picked,
              depreciation_rate when WDV is picked, and disposal_date >=
              purchase_date -- none of the three were enforced here, so a
              fully-filled-out form with an enabled Save still hit a late
              400 after the user was done. Mirrored exactly below. */}
          <button onClick={save} disabled={!form.asset_code || !form.asset_name || !form.category || !form.purchase_date || form.purchase_cost === '' || form.purchase_cost === undefined || (isDisposal && !form.disposal_date) || (form.depreciation_method === 'straight_line' && (form.useful_life_years === '' || form.useful_life_years === undefined || form.useful_life_years === null || Number(form.useful_life_years) <= 0)) || (form.depreciation_method === 'wdv' && (form.depreciation_rate === '' || form.depreciation_rate === undefined || form.depreciation_rate === null || !(Number(form.depreciation_rate) > 0 && Number(form.depreciation_rate) <= 100))) || (form.disposal_date && form.purchase_date && form.disposal_date < form.purchase_date) || saving} className="btn-gold btn-small" data-testid="asset-save">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </Modal>
      {bulkOpen && (
        <Modal open={true} onClose={() => { setBulkOpen(false); load(); }} title="Bulk Import — Fixed Assets" maxW={860}>
          <ExcelImportWizard lockedEntity="fixed_assets" />
        </Modal>
      )}
    </div>
  );
}

// ============= FORECAST =============
// `embedded`: rendered inline inside Dashboard's own page container
// (no outer max-width/padding/fade-up of its own, no repeated H1 — the
// Dashboard section header already says "Forecast") instead of as a
// standalone routed page. Moved out of its own nav link/route into
// Dashboard; the calculator itself (state, /forecast call, results) is
// unchanged either way.
function Forecast({ embedded = false }) {
  const [form, setForm] = useState({ expected_contract: 5000000, expected_expenses: 3500000, expected_investment: 1000000, partnership_model: 'equal_capital_bonus', money_pool_percentage: 50, company_fund_percentage: 0, num_money_partners: 3, num_work_partners: 3, num_equal_partners: 3, project_id: '' });
  const [projects, setProjects] = useState([]);
  const [result, setResult] = useState(null);
  useEffect(() => { api.get('/projects').then(r=>setProjects(r.data)); }, []);
  const calc = async () => {
    const payload = { ...form, project_id: form.project_id || null };
    ['expected_contract','expected_expenses','expected_investment','money_pool_percentage','company_fund_percentage','num_money_partners','num_work_partners','num_equal_partners'].forEach(k => payload[k] = Number(payload[k]||0));
    const r = await api.post('/forecast', payload);
    setResult(r.data);
  };
  return (
    <div className={embedded ? '' : 'max-w-5xl mx-auto px-6 py-8 fade-up'}>
      {!embedded && <><div className="section-eyebrow mb-2">Simulation</div>
      <h1 className="serif text-4xl mb-1">Profit Forecast</h1></>}
      <p className="text-muted text-sm mb-6">FORECAST — NOT ACTUAL FINANCIAL DATA. This tool does not modify project records.</p>
      <div className="card card-dark grid md:grid-cols-3 gap-4">
        <Field label="Expected Contract (₹)"><input className="input mono" type="number" value={form.expected_contract} onChange={e=>setForm({...form, expected_contract:e.target.value})} data-testid="fc-contract"/></Field>
        <Field label="Expected Expenses (₹)"><input className="input mono" type="number" value={form.expected_expenses} onChange={e=>setForm({...form, expected_expenses:e.target.value})} data-testid="fc-expenses"/></Field>
        <Field label="Expected Investment (₹)"><input className="input mono" type="number" value={form.expected_investment} onChange={e=>setForm({...form, expected_investment:e.target.value})}/></Field>
        <Field label="Model">
          <select className="input" value={form.partnership_model} onChange={e=>setForm({...form, partnership_model:e.target.value})}>
            <option value="equal_capital_bonus">{PARTNERSHIP_MODEL_LABELS.equal_capital_bonus}</option>
            <option value="money_work">{PARTNERSHIP_MODEL_LABELS.money_work}</option>
            <option value="equal_variable">{PARTNERSHIP_MODEL_LABELS.equal_variable}</option>
          </select>
        </Field>
        {(form.partnership_model==='money_work' || form.partnership_model==='equal_capital_bonus') && (
          <Field label={form.partnership_model==='money_work' ? 'Use Rule From Project (optional)' : 'Compare Against Project (optional)'}>
            <select className="input" value={form.project_id} onChange={e=>setForm({...form, project_id:e.target.value})}>
              <option value="">{form.partnership_model==='money_work' ? '— manual % below —' : '— hypothetical only —'}</option>
              {projects.map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
          </Field>
        )}
        {form.partnership_model==='money_work' && !form.project_id && <Field label="Money Pool %"><input className="input mono" type="number" min="0" max="100" value={form.money_pool_percentage} onChange={e=>setForm({...form, money_pool_percentage:e.target.value})}/></Field>}
        {/* Round 31 finding: Company Fund % now applies to every partnership
            model (see _resolve_company_fund_pct), but this field used to be
            gated to money_work-without-a-linked-project only -- for
            equal_capital_bonus/equal_variable, a manual (no project_id)
            forecast had no way to even type a reserve %, silently making the
            backend's own generalization unreachable from this form for 2 of
            the 3 models. Shown whenever manually typing (no linked project);
            once a project is linked the real configured rule is pulled
            server-side instead (see rule_applied below), same as before. */}
        {!form.project_id && <Field label="Company Fund % (optional, reserved before the partner split)"><input className="input mono" type="number" min="0" max="100" value={form.company_fund_percentage} onChange={e=>setForm({...form, company_fund_percentage:e.target.value})}/></Field>}
        <Field label="# Money Partners"><input className="input mono" type="number" value={form.num_money_partners} onChange={e=>setForm({...form, num_money_partners:e.target.value})}/></Field>
        <Field label="# Work Partners"><input className="input mono" type="number" value={form.num_work_partners} onChange={e=>setForm({...form, num_work_partners:e.target.value})}/></Field>
        <Field label="# Equal Partners"><input className="input mono" type="number" value={form.num_equal_partners} onChange={e=>setForm({...form, num_equal_partners:e.target.value})}/></Field>
        <div className="md:col-span-3 mt-2"><button onClick={calc} className="btn-gold" data-testid="fc-calc">Calculate Forecast</button></div>
      </div>
      {result && (
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <KpiCard label="Expected Profit" value={fmtINR(result.expected_profit)} gold/>
          <KpiCard label="Expected Margin" value={result.expected_margin_pct + '%'}/>
          {/* Round 31 finding: was gated inside the money_work-only block and
              on company_fund_pool>0, so (a) equal_capital_bonus/
              equal_variable forecasts never showed the reserve at all, even
              though available_pool below is already net of it, and (b) a
              loss forecast (profit negative, reserve % positive) has a
              NEGATIVE company_fund_pool by the same symmetric design as real
              approval -- pool>0 hid a real, nonzero retained figure. Uses
              company_fund_pct>0 instead, same condition SettlementPanel
              already uses correctly, and applies to every model. */}
          {result.company_fund_pct>0 && <KpiCard label="Company Fund" value={fmtINR(result.company_fund_pool)}/>}
          {form.partnership_model==='money_work' && <>
            <KpiCard label="Money Pool" value={fmtINR(result.money_pool)}/>
            <KpiCard label="Work Pool" value={fmtINR(result.work_pool)}/>
            <KpiCard label="Per Money Partner" value={fmtINR(result.per_money_partner)}/>
            <KpiCard label="Per Work Partner" value={fmtINR(result.per_work_partner)}/>
          </>}
          {form.partnership_model==='equal_capital_bonus' && <>
            <KpiCard label="Equal Share (before bonus)" value={fmtINR(result.equal_share_size)} hint="Guaranteed base — capital's own share, split among all N equal partners, comes on top"/>
            <KpiCard label="Per Equal Partner (if invested equally)" value={fmtINR(result.per_equal_partner)}/>
          </>}
          {form.partnership_model==='equal_variable' && <KpiCard label="Per Equal Partner" value={fmtINR(result.per_equal_partner)}/>}
          <KpiCard label="Investment / Money Partner" value={fmtINR(result.expected_investment_per_money_partner)} hint="Even split of the total you entered above — not weighted, since this tool doesn't collect per-partner amounts"/>
          {form.partnership_model==='money_work' && form.project_id && <div className="md:col-span-3 text-xs text-muted text-center">{result.rule_applied ? `Using this project's configured rule: ${result.money_pool_pct}% Money / ${100-result.money_pool_pct-result.company_fund_pct}% Work${result.company_fund_pct>0?` / ${result.company_fund_pct}% Fund`:''}` : 'No rule configured for this project — used the 50/50 fallback'}</div>}

          {result.real_partner_split && result.real_partner_split.length > 0 && (
            <div className="md:col-span-3 card card-dark">
              <div className="serif text-lg mb-1 text-[color:var(--gold)]">If this came true — this project's actual partners</div>
              <div className="text-xs text-muted mb-3">Split using each partner's real recorded investment on this project, the same formula the Final Calculation tab uses — not an even split.</div>
              <div className="overflow-x-auto">
                <table className="tbl">
                  {form.partnership_model==='money_work' ? (
                    <>
                      <thead><tr><th>Partner</th><th className="text-right">Current Investment</th><th className="text-right">Money %</th><th className="text-right">Work %</th><th className="text-right">Forecast Total</th></tr></thead>
                      <tbody>
                        {result.real_partner_split.map(r => (
                          <tr key={r.partner_id}>
                            <td>{r.partner_name}</td>
                            <td className="mono text-right">{fmtINR(r.current_investment)}</td>
                            <td className="mono text-right">{r.money_share_pct}%</td>
                            <td className="mono text-right">{r.work_share_pct}%</td>
                            <td className="mono text-right text-[color:var(--gold)]">{fmtINR(r.forecast_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : (
                    <>
                      <thead><tr><th>Partner</th><th className="text-right">Current Investment</th><th className="text-right">Equal Share</th><th className="text-right">Capital Bonus</th><th className="text-right">Forecast Total</th></tr></thead>
                      <tbody>
                        {result.real_partner_split.map(r => (
                          <tr key={r.partner_id}>
                            <td>{r.partner_name}</td>
                            <td className="mono text-right">{fmtINR(r.current_investment)}</td>
                            <td className="mono text-right">{fmtINR(r.equal_share)}</td>
                            <td className="mono text-right">{fmtINR(r.capital_bonus)}</td>
                            <td className="mono text-right text-[color:var(--gold)]">{fmtINR(r.forecast_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  )}
                </table>
              </div>
            </div>
          )}

          <div className="md:col-span-3 text-xs text-[color:var(--gold)] uppercase tracking-widest text-center">{result.label}</div>
        </div>
      )}
    </div>
  );
}

// ============= REPORTS =============
// ============= STAFF PAYMENTS REPORT (Staff page download + Reports) =============
// What was paid to each staff member (salary / travel allowance / other), for everyone at once or narrowed by category (designation),
// company, period, payment type, salary type, status or specific people. Reviewed payments only; pending ones are shown apart.
const STAFF_NONE_CATEGORY = '__none__';
function StaffPaymentReport({ companyIds, embedded }) {
  const [staffList, setStaffList] = useState([]);
  const catF = useMultiFilter('StaffPay.cat');
  const staffF = useMultiFilter('StaffPay.staff');
  const typeF = useMultiFilter('StaffPay.type');
  const [salaryType, setSalaryType] = useState('');
  const [status, setStatus] = useState('');
  const [fy, setFy] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [view, setView] = usePersistedState('StaffPay.view', 'staff');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const token = useRef(0);
  useEffect(() => { api.get('/staff').then(r => setStaffList(r.data)).catch(() => {}); }, []);
  const catOf = (x) => (x.designation || '').trim() || STAFF_NONE_CATEGORY;
  const scoped = staffList.filter(x => !companyIds || !companyIds.length || companyIds.includes(x.company_id));
  const catItems = [...new Set(scoped.map(catOf))].sort((a, b) => (a === STAFF_NONE_CATEGORY) - (b === STAFF_NONE_CATEGORY) || a.localeCompare(b)).map(c => ({ id: c, name: c === STAFF_NONE_CATEGORY ? 'No designation' : c }));
  const staffItems = scoped.filter(x => catF.matches(catOf(x))).map(x => ({ id: x.id, name: x.name + (x.designation ? ` · ${x.designation}` : '') }));
  const fyOptions = distinctFYs(Array.from({ length: 6 }, (_, i) => fyFromDateStr(new Date(Date.now() - i * 365 * 86400000).toISOString())));
  const params = () => ({
    ...(companyIds && companyIds.length ? { company_ids: companyIds.join(',') } : {}),
    ...(!catF.all && catF.ids.length ? { designation: catF.ids.join(',') } : {}),
    ...(!staffF.all && staffF.ids.length ? { staff_ids: staffF.ids.join(',') } : {}),
    ...(!typeF.all && typeF.ids.length ? { payment_type: typeF.ids.join(',') } : {}),
    ...(salaryType ? { salary_type: salaryType } : {}), ...(status ? { status } : {}),
    ...(fy ? { financial_year: fy } : {}), ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}),
  });
  const generate = async () => {
    const my = ++token.current; setBusy(true);
    try { const r = (await api.get('/reports/staff-payments', { params: params() })).data; if (my === token.current) setData(r); }
    catch (e) { if (my === token.current) toast.error(e.response?.data?.detail || 'Could not load the report'); }
    finally { if (my === token.current) setBusy(false); }
  };
  const [regen, setRegen] = useState(0);   // bumped by "Clear filters" so the reload runs after the cleared state is applied
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (regen) generate(); }, [regen]);
  const companyKey = (companyIds || []).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { generate(); }, [companyKey]);
  const catLabel = (c) => c === STAFF_NONE_CATEGORY ? 'No designation' : c;
  const subtitle = [
    fy ? `Financial year: ${fy}` : (from || to) ? '' : 'All periods', (from || to) ? `Period: ${from ? fmtDate(from) : 'start'} to ${to ? fmtDate(to) : 'today'}` : '',
    !catF.all && catF.ids.length ? `Category: ${catF.ids.map(catLabel).join(', ')}` : 'All categories',
    !typeF.all && typeF.ids.length ? `Payment type: ${typeF.ids.map(t => STAFF_PAYMENT_TYPE_LABELS[t]).join(', ')}` : '',
    'Reviewed payments only',
  ].filter(Boolean).join(' · ');
  const rowsStaff = (data?.rows || []).map(r => ({ Staff: r.name, Category: r.designation || '—', Company: r.company || '—', Status: r.status, 'Salary type': STAFF_SALARY_LABELS[r.salary_type] || r.salary_type || '—', Salary: r.salary, 'Travel allowance': r.travel_allowance, Other: r.other, Total: r.total, Payments: r.payments, 'Last payment': r.last_payment_date ? fmtDate(r.last_payment_date) : '—', 'Pending review': r.pending_total }));
  const rowsCat = (data?.categories || []).map(c => ({ Category: catLabel(c.category === 'No designation' ? STAFF_NONE_CATEGORY : c.category), Staff: c.staff, Payments: c.payments, Salary: c.salary, 'Travel allowance': c.travel_allowance, Other: c.other, Total: c.total, 'Pending review': c.pending_total }));
  const rowsLines = (data?.entries || []).map(e => ({ Date: fmtDate(e.date), Staff: e.staff_name, Category: e.designation || '—', Company: e.company || '—', Type: STAFF_PAYMENT_TYPE_LABELS[e.payment_type] || e.payment_type, Project: e.project, Voucher: e.voucher_number || '—', Description: e.description || '—', Amount: e.amount }));
  const shown = view === 'category' ? rowsCat : view === 'lines' ? rowsLines : rowsStaff;
  const sum = (rows, k) => rows.reduce((a, r) => a + Number(r[k] || 0), 0);
  const moneyKeys = view === 'lines' ? ['Amount'] : view === 'category' ? ['Salary', 'Travel allowance', 'Other', 'Total', 'Pending review'] : ['Salary', 'Travel allowance', 'Other', 'Total', 'Pending review'];
  const allSheets = async () => {
    if (!data) return;
    const XLSX = await loadXlsx(); const wb = XLSX.utils.book_new();
    const add = (name, rows) => { const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: 'Nothing to show' }], { origin: 'A3' }); XLSX.utils.sheet_add_aoa(ws, [['Staff payments — ' + name], [subtitle]], { origin: 'A1' }); XLSX.utils.book_append_sheet(wb, ws, name); };
    add('Per staff', rowsStaff); add('Per category', rowsCat); add('Payment lines', rowsLines);
    XLSX.writeFile(wb, `staff-payments-${Date.now()}.xlsx`);
  };
  const typeItems = Object.entries(STAFF_PAYMENT_TYPE_LABELS).map(([id, name]) => ({ id, name }));
  return (
    <div data-testid="staff-payment-report">
      <div className="flex flex-wrap items-end gap-2 mb-3 no-print">
        <div><div className="text-xs text-muted mb-1">Category</div><MultiFilterPicker filter={catF} items={catItems} allLabel="All categories" width={190} testId="sp-filter-category"/></div>
        <div><div className="text-xs text-muted mb-1">Staff</div><MultiFilterPicker filter={staffF} items={staffItems} allLabel="Everyone" width={190} testId="sp-filter-staff"/></div>
        <div><div className="text-xs text-muted mb-1">Payment type</div><MultiFilterPicker filter={typeF} items={typeItems} allLabel="All types" width={160} testId="sp-filter-type"/></div>
        <div><div className="text-xs text-muted mb-1">Financial year</div><FySelect value={fy} onChange={setFy} years={fyOptions} style={{ width: 140 }} testId="sp-fy"/></div>
        <div><div className="text-xs text-muted mb-1">From</div><input type="date" className="input" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 150 }} data-testid="sp-from"/></div>
        <div><div className="text-xs text-muted mb-1">To</div><input type="date" className="input" value={to} onChange={e => setTo(e.target.value)} style={{ width: 150 }} data-testid="sp-to"/></div>
        <div><div className="text-xs text-muted mb-1">Salary type</div><select className="input" value={salaryType} onChange={e => setSalaryType(e.target.value)} style={{ width: 150 }} data-testid="sp-salary-type"><option value="">All</option>{Object.entries(STAFF_SALARY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <div><div className="text-xs text-muted mb-1">Status</div><select className="input" value={status} onChange={e => setStatus(e.target.value)} style={{ width: 120 }} data-testid="sp-status"><option value="">All</option><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
        <button className="btn-gold btn-small" onClick={generate} disabled={busy} data-testid="sp-generate">{busy ? 'Loading…' : 'Generate'}</button>
        {(catF.active || staffF.active || typeF.active || salaryType || status || fy || from || to) && <button className="tab btn-small" onClick={() => { catF.reset(); staffF.reset(); typeF.reset(); setSalaryType(''); setStatus(''); setFy(''); setFrom(''); setTo(''); setRegen(n => n + 1); }} data-testid="sp-clear">Clear filters</button>}
      </div>
      {data && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <div className="serif text-xl">{embedded ? 'Staff payment details' : 'Staff Payments'}</div>
              <div className="text-muted text-xs">{data.totals.staff} staff · {data.totals.payments} payment(s) · paid {fmtINR(data.totals.total)}{data.pending_total > 0 ? ` · ${fmtINR(data.pending_total)} awaiting review (not counted)` : ''}</div>
              <div className="text-muted text-xs">{subtitle}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ReportExportBar rows={shown} name={`staff-payments-${view}`} title={`Staff payments — ${view === 'category' ? 'by category' : view === 'lines' ? 'every payment' : 'per staff'}`} subtitle={subtitle}/>
              <button onClick={allSheets} className="btn-outline btn-small no-print" data-testid="sp-excel-all" title="One Excel file with three sheets: per staff, per category, every payment">Excel — all sheets</button>
            </div>
          </div>
          <div className="mb-3 inline-flex gap-1 no-print" role="group" aria-label="Report view">
            {[['staff', 'Per staff'], ['category', 'Per category'], ['lines', 'Every payment']].map(([k, l]) => <button key={k} className={'tab btn-small ' + (view === k ? 'active' : '')} onClick={() => setView(k)} data-testid={`sp-view-${k}`}>{l}</button>)}
          </div>
          <div className="card card-dark overflow-x-auto">
            <table className="tbl" data-testid="sp-table">
              <thead><tr>{shown[0] ? Object.keys(shown[0]).map(k => <th key={k} className={moneyKeys.includes(k) || (k === 'Payments' || (k === 'Staff' && view === 'category')) ? 'text-right' : ''}>{k}</th>) : <th>Nothing to show</th>}</tr></thead>
              <tbody>
                {shown.length === 0 && <tr><td className="text-center text-muted py-6">No staff or payments match these filters.</td></tr>}
                {shown.map((r, i) => <tr key={i}>{Object.entries(r).map(([k, v]) => <td key={k} className={moneyKeys.includes(k) ? 'mono text-right' : (k === 'Payments' || (k === 'Staff' && view === 'category')) ? 'text-right' : 'text-xs'}>{moneyKeys.includes(k) ? fmtINR(v) : v}</td>)}</tr>)}
              </tbody>
              {shown.length > 0 && <tfoot><tr>{Object.keys(shown[0]).map((k, i) => <td key={k} className={'font-semibold ' + (moneyKeys.includes(k) ? 'mono text-right' : '')}>{i === 0 ? `Total (${shown.length})` : moneyKeys.includes(k) ? fmtINR(sum(shown, k)) : ''}</td>)}</tr></tfoot>}
            </table>
          </div>
          <div className="text-xs text-muted mt-2">{data.note}</div>
        </>
      )}
    </div>
  );
}

// ---- Month / date-range filter shared by the printable reports ----
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const monthBoundsIso = (ym) => { const [y, m] = ym.split('-').map(Number); return [isoLocal(new Date(y, m - 1, 1)), isoLocal(new Date(y, m, 0))]; };
const fyBoundsIso = (startYear) => [`${startYear}-04-01`, `${startYear + 1}-03-31`];
const monthLabelOf = (ym) => { const [y, m] = ym.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }); };
const periodLabel = (from, to) => (from || to) ? `${from ? fmtDate(from) : 'Start'} – ${to ? fmtDate(to) : 'Today'}` : '';
function PeriodPicker({ from, to, onChange, testId = 'period' }) {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const presets = [
    ['This month', monthBoundsIso(isoLocal(now).slice(0, 7))],
    ['Last month', monthBoundsIso(isoLocal(prev).slice(0, 7))],
    ['This FY', fyBoundsIso(fyStart)],
    ['Last FY', fyBoundsIso(fyStart - 1)],
  ];
  const wholeMonth = from && to && from.slice(0, 7) === to.slice(0, 7) && monthBoundsIso(from.slice(0, 7))[0] === from && monthBoundsIso(from.slice(0, 7))[1] === to;
  return (
    <div className="inline-flex flex-wrap items-center gap-2" data-testid={testId}>
      <label className="text-xs text-muted inline-flex items-center gap-1">Month
        <input type="month" className="input" style={{ width: 150 }} value={wholeMonth ? from.slice(0, 7) : ''} onChange={e => { const v = e.target.value; if (v) { const [f, t] = monthBoundsIso(v); onChange(f, t); } else onChange('', ''); }} data-testid={`${testId}-month`} />
      </label>
      {presets.map(([label, [f, t]]) => <button key={label} type="button" onClick={() => onChange(f, t)} className={"tab btn-small " + (from === f && to === t ? 'active' : '')} data-testid={`${testId}-${label.toLowerCase().replace(/ /g, '-')}`}>{label}</button>)}
      <input type="date" className="input" style={{ width: 150 }} value={from || ''} onChange={e => onChange(e.target.value, to || '')} title="From date" data-testid={`${testId}-from`} />
      <input type="date" className="input" style={{ width: 150 }} value={to || ''} onChange={e => onChange(from || '', e.target.value)} title="To date" data-testid={`${testId}-to`} />
      {(from || to) && <button type="button" onClick={() => onChange('', '')} className="tab btn-small" data-testid={`${testId}-clear`}>Clear</button>}
    </div>
  );
}
const inDateRange = (d, from, to) => (!from || (d || '') >= from) && (!to || (d || '') <= to);

// Every expense (project AND company-level) for a month / date range, grouped for printing.
function ExpenseRegisterReport({ expenses, projects, overheadCats, companies, subtitle }) {
  const [groupBy, setGroupBy] = useState('month');
  const projById = useMemo(() => Object.fromEntries((projects || []).map(p => [p.id, p])), [projects]);
  const catById = useMemo(() => Object.fromEntries((overheadCats || []).map(c => [c.id, c.name])), [overheadCats]);
  const coName = (id) => (companies.find(c => c.id === id) || {}).name || '—';
  const rows = useMemo(() => (expenses || []).map(e => {
    const proj = e.project_id ? projById[e.project_id] : null;
    const companyId = e.company_id || proj?.company_id || null;
    return {
      id: e.id, date: e.expense_date || '', voucher: e.voucher_number || '', company: coName(companyId),
      where: proj ? proj.project_name : `Company — ${catById[e.overhead_category_id] || 'overhead'}`,
      category: e.staff_id ? `${e.category || 'staff'} · ${STAFF_PAYMENT_TYPE_LABELS[e.staff_payment_type] || 'staff'}` : (e.category || '—'),
      description: e.description || '', vendor: e.vendor_name || '', amount: Number(e.amount || 0), audit: e.audit_status,
      eligible: CALC_ELIGIBLE_AUDIT_STATUSES.has(e.audit_status),
    };
  }).sort((a, b) => a.date.localeCompare(b.date) || String(a.voucher).localeCompare(String(b.voucher), undefined, { numeric: true })), [expenses, projById, catById, companies]);
  const multiCompany = new Set(rows.map(r => r.company)).size > 1;
  const keyOf = { month: r => r.date.slice(0, 7), category: r => r.category, project: r => r.where, company: r => r.company, none: () => '' }[groupBy];
  const labelOf = (k) => groupBy === 'month' ? (k ? monthLabelOf(k) : 'No date') : (k || 'All expenses');
  const groups = [];
  rows.forEach(r => { const k = keyOf(r); let g = groups.find(x => x.key === k); if (!g) { g = { key: k, rows: [] }; groups.push(g); } g.rows.push(r); });
  if (groupBy !== 'month' && groupBy !== 'none') groups.sort((a, b) => String(a.key).localeCompare(String(b.key)));
  const sum = (xs, f) => Math.round(xs.filter(f).reduce((t, r) => t + r.amount, 0) * 100) / 100;
  const confirmed = sum(rows, r => r.eligible), pending = sum(rows, r => !r.eligible);
  const exportRows = rows.map(r => ({ date: r.date, voucher: r.voucher, company: r.company, project_or_overhead: r.where, category: r.category, description: r.description, vendor: r.vendor, amount: r.amount, audit_status: r.audit }));
  const cols = 8 + (multiCompany ? 1 : 0);
  return <>
    <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
      <div className="serif text-2xl">Expense Register — Project &amp; Company Expenses</div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs text-muted inline-flex items-center gap-1 no-print">Group by
          <select className="input" style={{ width: 140 }} value={groupBy} onChange={e => setGroupBy(e.target.value)} data-testid="expreg-group">
            <option value="month">Month</option><option value="category">Category</option><option value="project">Project / overhead</option>{multiCompany && <option value="company">Company</option>}<option value="none">No grouping</option>
          </select>
        </label>
        <ReportExportBar rows={exportRows} name="expense-register" title="Expense Register" subtitle={subtitle} />
      </div>
    </div>
    <div className="flex flex-wrap gap-4 mb-3 text-sm" data-testid="expreg-totals">
      <div>{rows.length} expense{rows.length === 1 ? '' : 's'}</div>
      <div>Confirmed (counts in P&amp;L): <b className="mono">{fmtINR(confirmed)}</b></div>
      {pending > 0 && <div className="text-muted">Pending / excluded: <b className="mono">{fmtINR(pending)}</b></div>}
      <div>Total listed: <b className="mono">{fmtINR(confirmed + pending)}</b></div>
    </div>
    {groupBy !== 'none' && groups.length > 1 && <table className="tbl mb-4" style={{ maxWidth: 560 }} data-testid="expreg-summary">
      <thead><tr><th>{{ month: 'Month', category: 'Category', project: 'Project / overhead', company: 'Company' }[groupBy]}</th><th className="text-right">Entries</th><th className="text-right">Confirmed</th><th className="text-right">Pending</th></tr></thead>
      <tbody>{groups.map(g => <tr key={g.key}><td>{labelOf(g.key)}</td><td className="mono text-right">{g.rows.length}</td><td className="mono text-right">{fmtINR(sum(g.rows, r => r.eligible))}</td><td className="mono text-right text-muted">{sum(g.rows, r => !r.eligible) > 0 ? fmtINR(sum(g.rows, r => !r.eligible)) : '—'}</td></tr>)}</tbody>
      <tfoot><tr className="font-semibold"><td>Total</td><td className="mono text-right">{rows.length}</td><td className="mono text-right">{fmtINR(confirmed)}</td><td className="mono text-right">{pending > 0 ? fmtINR(pending) : '—'}</td></tr></tfoot>
    </table>}
    <table className="tbl"><thead><tr><th>Date</th><th>Voucher</th>{multiCompany && <th>Company</th>}<th>Project / overhead</th><th>Category</th><th>Description</th><th>Vendor</th><th className="text-right">Amount</th><th>Audit</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols + 1} className="text-center text-muted py-6">No expenses in this period.</td></tr>}
        {groups.map(g => <React.Fragment key={g.key}>
          {groupBy !== 'none' && <tr style={{ background: 'rgba(236,230,216,0.05)' }}><td colSpan={cols + 1} className="font-semibold">{labelOf(g.key)} <span className="text-xs text-muted">· {g.rows.length} entr{g.rows.length === 1 ? 'y' : 'ies'}</span></td></tr>}
          {g.rows.map(r => <tr key={r.id}><td className="whitespace-nowrap">{fmtDate(r.date)}</td><td className="mono">{r.voucher || '—'}</td>{multiCompany && <td className="text-xs">{r.company}</td>}<td>{r.where}</td><td>{r.category}</td><td>{r.description}</td><td>{r.vendor}</td><td className="mono text-right">{fmtINR(r.amount)}</td><td><AuditStatusChip status={r.audit} /></td></tr>)}
          {groupBy !== 'none' && <tr className="text-xs"><td colSpan={cols - 1} className="text-right text-muted">Subtotal — {labelOf(g.key)}</td><td className="mono text-right font-semibold">{fmtINR(sum(g.rows, r => r.eligible))}</td><td className="text-muted">{sum(g.rows, r => !r.eligible) > 0 ? `+${fmtINR(sum(g.rows, r => !r.eligible))} pending` : ''}</td></tr>}
        </React.Fragment>)}
      </tbody>
      <tfoot><tr className="font-semibold"><td colSpan={cols - 1} className="text-right">Total confirmed</td><td className="mono text-right">{fmtINR(confirmed)}</td><td className="text-xs text-muted">{pending > 0 ? `+${fmtINR(pending)} pending` : ''}</td></tr></tfoot>
    </table>
  </>;
}

const REPORT_LABELS = {
  project_pl: 'Project P&L',
  settlement: 'Partner Settlement — Single Project',
  cash_position: 'Cash Position',
  receivables: 'Receivables',
  investment: 'Investment Report',
  expense: 'Expense Report',
  expense_register: 'Expense Register — Project & Company Expenses',
  consolidated: 'Financial Year / Consolidated Summary',
  company_wise: 'Company Wise / Group Consolidated',
  department_wise: 'Department / Client Wise',
  partner_wise: 'Partner Settlement — All Projects',
  security_refundable: 'Security / Refundable Report',
  data_audit_log: 'Data Audit Log',
  pending_bills: 'Pending Bills',
  receipts: 'Receipts',
  staff_payments: 'Staff Payments',
};
// Reports needing a single selected project vs. a group-wide sweep —
// drives whether the project picker is shown/required in the toolbar.
const REPORT_NEEDS_PROJECT = new Set(['project_pl', 'settlement', 'cash_position', 'receivables', 'investment', 'expense']);

function ReportExportBar({ rows, name, title, subtitle }) {
  // subtitle carries whatever filter context applies (company / financial
  // year / project / date range) so the exported file — read on its own,
  // months later — still says what the numbers represent, instead of just
  // a bare title. The audit found exports silently dropping this.
  const csv = () => downloadCSV(rows, `${name}-${Date.now()}.csv`, subtitle ? [title, subtitle, ''] : [title, '']);
  const pdf = async () => {
    if (!rows || !rows.length) { toast.error('Nothing to export'); return; }
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(14); doc.text(title, 14, 16);
    let y = 22;
    if (subtitle) { doc.setFontSize(9); doc.setTextColor(90); doc.text(subtitle, 14, y); doc.setTextColor(0); y += 6; }
    doc.setFontSize(8); doc.text(`Generated ${fmtGeneratedAt()}`, 14, y);
    const keys = Object.keys(rows[0]);
    // Round 21 finding: uncapped toLocaleString defaults to 3 decimal
    // places, vs. the 2 every currency figure elsewhere in the app
    // (fmtINR) is capped at -- this exporter takes arbitrary report rows
    // and doesn't know which columns are money, so it can't add a "Rs"
    // prefix the way the ledger-specific PDF exports now do, but it can
    // still match their precision convention for any numeric column.
    autoTable(doc, {
      startY: y + 6, head: [keys], body: rows.map(r => keys.map(k => typeof r[k] === 'number' ? Number(r[k]).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : (r[k] ?? '—'))),
      styles: { fontSize: 8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] },
    });
    doc.save(`${name}-${Date.now()}.pdf`);
  };
  const xlsx = async () => {
    if (!rows || !rows.length) { toast.error('Nothing to export'); return; }
    const XLSX = await loadXlsx();
    const ws = XLSX.utils.json_to_sheet(rows, { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [[title], [subtitle || '']], { origin: 'A1' });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${name}-${Date.now()}.xlsx`);
  };
  return (
    <div className="flex gap-2 no-print">
      <button onClick={csv} className="btn-outline btn-small">CSV</button>
      <button onClick={pdf} className="btn-outline btn-small">PDF</button>
      <button onClick={xlsx} className="btn-outline btn-small">Excel</button>
    </div>
  );
}

const REPORT_BREAKDOWN_TYPES = new Set(['company_wise','department_wise','partner_wise']);
// Reports that list dated transactions, so a month / date range makes sense for them.
const REPORT_DATE_RANGE_TYPES = new Set(['expense', 'expense_register', 'receipts', 'pending_bills', 'security_refundable', 'data_audit_log']);

function Reports() {
  const { user } = useAuth();
  const canAudit = ['admin','master_admin'].includes(user.role);
  const [projects, setProjects] = useState([]);
  // User-requested: multiple selection for the project picker (previously
  // a plain single <select>) -- lets several projects' P&L/settlement/
  // cash-position/etc. be pulled at once instead of one Generate click
  // per project. `canGrantAll={false}` on the GrantPicker below (same
  // usage PartnerSettlementDashboard's own project filter already has)
  // deliberately offers no "All Projects" shortcut here -- unlike a read
  // filter, each selected project fires its own /financials fetch, so
  // "all" isn't a free action the way it is for narrowing a list.
  const [projectIds, setProjectIds] = usePersistedState('Reports.projectIds', []);
  const [type, setType] = usePersistedState('Reports.type', 'project_pl');
  const [fy, setFy] = usePersistedState('Reports.fy', '');
  const [detailed, setDetailed] = usePersistedState('Reports.detailed', false);
  const [dateFrom, setDateFrom] = usePersistedState('Reports.dateFrom', '');
  const [dateTo, setDateTo] = usePersistedState('Reports.dateTo', '');
  const [entityType, setEntityType] = usePersistedState('Reports.entityType', '');
  const [data, setData] = useState(null);
  // Round 18 finding: nothing stopped an in-flight gen() call from a
  // previous project/report-type selection from resolving AFTER a newer
  // one and overwriting data.fin with stale numbers, while the header
  // (driven by live projectId/type state) kept showing the NEW selection
  // -- a report visibly labeled one project but showing another's
  // numbers, no error. genToken tags each call; a resolving call only
  // applies its result if it's still the most recent one issued.
  const genToken = useRef(0);
  const registerRaw = useRef(null);
  const [genBusy, setGenBusy] = useState(false);
  const { companies, needsPicker: showCompanyFilter } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  // Defaults to "all" (every company this login can see) so a caller who
  // never touches this filter gets exactly the old, unfiltered behavior.
  const [companyFilterIds, setCompanyFilterIds] = usePersistedState('Reports.companyFilterIds', []);
  const [allCompaniesSelected, setAllCompaniesSelected] = usePersistedState('Reports.allCompaniesSelected', true);
  const masters = useMasters();
  // Real-usage-gap finding: "Partner Settlement — All Projects" (the
  // reworked partner_wise report) can now be narrowed to one or two
  // partners — defaults to "all" so the existing behavior is unchanged
  // until a caller actually picks someone.
  const [partners, setPartners] = useState([]);
  const [partnerFilterIds, setPartnerFilterIds] = usePersistedState('Reports.partnerFilterIds', []);
  const [allPartnersSelected, setAllPartnersSelected] = usePersistedState('Reports.allPartnersSelected', true);
  // User-reported real gap (also fixed in Partner Payouts Summary): the
  // Partner filter's own picker below now narrows to partners actually
  // linked to the selected company(ies) -- this clears a selection that's
  // no longer visible once the company filter changes out from under it,
  // same stale-selection guard this file's other company+something
  // filter pairs already apply.
  useEffect(() => {
    if (allCompaniesSelected) return;
    setPartnerFilterIds(cur => cur.filter(id => {
      const p = partners.find(x => x.id === id);
      return p && (p.company_ids||(p.company_id?[p.company_id]:[])).some(cid => companyFilterIds.includes(cid));
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyFilterIds, allCompaniesSelected]);
  // User-requested: "client name selection option to generate a report of
  // specific clients" -- same GrantPicker convention as the partner filter
  // just above. For Department / Client Wise, narrows which client
  // row(s)/total the breakdown itself returns (department_filter, mirrors
  // partner_filter server-side); for every project-scoped report type, it
  // narrows the Project picker to just that client's projects instead, so
  // a client with several projects doesn't need to be found by name one at
  // a time.
  const [clientFilterIds, setClientFilterIds] = usePersistedState('Reports.clientFilterIds', []);
  const [allClientsSelected, setAllClientsSelected] = usePersistedState('Reports.allClientsSelected', true);

  // User-requested: Bank Account Ledger and Company (Fund) Ledger, both
  // "kept connected" to their existing homes (Settings > Bank Accounts,
  // Company Expenses) rather than forked into new copies -- picking an
  // account/company here just opens/renders the SAME components those
  // pages already use, via the same GET endpoints.
  const [bankAccounts, setBankAccounts] = useState([]);
  const [reportBankAccountId, setReportBankAccountId] = useState('');
  const [reportLedgerCompanyId, setReportLedgerCompanyId] = useState('');
  useEffect(() => { api.get('/bank-accounts').then(r=>setBankAccounts(r.data)).catch(()=>{}); }, []);

  useEffect(() => { api.get('/projects').then(r=>setProjects(r.data)); api.get('/partners').then(r=>setPartners(r.data)); }, []);

  const companyFilterParam = (!showCompanyFilter || allCompaniesSelected) ? undefined : companyFilterIds.join(',');
  // The single-project reports don't take a company_filter param at all
  // (they're scoped by the chosen project, not a company list) -- instead
  // the project picker itself is narrowed to the selected companies, so a
  // filtered-out company's projects simply aren't selectable.
  const visibleProjects = ((!showCompanyFilter || allCompaniesSelected) ? projects : projects.filter(p => companyFilterIds.includes(p.company_id)))
    .filter(p => allClientsSelected || !clientFilterIds.length || clientFilterIds.includes(clientIdOfProject(p, masters.department_client)));
  const clientOptions = useMemo(() =>
    (masters.department_client || [])
      .filter(m => (!showCompanyFilter || allCompaniesSelected) || (m.company_ids||[]).some(cid => companyFilterIds.includes(cid)))
      .slice().sort((a,b)=>a.name.localeCompare(b.name)),
    [masters.department_client, showCompanyFilter, allCompaniesSelected, companyFilterIds]);
  // If the company filter changes out from under an already-picked
  // project (its company just got deselected), drop the stale selection(s)
  // rather than silently keep generating a report for a now-hidden project.
  // Same for a client selection narrowing the project picker further.
  useEffect(() => {
    setProjectIds(cur => cur.filter(id => visibleProjects.some(p => p.id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyFilterIds, allCompaniesSelected, clientFilterIds, allClientsSelected]);
  // A company selection change can leave a stale Client selection that no
  // longer belongs to it -- clear rather than silently filtering to zero.
  useEffect(() => {
    if (!allClientsSelected && clientFilterIds.length) {
      setClientFilterIds(cur => cur.filter(id => clientOptions.some(c => c.id === id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyFilterIds, allCompaniesSelected]);
  // The outer print-header/subtitle only ever names ONE project by
  // itself -- with 2+ selected, each project gets its own "Project: X"
  // heading and its own accurate per-project subtitle instead (see
  // subtitleFor, used per-block in the render section below); the outer
  // one degrades to just the company/FY/date context, same as any other
  // multi-scope report already does.
  const singleProjectId = projectIds.length === 1 ? projectIds[0] : '';
  const printCompanyName = (() => {
    if (!REPORT_NEEDS_PROJECT.has(type) || !singleProjectId) return null;
    const proj = projects.find(p => p.id === singleProjectId);
    const co = proj && companies.find(c => c.id === proj.company_id);
    return co ? (co.legal_name || co.name) : null;
  })();
  // Printed into every export (PDF header, CSV/Excel preamble) alongside
  // the report title, so a downloaded file still says what it represents
  // months later — the audit found exports carrying no filter context at
  // all beyond a bare title.
  const companyFilterSubtitle = showCompanyFilter && !allCompaniesSelected
    ? (companyFilterIds.length === 1 ? companies.find(c => c.id === companyFilterIds[0])?.name : `${companyFilterIds.length} companies`)
    : null;
  const reportSubtitle = [
    printCompanyName || companyFilterSubtitle,
    fy || null,
    singleProjectId ? (projects.find(p => p.id === singleProjectId)?.project_name) : (REPORT_NEEDS_PROJECT.has(type) && projectIds.length > 1 ? `${projectIds.length} projects selected` : null),
    ((dateFrom || dateTo) && REPORT_DATE_RANGE_TYPES.has(type)) ? `${dateFrom || '…'} to ${dateTo || '…'}` : null,
  ].filter(Boolean).join(' · ') || null;
  // Per-project variant of the subtitle above, used on each project's own
  // block (and its own CSV/PDF/Excel export via ReportExportBar) when
  // multiple projects are selected at once -- resolves that PROJECT's own
  // company rather than the single hoisted printCompanyName, since a
  // multi-select spanning "All Companies" can genuinely mix companies.
  const subtitleFor = (pid) => {
    const proj = projects.find(p => p.id === pid);
    const co = proj && companies.find(c => c.id === proj.company_id);
    return [
      (co && (co.legal_name || co.name)) || companyFilterSubtitle,
      fy || null,
      proj?.project_name,
      ((dateFrom || dateTo) && REPORT_DATE_RANGE_TYPES.has(type)) ? `${dateFrom || '…'} to ${dateTo || '…'}` : null,
    ].filter(Boolean).join(' · ') || null;
  };

  const gen = async (silent) => {
    const quiet = silent === true;      // true only from the automatic refresh; a button click passes an event object
    const myToken = ++genToken.current;
    if (!quiet) setGenBusy(true);
    try {
      let result;
      if (type === 'consolidated') {
        const r = await api.get('/dashboard', { params: { ...(fy?{financial_year:fy}:{}), ...(companyFilterParam?{company_filter:companyFilterParam}:{}) } });
        result = { type, dashboard: r.data };
      } else if (REPORT_BREAKDOWN_TYPES.has(type)) {
        const dimension = { company_wise: 'company', department_wise: 'department', partner_wise: 'partner' }[type];
        const partnerFilterParam = (dimension==='partner' && !allPartnersSelected && partnerFilterIds.length) ? partnerFilterIds.join(',') : undefined;
        const departmentFilterParam = (dimension==='department' && !allClientsSelected && clientFilterIds.length) ? clientFilterIds.join(',') : undefined;
        const r = await api.get('/reports/breakdown', { params: { dimension, ...(fy?{financial_year:fy}:{}) , detailed, ...(companyFilterParam?{company_filter:companyFilterParam}:{}), ...(partnerFilterParam?{partner_filter:partnerFilterParam}:{}), ...(departmentFilterParam?{department_filter:departmentFilterParam}:{}) } });
        result = { type, breakdown: r.data };
      } else if (type === 'security_refundable') {
        const extraParams = companyFilterParam ? { company_filter: companyFilterParam } : {};
        const [i, s] = await Promise.all([api.get('/security-instruments', { params: extraParams }), api.get('/security-instruments/summary', { params: extraParams })]);
        const ranged = !!(dateFrom || dateTo);
        // the server summary covers every instrument, so it is only shown when no date range narrows the list
        result = { type, items: i.data.filter(x => inDateRange(x.date_lodged, dateFrom, dateTo)), summary: ranged ? null : s.data };
      } else if (type === 'data_audit_log') {
        const r = await api.get('/reports/data-audit-log', { params: { ...(entityType?{entity_type:entityType}:{}), ...(dateFrom?{date_from:dateFrom}:{}), ...(dateTo?{date_to:dateTo}:{}), ...(companyFilterParam?{company_filter:companyFilterParam}:{}) } });
        result = { type, logs: r.data };
      } else if (type === 'expense_register') {
        // one fetch of everything the login may see; month / date changes re-filter the cached copy instead of re-downloading
        if (!quiet || !registerRaw.current) {
          const [er, pr, oc] = await Promise.all([api.get('/expenses'), api.get('/projects', { params: { include_extra_work: true } }), api.get('/expense-categories', { params: { scope: 'overhead' } })]);
          registerRaw.current = { expenses: er.data, projects: pr.data, overheadCats: oc.data };
        }
        const raw = registerRaw.current;
        const projById = Object.fromEntries(raw.projects.map(p => [p.id, p]));
        const filtered = raw.expenses
          .filter(e => (!showCompanyFilter || allCompaniesSelected) || companyFilterIds.includes(e.company_id || projById[e.project_id]?.company_id))
          .filter(e => inDateRange(e.expense_date, dateFrom, dateTo));
        result = { type, expenses: filtered, projects: raw.projects, overheadCats: raw.overheadCats };
      } else if (type === 'staff_payments') {
        result = { type };   // StaffPaymentReport fetches and filters itself
      } else if (type === 'pending_bills') {
        const r = await api.get('/bills', { params: { include_allocation: true, ...(companyFilterParam?{company_filter:companyFilterParam}:{}) } });
        result = { type, bills: r.data.filter(b => inDateRange(b.bill_date, dateFrom, dateTo)) };
      } else if (type === 'receipts') {
        // User-requested: Receipts covered under Reports too. GET /receipts
        // has no server-side company filter (same as the Income page it's
        // otherwise identical to), so this narrows client-side the same
        // way Income already does; date range reuses the same dateFrom/
        // dateTo state data_audit_log already exposes.
        const r = await api.get('/receipts');
        const filtered = r.data
          .filter(x => (!showCompanyFilter || allCompaniesSelected) || companyFilterIds.includes(projects.find(p=>p.id===x.project_id)?.company_id))
          .filter(x => !dateFrom || x.receipt_date >= dateFrom)
          .filter(x => !dateTo || x.receipt_date <= dateTo);
        result = { type, receipts: filtered };
      } else {
        // User-requested: multiple projects at once for every project-
        // scoped report type. 'expense' doesn't fetch /financials at all
        // (ExpenseReport self-fetches off its own projectId prop) -- fins
        // just carries the id/name for it to key off of; every other type
        // here fetches the SAME endpoint the single-project version always
        // used, just once per selected project instead of only ever one.
        if (!projectIds.length) { toast.error('Select at least one project'); return; }
        const fins = await Promise.all(projectIds.map(async pid => {
          const project_name = projects.find(p => p.id === pid)?.project_name;
          if (type === 'expense') return { projectId: pid, project_name };
          const r = await api.get(`/projects/${pid}/financials`);
          return { projectId: pid, project_name, fin: r.data };
        }));
        result = { type, fins };
      }
      if (genToken.current === myToken) setData(result);
    } catch (e) {
      if (genToken.current === myToken && !quiet) toast.error(e.response?.data?.detail || 'Error');
    } finally {
      if (genToken.current === myToken && !quiet) setGenBusy(false);
    }
  };
  // A report that is already on screen is re-read when the tab regains focus, so its money figures never go stale.
  const reportRef = useRef(null); reportRef.current = data;
  useLiveRefresh(() => { if (reportRef.current && reportRef.current.type !== 'data_audit_log' && reportRef.current.type !== 'expense_register') gen(true); });
  // Changing the month / date range (or company) while a dated report is on screen re-applies it straight away.
  useEffect(() => {
    if (reportRef.current && reportRef.current.type === type && REPORT_DATE_RANGE_TYPES.has(type)) gen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="section-eyebrow mb-2">Insights</div>
      <h1 className="serif text-4xl mb-6">Reports</h1>
      <div className="card card-dark flex flex-wrap gap-3 mb-6">
        {showCompanyFilter && <GrantPicker
          items={companies}
          selectedIds={companyFilterIds}
          allAccess={allCompaniesSelected}
          onToggle={id => setCompanyFilterIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id])}
          onSetAllAccess={v => { setAllCompaniesSelected(v); if (v) setCompanyFilterIds([]); }}
          allLabel="All Companies"
          noneWidth={200}
          data-testid="report-filter-companies"
        />}
        <select className="input" style={{width:260}} value={type} onChange={e=>setType(e.target.value)} data-testid="report-type">
          <option value="project_pl">Project P&L</option>
          <option value="settlement">Partner Settlement — Single Project</option>
          <option value="cash_position">Cash Position</option>
          <option value="receivables">Receivables</option>
          <option value="investment">Investment Report</option>
          <option value="expense">Expense Report — per project</option>
          <option value="expense_register">Expense Register — all expenses (project + company)</option>
          <option value="consolidated">Financial Year / Consolidated Summary</option>
          <option value="company_wise">Company Wise / Group Consolidated</option>
          <option value="department_wise">Department / Client Wise</option>
          <option value="partner_wise">Partner Settlement — All Projects</option>
          {user.role !== 'partner' && <option value="security_refundable">Security / Refundable Report</option>}
          <option value="pending_bills">Pending Bills</option>
          <option value="receipts">Receipts</option>
          {['admin','accountant','master_admin'].includes(user.role) && <option value="staff_payments">Staff Payments (per staff / category)</option>}
          {user.role !== 'partner' && <option value="bank_ledger">Bank Account Ledger</option>}
          {user.role !== 'partner' && <option value="company_ledger">Company (Fund) Ledger</option>}
          {canAudit && <option value="data_audit_log">Data Audit Log</option>}
        </select>
        {(REPORT_NEEDS_PROJECT.has(type) || type==='department_wise') && clientOptions.length > 0 && <GrantPicker
          items={clientOptions}
          selectedIds={clientFilterIds}
          allAccess={allClientsSelected}
          onToggle={id => setClientFilterIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id])}
          onSetAllAccess={v => { setAllClientsSelected(v); if (v) setClientFilterIds([]); }}
          allLabel="All Clients"
          noneWidth={220}
          data-testid="report-filter-clients"
        />}
        {REPORT_NEEDS_PROJECT.has(type) && <GrantPicker
          items={showCompanyFilter ? visibleProjects.map(p=>({...p, name: p.project_name + ` — ${companyName(p.company_id)}`})) : visibleProjects.map(p=>({...p, name: p.project_name}))}
          selectedIds={projectIds}
          allAccess={false}
          onToggle={pid => setProjectIds(cur => cur.includes(pid) ? cur.filter(x=>x!==pid) : [...cur, pid])}
          onSetAllAccess={()=>{}}
          canGrantAll={false}
          allLabel="All Projects"
          noneWidth={280}
          data-testid="report-project"
        />}
        {type==='bank_ledger' && <select className="input" style={{width:320}} value={reportBankAccountId} onChange={e=>setReportBankAccountId(e.target.value)} data-testid="report-bank-account">
          <option value="">-- select bank account --</option>
          {((!showCompanyFilter || allCompaniesSelected) ? bankAccounts : bankAccounts.filter(b=>companyFilterIds.includes(b.company_id))).map(b =>
            <option key={b.id} value={b.id}>{b.bank_name}{b.account_number ? ' ••'+String(b.account_number).slice(-4) : ''}{showCompanyFilter ? ` — ${companyName(b.company_id)}` : ''}</option>
          )}
        </select>}
        {type==='company_ledger' && <select className="input" style={{width:260}} value={reportLedgerCompanyId} onChange={e=>setReportLedgerCompanyId(e.target.value)} data-testid="report-ledger-company">
          <option value="">-- select company --</option>
          {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>}
        {(REPORT_BREAKDOWN_TYPES.has(type) || type==='consolidated') && <FySelect value={fy} onChange={setFy} years={distinctFYs(projects.map(p=>p.financial_year))} style={{width:160}} testId="report-fy"/>}
        {REPORT_BREAKDOWN_TYPES.has(type) && <label className="flex items-center gap-2 text-sm px-3"><input type="checkbox" checked={detailed} onChange={e=>setDetailed(e.target.checked)} data-testid="report-detailed"/>Detailed (per-project)</label>}
        {type==='partner_wise' && <GrantPicker
          items={(allCompaniesSelected ? partners : partners.filter(p => (p.company_ids||(p.company_id?[p.company_id]:[])).some(cid => companyFilterIds.includes(cid))))
            .map(p => showCompanyFilter ? {...p, name: p.name + ((p.company_ids||[]).length ? ` — ${p.company_ids.map(companyName).join(', ')}` : '')} : p)}
          selectedIds={partnerFilterIds}
          allAccess={allPartnersSelected}
          onToggle={id => setPartnerFilterIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id])}
          onSetAllAccess={v => { setAllPartnersSelected(v); if (v) setPartnerFilterIds([]); }}
          allLabel="All Partners"
          noneWidth={200}
          data-testid="report-filter-partners"
        />}
        {type==='data_audit_log' && <select className="input" style={{width:160}} value={entityType} onChange={e=>setEntityType(e.target.value)}>
          <option value="">All Entity Types</option>
          {['project','expense','receipt','investment','deduction','partner','master','profile','import','bulk_import'].map(t=><option key={t} value={t}>{t}</option>)}
        </select>}
        {REPORT_DATE_RANGE_TYPES.has(type) && <PeriodPicker from={dateFrom} to={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} testId="report-period" />}
        {type!=='bank_ledger' && type!=='company_ledger' && <button onClick={gen} disabled={genBusy} className="btn-gold btn-small" data-testid="report-generate">{genBusy ? 'Generating…' : 'Generate'}</button>}
        {type!=='bank_ledger' && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><FileText size={14}/>Print</button>}
      </div>
      {type==='bank_ledger' && !reportBankAccountId && <div className="text-muted">Select a bank account above to open its ledger.</div>}
      {type==='bank_ledger' && reportBankAccountId && (() => {
        const acct = bankAccounts.find(b => b.id === reportBankAccountId);
        return acct ? <BankLedgerModal account={acct} companyName={companies.find(c=>c.id===acct.company_id)?.name} canEdit={false} onClose={()=>setReportBankAccountId('')}/> : null;
      })()}
      {type==='company_ledger' && !reportLedgerCompanyId && <div className="text-muted">Select a company above to view its Fund Ledger.</div>}
      {type==='company_ledger' && reportLedgerCompanyId && (
        <CompanyFundLedgerCard companyId={reportLedgerCompanyId} companyLabel={companies.find(c=>c.id===reportLedgerCompanyId)?.name} canEdit={false} printable/>
      )}
      {type!=='bank_ledger' && type!=='company_ledger' && !data && <div className="text-muted">Select a report and click Generate.</div>}
      {type!=='bank_ledger' && type!=='company_ledger' && data && (
        <div className="card card-dark print-area overflow-x-auto">
          <div className="print-header">
            <div className="print-company">{printCompanyName || companyFilterSubtitle || 'Consolidated — All Companies'}</div>
            <div className="print-report-title">{REPORT_LABELS[data.type] || 'Report'}</div>
            <div className="print-meta">{REPORT_DATE_RANGE_TYPES.has(data.type) && (dateFrom || dateTo) ? `Period: ${periodLabel(dateFrom, dateTo)} · ` : ''}Generated {new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>
          </div>
          {/* User-requested: multiple projects at once -- each selected
              project renders its own report block, in the same layout a
              single selection always used, just repeated (with a "Project:
              X" heading and a divider once there's more than one so it's
              never ambiguous which numbers belong to which project). */}
          {REPORT_NEEDS_PROJECT.has(data.type) && (data.fins||[]).map((f, i) => (
            <div key={f.projectId} className={i>0 ? 'mt-8 pt-8' : ''} style={i>0 ? {borderTop:'1px solid var(--border-subtle)'} : undefined}>
              {data.fins.length > 1 && <div className="text-xs text-muted mb-2 uppercase tracking-wide">Project: <b className="text-[color:var(--gold)]">{f.project_name}</b></div>}
              {data.type==='project_pl' && <ProjectPLView fin={f.fin} subtitle={subtitleFor(f.projectId)}/>}
              {data.type==='settlement' && <SettlementReport fin={f.fin} subtitle={subtitleFor(f.projectId)}/>}
              {data.type==='cash_position' && <CashPositionReport fin={f.fin} subtitle={subtitleFor(f.projectId)}/>}
              {data.type==='receivables' && <ReceivablesReport fin={f.fin} subtitle={subtitleFor(f.projectId)}/>}
              {data.type==='investment' && <SimpleTable rows={f.fin?.settlements?.map(s=>({partner:s.partner_name, investment:s.investment}))||[]} name="investment" title="Investment Report" subtitle={subtitleFor(f.projectId)}/>}
              {data.type==='expense' && <ExpenseReport projectId={f.projectId} subtitle={subtitleFor(f.projectId)} dateFrom={dateFrom} dateTo={dateTo}/>}
            </div>
          ))}
          {data.type==='consolidated' && <ConsolidatedReport dashboard={data.dashboard} subtitle={reportSubtitle}/>}
          {data.type==='company_wise' && <CompanyWiseReport breakdown={data.breakdown} subtitle={reportSubtitle}/>}
          {data.type==='department_wise' && <DepartmentWiseReport breakdown={data.breakdown} masters={masters} subtitle={reportSubtitle}/>}
          {data.type==='partner_wise' && <PartnerWiseReport breakdown={data.breakdown} subtitle={reportSubtitle}/>}
          {data.type==='security_refundable' && <SecurityRefundableReport items={data.items} summary={data.summary} subtitle={reportSubtitle}/>}
          {data.type==='data_audit_log' && <DataAuditLogReport logs={data.logs} subtitle={reportSubtitle} onRestored={gen}/>}
          {data.type==='pending_bills' && <PendingBillsReport bills={data.bills} projects={projects} subtitle={reportSubtitle}/>}
          {data.type==='staff_payments' && <StaffPaymentReport companyIds={(showCompanyFilter && !allCompaniesSelected) ? companyFilterIds : null}/>}
          {data.type==='expense_register' && <ExpenseRegisterReport expenses={data.expenses} projects={data.projects} overheadCats={data.overheadCats} companies={companies} subtitle={reportSubtitle}/>}
          {data.type==='receipts' && <ReceiptsReport receipts={data.receipts} projects={projects} subtitle={reportSubtitle}/>}
        </div>
      )}
    </div>
  );
}

function DataAuditLogReport({ logs, subtitle, onRestored }) {
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const restore = async (l) => {
    if (!window.confirm(`Restore this deleted ${l.entity_type.replace(/_/g,' ').replace(/-/g,' ')}? It will come back exactly as it was right before deletion.`)) return;
    try { await api.post(`/audit-logs/${l.id}/restore`); toast.success('Restored'); onRestored?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not restore'); }
  };
  const filtered = (logs||[]).filter(l => {
    if (action && l.action !== action) return false;
    if (q) {
      const hay = `${l.user_email||''} ${l.entity_type||''} ${l.entity_id||''} ${JSON.stringify(l.old_value||{})} ${JSON.stringify(l.new_value||{})}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  const actions = [...new Set((logs||[]).map(l=>l.action).filter(Boolean))];
  const rows = filtered.map(l => ({
    when: l.created_at ? new Date(l.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '',
    auditor: l.user_email || '—',
    entity_type: l.entity_type || '',
    action: l.action || '',
    entity_id: l.entity_id || '',
    change: summarizeAuditChange(l),
  }));
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4 no-print">
        <input className="input" style={{width:280}} placeholder="Search auditor, entity, values..." value={q} onChange={e=>setQ(e.target.value)}/>
        <select className="input" style={{width:160}} value={action} onChange={e=>setAction(e.target.value)}>
          <option value="">All Actions</option>
          {actions.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <div className="text-muted text-sm self-center">{filtered.length} of {(logs||[]).length} entries</div>
      </div>
      <ReportExportBar rows={rows} name="data_audit_log" title="Data Audit Log" subtitle={subtitle}/>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted border-b border-white/10">
            <th className="py-2 pr-4">Date &amp; Time</th>
            <th className="py-2 pr-4">Auditor</th>
            <th className="py-2 pr-4">Entity</th>
            <th className="py-2 pr-4">Action</th>
            <th className="py-2 pr-4">Change</th>
            {isMasterAdmin(user) && <th className="py-2 pr-4 no-print"></th>}
          </tr></thead>
          <tbody>
            {rows.map((r,i)=>(
              <tr key={i} className="border-b border-white/5">
                <td className="py-2 pr-4 whitespace-nowrap">{r.when}</td>
                <td className="py-2 pr-4">{r.auditor}</td>
                <td className="py-2 pr-4">{r.entity_type}</td>
                <td className="py-2 pr-4 capitalize">{r.action}</td>
                <td className="py-2 pr-4 text-muted">{r.change}</td>
                {isMasterAdmin(user) && <td className="py-2 pr-4 no-print">
                  {filtered[i].action === 'delete' && RESTORABLE_ENTITY_TYPES.has(filtered[i].entity_type) && <button onClick={()=>restore(filtered[i])} className="tab btn-small inline-flex items-center gap-1" data-testid={`restore-${filtered[i].id}`}><Undo2 size={12}/>Restore</button>}
                </td>}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={isMasterAdmin(user) ? 6 : 5} className="py-6 text-center text-muted">No audit entries match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function summarizeAuditChange(l) {
  const oldV = l.old_value, newV = l.new_value;
  if (l.action === 'delete') return 'Record deleted';
  if (l.action === 'create') return 'Record created';
  if (!oldV || !newV) return '';
  const keys = new Set([...Object.keys(oldV||{}), ...Object.keys(newV||{})]);
  const diffs = [];
  for (const k of keys) {
    if (k === 'updated_at' || k === 'created_at') continue;
    const a = oldV[k], b = newV[k];
    if (JSON.stringify(a) !== JSON.stringify(b)) diffs.push(`${k}: ${a??'—'} → ${b??'—'}`);
  }
  return diffs.slice(0,3).join('; ') || 'No field changes';
}

// Bills with something still owed, across every project the caller can
// see (respecting the Reports page's own company multi-select) — reuses
// the existing GET /bills (include_allocation:true, company_filter) with
// no new backend endpoint; a bill counts as "pending" using the same
// outstanding_amount/net_bank_credit fallback and 0.5 threshold BillsHub's
// own "Pending only" filter already uses, for consistency.
function PendingBillsReport({ bills, projects, subtitle }) {
  const projectName = (id) => projects.find(p=>p.id===id)?.project_name || '—';
  const pending = (bills||[]).filter(b => (b.outstanding_amount ?? b.net_bank_credit ?? 0) > 0.5);
  const totalOutstanding = pending.reduce((a,b) => a + (b.outstanding_amount ?? b.net_bank_credit ?? 0), 0);
  const rows = pending.map(b => ({
    project: projectName(b.project_id), bill_number: b.bill_number || '—', bill_date: fmtDate(b.bill_date),
    gross_amount: b.gross_amount, net_bank_credit: b.net_bank_credit, received: b.allocated_amount ?? 0,
    outstanding: b.outstanding_amount ?? b.net_bank_credit, status: b.status,
  }));
  // User-requested: Gross/Net Credit/Received only ever had Outstanding's
  // own total shown, as a text line above the table -- matching every
  // other report's tfoot convention (Company/Department-Wise) for the
  // other three amount columns too.
  const totalGross = rows.reduce((a,r)=>a+Number(r.gross_amount||0),0);
  const totalNetCredit = rows.reduce((a,r)=>a+Number(r.net_bank_credit||0),0);
  const totalReceived = rows.reduce((a,r)=>a+Number(r.received||0),0);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="serif text-2xl">Pending Bills</div>
          <div className="text-muted text-xs">{pending.length} bill(s) with an outstanding balance — total {fmtINR(totalOutstanding)}</div>
        </div>
        <ReportExportBar rows={rows} name="pending-bills" title="Pending Bills" subtitle={subtitle}/>
      </div>
      <table className="tbl">
        <thead><tr><th>Project</th><th>Bill #</th><th>Date</th><th className="text-right">Gross</th><th className="text-right">Net Credit</th><th className="text-right">Received</th><th className="text-right">Outstanding</th><th>Status</th></tr></thead>
        <tbody>
          {rows.length===0 && <tr><td colSpan={8} className="text-center text-muted py-6">No pending bills — everything's been received.</td></tr>}
          {rows.map((r,i) => (
            <tr key={i}>
              <td>{r.project}</td><td>{r.bill_number}</td><td className="text-xs">{r.bill_date}</td>
              <td className="mono text-right">{fmtINR(r.gross_amount)}</td>
              <td className="mono text-right">{fmtINR(r.net_bank_credit)}</td>
              <td className="mono text-right">{fmtINR(r.received)}</td>
              <td className="mono text-right text-red-400">{fmtINR(r.outstanding)}</td>
              <td><span className="badge status-running">{r.status}</span></td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr className="font-semibold">
              <td colSpan={3} className="text-right text-xs text-muted">Total</td>
              <td className="mono text-right">{fmtINR(totalGross)}</td>
              <td className="mono text-right">{fmtINR(totalNetCredit)}</td>
              <td className="mono text-right">{fmtINR(totalReceived)}</td>
              <td className="mono text-right text-red-400">{fmtINR(totalOutstanding)}</td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// User-requested: Receipts covered under Reports too, matching every
// other listing report here (Pending Bills, Security/Refundable) --
// same CALC_ELIGIBLE_AUDIT_STATUSES split as the Income page itself, so
// the "Confirmed" figure here always agrees with what Income shows.
function ReceiptsReport({ receipts, projects, subtitle }) {
  const projectName = (id) => projects.find(p=>p.id===id)?.project_name || '—';
  const list = receipts || [];
  const confirmed = list.reduce((a,r)=>a+(CALC_ELIGIBLE_AUDIT_STATUSES.has(r.audit_status)?Number(r.amount||0):0),0);
  const pending = list.reduce((a,r)=>a+(CALC_ELIGIBLE_AUDIT_STATUSES.has(r.audit_status)?0:Number(r.amount||0)),0);
  const rows = list.map(r => ({
    date: fmtDate(r.receipt_date), project: projectName(r.project_id),
    type: (r.receipt_type||'').replace(/_/g,' '), amount: r.amount, audit_status: r.audit_status,
  }));
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="serif text-2xl">Receipts</div>
          <div className="text-muted text-xs">{list.length} entries — Confirmed {fmtINR(confirmed)}{pending>0?`, Pending Review ${fmtINR(pending)}`:''}</div>
        </div>
        <ReportExportBar rows={rows} name="receipts" title="Receipts" subtitle={subtitle}/>
      </div>
      <table className="tbl">
        <thead><tr><th>Date</th><th>Project</th><th>Type</th><th className="text-right">Amount</th><th>Audit</th></tr></thead>
        <tbody>
          {list.length===0 && <tr><td colSpan={5} className="text-center text-muted py-6">No receipts match these filters.</td></tr>}
          {list.map(r => (
            <tr key={r.id}>
              <td className="text-xs">{fmtDate(r.receipt_date)}</td>
              <td>{r.project_id ? <Link to={`/app/projects/${r.project_id}`} className="hover:text-[color:var(--gold)]">{projectName(r.project_id)}</Link> : '—'}</td>
              <td className="text-xs">{(r.receipt_type||'').replace(/_/g,' ')}</td>
              <td className="mono text-right text-green-400">{fmtINR(r.amount)}</td>
              <td><AuditStatusChip status={r.audit_status}/></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectPLView({ fin, subtitle }) {
  const t = fin.totals; const p = fin.project;
  const rows = [
    { line: 'Contract (Net)', amount: t.net_contract_value },
    { line: 'Expenses', amount: -t.total_expenses },
    ...(t.total_deductions_non_recoverable > 0 ? [{ line: 'Non-Recoverable Deductions', amount: -t.total_deductions_non_recoverable }] : []),
    ...(t.total_security_forfeited > 0 ? [{ line: 'Forfeited Security', amount: -t.total_security_forfeited }] : []),
    { line: 'Project Profit', amount: t.project_profit },
    { line: 'Profit Adjustment', amount: t.profit_adjustment },
    { line: 'Distributable Profit', amount: t.distributable_profit },
    ...(fin.cash_backing ? [
      { line: 'Profit realised in cash (receipts - costs)', amount: fin.cash_backing.cash_realised_profit },
      { line: 'Profit still to be collected', amount: fin.cash_backing.uncollected_profit },
      { line: "Partners' profit payable now (cash-backed)", amount: fin.cash_backing.totals.payable_now },
      ...(fin.cash_backing.totals.paid_ahead > 0.5 ? [{ line: 'Paid to partners ahead of collection', amount: fin.cash_backing.totals.paid_ahead }] : []),
    ] : []),
  ];
  return <>
    {/* Mobile audit fix: a real project name can be long (confirmed in
        production data), so this row now wraps instead of risking the
        export buttons squeezed against an un-wrapping title. */}
    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
      <div><div className="serif text-2xl">{p.project_name}</div><div className="text-muted text-xs">{p.financial_year} · P&L</div></div>
      <ReportExportBar rows={rows} name="project-pl" title={`Project P&L — ${p.project_name}`} subtitle={subtitle}/>
    </div>
    <table className="tbl"><thead><tr><th>Line</th><th className="text-right">Amount</th></tr></thead><tbody>
      {rows.map((r,i) => <tr key={i}><td>{r.line}</td><td className="mono text-right">{fmtINR(r.amount)}</td></tr>)}
    </tbody></table>
  </>;
}
function CashPositionReport({ fin, subtitle }) {
  const t = fin.totals;
  const rows = [{ metric: 'Cash Received', amount: t.cash_received }, { metric: 'Cash Paid', amount: t.cash_paid }, { metric: 'Cash Position', amount: t.cash_position },
    ...(t.cash_earmarked_company_fund > 0 ? [{ metric: '  — of which earmarked for Company Fund', amount: t.cash_earmarked_company_fund }] : []),
    ...(fin.cash_backing ? [{ metric: 'Profit realised in cash (receipts - costs)', amount: fin.cash_backing.cash_realised_profit }, { metric: "Partners' profit payable now (cash-backed, after payouts)", amount: fin.cash_backing.totals.payable_now }, ...(fin.cash_backing.totals.paid_ahead > 0.5 ? [{ metric: 'Paid to partners ahead of collection', amount: fin.cash_backing.totals.paid_ahead }] : [])] : [])];
  return <><div className="flex flex-wrap items-center justify-between gap-2 mb-4"><div className="serif text-2xl">Cash Position — {fin.project.project_name}</div><ReportExportBar rows={rows} name="cash-position" title={`Cash Position — ${fin.project.project_name}`} subtitle={subtitle}/></div>
    <table className="tbl"><tbody>{rows.map((r,i)=><tr key={i}><td>{r.metric}</td><td className="mono text-right">{fmtINR(r.amount)}</td></tr>)}</tbody></table>
    <div className="text-xs text-muted mt-3">Note: Cash position is NOT project profit.</div>
  </>;
}
function ReceivablesReport({ fin, subtitle }) {
  const t = fin.totals;
  const rows = [{item:'Contract Net', v: t.net_contract_value}, {item:'Received', v: t.total_receipts}, {item:'Deductions (withheld, not owed)', v: t.total_deductions}, {item:'Outstanding Receivable', v: t.outstanding_receivable}, {item:'  — Recoverable Deductions', v: t.total_deductions_recoverable}, {item:'  — Non-Recoverable Deductions', v: t.total_deductions_non_recoverable}];
  return <><div className="flex flex-wrap items-center justify-between gap-2 mb-4"><div className="serif text-2xl">Receivables — {fin.project.project_name}</div><ReportExportBar rows={rows} name="receivables" title={`Receivables — ${fin.project.project_name}`} subtitle={subtitle}/></div>
    <table className="tbl"><tbody>{rows.map((r,i)=><tr key={i}><td>{r.item}</td><td className="mono text-right">{fmtINR(r.v)}</td></tr>)}</tbody></table>
  </>;
}
function SettlementReport({ fin, subtitle }) {
  return <><div className="flex flex-wrap items-center justify-between gap-2 mb-4"><div className="serif text-2xl">Settlement — {fin.project.project_name}</div><ReportExportBar rows={fin.settlements.map(x => { const c = (fin.cash_backing?.partners || []).find(y => y.partner_id === x.partner_id) || {}; return { ...x, cash_backed: c.cash_backed, payable_now: c.payable_now, paid_ahead: c.paid_ahead, awaiting_collection: c.deferred }; })} name="settlement" title={`Settlement — ${fin.project.project_name}`} subtitle={subtitle}/></div>
    <SettlementPanel fin={fin} projectId={fin.project.id} reload={()=>{}} canApprove={false} user={null}/></>;
}
function SimpleTable({ rows, name, title, subtitle }) {
  if (!rows.length) return <div className="text-muted">No data.</div>;
  const keys = Object.keys(rows[0]);
  // User-requested: a total row, same tfoot convention Company/Department-
  // Wise reports already use (see their own `t &&` footer) -- computed
  // client-side here since this generic table (used by Consolidated
  // Summary and the Investment Report) takes arbitrary rows with no
  // backend-provided total object. Sums every column that's numeric on
  // every row; the first non-numeric column shows the "Total" label,
  // every other non-numeric column stays blank -- covers both real
  // shapes this renders today (Consolidated's project/fy/status beside
  // real number columns, Investment Report's plain partner name).
  const totals = keys.map((k, i) => {
    if (rows.every(r => typeof r[k] === 'number')) return rows.reduce((sum, r) => sum + Number(r[k] || 0), 0);
    return i === 0 ? 'Total' : null;
  });
  return <><div className="flex justify-end mb-3 no-print"><ReportExportBar rows={rows} name={name||'report'} title={title||'Report'} subtitle={subtitle}/></div>
    <table className="tbl"><thead><tr>{keys.map(k=><th key={k}>{k}</th>)}</tr></thead>
    <tbody>{rows.map((r,i)=><tr key={i}>{keys.map(k=><td key={k} className="mono">{typeof r[k]==='number'?fmtINR(r[k]):r[k]}</td>)}</tr>)}</tbody>
    <tfoot><tr className="font-semibold">{totals.map((t,i)=><td key={keys[i]} className="mono">{typeof t==='number'?fmtINR(t):(t||'')}</td>)}</tr></tfoot>
    </table></>;
}
function ExpenseReport({ projectId, subtitle, dateFrom, dateTo }) {
  const [allRows, setAllRows] = useState([]);
  useEffect(() => { api.get('/expenses', { params: { project_id: projectId } }).then(r=>setAllRows(r.data)); }, [projectId]);
  const rows = allRows.filter(r => inDateRange(r.expense_date, dateFrom, dateTo));
  // Round 3 audit P1-3: this used to sum every row regardless of
  // audit_status, so its total silently disagreed with the P&L/Dashboard
  // "Expenses" figure (which only counts matched/final rows) with no
  // on-screen explanation. Same eligible-set the backend and every other
  // report already use — no new/competing definition.
  const eligible = rows.filter(r => CALC_ELIGIBLE_AUDIT_STATUSES.has(r.audit_status));
  const eligibleTotal = eligible.reduce((s, r) => s + Number(r.amount || 0), 0);
  const pendingTotal = rows.reduce((s, r) => s + (CALC_ELIGIBLE_AUDIT_STATUSES.has(r.audit_status) ? 0 : Number(r.amount || 0)), 0);
  const exportRows = rows.map(r=>({date:r.expense_date, category:r.category, description:r.description, amount:r.amount, audit_status:r.audit_status}));
  return <><div className="flex justify-between mb-3"><div className="serif text-2xl">Expenses</div><ReportExportBar rows={exportRows} name="expenses" title="Expense Report" subtitle={subtitle}/></div>
    <div className="flex flex-wrap gap-4 mb-3 text-sm">
      <div>Confirmed (matches P&amp;L): <b className="mono">{fmtINR(eligibleTotal)}</b></div>
      {pendingTotal > 0 && <div className="text-muted">Pending/Excluded: <b className="mono">{fmtINR(pendingTotal)}</b></div>}
    </div>
    <table className="tbl"><thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Audit</th></tr></thead><tbody>
      {rows.length===0 && <tr><td colSpan={5} className="text-center text-muted py-6">No expenses found.</td></tr>}
      {rows.map(r=><tr key={r.id}><td>{fmtDate(r.expense_date)}</td><td>{r.category}</td><td>{r.description}</td><td className="mono text-right">{fmtINR(r.amount)}</td><td><AuditStatusChip status={r.audit_status}/></td></tr>)}
    </tbody></table>
  </>;
}
function ConsolidatedReport({ dashboard, subtitle }) {
  const rows = dashboard.projects.map(({project, totals, cash_backing}) => ({
    project: project.project_name, fy: project.financial_year, status: project.status,
    contract: totals.net_contract_value, expenses: totals.total_expenses, receipts: totals.total_receipts,
    receivable: totals.outstanding_receivable, profit: totals.distributable_profit, cash_position: totals.cash_position,
    profit_in_cash: cash_backing ? cash_backing.cash_realised_profit : null, partners_payable_now: cash_backing ? cash_backing.payable_now : null, partners_awaiting_collection: cash_backing ? cash_backing.deferred : null, paid_ahead_of_cash: cash_backing ? cash_backing.paid_ahead : null,
  }));
  return <>
    <div className="serif text-2xl mb-3">Consolidated Summary</div>
    <SimpleTable rows={rows} name="consolidated" title="Consolidated Project Summary" subtitle={subtitle}/>
  </>;
}
function DetailProjectRows({ projects, cols }) {
  if (!projects?.length) return null;
  return projects.map((pr,i) => (
    <tr key={`d${i}`} className="text-xs text-muted" style={{background:'rgba(236,230,216,0.02)'}}>
      <td className="pl-6">↳ {pr.project_name}</td>
      {cols.map(c => <td key={c} className="mono">{typeof pr[c]==='number' ? fmtINR(pr[c]) : (pr[c]??'—')}</td>)}
    </tr>
  ));
}
function CompanyWiseReport({ breakdown, subtitle }) {
  const raw = breakdown?.rows||[];
  const rows = raw.map(r => ({
    company: r.company_name, projects: r.project_count, contract_value: r.contract_value,
    expenses: r.expenses, receipts: r.receipts, receivable: r.receivable, profit: r.profit, cash_backed_profit: r.cash_backed_profit, awaiting_collection: r.uncollected_profit,
  }));
  const t = breakdown?.total;
  return <>
    <div className="flex justify-between mb-3"><div className="serif text-2xl">Company Wise / Group Consolidated</div><ReportExportBar rows={rows} name="company-wise" title="Company Wise / Group Consolidated" subtitle={subtitle}/></div>
    <table className="tbl"><thead><tr><th>Company</th><th>Projects</th><th>Contract Value</th><th>Expenses</th><th>Receipts</th><th>Receivable</th><th>Profit</th><th>Cash-backed</th><th>Awaiting collection</th></tr></thead>
      <tbody>
        {rows.length===0 && <tr><td colSpan={9} className="text-center text-muted py-6">No projects found.</td></tr>}
        {rows.map((r,i)=><React.Fragment key={i}>
          <tr><td>{r.company}</td><td className="mono">{r.projects}</td><td className="mono">{fmtINR(r.contract_value)}</td><td className="mono">{fmtINR(r.expenses)}</td><td className="mono">{fmtINR(r.receipts)}</td><td className="mono">{fmtINR(r.receivable)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(r.profit)}</td><td className="mono">{fmtINR(r.cash_backed_profit)}</td><td className="mono text-amber-400">{fmtINR(r.awaiting_collection)}</td></tr>
          <DetailProjectRows projects={raw[i]?.projects} cols={['contract_value','expenses','receipts','receivable','profit','cash_backed_profit','uncollected_profit']}/>
        </React.Fragment>)}
      </tbody>
      {t && <tfoot><tr className="font-semibold"><td>{t.company_name}</td><td className="mono">{t.project_count}</td><td className="mono">{fmtINR(t.contract_value)}</td><td className="mono">{fmtINR(t.expenses)}</td><td className="mono">{fmtINR(t.receipts)}</td><td className="mono">{fmtINR(t.receivable)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(t.profit)}</td><td className="mono">{fmtINR(t.cash_backed_profit)}</td><td className="mono text-amber-400">{fmtINR(t.uncollected_profit)}</td></tr></tfoot>}
    </table>
  </>;
}
function DepartmentWiseReport({ breakdown, masters, subtitle }) {
  const deptName = (id) => id ? (masters.department_client?.find(m=>m.id===id)?.name || 'Unknown') : 'Unassigned';
  const raw = breakdown?.rows||[];
  const rows = raw.map(r => ({
    department: deptName(r.department_client_id), projects: r.project_count, contract_value: r.contract_value,
    expenses: r.expenses, receipts: r.receipts, receivable: r.receivable, profit: r.profit, cash_backed_profit: r.cash_backed_profit, awaiting_collection: r.uncollected_profit,
  }));
  const t = breakdown?.total;
  return <>
    <div className="flex justify-between mb-3"><div className="serif text-2xl">Department / Client Wise</div><ReportExportBar rows={rows} name="department-wise" title="Department / Client Wise" subtitle={subtitle}/></div>
    <table className="tbl"><thead><tr><th>Department / Client</th><th>Projects</th><th>Contract Value</th><th>Expenses</th><th>Receipts</th><th>Receivable</th><th>Profit</th><th>Cash-backed</th><th>Awaiting collection</th></tr></thead>
      <tbody>
        {rows.length===0 && <tr><td colSpan={9} className="text-center text-muted py-6">No projects found.</td></tr>}
        {rows.map((r,i)=><React.Fragment key={i}>
          <tr><td>{r.department}</td><td className="mono">{r.projects}</td><td className="mono">{fmtINR(r.contract_value)}</td><td className="mono">{fmtINR(r.expenses)}</td><td className="mono">{fmtINR(r.receipts)}</td><td className="mono">{fmtINR(r.receivable)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(r.profit)}</td><td className="mono">{fmtINR(r.cash_backed_profit)}</td><td className="mono text-amber-400">{fmtINR(r.awaiting_collection)}</td></tr>
          <DetailProjectRows projects={raw[i]?.projects} cols={['contract_value','expenses','receipts','receivable','profit','cash_backed_profit','uncollected_profit']}/>
        </React.Fragment>)}
      </tbody>
      {t && <tfoot><tr className="font-semibold"><td>Total</td><td className="mono">{t.project_count}</td><td className="mono">{fmtINR(t.contract_value)}</td><td className="mono">{fmtINR(t.expenses)}</td><td className="mono">{fmtINR(t.receipts)}</td><td className="mono">{fmtINR(t.receivable)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(t.profit)}</td><td className="mono">{fmtINR(t.cash_backed_profit)}</td><td className="mono text-amber-400">{fmtINR(t.uncollected_profit)}</td></tr></tfoot>}
    </table>
  </>;
}
function PartnerWiseReport({ breakdown, subtitle }) {
  const raw = breakdown?.rows||[];
  const rows = raw.map(r => ({
    partner: r.partner_name, projects: r.project_count, investment: r.investment,
    total_profit: r.total_profit, profit_paid: r.profit_paid, balance: r.balance, cash_backed: r.cash_backed, payable_now: r.payable_now, paid_ahead: r.paid_ahead,
  }));
  const t = breakdown?.total;
  return <>
    <div className="flex justify-between mb-3"><div className="serif text-2xl">Partner Settlement — All Projects</div><ReportExportBar rows={rows} name="partner-settlement-all-projects" title="Partner Settlement — All Projects" subtitle={subtitle}/></div>
    <table className="tbl"><thead><tr><th>Partner</th><th>Projects</th><th>Investment</th><th>Total Profit</th><th>Paid</th><th>Balance</th><th>Cash-backed</th><th>Payable now</th><th>Paid ahead</th></tr></thead>
      <tbody>
        {rows.length===0 && <tr><td colSpan={9} className="text-center text-muted py-6">No settlements found.</td></tr>}
        {rows.map((r,i)=><React.Fragment key={i}>
          <tr><td>{r.partner}</td><td className="mono">{r.projects}</td><td className="mono">{fmtINR(r.investment)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(r.total_profit)}</td><td className="mono">{fmtINR(r.profit_paid)}</td><td className={"mono "+(r.balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(r.balance)}</td><td className="mono">{fmtINR(r.cash_backed)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(r.payable_now)}</td><td className={"mono "+(r.paid_ahead>0.5?'text-red-400':'')}>{fmtINR(r.paid_ahead)}</td></tr>
          <DetailProjectRows projects={raw[i]?.projects} cols={['investment','total_profit','profit_paid','balance']}/>
        </React.Fragment>)}
      </tbody>
      {t && <tfoot><tr className="font-semibold"><td>{t.partner_name}</td><td className="mono">{t.project_count}</td><td className="mono">{fmtINR(t.investment)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(t.total_profit)}</td><td className="mono">{fmtINR(t.profit_paid)}</td><td className={"mono "+(t.balance>=0?'text-[color:var(--gold)]':'text-red-400')}>{fmtINR(t.balance)}</td><td className="mono">{fmtINR(t.cash_backed)}</td><td className="mono text-[color:var(--gold)]">{fmtINR(t.payable_now)}</td><td className="mono">{fmtINR(t.paid_ahead)}</td></tr></tfoot>}
    </table>
  </>;
}
function fyFromDateStr(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  const y = dt.getUTCFullYear();
  return dt.getUTCMonth() >= 3 ? `FY ${y}-${String(y+1).slice(-2)}` : `FY ${y-1}-${String(y).slice(-2)}`;
}
// The one canonical FY helper in the app (a second, timezone-divergent
// copy in lib/api.js was dead code — removed). The audit found 5 free-text
// FY filter boxes with 5 different, sometimes-buggy matching strategies
// behind an identical-looking text input; distinctFYs + FySelect replace
// that with one dropdown of years that actually exist in the data, so
// there's nothing to mistype and no format to get wrong.
function distinctFYs(values) {
  return Array.from(new Set(values.filter(Boolean))).sort().reverse();
}
function FySelect({ value, onChange, years, style, testId }) {
  return (
    <select className="input" style={style || { width: 150 }} value={value} onChange={e => onChange(e.target.value)} data-testid={testId}>
      <option value="">All Years</option>
      {years.map(y => <option key={y} value={y}>{y}</option>)}
    </select>
  );
}
function SecurityRefundableReport({ items, summary, subtitle }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [fyFilter, setFyFilter] = useState('');
  const availableFYs = distinctFYs((items||[]).map(s => fyFromDateStr(s.date_lodged)));
  // Same dynamic per-deduction-type filter options as the main Security &
  // Refundables page -- see deductionSyncedTypeName's own comment.
  const deductionTypeOptions = useMemo(() =>
    [...new Set((items||[]).map(deductionSyncedTypeName).filter(Boolean))].sort((a,b)=>a.localeCompare(b)),
    [items]);
  // This report's own status/type/FY filters are more specific than
  // whatever the parent Reports page passed down, so fold them in too —
  // otherwise an export filtered to "Dues, FY 2026-27" would carry only
  // the outer company context and look like it covers everything.
  const fullSubtitle = [subtitle, statusFilter !== 'all' ? statusFilter : null, typeFilter !== 'all' ? (typeFilter.startsWith('deduction:') ? typeFilter.slice('deduction:'.length) : SECURITY_TYPE_LABELS[typeFilter]) : null, fyFilter || null].filter(Boolean).join(' · ') || null;
  const filtered = (items||[]).filter(s => {
    if (statusFilter==='settled' && s.status!=='refunded') return false;
    if (statusFilter==='dues' && s.status==='refunded') return false;
    // Same receipt-deducted-vs-directly-lodged split as the main Security &
    // Refundables page -- see deductionSyncedTypeName/securityTypeLabel.
    if (typeFilter==='all') { /* no type filter */ }
    else if (typeFilter.startsWith('deduction:')) { if (deductionSyncFilterValue(deductionSyncedTypeName(s)) !== typeFilter) return false; }
    else if (s.instrument_type!==typeFilter || deductionSyncedTypeName(s)) return false;
    if (fyFilter && fyFromDateStr(s.date_lodged) !== fyFilter) return false;
    return true;
  });
  const rows = filtered.map(s => ({
    date_lodged: s.date_lodged, type: securityTypeLabel(s), party: s.party_name||'—',
    amount: s.amount, refund_due: s.refund_due_date||'—', status: SECURITY_STATUS_LABELS[s.status]||s.status,
  }));
  return <>
    <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
      <div className="serif text-2xl">Security / Refundable Report</div>
      <div className="flex gap-2 flex-wrap no-print">
        <select className="input" style={{width:140}} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
          <option value="all">All</option><option value="settled">Settled</option><option value="dues">Dues</option>
        </select>
        <select className="input" style={{width:160}} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          {Object.entries(SECURITY_TYPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
          {deductionTypeOptions.map(t=><option key={t} value={deductionSyncFilterValue(t)}>{t}</option>)}
        </select>
        <FySelect value={fyFilter} onChange={setFyFilter} years={availableFYs} style={{width:140}}/>
        <ReportExportBar rows={rows} name="security-refundable" title="Security / Refundable Report" subtitle={fullSubtitle}/>
      </div>
    </div>
    {summary && <div className="grid md:grid-cols-4 gap-3 mb-4">
      <KpiCard label="Outstanding Security" value={fmtINR(summary.outstanding_security)}/>
      <KpiCard label="Pending Refund" value={fmtINR(summary.pending_refund)}/>
      <KpiCard label="Overdue Refund" value={fmtINR(summary.overdue_refund)}/>
      <KpiCard label="Returned Amount" value={fmtINR(summary.returned_amount)}/>
    </div>}
    <table className="tbl"><thead><tr><th>Date Lodged</th><th>Type</th><th>Party</th><th>Amount</th><th>Refund Due</th><th>Status</th></tr></thead>
      <tbody>
        {rows.length===0 && <tr><td colSpan={6} className="text-center text-muted py-6">None recorded.</td></tr>}
        {rows.map((r,i)=><tr key={i}><td>{fmtDate(r.date_lodged)}</td><td>{r.type}</td><td>{r.party}</td><td className="mono">{fmtINR(r.amount)}</td><td>{r.refund_due!=='—'?fmtDate(r.refund_due):'—'}</td><td>{r.status}</td></tr>)}
      </tbody>
      {rows.length>0 && <tfoot><tr className="font-semibold"><td colSpan={3} className="text-right text-muted uppercase text-xs">Total</td><td className="mono">{fmtINR(rows.reduce((a,r)=>a+Number(r.amount||0),0))}</td><td colSpan={2}></td></tr></tfoot>}
    </table>
  </>;
}

// The two per-row buttons for master admins: a same-kind MOVE (Project Expense
// -> another project; Company Expense -> another company) and a CONVERT between
// the kinds (Project <-> Company Expense).
function ExpenseMoveButtons({ id, projectId, onOpen }) {
  const isProject = !!projectId;
  return (
    <>
      <button type="button" onClick={()=>onOpen(isProject ? 'project' : 'company')} className="btn-blue btn-small mr-1 inline-flex items-center" style={{padding:'5px 11px'}} title={isProject ? 'Move to another project' : 'Move to another company'} aria-label={isProject ? 'Move to another project' : 'Move to another company'} data-testid={`aq-move-${id}`}>{isProject ? <ArrowRight size={12}/> : <Building2 size={12}/>}</button>
      <button type="button" onClick={()=>onOpen('convert')} className="btn-violet btn-small mr-1 inline-flex items-center" style={{padding:'5px 11px'}} title={isProject ? 'Convert to a Company Expense' : 'Convert to a Project Expense'} aria-label={isProject ? 'Convert to a Company Expense' : 'Convert to a Project Expense'} data-testid={`aq-convert-${id}`}><ArrowRightLeft size={12}/></button>
    </>
  );
}

// Master-admin-only expense moves, at ANY audit stage (pending, matched,
// discrepancy or final), all through POST /expenses/{id}/move-scope -- which
// changes only the scope link and leaves audit_status and every other field
// untouched. Three modes:
//   'project' -- Project Expense -> another project (same company)
//   'company' -- Company Expense -> another company's Company Expense
//   'convert' -- Project Expense <-> Company Expense (same company)
function ExpenseMoveModal({ row, mode, onClose, onSaved }) {
  const isProjectExpense = !!row.project_id;
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allOverhead, setAllOverhead] = useState([]);
  const [projectCats, setProjectCats] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [category, setCategory] = useState('');
  const [targetCompanyId, setTargetCompanyId] = useState('');
  const [overheadId, setOverheadId] = useState('');
  const [saving, setSaving] = useState(false);
  // Fund-paid Company Expenses can't leave their company's fund (to a project, or another company).
  const fundBlocked = !isProjectExpense && !!row._edit?.paid_from_company_fund && mode !== 'project';
  useEffect(() => {
    if (mode === 'project') {
      api.get('/projects').then(p => setProjects(p.data.filter(x => x.company_id === row.company_id && x.id !== row.project_id && !isProjectLocked(x))));
    } else if (mode === 'company') {
      Promise.all([api.get('/companies'), api.get('/expense-categories', { params: { scope: 'overhead' } })]).then(([c, o]) => {
        setCompanies(c.data.filter(x => x.id !== row.company_id)); setAllOverhead(o.data);
      });
    } else if (isProjectExpense) {
      api.get('/expense-categories', { params: { scope: 'overhead' } }).then(c => setAllOverhead(c.data));
    } else {
      Promise.all([api.get('/projects'), api.get('/expense-categories', { params: { scope: 'project' } })]).then(([p, c]) => {
        setProjects(p.data.filter(x => x.company_id === row.company_id && !isProjectLocked(x)));
        setProjectCats(mastersForCompany(c.data, row.company_id));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, mode]);
  const overheadFor = (cid) => allOverhead.filter(c => (c.company_ids || []).includes(cid));
  const title = mode === 'project' ? 'Move to Another Project'
    : mode === 'company' ? 'Move to Another Company'
    : isProjectExpense ? 'Convert to Company Expense' : 'Convert to Project Expense';
  const missing = mode === 'project' ? !projectId
    : mode === 'company' ? (!targetCompanyId || !overheadId)
    : isProjectExpense ? !overheadId : (!projectId || !category);
  const save = async () => {
    if (saving || missing || fundBlocked) return;
    let body, targetLabel;
    if (mode === 'project') {
      body = { target: 'project', project_id: projectId };
      targetLabel = `project "${projects.find(x => x.id === projectId)?.project_name || ''}"`;
    } else if (mode === 'company') {
      body = { target: 'company', company_id: targetCompanyId, overhead_category_id: overheadId };
      targetLabel = `${companies.find(c => c.id === targetCompanyId)?.name || 'the other company'} (Company Expense — ${overheadFor(targetCompanyId).find(c => c.id === overheadId)?.name || ''})`;
    } else if (isProjectExpense) {
      body = { target: 'company', overhead_category_id: overheadId };
      targetLabel = `Company Expense (${allOverhead.find(c => c.id === overheadId)?.name || ''})`;
    } else {
      body = { target: 'project', project_id: projectId, category };
      targetLabel = `Project Expense on "${projects.find(x => x.id === projectId)?.project_name || ''}"`;
    }
    if (!window.confirm(`Move ${fmtINR(row.amount)} (voucher ${row.voucher_number || '—'}) from ${row.project_name || row.expense_head || 'its current place'} to ${targetLabel}?\n\nIts audit status (${(row.audit_status || '').replace('_',' ')}) and every other field stay exactly as they are.${mode === 'company' ? ' This moves it into the other company\'s books.' : ' Profit on the project(s) involved changes accordingly.'}`)) return;
    setSaving(true);
    try {
      await api.post(`/expenses/${row.id}/move-scope`, body);
      toast.success('Moved');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title={title}>
      <div className="text-sm mb-3">
        <span className="mono">{fmtINR(row.amount)}</span> · voucher {row.voucher_number || '—'} · {fmtDate(row.date)}<br/>
        Currently: <b>{isProjectExpense ? `Project Expense — ${row.project_name || ''}` : `Company Expense — ${row.expense_head || ''}`}</b>
        {' '}· status stays <b>{(row.audit_status || '').replace('_',' ')}</b>
      </div>
      {fundBlocked && <div className="text-xs text-red-300 mb-3">This expense is marked Paid from Company Fund. Company Fund draws are only tracked on Company Expenses, so it cannot be moved without dropping out of the Company Fund Ledger.</div>}
      {mode === 'company' && <div className="text-xs text-muted mb-3">Moves this expense into another company's books. If it was paid from a bank account, linked to a staff member or paid from Company Fund, edit it first; the partner who paid must also belong to the target company, and its voucher number must be unused there.</div>}
      {mode === 'project' && (
        <Field label="Project *">
          <select className="input" value={projectId} onChange={e=>setProjectId(e.target.value)} data-testid="aq-move-project">
            <option value="">— choose —</option>
            {projects.map(x => <option key={x.id} value={x.id}>{x.project_name}</option>)}
          </select>
        </Field>
      )}
      {mode === 'company' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Move to Company *">
            <select className="input" value={targetCompanyId} onChange={e=>{setTargetCompanyId(e.target.value); setOverheadId('');}} data-testid="aq-move-company">
              <option value="">— choose —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Overhead Category *">
            <select className="input" value={overheadId} onChange={e=>setOverheadId(e.target.value)} disabled={!targetCompanyId} data-testid="aq-move-overhead">
              <option value="">— choose —</option>
              {overheadFor(targetCompanyId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </div>
      )}
      {mode === 'convert' && isProjectExpense && (
        <Field label="Overhead Category *">
          <select className="input" value={overheadId} onChange={e=>setOverheadId(e.target.value)} data-testid="aq-move-overhead">
            <option value="">— choose —</option>
            {overheadFor(row.company_id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      )}
      {mode === 'convert' && !isProjectExpense && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Project *">
            <select className="input" value={projectId} onChange={e=>setProjectId(e.target.value)} data-testid="aq-move-project">
              <option value="">— choose —</option>
              {projects.map(x => <option key={x.id} value={x.id}>{x.project_name}</option>)}
            </select>
          </Field>
          <Field label="Category *">
            <select className="input" value={category} onChange={e=>setCategory(e.target.value)} data-testid="aq-move-category">
              <option value="">— choose —</option>
              {projectCats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn-outline btn-small">Cancel</button>
        <button onClick={save} disabled={saving || missing || fundBlocked} className={`${mode === 'convert' ? 'btn-violet' : 'btn-blue'} btn-small`} data-testid="aq-move-save">{saving ? 'Moving…' : mode === 'convert' ? 'Convert' : 'Move'}</button>
      </div>
    </Modal>
  );
}

// Expense rows that pay a vendor or a staff member: show WHO was paid right on
// the row, so the reviewer sees it while auditing / moving the line.
function AuditPayeeLine({ r }) {
  if (r.entity_type !== 'expense') return null;
  const staff = r.staff_name ? `${r.staff_name}${r.staff_payment_type ? ` · ${STAFF_PAYMENT_TYPE_LABELS[r.staff_payment_type] || r.staff_payment_type}` : ''}` : null;
  if (!r.vendor_name && !staff) return null;
  return (
    <div style={{fontSize:'0.72rem', marginTop:2, color:'var(--gold)'}} data-testid={`aq-payee-${r.id}`}>
      {r.vendor_name && <div>Vendor: <b>{r.vendor_name}</b></div>}
      {staff && <div>Staff: <b>{staff}</b></div>}
    </div>
  );
}

// Partner-transaction rows: the Partner column is who the money went to; when
// a DIFFERENT partner (or the Company Fund) actually fronted the cash, say so
// underneath, so both partners' ledgers can be traced from the queue.
function AuditPartnerCell({ r }) {
  if (r.entity_type !== 'partner_transaction') return <>{r.paid_by || '—'}</>;
  const note = r.payer_name ? `paid by ${r.payer_name}` : (r.paid_from_company_fund ? 'paid from Company Fund' : '');
  return (
    <>
      <div>{r.paid_by || '—'}</div>
      {note && <div className="text-muted" style={{fontSize:'0.7rem'}} data-testid={`aq-paidby-${r.id}`}>{note}</div>}
    </>
  );
}

// User-requested "must needed" function: correct a discrepancy right here
// in the Audit Queue instead of navigating away to the entity's own page.
// Reuses the exact same underlying PATCH /expenses/{id} or /receipts/{id}
// endpoints every other edit form in the app already goes through -- this
// is just a purpose-built entry point into them, pre-filled from the
// row's own `_edit` payload (the Audit Queue GET response's raw fields,
// since the display-only shape it normally returns can't round-trip back
// into a full ExpenseIn/ReceiptIn).
function AuditQueueEditModal({ row, onClose, onSaved }) {
  const vendors = useVendors();
  const { user: currentUser } = useAuth();
  const isExpense = row.entity_type === 'expense';
  const isReceipt = row.entity_type === 'receipt';
  const isSecurityInstrument = row.entity_type === 'security_instrument';
  // v43: Investments/Partner Transactions/Company Capital Contributions
  // joined the Audit Queue's review workflow -- same edit-in-queue parity
  // every other entity type here already has.
  const isInvestment = row.entity_type === 'investment';
  const isPartnerTxn = row.entity_type === 'partner_transaction';
  const isContribution = row.entity_type === 'company_capital_contribution';
  const isOverhead = isExpense && !row.project_id;
  // User-requested "very convenient" one-click fix: correcting an expense
  // entered under the wrong scope (Project vs Company/overhead) while
  // reviewing it here, instead of deleting and re-adding by hand. Backend
  // (_crud_endpoints' _update) already gates any real project_id CHANGE --
  // including clearing it to move onto overhead, the other half of this
  // same move -- to master_admin only, since it shifts profit-sharing math
  // off/onto a project exactly like the existing project-to-project Move
  // feature does. scopeOverhead tracks which shape this modal is currently
  // showing/will save as; starts matching the row's real, saved scope.
  const [scopeOverhead, setScopeOverhead] = useState(isOverhead);
  const [moveTargetProjectId, setMoveTargetProjectId] = useState(row.project_id || '');
  const [moveProjects, setMoveProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [form, setForm] = useState({ ...row._edit });
  const [saving, setSaving] = useState(false);
  // User-requested: an Investor is hidden from Paid By/Borne By by
  // default -- this reveals them for the rare genuine one-off exception.
  const [showAllPartners, setShowAllPartners] = useState(false);
  const [queueClients, setQueueClients] = useState([]);
  useEffect(() => {
    if (row.entity_type === 'security_instrument') api.get('/security-instruments/party-clients').then(r => setQueueClients(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  useEffect(() => {
    Promise.all([
      api.get('/partners'),
      api.get('/bank-accounts'),
      isExpense ? api.get('/staff') : Promise.resolve({ data: [] }),
      // Target list for the master-admin-only Move to Project Expense
      // toggle below -- same company only (a move can never cross
      // companies), same locked-project exclusion CrudPanel's own
      // project-to-project Move already uses (the backend would 400 on a
      // locked target anyway; filtering it out here avoids a dead-end
      // pick in the dropdown).
      (isExpense && isMasterAdmin(currentUser)) ? api.get('/projects') : Promise.resolve({ data: [] }),
    ]).then(([p, b, s, pr]) => {
      setPartners(mastersForCompany(p.data, row.company_id));
      setBankAccounts((b.data || []).filter(x => x.company_id === row.company_id));
      setStaffList(forCompany(s.data, row.company_id));
      setMoveProjects((pr.data || []).filter(x => x.company_id === row.company_id && !isProjectLocked(x)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  // Separate from the effect above: the category LIST depends on which
  // scope the modal is currently showing (project vs overhead), which
  // changes when the Move toggle below is clicked -- re-fetches only this,
  // not partners/bank accounts/staff/projects, on every toggle.
  useEffect(() => {
    if (!isExpense) return;
    api.get('/expense-categories', { params: { scope: scopeOverhead ? 'overhead' : 'project' } })
      .then(c => setCategories(mastersForCompany(c.data, row.company_id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, scopeOverhead]);

  // Same mandatory rules as every other entry surface for these entities
  // (Add Expense, Add Security Instrument, bulk import): an expense needs
  // a partner, bank account, or Company Fund tick; a staff Payment Type
  // requires a matching Staff Member.
  const missingPaidBy = isExpense && !form.paid_by_partner_id && !form.bank_account_id && !form.paid_from_company_fund;
  const missingStaff = isExpense && !!form.staff_payment_type && !form.staff_id;
  // User-requested: Security & Refundables' Bulk Import already requires
  // Borne By (partner or company fund) -- editing the same entity here
  // shouldn't allow the field to stay blank just because it slipped in
  // through the manual Add form before this rule existed.
  const missingBorneBy = isSecurityInstrument && !form.borne_by_partner_id && !form.paid_from_company_fund;
  // User-requested: Voucher No. is now mandatory on Security & Refundables
  // everywhere it's entered/edited (Add form, this modal, and the API
  // itself) -- same digits-only rule as expenses.
  const missingVoucher = isSecurityInstrument && !/^\d+$/.test(form.voucher_number||'');
  const missingAmount = isSecurityInstrument && (!form.amount || Number(form.amount) <= 0);
  // User-requested: Ledger Folio Number replaces Purpose as a mandatory
  // field here too, same everywhere-it's-edited parity as Voucher No.
  const missingLedgerFolio = isSecurityInstrument && !(form.ledger_folio_number||'').trim();
  const missingParty = isSecurityInstrument && !form.party_client_id && !(form.party_name||'').trim();
  // v43: same mandatory rules as the entry forms for these 3 tables --
  // Partner is a hard-required field at the API layer for all three (a
  // blank one would otherwise surface as a raw 422, not a clean toast);
  // Voucher No. follows the same digits-only rule everywhere else, except
  // Partner Transaction's own interest_credit/interest_charge exemption
  // (mirrors the backend's identical exemption in _validate_partner_txn_type).
  const missingPartner = (isInvestment || isPartnerTxn || isContribution) && !form.partner_id;
  const missingAmountNew = (isInvestment || isPartnerTxn || isContribution) && (!form.amount || Number(form.amount) <= 0);
  const missingVoucherNew = (isInvestment || isContribution || (isPartnerTxn && !['interest_credit','interest_charge'].includes(form.transaction_type)))
    && !/^\d+$/.test(form.voucher_number||'');
  // Category/Overhead Category (whichever scope is currently shown) were
  // never actually enforced as required in THIS modal before -- every
  // other entry surface (Add Expense, bulk import) already treats them as
  // mandatory, this just closes that gap here too, plus makes the newly-
  // revealed field required specifically while the Move toggle below is
  // active, so it fails with a clean toast instead of a raw 400 on Save.
  const missingMoveProject = isExpense && !scopeOverhead && !moveTargetProjectId;
  const missingMoveCategory = isExpense && !scopeOverhead && !(form.category||'').trim();
  const missingMoveOverheadCategory = isExpense && scopeOverhead && !form.overhead_category_id;

  const save = async () => {
    if (saving) return;
    if (missingPaidBy) { toast.error('Paid By is required — choose the partner who personally paid this, the bank account it was paid from, or tick Paid from Company Fund.'); return; }
    if (missingStaff) { toast.error('Staff Member is required when a Payment Type is set.'); return; }
    if (missingBorneBy) { toast.error('Borne By is required — choose the partner who personally bore this, or tick Paid from Company Fund.'); return; }
    if (missingVoucher) { toast.error('Voucher number is required and must contain digits only.'); return; }
    if (missingAmount) { toast.error('Amount is required and must be greater than 0.'); return; }
    if (missingLedgerFolio) { toast.error('Ledger Folio Number is required.'); return; }
    if (missingParty) { toast.error('Party Name is required — who the instrument is lodged with.'); return; }
    if (missingPartner) { toast.error('Partner is required.'); return; }
    if (missingVoucherNew) { toast.error('Voucher number is required and must contain digits only.'); return; }
    if (missingAmountNew) { toast.error('Amount is required and must be greater than 0.'); return; }
    if (missingMoveProject) { toast.error('Choose which project this moves to.'); return; }
    if (missingMoveCategory) { toast.error('Choose a Category for the project it\'s moving to.'); return; }
    if (missingMoveOverheadCategory) { toast.error('Choose an Overhead Category for the company expense it\'s moving to.'); return; }
    if (isExpense) { const vp = vendorStaffProblem({ ...form, id: row.id }, scopeOverhead ? (categories.find(c => c.id === form.overhead_category_id) || {}).name : form.category); if (vp) { toast.error(vp); return; } }
    setSaving(true);
    try {
      // Verification-caught bug: row._edit deliberately never includes
      // audit_status (it's changed via the queue's own status dropdown,
      // not this modal) -- but PATCH is a full-object replace, so leaving
      // it out of the payload let Pydantic's model default silently reset
      // a matched/final/discrepancy row back to pending_review on every
      // save through this modal, dropping it out of
      // CALC_ELIGIBLE_AUDIT_STATUSES with no error or warning. Pin it to
      // the row's own current status explicitly -- this modal never
      // offers a way to change it, so it should never change here.
      const payload = { ...form, project_id: row.project_id, amount: Number(form.amount || 0), audit_status: row.audit_status };
      if (isSecurityInstrument) Object.assign(payload, normalizeCapitalLock(form));
      if (isExpense) payload.vendor_name = cleanVendor(form.vendor_name);
      // Move to Project Expense / Move to Company Expense: the ONE case
      // that's allowed to actually change project_id through this modal
      // (every other entity type above stays pinned to its original,
      // unmoveable value) -- null is deliberate here, not a bug, when
      // scopeOverhead ends up true. Backend's _validate_expense_scope
      // still enforces "exactly one of project_id/overhead_category_id",
      // and _update's own master_admin gate still applies to the actual
      // change, exactly like the existing project-to-project Move.
      if (isExpense) {
        payload.project_id = scopeOverhead ? null : moveTargetProjectId;
        payload.overhead_category_id = scopeOverhead ? form.overhead_category_id : null;
      }
      // Same reasoning as audit_status above -- this modal has no bill-
      // linking UI (single- or multi-bill), so bill_id/bill_allocations
      // must stay pinned to whatever they already were, never touched by
      // an unrelated save here. bill_id alone is enough either way: a
      // multi-bill receipt already has bill_id=null, and the backend
      // leaves its real allocations alone whenever bill_id comes back
      // unchanged (see _sync_receipt_bill_allocations's bill_id_changed).
      if (isReceipt) payload.bill_id = row._edit.bill_id;
      if (isExpense) await api.patch(`/expenses/${row.id}`, payload);
      else if (isReceipt) await api.patch(`/receipts/${row.id}`, payload);
      else if (isSecurityInstrument) await api.patch(`/security-instruments/${row.id}`, payload);
      else if (isInvestment) await api.patch(`/investments/${row.id}`, payload);
      else if (isPartnerTxn) await api.patch(`/partner-transactions/${row.id}`, payload);
      else await api.patch(`/company-capital-contributions/${row.id}`, payload);
      toast.success('Saved');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title={`Edit ${isExpense ? 'Expense' : isReceipt ? 'Receipt' : isSecurityInstrument ? 'Security Instrument' : isInvestment ? 'Investment' : isPartnerTxn ? 'Partner Transaction' : 'Capital Contribution'} — ${row.project_name || ''}`} maxW={640}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(isExpense || isReceipt) && <>
          <Field label="Date"><input className="input" type="date"
            value={(isExpense ? form.expense_date : form.receipt_date) || ''}
            onChange={e=>setForm({...form, [isExpense?'expense_date':'receipt_date']: e.target.value})}/></Field>
          <Field label="Amount"><input className="input mono" type="number" min="0" value={form.amount ?? ''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
        </>}
        {isExpense ? (
          <>
            {/* User-requested "very convenient" fix: correcting an
                expense's scope (e.g. entered under Company by mistake, but
                really belongs to a project) used to mean delete and
                re-add by hand -- this toggle switches which fields below
                are shown/required, same one-click convenience as the
                existing project-to-project Move, just for the OTHER axis
                (project vs company/overhead). Backend gates the actual
                change to master_admin only (see _update's project_id
                guard) since it moves the amount off/onto a project's
                profit math -- hidden entirely for anyone else, who'd just
                get a 403 on Save. */}
            {isMasterAdmin(currentUser) && (
              <div className="col-span-2">
                <button type="button" className="btn-outline btn-small inline-flex items-center gap-1" onClick={() => {
                  const next = !scopeOverhead;
                  setScopeOverhead(next);
                  setForm(f => ({ ...f, category: next ? f.category : '', overhead_category_id: next ? '' : f.overhead_category_id }));
                  if (!next) setMoveTargetProjectId('');
                }} data-testid="aq-edit-move-scope">
                  <ArrowRightLeft size={14}/> Move to {scopeOverhead ? 'Project Expense' : 'Company Expense'}
                </button>
                {scopeOverhead !== isOverhead && <div className="text-xs mt-1" style={{color:'var(--accent-amber)'}}>Fill in the {scopeOverhead ? 'Overhead Category' : 'Project and Category'} below, then Save to move it.</div>}
              </div>
            )}
            {/* Only needed while actively moving a Company Expense onto a
                project -- an untouched Project Expense already has its
                real project fixed (moveTargetProjectId starts pinned to
                it), so no picker is needed just to leave it alone. Moving
                to a DIFFERENT project while staying a Project Expense is
                the existing project-to-project Move feature, not this
                toggle. */}
            {!scopeOverhead && isOverhead && <Field label="Project *"><select className="input" value={moveTargetProjectId} onChange={e=>setMoveTargetProjectId(e.target.value)} data-testid="aq-edit-move-project">
              <option value="">— select —</option>
              {moveProjects.map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select></Field>}
            {/* User-reported bug: the row's real current category (e.g. a
                legacy/free-text value, or one whose casing differs from
                the live master list) didn't necessarily match any <option>
                here by exact string/id -- HTML silently shows a select as
                blank when its value matches nothing, even though form.category
                still held the correct value underneath. Adding an explicit
                fallback option for the current value (when it's not already
                in the live list) means it always shows correctly pre-filled,
                and a user who doesn't touch this field keeps the exact value
                it already had -- only picking a different one actually
                changes it, per "a user will change if it entered wrong." */}
            {!scopeOverhead && <Field label={scopeOverhead === isOverhead ? "Category" : "Category *"}><select className="input" value={form.category||''} onChange={e=>setForm({...form, category:e.target.value})}>
              <option value="">— select —</option>
              {form.category && !categories.some(c=>c.name===form.category) && <option value={form.category}>{form.category} (current)</option>}
              {categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
            </select></Field>}
            {scopeOverhead && <Field label={scopeOverhead === isOverhead ? "Overhead Category" : "Overhead Category *"}><select className="input" value={form.overhead_category_id||''} onChange={e=>setForm({...form, overhead_category_id:e.target.value})}>
              <option value="">— select —</option>
              {form.overhead_category_id && !categories.some(c=>c.id===form.overhead_category_id) && <option value={form.overhead_category_id}>(current category)</option>}
              {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select></Field>}
            <Field label="Voucher No."><input className="input mono" inputMode="numeric" value={form.voucher_number||''} onChange={e=>setForm({...form, voucher_number:e.target.value})}/></Field>
            <Field label={vendorIsRequired({ ...form, id: row.id }, scopeOverhead ? (categories.find(c => c.id === form.overhead_category_id) || {}).name : form.category) ? 'Vendor *' : 'Vendor'}><VendorSelect vendors={vendors} companyId={row.company_id} value={form.vendor_name} isNew={false} categoryName={scopeOverhead ? (categories.find(c => c.id === form.overhead_category_id) || {}).name : form.category} onChange={v=>setForm({...form, vendor_name:v})}/></Field>
            <Field label="Paid By Partner *"><select className="input" value={form.paid_by_partner_id||''} onChange={e=>setForm({...form, paid_by_partner_id:e.target.value||null, bank_account_id: e.target.value ? null : form.bank_account_id, paid_from_company_fund: e.target.value ? false : form.paid_from_company_fund})}>
              <option value="">— none, paid from bank instead —</option>
              {eligiblePaidByPartners(partners, row.company_id, row.project_id, showAllPartners, form.paid_by_partner_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={showAllPartners} onChange={e=>setShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
            </Field>
            <Field label="Or Bank Account *"><select className="input" value={form.bank_account_id||''} onChange={e=>setForm({...form, bank_account_id:e.target.value||null, paid_by_partner_id: e.target.value ? null : form.paid_by_partner_id, paid_from_company_fund: e.target.value ? false : form.paid_from_company_fund})}>
              <option value="">— none, paid by partner instead —</option>
              {bankAccounts.map(b=><option key={b.id} value={b.id}>{b.bank_name}</option>)}
            </select></Field>
            {/* User-requested: mirrors CompanyExpensesPanel's own reordered
                layout -- Payment Type first, drives Staff Member's
                requirement, rather than the reverse. */}
            <Field label="Payment Type"><select className="input" value={form.staff_payment_type||''} onChange={e=>{ const v = e.target.value||null; setForm({...form, staff_payment_type:v, staff_id: staffAfterTypeChange(staffList, v, form.staff_id)}); }}>
              <option value="">— not a staff payment —</option>
              {Object.entries(STAFF_PAYMENT_TYPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
            </select></Field>
            <Field label={staffIsRequired(form, scopeOverhead ? (categories.find(c => c.id === form.overhead_category_id) || {}).name : form.category) ? "Staff Member *" : "Staff Member"}><select className="input" value={form.staff_id||''} onChange={e=>setForm({...form, staff_id:e.target.value||null})}>
              <option value="">{staffNoneLabel(form.staff_payment_type)}</option>
              {staffPickList(staffList, form.staff_payment_type, form.staff_id).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select></Field>
            {scopeOverhead && (
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.paid_from_company_fund} onChange={e=>setForm({...form, paid_from_company_fund:e.target.checked, paid_by_partner_id: e.target.checked ? null : form.paid_by_partner_id, bank_account_id: e.target.checked ? null : form.bank_account_id})}/>
                  Paid from Company Reserve / Common Fund
                </label>
              </div>
            )}
          </>
        ) : isReceipt ? (
          <>
            <Field label="Type"><select className="input" value={form.receipt_type||'customer_payment'} onChange={e=>setForm({...form, receipt_type:e.target.value})}>
              {RCP_TYPES.map(t=><option key={t} value={t}>{RCP_TYPE_LABELS[t]||t}</option>)}
            </select></Field>
            <Field label="Bank Account"><select className="input" value={form.bank_account_id||''} onChange={e=>setForm({...form, bank_account_id:e.target.value||null})}>
              <option value="">— none —</option>
              {bankAccounts.map(b=><option key={b.id} value={b.id}>{b.bank_name}</option>)}
            </select></Field>
            <Field label="Bank Reference"><input className="input" value={form.bank_reference||''} onChange={e=>setForm({...form, bank_reference:e.target.value})}/></Field>
          </>
        ) : isSecurityInstrument ? (
          <>
            {/* User-requested: Voucher No. first (right after Company, which
                this modal doesn't itself expose -- it's fixed by the row),
                mandatory, same digits-only rule as expenses. Type moved to
                right before Amount. */}
            <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={form.voucher_number||''} onChange={e=>setForm({...form, voucher_number:e.target.value})}/></Field>
            <Field label="Date Lodged"><input type="date" className="input" value={form.date_lodged||''} onChange={e=>setForm({...form, date_lodged:e.target.value})}/></Field>
            <Field label="Type"><select className="input" value={form.instrument_type||'emd'} onChange={e=>setForm({...form, instrument_type:e.target.value})}>
              {Object.entries(SECURITY_TYPE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
            </select></Field>
            <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
            <SecurityPartyField form={form} setForm={setForm} clients={queueClients} companyId={row.company_id} testId="aq-party"/>
            <Field label="Tender Reference"><input className="input" value={form.tender_reference||''} onChange={e=>setForm({...form, tender_reference:e.target.value})}/></Field>
            <Field label="Bank Account"><input className="input" value={form.bank_account_name||''} onChange={e=>setForm({...form, bank_account_name:e.target.value})}/></Field>
            <Field label="Borne By Partner *"><select className="input" value={form.borne_by_partner_id||''} onChange={e=>setForm({...form, borne_by_partner_id:e.target.value||null, paid_from_company_fund: e.target.value ? false : form.paid_from_company_fund})}>
              <option value="">— none —</option>
              {eligiblePaidByPartners(partners, row.company_id, row.project_id, showAllPartners, form.borne_by_partner_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={showAllPartners} onChange={e=>setShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
            </Field>
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.paid_from_company_fund} onChange={e=>setForm({...form, paid_from_company_fund:e.target.checked, borne_by_partner_id: e.target.checked ? null : form.borne_by_partner_id})}/>
                Paid from Company Reserve / Common Fund
              </label>
              {missingBorneBy && <div className="text-xs mt-1" style={{color:'var(--accent-amber)'}}>Choose a partner above, or tick this — one is required.</div>}
            </div>
            <CapitalLockField form={form} setForm={setForm} partners={partners} companyId={row.company_id} projectId={row.project_id}/>
            <Field label="Status"><select className="input" value={form.status||'pending'} onChange={e=>setForm({...form, status:e.target.value})}>
              {Object.entries(SECURITY_STATUS_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
            </select></Field>
            <Field label="Refund Due Date"><input type="date" className="input" value={form.refund_due_date||''} onChange={e=>setForm({...form, refund_due_date:e.target.value})}/></Field>
            <Field label="Ledger Folio No. *"><input className="input" value={form.ledger_folio_number||''} onChange={e=>setForm({...form, ledger_folio_number:e.target.value})}/></Field>
          </>
        ) : isInvestment ? (
          <>
            <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={form.voucher_number||''} onChange={e=>setForm({...form, voucher_number:e.target.value})}/></Field>
            <Field label="Date"><input type="date" className="input" value={form.transaction_date||''} onChange={e=>setForm({...form, transaction_date:e.target.value})}/></Field>
            <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
            {/* Same user-reported bug as the Investments/Partner Transactions
                tabs: was every company partner unfiltered, so an investor
                mapped to a different project showed up here too. This
                modal already has form in scope (unlike CrudPanel's static
                opts), so eligiblePaidByPartners applies directly. */}
            <Field label="Partner *"><select className="input" value={form.partner_id||''} onChange={e=>setForm({...form, partner_id:e.target.value})}>
              <option value="">— select —</option>
              {eligiblePaidByPartners(partners, row.company_id, row.project_id, showAllPartners, form.partner_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={showAllPartners} onChange={e=>setShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
            </Field>
            <Field label="Type"><select className="input" value={form.investment_type||'capital_investment'} onChange={e=>setForm({...form, investment_type:e.target.value})}>
              {INV_TYPES.map(t=><option key={t} value={t}>{INV_TYPE_LABELS[t]||t}</option>)}
            </select></Field>
            {/* Correction parity with the Investments tab's own checkbox —
                this row's real paid_to_company_fund value is already sent
                by the backend's _edit block but had no UI control here. */}
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.paid_to_company_fund} onChange={e=>setForm({...form, paid_to_company_fund:e.target.checked})}/>
                Added to Company Reserve / Common Fund
              </label>
            </div>
          </>
        ) : isPartnerTxn ? (
          <>
            <Field label="Voucher No.">
              <input className="input mono" inputMode="numeric" value={form.voucher_number||''} onChange={e=>setForm({...form, voucher_number:e.target.value})} disabled={['interest_credit','interest_charge'].includes(form.transaction_type)}/>
            </Field>
            <Field label="Date"><input type="date" className="input" value={form.transaction_date||''} onChange={e=>setForm({...form, transaction_date:e.target.value})}/></Field>
            <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
            {/* Same user-reported bug as the Investments tab's Partner
                field above. */}
            <Field label="Partner *"><select className="input" value={form.partner_id||''} onChange={e=>setForm({...form, partner_id:e.target.value})}>
              <option value="">— select —</option>
              {eligiblePaidByPartners(partners, row.company_id, row.project_id, showAllPartners, form.partner_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
            </select></Field>
            {/* Different call than Partner Settlement's own Add/Edit
                Transaction modal, deliberately: that one is everyday data
                entry, where hiding Interest Credit/Charge behind "Post
                Interest as Transaction" prevents picking them by
                accident. This modal exists specifically to CORRECT an
                already-entered mistake -- restricting the type list here
                the same way would remove the ability to fix a row that
                was genuinely mis-entered into or out of an Interest type.
                Every type stays selectable, all with the same consistent
                plain-language wording used everywhere else (see
                AUDIT_TXN_TYPE_LABELS). */}
            <Field label="Type"><select className="input" value={form.transaction_type||''} onChange={e=>setForm({...form, transaction_type:e.target.value})}>
              <option value="">— select —</option>
              {TXN_TYPES.map(t=><option key={t} value={t}>{AUDIT_TXN_TYPE_LABELS[t]}</option>)}
            </select></Field>
            {!['interest_credit','interest_charge'].includes(form.transaction_type) && (
              <Field label="Paid By Partner (if not this partner / company funds)">
                <select className="input" value={form.paid_by_partner_id||''} onChange={e=>setForm({...form, paid_by_partner_id:e.target.value||null, paid_from_company_fund: e.target.value ? false : form.paid_from_company_fund})}>
                  <option value="">— none —</option>
                  {eligiblePaidByPartners(partners, row.company_id, row.project_id, showAllPartners, form.paid_by_partner_id).filter(p=>p.id!==form.partner_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={showAllPartners} onChange={e=>setShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
              </Field>
            )}
            {/* Correction-tool parity with Partner Settlement's own Add/Edit
                modal, which already has this checkbox — was missing here,
                so a row's real paid_from_company_fund value (already sent
                by the backend's _edit block) was never editable from the
                queue. Same interest exclusion as everywhere else it
                appears: those types never pay out real cash. */}
            {!['interest_credit','interest_charge'].includes(form.transaction_type) && (
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.paid_from_company_fund} onChange={e=>setForm({...form, paid_from_company_fund:e.target.checked, paid_by_partner_id: e.target.checked ? null : form.paid_by_partner_id})}/>
                  Paid from Company Reserve / Common Fund
                </label>
              </div>
            )}
          </>
        ) : (
          <>
            <Field label="Voucher No. *"><input className="input mono" inputMode="numeric" value={form.voucher_number||''} onChange={e=>setForm({...form, voucher_number:e.target.value})}/></Field>
            <Field label="Date"><input type="date" className="input" value={form.contribution_date||''} onChange={e=>setForm({...form, contribution_date:e.target.value})}/></Field>
            <Field label="Amount *"><input className="input mono" type="number" min="0" value={form.amount||''} onChange={e=>setForm({...form, amount:e.target.value})}/></Field>
            {/* Same fix as the two Partner fields above -- a Company
                Capital Contribution has no project of its own, so this
                passes null projectId, matching how the rest of the app
                already treats project-less forms (Company Expenses etc.):
                every investor hidden by default, since none of them
                personally contribute -- profit share/interest only. */}
            <Field label="Partner *"><select className="input" value={form.partner_id||''} onChange={e=>setForm({...form, partner_id:e.target.value})}>
              <option value="">— select —</option>
              {eligiblePaidByPartners(partners, row.company_id, null, showAllPartners, form.partner_id).map(p=><option key={p.id} value={p.id}>{partnerOptionLabel(p)}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-muted mt-1 cursor-pointer"><input type="checkbox" checked={showAllPartners} onChange={e=>setShowAllPartners(e.target.checked)}/>Show all partners (incl. investors)</label>
            </Field>
          </>
        )}
        <div className="col-span-1 md:col-span-2"><Field label={isSecurityInstrument || isInvestment || isContribution ? "Notes" : "Description"}>
          {isSecurityInstrument || isInvestment || isContribution
            ? <input className="input" value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/>
            : <input className="input" value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/>}
        </Field></div>
      </div>
      <div className="mt-6 flex justify-end gap-2"><button onClick={onClose} className="btn-outline btn-small">Cancel</button>
        <button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="aq-edit-save">{saving ? 'Saving…' : 'Save'}</button></div>
    </Modal>
  );
}

// ============= AUDIT QUEUE =============
// Replaces, not removes, the Sheets' own Audit Status column — every new
// expense/receipt starts pending_review here until a reviewer clears it.
function AuditQueuePage() {
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const companyF = useMultiFilter('AuditQueuePage.company');
  const entityF = useMultiFilter('AuditQueuePage.entity');
  const projectF = useMultiFilter('AuditQueuePage.project');
  const partnerF = useMultiFilter('AuditQueuePage.partner');
  const [dupOnly, setDupOnly] = usePersistedState('AuditQueuePage.dupOnly', false);
  // User-requested: a date filter for the Working Queue itself, so a
  // recently-added expense/receipt/security instrument can be found. This
  // filters by `added_at` (created_at -- when the row was actually
  // entered into the app), not `date` (the transaction's own expense/
  // receipt/lodged date) -- a backdated entry made today still needs to
  // show up under "added today", which the transaction date alone
  // wouldn't catch. Same From/To convention already used for the Audit
  // History tab's "Audited from/to" (auditedFrom/auditedTo, below).
  const [dateFrom, setDateFrom] = usePersistedState('AuditQueuePage.dateFrom', '');
  const [dateTo, setDateTo] = usePersistedState('AuditQueuePage.dateTo', '');
  // User-requested: free-text search across the queue, same convention
  // the Projects list search already uses.
  const [searchQuery, setSearchQuery] = usePersistedState('AuditQueuePage.searchQuery', '');
  // User-requested: client-wise search/filter, same dropdown convention
  // as the Projects list's own Client filter. Audit Queue rows don't
  // carry a client_name/department_client_id of their own (only
  // project_id/project_name) -- rather than widen every one of
  // _audit_queue_rows' several row-construction call sites on the
  // backend, resolved purely client-side via a project_id -> client_id
  // map built from a single /projects fetch, mirroring how the Projects
  // list itself already resolves department_client_id -> name.
  const clientF = useMultiFilter('AuditQueuePage.client');
  const [allProjects, setAllProjects] = useState([]);
  useEffect(() => { api.get('/projects').then(r => setAllProjects(r.data)).catch(() => {}); }, []);
  const masters = useMasters();
  const projectClientMap = useMemo(() => new Map(allProjects.map(p => [p.id, clientIdOfProject(p, masters.department_client)])), [allProjects, masters.department_client]);
  const clientOptions = useMemo(() =>
    (companyF.all ? mastersForCompany(masters.department_client, '') : companyF.ids.flatMap(id => mastersForCompany(masters.department_client, id)).filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i)).slice().sort((a,b)=>a.name.localeCompare(b.name)),
    [masters.department_client, companyF.all, companyF.ids]);
  // User-requested: same collapse/expand pattern Dashboard's Projects
  // Snapshot and Import History already use -- lets the queue be tucked
  // away once it's not actively being worked, without leaving the page.
  const [queueCollapsed, setQueueCollapsed] = useState(false);
  const { companies, needsPicker } = useMyCompanies();
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '—';
  const load = async () => {
    setLoading(true);
    try { setRows((await api.get('/audit-queue')).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error loading audit queue'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  // User-reported bug: these options used to build off every row in the
  // queue regardless of the selected Company, so picking a company still
  // left another company's projects/partners listed in these two
  // pickers (harmless to the actual filtered results -- companyFilter
  // was always applied there correctly -- but confusing/wrong-looking in
  // the pickers themselves). Both now narrow to the currently selected
  // company first, same as the actual row filter below.
  const projectOptions = useMemo(() => {
    const seen = new Map();
    rows.filter(r => companyF.matches(r.company_id))
      .forEach(r => { if (r.project_id && !seen.has(r.project_id)) seen.set(r.project_id, r.project_name); });
    // User-requested: A-Z, same as every other dropdown -- this one was
    // built by row-encounter order (whichever project's row happened to
    // load first), not alphabetical.
    return [...seen.entries()].sort((a,b)=>(a[1]||'').localeCompare(b[1]||''));
  }, [rows, companyF.all, companyF.ids]);
  // User-requested: filter by which partner personally paid, so a fix to
  // a batch of "Paid By" corrections (or just routine review) can be
  // cross-checked partner-by-partner against the totals below, without
  // needing every row's audit_status to already be Matched/Final first
  // (unlike the Partner Ledger, which only ever counts reviewed rows —
  // this reads directly off what's still sitting in the queue).
  const partnerOptions = useMemo(() => {
    const seen = new Map();
    rows.filter(r => companyF.matches(r.company_id))
      .forEach(r => { if (r.paid_by_partner_id && !seen.has(r.paid_by_partner_id)) seen.set(r.paid_by_partner_id, r.paid_by); });
    // User-requested: A-Z, same as every other partner dropdown in the app.
    return [...seen.entries()].sort((a,b)=>(a[1]||'').localeCompare(b[1]||''));
  }, [rows, companyF.all, companyF.ids]);
  // A company switch can leave a stale Project/Partner selection that no
  // longer belongs to it -- clear rather than silently filtering to zero
  // results with no explanation.
  useEffect(() => {
    projectF.prune(projectOptions.map(([id]) => id));
    partnerF.prune(partnerOptions.map(([id]) => id));
    clientF.prune(clientOptions.map(c => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyF.all, companyF.ids]);
  // User-requested: Company Expenses and Project Expenses are now separate
  // filter options (both were lumped under one generic "Expense" entity
  // type before) -- the backend doesn't need to know about this split,
  // since every expense row already carries project_id (or not), so it's
  // a pure client-side distinction. Shared by both tabs (Working Queue and
  // Audit History), since they filter off the same entityFilter state.
  const matchesEntityFilter = (r, filter) => {
    if (!filter) return true;
    if (filter === 'expense_project') return r.entity_type === 'expense' && !!r.project_id;
    if (filter === 'expense_company') return r.entity_type === 'expense' && !r.project_id;
    return r.entity_type === filter;
  };
  // The Type filter can hold several values: a row passes when it matches any ticked type.
  const matchesEntityF = (r) => entityF.all || entityF.ids.some(f => matchesEntityFilter(r, f));
  // Is a given kind of entity possibly in the current Type selection (drives which "Total of filtered ..." chips show)?
  const entityShows = (...keys) => entityF.all || keys.some(k => entityF.ids.includes(k));
  // User-requested: free-text search, same fields a reviewer would
  // actually be scanning for -- project, head/category, paid-by, voucher,
  // and any remarks/notes.
  const matchesAuditSearch = (r, q) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    if ([r.project_name, r.expense_head, r.paid_by, r.payer_name, r.vendor_name, r.staff_name, r.voucher_number, r.remarks].some(v => (v||'').toLowerCase().includes(s))) return true;
    // Amount search: accepts what's typed however the number is written
    // (118590, 118,590, 1,18,590, ₹118590, 118590.50) and matches any part of
    // the amount, so a half-remembered figure still finds the transaction.
    const digits = s.replace(/[,₹\s]|rs\.?/g, '');
    if (!/^\d+(\.\d*)?$/.test(digits)) return false;
    const amt = Number(r.amount);
    if (!isFinite(amt)) return false;
    return String(amt).includes(digits) || amt.toFixed(2).includes(digits);
  };
  const filteredRows = rows
    .filter(r => companyF.matches(r.company_id))
    .filter(r => matchesEntityF(r))
    .filter(r => projectF.matches(r.project_id))
    .filter(r => clientF.matches(projectClientMap.get(r.project_id)))
    .filter(r => partnerF.all || partnerF.matches(r.paid_by_partner_id) || partnerF.matches(r.payer_partner_id))
    .filter(r => !dupOnly || r.duplicate_of_id)
    .filter(r => !dateFrom || (r.added_at || '').slice(0,10) >= dateFrom)
    .filter(r => !dateTo || (r.added_at || '').slice(0,10) <= dateTo)
    .filter(r => matchesAuditSearch(r, searchQuery));
  const { sorted: visibleRows, sortKey, sortDir, toggleSort } = useSortableRows(filteredRows, 'date', 'desc');
  // User-requested: a running total of what's currently filtered, so it
  // can be checked at a glance against the source sheet's own total for
  // "did everything actually get imported" -- split by entity since an
  // Expenses total and a Receipts total answer two different questions,
  // and the split still makes sense even with "All Types" selected.
  const filteredTotals = useMemo(() => {
    const t = { expense: 0, receipt: 0, security_instrument: 0, investment: 0, partner_transaction: 0, company_capital_contribution: 0 };
    for (const r of filteredRows) t[r.entity_type] = (t[r.entity_type] || 0) + (Number(r.amount) || 0);
    return t;
  }, [filteredRows]);

  const [editRow, setEditRow] = useState(null);
  const [moveRow, setMoveRow] = useState(null);
  const [moveMode, setMoveMode] = useState('convert');
  const setStatus = async (row, status) => {
    try {
      await api.patch(`/audit-queue/${row.entity_type}/${row.id}`, { audit_status: status });
      toast.success('Status updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // User-requested: master_admin-only delete, only while a row is still
  // flagged Discrepancy -- same REST paths every other page in this app
  // already uses to delete these entities (already master_admin-gated
  // server-side), just a new entry point from the queue itself.
  const QUEUE_DELETE_PATH = { expense: '/expenses', receipt: '/receipts', security_instrument: '/security-instruments', investment: '/investments', partner_transaction: '/partner-transactions', company_capital_contribution: '/company-capital-contributions' };
  const deleteQueueRow = async (row) => {
    if (!window.confirm('Delete this row? This cannot be undone.')) return;
    try {
      await api.delete(`${QUEUE_DELETE_PATH[row.entity_type]}/${row.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // User-requested: a deliberate, separate "Accept & Mark Final" action --
  // matched/discrepancy/final now sit in the queue together (see the
  // backend's own comment on `pending`), so a plain status dropdown is
  // still there for correcting a mis-click, but ACTUALLY leaving the queue
  // now takes this second, explicit step instead of happening the instant
  // "matched" gets picked. Also covers the user's own alternative
  // suggestion (select-all-matched, finalize together) as one combined
  // design rather than picking just one.
  const [selected, setSelected] = useState(() => new Set());
  const [finalizing, setFinalizing] = useState(false);
  const toggleSelect = (row) => setSelected(s => {
    const next = new Set(s);
    const key = `${row.entity_type}:${row.id}`;
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const selectAllMatched = () => setSelected(new Set(filteredRows.filter(r => r.audit_status === 'matched').map(r => `${r.entity_type}:${r.id}`)));
  const clearSelection = () => setSelected(new Set());
  const matchedCount = filteredRows.filter(r => r.audit_status === 'matched').length;
  // User-requested: master-admin-only bulk "Mark Matched" for rows still
  // pending_review/discrepancy -- the per-row status dropdown already
  // lets any admin/accountant do this one row at a time (unrestricted,
  // same as every other routine edit), so this bulk convenience is a
  // workflow safeguard against an accidental mass status-change by a
  // less senior admin, not a real security boundary -- same reasoning
  // "Mark Final" itself was already built on (see [[project_anvid_
  // audit_queue_final_safety]]): the guard lives at the UI layer, not by
  // restricting the shared PATCH /audit-queue/{type}/{id} endpoint,
  // which stays reachable by any admin/accountant for the ordinary
  // per-row dropdown.
  const selectAllPending = () => setSelected(new Set(filteredRows.filter(r => r.audit_status !== 'matched').map(r => `${r.entity_type}:${r.id}`)));
  const pendingCount = filteredRows.filter(r => r.audit_status !== 'matched').length;
  const byId = useMemo(() => {
    const m = {};
    rows.forEach(r => { m[r.id] = r; });
    return m;
  }, [rows]);
  // Shared `selected` set can hold a mix of matched and not-yet-matched
  // rows at once (master_admin sees a checkbox on every row) -- each
  // bulk action only ever acts on its own eligible subset, never the
  // other's, so selecting a mix and clicking one button can't silently
  // skip a status transition the other button was meant to handle.
  const selectedMatchedKeys = useMemo(() => [...selected].filter(key => byId[key.split(':')[1]]?.audit_status === 'matched'), [selected, byId]);
  const selectedMatchableKeys = useMemo(() => [...selected].filter(key => byId[key.split(':')[1]] && byId[key.split(':')[1]].audit_status !== 'matched'), [selected, byId]);
  const bulkPatch = async (keys, targetStatus, verb) => {
    let ok = 0, failed = 0;
    // Modest concurrency (not one giant Promise.all, not fully sequential)
    // -- this can realistically be run against a large backlog (the
    // user's own 804-row Paid By cleanup, once reviewed) without either
    // taking forever one-at-a-time or hammering the API with hundreds of
    // simultaneous requests at once.
    const CHUNK = 10;
    for (let i = 0; i < keys.length; i += CHUNK) {
      const batch = keys.slice(i, i + CHUNK);
      const results = await Promise.allSettled(batch.map(key => {
        const [entity_type, id] = key.split(':');
        return api.patch(`/audit-queue/${entity_type}/${id}`, { audit_status: targetStatus });
      }));
      results.forEach(r => r.status === 'fulfilled' ? ok++ : failed++);
    }
    if (failed) toast.warning(`${ok} marked ${verb}, ${failed} failed — check those rows individually.`);
    else toast.success(`${ok} row(s) marked ${verb}.`);
  };
  const bulkFinalize = async () => {
    if (finalizing || selectedMatchedKeys.length === 0) return;
    if (!window.confirm(`Mark ${selectedMatchedKeys.length} selected row(s) Final and remove them from the queue? This can still be reopened later from the row's own project/company-expenses page if needed.`)) return;
    setFinalizing(true);
    await bulkPatch(selectedMatchedKeys, 'final', 'Final');
    setFinalizing(false);
    clearSelection();
    load();
  };
  const [matching, setMatching] = useState(false);
  const bulkMatch = async () => {
    if (matching || selectedMatchableKeys.length === 0) return;
    if (!window.confirm(`Mark ${selectedMatchableKeys.length} selected row(s) Matched? They'll then count toward project profit and be ready to finalize.`)) return;
    setMatching(true);
    await bulkPatch(selectedMatchableKeys, 'matched', 'Matched');
    setMatching(false);
    clearSelection();
    load();
  };

  // User-requested: a row Marked Final drops out of the working queue
  // above by design and used to have no way to be found again short of
  // digging through the project's own Expenses/Receipts tab. This tab
  // surfaces everything currently Final, with a master_admin-only batch
  // "Restore" back to Matched -- deliberately not all the way back to
  // Pending Review, since Matched and Final are equally eligible for
  // profit calculation (CALC_ELIGIBLE_AUDIT_STATUSES on the backend), so
  // restoring never changes a number, only whether the row is visible
  // here for further review. The backend blocks a restore the same way
  // it blocks every other audit_status change on a locked project.
  const isMaster = isMasterAdmin(user);
  const entryOnly = isDataEntry(user); // Data Entry: only its own entries, working queue only, view-only
  const [mainTabStored, setMainTab] = usePersistedState('AuditQueuePage.mainTab', 'working');
  const mainTab = entryOnly ? 'working' : mainTabStored;
  const [finalRows, setFinalRows] = useState([]);
  const [finalLoading, setFinalLoading] = useState(false);
  const [finalLoaded, setFinalLoaded] = useState(false);
  const loadFinal = async () => {
    setFinalLoading(true);
    try { setFinalRows((await api.get('/audit-queue/final')).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error loading final audit'); }
    setFinalLoading(false); setFinalLoaded(true);
  };
  useEffect(() => { if (mainTab === 'final' && !finalLoaded && !entryOnly) loadFinal(); }, [mainTab]);
  // User-requested: filter Audit History by "date of audit" (when a row
  // was actually marked Final -- audited_at, not the transaction's own
  // date, which the other tab's date sort already covers).
  const [auditedFrom, setAuditedFrom] = useState('');
  const [auditedTo, setAuditedTo] = useState('');
  const filteredFinalRows = finalRows
    .filter(r => companyF.matches(r.company_id))
    .filter(r => matchesEntityF(r))
    // Bug fix: the Client dropdown above is shown on both tabs (no
    // mainTab==='working' gate, unlike Project/Partner), but this list
    // never applied it -- picking a client while on Audit History had no
    // effect at all. Same projectClientMap lookup the Working Queue's own
    // filteredRows already uses.
    .filter(r => clientF.matches(projectClientMap.get(r.project_id)))
    .filter(r => !auditedFrom || (r.audited_at && r.audited_at.slice(0,10) >= auditedFrom))
    .filter(r => !auditedTo || (r.audited_at && r.audited_at.slice(0,10) <= auditedTo))
    .filter(r => matchesAuditSearch(r, searchQuery));
  const { sorted: visibleFinalRows, sortKey: finalSortKey, sortDir: finalSortDir, toggleSort: toggleFinalSort } = useSortableRows(filteredFinalRows, 'date', 'desc');
  const [finalSelected, setFinalSelected] = useState(() => new Set());
  const [restoring, setRestoring] = useState(false);
  const toggleFinalSelect = (row) => setFinalSelected(s => {
    const next = new Set(s);
    const key = `${row.entity_type}:${row.id}`;
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const clearFinalSelection = () => setFinalSelected(new Set());
  // User-requested: show the running total of what's ticked, right
  // alongside the restore action -- same "check the number before you
  // commit" habit the working queue's own filtered-totals chip already
  // supports.
  const finalSelectedTotal = useMemo(() => {
    if (!finalSelected.size) return 0;
    let sum = 0;
    for (const r of finalRows) {
      if (finalSelected.has(`${r.entity_type}:${r.id}`)) sum += Number(r.amount) || 0;
    }
    return sum;
  }, [finalSelected, finalRows]);
  const restoreSelected = async () => {
    if (restoring || finalSelected.size === 0) return;
    if (!window.confirm(`Restore ${finalSelected.size} selected row(s) totalling ${fmtINR(finalSelectedTotal)} back to Matched? They'll reappear in the working Audit Queue for review. This does not change any amount or profit calculation — Matched and Final are already counted the same way.`)) return;
    setRestoring(true);
    const items = [...finalSelected].map(key => { const [entity_type, id] = key.split(':'); return { entity_type, id }; });
    try {
      const results = (await api.post('/audit-queue/restore', { items })).data.results;
      const ok = results.filter(r=>r.ok).length;
      const failed = results.filter(r=>!r.ok);
      clearFinalSelection();
      loadFinal();
      if (failed.length) toast.warning(`${ok} restored, ${failed.length} failed — ${failed[0].error}${failed.length>1?' (+ more)':''}`);
      else toast.success(`${ok} row(s) restored to Matched.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setRestoring(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6 no-print">
        <div>
          <div className="section-eyebrow mb-1">Transaction Review</div>
          <h1 className="serif text-4xl">Audit Queue</h1>
          <div className="text-sm text-muted mt-1">Every new expense and receipt lands here as <em>pending review</em>. Nothing is auto-rejected — a possible-duplicate flag just tells the reviewer where to look twice.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input className="input" style={{width:200}} placeholder="Search…" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} data-testid="aq-search"/>
          {needsPicker && <MultiFilterPicker filter={companyF} items={companies.map(c=>({id:c.id, name:c.name}))} allLabel="All Companies" width={170} testId="aq-filter-company"/>}
          <MultiFilterPicker filter={entityF} items={[{id:'expense_project',name:'Project Expense'},{id:'expense_company',name:'Company Expense'},{id:'receipt',name:'Receipt'},{id:'security_instrument',name:'Security Instrument'},{id:'investment',name:'Investment'},{id:'partner_transaction',name:'Partner Transaction'},{id:'company_capital_contribution',name:'Capital Contribution'}]} allLabel="All Types" width={160} testId="aq-filter-entity"/>
          {mainTab==='working' && projectOptions.length > 0 && <MultiFilterPicker filter={projectF} items={projectOptions.map(([id,name])=>({id, name: name || '—'}))} allLabel="All Projects" width={180} testId="aq-filter-project"/>}
          {clientOptions.length > 0 && <MultiFilterPicker filter={clientF} items={clientOptions.map(c=>({id:c.id, name:c.name}))} allLabel="All Clients" width={180} testId="aq-filter-client"/>}
          {mainTab==='working' && partnerOptions.length > 0 && <MultiFilterPicker filter={partnerF} items={partnerOptions.map(([id,name])=>({id, name: name || '—'}))} allLabel="All Partners" width={170} testId="aq-filter-partner"/>}
          <ClearFiltersButton filters={[companyF, entityF, projectF, clientF, partnerF]}/>
          {mainTab==='working' && <button onClick={()=>setDupOnly(v=>!v)} className={"tab btn-small "+(dupOnly?'active':'')} data-testid="aq-filter-dup">Possible Duplicates Only</button>}
          {mainTab==='working' && <label className="flex items-center gap-1.5 text-xs">Added from <input type="date" className="input" style={{width:150}} value={dateFrom} onChange={e=>setDateFrom(e.target.value)} data-testid="aq-date-from"/></label>}
          {mainTab==='working' && <label className="flex items-center gap-1.5 text-xs">to <input type="date" className="input" style={{width:150}} value={dateTo} onChange={e=>setDateTo(e.target.value)} data-testid="aq-date-to"/></label>}
          {mainTab==='working' && (dateFrom || dateTo) && <button onClick={()=>{setDateFrom('');setDateTo('');}} className="tab btn-small">Clear dates</button>}
          {mainTab==='final' && <label className="flex items-center gap-1.5 text-xs">Audited from <input type="date" className="input" style={{width:150}} value={auditedFrom} onChange={e=>setAuditedFrom(e.target.value)} data-testid="aq-final-audited-from"/></label>}
          {mainTab==='final' && <label className="flex items-center gap-1.5 text-xs">to <input type="date" className="input" style={{width:150}} value={auditedTo} onChange={e=>setAuditedTo(e.target.value)} data-testid="aq-final-audited-to"/></label>}
          {mainTab==='final' && (auditedFrom || auditedTo) && <button onClick={()=>{setAuditedFrom('');setAuditedTo('');}} className="tab btn-small">Clear dates</button>}
          {!entryOnly && <button onClick={()=>window.print()} className="btn-outline btn-small inline-flex items-center gap-1"><Printer size={14}/>Print</button>}
        </div>
      </div>
      {entryOnly && <div className="card card-dark mb-6 text-sm" data-testid="aq-de-note">This shows only the entries <b>you</b> added. <i>Pending review</i> means waiting for the accounts team, <i>Matched</i> means checked, <i>Discrepancy</i> means the reviewer has a question — please contact them. Once an entry is fully approved it leaves this list.</div>}
      {!entryOnly && <div className="flex gap-2 mb-6 no-print">
        <button className={"tab "+(mainTab==='working'?'active':'')} onClick={()=>setMainTab('working')} data-testid="aq-tab-working">Working Queue</button>
        <button className={"tab "+(mainTab==='final'?'active':'')} onClick={()=>setMainTab('final')} data-testid="aq-tab-final">Audit History{finalRows.length > 0 ? ` (${finalRows.length})` : ''}</button>
      </div>}
      {mainTab==='working' && (
      <div className="card card-dark print-area">
        <div className="print-header">
          <div className="print-company">Audit Queue</div>
          <div className="print-report-title">{visibleRows.length} of {rows.length} entries{dupOnly ? ' — possible duplicates only' : ''}</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        {/* User-requested: same collapse/expand pattern Dashboard's Projects
            Snapshot and Import History already use -- tucks the whole
            working queue away once it's not actively being reviewed,
            without leaving the page (the filters above stay visible either
            way, since narrowing them down is often the first thing done
            before expanding back to look at the result). */}
        <button onClick={()=>setQueueCollapsed(v=>!v)} className="flex items-center justify-between w-full text-left mb-1 no-print" data-testid="aq-collapse-toggle">
          <div className="flex items-center gap-2">
            <div className="serif text-xl text-[color:var(--gold)]">Audit Queue</div>
            <span className="text-xs text-muted">({visibleRows.length} of {rows.length})</span>
          </div>
          {queueCollapsed ? <ChevronRight size={18}/> : <ChevronDown size={18}/>}
        </button>
        {!queueCollapsed && (
        <>
        <div className="flex flex-wrap items-center gap-3 mb-3 no-print">
          <div className="text-xs text-muted" data-testid="aq-count">Showing <b>{visibleRows.length}</b> of <b>{rows.length}</b> entries{visibleRows.length !== rows.length ? ' (filtered)' : ''}</div>
          {/* v43: rewritten from an exclude-list per chip (unwieldy once a
              6th/7th entity type joined Expense/Receipt/Security
              Instrument) to a plain positive match -- show a chip when
              nothing is filtered, or when the filter is exactly its own
              type(s). */}
          {entityShows('expense_project', 'expense_company') && <MetaChip icon={Receipt} label="Total of filtered Expenses — compare against your source sheet" value={filteredTotals.expense ? fmtINR(filteredTotals.expense) : null} tone="amber"/>}
          {entityShows('receipt') && <MetaChip icon={TrendingUp} label="Total of filtered Receipts — compare against your source sheet" value={filteredTotals.receipt ? fmtINR(filteredTotals.receipt) : null} tone="green"/>}
          {entityShows('security_instrument') && <MetaChip icon={Lock} label="Total of filtered Security Instruments — compare against your source sheet" value={filteredTotals.security_instrument ? fmtINR(filteredTotals.security_instrument) : null} tone="gold"/>}
          {entityShows('investment') && <MetaChip icon={Landmark} label="Total of filtered Investments — compare against your source sheet" value={filteredTotals.investment ? fmtINR(filteredTotals.investment) : null} tone="blue"/>}
          {entityShows('partner_transaction') && <MetaChip icon={Users} label="Total of filtered Partner Transactions — compare against your source sheet" value={filteredTotals.partner_transaction ? fmtINR(filteredTotals.partner_transaction) : null} tone="violet"/>}
          {entityShows('company_capital_contribution') && <MetaChip icon={Building2} label="Total of filtered Capital Contributions — compare against your source sheet" value={filteredTotals.company_capital_contribution ? fmtINR(filteredTotals.company_capital_contribution) : null} tone="blue"/>}
        </div>
        {/* User-requested: "matched" no longer silently vanishes the moment
            the dropdown changes -- it stays here until this deliberate,
            separate action promotes it to Final. Also covers the user's
            own alternative idea (select-all-matched, finalize together)
            in the same control. Extended: master_admin also gets a bulk
            "Mark Matched" for rows still pending_review/discrepancy (see
            selectAllPending's own comment for why this is UI-gated only,
            not a backend restriction). Both live in the same bar since
            they share the one `selected` set -- a master_admin can select
            a mix of matched and not-yet-matched rows at once, and each
            button only ever acts on its own eligible subset. */}
        {((canEdit && matchedCount > 0) || (isMaster && pendingCount > 0)) && (
          <div className="flex flex-wrap items-center gap-2 mb-3 no-print p-2 rounded surface-2">
            {canEdit && matchedCount > 0 && <span className="text-xs text-muted">{matchedCount} row(s) currently Matched, ready to finalize.</span>}
            {canEdit && matchedCount > 0 && <button onClick={selectAllMatched} className="tab btn-small" data-testid="aq-select-all-matched">Select all Matched ({matchedCount})</button>}
            {isMaster && pendingCount > 0 && <button onClick={selectAllPending} className="tab btn-small" data-testid="aq-select-all-pending">Select all Pending/Discrepancy ({pendingCount})</button>}
            {selected.size > 0 && <>
              <span className="text-xs text-[color:var(--gold)]">{selected.size} selected</span>
              {isMaster && selectedMatchableKeys.length > 0 && <button onClick={bulkMatch} disabled={matching} className="btn-outline btn-small inline-flex items-center gap-1" data-testid="aq-bulk-match"><CheckCircle size={14}/>{matching ? 'Marking…' : `Mark ${selectedMatchableKeys.length} Matched`}</button>}
              {selectedMatchedKeys.length > 0 && <button onClick={bulkFinalize} disabled={finalizing} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="aq-bulk-finalize"><CheckCircle size={14}/>{finalizing ? 'Finalizing…' : `Mark ${selectedMatchedKeys.length} Final`}</button>}
              <button onClick={clearSelection} className="tab btn-small">Clear</button>
            </>}
          </div>
        )}
        <div className="overflow-x-auto">
        <table className="tbl">
          <thead><tr>
            {canEdit && <th className="no-print"></th>}
            <SortTh label="Voucher No." sortKeyName="voucher_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Date" sortKeyName="date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {needsPicker && <th>Company</th>}
            <SortTh label="Project" sortKeyName="project_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Head" sortKeyName="expense_head" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            {/* User-reported: labeled "Paid By" for every entity type here,
                but this column's own backend value (r.paid_by) is the
                SUBJECT partner for investments/partner_transactions/
                company_capital_contributions (partner_by_id.get(partner_id)),
                not literally who fronted the cash -- that's a separate,
                correctly-labeled field only the Edit-in-Queue modal exposes
                ("Paid By Partner (if not this partner / company funds)").
                "Paid By" is accurate for an Expense (its own paid_by_
                partner_id really is who personally paid), but reads
                backwards for e.g. a Withdrawal (the partner is RECEIVING
                money, not paying it) or an Investment/Capital Contribution
                (the partner is putting money IN). "Partner" is correct for
                every row type in this shared, mixed-entity table. */}
            <SortTh label="Partner" sortKeyName="paid_by" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <SortTh label="Amount" sortKeyName="amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right/>
            <th>Duplicate?</th>
            <SortTh label="Status" sortKeyName="audit_status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
            <th>Remarks</th>{canEdit && <th className="no-print"></th>}
          </tr></thead>
          <tbody>
            {!loading && visibleRows.length===0 && <tr><td colSpan={canEdit?12:11} className="text-center text-muted py-8">{rows.length===0 ? 'Queue is empty — nothing pending review.' : 'No entries match these filters.'}</td></tr>}
            {loading && <LoadingRow colSpan={canEdit?12:11}/>}
            {visibleRows.map(r => {
              const dup = r.duplicate_of_id ? byId[r.duplicate_of_id] : null;
              const key = `${r.entity_type}:${r.id}`;
              return (
                <tr key={`${r.entity_type}-${r.id}`}>
                  {/* A regular admin/accountant only ever sees this for a
                      Matched row (the existing bulk-Finalize flow) -- a
                      master_admin sees it on every row, since they can
                      also bulk Mark Matched a pending_review/discrepancy
                      one. */}
                  {canEdit && <td className="no-print">{(r.audit_status==='matched' || isMaster) && <input type="checkbox" checked={selected.has(key)} onChange={()=>toggleSelect(r)} aria-label={`Select ${key}`} data-testid={`aq-select-${r.id}`}/>}</td>}
                  <td className="text-xs">{r.voucher_number || '—'}</td>
                  <td className="text-xs">{fmtDate(r.date)}</td>
                  {needsPicker && <td className="text-xs">{companyName(r.company_id)}</td>}
                  <td className="text-xs">{r.project_name || '—'}</td>
                  <td className="text-xs">{r.expense_head || '—'} <span className="chip" style={{marginLeft:4}}>{r.entity_type}</span><AuditPayeeLine r={r}/></td>
                  <td className="text-xs"><AuditPartnerCell r={r}/></td>
                  <td className="mono text-right">{fmtINR(r.amount)}</td>
                  <td>{r.duplicate_of_id
                    ? <span className="badge" style={{color:'#fb923c',borderColor:'rgba(251,146,60,0.4)'}} title={dup ? `Possible duplicate of ${fmtDate(dup.date)} · ${fmtINR(dup.amount)}` : 'Possible duplicate'}>possible duplicate</span>
                    : <span className="text-muted text-xs">—</span>}
                  </td>
                  <td><AuditStatusChip status={r.audit_status}/></td>
                  <td className="text-xs">{r.remarks || '—'}</td>
                  {canEdit && <td className="text-right whitespace-nowrap no-print">
                    {/* User-requested "must needed" function: fix a
                        discrepancy or mismatch right here, without leaving
                        the queue to hunt down the row on its own project/
                        company-expenses page. Edit-in-queue now covers all
                        three entity types, including security_instrument
                        (previously the one type without an _edit payload). */}
                    <button onClick={()=>setEditRow(r)} className="tab btn-small mr-1" data-testid={`aq-edit-${r.id}`}><Edit3 size={12}/></button>
                    {isMasterAdmin(user) && r.entity_type==='expense' && !r.project_locked && <ExpenseMoveButtons id={r.id} projectId={r.project_id} onOpen={(m)=>{setMoveMode(m); setMoveRow(r);}}/>}
                    {/* User-requested: master_admin can delete a row right
                        from the queue while it's still flagged Discrepancy
                        -- the most likely case of a genuinely wrong entry
                        that needs removing rather than corrected. The
                        underlying DELETE endpoints (expenses/receipts/
                        security-instruments) are already master_admin-only
                        server-side; this is just a new UI path to them. */}
                    {isMasterAdmin(user) && r.audit_status==='discrepancy' && <button aria-label="Delete" onClick={()=>deleteQueueRow(r)} className="tab btn-small text-red-300 mr-1" data-testid={`aq-delete-${r.id}`}><Trash2 size={12}/></button>}
                    {/* User-reported real risk: 'final' sat here as a
                        plain, instant-on-change option -- the same casual
                        control used for routine pending/matched/
                        discrepancy triage, with no confirmation, open to
                        any admin/accountant on any open project. The
                        dedicated "Mark Final" button above already exists
                        specifically to make that a deliberate, confirmed
                        action -- this dropdown never should have offered
                        a second, unguarded path to it. A row shown here
                        is never already 'final' (the working queue query
                        excludes it), so this was never displaying an
                        existing value, only offering an accidental one. */}
                    <select className="input" style={{width:150}} value={r.audit_status||'pending_review'} onChange={e=>setStatus(r, e.target.value)} data-testid={`aq-status-${r.id}`}>
                      {['pending_review','matched','discrepancy'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                    </select>
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        </>
        )}
      </div>
      )}
      {mainTab==='final' && (
      <div className="card card-dark print-area">
        <div className="print-header">
          <div className="print-company">Audit Queue — History</div>
          <div className="print-report-title">{visibleFinalRows.length} of {finalRows.length} entries</div>
          <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
        </div>
        <div className="text-xs text-muted mb-1 no-print">Rows already marked Final — these are excluded from the working queue above. {isMaster ? <>Select any and restore them back to Matched if they need another look. A row with <Lock size={11} className="inline"/> belongs to a still-locked project and can't be restored until that project is reopened.</> : 'Only master_admin can restore a row from here.'}</div>
        {/* User-requested: a default total for whatever's currently
            visible (filtered), not gated behind selecting rows first --
            the selection total just below answers a different question
            ("what would I restore"), this one answers "how much is
            sitting in Final right now under these filters". */}
        {visibleFinalRows.length > 0 && <div className="text-xs text-muted mb-3 no-print">Total ({visibleFinalRows.length} shown): <span className="mono text-[color:var(--gold)]" data-testid="aq-final-visible-total">{fmtINR(visibleFinalRows.reduce((sum,r)=>sum+Number(r.amount||0),0))}</span></div>}
        {isMaster && finalSelected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3 no-print p-2 rounded surface-2">
            <span className="text-xs text-[color:var(--gold)]">{finalSelected.size} selected</span>
            <span className="text-xs text-muted">Total: <span className="mono text-[color:var(--gold)]" data-testid="aq-final-selected-total">{fmtINR(finalSelectedTotal)}</span></span>
            <button onClick={restoreSelected} disabled={restoring} className="btn-gold btn-small inline-flex items-center gap-1" data-testid="aq-restore-selected"><Undo2 size={14}/>{restoring ? 'Restoring…' : `Restore ${finalSelected.size} (${fmtINR(finalSelectedTotal)}) to Matched`}</button>
            <button onClick={clearFinalSelection} className="tab btn-small">Clear</button>
          </div>
        )}
        <div className="overflow-x-auto">
        <table className="tbl">
          <thead><tr>
            {isMaster && <th className="no-print"></th>}
            <SortTh label="Voucher No." sortKeyName="voucher_number" sortKey={finalSortKey} sortDir={finalSortDir} onSort={toggleFinalSort}/>
            <SortTh label="Date" sortKeyName="date" sortKey={finalSortKey} sortDir={finalSortDir} onSort={toggleFinalSort}/>
            {needsPicker && <th>Company</th>}
            <SortTh label="Project" sortKeyName="project_name" sortKey={finalSortKey} sortDir={finalSortDir} onSort={toggleFinalSort}/>
            <SortTh label="Head" sortKeyName="expense_head" sortKey={finalSortKey} sortDir={finalSortDir} onSort={toggleFinalSort}/>
            {/* Same relabel as the Working Queue table above -- "Paid By"
                reads backwards for a Withdrawal/Investment/Capital
                Contribution row, "Partner" is correct for all of them. */}
            <SortTh label="Partner" sortKeyName="paid_by" sortKey={finalSortKey} sortDir={finalSortDir} onSort={toggleFinalSort}/>
            <SortTh label="Amount" sortKeyName="amount" sortKey={finalSortKey} sortDir={finalSortDir} onSort={toggleFinalSort} right/>
            <SortTh label="Audited On" sortKeyName="audited_at" sortKey={finalSortKey} sortDir={finalSortDir} onSort={toggleFinalSort}/>
            <th>Remarks</th>
            {isMaster && <th className="no-print"></th>}
          </tr></thead>
          <tbody>
            {!finalLoading && visibleFinalRows.length===0 && <tr><td colSpan={isMaster?(needsPicker?10:9):(needsPicker?8:7)} className="text-center text-muted py-8">{finalRows.length===0 ? 'Nothing marked Final yet.' : 'No entries match these filters.'}</td></tr>}
            {finalLoading && <LoadingRow colSpan={isMaster?(needsPicker?10:9):(needsPicker?8:7)}/>}
            {visibleFinalRows.map(r => {
              const key = `${r.entity_type}:${r.id}`;
              // User-requested: the backend already blocks Restore when
              // the row's project is locked (POST /audit-queue/restore's
              // own _assert_project_unlocked check, verified live) -- this
              // just surfaces that upfront instead of only after a click,
              // confirm, and a per-item rejection toast.
              const lockTitle = 'This project is approved & locked — an admin must reopen it first before this row can be restored.';
              return (
                <tr key={key}>
                  {isMaster && <td className="no-print"><input type="checkbox" checked={finalSelected.has(key)} disabled={r.project_locked} onChange={()=>toggleFinalSelect(r)} aria-label={`Select ${key}`} title={r.project_locked ? lockTitle : undefined} data-testid={`aq-final-select-${r.id}`}/></td>}
                  <td className="text-xs">{r.voucher_number || '—'}</td>
                  <td className="text-xs">{fmtDate(r.date)}</td>
                  {needsPicker && <td className="text-xs">{companyName(r.company_id)}</td>}
                  <td className="text-xs">{r.project_name || '—'}{r.project_locked && <Lock size={11} className="inline ml-1 text-muted" title={lockTitle}/>}</td>
                  <td className="text-xs">{r.expense_head || '—'} <span className="chip" style={{marginLeft:4}}>{r.entity_type}</span><AuditPayeeLine r={r}/></td>
                  <td className="text-xs"><AuditPartnerCell r={r}/></td>
                  <td className="mono text-right">{fmtINR(r.amount)}</td>
                  <td className="text-xs">{r.audited_at ? fmtDate(r.audited_at) : '—'}</td>
                  <td className="text-xs">{r.remarks || '—'}</td>
                  {isMaster && <td className="text-right whitespace-nowrap no-print">
                    {r.entity_type==='expense' && !r.project_locked && <ExpenseMoveButtons id={r.id} projectId={r.project_id} onOpen={(m)=>{setMoveMode(m); setMoveRow(r);}}/>}
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      )}
      {editRow && <AuditQueueEditModal row={editRow} onClose={()=>setEditRow(null)} onSaved={load}/>}
      {moveRow && <ExpenseMoveModal row={moveRow} mode={moveMode} onClose={()=>setMoveRow(null)} onSaved={()=>{ load(); loadFinal && loadFinal(); }}/>}
    </div>
  );
}

// Real-usage finding: after running the Excel Import Wizard, there was no
// durable, easy-to-find record of what happened -- the result toast
// disappears, and the Project/Audit Trail tab structurally can't show it
// (it's scoped to that one project's own record, never a company-wide
// bulk-import event). The backend already logs one summary row per import
// (entity, rows submitted, inserted, error/warning detail) to audit_logs
// as entity_type="bulk_import" -- this just surfaces it, so a user can
// check what actually happened, and whether a file was already imported,
// before running it again.
const IMPORT_STATUS_STYLE = {
  success: { label: 'Success', color: '#86efac', border: 'rgba(134,239,172,0.35)' },
  partial: { label: 'Partial', color: '#fcd34d', border: 'rgba(252,211,77,0.35)' },
  failed: { label: 'Failed', color: '#fca5a5', border: 'rgba(252,165,165,0.35)' },
  preview: { label: 'Preview', color: '#7dd3fc', border: 'rgba(125,211,252,0.35)' },
};

function ImportHistoryPanel({ entityFilter, title } = {}) {
  const { user } = useAuth();
  const isMaster = isMasterAdmin(user);
  const { companies, needsPicker } = useMyCompanies();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  // User feedback (2026-09-05): this list, expanded by default, pushed
  // the Excel/CSV Import Wizard below it further down the page than it
  // needed to be -- collapsed by default now, same complaint/fix shape
  // as every other "long list crowding out what's below it" instance.
  const [collapsed, setCollapsed] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  // entityFilter can be a plain entity string (existing callers, e.g.
  // "fixed_assets") or a predicate over the log's new_value, for a caller
  // that needs more than one entity/condition -- e.g. Extra Works' history
  // covering both 'extra_works' (new buckets) AND 'expenses' rows that
  // specifically targeted an Extra Work bucket (locked_project_is_extra_work).
  const matchesFilter = (d) => !entityFilter ? true : typeof entityFilter === 'function' ? entityFilter(d) : d.entity === entityFilter;
  const load = () => {
    setLoading(true);
    api.get('/audit-logs', { params: { entity_type: 'bulk_import', limit: 100 } })
      .then(r => setLogs(r.data.filter(l => matchesFilter(l.new_value || {}))))
      .catch(() => setLogs([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const companyName = (id) => companies.find(c => c.id === id)?.name || '—';

  const flatLogs = useMemo(() => logs.map(l => {
    const d = l.new_value || {};
    const inserted = d.inserted ?? 0;
    const errorCount = d.error_count ?? 0;
    const warningCount = d.warning_count ?? 0;
    // Real-usage finding: a batch rejected entirely via WARNINGS (a
    // missing/non-matching mandatory field, e.g. Extra Work's
    // client_name -- see ExcelImportWizard's own identical Step-4 fix)
    // had errorCount===0 and inserted===0, which this condition never
    // treated as "failed" -- it fell all the way through to "success",
    // the exact same misleading-badge bug just fixed in the wizard's own
    // result screen, here too.
    const status = d.dry_run ? 'preview' : (inserted === 0 && (errorCount > 0 || warningCount > 0)) ? 'failed' : errorCount > 0 ? 'partial' : 'success';
    return {
      ...l, _d: d, status,
      entity_label: (IMPORT_TARGETS[d.entity]?.label || d.entity || '—') + (d.locked_project_name ? ` — ${d.locked_project_name}` : ''),
      rows_submitted: d.rows_submitted ?? 0, inserted, error_count: errorCount, warning_count: warningCount,
    };
  }), [logs]);
  const { sorted, sortKey, sortDir, toggleSort } = useSortableRows(flatLogs, 'created_at', 'desc');

  const deleteEntry = async (logId) => {
    if (deletingId) return;
    if (!window.confirm('Delete this import-history entry? This only removes the history record — it does NOT undo any data that import created.')) return;
    setDeletingId(logId);
    try {
      await api.delete(`/audit-logs/${logId}`);
      setLogs(cur => cur.filter(l => l.id !== logId));
      toast.success('Deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="card card-dark mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="serif text-xl text-[color:var(--gold)]">{title || 'Import History'}</div>
        <div className="flex gap-2">
          <button onClick={()=>setCollapsed(c=>!c)} className="tab btn-small inline-flex items-center gap-1">
            {collapsed ? <ChevronDown size={14}/> : <ChevronUp size={14}/>}{collapsed ? 'Show' : 'Hide'}
          </button>
          <button onClick={load} className="tab btn-small" data-testid="import-history-refresh">Refresh</button>
        </div>
      </div>
      <p className="text-muted text-sm mb-3">
        Every Excel/CSV import run below, most recent first — check here before re-running a file, so you don't create duplicates.
        {!loading && ` (${logs.length} import${logs.length===1?'':'s'})`}
      </p>
      {!collapsed && (loading ? <Loading/> : logs.length === 0 ? (
        <div className="text-sm text-muted py-4 text-center">No imports yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead><tr>
              <SortTh label="When" sortKeyName="created_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
              <SortTh label="Who" sortKeyName="user_email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
              {needsPicker && <SortTh label="Company" sortKeyName="company_id" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>}
              <SortTh label="Imported" sortKeyName="entity_label" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
              <SortTh label="Rows in File" sortKeyName="rows_submitted" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
              <SortTh label="Inserted" sortKeyName="inserted" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
              <SortTh label="Errors" sortKeyName="error_count" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
              <SortTh label="Warnings" sortKeyName="warning_count" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
              <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}/>
              <th></th>
            </tr></thead>
            <tbody>
              {sorted.map(l => {
                const d = l._d;
                const hasDetail = (d.errors||[]).length > 0 || (d.warnings||[]).length > 0;
                const st = IMPORT_STATUS_STYLE[l.status];
                return (
                  <React.Fragment key={l.id}>
                    <tr>
                      <td className="text-xs">{fmtDate(l.created_at)}</td>
                      <td className="text-xs">{l.user_email || '—'}</td>
                      {needsPicker && <td className="text-xs">{companyName(l.company_id)}</td>}
                      <td className="text-xs">{l.entity_label}</td>
                      <td className="mono text-right">{l.rows_submitted}</td>
                      <td className="mono text-right text-[color:var(--gold)]">{l.inserted}</td>
                      <td className={"mono text-right " + (l.error_count > 0 ? 'text-red-400' : '')}>{l.error_count}</td>
                      <td className={"mono text-right " + (l.warning_count > 0 ? 'text-amber-400' : '')}>{l.warning_count}</td>
                      <td><span className="badge" style={{color:st.color, borderColor:st.border}}>{st.label}</span></td>
                      <td className="text-right whitespace-nowrap">
                        {hasDetail && <button onClick={()=>setExpandedId(expandedId===l.id?null:l.id)} className="tab btn-small">{expandedId===l.id?'Hide':'Details'}</button>}
                        {isMaster && <button onClick={()=>deleteEntry(l.id)} disabled={deletingId===l.id} aria-label="Delete history entry" title="Delete this history entry (does not undo the import)" className="tab btn-small ml-1"><Trash2 size={12}/></button>}
                      </td>
                    </tr>
                    {expandedId===l.id && hasDetail && (
                      <tr><td colSpan={needsPicker?10:9}>
                        <div className="text-xs mono space-y-1 p-3 rounded surface-2 my-1">
                          {(d.errors||[]).map((e,i) => <div key={'e'+i} className="text-red-400">{e}</div>)}
                          {(d.warnings||[]).map((w,i) => <div key={'w'+i} className="text-amber-400">{w}</div>)}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ============= DATA / IMPORT/EXPORT =============
function DataPanel() {
  const { user } = useAuth();
  const canEdit = ['admin','accountant','master_admin'].includes(user.role);
  const [preview, setPreview] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [dupeAck, setDupeAck] = useState(false);
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [restorePayload, setRestorePayload] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotData, setSnapshotData] = useState(null);
  const [snapshotPreview, setSnapshotPreview] = useState(null);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const importFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const text = await file.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) { toast.error('Invalid JSON'); return; }
    // Support raw localStorage export shape or already-mapped
    const p = {
      partners: data.partners || data.abpl_fin_partners || [],
      projects: data.projects || data.abpl_fin_projects || [],
      investments: data.investments || data.abpl_fin_investments || [],
      expenses: data.expenses || data.abpl_fin_expenses || [],
      receipts: data.receipts || data.abpl_fin_receipts || [],
      partnerTxns: data.partnerTxns || data.abpl_fin_partnerTransactions || [],
    };
    setPayload(p);
    setCommitResult(null);
    try {
      const r = await api.post('/import/preview', p);
      setPreview(r.data.counts);
      setDuplicates(r.data.duplicates || []);
      setDupeAck(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/import/commit', payload);
      // Whole-app final-check finding: this used to show only the error
      // COUNT in a transient success-toned toast, then immediately clear
      // every bit of state -- a user had no way to ever find out which
      // rows failed or why, even though the backend computes full
      // per-row error/warning text (lock rejections, unresolved project/
      // partner references, negative amounts, ...). Now keeps the full
      // result on screen, same rendering the "Restore from Backup"
      // section below already uses for exactly this purpose.
      setCommitResult(r.data);
      setPreview(null); setPayload(null); setDuplicates([]); setDupeAck(false);
      toast[r.data.errors.length > 0 ? 'warning' : 'success'](`Imported. Errors: ${r.data.errors.length}, Warnings: ${r.data.warnings.length}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };
  const canRestore = ['admin','master_admin'].includes(user.role);
  const canRestoreSnapshot = user.role === 'master_admin';
  useEffect(() => { if (canRestoreSnapshot) api.get('/backup/snapshots').then(r=>setSnapshots(r.data)).catch(()=>{}); }, [canRestoreSnapshot]);
  const previewSnapshot = async () => {
    if (!snapshotName) { toast.error('Choose a snapshot first'); return; }
    setSnapshotBusy(true);
    try {
      const r = await api.get(`/backup/snapshots/${snapshotName}`);
      setSnapshotData(r.data);
      const p = await api.post('/backup/restore-snapshot', { tables: r.data.tables, dry_run: true });
      setSnapshotPreview(p.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load snapshot'); }
    finally { setSnapshotBusy(false); }
  };
  const confirmSnapshotRestore = async () => {
    if (!window.confirm('Restore missing rows from this snapshot? This only fills in rows missing from your current database — it never overwrites or deletes existing data.')) return;
    setSnapshotBusy(true);
    try {
      const r = await api.post('/backup/restore-snapshot', { tables: snapshotData.tables, dry_run: false });
      const restored = Object.values(r.data.results).reduce((s,x)=>s+x.restored, 0);
      toast.success(`Restored ${restored} row(s) across ${Object.keys(r.data.results).length} tables`);
      setSnapshotPreview(r.data); setSnapshotData(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    finally { setSnapshotBusy(false); }
  };
  const restoreFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const text = await file.text();
    try {
      const data = JSON.parse(text);
      const p = {
        projects: data.projects || [],
        partners: data.partners || [],
        investments: data.investments || [],
        expenses: data.expenses || [],
        receipts: data.receipts || [],
        partnerTxns: data.partnerTxns || [],
        dry_run: true,
      };
      setRestorePayload(p);
      const r = await api.post('/backup/restore', p);
      setRestorePreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid backup file'); }
  };
  const confirmRestore = async () => {
    if (!window.confirm('Restore this backup into your company\'s live data? Existing partners/projects with matching names will be reused, not duplicated.')) return;
    setRestoreBusy(true);
    try {
      const r = await api.post('/backup/restore', { ...restorePayload, dry_run: false });
      toast.success(`Restored. Errors: ${r.data.errors.length}, Warnings: ${r.data.warnings.length}`);
      setRestorePayload(null); setRestorePreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    finally { setRestoreBusy(false); }
  };
  return (
    <div className="max-w-4xl mx-auto px-6 py-8 fade-up">
      <div className="section-eyebrow mb-2">Migration & Data</div>
      <h1 className="serif text-4xl mb-6">Data & Backup</h1>

      {canEdit && <ImportHistoryPanel/>}

      {canEdit && <div className="card card-dark mb-4">
        <div className="serif text-xl mb-2 text-[color:var(--gold)]">Excel / CSV Import Wizard</div>
        <p className="text-muted text-sm mb-3">Upload an .xlsx/.csv, map columns, preview, then commit. Supports Projects, Extra Works, Investments, Project Expenses, Company Expenses, Receipts, Partner Transactions, Fixed Assets.</p>
        <ExcelImportWizard/>
      </div>}

      {canEdit && <div className="card card-dark mb-4">
        <div className="serif text-xl mb-2 text-[color:var(--gold)]">Import from localStorage</div>
        <p className="text-muted text-sm mb-3">Export your legacy Anvid Buildcon financials JSON and upload it here. Duplicates are detected by name.</p>
        <input type="file" accept=".json,application/json" onChange={importFile} className="input" data-testid="import-file"/>
        {preview && (
          <div className="mt-4">
            <div className="text-sm mb-3">
              Preview: {Object.entries(preview).map(([k,v])=><span key={k} className="chip mr-2">{k}: {v}</span>)}
            </div>
            {duplicates.length > 0 && (
              <div className="card card-dark mb-3" style={{border:'1px solid rgba(230,167,103,0.4)'}}>
                <div className="text-xs text-[color:var(--gold)] mb-2 font-semibold">Possible duplicates found ({duplicates.length}) — clean the source data first if these aren't intentional:</div>
                <div className="text-xs mono max-h-32 overflow-y-auto space-y-1">{duplicates.slice(0,20).map((d,i)=><div key={i}>{d}</div>)}</div>
                <label className="flex items-center gap-2 text-xs mt-3 pt-2 cursor-pointer" style={{borderTop:'1px solid var(--border-subtle)'}}>
                  <input type="checkbox" checked={dupeAck} onChange={e=>setDupeAck(e.target.checked)} data-testid="legacy-dupe-ack"/>
                  I've reviewed these duplicates and want to import anyway
                </label>
              </div>
            )}
            <button onClick={commit} disabled={busy || (duplicates.length > 0 && !dupeAck)} className="btn-gold btn-small" data-testid="import-commit" title={(duplicates.length > 0 && !dupeAck) ? 'Acknowledge the duplicate warnings above first' : undefined}>{busy ? 'Importing…' : 'Confirm & Import'}</button>
          </div>
        )}
        {commitResult && (
          <div className="mt-4">
            <div className="text-sm mb-3">
              {Object.entries(commitResult.imported||{}).map(([k,v])=><span key={k} className="chip mr-2">{k}: {v}</span>)}
              <span className="chip mr-2">Warnings: {commitResult.warnings.length}</span>
              <span className="chip mr-2">Errors: {commitResult.errors.length}</span>
            </div>
            {commitResult.errors.length > 0 && (
              <div className="text-xs mono max-h-32 overflow-y-auto space-y-1 mb-2" style={{color:'var(--red-400, #f87171)'}}>{commitResult.errors.slice(0,20).map((e,i)=><div key={i}>{e}</div>)}</div>
            )}
            {commitResult.warnings.length > 0 && (
              <div className="text-xs mono max-h-32 overflow-y-auto space-y-1">{commitResult.warnings.slice(0,20).map((w,i)=><div key={i}>{w}</div>)}</div>
            )}
          </div>
        )}
      </div>}

      <div className="card card-dark">
        <div className="serif text-xl mb-2 text-[color:var(--gold)]">Export Backup</div>
        <p className="text-muted text-sm mb-3">Download all projects, partners and transactions as a JSON file (for re-importing via "Restore from Backup" below). Use the Reports page for CSV/PDF/Excel exports.</p>
        <button onClick={async ()=>{
          const [p,pa,e,i,rc,pt] = await Promise.all([
            api.get('/projects'), api.get('/partners'), api.get('/expenses'), api.get('/investments'), api.get('/receipts'), api.get('/partner-transactions')
          ]);
          const blob = new Blob([JSON.stringify({projects:p.data, partners:pa.data, expenses:e.data, investments:i.data, receipts:rc.data, partnerTxns:pt.data}, null, 2)], {type:'application/json'});
          const url = URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download=`anvid-backup-${Date.now()}.json`; a.click(); URL.revokeObjectURL(url);
        }} className="btn-outline btn-small inline-flex items-center gap-1"><Download size={14}/>Export JSON Backup</button>
      </div>

      {canRestore && <div className="card card-dark mt-4">
        <div className="serif text-xl mb-2 text-[color:var(--gold)]">Restore from Backup</div>
        <p className="text-muted text-sm mb-3">Upload a JSON file previously downloaded via "Export JSON Backup" above. Matching partners/projects are reused, never duplicated. Note: partner roles, deductions, and security instruments are not part of this backup and won't be restored — re-check those manually after a restore.</p>
        <input type="file" accept=".json,application/json" onChange={restoreFile} className="input" data-testid="restore-file"/>
        {restorePreview && (
          <div className="mt-4">
            <div className="text-sm mb-3">
              <span className="chip mr-2">Would insert: {restorePreview.would_insert}</span>
              <span className="chip mr-2">Warnings: {restorePreview.warnings.length}</span>
              <span className="chip mr-2">Errors: {restorePreview.errors.length}</span>
            </div>
            {restorePreview.warnings.length > 0 && (
              <div className="text-xs mono max-h-32 overflow-y-auto space-y-1 mb-3">{restorePreview.warnings.slice(0,20).map((w,i)=><div key={i}>{w}</div>)}</div>
            )}
            <button onClick={confirmRestore} disabled={restoreBusy || restorePreview.would_insert===0} className="btn-gold btn-small" data-testid="restore-commit">{restoreBusy ? 'Restoring…' : 'Confirm & Restore'}</button>
          </div>
        )}
      </div>}

      {canRestoreSnapshot && <div className="card card-dark mt-4">
        <div className="serif text-xl mb-2 text-[color:var(--gold)]">Restore from Weekly Snapshot</div>
        <p className="text-muted text-sm mb-3">
          Restores from the automated weekly database backup (27 tables — everything except user accounts, sessions, and the audit log). This only <strong>fills in rows missing</strong> from your current database by their original ID — it can never overwrite or delete anything that already exists. For recovering lost data, not for day-to-day imports.
        </p>
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <select className="input" style={{width:280}} value={snapshotName} onChange={e=>{ setSnapshotName(e.target.value); setSnapshotData(null); setSnapshotPreview(null); }} data-testid="snapshot-select">
            <option value="">— select a weekly backup —</option>
            {snapshots.map(s => <option key={s.name} value={s.name}>{s.name.replace('backup-','').replace('.json','')} ({s.size ? Math.round(s.size/1024)+' KB' : '—'})</option>)}
          </select>
          <button onClick={previewSnapshot} disabled={snapshotBusy || !snapshotName} className="btn-outline btn-small" data-testid="snapshot-preview">{snapshotBusy ? 'Loading…' : 'Preview'}</button>
        </div>
        {snapshots.length === 0 && <div className="text-xs text-muted">No weekly backups found yet — the first one lands after Sunday's scheduled run, or trigger one manually from the repo's Actions tab.</div>}
        {snapshotPreview && (
          <div className="mt-2">
            <div className="overflow-x-auto mb-3">
              <table className="tbl">
                <thead><tr><th>Table</th><th className="text-right">In Snapshot</th><th className="text-right">Already Present</th><th className="text-right">{snapshotPreview.dry_run ? 'Would Restore' : 'Restored'}</th></tr></thead>
                <tbody>
                  {Object.entries(snapshotPreview.results).filter(([,v])=>v.in_snapshot>0 || v.error).map(([t,v]) => (
                    <tr key={t}>
                      <td>{t}{v.error && <span className="text-red-400 text-xs"> — {v.error}</span>}</td>
                      <td className="mono text-right">{v.in_snapshot}</td>
                      <td className="mono text-right">{v.already_present}</td>
                      <td className="mono text-right text-[color:var(--gold)]">{v.restored}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {snapshotPreview.dry_run ? (
              <button onClick={confirmSnapshotRestore} disabled={snapshotBusy || Object.values(snapshotPreview.results).every(v=>v.in_snapshot===v.already_present)} className="btn-gold btn-small" data-testid="snapshot-commit">{snapshotBusy ? 'Restoring…' : 'Confirm & Restore Missing Rows'}</button>
            ) : (
              <div className="text-sm text-[color:var(--gold)]">Restore complete.</div>
            )}
          </div>
        )}
      </div>}
    </div>
  );
}

const ROLE_LABELS = { master_admin: 'Master Admin', admin: 'Company Admin', accountant: 'Accountant', partner: 'Partner', viewer: 'Viewer', data_entry: 'Data Entry' };
// master_admin is a strict superset of admin/'Company Admin' access — every UI
// gate that checks for admin needs to pass for master_admin too, matching the
// backend's require_roles() which already treats master_admin as always-allowed.
const isAdmin = (user) => user?.role === 'admin' || user?.role === 'master_admin';
// Deleting a real financial/business record (project, expense, extra work,
// vendor, bill, asset, ...) is Master Admin-only, matching every delete
// endpoint's require_master_admin() backend gate — plain Company Admin still
// passes isAdmin() for create/edit, just not delete.
const isMasterAdmin = (user) => user?.role === 'master_admin';
// User-requested: a narrower login than Accountant for daily data entry --
// full read access everywhere (same as any authenticated login, scoped to
// its granted companies/projects — unchanged), but write access limited to
// exactly 5 Add actions (Add Expense, Add EW Expenses, Add Company Expense,
// Add Security Instrument, Partner Transaction, on the Projects list and
// Company Expenses pages specifically) and read-only on the Audit Queue.
// Deliberately checked separately from isAdmin/isMasterAdmin -- this role
// must NOT satisfy any existing canEdit-style check elsewhere in the app
// (Bills, Receipts, Investments, Deductions, Projects, Masters, Users,
// Audit Queue edits/bulk actions, ...), only the specific Add buttons that
// opt into it via `canEdit || isDataEntry(user)`.
const isDataEntry = (user) => user?.role === 'data_entry';
// The only screens a Data Entry login can open (everything else is refused by
// the server as well): its dashboard guide, the two entry pages, and the
// view-only Audit Queue showing its own entries.
const DATA_ENTRY_PATHS = ['/app/dashboard', '/app/projects', '/app/company-expenses', '/app/audit-queue', '/app/gst-input'];
// A Partner login only gets the screens that show its own stake (the server refuses
// everything else): dashboard, its projects (+ their detail pages), Income and Bills
// for those projects, its Partner Settlement, and Reports.
const PARTNER_NAV_PATHS = ['/app/dashboard', '/app/projects', '/app/income', '/app/bills', '/app/partner-settlement', '/app/reports'];
const isPartnerPathAllowed = (path) => {
  const p = String(path || '').replace(/\/+$/, '');
  return PARTNER_NAV_PATHS.includes(p) || /^\/app\/projects\/[^/]+$/.test(p) || /^\/app\/partners\/[^/]+$/.test(p);
};
function NotForPartner({ children }) {
  const { user } = useAuth();
  return user?.role === 'partner' ? <Navigate to="/app/dashboard" replace/> : children;
}
function NotForDataEntry({ children }) {
  const { user } = useAuth();
  return isDataEntry(user) ? <Navigate to="/app/dashboard" replace/> : children;
}
// Printing is strictly off for a Data Entry login: no Print buttons, the
// browser's print command (menu or Ctrl/Cmd+P) does nothing, and anything
// that still reaches the print dialog prints a blank page (see index.css).
function PrintGuard() {
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('no-print-user');
    const origPrint = window.print;
    window.print = () => {};
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === 'p') { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener('keydown', onKey, true);
    return () => { html.classList.remove('no-print-user'); window.print = origPrint; window.removeEventListener('keydown', onKey, true); };
  }, []);
  return null;
}
// What a Data Entry login sees instead of the dashboard: no figures at all,
// just where to enter each kind of record and the rules that matter.
function DataEntryHome() {
  const { user } = useAuth();
  const tiles = [
    { to: '/app/projects?add=expense', title: 'Project Expense', text: 'An expense paid for a project.', btn: 'btn-red', go: 'Add Expense' },
    { to: '/app/projects?add=ew', title: 'Extra Work Expense', text: 'An expense for an Extra Work bucket (work not yet tied to a named tender).', btn: 'btn-amber', go: 'Add EW Expenses' },
    { to: '/app/company-expenses?add=company', title: 'Company Expense', text: 'An office / overhead expense that belongs to the company, not a project.', btn: 'btn-gold', go: 'Add Company Expense' },
    { to: '/app/company-expenses?add=security', title: 'Security Instrument', text: 'EMD, bank guarantee, FDR and similar deposits.', btn: 'btn-blue', go: 'Add Security Instrument' },
    { to: '/app/company-expenses?add=partner', title: 'Partner Transaction', text: 'Money paid out to a partner (withdrawal, reimbursement, profit payment…).', btn: 'btn-violet', go: 'Partner Transaction' },
    { to: '/app/audit-queue', title: 'My Entries', text: 'See the entries you added and where each one is in review.', btn: 'btn-outline', go: 'Open Audit Queue' },
  ];
  return (
    <div className="max-w-5xl mx-auto px-6 py-8 fade-up" data-testid="data-entry-home">
      <div className="section-eyebrow mb-2">Data Entry</div>
      <h1 className="serif text-4xl">Welcome{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}</h1>
      <div className="text-sm text-muted mt-1 mb-6">This login is for entering records only. Choose what you want to add below. Everything you add goes to the accounts team for review.</div>
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {tiles.map(t => (
          // The whole tile is one link: it opens the matching form directly.
          <Link key={t.title} to={t.to} className="card card-dark block hover:border-[color:var(--gold)]" style={{ textDecoration: 'none', color: 'inherit' }} data-testid={`de-tile-${t.title.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="serif text-xl mb-1">{t.title}</div>
            <div className="text-sm text-muted mb-4">{t.text}</div>
            <span className={t.btn + ' btn-small inline-flex items-center gap-1'}>{t.to.includes('add=') && <Plus size={14}/>}{t.go}</span>
          </Link>
        ))}
      </div>
      <div className="card card-dark mb-6">
        <div className="flex items-center gap-2 mb-3"><BookOpen size={18} className="text-[color:var(--gold)]"/><div className="serif text-2xl">Data Entry Guide</div></div>
        <ol className="text-sm grid gap-2" style={{ paddingLeft: 18, listStyle: 'decimal' }}>
          <li><b>Pick the company first</b> (if you see a company box), then the project or category.</li>
          <li><b>Voucher number</b> is required, digits only, and can never be used twice for the same company. If it is rejected, check the voucher book — do not change the number to get past it.</li>
          <li><b>Date, category and amount</b> must match the voucher exactly. Enter the amount without commas.</li>
          <li><b>Paid by:</b> choose the partner who personally paid, or the bank account it was paid from.</li>
          <li><b>Vendor:</b> choose the vendor from the list, or "Not a vendor payment". Salary or staff payments need the Staff Member too.</li>
          <li>After you press Save the entry is locked for review — you cannot edit or delete it. If you made a mistake, tell the accounts team and give them the voucher number.</li>
          <li>Open <b>Audit Queue</b> to see the entries you added and where each one is in review. <i>Pending review</i> = waiting; <i>Matched</i> = checked; <i>Discrepancy</i> = the reviewer has a question — contact them.</li>
          <li>Entries on a project that is already approved and locked are refused. You can enter Extra Work <b>expenses</b> with <b>Add EW Expenses</b> (on the Projects page); income and every other Extra Work record are not part of this login.</li>
        </ol>
      </div>
      <div className="text-xs text-muted">For data safety this login cannot view reports, other people's entries, or print. If you need something you can't see here, ask the accounts team.</div>
    </div>
  );
}

// Multi-select grant picker + "All ___" toggle, used both in the User
// Management table (inline) and the Invite User modal — for company access
// (v16) and partner links (v17), which share the exact same shape: allAccess
// and selectedIds are mutually exclusive at the UI level, matching the
// backend's all_X_access flag vs profile_X join-table rows.
function GrantPicker({ items, selectedIds, allAccess, onToggle, onSetAllAccess, disabled, canGrantAll = true, allLabel, noneWidth = 190 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef(null);
  // Same tap-outside/Escape pattern as the nav's "More" dropdown — without
  // it this only closes by clicking its own toggle button again, so it
  // stays open (and visually stuck on top of whatever's underneath) once
  // the user's attention has moved elsewhere on the page.
  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onEscape = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onOutside);
    document.addEventListener('keydown', onEscape);
    return () => { document.removeEventListener('pointerdown', onOutside); document.removeEventListener('keydown', onEscape); };
  }, [open]);
  // User-reported (real device screenshot): "2 Projects" was rendering as
  // "2 Projectss" -- allLabel is always already-plural ("All Companies" /
  // "All Projects" / "All Partners"), so `noun` here was already plural,
  // and unconditionally appending another "s" whenever the count wasn't
  // exactly 1 double-pluralized every one of them (Companiess/Projectss/
  // Partnerss), not just this one call site -- this function is shared by
  // every multi-select company/project/partner picker in the app. Derive
  // the real singular instead (only ever needed for the count===1 case)
  // rather than guessing a suffix to add.
  const plural = allLabel.replace('All ', '');
  // User-reported: "All Statuses" singularized to "Statuse" (stripping
  // only the trailing "s") whenever exactly one status was picked --
  // "Statuses" adds "-es", not "-s", the same class of irregular plural
  // "-ies" already got its own branch for below. Generalized to every
  // "-ses/-xes/-zes/-ches/-shes" ending instead of special-casing this
  // one word, so any future allLabel ending the same way (Boxes, Bushes,
  // ...) singularizes correctly too.
  const singular = plural.endsWith('ies') ? plural.slice(0, -3) + 'y'
    : /(s|x|z|ch|sh)es$/i.test(plural) ? plural.slice(0, -2)
    : plural.replace(/s$/, '');
  const summary = allAccess ? allLabel : (selectedIds.length === 0 ? '— none —' : `${selectedIds.length} ${selectedIds.length === 1 ? singular : plural}`);
  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => { setOpen(v => !v); setQ(''); }} disabled={disabled} className="input text-left" style={{ width: noneWidth, cursor: disabled ? 'default' : 'pointer' }}>{summary}</button>
      {open && !disabled && (
        <div className="card card-dark" style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, marginTop: 4, width: 220, maxHeight: 260, overflowY: 'auto', padding: 12 }}>
          {canGrantAll && <label className="flex items-center gap-2 text-sm mb-2 pb-2 cursor-pointer" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <input type="checkbox" checked={allAccess} onChange={e => onSetAllAccess(e.target.checked)} />
            {allLabel}
          </label>}
          {items.length > 8 && <input className="input mb-2" style={{ padding: '6px 10px' }} placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} aria-label="Search the list" data-testid="picker-search"/>}
          {items.filter(it => !q.trim() || String(it.name).toLowerCase().includes(q.trim().toLowerCase())).map(it => (
            <label key={it.id} className="flex items-center gap-2 text-sm mb-1 cursor-pointer" style={{ opacity: allAccess ? 0.4 : 1 }}>
              <input type="checkbox" disabled={allAccess} checked={selectedIds.includes(it.id)} onChange={() => onToggle(it.id)} />
              {it.name}
            </label>
          ))}
          {q.trim() && !items.some(it => String(it.name).toLowerCase().includes(q.trim().toLowerCase())) && <div className="text-xs text-muted">No match.</div>}
        </div>
      )}
    </div>
  );
}

// A filter that can hold several values or "all": the same flag-plus-ids convention the Projects Status
// filter uses (the "all" flag is its own state, not derived from ids.length===0, so unticking All with
// nothing ticked is a real "none" state instead of silently snapping back to everything).
function useMultiFilter(key) {
  const [all, setAll] = usePersistedState(key + 'All', true);
  const [ids, setIds] = usePersistedState(key + 'Ids', []);
  return {
    all, ids,
    matches: (v) => all || ids.includes(v),
    toggle: (id) => setIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]),
    setAllAccess: (v) => { setAll(v); if (v) setIds([]); },
    reset: () => { setAll(true); setIds([]); },
    active: !all,
    // exactly one value picked -> that value, else '' (for the few places that need a single value, e.g. an export parameter)
    soleId: !all && ids.length === 1 ? ids[0] : '',
    // jump straight to one value (e.g. "view this project's entries")
    only: (id) => { setAll(false); setIds([id]); },
    // drop picks that are no longer valid options; if none are left, go back to "all" (never a silent empty "none")
    prune: (validIds) => { if (all) return; const kept = ids.filter(x => validIds.includes(x)); if (kept.length === ids.length) return; if (kept.length === 0) { setAll(true); setIds([]); } else setIds(kept); },
  };
}
// Shown only while a dropdown filter is narrowing the list; resets every filter passed in (plus anything extra).
function ClearFiltersButton({ filters, extraActive, onClear }) {
  if (!(extraActive || filters.some(f => f.active))) return null;
  return <button type="button" className="tab btn-small" onClick={() => { filters.forEach(f => f.reset()); if (onClear) onClear(); }} data-testid="clear-filters">Clear filters</button>;
}
function MultiFilterPicker({ filter, items, allLabel, width, testId, onChange }) {
  return (
    <div data-testid={testId}>
      <GrantPicker items={items} selectedIds={filter.ids} allAccess={filter.all}
        onToggle={id => { filter.toggle(id); if (onChange) onChange(); }}
        onSetAllAccess={v => { filter.setAllAccess(v); if (onChange) onChange(); }}
        allLabel={allLabel} noneWidth={width}/>
    </div>
  );
}

// ============= VOUCHER TOOLS =============
// User-requested: master_admin/company_admin only (see isAdmin gate on
// this nav link in TopNav) -- the printable Payment Voucher, plus a
// reference sheet of real, current names to write on it. Unlike the
// static Payment Voucher/Handbook (bundled files, never change without a
// redeploy), this reference is generated fresh on every visit from the
// same live endpoints (/companies, /projects, /expense-categories) every
// other page already uses -- "customisable... generate time to time" was
// the explicit ask, so a stale snapshot wasn't the right fit here.
const LOCKED_PROJECT_STATUSES_REF = ['profit_approved', 'settled', 'closed'];
const SECURITY_INSTRUMENT_TYPES_REF = ['EMD', 'Fixed Deposit', 'Bank Guarantee', 'Demand Draft (DD)', 'Refundable Advance', 'Other'];
// User-requested: left off the first pass -- the everyday Partner
// Transaction types (mirrors PARTNER_TXN_QUICK_CATEGORIES' own plain-
// language labels, minus Interest Credit/Charge, which that same list
// already deliberately hides from ordinary data entry -- an admin posts
// those directly from Current Account, never from a hand-filled voucher).
const PARTNER_TXN_TYPES_REF = [
  { label: 'Capital Investment', dir: 'in' },
  { label: 'Loan from Partner', dir: 'in' },
  { label: 'Capital Return', dir: 'out' },
  { label: 'Loan Repayment', dir: 'out' },
  { label: 'Profit Payment', dir: 'out' },
  { label: 'Reimbursement', dir: 'out' },
  { label: 'Withdrawal', dir: 'out' },
  { label: 'Other', dir: 'out' },
];

// Printable Payment Voucher for Anvid Buildcon only (no company tick-boxes,
// no EW / Security Instrument options), with the Vendor Name chosen from the
// vendors already added to Anvid Buildcon in Staff & Vendor. Same A4-landscape
// 2-up front/back layout as the generic /payment-voucher.html. Lives outside
// AppShell (no nav chrome) so it prints clean; every class is apv- prefixed so
// nothing collides with the app's own styles.
const APV_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@500;600&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
.apv{--paper:#faf7f0;--ink:#201c14;--ink-soft:#4a4436;--line:#c7bda3;--brass:#8a6a2a;--brass-deep:#5f4a1e;--panel:#f2ecdd;--screen-bg:#3a3226;
  min-height:100vh;background:var(--screen-bg);color:var(--ink);font-family:'IBM Plex Sans',system-ui,sans-serif;padding:28px 0;display:flex;flex-direction:column;align-items:center;gap:16px;box-sizing:border-box;}
.apv *{box-sizing:border-box;}
.apv-toolbar{width:100%;max-width:640px;padding:0 16px;display:flex;flex-direction:column;gap:10px;align-items:center;text-align:center;color:#e9e2cf;}
.apv-toolbar h1{font-family:'Source Serif 4',Georgia,serif;font-weight:600;font-size:21px;margin:0;color:#f5efdd;}
.apv-toolbar p{margin:0;font-size:13px;line-height:1.65;color:#c9bfa2;max-width:560px;}
.apv-toolbar .apv-note{font-size:11.5px;color:#a89a78;}
.apv-printbtn{font-family:inherit;font-weight:600;font-size:13px;background:var(--brass);color:#1c1607;border:none;padding:10px 22px;border-radius:3px;cursor:pointer;}
.apv-printbtn:hover{background:#a17f36;}
.apv-back{color:#c9bfa2;font-size:12px;text-decoration:underline;}
.apv-scroll{max-width:100%;overflow-x:auto;padding:2px;}
.apv-pagelabel{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:#a89a78;margin-bottom:6px;text-align:center;}
.apv-a4{display:flex;width:297mm;height:210mm;background:var(--paper);box-shadow:0 12px 34px rgba(0,0,0,.45);}
.apv-sheet{width:148.5mm;height:210mm;padding:6mm 8mm;position:relative;display:flex;flex-direction:column;gap:1.8mm;overflow:hidden;}
.apv-sheet:first-child{border-right:0.6pt dashed var(--line);}
.apv-sheet::before{content:"";position:absolute;inset:3mm;border:0.6pt solid var(--line);pointer-events:none;}
.apv-line{border:none;border-bottom:0.75pt solid var(--ink-soft);background:transparent;font-family:inherit;font-size:9.5pt;color:var(--ink);padding:1.5pt 2pt;width:100%;border-radius:0;}
select.apv-line{-webkit-appearance:none;appearance:none;cursor:pointer;padding-right:16px;background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%238a6a2a'/%3E%3C/svg%3E") no-repeat right 3px center;}
textarea.apv-line{resize:none;height:7mm;border:0.75pt solid var(--ink-soft);padding:2pt 3pt;}
.apv-tiny{display:block;font-size:6.6pt;letter-spacing:.08em;text-transform:uppercase;color:var(--brass-deep);font-weight:600;margin-bottom:1pt;}
.apv-box{display:flex;flex-direction:column;}
.apv-row{display:flex;gap:4mm;}
.apv-row>.apv-box{flex:1;min-width:0;}
.apv-row+.apv-row{margin-top:1.6mm;}
.apv-mast{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:1.4pt solid var(--brass-deep);padding-bottom:1.6mm;}
.apv-brand{font-family:'Source Serif 4',Georgia,serif;font-weight:500;font-size:13pt;line-height:1;color:var(--ink);}
.apv-num{display:flex;align-items:baseline;gap:3pt;justify-content:flex-end;font-family:'IBM Plex Mono',monospace;}
.apv-num span{font-size:7pt;color:var(--ink-soft);}
.apv-num input{width:24mm;text-align:center;font-size:9.5pt;}
.apv-sec{font-size:7.4pt;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:#fff;background:var(--brass-deep);padding:1.6pt 5pt;display:inline-block;margin-bottom:1.3mm;print-color-adjust:exact;-webkit-print-color-adjust:exact;}
.apv-panel{border:0.6pt solid var(--line);background:var(--panel);padding:1.8mm 3mm;print-color-adjust:exact;-webkit-print-color-adjust:exact;}
.apv-checks{display:flex;flex-wrap:wrap;row-gap:1.4mm;column-gap:4.5mm;align-items:center;font-size:8.6pt;}
.apv-checks label{display:flex;align-items:center;gap:2pt;white-space:nowrap;}
.apv-chk{width:8.5pt;height:8.5pt;border:0.9pt solid var(--ink);display:inline-block;flex-shrink:0;}
.apv-company{font-family:'Source Serif 4',Georgia,serif;font-weight:600;font-size:11pt;padding:1pt 0;border-bottom:0.75pt solid var(--ink-soft);}
.apv-foot{display:flex;gap:4mm;}
.apv-foot>div{flex:1;}
.apv-sign{display:flex;justify-content:space-between;gap:3mm;}
.apv-sign>div{flex:1;text-align:center;}
.apv-sign .apv-sl{border-top:0.75pt solid var(--ink);margin-top:9mm;padding-top:1.4pt;font-size:6.4pt;letter-spacing:.04em;text-transform:uppercase;color:var(--ink-soft);}
@media print{
  @page{size:A4 landscape;margin:0;}
  html,body{background:#fff !important;margin:0 !important;padding:0 !important;}
  .apv{background:#fff;padding:0;display:block;min-height:0;}
  .apv-toolbar,.apv-pagelabel{display:none !important;}
  select.apv-line{background-image:none;padding-right:2pt;}
  .apv-scroll{overflow:visible;padding:0;}
  .apv-a4{box-shadow:none;margin:0;break-after:page;}
  .apv-a4:last-child{break-after:auto;}
}`;

function ApvVoucher({ companyName, vendors }) {
  const Chk = ({ children }) => <label><span className="apv-chk"/> {children}</label>;
  return (
    <div className="apv-sheet">
      <div className="apv-mast">
        <div className="apv-brand">Payment Voucher</div>
        <div className="apv-num"><span>No.</span><input className="apv-line"/></div>
      </div>
      <div className="apv-row">
        <div className="apv-box" style={{flex:'0 0 32mm'}}><label className="apv-tiny">Date</label><input className="apv-line"/></div>
        <div className="apv-box"><label className="apv-tiny">Company</label><div className="apv-company">{companyName}</div></div>
      </div>
      <div>
        <span className="apv-sec">Charged To</span>
        <div className="apv-panel">
          <div className="apv-checks">
            <Chk>Project Expense</Chk>
            <Chk>Company Expense</Chk>
            <Chk>Partner Transaction</Chk>
          </div>
          <div className="apv-row">
            <div className="apv-box"><label className="apv-tiny">Project / Tender Name</label><input className="apv-line"/></div>
            <div className="apv-box"><label className="apv-tiny">Expense/Overhead Category</label><input className="apv-line"/></div>
          </div>
          <div className="apv-row">
            <div className="apv-box"><label className="apv-tiny">Partner Name — Partner Transaction</label><input className="apv-line"/></div>
          </div>
        </div>
      </div>
      <div>
        <span className="apv-sec">Paid Through</span>
        <div className="apv-panel">
          <div className="apv-row">
            <div className="apv-box"><label className="apv-tiny">Paid By</label>
              <div className="apv-checks">
                <Chk>Company Funds</Chk>
                <Chk>Partner —</Chk>
                <input className="apv-line" style={{flex:1,minWidth:'20mm'}}/>
              </div>
            </div>
          </div>
          <div className="apv-row">
            <div className="apv-box"><label className="apv-tiny">Bank Account (if bank / cheque / UPI)</label><input className="apv-line"/></div>
            <div className="apv-box" style={{flex:'0 0 40mm'}}>
              <label className="apv-tiny">Reserve / Common Fund</label>
              <div className="apv-checks"><Chk>Paid from Company Fund</Chk></div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <span className="apv-sec">Staff Payment (if applicable)</span>
        <div className="apv-panel">
          <div className="apv-row">
            <div className="apv-box"><label className="apv-tiny">Staff Name</label><input className="apv-line"/></div>
            <div className="apv-box" style={{flex:'0 0 62mm'}}><label className="apv-tiny">Type</label>
              <div className="apv-checks"><Chk>Salary</Chk><Chk>Travel</Chk><Chk>Other</Chk></div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <span className="apv-sec">Payment — one voucher, one entry</span>
        <div className="apv-panel">
          <div className="apv-row">
            <div className="apv-box"><label className="apv-tiny">Vendor Name</label>
              <select className="apv-line" defaultValue="" data-testid="apv-vendor">
                <option value=""></option>
                {vendors.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
              </select>
            </div>
            <div className="apv-box" style={{flex:'0 0 34mm'}}><label className="apv-tiny">Bill No.</label><input className="apv-line"/></div>
          </div>
          <div className="apv-row">
            <div className="apv-box"><label className="apv-tiny">Particulars</label><input className="apv-line"/></div>
          </div>
          <div className="apv-row">
            <div className="apv-box" style={{flex:'0 0 30mm'}}><label className="apv-tiny">Mode</label><input className="apv-line"/></div>
            <div className="apv-box"><label className="apv-tiny">Amount (₹)</label><input className="apv-line" style={{fontWeight:700,fontSize:'11pt'}}/></div>
          </div>
          <div className="apv-row">
            <div className="apv-box"><label className="apv-tiny">Amount in Words</label><input className="apv-line" style={{fontStyle:'italic'}}/></div>
          </div>
        </div>
      </div>
      <div className="apv-foot">
        <div className="apv-box"><label className="apv-tiny">Remarks / Notes</label><textarea className="apv-line"/></div>
        <div className="apv-box" style={{flex:'0 0 45mm'}}><label className="apv-tiny">Attachments</label>
          <div className="apv-checks" style={{flexDirection:'column',alignItems:'flex-start',rowGap:'1mm'}}><Chk>Bill / Invoice</Chk><Chk>Receipt</Chk></div>
        </div>
      </div>
      <div style={{flex:1}}/>
      <div className="apv-sign">
        <div><div className="apv-sl">Written By</div></div>
        <div><div className="apv-sl">Entered By (Data Entry)</div></div>
        <div><div className="apv-sl">Audited By</div></div>
      </div>
    </div>
  );
}

// Standalone, offline-usable copy of the Anvid Buildcon voucher (same markup
// as the on-screen page, rendered from the SAME ApvVoucher component so the
// two can never drift). The vendor dropdown is baked in with the vendors as
// they are at the moment of download.
async function downloadAnvidVoucher(companyName, vendors) {
  const { renderToStaticMarkup } = await import('react-dom/server');
  const pair = renderToStaticMarkup(<div className="apv-a4"><ApvVoucher companyName={companyName} vendors={vendors}/><ApvVoucher companyName={companyName} vendors={vendors}/></div>);
  const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const html = '<!doctype html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/>'
    + `<title>${esc(companyName)} — Payment Voucher</title><style>${APV_CSS}</style></head><body style="margin:0"><div class="apv">`
    + `<div class="apv-toolbar"><h1>Payment Voucher — ${esc(companyName)}</h1><button class="apv-printbtn" onclick="window.print()">Print / Save as PDF</button>`
    + '<p class="apv-note">Prints as one A4 sheet, landscape, 2 vouchers side by side (page 1 = front). A second identical page follows for the back of the same sheet — print double-sided (flip on the short edge), cut down the dashed centre line.</p></div>'
    + `<div class="apv-scroll"><div class="apv-pagelabel">Page 1 — Front</div>${pair}</div>`
    + `<div class="apv-scroll"><div class="apv-pagelabel">Page 2 — Back (same sheet, other side)</div>${pair}</div>`
    + '</div></body></html>';
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const a = document.createElement('a');
  a.href = url; a.download = `${companyName.replace(/[^A-Za-z0-9]+/g, '-')}-Payment-Voucher.html`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function AnvidPaymentVoucher() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    Promise.all([api.get('/companies'), api.get('/vendors')])
      .then(([c, v]) => {
        const anvid = c.data.find(x => /anvid/i.test(x.name || ''));
        if (!anvid) { setError('Anvid Buildcon is not among the companies this login can see.'); return; }
        const vendors = v.data.filter(x => x.company_id === anvid.id && (x.status || 'active') === 'active')
          .slice().sort((a, b) => a.name.localeCompare(b.name));
        setData({ companyName: anvid.name, vendors });
      })
      .catch(e => setError(e.response?.data?.detail || 'Could not load vendors'));
  }, []);
  if (!isAdmin(user)) return <Navigate to="/app/dashboard" replace/>;
  if (error) return <div className="max-w-2xl mx-auto px-6 py-16 text-center"><div className="text-lg text-red-400 mb-2">{error}</div><Link to="/app/voucher-tools" className="text-sm text-muted">← Back to Voucher Tools</Link></div>;
  if (!data) return <Loading full/>;
  const pair = (key) => <div className="apv-a4" key={key}><ApvVoucher {...data}/><ApvVoucher {...data}/></div>;
  return (
    <div className="apv">
      <style>{APV_CSS}</style>
      <div className="apv-toolbar">
        <h1>Payment Voucher — {data.companyName}</h1>
        <p>Pick the Vendor Name from the vendors already added to {data.companyName} (Staff &amp; Vendor), or leave it blank to write it by hand. Everything else is filled by hand on the printed voucher, one payment per voucher.</p>
        <div style={{display:'flex',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
          <button className="apv-printbtn" onClick={() => window.print()}>Print / Save as PDF</button>
          <button className="apv-printbtn" style={{background:'transparent',color:'#e9e2cf',border:'1px solid #8a6a2a'}} onClick={() => downloadAnvidVoucher(data.companyName, data.vendors).catch(() => toast.error('Could not build the download'))} data-testid="apv-download">Download</button>
        </div>
        <p className="apv-note">Prints as one A4 sheet, landscape, 2 vouchers side by side (page 1 = front). A second identical page follows for the back of the same sheet — print double-sided (flip on the short edge), cut down the dashed centre line: 4 voucher uses per sheet.</p>
        <Link to="/app/voucher-tools" className="apv-back">← Back to Voucher Tools</Link>
      </div>
      <div className="apv-scroll"><div className="apv-pagelabel">Page 1 — Front</div>{pair('p1')}</div>
      <div className="apv-scroll"><div className="apv-pagelabel">Page 2 — Back (same sheet, other side)</div>{pair('p2')}</div>
    </div>
  );
}

function VoucherToolsPage() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [overheadCats, setOverheadCats] = useState([]);
  const [projectCats, setProjectCats] = useState([]);
  const [vendors, setVendors] = useState([]);
  // User-reported: Regenerate wasn't "wired properly" -- it called the
  // exact same load() as the very first mount, which set the one shared
  // `loading` flag this whole component gates its full return on. Every
  // click blanked the ENTIRE page (including the company tick-boxes the
  // admin had just set up) behind a bare full-screen spinner, instead of
  // refreshing the data in place. Split into two flags: initialLoading
  // (still gates the full-page Loading, only true before the first
  // successful load ever completes) and regenerating (a lightweight
  // button-only state for every load after that) -- same "Saving…"-style
  // disabled-button convention every save action in this app already
  // uses, rather than a second full-page blank.
  const [initialLoading, setInitialLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const hasLoadedRef = useRef(false);
  // User-requested: tick which companies to include in a given print run
  // (e.g. just one, instead of every company every time). Defaults to
  // every company selected the first time the list loads -- initialized
  // via a ref, not on every load(), so clicking Regenerate refreshes the
  // underlying data without silently re-selecting companies the admin
  // had deliberately unticked.
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => new Set());
  const didInitSelection = useRef(false);

  const load = async () => {
    const isRegenerate = hasLoadedRef.current;
    if (isRegenerate) setRegenerating(true); else setInitialLoading(true);
    try {
      const [c, p, oc, pc, v] = await Promise.all([
        api.get('/companies'),
        // User-reported: EW Buckets always showed 0 -- GET /projects
        // excludes is_extra_work rows by default (every ordinary project
        // picker relies on that), so this page's own EW Buckets column
        // was filtering an is_extra_work field that had already been
        // stripped out server-side before it ever arrived here.
        api.get('/projects', { params: { include_extra_work: true } }),
        api.get('/expense-categories', { params: { scope: 'overhead' } }),
        api.get('/expense-categories', { params: { scope: 'project' } }),
        api.get('/vendors'),
      ]);
      setCompanies(c.data);
      setProjects(p.data);
      setOverheadCats(oc.data);
      setProjectCats(pc.data);
      setVendors(v.data);
      if (!didInitSelection.current && c.data.length > 0) {
        didInitSelection.current = true;
        setSelectedCompanyIds(new Set(c.data.map(x => x.id)));
      }
      hasLoadedRef.current = true;
      if (isRegenerate) toast.success('Reference data refreshed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load reference data'); }
    finally { setInitialLoading(false); setRegenerating(false); }
  };
  useEffect(() => { load(); }, []);

  // Not just a hidden nav link -- a non-admin landing here directly (typed
  // URL, old bookmark) gets bounced, same as the catch-all route does for
  // an unknown path.
  if (!isAdmin(user)) return <Navigate to="/app/dashboard" replace/>;
  if (initialLoading) return <Loading/>;

  const toggleCompany = (id) => setSelectedCompanyIds(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const printCompanies = companies.filter(c => selectedCompanyIds.has(c.id));

  const catNamesFor = (cats, companyId) => cats.filter(c => (c.company_ids||[]).includes(companyId)).map(c=>c.name).sort((a,b)=>a.localeCompare(b));

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up">
      {/* User-reported: the first version reused the app's generic
          .print-area convention (built for plain data tables) -- on
          print it forced flat black-on-white with no visual hierarchy
          ("very simple"/plain), AND its 4-up grid stretched every column
          to the SAME height per row. OSK's 40 projects next to its own
          8-10 categories meant the 3 short columns each carried a huge
          blank gap stretched to match the Projects column -- the "wasted
          space" report. Fixed here with a self-contained style block
          (same paper/brass palette as the Payment Voucher, so the two
          read as a matched set) instead of the shared convention:
          align-items:start (a column ends where its own content ends,
          nothing stretched to fill), and real section-title colour bands
          so each list is easy to tell apart at a glance. Still layered
          on top of .print-area for the one thing that convention alone
          provides -- the app chrome (nav, sidebar, buttons) actually
          disappearing on print -- with higher-specificity selectors
          (.print-area .vref-x) so this page's own colours survive over
          that convention's blanket !important black/white reset. */}
      <style>{`
        .vref-sheet{
          background:var(--panel-alt, #f7f3e8); border:1px solid #c7bda3; border-radius:6px;
          padding:20px 24px; color:#201c14;
        }
        .print-area .vref-sheet{ background:#faf7f0 !important; color:#201c14 !important; }
        .vref-masthead{ display:flex; justify-content:space-between; align-items:baseline; border-bottom:2px solid #5f4a1e; padding-bottom:8px; margin-bottom:4px; }
        .print-area .vref-masthead{ border-bottom-color:#5f4a1e !important; }
        .vref-title{ font-family:'Source Serif 4', Georgia, serif; font-weight:700; font-size:20px; color:#201c14; }
        .print-area .vref-title{ color:#201c14 !important; }
        .vref-meta{ font-size:11px; color:#4a4436; font-family:monospace; white-space:nowrap; }
        .print-area .vref-meta{ color:#4a4436 !important; }
        .vref-lede{ font-size:12px; color:#4a4436; line-height:1.6; margin:10px 0 18px; }
        .print-area .vref-lede{ color:#4a4436 !important; }
        /* User-reported (with a real printed PDF as proof): a plain 5-up
           CSS grid does NOT paginate sanely once one column (Projects,
           40 items for Om Shankar Kumar) is wildly taller than the other
           four (EW/Overhead/Expense/Vendors, all under a dozen). Grid
           treats the whole row as one unit -- break-inside:avoid on the
           company block just shoved the ENTIRE thing onto a fresh page
           (wasting nearly all of the page it didn't fit on), and once it
           genuinely couldn't fit on ONE page either, the short columns
           came out misaligned/reordered relative to where Projects had
           continued to. Fixed by no longer putting Projects in that grid
           at all: it now gets its own full-width block using CSS multi-
           column text flow ("columns"), which -- unlike grid -- is
           specifically designed to paginate, filling each page's column
           height before continuing on the next, in the right order, with
           no page left mostly blank. The other four lists (always short)
           stay in a small, safe grid below it. */
        .vref-company{ border:1px solid #c7bda3; border-radius:6px; margin-bottom:16px; overflow:hidden; }
        .vref-company-head{
          background:#5f4a1e; color:#fff !important; padding:7px 14px; font-family:'Source Serif 4', Georgia, serif;
          font-weight:700; font-size:15px; print-color-adjust:exact; -webkit-print-color-adjust:exact;
          break-after:avoid-page; page-break-after:avoid;
        }
        .print-area .vref-company-head{ background:#5f4a1e !important; color:#fff !important; }
        .vref-projects{ padding:14px 14px 4px; }
        .vref-projects .vref-col-h{ break-after:avoid-page; page-break-after:avoid; }
        .vref-projects ul{ list-style:none; margin:0; padding:0; columns:4 190px; column-gap:16px; }
        .vref-cols{ display:grid; grid-template-columns:repeat(4,1fr); gap:14px; align-items:start; padding:10px 14px 14px; break-inside:avoid; page-break-inside:avoid; }
        .vref-col-h{ font-size:9.5px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:#8a6a2a; margin-bottom:5px; border-bottom:1px solid #e1d9c4; padding-bottom:4px; }
        .print-area .vref-col-h{ color:#8a6a2a !important; border-bottom-color:#e1d9c4 !important; }
        .vref-col ul{ list-style:none; margin:0; padding:0; }
        .vref-col li, .vref-projects li{ font-size:12px; padding:2.5px 0; border-bottom:1px solid #efe9d8; color:#201c14; break-inside:avoid; page-break-inside:avoid; }
        .print-area .vref-col li, .print-area .vref-projects li{ color:#201c14 !important; border-bottom-color:#efe9d8 !important; }
        .vref-col li .sub, .vref-projects li .sub{ color:#4a4436; font-size:10.5px; }
        .print-area .vref-col li .sub, .print-area .vref-projects li .sub{ color:#4a4436 !important; }
        .vref-empty{ color:#4a4436; font-style:italic; font-size:11.5px; }
        .print-area .vref-empty{ color:#4a4436 !important; }
        .vref-legend{ display:grid; grid-template-columns:repeat(2,1fr); gap:18px; padding:16px; }
        .vref-badge{
          display:inline-block; background:#f2ecdd; border:1px solid #c7bda3; color:#201c14 !important;
          padding:3px 11px; border-radius:12px; font-size:11.5px; margin:2px 4px 2px 0;
          print-color-adjust:exact; -webkit-print-color-adjust:exact;
        }
        .print-area .vref-badge{ background:#f2ecdd !important; border-color:#c7bda3 !important; color:#201c14 !important; }
        .vref-badge .dir{ color:#8a6a2a; font-size:9.5px; }
        .print-area .vref-badge .dir{ color:#8a6a2a !important; }
        @media (max-width:1100px){ .vref-cols{ grid-template-columns:repeat(3,1fr); } .vref-projects ul{ columns:3 190px; } }
        @media (max-width:700px){ .vref-cols{ grid-template-columns:repeat(2,1fr); } .vref-legend{ grid-template-columns:1fr; } .vref-projects ul{ columns:2 160px; } }
        .vref-company-picker{ display:flex; flex-wrap:wrap; gap:10px; }
        .vref-company-picker label{ display:flex; align-items:center; gap:6px; font-size:13px; cursor:pointer; }
        /* Printing: always at 100% (nothing to shrink) with every line at 11pt or larger, laid out on A4
           landscape. Content simply flows: a company that fits stays whole on one page, one that does not
           fit starts on a fresh page and continues, instead of being scaled down until it is unreadable. */
        @media print{
          @page{ size:A4 landscape; margin:7mm 10mm; }
          .vref-sheet{ border:none !important; border-radius:0; padding:0 !important; }
          .vref-masthead{ padding-bottom:2mm; margin-bottom:0; }
          .vref-title{ font-size:18pt; }
          .vref-meta{ font-size:11pt; }
          .vref-lede{ font-size:11pt; line-height:1.3; margin:1.5mm 0 3mm; }
          .vref-company{ break-inside:avoid; page-break-inside:avoid; overflow:visible; margin-bottom:3.5mm; }
          .vref-company-head{ font-size:14pt; padding:1.1mm 4mm; }
          /* every list of a company flows through ONE set of 4 columns, so short lists sit side by side
             and a long one simply continues in the next column - no tall, mostly empty columns */
          .vref-body{ columns:4; column-gap:6mm; padding:2.5mm 4mm 2mm; }
          .vref-cols{ display:block; padding:0; columns:auto; break-inside:auto; page-break-inside:auto; }
          .vref-projects{ display:block; padding:0; columns:auto; }
          .vref-col, .vref-projects{ break-inside:avoid; page-break-inside:avoid; }
          .vref-col.vref-split, .vref-projects.vref-split{ break-inside:auto; page-break-inside:auto; }
          .vref-company.vref-long{ break-inside:auto; page-break-inside:auto; }
          .vref-projects ul{ columns:auto; }
          .vref-col, .vref-projects{ margin-bottom:3mm; }
          .vref-col-h{ font-size:11pt; margin-bottom:1.5mm; padding-bottom:1mm; break-after:avoid-column; break-after:avoid; }
          .vref-col li, .vref-projects li{ font-size:11pt; line-height:1.2; padding:0.4mm 0; }
          .vref-col li .sub, .vref-projects li .sub{ font-size:11pt; }
          .vref-empty{ font-size:11pt; }
          .vref-legend{ padding:2mm 4mm 3mm; gap:8mm; }
          .vref-badge{ font-size:11pt; padding:0.5mm 3mm; }
          .vref-badge .dir{ font-size:11pt; }
        }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 no-print">
        <div>
          <div className="section-eyebrow mb-2">Master Admin / Company Admin</div>
          <h1 className="serif text-4xl">Voucher Tools</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={regenerating} className="btn-outline btn-small inline-flex items-center gap-1"><RefreshCw size={14}/>{regenerating ? 'Regenerating…' : 'Regenerate'}</button>
          <button onClick={()=>window.print()} className="btn-gold btn-small inline-flex items-center gap-1"><Printer size={14}/>Print / Save as PDF</button>
        </div>
      </div>

      <div className="card card-dark mb-6 no-print" style={{padding:'12px 20px'}}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="inline-flex items-center gap-2"><Receipt size={18} className="text-[color:var(--gold)] flex-shrink-0"/><b>Payment Vouchers</b></span>
          <span className="inline-flex items-center gap-2" title="One voucher, one payment — matches the app's own voucher-number rule">
            General (tick company)
            <a href="/payment-voucher.html" target="_blank" rel="noopener noreferrer" className="btn-blue btn-small inline-flex items-center gap-1" style={{padding:'5px 14px'}}><ExternalLink size={12}/>Open</a>
            <a href="/payment-voucher.html" download="Anvid-Buildcon-Payment-Voucher.html" className="btn-gold btn-small inline-flex items-center gap-1" style={{padding:'5px 14px'}}><Download size={12}/>Download</a>
          </span>
          <span className="inline-flex items-center gap-2" title="Anvid Buildcon only — Project / Company / Partner Transaction, Vendor Name picked from Staff & Vendor">
            Anvid Buildcon
            <a href="/voucher/anvid-buildcon" target="_blank" rel="noopener noreferrer" className="btn-blue btn-small inline-flex items-center gap-1" style={{padding:'5px 14px'}} data-testid="anvid-voucher-open"><ExternalLink size={12}/>Open</a>
            <button type="button" onClick={() => { const co = companies.find(c => /anvid/i.test(c.name || '')); if (!co) { toast.error('Anvid Buildcon is not among the companies this login can see.'); return; } downloadAnvidVoucher(co.name, vendors.filter(v => v.company_id === co.id && (v.status || 'active') === 'active').slice().sort((a, b) => a.name.localeCompare(b.name))).catch(() => toast.error('Could not build the download')); }} className="btn-gold btn-small inline-flex items-center gap-1" style={{padding:'5px 14px'}} data-testid="anvid-voucher-download"><Download size={12}/>Download</button>
          </span>
        </div>
      </div>

      {/* User-requested: tick which companies actually go into this print
          run, rather than always printing every company. */}
      <div className="card card-dark mb-6 no-print" style={{padding:'14px 20px'}}>
        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
          <div className="text-sm text-muted">Companies to include in this print</div>
          <div className="flex gap-2">
            <button onClick={()=>setSelectedCompanyIds(new Set(companies.map(c=>c.id)))} className="tab btn-small">Select all</button>
            <button onClick={()=>setSelectedCompanyIds(new Set())} className="tab btn-small">Select none</button>
          </div>
        </div>
        <div className="vref-company-picker">
          {companies.length === 0 && <span className="text-xs text-muted">No companies visible to this login.</span>}
          {companies.map(c => (
            <label key={c.id}>
              <input type="checkbox" checked={selectedCompanyIds.has(c.id)} onChange={()=>toggleCompany(c.id)}/>
              {c.name}
            </label>
          ))}
        </div>
      </div>

      <div className="print-area">
        <div className="vref-sheet">
          <div className="vref-masthead">
            <div className="vref-title">Voucher Filling Reference</div>
            <div className="vref-meta">Generated: {fmtGeneratedAt()}</div>
          </div>
          <p className="vref-lede">
            Exact Project, EW Bucket, Overhead Category, Expense Category and Vendor names as they exist right now — write them exactly as shown when filling a Payment Voucher by hand. "Projects" lists only projects not yet finally billed (excludes any project already Approved &amp; Locked / Settled / Closed); "Vendors" lists only active ones. Regenerate before a fresh print run — this is a live pull, not a saved file.
          </p>
          {printCompanies.length === 0 && <div className="vref-empty" style={{textAlign:'center', padding:'24px 0'}}>{companies.length === 0 ? 'No companies visible to this login.' : 'No companies ticked above — tick at least one to include it here.'}</div>}
          {printCompanies.map(c => {
            const compProjects = projects.filter(p => p.company_id === c.id && !p.is_extra_work && !LOCKED_PROJECT_STATUSES_REF.includes(p.status))
              .sort((a,b)=>a.project_name.localeCompare(b.project_name));
            const compEW = projects.filter(p => p.company_id === c.id && p.is_extra_work && !LOCKED_PROJECT_STATUSES_REF.includes(p.status))
              .sort((a,b)=>a.project_name.localeCompare(b.project_name));
            const compOverhead = catNamesFor(overheadCats, c.id);
            const compProject = catNamesFor(projectCats, c.id);
            const compVendors = vendors.filter(v => v.company_id === c.id && v.status === 'active')
              .map(v=>v.name).sort((a,b)=>a.localeCompare(b));
            // A company whose lists clearly cannot fit one landscape page at 11pt may start right away and run on
            // to the next page; a shorter one is kept whole (moved to the next page if it does not fit the rest of this one).
            const estRows = compProjects.length * 1.6 + compEW.length + compOverhead.length + compProject.length + compVendors.length + 8;
            const runsLong = estRows / 4 > 26;
            return (
              <div key={c.id} className={"vref-company" + (runsLong ? " vref-long" : "")}>
                <div className="vref-company-head">{c.name}</div>
                <div className="vref-body">
                <div className={"vref-projects" + (compProjects.length > 22 ? " vref-split" : "")}>
                  <div className="vref-col-h">Projects ({compProjects.length})</div>
                  <ul>
                    {compProjects.length===0 && <li className="vref-empty">None currently open</li>}
                    {compProjects.map(p=><li key={p.id}>{p.project_name}{p.tender_number ? <span className="sub"> — {p.tender_number}</span> : ''}</li>)}
                  </ul>
                </div>
                <div className="vref-cols">
                  <div className={"vref-col" + (compEW.length > 22 ? " vref-split" : "")}>
                    <div className="vref-col-h">EW Buckets ({compEW.length})</div>
                    <ul>
                      {compEW.length===0 && <li className="vref-empty">None currently open</li>}
                      {compEW.map(p=><li key={p.id}>{p.project_name}</li>)}
                    </ul>
                  </div>
                  <div className={"vref-col" + (compOverhead.length > 22 ? " vref-split" : "")}>
                    <div className="vref-col-h">Overhead Categories ({compOverhead.length})</div>
                    <ul>
                      {compOverhead.length===0 && <li className="vref-empty">None granted yet</li>}
                      {compOverhead.map(n=><li key={n}>{n}</li>)}
                    </ul>
                  </div>
                  <div className={"vref-col" + (compProject.length > 22 ? " vref-split" : "")}>
                    <div className="vref-col-h">Expense Categories ({compProject.length})</div>
                    <ul>
                      {compProject.length===0 && <li className="vref-empty">None granted yet</li>}
                      {compProject.map(n=><li key={n}>{n}</li>)}
                    </ul>
                  </div>
                  <div className={"vref-col" + (compVendors.length > 22 ? " vref-split" : "")}>
                    <div className="vref-col-h">Vendors ({compVendors.length})</div>
                    <ul>
                      {compVendors.length===0 && <li className="vref-empty">None recorded yet</li>}
                      {compVendors.map(n=><li key={n}>{n}</li>)}
                    </ul>
                  </div>
                </div>
                </div>
              </div>
            );
          })}
          <div className="vref-company">
            <div className="vref-company-head">Security Instrument &amp; Partner Transaction Types — all companies</div>
            <div className="vref-legend">
              <div>
                <div className="vref-col-h">Security Instrument</div>
                <div>{SECURITY_INSTRUMENT_TYPES_REF.map(t => <span key={t} className="vref-badge">{t}</span>)}</div>
              </div>
              <div>
                <div className="vref-col-h">Partner Transaction</div>
                <div>{PARTNER_TXN_TYPES_REF.map(t => <span key={t.label} className="vref-badge">{t.label} <span className="dir">({t.dir})</span></span>)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============= ADMIN =============

function Admin() {
  const { user, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [signature, setSignature] = useState(user?.signature_url || '');
  const [drafts, setDrafts] = useState({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ role: 'viewer' });
  const [tempPassword, setTempPassword] = useState(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  // Round 19 finding: no guard against a rapid double-click double-
  // submitting a row's save -- keyed by user id (not a single shared
  // flag) since each row saves independently.
  const [savingIds, setSavingIds] = useState(() => new Set());
  // Round 21 finding: resetUserPassword is categorically different from
  // every other unguarded per-row action in this app -- delete/toggle/
  // upsert-style actions all converge to the same end state on a
  // double-fire, but this one generates a FRESH random password
  // (secrets.token_urlsafe on the backend) every single call. Two
  // near-simultaneous clicks produce two different passwords, and
  // there's no email delivery in this app -- the admin manually relays
  // whichever one the UI happens to show, which may not be the one that
  // actually ended up live server-side. A separate Set (not reused from
  // savingIds) since Save and Reset Password are independent actions on
  // the same row.
  const [resettingIds, setResettingIds] = useState(() => new Set());

  const load = async () => {
    const [u, p, c] = await Promise.all([api.get('/profiles'), api.get('/partners'), api.get('/admin/companies')]);
    setUsers(u.data); setPartners(p.data); setCompanies(c.data);
    setDrafts(Object.fromEntries(u.data.map(x => [x.id, {
      role: x.role, status: x.status || 'active',
      company_ids: x.company_ids || [], all_companies_access: !!x.all_companies_access,
      partner_ids: x.partner_ids || [], all_partners_access: !!x.all_partners_access,
    }])));
  };
  useEffect(() => { load(); }, []);

  const setDraft = (id, field, val) => setDrafts(d => ({ ...d, [id]: { ...d[id], [field]: val } }));
  const toggleDraftIds = (id, field, itemId) => setDrafts(d => {
    const cur = d[id]?.[field] || [];
    const next = cur.includes(itemId) ? cur.filter(x => x !== itemId) : [...cur, itemId];
    return { ...d, [id]: { ...d[id], [field]: next } };
  });
  const toggleDraftCompany = (id, cid) => toggleDraftIds(id, 'company_ids', cid);
  const toggleDraftPartner = (id, pid) => toggleDraftIds(id, 'partner_ids', pid);
  const sameIds = (a, b) => a.length === b.length && a.every(x => b.includes(x));
  const isDirty = (u) => {
    const d = drafts[u.id]; if (!d) return false;
    return d.role !== u.role || d.status !== (u.status || 'active')
      || !sameIds(d.company_ids || [], u.company_ids || []) || d.all_companies_access !== !!u.all_companies_access
      || !sameIds(d.partner_ids || [], u.partner_ids || []) || d.all_partners_access !== !!u.all_partners_access;
  };
  const saveUser = async (u) => {
    if (savingIds.has(u.id)) return;
    setSavingIds(prev => new Set(prev).add(u.id));
    const d = drafts[u.id];
    // Whole-app final-check finding: /api/profiles now correctly narrows
    // company_ids/partner_ids to what THIS caller can see (the earlier
    // cross-company grant leak fix) -- but the backend treats an included
    // array as the complete, authoritative new grant list (documented:
    // omitted = untouched, [] = clear all). Always sending the loaded
    // (possibly narrowed) array back, even for an unrelated save like a
    // role bump, silently stripped a shared user's access to any company
    // the editing admin couldn't see themselves -- no error, no warning.
    // Only send a grant field when the admin actually touched that picker.
    const payload = { role: d.role, status: d.status };
    if (!sameIds(d.company_ids || [], u.company_ids || [])) payload.company_ids = d.company_ids || [];
    if (d.all_companies_access !== !!u.all_companies_access) payload.all_companies_access = d.all_companies_access;
    if (!sameIds(d.partner_ids || [], u.partner_ids || [])) payload.partner_ids = d.partner_ids || [];
    if (d.all_partners_access !== !!u.all_partners_access) payload.all_partners_access = d.all_partners_access;
    try {
      await api.patch(`/profiles/${u.id}`, payload);
      toast.success('User updated'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSavingIds(prev => { const next = new Set(prev); next.delete(u.id); return next; }); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.full_name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/profiles/${u.id}`);
      toast.success('User deleted'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Master Admin can do this for anyone; a Company Admin only sees users
  // within their own capacity in this table to begin with (list_profiles
  // is scoped the same way server-side), so the button doesn't need its
  // own extra scoping check here — same reasoning as the Company/Partner
  // pickers above.
  const resetUserPassword = async (u) => {
    if (resettingIds.has(u.id)) return;
    if (!window.confirm(`Reset the password for ${u.full_name} (${u.email})? Their current password stops working immediately.`)) return;
    setResettingIds(prev => new Set(prev).add(u.id));
    try {
      const r = await api.post(`/profiles/${u.id}/reset-password`);
      setTempPassword({ email: u.email, password: r.data.temporary_password, mode: 'reset' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setResettingIds(prev => { const next = new Set(prev); next.delete(u.id); return next; }); }
  };

  const uploadSignature = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 300000) { toast.error('Please upload a signature under 300KB'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setSignature(dataUrl);
      await api.patch('/profiles/me', { signature_url: dataUrl });
      await refresh();
      toast.success('Signature saved');
    };
    reader.readAsDataURL(file);
  };
  const clearSignature = async () => { if (!window.confirm('Remove your saved signature? You\'ll need to draw/upload it again to sign printed reports.')) return; await api.patch('/profiles/me', { signature_url: '' }); setSignature(''); await refresh(); toast.success('Signature removed'); };

  const sendInvite = async () => {
    try {
      const r = await api.post('/admin/users', inviteForm);
      setTempPassword({ email: inviteForm.email, password: r.data.temporary_password });
      setInviteOpen(false); setInviteForm({ role: 'viewer' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const visibleUsers = users
    .filter(u => !roleFilter || u.role === roleFilter)
    .filter(u => !statusFilter || (u.status||'active') === statusFilter)
    .filter(u => !q.trim() || `${u.full_name||''} ${u.email||''}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="section-eyebrow mb-2">Administration</div>
          <h1 className="serif text-4xl">User Management</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {users.length > 6 && <input className="input" style={{width:200}} placeholder="Search name or email…" value={q} onChange={e=>setQ(e.target.value)} data-testid="user-filter-search"/>}
          <select className="input" style={{width:150}} value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} data-testid="user-filter-role">
            <option value="">All Roles</option>
            {Object.entries(ROLE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
          </select>
          <select className="input" style={{width:130}} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} data-testid="user-filter-status">
            <option value="">All Statuses</option>
            {['pending','active','suspended'].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={()=>{setInviteForm({ role:'viewer', company_ids: user.role==='master_admin' ? [] : [user.company_id].filter(Boolean), partner_ids: [], all_partners_access: false }); setInviteOpen(true);}} className="btn-gold inline-flex items-center gap-1" data-testid="invite-user-btn"><Plus size={16}/>Invite User</button>
        </div>
      </div>

      {['admin','accountant','master_admin'].includes(user.role) && <div className="card card-dark mb-6">
        <div className="serif text-xl mb-2 text-[color:var(--gold)]">Your Approval Signature</div>
        <p className="text-muted text-sm mb-3">Upload a PNG/JPG of your signature. It will be stamped on approved settlement PDF exports.</p>
        {signature && <div className="mb-3 p-3 rounded border border-subtle inline-block" style={{background:'#fff'}}><img src={signature} alt="signature" style={{maxHeight:60}}/></div>}
        <div className="flex gap-2 items-center">
          <label className="btn-outline btn-small cursor-pointer inline-flex items-center gap-1"><PenLine size={14}/>Upload Signature<input type="file" accept="image/*" onChange={uploadSignature} className="hidden" data-testid="signature-upload"/></label>
          {signature && <button onClick={clearSignature} className="tab btn-small text-red-300" data-testid="signature-clear">Remove</button>}
        </div>
      </div>}

      <div className="card card-dark overflow-x-auto">
        <div className="text-xs text-muted mb-3">New sign-ups land here as <span className="chip">pending</span> and can't access anything until approved (status → active).</div>
        <table className="tbl"><thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Company</th><th>Linked Partner</th><th>Status</th><th></th><th></th><th></th></tr></thead>
          <tbody>
          {visibleUsers.length===0 && <tr><td colSpan={9} className="text-center text-muted py-8">{users.length===0 ? 'No users yet.' : 'No users match these filters.'}</td></tr>}
          {visibleUsers.map(u => {
            const d = drafts[u.id] || {};
            const dirty = isDirty(u);
            // A Company Admin can't manage a Master Admin account at all —
            // the backend rejects it (_assert_can_manage_profile), and the
            // controls stay read-only here so that rejection is never the
            // first the caller hears of it.
            const targetIsMasterAdmin = u.role === 'master_admin' && user.role !== 'master_admin';
            return (
            <tr key={u.id} data-testid={`user-${u.id}`}>
              <td>{u.full_name}</td><td className="text-xs">{u.email}</td>
              <td><select className="input" style={{width:150}} value={d.role||'viewer'} onChange={e=>setDraft(u.id,'role',e.target.value)} disabled={targetIsMasterAdmin} data-testid={`role-${u.id}`}>
                {(user.role==='master_admin' ? ['master_admin','admin','accountant','partner','viewer','data_entry'] : ['admin','accountant','partner','viewer','data_entry']).map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select></td>
              <td data-testid={`company-${u.id}`}>
                <GrantPicker
                  items={companies}
                  selectedIds={d.company_ids || []}
                  allAccess={!!d.all_companies_access}
                  onToggle={cid => toggleDraftCompany(u.id, cid)}
                  onSetAllAccess={val => setDraft(u.id, 'all_companies_access', val)}
                  canGrantAll={user.role === 'master_admin'}
                  allLabel="All Companies"
                  disabled={targetIsMasterAdmin}
                />
                {/* companies list is already scoped to the caller's own access via GET /admin/companies,
                    and non-master admins get a 403 server-side if they somehow submit outside it — no
                    separate disabled state needed here. */}
              </td>
              <td data-testid={`partner-${u.id}`}>
                <GrantPicker
                  items={partners.map(p => ({ id: p.id, name: partnerOptionLabel(p) }))}
                  selectedIds={d.partner_ids || []}
                  allAccess={!!d.all_partners_access}
                  onToggle={pid => toggleDraftPartner(u.id, pid)}
                  onSetAllAccess={val => setDraft(u.id, 'all_partners_access', val)}
                  canGrantAll={user.role === 'master_admin'}
                  allLabel="All Partners"
                  noneWidth={170}
                  disabled={targetIsMasterAdmin}
                />
              </td>
              <td><select className="input" style={{width:120}} value={d.status||'active'} onChange={e=>setDraft(u.id,'status',e.target.value)} disabled={u.id === user.id || targetIsMasterAdmin} data-testid={`status-${u.id}`}>
                {/* Full-app audit finding: approving a brand-new pending
                    sign-up (status pending -> active) is master_admin-only
                    server-side (update_profile) -- this control stayed
                    fully enabled for a plain Company Admin looking at a
                    pending row, who'd get a confusing 403 on Save with no
                    indication which field caused it. Disabling just the
                    'active' option here (not the whole control -- a
                    Company Admin can still e.g. leave it pending or, once
                    genuinely approved by someone else, suspend it later)
                    matches the server's own exact restriction. */}
                {['pending','active','suspended'].map(s=><option key={s} value={s} disabled={s==='active' && u.status==='pending' && user.role!=='master_admin'}>{s}</option>)}
              </select></td>
              <td>{dirty && !targetIsMasterAdmin && <button onClick={()=>saveUser(u)} disabled={savingIds.has(u.id)} className="btn-gold btn-small" data-testid={`save-user-${u.id}`}>{savingIds.has(u.id) ? 'Saving…' : 'Save'}</button>}</td>
              <td>{!targetIsMasterAdmin && <button onClick={()=>resetUserPassword(u)} disabled={resettingIds.has(u.id)} className="tab btn-small" title="Reset password" data-testid={`reset-password-${u.id}`}><Lock size={12}/></button>}</td>
              <td>{user.role === 'master_admin' && u.id !== user.id &&
                <button aria-label="Delete" onClick={()=>deleteUser(u)} className="tab btn-small text-red-300" data-testid={`delete-user-${u.id}`}><Trash2 size={12}/></button>}</td>
            </tr>
          );})}</tbody>
        </table>
      </div>

      <Modal open={inviteOpen} onClose={()=>setInviteOpen(false)} title="Invite User">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full Name *"><input className="input" value={inviteForm.full_name||''} onChange={e=>setInviteForm({...inviteForm, full_name:e.target.value})} data-testid="invite-name"/></Field>
          <Field label="Email *"><input className="input" type="email" value={inviteForm.email||''} onChange={e=>setInviteForm({...inviteForm, email:e.target.value})} data-testid="invite-email"/></Field>
          <Field label="Role"><select className="input" value={inviteForm.role||'viewer'} onChange={e=>setInviteForm({...inviteForm, role:e.target.value})} data-testid="invite-role">
            {(user.role==='master_admin' ? ['master_admin','admin','accountant','partner','viewer','data_entry'] : ['admin','accountant','partner','viewer','data_entry']).map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select></Field>
          <Field label="Company"><div data-testid="invite-company">
            <GrantPicker
              items={companies}
              selectedIds={inviteForm.company_ids || []}
              allAccess={!!inviteForm.all_companies_access}
              onToggle={cid => setInviteForm(f => {
                const cur = f.company_ids || [];
                return { ...f, company_ids: cur.includes(cid) ? cur.filter(x => x !== cid) : [...cur, cid] };
              })}
              onSetAllAccess={val => setInviteForm(f => ({ ...f, all_companies_access: val }))}
              canGrantAll={user.role === 'master_admin'}
              allLabel="All Companies"
            />
          </div></Field>
          <Field label="Linked Partner"><div data-testid="invite-partner">
            <GrantPicker
              items={partners.map(p => ({ id: p.id, name: partnerOptionLabel(p) }))}
              selectedIds={inviteForm.partner_ids || []}
              allAccess={!!inviteForm.all_partners_access}
              onToggle={pid => setInviteForm(f => {
                const cur = f.partner_ids || [];
                return { ...f, partner_ids: cur.includes(pid) ? cur.filter(x => x !== pid) : [...cur, pid] };
              })}
              onSetAllAccess={val => setInviteForm(f => ({ ...f, all_partners_access: val }))}
              canGrantAll={user.role === 'master_admin'}
              allLabel="All Partners"
            />
          </div></Field>
        </div>
        <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setInviteOpen(false)} className="btn-outline btn-small">Cancel</button>
          <button onClick={sendInvite} className="btn-gold btn-small" disabled={!inviteForm.full_name||!inviteForm.email||(!inviteForm.all_companies_access && !(inviteForm.company_ids||[]).length)} data-testid="invite-save">Create User</button></div>
      </Modal>

      <Modal open={!!tempPassword} onClose={()=>setTempPassword(null)} title={tempPassword?.mode === 'reset' ? 'Password Reset' : 'User Created'}>
        {tempPassword && <div>
          <p className="text-sm text-muted mb-3">
            {tempPassword.mode === 'reset'
              ? <>Share this new password with <strong>{tempPassword.email}</strong> — there's no automatic email, so relay this yourself. Their old password no longer works; they can change this one themselves afterwards from the sign-in page's "Forgot your password?" link.</>
              : <>Share these sign-in details with <strong>{tempPassword.email}</strong> — there's no automatic email, so relay this yourself. They should change the password after first sign-in.</>}
          </p>
          <div className="p-3 rounded surface-2 mono text-sm">
            <div>Email: {tempPassword.email}</div>
            <div>{tempPassword.mode === 'reset' ? 'New' : 'Temporary'} password: <span className="text-[color:var(--gold)]">{tempPassword.password}</span></div>
          </div>
        </div>}
        <div className="mt-6 flex justify-end"><button onClick={()=>setTempPassword(null)} className="btn-gold btn-small">Done</button></div>
      </Modal>
    </div>
  );
}

// ============= APP SHELL =============
// Real-usage finding: long pages/panels (Audit Trail, Data Audit Log,
// import lists, ...) relied purely on the browser's own scrollbar for
// "there's more below" -- on macOS in particular, that scrollbar is
// invisible at rest by default (only appears while actively scrolling),
// so there was no visual cue at all that content extended past the
// screen. Mounted once here at the app shell (not per-page) so every
// page gets it automatically -- appears once you've scrolled down a bit,
// jumps back to the top on click.
function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="no-print"
      aria-label="Back to top"
      title="Back to top"
      data-testid="back-to-top"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 55,
        width: 44, height: 44, borderRadius: '50%',
        background: 'var(--gold)', color: '#1a1408', border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)', cursor: 'pointer',
      }}
    ><ChevronUp size={20}/></button>
  );
}

// User feedback: on a narrow screen, a wide table (many columns) is only
// reachable via the browser's own horizontal scrollbar/trackpad swipe --
// same invisible-affordance problem BackToTop above already solved for
// vertical scrolling, just on the other axis. Every wide-table wrapper in
// this app already shares one class, `.overflow-x-auto` (~40 sites, no
// single shared component covers them all the way CrudPanel does for
// sorting), so rather than touching every site individually this mounts
// ONCE here and finds them all itself: a MutationObserver + resize
// listener rescans the page, and any `.overflow-x-auto` element that's
// actually overflowing gets a small "Scroll for more" control bar inserted
// as a SIBLING immediately before it (never nested inside the scrolling
// content itself). Tried pinning the buttons to each table's own left/
// right edge via `position:sticky` first -- doesn't hold up cleanly for a
// "one at each far edge" pair without per-site layout tuning (sticky only
// clamps an element to an edge it would otherwise scroll past, it can't
// pull one toward the FAR edge from a natural resting point elsewhere) --
// so both controls live together above the table instead, verified
// working via a live mockup (real click, both boundary states correct).
// Deliberately ADDS plain DOM nodes rather than rendering them through
// React (no existing component here would need touching, and it never
// touches an existing element's OWN children) -- self-heals if a parent's
// own re-render ever displaces one, since that re-render is itself a DOM
// mutation the same observer reacts to.
function HScrollButtons() {
  useEffect(() => {
    const ICON_LEFT = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    const ICON_RIGHT = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    const BTN_STYLE = 'width:24px;height:24px;border-radius:50%;background:var(--gold);color:#1a1408;border:none;display:none;align-items:center;justify-content:center;cursor:pointer;opacity:0.9;flex:none;';

    const setup = (el) => {
      if (el.dataset.hscrollReady || !el.parentNode) return;
      el.dataset.hscrollReady = '1';
      try {
        const bar = document.createElement('div');
        bar.className = 'no-print';
        // Bug found via real usage: `bar` is inserted as a raw DOM sibling
        // BEFORE `el`, entirely outside React's own vdom for this subtree
        // -- so when a panel swaps its loading state (e.g. ImportHistory-
        // Panel's Refresh: <Loading/> in, then a BRAND NEW `.overflow-x-
        // auto` div back in), React unmounts the OLD `el` it knows about
        // but has no idea `bar` exists at all, so `bar` is simply orphaned
        // in the DOM rather than removed with it. The MutationObserver
        // below then finds the NEW `.overflow-x-auto` (fresh element, no
        // `hscrollReady` flag) and happily creates ANOTHER bar for it --
        // one more orphaned bar stacking up per refresh, forever, on any
        // page whose wide-table wrapper gets unmounted/remounted this way.
        // Tagging the pairing both directions lets cleanupOrphans() (below)
        // sweep away a bar once its target element is no longer connected.
        bar.dataset.hscrollBar = '1';
        bar._hscrollTarget = el;
        // User feedback: a control that only lived at the very top of the
        // table (a plain in-flow sibling) scrolled away with the page the
        // moment you scrolled down into a tall table's later rows -- it
        // needs to stay visible for as long as the table itself is still
        // on screen, not just at the instant you arrive at it. `position:
        // sticky` on the PAGE's own vertical scroll (a different axis/use
        // than the horizontal-within-the-table attempt that didn't pan
        // out, see the comment above this function) does exactly that,
        // pinned just below the already-sticky top nav (`<header
        // className="sticky top-0">`) -- it naturally un-sticks and
        // scrolls away once you've scrolled past the table's own
        // containing section, exactly matching "visible the whole time
        // the table's in view, not pinned forever".
        bar.style.cssText = 'display:none;align-items:center;justify-content:flex-end;gap:6px;margin-bottom:4px;position:sticky;z-index:35;';
        const hint = document.createElement('span');
        hint.textContent = 'Scroll for more →';
        hint.style.cssText = 'font-size:11px;color:var(--gold);opacity:0.8;margin-right:2px;';
        const leftBtn = document.createElement('button');
        leftBtn.type = 'button';
        leftBtn.setAttribute('aria-label', 'Scroll left');
        leftBtn.style.cssText = BTN_STYLE;
        leftBtn.innerHTML = ICON_LEFT;
        const rightBtn = document.createElement('button');
        rightBtn.type = 'button';
        rightBtn.setAttribute('aria-label', 'Scroll right');
        rightBtn.style.cssText = BTN_STYLE;
        rightBtn.innerHTML = ICON_RIGHT;
        leftBtn.onclick = () => el.scrollBy({ left: -260, behavior: 'smooth' });
        rightBtn.onclick = () => el.scrollBy({ left: 260, behavior: 'smooth' });
        bar.appendChild(hint);
        bar.appendChild(leftBtn);
        bar.appendChild(rightBtn);
        el.parentNode.insertBefore(bar, el);
        const update = () => {
          const overflowing = el.scrollWidth > el.clientWidth + 2;
          bar.style.width = el.getBoundingClientRect().width + 'px';
          const navH = document.querySelector('header')?.getBoundingClientRect().height || 0;
          bar.style.top = (navH + 8) + 'px';
          bar.style.display = overflowing ? 'flex' : 'none';
          leftBtn.style.display = overflowing && el.scrollLeft > 4 ? 'flex' : 'none';
          rightBtn.style.display = overflowing && el.scrollLeft < el.scrollWidth - el.clientWidth - 4 ? 'flex' : 'none';
          hint.style.display = overflowing && el.scrollLeft < 4 ? 'inline' : 'none';
        };
        el.addEventListener('scroll', update, { passive: true });
        el._hscrollUpdate = update;
        update();
      } catch { /* best-effort visual enhancement -- never break the page over this */ }
    };

    const cleanupOrphans = () => {
      document.querySelectorAll('[data-hscroll-bar="1"]').forEach((bar) => {
        if (!bar._hscrollTarget || !bar._hscrollTarget.isConnected) bar.remove();
      });
    };

    const scanAll = () => {
      cleanupOrphans();
      document.querySelectorAll('.overflow-x-auto').forEach((el) => {
        setup(el);
        if (el._hscrollUpdate) el._hscrollUpdate();
      });
    };

    scanAll();
    const mo = new MutationObserver(() => scanAll());
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', scanAll);
    const id = setInterval(scanAll, 1500); // catches width changes with no DOM mutation (e.g. column content settling)
    return () => { mo.disconnect(); window.removeEventListener('resize', scanAll); clearInterval(id); };
  }, []);
  return null;
}

// ==================== GST INPUT CREDIT (purchase bills, ITC tracking, GSTR-2B) ====================
const GST_STATUS_LABELS = { pending: 'Pending', in_2b: 'In 2B — ready to claim', claimed: 'Claimed', not_available: 'Not available', ineligible: 'Ineligible', reversed: 'Reversed' };
const GST_STATUS_STYLE = {
  pending: { color: '#7dd3fc', borderColor: 'rgba(125,211,252,0.35)' },
  in_2b: { color: '#86efac', borderColor: 'rgba(134,239,172,0.4)' },
  claimed: { color: '#c9a84c', borderColor: 'rgba(201,168,76,0.5)' },
  not_available: { color: '#fb923c', borderColor: 'rgba(251,146,60,0.4)' },
  ineligible: { color: '#a8a29e', borderColor: 'rgba(168,162,158,0.4)' },
  reversed: { color: '#f87171', borderColor: 'rgba(248,113,113,0.4)' },
};
const GST_KIND_LABELS = { goods: 'Goods', services: 'Services', capital_goods: 'Capital goods' };
const gstMonthLabel = (m) => { if (!m) return '—'; const [y, mm] = m.split('-'); return new Date(Date.UTC(Number(y), Number(mm) - 1, 1)).toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' }); };
const gstCurrentFy = () => fyFromDateStr(todayLocalISO());
const gstFyOptions = () => { const y = Number(gstCurrentFy().slice(3, 7)); return [y + 1, y, y - 1, y - 2, y - 3, y - 4].map(a => `FY ${a}-${String(a + 1).slice(-2)}`); };
const gstSplit = (taxable, rate, interstate) => {
  const tax = Math.round((Number(taxable) || 0) * (Number(rate) || 0)) / 100;
  if (interstate) return { cgst: 0, sgst: 0, igst: tax };
  const half = Math.round(tax * 50) / 100;
  return { cgst: half, sgst: Math.round((tax - half) * 100) / 100, igst: 0 };
};
// Print / PDF for the GST pages: one shared PDF writer (same jsPDF + autoTable look as the other exports in the app).
// Each section is { heading, head: [...], body: [[...]], foot: [...], columnStyles: { idx: { halign: 'right' } } }.
async function gstExportPdf({ title, company, filters, sections, filename, landscape = true }) {
  const { jsPDF, autoTable } = await loadPdf();
  const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFontSize(15); doc.text(title, 10, 14);
  doc.setFontSize(9);
  doc.text(`${company}   ·   Generated: ${fmtGeneratedAt()}`, 10, 20);
  let y = 25;
  if (filters) { const lines = doc.splitTextToSize(`Filters: ${filters}`, pageW - 20); doc.text(lines, 10, y); y += lines.length * 4 + 1; }
  sections.forEach(sec => {
    if (sec.heading) { if (y > doc.internal.pageSize.getHeight() - 30) { doc.addPage(); y = 14; } doc.setFontSize(11); doc.text(sec.heading, 10, y + 4); y += 6; }
    const body = sec.body.length ? sec.body : [[{ content: 'None', colSpan: sec.head.length, styles: { halign: 'center', textColor: [120, 120, 120] } }]];
    autoTable(doc, {
      startY: y, head: [sec.head], body, foot: sec.foot && sec.body.length ? [sec.foot] : undefined, showFoot: 'lastPage', margin: { left: 10, right: 10 },
      styles: { fontSize: 8, cellPadding: 1.8 }, headStyles: { fillColor: [201, 168, 76], textColor: [20, 20, 20] }, footStyles: { fillColor: [240, 232, 205], textColor: [20, 20, 20], fontStyle: 'bold' },
      columnStyles: sec.columnStyles || {},
    });
    y = doc.lastAutoTable.finalY + 8;
  });
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.text(`Page ${i} of ${pages}`, pageW - 10, doc.internal.pageSize.getHeight() - 6, { align: 'right' }); }
  doc.save(filename);
}
const gstSeg = (v) => String(v || '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
const GstPrintButtons = ({ onPrint, onPdf, busy, testId }) => (
  <>
    <button className="btn-outline btn-small inline-flex items-center gap-1" onClick={onPrint} data-testid={`${testId}-print`}><Printer size={14}/>Print</button>
    <button className="btn-outline btn-small inline-flex items-center gap-1" onClick={onPdf} disabled={busy} data-testid={`${testId}-pdf`}><Download size={14}/>{busy ? 'Preparing…' : 'Download PDF'}</button>
  </>
);
function GstStatusChip({ bill }) {
  return <span className="badge" style={GST_STATUS_STYLE[bill.itc_status] || {}} title={bill.itc_note || ''}>{GST_STATUS_LABELS[bill.itc_status] || bill.itc_status}{bill.itc_status === 'claimed' && bill.claim_period ? ` · ${gstMonthLabel(bill.claim_period)}` : ''}{bill.itc_status === 'reversed' && bill.reversal_period ? ` · ${gstMonthLabel(bill.reversal_period)}` : ''}</span>;
}

function GstBillModal({ open, onClose, onSaved, initial, vendors, projects }) {
  const { user } = useAuth();
  const { companies, needsPicker } = useMyCompanies();
  const [form, setForm] = useState({});
  const [rate, setRate] = useState('18');
  const [interstate, setInterstate] = useState(false);
  const [auto, setAuto] = useState(true);   // GST amounts follow taxable value x rate until edited by hand
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    const f = initial || {};
    setForm({ purchase_kind: 'goods', invoice_date: new Date().toISOString().slice(0, 10), ...f });
    setRate('18'); setInterstate(!!(f.igst > 0)); setAuto(!f.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const recalc = (taxable, r, inter) => { const sp = gstSplit(taxable, r, inter); return { cgst: sp.cgst, sgst: sp.sgst, igst: sp.igst }; };
  const set = (patch) => setForm(f => {
    const next = { ...f, ...patch };
    if (auto && ('taxable_value' in patch)) Object.assign(next, recalc(next.taxable_value, rate, interstate));
    return next;
  });
  const taxSum = ['cgst', 'sgst', 'igst', 'cess'].reduce((a, k) => a + (Number(form[k]) || 0), 0);
  const total = Math.round(((Number(form.taxable_value) || 0) + taxSum) * 100) / 100;
  const companyId = form.company_id || (companies.length === 1 ? companies[0].id : '');
  const vendorList = (vendors || []).filter(v => !companyId || v.company_id === companyId);
  const projectList = (projects || []).filter(p => !companyId || p.company_id === companyId);
  const gstinBad = form.seller_gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(String(form.seller_gstin).toUpperCase().replace(/\s/g, ''));
  const save = async () => {
    if (saving) return;
    if (!(form.seller_name || '').trim()) { toast.error('Seller name is required.'); return; }
    if (gstinBad) { toast.error('Seller GSTIN is not valid — 15 characters like 10ABCDE1234F1Z5.'); return; }
    if (!(form.invoice_number || '').trim()) { toast.error('Invoice number is required.'); return; }
    if (!form.invoice_date) { toast.error('Invoice date is required.'); return; }
    if (!(Number(form.taxable_value) >= 0) || form.taxable_value === '' || form.taxable_value === undefined) { toast.error('Taxable value is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        company_id: form.company_id || null, project_id: form.project_id || null, vendor_id: form.vendor_id || null,
        seller_name: form.seller_name, seller_gstin: (form.seller_gstin || '').toUpperCase().replace(/\s/g, '') || null, invoice_number: form.invoice_number,
        invoice_date: form.invoice_date, description: form.description || null, purchase_kind: form.purchase_kind || 'goods',
        taxable_value: Number(form.taxable_value) || 0, cgst: Number(form.cgst) || 0, sgst: Number(form.sgst) || 0, igst: Number(form.igst) || 0, cess: Number(form.cess) || 0,
        invoice_total: form.invoice_total_manual ? Number(form.invoice_total) : total,
      };
      if (form.id) await api.patch(`/gst/purchase-bills/${form.id}`, payload); else await api.post('/gst/purchase-bills', payload);
      toast.success(form.id ? 'Saved' : 'Purchase bill recorded');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={form.id ? 'Edit purchase bill' : 'Add purchase bill (GST paid)'} maxW={760}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gst-bill-modal">
        <CompanyField form={form} setForm={setForm} companies={companies} needsPicker={needsPicker && !form.id} clear={['project_id', 'vendor_id']}/>
        <Field label="Seller (supplier) *">
          <input className="input" list="gst-vendor-list" maxLength={150} value={form.seller_name || ''} onChange={e => { const v = vendorList.find(x => x.name === e.target.value); set({ seller_name: e.target.value, vendor_id: v ? v.id : null }); }} data-testid="gst-seller" placeholder="Type or pick a vendor"/>
          <datalist id="gst-vendor-list">{vendorList.map(v => <option key={v.id} value={v.name}/>)}</datalist>
        </Field>
        <Field label="Seller GSTIN">
          <input className="input mono" maxLength={15} value={form.seller_gstin || ''} onChange={e => set({ seller_gstin: e.target.value.toUpperCase() })} data-testid="gst-gstin" placeholder="10ABCDE1234F1Z5"/>
          {gstinBad && <div className="text-xs mt-1 text-red-400">Not a valid GSTIN (15 characters).</div>}
          {!form.seller_gstin && <div className="text-xs mt-1 text-muted">Needed to claim credit and to match the GSTR-2B.</div>}
        </Field>
        <Field label="Invoice number *"><input className="input" maxLength={60} value={form.invoice_number || ''} onChange={e => set({ invoice_number: e.target.value })} data-testid="gst-invoice-no"/></Field>
        <Field label="Invoice date *"><input type="date" className="input" max={new Date().toISOString().slice(0, 10)} value={form.invoice_date || ''} onChange={e => set({ invoice_date: e.target.value })}/></Field>
        <Field label="Purchase of"><select className="input" value={form.purchase_kind || 'goods'} onChange={e => set({ purchase_kind: e.target.value })}>{Object.entries(GST_KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <Field label="Taxable value (before GST) *"><input className="input mono" type="number" min="0" value={form.taxable_value ?? ''} onChange={e => set({ taxable_value: e.target.value })} data-testid="gst-taxable"/></Field>
        <div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="GST rate %"><select className="input" value={rate} onChange={e => { setRate(e.target.value); if (auto) setForm(f => ({ ...f, ...recalc(f.taxable_value, e.target.value, interstate) })); }}>{['0', '0.25', '3', '5', '12', '18', '28'].map(r => <option key={r} value={r}>{r}%</option>)}</select></Field>
            <Field label="Supply"><select className="input" value={interstate ? 'inter' : 'intra'} onChange={e => { const inter = e.target.value === 'inter'; setInterstate(inter); if (auto) setForm(f => ({ ...f, ...recalc(f.taxable_value, rate, inter) })); }}><option value="intra">Same state (CGST + SGST)</option><option value="inter">Other state (IGST)</option></select></Field>
          </div>
        </div>
        <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[['cgst', 'CGST'], ['sgst', 'SGST'], ['igst', 'IGST'], ['cess', 'Cess']].map(([k, l]) => (
            <Field key={k} label={l}><input className="input mono" type="number" min="0" value={form[k] ?? ''} onChange={e => { setAuto(false); setForm(f => ({ ...f, [k]: e.target.value })); }} data-testid={`gst-${k}`}/></Field>
          ))}
        </div>
        <div className="md:col-span-2 text-sm">Invoice total <b className="mono text-[color:var(--gold)]">{fmtINR(total)}</b> <span className="text-muted">= taxable {fmtINR(Number(form.taxable_value) || 0)} + GST {fmtINR(taxSum)}. The GST amounts follow the rate above until you edit them by hand — always match the supplier's invoice.</span></div>
        <Field label="Project (optional)"><select className="input" value={form.project_id || ''} onChange={e => set({ project_id: e.target.value || null })}><option value="">— not project-specific —</option>{projectList.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}</select></Field>
        <Field label="What was bought"><input className="input" maxLength={500} value={form.description || ''} onChange={e => set({ description: e.target.value })}/></Field>
      </div>
      <div className="mt-6 flex justify-end gap-2"><button onClick={onClose} className="btn-outline btn-small">Cancel</button><button onClick={save} disabled={saving} className="btn-gold btn-small" data-testid="gst-bill-save">{saving ? 'Saving…' : 'Save'}</button></div>
    </Modal>
  );
}

function GstMarkModal({ open, onClose, onDone, bills }) {
  const [action, setAction] = useState('claimed');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [note, setNote] = useState('');
  const [amounts, setAmounts] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setAction('claimed'); setMonth(new Date().toISOString().slice(0, 7)); setNote(''); setAmounts({}); } }, [open]);
  const total = bills.reduce((a, b) => a + (action === 'claimed' ? (amounts[b.id] !== undefined && amounts[b.id] !== '' ? Number(amounts[b.id]) : b.total_gst) : b.total_gst), 0);
  const noGstin = bills.filter(b => !b.seller_gstin);
  const submit = async () => {
    if (saving) return; setSaving(true);
    try {
      const body = { ids: bills.map(b => b.id), status: action, note: note || undefined };
      if (action === 'claimed') { body.claim_period = month; const am = {}; bills.forEach(b => { if (amounts[b.id] !== undefined && amounts[b.id] !== '') am[b.id] = Number(amounts[b.id]); }); if (Object.keys(am).length) body.amounts = am; }
      if (action === 'reversed') body.reversal_period = month;
      const r = (await api.post('/gst/purchase-bills/mark-itc', body)).data;
      toast.success(`${r.updated} bill${r.updated === 1 ? '' : 's'} updated`); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const OPTIONS = [['claimed', 'Claimed in a GST return (GSTR-3B)'], ['not_available', 'Not available — seller did not file'], ['ineligible', 'Ineligible (blocked credit)'], ['reversed', 'Reverse a claimed credit'], ['in_2b', 'Appears in GSTR-2B'], ['pending', 'Back to pending']];
  return (
    <Modal open={open} onClose={onClose} title={`Mark credit — ${bills.length} bill${bills.length === 1 ? '' : 's'}`} maxW={640}>
      <div data-testid="gst-mark-modal">
        <div className="grid gap-2 mb-4">
          {OPTIONS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="gst-action" checked={action === k} onChange={() => setAction(k)}/>{l}</label>)}
        </div>
        {(action === 'claimed' || action === 'reversed') && <Field label={action === 'claimed' ? 'GST return month the credit is claimed in' : 'GST return month it is reversed in'}><input type="month" className="input" value={month} onChange={e => setMonth(e.target.value)} data-testid="gst-claim-month"/></Field>}
        {action === 'claimed' && noGstin.length > 0 && <div className="text-xs text-red-400 mb-3">{noGstin.length} bill(s) have no seller GSTIN — add it first (Edit) or untick them.</div>}
        {action === 'claimed' && bills.length <= 8 && (
          <div className="mb-3 text-xs"><div className="text-muted mb-1">Amount claimed (leave blank for the full GST on the bill)</div>
            {bills.map(b => <div key={b.id} className="flex items-center justify-between gap-3 py-1"><span>{b.seller_name} · {b.invoice_number} <span className="text-muted">(GST {fmtINR(b.total_gst)})</span></span><input className="input mono" style={{ width: 120 }} type="number" min="0" placeholder={String(b.total_gst)} value={amounts[b.id] ?? ''} onChange={e => setAmounts({ ...amounts, [b.id]: e.target.value })}/></div>)}
          </div>
        )}
        <Field label="Note (optional)"><input className="input" maxLength={500} value={note} onChange={e => setNote(e.target.value)} placeholder={action === 'not_available' ? 'e.g. supplier has not filed GSTR-1 for the month' : ''}/></Field>
        <div className="mt-4 flex items-center justify-between"><div className="text-sm">GST on selected: <b className="mono">{fmtINR(total)}</b></div><div className="flex gap-2"><button className="btn-outline btn-small" onClick={onClose}>Cancel</button><button className="btn-gold btn-small" onClick={submit} disabled={saving || (action === 'claimed' && noGstin.length > 0)} data-testid="gst-mark-save">{saving ? 'Saving…' : 'Apply'}</button></div></div>
      </div>
    </Modal>
  );
}

function GstSummaryTab({ summary, companyLabel }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  if (!summary) return <Loading/>;
  const y = summary.year;
  const printIt = () => setTimeout(() => printLandscape(), 50);
  const pdf = async () => {
    if (pdfBusy) return; setPdfBusy(true);
    try {
      const m0 = (n) => (n ? fmtINRPdf(n) : '—');
      const right = (from, to) => Object.fromEntries(Array.from({ length: to - from + 1 }, (_, i) => [from + i, { halign: 'right' }]));
      await gstExportPdf({
        title: `GST Input Credit — ${summary.financial_year}`, company: companyLabel, filename: `gst-input-credit-summary-${gstSeg(summary.financial_year)}-${todayLocalISO()}.pdf`,
        sections: [
          { heading: 'By GST return month', head: ['GST return month', 'Bills', 'Purchases', 'GST paid', 'CGST', 'SGST', 'IGST', 'Cess', 'Credit claimed', 'Reversed', 'Net credit'],
            body: summary.months.map(m => [gstMonthLabel(m.period), m.bills || '—', m0(m.purchases), m0(m.gst_paid), m0(m.claimed.cgst), m0(m.claimed.sgst), m0(m.claimed.igst), m0(m.claimed.cess), m0(m.claimed.total), m0(m.reversed.total), m0(m.net_credit.total)]),
            foot: [`${summary.financial_year} total`, y.bills, fmtINRPdf(y.purchases), fmtINRPdf(y.gst_paid), fmtINRPdf(y.claimed.cgst), fmtINRPdf(y.claimed.sgst), fmtINRPdf(y.claimed.igst), fmtINRPdf(y.claimed.cess), fmtINRPdf(y.claimed.total), fmtINRPdf(y.reversed.total), fmtINRPdf(y.net_credit)], columnStyles: right(1, 10) },
          { heading: 'Quarterly', head: ['Quarter', 'Bills', 'Purchases', 'GST paid', 'CGST', 'SGST', 'IGST', 'Cess', 'Net credit'],
            body: summary.quarters.map(q => [q.quarter, q.bills || '—', m0(q.purchases), m0(q.gst_paid), m0(q.net_credit.cgst), m0(q.net_credit.sgst), m0(q.net_credit.igst), m0(q.net_credit.cess), m0(q.net_credit.total)]), columnStyles: right(1, 8) },
          { heading: 'Credit still to come, and what is at risk', head: ['', 'Bills', 'GST amount'],
            body: [['Ready to claim (in GSTR-2B)', summary.waiting.in_2b.count, fmtINRPdf(summary.waiting.in_2b.total)], ['Waiting for the seller (not in 2B yet)', summary.waiting.pending.count, fmtINRPdf(summary.waiting.pending.total)], ['Not available in 2B', summary.lost.not_available.count, fmtINRPdf(summary.lost.not_available.total)], ['Past the claim deadline', summary.lost.time_barred.count, fmtINRPdf(summary.lost.time_barred.total)], ['Ineligible', summary.ineligible.count, fmtINRPdf(summary.ineligible.total)]], columnStyles: right(1, 2) },
        ],
      });
    } catch (e) { toast.error('Could not prepare the PDF'); }
    finally { setPdfBusy(false); }
  };
  const csv = () => {
    const rows = summary.months.map(m => ({ Month: m.period, Bills: m.bills, 'Purchases (taxable)': m.purchases, 'GST paid': m.gst_paid, 'Claimed CGST': m.claimed.cgst, 'Claimed SGST': m.claimed.sgst, 'Claimed IGST': m.claimed.igst, 'Claimed Cess': m.claimed.cess, 'Claimed total': m.claimed.total, Reversed: m.reversed.total, 'Net credit': m.net_credit.total }));
    rows.push({ Month: `${summary.financial_year} TOTAL`, Bills: y.bills, 'Purchases (taxable)': y.purchases, 'GST paid': y.gst_paid, 'Claimed CGST': y.claimed.cgst, 'Claimed SGST': y.claimed.sgst, 'Claimed IGST': y.claimed.igst, 'Claimed Cess': y.claimed.cess, 'Claimed total': y.claimed.total, Reversed: y.reversed.total, 'Net credit': y.net_credit });
    downloadCSV(rows, `gst-input-credit-${summary.financial_year.replace(' ', '-')}.csv`);
  };
  return (
    <div data-testid="gst-summary" className="print-area">
      <div className="print-header">
        <div className="print-company">{companyLabel}</div>
        <div className="print-report-title">GST Input Credit — {summary.financial_year}</div>
        <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 no-print"><div className="text-xs text-muted">{summary.note}</div><div className="flex gap-2"><GstPrintButtons onPrint={printIt} onPdf={pdf} busy={pdfBusy} testId="gst-summary"/><button className="btn-outline btn-small inline-flex items-center gap-1" onClick={csv}><Download size={14}/>Download CSV</button></div></div>
      <div className="card card-dark overflow-x-auto mb-6">
        <table className="tbl">
          <thead><tr><th>GST return month</th><th className="text-right">Bills</th><th className="text-right">Purchases</th><th className="text-right">GST paid</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th><th className="text-right">Cess</th><th className="text-right">Credit claimed</th><th className="text-right">Reversed</th><th className="text-right">Net credit</th></tr></thead>
          <tbody>
            {summary.months.map(m => (
              <tr key={m.period}><td>{gstMonthLabel(m.period)}</td><td className="mono text-right">{m.bills || '—'}</td><td className="mono text-right">{m.purchases ? fmtINR(m.purchases) : '—'}</td><td className="mono text-right">{m.gst_paid ? fmtINR(m.gst_paid) : '—'}</td>
                {['cgst', 'sgst', 'igst', 'cess'].map(k => <td key={k} className="mono text-right">{m.claimed[k] ? fmtINR(m.claimed[k]) : '—'}</td>)}
                <td className="mono text-right text-[color:var(--gold)]">{m.claimed.total ? fmtINR(m.claimed.total) : '—'}</td><td className="mono text-right text-red-400">{m.reversed.total ? fmtINR(m.reversed.total) : '—'}</td><td className="mono text-right font-semibold">{m.net_credit.total ? fmtINR(m.net_credit.total) : '—'}</td></tr>
            ))}
            <tr style={{ fontWeight: 700 }}><td>{summary.financial_year} total</td><td className="mono text-right">{y.bills}</td><td className="mono text-right">{fmtINR(y.purchases)}</td><td className="mono text-right">{fmtINR(y.gst_paid)}</td>{['cgst', 'sgst', 'igst', 'cess'].map(k => <td key={k} className="mono text-right">{fmtINR(y.claimed[k])}</td>)}<td className="mono text-right text-[color:var(--gold)]">{fmtINR(y.claimed.total)}</td><td className="mono text-right text-red-400">{fmtINR(y.reversed.total)}</td><td className="mono text-right">{fmtINR(y.net_credit)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="serif text-xl mb-3 text-[color:var(--gold)]">Quarterly</div>
      <div className="card card-dark overflow-x-auto mb-6">
        <table className="tbl"><thead><tr><th>Quarter</th><th className="text-right">Bills</th><th className="text-right">Purchases</th><th className="text-right">GST paid</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th><th className="text-right">Cess</th><th className="text-right">Net credit</th></tr></thead>
          <tbody>{summary.quarters.map(q => <tr key={q.quarter} data-testid={`gst-q-${q.quarter.slice(0, 2)}`}><td>{q.quarter}</td><td className="mono text-right">{q.bills || '—'}</td><td className="mono text-right">{fmtINR(q.purchases)}</td><td className="mono text-right">{fmtINR(q.gst_paid)}</td>{['cgst', 'sgst', 'igst', 'cess'].map(k => <td key={k} className="mono text-right">{fmtINR(q.net_credit[k])}</td>)}<td className="mono text-right font-semibold text-[color:var(--gold)]">{fmtINR(q.net_credit.total)}</td></tr>)}</tbody></table>
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        <KpiCard label="Ready to claim (in 2B)" value={fmtINR(summary.waiting.in_2b.total)} hint={`${summary.waiting.in_2b.count} bill(s) the seller has filed`} gold/>
        <KpiCard label="Waiting (not in 2B yet)" value={fmtINR(summary.waiting.pending.total)} hint={`${summary.waiting.pending.count} bill(s) — follow up with the seller`}/>
        <KpiCard label="Lost / at risk" value={fmtINR(summary.lost.not_available.total + summary.lost.time_barred.total)} hint={`${summary.lost.not_available.count} not available · ${summary.lost.time_barred.count} past the claim deadline`} tone="red"/>
        <KpiCard label="Ineligible" value={fmtINR(summary.ineligible.total)} hint={`${summary.ineligible.count} bill(s)`}/>
      </div>
    </div>
  );
}

function GstReconcileTab({ companyId, companyName, onChanged, canEdit }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [addInit, setAddInit] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [markBills, setMarkBills] = useState(null);
  const [sel, setSel] = useState({});            // 2B invoices ticked to add as purchase bills in one go
  const [bulkKind, setBulkKind] = useState('goods');
  const [bulkProject, setBulkProject] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const load = (p) => { if (!companyId) return; setSel({}); api.get('/gst/2b/reconcile', { params: { company_id: companyId, ...(p ? { period: p } : {}) } }).then(r => { setData(r.data); setPeriod(r.data.period || ''); }).catch(e => toast.error(e.response?.data?.detail || 'Could not load')); };
  useEffect(() => { load(''); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId]);
  useEffect(() => { api.get('/vendors').then(r => setVendors(r.data)).catch(() => {}); api.get('/projects').then(r => setProjects(r.data)).catch(() => {}); }, []);
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = ''; if (!f) return;
    setBusy(true);
    try {
      const text = await f.text(); let doc; try { doc = JSON.parse(text); } catch { throw new Error('That file is not valid JSON.'); }
      const r = (await api.post('/gst/2b/import', { company_id: companyId, filename: f.name, data: doc })).data;
      setLast(r); toast.success(`GSTR-2B for ${gstMonthLabel(r.period)} read — ${r.matched} of ${r.entries} invoices matched`); load(r.period); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || err.message || 'Could not read the file'); }
    finally { setBusy(false); }
  };
  const selIds = Object.keys(sel).filter(k => sel[k]);
  const addSelected = async () => {
    if (bulkBusy || !selIds.length || !data || !data.import) return;
    if (!window.confirm(`Add ${selIds.length} invoice(s) from the GSTR-2B as purchase bills (${GST_KIND_LABELS[bulkKind]})?`)) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const r = (await api.post('/gst/2b/add-to-books', { company_id: companyId, import_id: data.import.id, entry_ids: selIds, purchase_kind: bulkKind, project_id: bulkProject || null })).data;
      setBulkResult(r);
      if (r.created.length) toast.success(`${r.created.length} purchase bill(s) added${r.skipped.length ? ` · ${r.skipped.length} skipped` : ''}`); else toast.error('None of the selected invoices could be added — see the reasons below');
      load(period); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not add the bills'); }
    finally { setBulkBusy(false); }
  };
  const printRec = () => setTimeout(() => printLandscape(), 50);
  const recPdf = async () => {
    if (pdfBusy || !data || !data.import) return; setPdfBusy(true);
    try {
      const gst2b = (e) => fmtINRPdf((e.cgst || 0) + (e.sgst || 0) + (e.igst || 0) + (e.cess || 0));
      await gstExportPdf({
        title: `GSTR-2B check — ${gstMonthLabel(period)}`, company: companyName || 'Company', filename: `gstr2b-check-${gstSeg(companyName)}-${period || 'month'}.pdf`,
        sections: [
          { heading: `Recorded here but not in ${gstMonthLabel(period)}'s 2B — ${data.not_in_2b.length}`, head: ['Date', 'Seller', 'GSTIN', 'Invoice', 'GST', 'Credit'],
            body: data.not_in_2b.map(b => [fmtDate(b.invoice_date), b.seller_name, b.seller_gstin || 'no GSTIN', b.invoice_number, fmtINRPdf(b.total_gst), GST_STATUS_LABELS[b.itc_status] || b.itc_status]), columnStyles: { 4: { halign: 'right' } } },
          { heading: `In 2B but not recorded here — ${data.not_in_books.length}`, head: ['Date', 'Supplier', 'GSTIN', 'Invoice', 'Taxable', 'GST', 'ITC'],
            body: data.not_in_books.map(e => [fmtDate(e.invoice_date), e.supplier_name || '—', e.supplier_gstin || '—', e.invoice_number, fmtINRPdf(e.taxable_value), gst2b(e), e.itc_available === 'N' ? 'not available' : 'available']), columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' } } },
          { heading: `Matched — ${data.matched.length}`, head: ['Seller', 'Invoice', 'GST in 2B', 'GST in books', 'Match'],
            body: data.matched.map(e => [e.supplier_name || (e.bill && e.bill.seller_name) || '—', e.invoice_number, gst2b(e), e.bill ? fmtINRPdf(e.bill.total_gst) : '—', e.match_quality === 'exact' ? 'exact' : 'amounts differ — check']), columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } } },
        ],
      });
    } catch (e) { toast.error('Could not prepare the PDF'); }
    finally { setPdfBusy(false); }
  };
  if (!companyId) return <div className="text-sm text-muted py-6">Choose one company above to reconcile its GSTR-2B.</div>;
  return (
    <div data-testid="gst-reconcile">
      <div className="card card-dark mb-4">
        <div className="serif text-lg text-[color:var(--gold)] mb-1">GSTR-2B check (free — no GST-portal connection needed)</div>
        <ol className="text-sm text-muted list-decimal ml-5 mb-3"><li>On the GST portal: <b>Returns → GSTR-2B → Download → JSON</b> for the month.</li><li>Upload that file here. Every supplier invoice in it is matched to your purchase bills (seller GSTIN + invoice number).</li><li>Matched bills turn <b>In 2B — ready to claim</b>; bills missing from 2B show what the seller has not filed.</li></ol>
        {canEdit && <label className="btn-gold btn-small inline-flex items-center gap-1 cursor-pointer"><Upload size={14}/>{busy ? 'Reading…' : 'Upload GSTR-2B (JSON)'}<input type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={onFile} disabled={busy} data-testid="gst-2b-file"/></label>}
        {last && <div className="text-xs mt-3">Last upload: {last.entries} invoice(s) · <b>{last.matched}</b> matched{last.amount_differs ? ` (${last.amount_differs} with different amounts)` : ''} · <b>{last.not_in_books}</b> not in your books{Object.keys(last.skipped_sections || {}).length ? ` · not matched: ${Object.entries(last.skipped_sections).map(([k, n]) => `${k} ${n}`).join(', ')}` : ''}</div>}
      </div>
      {data && (data.imports || []).length > 0 && <div className="mb-4 flex items-center gap-2 text-sm">Month <select className="input" style={{ width: 170 }} value={period} onChange={e => { setPeriod(e.target.value); load(e.target.value); }}>{data.imports.map(i => <option key={i.id} value={i.period}>{gstMonthLabel(i.period)} · {i.entry_count} invoices</option>)}</select></div>}
      {data && !(data.imports || []).length && <div className="text-sm text-muted">No GSTR-2B uploaded for this company yet.</div>}
      {data && data.import && (
        <div className="print-area">
          <div className="print-header">
            <div className="print-company">{companyName}</div>
            <div className="print-report-title">GSTR-2B check — {gstMonthLabel(period)}</div>
            <div className="print-meta">Generated: {fmtGeneratedAt()}</div>
          </div>
          <div className="flex justify-end gap-2 mb-3 no-print"><GstPrintButtons onPrint={printRec} onPdf={recPdf} busy={pdfBusy} testId="gst-2b"/></div>
          <div className="serif text-lg mb-2">Recorded here but <span className="text-red-400">not in {gstMonthLabel(period)}'s 2B</span> — {data.not_in_2b.length}</div>
          <div className="card card-dark overflow-x-auto mb-6">
            <table className="tbl"><thead><tr><th>Date</th><th>Seller</th><th>Invoice</th><th className="text-right">GST</th><th>Status</th></tr></thead>
              <tbody>{data.not_in_2b.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-4">Every pending bill is in the 2B.</td></tr>}
                {data.not_in_2b.map(b => <tr key={b.id}><td className="text-xs">{fmtDate(b.invoice_date)}</td><td>{b.seller_name}<div className="text-xs text-muted mono">{b.seller_gstin || 'no GSTIN'}</div></td><td className="text-xs">{b.invoice_number}</td><td className="mono text-right">{fmtINR(b.total_gst)}</td><td><GstStatusChip bill={b}/>{canEdit && <button className="tab btn-small ml-2 no-print" onClick={() => setMarkBills([b])}>Mark…</button>}</td></tr>)}</tbody></table>
          </div>
          <div className="serif text-lg mb-2">In 2B but <span className="text-[color:var(--gold)]">not recorded here</span> — {data.not_in_books.length}</div>
          {canEdit && data.not_in_books.length > 0 && (
            <div className="no-print flex flex-wrap items-center gap-2 mb-2 text-sm" data-testid="gst-2b-bulk-bar">
              <label className="inline-flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={selIds.length === data.not_in_books.length} onChange={e => setSel(e.target.checked ? Object.fromEntries(data.not_in_books.map(x => [x.id, true])) : {})} data-testid="gst-2b-select-all"/> Select all</label>
              <span className="text-muted">{selIds.length} selected</span>
              <select className="input" style={{ width: 150 }} value={bulkKind} onChange={e => setBulkKind(e.target.value)} aria-label="Purchase type" data-testid="gst-2b-bulk-kind">{Object.entries(GST_KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              <select className="input" style={{ width: 200 }} value={bulkProject} onChange={e => setBulkProject(e.target.value)} aria-label="Project" data-testid="gst-2b-bulk-project"><option value="">No project</option>{projects.filter(p => p.company_id === companyId).map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}</select>
              <button className="btn-gold btn-small" disabled={!selIds.length || bulkBusy} onClick={addSelected} data-testid="gst-2b-bulk-add">{bulkBusy ? 'Adding…' : `Add ${selIds.length || ''} selected as purchase bills`}</button>
            </div>
          )}
          {bulkResult && bulkResult.skipped.length > 0 && (
            <div className="no-print card card-dark mb-2 text-sm" data-testid="gst-2b-bulk-skipped">
              <div className="mb-1"><b>{bulkResult.skipped.length}</b> not added:</div>
              <ul className="list-disc ml-5 text-muted">{bulkResult.skipped.map(x => <li key={x.entry_id}>{x.invoice} — {x.reason}</li>)}</ul>
            </div>
          )}
          <div className="card card-dark overflow-x-auto mb-6">
            <table className="tbl"><thead><tr>{canEdit && <th className="no-print" style={{ width: 28 }}></th>}<th>Date</th><th>Supplier</th><th>Invoice</th><th className="text-right">Taxable</th><th className="text-right">GST</th><th>ITC</th><th className="no-print"></th></tr></thead>
              <tbody>{data.not_in_books.length === 0 && <tr><td colSpan="8" className="text-center text-muted py-4">Nothing missing.</td></tr>}
                {data.not_in_books.map(e => <tr key={e.id}>{canEdit && <td className="no-print"><input type="checkbox" checked={!!sel[e.id]} onChange={ev => setSel(v => ({ ...v, [e.id]: ev.target.checked }))} aria-label={`Select ${e.invoice_number}`} data-testid={`gst-2b-select-${e.id}`}/></td>}<td className="text-xs">{fmtDate(e.invoice_date)}</td><td>{e.supplier_name || '—'}<div className="text-xs text-muted mono">{e.supplier_gstin}</div></td><td className="text-xs">{e.invoice_number}</td><td className="mono text-right">{fmtINR(e.taxable_value)}</td><td className="mono text-right">{fmtINR(e.cgst + e.sgst + e.igst + e.cess)}</td><td className="text-xs">{e.itc_available === 'N' ? 'not available' : 'available'}</td>
                  <td className="text-right no-print">{canEdit && <button className="btn-outline btn-small" onClick={() => setAddInit({ seller_name: e.supplier_name || '', seller_gstin: e.supplier_gstin || '', invoice_number: e.invoice_number || '', invoice_date: e.invoice_date || '', taxable_value: e.taxable_value, cgst: e.cgst, sgst: e.sgst, igst: e.igst, cess: e.cess, company_id: companyId })}>Add as purchase bill</button>}</td></tr>)}</tbody></table>
          </div>
          <div className="serif text-lg mb-2">Matched — {data.matched.length}</div>
          <div className="card card-dark overflow-x-auto">
            <table className="tbl"><thead><tr><th>Seller</th><th>Invoice</th><th className="text-right">GST in 2B</th><th className="text-right">GST in books</th><th>Match</th></tr></thead>
              <tbody>{data.matched.map(e => <tr key={e.id}><td>{e.supplier_name || e.bill?.seller_name}</td><td className="text-xs">{e.invoice_number}</td><td className="mono text-right">{fmtINR(e.cgst + e.sgst + e.igst + e.cess)}</td><td className="mono text-right">{e.bill ? fmtINR(e.bill.total_gst) : '—'}</td><td className="text-xs">{e.match_quality === 'exact' ? <span style={{ color: '#86efac' }}>exact</span> : <span style={{ color: '#fb923c' }}>amounts differ — check</span>}</td></tr>)}</tbody></table>
          </div>
        </div>
      )}
      <GstBillModal open={!!addInit} onClose={() => setAddInit(null)} onSaved={() => { load(period); onChanged(); }} initial={addInit} vendors={vendors} projects={projects}/>
      <GstMarkModal open={!!markBills} onClose={() => setMarkBills(null)} onDone={() => { load(period); onChanged(); }} bills={markBills || []}/>
    </div>
  );
}

function GstInputPage() {
  const { user } = useAuth();
  const dataEntry = isDataEntry(user);
  const canEdit = ['admin', 'accountant', 'master_admin'].includes(user.role);
  const { companies, needsPicker } = useMyCompanies();
  const companyF = useMultiFilter('GstInput.company');
  const [fy, setFy] = usePersistedState('GstInput.fy', gstCurrentFy());
  const [tab, setTab] = usePersistedState('GstInput.tab', 'bills');
  const [statusFilter, setStatusFilter] = usePersistedState('GstInput.status', 'all');
  const [search, setSearch] = useState('');
  const [bills, setBills] = useState(null);
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [markOpen, setMarkOpen] = useState(false);
  const params = {};
  if (!companyF.all && companyF.ids.length) params.company_ids = companyF.ids.join(',');
  const load = () => {
    api.get('/gst/purchase-bills', { params: { ...params, ...(fy && !dataEntry ? { fy } : {}) } }).then(r => setBills(r.data)).catch(e => { setBills([]); toast.error(e.response?.data?.detail || 'Could not load the bills'); });
    if (canEdit) api.get('/gst/summary', { params: { ...params, fy: fy || gstCurrentFy() } }).then(r => setSummary(r.data)).catch(() => setSummary(null));
  };
  useEffect(() => { load(); setSelected(new Set()); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fy, companyF.all, companyF.ids.join(',')]);
  useEffect(() => { api.get('/vendors').then(r => setVendors(r.data)).catch(() => {}); api.get('/projects').then(r => setProjects(r.data)).catch(() => {}); }, []);
  const shown = (bills || []).filter(b => (statusFilter === 'all' || b.itc_status === statusFilter) && (!search.trim() || [b.seller_name, b.invoice_number, b.seller_gstin, b.description].some(x => (x || '').toLowerCase().includes(search.trim().toLowerCase()))));
  const selectedBills = shown.filter(b => selected.has(b.id));
  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const counts = (bills || []).reduce((a, b) => { a[b.itc_status] = (a[b.itc_status] || 0) + 1; return a; }, {});
  const totalGst = shown.reduce((a, b) => a + b.total_gst, 0);
  const [pdfBusy, setPdfBusy] = useState(false);
  const companyLabel = (!companyF.all && companyF.ids.length) ? companies.filter(c => companyF.ids.includes(c.id)).map(c => c.name).join(', ') : (needsPicker ? 'All companies' : ((companies[0] || {}).name || 'Company'));
  const filterSummary = [fy ? fy : 'All years', statusFilter !== 'all' ? `Credit: ${GST_STATUS_LABELS[statusFilter]}` : '', search.trim() ? `Search: "${search.trim()}"` : ''].filter(Boolean).join('  ·  ');
  const printBills = () => setTimeout(() => printLandscape(), 50);
  const billsPdf = async () => {
    if (pdfBusy) return; setPdfBusy(true);
    try {
      const hasCess = shown.some(b => b.cess > 0);
      const sum = (k) => shown.reduce((a, b) => a + (Number(b[k]) || 0), 0);
      const head = ['Invoice date', 'Seller', 'GSTIN', 'Invoice no.', 'Taxable', 'CGST', 'SGST', 'IGST', ...(hasCess ? ['Cess'] : []), 'Total GST', 'Invoice total', 'Seller filed', 'Credit', 'Claim by'];
      const body = shown.map(b => [
        fmtDate(b.invoice_date), `${b.seller_name}${b.project_name ? `\n${b.project_name}` : ''}${needsPicker && b.company_name ? `\n${b.company_name}` : ''}`, b.seller_gstin || 'no GSTIN', b.invoice_number,
        fmtINRPdf(b.taxable_value), fmtINRPdf(b.cgst), fmtINRPdf(b.sgst), fmtINRPdf(b.igst), ...(hasCess ? [fmtINRPdf(b.cess)] : []), fmtINRPdf(b.total_gst), fmtINRPdf(b.invoice_total),
        b.seller_filed === 'yes' ? `Yes${b.gstr2b_period ? ` · ${gstMonthLabel(b.gstr2b_period)}` : ''}` : b.seller_filed === 'no' ? 'No' : 'Not checked',
        `${GST_STATUS_LABELS[b.itc_status] || b.itc_status}${b.itc_status === 'claimed' && b.claim_period ? ` · ${gstMonthLabel(b.claim_period)}` : ''}${b.itc_status === 'reversed' && b.reversal_period ? ` · ${gstMonthLabel(b.reversal_period)}` : ''}${b.itc_status === 'claimed' && b.itc_claimed_amount != null && b.itc_claimed_amount < b.total_gst - 0.005 ? ` (${fmtINRPdf(b.itc_claimed_amount)} of ${fmtINRPdf(b.total_gst)})` : ''}`,
        ['pending', 'in_2b'].includes(b.itc_status) ? `${fmtDate(b.claim_deadline)}${b.time_barred ? ' (passed)' : ''}` : '—',
      ]);
      const foot = [`Total — ${shown.length} bill${shown.length === 1 ? '' : 's'}`, '', '', '', fmtINRPdf(sum('taxable_value')), fmtINRPdf(sum('cgst')), fmtINRPdf(sum('sgst')), fmtINRPdf(sum('igst')), ...(hasCess ? [fmtINRPdf(sum('cess'))] : []), fmtINRPdf(totalGst), fmtINRPdf(sum('invoice_total')), '', '', ''];
      const firstNum = 4, lastNum = 8 + (hasCess ? 1 : 0) + 1;
      await gstExportPdf({
        title: 'GST Input Credit — purchase bills', company: companyLabel, filters: filterSummary,
        filename: `gst-purchase-bills-${gstSeg(fy || 'all-years')}-${todayLocalISO()}.pdf`,
        sections: [{ head, body, foot, columnStyles: Object.fromEntries(Array.from({ length: lastNum - firstNum + 1 }, (_, i) => [firstNum + i, { halign: 'right' }])) }],
      });
    } catch (e) { toast.error('Could not prepare the PDF'); }
    finally { setPdfBusy(false); }
  };
  const soleCompany = !companyF.all && companyF.ids.length === 1 ? companyF.ids[0] : (companies.length === 1 ? companies[0].id : '');
  const del = async (b) => { if (!window.confirm(`Delete ${b.seller_name} · ${b.invoice_number}?`)) return; try { await api.delete(`/gst/purchase-bills/${b.id}`); load(); toast.success('Deleted'); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };
  return (
    <div className="max-w-7xl mx-auto px-6 py-8 fade-up" data-testid="gst-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="section-eyebrow mb-2">GST · Input Tax Credit</div>
          <h1 className="serif text-4xl">GST Input Credit</h1>
          <div className="text-sm text-muted mt-1">{dataEntry ? 'Record every purchase bill you paid GST on. The accounts team claims the credit.' : 'Purchase bills we paid GST on, what the seller has filed (GSTR-2B), and the credit claimed — by month, quarter and financial year.'}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {needsPicker && !dataEntry && <MultiFilterPicker filter={companyF} items={companies.map(c => ({ id: c.id, name: c.name }))} allLabel="All Companies" width={170} testId="gst-filter-company"/>}
          {!dataEntry && <select className="input" style={{ width: 150 }} value={fy} onChange={e => setFy(e.target.value)} data-testid="gst-fy">{gstFyOptions().map(f => <option key={f} value={f}>{f}</option>)}<option value="">All years</option></select>}
          <button className="btn-gold btn-small inline-flex items-center gap-1" onClick={() => { setEditBill(null); setModalOpen(true); }} data-testid="gst-add"><Plus size={14}/>Add purchase bill</button>
        </div>
      </div>
      {canEdit && summary && (
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <KpiCard label={`Credit claimed — ${summary.financial_year}`} value={fmtINR(summary.year.net_credit)} hint={`CGST ${fmtINR(summary.year.claimed.cgst)} · SGST ${fmtINR(summary.year.claimed.sgst)} · IGST ${fmtINR(summary.year.claimed.igst)}`} gold/>
          <KpiCard label="Ready to claim (in 2B)" value={fmtINR(summary.waiting.in_2b.total)} hint={`${summary.waiting.in_2b.count} bill(s) — seller has filed`}/>
          <KpiCard label="Waiting for the seller" value={fmtINR(summary.waiting.pending.total)} hint={`${summary.waiting.pending.count} bill(s) not in 2B yet`}/>
          <KpiCard label="Lost / at risk" value={fmtINR(summary.lost.not_available.total + summary.lost.time_barred.total)} hint={`${summary.lost.not_available.count} not available · ${summary.lost.time_barred.count} past deadline`} tone="red"/>
        </div>
      )}
      {canEdit && (
        <div className="flex gap-2 mb-5 no-print">
          {[['bills', 'Purchase bills'], ['summary', 'Credit summary (month · quarter · year)'], ['reconcile', 'GSTR-2B check']].map(([k, l]) => <button key={k} className={'tab ' + (tab === k ? 'active' : '')} onClick={() => setTab(k)} data-testid={`gst-tab-${k}`}>{l}</button>)}
        </div>
      )}
      {(tab === 'bills' || dataEntry) && (
        <>
          {!dataEntry && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {[['all', 'All'], ...Object.entries(GST_STATUS_LABELS)].map(([k, l]) => <button key={k} className={'tab btn-small ' + (statusFilter === k ? 'active' : '')} onClick={() => setStatusFilter(k)}>{k === 'all' ? `All (${(bills || []).length})` : `${l}${counts[k] ? ` (${counts[k]})` : ''}`}</button>)}
              <input className="input" style={{ width: 220 }} placeholder="Search seller, invoice, GSTIN…" value={search} onChange={e => setSearch(e.target.value)}/>
              {canEdit && selectedBills.length > 0 && <button className="btn-gold btn-small" onClick={() => setMarkOpen(true)} data-testid="gst-mark">Mark credit — {selectedBills.length} selected</button>}
              {bills !== null && shown.length > 0 && <span className="ml-auto flex gap-2"><GstPrintButtons onPrint={printBills} onPdf={billsPdf} busy={pdfBusy} testId="gst-bills"/></span>}
            </div>
          )}
          <div className="card card-dark overflow-x-auto print-area">
            <div className="print-header">
              <div className="print-company">{companyLabel}</div>
              <div className="print-report-title">GST Input Credit — purchase bills</div>
              <div className="print-meta">Generated: {fmtGeneratedAt()}{filterSummary ? ` · ${filterSummary}` : ''}</div>
            </div>
            {bills === null ? <Loading/> : (
              <table className="tbl">
                <thead><tr>{canEdit && <th className="no-print"><input type="checkbox" checked={shown.length > 0 && shown.every(b => selected.has(b.id))} onChange={e => setSelected(e.target.checked ? new Set(shown.map(b => b.id)) : new Set())} aria-label="Select all"/></th>}<th>Invoice date</th><th>Seller</th><th>Invoice no.</th><th className="text-right">Taxable</th><th className="text-right">GST</th><th className="text-right">Total</th><th>Seller filed</th><th>Credit</th>{!dataEntry && <th>Claim by</th>}{canEdit && <th className="no-print"></th>}</tr></thead>
                <tbody>
                  {shown.length === 0 && <tr><td colSpan="11" className="text-center text-muted py-8">{(bills || []).length === 0 ? (dataEntry ? 'You have not recorded any purchase bills yet.' : 'No purchase bills yet — add the first one.') : 'No bills match these filters.'}</td></tr>}
                  {shown.map(b => (
                    <tr key={b.id} data-testid={`gst-row-${b.invoice_number}`}>
                      {canEdit && <td className="no-print"><input type="checkbox" checked={selected.has(b.id)} onChange={() => toggle(b.id)} aria-label={`Select ${b.invoice_number}`}/></td>}
                      <td className="text-xs">{fmtDate(b.invoice_date)}</td>
                      <td>{b.seller_name}<div className="text-xs text-muted mono">{b.seller_gstin || <span style={{ color: 'var(--accent-amber)' }}>no GSTIN</span>}</div>{b.project_name && <div className="text-xs text-muted">{b.project_name}</div>}{needsPicker && <div className="text-xs text-muted">{b.company_name}</div>}</td>
                      <td className="text-xs">{b.invoice_number}</td>
                      <td className="mono text-right">{fmtINR(b.taxable_value)}</td>
                      <td className="mono text-right" title={`CGST ${fmtINR(b.cgst)} · SGST ${fmtINR(b.sgst)} · IGST ${fmtINR(b.igst)}${b.cess ? ' · Cess ' + fmtINR(b.cess) : ''}`}>{fmtINR(b.total_gst)}</td>
                      <td className="mono text-right">{fmtINR(b.invoice_total)}</td>
                      <td className="text-xs">{b.seller_filed === 'yes' ? <span style={{ color: '#86efac' }}>Yes{b.gstr2b_period ? ` · ${gstMonthLabel(b.gstr2b_period)}` : ''}</span> : b.seller_filed === 'no' ? <span className="text-red-400">No</span> : <span className="text-muted">Not checked</span>}</td>
                      <td><GstStatusChip bill={b}/>{b.itc_status === 'claimed' && b.itc_claimed_amount != null && b.itc_claimed_amount < b.total_gst - 0.005 && <div className="text-xs text-muted">{fmtINR(b.itc_claimed_amount)} of {fmtINR(b.total_gst)}</div>}</td>
                      {!dataEntry && <td className="text-xs">{['pending', 'in_2b'].includes(b.itc_status) ? <span className={b.time_barred ? 'text-red-400' : ''}>{fmtDate(b.claim_deadline)}{b.time_barred ? ' (passed)' : ''}</span> : '—'}</td>}
                      {canEdit && <td className="text-right whitespace-nowrap no-print"><button className="tab btn-small" onClick={() => { setEditBill(b); setModalOpen(true); }} aria-label="Edit"><Edit3 size={12}/></button><button className="tab btn-small ml-1" onClick={() => del(b)} aria-label="Delete"><Trash2 size={12}/></button></td>}
                    </tr>
                  ))}
                  {shown.length > 0 && <tr>{canEdit && <td className="no-print"></td>}<td colSpan={4} className="text-right text-xs text-muted uppercase">Total GST on {shown.length} bill{shown.length === 1 ? '' : 's'}</td><td className="mono text-right text-[color:var(--gold)]">{fmtINR(totalGst)}</td><td colSpan={5}></td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      {canEdit && tab === 'summary' && <GstSummaryTab summary={summary} companyLabel={companyLabel}/>}
      {canEdit && tab === 'reconcile' && <GstReconcileTab companyId={soleCompany} companyName={(companies.find(c => c.id === soleCompany) || {}).name || companyLabel} canEdit={canEdit} onChanged={load}/>}
      <GstBillModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} initial={editBill} vendors={vendors} projects={projects}/>
      <GstMarkModal open={markOpen} onClose={() => setMarkOpen(false)} onDone={() => { setSelected(new Set()); load(); }} bills={selectedBills}/>
    </div>
  );
}

// ==================== ORGANISATION: trial banner, getting started, platform console ====================
function TrialBanner() {
  const { user } = useAuth();
  const org = user && user.organization;
  if (!org || org.plan !== 'trial' || !org.trial_ends_at) return null;
  const days = Math.ceil((new Date(org.trial_ends_at).getTime() - Date.now()) / 86400000);
  const expired = days <= 0;
  const native = isNativeApp();   // store rules: no purchase prompts inside the iPhone / Android apps
  const canBuy = !native && ['master_admin', 'admin'].includes(user.role);
  return (
    <div className="no-print text-center text-sm py-2 px-4" style={{ background: expired ? 'rgba(220,80,80,0.15)' : 'rgba(217,164,65,0.14)', color: expired ? '#f87171' : 'var(--accent-amber)' }} data-testid="trial-banner">
      {expired ? 'Your free trial has ended — you can still view and export everything, but changes are paused.' : `Free trial: ${days} day${days === 1 ? '' : 's'} left.`}
      {canBuy && <> <Link to="/app/billing" className="underline ml-1" data-testid="trial-billing-link">{expired ? 'Subscribe to continue' : 'Choose a plan'}</Link></>}
      {native && expired && <> Please ask your administrator to continue on the website.</>}
    </div>
  );
}

function BillingPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState('monthly');
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => api.get('/billing/overview').then(r => { setData(r.data); setDetails(d => d || { billing_name: r.data.organization.billing_name || '', billing_email: r.data.organization.billing_email || '', billing_gstin: r.data.organization.billing_gstin || '', billing_address: r.data.organization.billing_address || '' }); }).catch(e => toast.error(e.response?.data?.detail || 'Could not load billing'));
  useEffect(() => { load(); const f = () => load(); window.addEventListener('focus', f); return () => window.removeEventListener('focus', f); // eslint-disable-next-line
  }, []);
  if (isNativeApp()) return <Navigate to="/app/dashboard" replace/>;
  if (!data) return <Loading/>;
  const org = data.organization;
  const canManage = data.can_manage;
  const PLAN_NAMES = { trial: 'Free trial', starter: 'Starter', business: 'Business', enterprise: 'Enterprise', internal: 'Internal (no charge)' };
  const subscribe = async (code) => {
    setBusy(code);
    try { const r = (await api.post('/billing/subscribe', { plan_code: code, cycle })).data; if (r.payment_url) window.location.href = r.payment_url; else toast.success('Subscription started'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start the subscription'); }
    finally { setBusy(''); }
  };
  const cancel = async () => {
    if (!window.confirm('Cancel the subscription at the end of the current paid period? You keep full access until then.')) return;
    try { await api.post('/billing/cancel'); toast.success('Cancellation scheduled'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const saveDetails = async () => {
    setBusy('details');
    try { await api.put('/billing/details', details); toast.success('Billing details saved'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(''); }
  };
  const statusLabel = { none: 'Managed by us', trialing: 'Trial', active: 'Active', past_due: 'Payment overdue', cancelled: 'Cancelled' }[org.billing_status] || org.billing_status;
  const price = (p) => cycle === 'yearly' ? p.yearly_price_inr : p.monthly_price_inr;
  const avail = (p) => cycle === 'yearly' ? p.yearly_available : p.monthly_available;
  return (
    <div className="max-w-5xl mx-auto px-6 py-8 fade-up" data-testid="billing-page">
      <div className="section-eyebrow mb-2">Subscription</div>
      <h1 className="serif text-4xl mb-6">Plan &amp; billing</h1>
      {data.write_blocked_reason && <div className="card card-dark mb-6 text-sm" style={{ borderColor: 'rgba(248,113,113,0.5)', color: '#f87171' }} data-testid="billing-blocked">{data.write_blocked_reason}</div>}
      <div className="card card-dark mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><div className="serif text-2xl text-[color:var(--gold)]">{PLAN_NAMES[org.plan] || org.plan}</div>
            <div className="text-sm text-muted mt-1">{org.name} · {statusLabel}{org.plan === 'trial' && data.trial_days_left != null ? ` · ${data.trial_days_left} day${data.trial_days_left === 1 ? '' : 's'} left` : ''}{org.current_period_end && org.billing_status !== 'none' ? ` · ${org.cancel_at_period_end || org.billing_status === 'cancelled' ? 'ends' : 'renews'} ${fmtDate(org.current_period_end)}${org.billing_cycle ? ` (${org.billing_cycle})` : ''}` : ''}</div>
            <div className="text-xs text-muted mt-1">Limits: {org.max_companies ? `${org.max_companies} companies` : 'unlimited companies'} · {org.max_users ? `${org.max_users} users` : 'unlimited users'}</div></div>
          {canManage && org.billing_status === 'active' && !org.cancel_at_period_end && <button className="btn-outline btn-small" onClick={cancel} data-testid="billing-cancel">Cancel subscription</button>}
        </div>
      </div>
      {org.plan !== 'internal' && (
        <>
          <div className="flex items-center justify-between mb-3"><div className="serif text-xl">Choose a plan</div>
            <div className="flex gap-2"><button className={'tab btn-small ' + (cycle === 'monthly' ? 'active' : '')} onClick={() => setCycle('monthly')}>Monthly</button><button className={'tab btn-small ' + (cycle === 'yearly' ? 'active' : '')} onClick={() => setCycle('yearly')}>Yearly</button></div></div>
          {data.plans.length === 0 ? <div className="card card-dark text-sm text-muted mb-6">Plans and prices will appear here shortly. Until then, please contact us to subscribe — your workspace keeps working in the meantime.</div> : (
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {data.plans.map(p => (
                <div key={p.code} className="card card-dark" data-testid={`plan-${p.code}`}>
                  <div className="serif text-xl">{p.name}</div>
                  <div className="text-xs text-muted mb-3">{p.description}</div>
                  <div className="mono text-2xl text-[color:var(--gold)]">{price(p) ? fmtINR(price(p)) : '—'}<span className="text-xs text-muted"> / {cycle === 'yearly' ? 'year' : 'month'}</span></div>
                  <div className="text-xs text-muted mt-1">+ GST as applicable</div>
                  <ul className="text-xs mt-3 mb-4 space-y-1"><li>{p.max_companies ? `${p.max_companies} compan${p.max_companies === 1 ? 'y' : 'ies'}` : 'Unlimited companies'}</li><li>{p.max_users ? `Up to ${p.max_users} users` : 'Unlimited users'}</li></ul>
                  {canManage ? (data.payments_available && avail(p)
                    ? <button className="btn-gold btn-small w-full" disabled={!!busy || (org.billing_status === 'active' && !org.cancel_at_period_end)} onClick={() => subscribe(p.code)} data-testid={`subscribe-${p.code}`}>{busy === p.code ? 'Opening…' : org.plan === p.code && org.billing_status === 'active' ? 'Current plan' : 'Subscribe'}</button>
                    : <div className="text-xs text-muted">{data.payments_available ? 'Not offered for this billing period.' : 'Online payment is being set up — contact us to subscribe.'}</div>)
                    : <div className="text-xs text-muted">Only the account owner can subscribe.</div>}
                </div>
              ))}
            </div>
          )}
          <div className="card card-dark mb-6">
            <div className="serif text-xl mb-1">Billing details (for your GST invoice)</div>
            <div className="text-xs text-muted mb-3">Shown on invoices. The payment itself happens on Razorpay's secure page — card details never touch this app.</div>
            {details && <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name on invoice"><input className="input" disabled={!canManage} value={details.billing_name} onChange={e => setDetails({ ...details, billing_name: e.target.value })}/></Field>
              <Field label="Invoice email"><input className="input" type="email" disabled={!canManage} value={details.billing_email} onChange={e => setDetails({ ...details, billing_email: e.target.value })}/></Field>
              <Field label="GSTIN"><input className="input mono" maxLength={15} disabled={!canManage} value={details.billing_gstin} onChange={e => setDetails({ ...details, billing_gstin: e.target.value.toUpperCase() })}/></Field>
              <Field label="Billing address"><input className="input" disabled={!canManage} value={details.billing_address} onChange={e => setDetails({ ...details, billing_address: e.target.value })}/></Field>
            </div>}
            {canManage && <div className="mt-4 text-right"><button className="btn-gold btn-small" disabled={busy === 'details'} onClick={saveDetails}>{busy === 'details' ? 'Saving…' : 'Save details'}</button></div>}
          </div>
        </>
      )}
    </div>
  );
}

function GettingStartedCard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);
  const org = user && user.organization;
  const eligible = !!org && org.plan === 'trial' && ['master_admin', 'admin'].includes(user.role);
  useEffect(() => {
    if (!eligible) return;
    Promise.all([api.get('/projects'), api.get('/partners'), api.get('/profiles')]).then(([p, pt, pr]) => setCounts({ projects: p.data.length, partners: pt.data.length, users: pr.data.length })).catch(() => {});
  }, [eligible]);
  if (!eligible || !counts) return null;
  const steps = [
    ['Add your partners', counts.partners > 0, '/app/settings', 'Settings → Partners'],
    ['Create your first project', counts.projects > 0, '/app/projects', 'Projects → Add Project'],
    ['Import your existing records', false, '/app/data', 'Data → Import'],
    ['Invite your team', counts.users > 1, '/app/admin', 'User Management'],
  ];
  if (counts.projects > 0 && counts.partners > 0) return null;
  return (
    <div className="card card-dark mb-6" data-testid="getting-started">
      <div className="serif text-xl text-[color:var(--gold)] mb-1">Welcome — let's set up {org.name}</div>
      <div className="text-xs text-muted mb-3">A few steps and the dashboard fills itself in.</div>
      <ol className="text-sm space-y-1">
        {steps.map(([label, done, to, where]) => <li key={label} className="flex items-center gap-2"><span style={{ color: done ? '#86efac' : 'var(--text-muted)' }}>{done ? '✓' : '○'}</span><Link to={to} className="hover:text-[color:var(--gold)]">{label}</Link><span className="text-xs text-muted">· {where}</span></li>)}
      </ol>
    </div>
  );
}

function PlatformPlans() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get('/platform/plans').then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => (v === '' || v == null) ? undefined : Number(v);
      await api.put(`/platform/plans/${edit.code}`, { name: edit.name, description: edit.description, monthly_price_inr: num(edit.monthly_price_inr), yearly_price_inr: num(edit.yearly_price_inr), max_companies: num(edit.max_companies), max_users: num(edit.max_users), razorpay_monthly_plan_id: edit.razorpay_monthly_plan_id || '', razorpay_yearly_plan_id: edit.razorpay_yearly_plan_id || '', active: !!edit.active });
      toast.success('Plan saved'); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };
  return (
    <div className="card card-dark mb-6" data-testid="platform-plans">
      <div className="serif text-xl text-[color:var(--gold)] mb-1">Plans &amp; pricing</div>
      <div className="text-xs text-muted mb-3">Payments: {data.payments_configured ? 'Razorpay keys set' : 'Razorpay keys NOT set'} · webhook secret {data.webhook_configured ? 'set' : 'NOT set'}. Webhook address for Razorpay (events: subscription.activated / charged / halted / cancelled / completed, payment.failed): <span className="mono">https://api.finance.anvidbuildcon.com/api/billing/razorpay-webhook</span></div>
      <div className="overflow-x-auto"><table className="tbl"><thead><tr><th>Plan</th><th className="text-right">Monthly ₹</th><th className="text-right">Yearly ₹</th><th className="text-right">Companies</th><th className="text-right">Users</th><th>Razorpay plan ids</th><th>Offered</th><th></th></tr></thead>
        <tbody>{data.plans.map(p => <tr key={p.code}><td>{p.name}</td><td className="mono text-right">{p.monthly_price_inr ?? '—'}</td><td className="mono text-right">{p.yearly_price_inr ?? '—'}</td><td className="mono text-right">{p.max_companies ?? '∞'}</td><td className="mono text-right">{p.max_users ?? '∞'}</td><td className="text-xs mono">{p.razorpay_monthly_plan_id || '—'} / {p.razorpay_yearly_plan_id || '—'}</td><td className="text-xs">{p.active ? 'yes' : 'no'}</td><td className="text-right"><button className="tab btn-small" onClick={() => setEdit({ ...p })}><Edit3 size={12}/></button></td></tr>)}</tbody></table></div>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Edit plan — ${edit ? edit.name : ''}`} maxW={640}>
        {edit && <div className="grid grid-cols-2 gap-4">
          <Field label="Name"><input className="input" value={edit.name || ''} onChange={e => setEdit({ ...edit, name: e.target.value })}/></Field>
          <Field label="Description"><input className="input" value={edit.description || ''} onChange={e => setEdit({ ...edit, description: e.target.value })}/></Field>
          <Field label="Monthly price (₹, before GST)"><input className="input mono" type="number" min="0" value={edit.monthly_price_inr ?? ''} onChange={e => setEdit({ ...edit, monthly_price_inr: e.target.value })}/></Field>
          <Field label="Yearly price (₹, before GST)"><input className="input mono" type="number" min="0" value={edit.yearly_price_inr ?? ''} onChange={e => setEdit({ ...edit, yearly_price_inr: e.target.value })}/></Field>
          <Field label="Max companies (blank = unlimited)"><input className="input mono" type="number" min="1" value={edit.max_companies ?? ''} onChange={e => setEdit({ ...edit, max_companies: e.target.value })}/></Field>
          <Field label="Max users (blank = unlimited)"><input className="input mono" type="number" min="1" value={edit.max_users ?? ''} onChange={e => setEdit({ ...edit, max_users: e.target.value })}/></Field>
          <Field label="Razorpay plan id — monthly"><input className="input mono" value={edit.razorpay_monthly_plan_id || ''} onChange={e => setEdit({ ...edit, razorpay_monthly_plan_id: e.target.value })} placeholder="plan_XXXXXXXX"/></Field>
          <Field label="Razorpay plan id — yearly"><input className="input mono" value={edit.razorpay_yearly_plan_id || ''} onChange={e => setEdit({ ...edit, razorpay_yearly_plan_id: e.target.value })} placeholder="plan_XXXXXXXX"/></Field>
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!edit.active} onChange={e => setEdit({ ...edit, active: e.target.checked })}/>Offer this plan to customers</label>
          <div className="col-span-2 flex justify-end gap-2"><button className="btn-outline btn-small" onClick={() => setEdit(null)}>Cancel</button><button className="btn-gold btn-small" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button></div>
        </div>}
      </Modal>
    </div>
  );
}

function PlatformPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [create, setCreate] = useState(null);
  const [edit, setEdit] = useState(null);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get('/platform/organizations').then(r => setData(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Could not load'));
  useEffect(() => { load(); }, []);
  if (!user.is_platform_admin) return <Navigate to="/app/dashboard" replace/>;
  const saveCreate = async () => {
    if (saving) return; setSaving(true);
    try {
      const r = (await api.post('/platform/organizations', { ...create, trial_days: Number(create.trial_days) || 14, max_companies: create.max_companies ? Number(create.max_companies) : null, max_users: create.max_users ? Number(create.max_users) : null })).data;
      setCreated(r); setCreate(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };
  const saveEdit = async () => {
    if (saving) return; setSaving(true);
    try {
      await api.patch(`/platform/organizations/${edit.id}`, { name: edit.name, plan: edit.plan, status: edit.status, trial_ends_at: edit.trial_end_date || undefined, max_companies: edit.max_companies ? Number(edit.max_companies) : undefined, max_users: edit.max_users ? Number(edit.max_users) : undefined });
      toast.success('Saved'); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };
  const PLANS = ['trial', 'starter', 'business', 'enterprise', 'internal'];
  return (
    <div className="max-w-6xl mx-auto px-6 py-8 fade-up" data-testid="platform-page">
      <div className="flex items-center justify-between mb-6">
        <div><div className="section-eyebrow mb-2">Product staff only</div><h1 className="serif text-4xl">Platform</h1><div className="text-sm text-muted mt-1">Every customer organisation, its plan and limits. Customers never see each other.</div></div>
        <button className="btn-gold btn-small inline-flex items-center gap-1" onClick={() => setCreate({ plan: 'trial', trial_days: 14, max_companies: '3', max_users: '10' })} data-testid="platform-create"><Plus size={14}/>New organisation</button>
      </div>
      {data && data.orphan_profiles > 0 && <div className="text-sm mb-4" style={{ color: 'var(--accent-amber)' }}>{data.orphan_profiles} login(s) belong to no organisation (signed in without sign-up).</div>}
      <PlatformPlans/>
      <div className="card card-dark overflow-x-auto">
        {!data ? <Loading/> : (
          <table className="tbl"><thead><tr><th>Organisation</th><th>Plan</th><th>Status</th><th className="text-right">Companies</th><th className="text-right">Users</th><th>Trial ends</th><th>Created</th><th></th></tr></thead>
            <tbody>{data.organizations.map(o => (
              <tr key={o.id}><td>{o.name}</td><td className="text-xs">{o.plan}</td><td className="text-xs" style={{ color: o.status === 'suspended' ? '#f87171' : undefined }}>{o.status}</td>
                <td className="mono text-right">{o.company_count}{o.max_companies ? ` / ${o.max_companies}` : ''}</td><td className="mono text-right">{o.user_count}{o.max_users ? ` / ${o.max_users}` : ''}</td>
                <td className="text-xs">{o.trial_ends_at ? fmtDate(o.trial_ends_at) : '—'}</td><td className="text-xs">{fmtDate(o.created_at)}</td>
                <td className="text-right"><button className="tab btn-small" onClick={() => setEdit({ ...o, trial_end_date: o.trial_ends_at ? o.trial_ends_at.slice(0, 10) : '', max_companies: o.max_companies || '', max_users: o.max_users || '' })}><Edit3 size={12}/></button></td></tr>
            ))}</tbody></table>
        )}
      </div>
      <Modal open={!!create} onClose={() => setCreate(null)} title="New organisation" maxW={620}>
        {create && <div className="grid grid-cols-2 gap-4">
          <Field label="Organisation name *"><input className="input" value={create.name || ''} onChange={e => setCreate({ ...create, name: e.target.value })}/></Field>
          <Field label="Plan"><select className="input" value={create.plan} onChange={e => setCreate({ ...create, plan: e.target.value })}>{PLANS.map(p => <option key={p}>{p}</option>)}</select></Field>
          <Field label="Owner name *"><input className="input" value={create.owner_name || ''} onChange={e => setCreate({ ...create, owner_name: e.target.value })}/></Field>
          <Field label="Owner email *"><input className="input" type="email" value={create.owner_email || ''} onChange={e => setCreate({ ...create, owner_email: e.target.value })}/></Field>
          <Field label="Trial days"><input className="input mono" type="number" min="0" value={create.trial_days} onChange={e => setCreate({ ...create, trial_days: e.target.value })}/></Field>
          <div className="grid grid-cols-2 gap-3"><Field label="Max companies"><input className="input mono" type="number" min="1" value={create.max_companies} onChange={e => setCreate({ ...create, max_companies: e.target.value })}/></Field><Field label="Max users"><input className="input mono" type="number" min="1" value={create.max_users} onChange={e => setCreate({ ...create, max_users: e.target.value })}/></Field></div>
          <div className="col-span-2 flex justify-end gap-2"><button className="btn-outline btn-small" onClick={() => setCreate(null)}>Cancel</button><button className="btn-gold btn-small" disabled={saving || !create.name || !create.owner_email || !create.owner_name} onClick={saveCreate}>{saving ? 'Creating…' : 'Create'}</button></div>
        </div>}
      </Modal>
      <Modal open={!!created} onClose={() => setCreated(null)} title="Organisation created" maxW={520}>
        {created && <div className="text-sm"><p className="mb-3"><b>{created.organization.name}</b> is ready. Give the owner these sign-in details — the password is shown only now and must be changed at first sign-in.</p><div className="card card-dark mono text-sm p-3">{created.owner.email}<br/>{created.temporary_password}</div><div className="mt-4 text-right"><button className="btn-gold btn-small" onClick={() => setCreated(null)}>Done</button></div></div>}
      </Modal>
      <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit organisation" maxW={620}>
        {edit && <div className="grid grid-cols-2 gap-4">
          <Field label="Name"><input className="input" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })}/></Field>
          <Field label="Plan"><select className="input" value={edit.plan} onChange={e => setEdit({ ...edit, plan: e.target.value })}>{PLANS.map(p => <option key={p}>{p}</option>)}</select></Field>
          <Field label="Status"><select className="input" value={edit.status} onChange={e => setEdit({ ...edit, status: e.target.value })}><option>active</option><option>suspended</option></select></Field>
          <Field label="Trial ends"><input className="input" type="date" value={edit.trial_end_date} onChange={e => setEdit({ ...edit, trial_end_date: e.target.value })}/></Field>
          <Field label="Max companies"><input className="input mono" type="number" min="1" value={edit.max_companies} onChange={e => setEdit({ ...edit, max_companies: e.target.value })}/></Field>
          <Field label="Max users"><input className="input mono" type="number" min="1" value={edit.max_users} onChange={e => setEdit({ ...edit, max_users: e.target.value })}/></Field>
          <div className="col-span-2 flex justify-end gap-2"><button className="btn-outline btn-small" onClick={() => setEdit(null)}>Cancel</button><button className="btn-gold btn-small" disabled={saving} onClick={saveEdit}>{saving ? 'Saving…' : 'Save'}</button></div>
        </div>}
      </Modal>
    </div>
  );
}

function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  // Data Entry: any page other than its four screens bounces to the dashboard
  // (the server refuses the data anyway; this just keeps the screens tidy).
  if (isDataEntry(user) && !DATA_ENTRY_PATHS.includes(location.pathname.replace(/\/+$/, ''))) {
    return <Navigate to="/app/dashboard" replace/>;
  }
  if (user?.role === 'partner' && !isPartnerPathAllowed(location.pathname)) {
    return <Navigate to="/app/dashboard" replace/>;
  }
  return (
    <div className="min-h-screen">
      {isDataEntry(user) && <PrintGuard/>}
      <TopNav/>
      <TrialBanner/>
      <BackToTop/>
      <HScrollButtons/>
      <Routes>
        <Route path="platform" element={<PlatformPage/>}/>
        <Route path="billing" element={<BillingPage/>}/>
        <Route path="dashboard" element={<Dashboard/>}/>
        <Route path="projects" element={<Projects/>}/>
        <Route path="extra-works" element={<ExtraWorks/>}/>
        <Route path="income" element={<Income/>}/>
        <Route path="projects/:id" element={<ProjectDetail/>}/>
        <Route path="company-expenses" element={<CompanyExpensesPanel/>}/>
        <Route path="gst-input" element={<GstInputPage/>}/>
        <Route path="bills" element={<BillsHub/>}/>
        <Route path="security" element={<SecurityInstrumentsPanel/>}/>
        <Route path="partner-settlement" element={<PartnerSettlementDashboard/>}/>
        <Route path="partners/:id" element={<PartnerDetailRedirect/>}/>
        <Route path="vendors" element={<StaffAndVendor/>}/>
        <Route path="vendors/:id" element={<VendorDetail/>}/>
        <Route path="staff/:id" element={<StaffDetail/>}/>
        <Route path="assets" element={<FixedAssets/>}/>
        <Route path="reports" element={<Reports/>}/>
        <Route path="audit-queue" element={<AuditQueuePage/>}/>
        <Route path="data" element={<DataPanel/>}/>
        <Route path="settings" element={<Settings/>}/>
        <Route path="admin" element={<Admin/>}/>
        <Route path="voucher-tools" element={<VoucherToolsPage/>}/>
        <Route path="*" element={<Navigate to="dashboard" replace/>}/>
      </Routes>
    </div>
  );
}

// Running inside the Android / iPhone app (Capacitor shell) rather than a browser.
const isNativeApp = () => { try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); } catch { return false; } };
// In-app update (Android): compares the installed build with /downloads/latest.json and offers the new signed APK.
// Installing it over the existing app keeps all data (same signing key, higher version code). iPhone updates come from the App Store.
const UPDATE_URL = 'https://finance.anvidbuildcon.com/downloads/';
async function checkNativeUpdate() {
  try {
    const cap = window.Capacitor;
    if (!isNativeApp() || cap.getPlatform() !== 'android') return null;
    const info = await cap.Plugins.App.getInfo();
    const installed = parseInt(info.build, 10) || 0;
    const r = await fetch(`${UPDATE_URL}latest.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return { installedName: info.version, available: false };
    const a = (await r.json()).android || {};
    const latest = parseInt(a.versionCode, 10) || 0;
    return { installedName: info.version, available: latest > installed, version: a.version, url: UPDATE_URL + (a.file || 'anvid-financials.apk') };
  } catch { return null; }
}
function useNativeUpdate() {
  const [upd, setUpd] = useState(null);
  const recheck = useCallback(async () => { const u = await checkNativeUpdate(); setUpd(u); return u; }, []);
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    recheck();
    const AppPlugin = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App;
    let handle = null; let cancelled = false;
    if (AppPlugin && AppPlugin.addListener) Promise.resolve(AppPlugin.addListener('resume', recheck)).then(h => { if (cancelled && h && h.remove) h.remove(); else handle = h; }).catch(() => {});
    return () => { cancelled = true; if (handle && handle.remove) handle.remove(); };
  }, [recheck]);
  return [upd, recheck];
}
const startNativeUpdate = (u) => { window.location.href = u.url; };   // the system browser downloads it; open the file to install
function NativeUpdateBanner({ upd }) {
  const [hidden, setHidden] = useState(null);
  if (!upd || !upd.available || hidden === upd.version) return null;
  return (
    <div className="border-t border-subtle px-4 py-2 flex items-center justify-between gap-3 text-sm" style={{ background: 'rgba(201,168,76,0.15)' }} data-testid="app-update-banner">
      <span>A new version of the app is available ({upd.version}).</span>
      <span className="flex items-center gap-2 shrink-0">
        <button onClick={() => startNativeUpdate(upd)} className="btn-gold btn-small" data-testid="app-update-btn">Update</button>
        <button onClick={() => setHidden(upd.version)} className="text-muted text-xs underline" data-testid="app-update-later">Later</button>
      </span>
    </div>
  );
}
// Phone's Back button: step back through the app's own pages; leave the app only from the first screen.
function NativeBridge() {
  const nav = useNavigate();
  const loc = useLocation();
  const pathRef = useRef(loc.pathname);
  pathRef.current = loc.pathname;
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const AppPlugin = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App;
    if (!AppPlugin || !AppPlugin.addListener) return undefined;
    let handle = null; let cancelled = false;
    Promise.resolve(AppPlugin.addListener('backButton', () => {
      if (['/', '/login', '/app/dashboard'].includes(pathRef.current)) { if (AppPlugin.exitApp) AppPlugin.exitApp(); } else { nav(-1); }
    })).then(h => { if (cancelled && h && h.remove) h.remove(); else handle = h; }).catch(() => {});
    return () => { cancelled = true; if (handle && handle.remove) handle.remove(); };
  }, [nav]);
  return null;
}

function AppRouter() {
  return (
    <>
    <NativeBridge/>
    <Routes>
      <Route path="/" element={isNativeApp() ? <Navigate to="/app/dashboard" replace/> : <Landing/>}/>
      <Route path="/login" element={<Login/>}/>
      <Route path="/reset-password" element={<ResetPassword/>}/>
      <Route path="/change-password" element={<ForceChangePassword/>}/>
      <Route path="/voucher/anvid-buildcon" element={<Protected><NotForDataEntry><NotForPartner><AnvidPaymentVoucher/></NotForPartner></NotForDataEntry></Protected>}/>
      <Route path="/app/*" element={<Protected><AppShell/></Protected>}/>
      {/* Round 20 finding: with no catch-all here, any URL outside these
          5 patterns (a typo'd bookmark, a password-reset email whose
          configured redirect doesn't exactly match /reset-password)
          matched nothing -- React Router renders null, and this
          top-level router has no chrome outside <Routes>, so the result
          was a genuinely blank, unrecoverable white page (not even a
          reload fixes a client-side routing gap). Funnel through /app/*
          the same way its own inner catch-all already does -- Protected
          then sends a logged-out visitor to /login and shows a real
          logged-in user the dashboard. */}
      <Route path="*" element={<Navigate to="/app/dashboard" replace/>}/>
    </Routes>
    </>
  );
}

export default function App() {
  return <AuthProvider><AppRouter/></AuthProvider>;
}
